// ZuZan App v2.1 — PO module: retry, draft, pay
import { useState, useEffect, useRef, useMemo } from "react";
import * as Sentry from "@sentry/react";
import * as XLSX from "xlsx";
import { BarChart, Bar, AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell } from "recharts";

// ── Sentry error monitoring ────────────────────────────────────────────────────
Sentry.init({
  dsn: "https://4a7fc4a5cc7f0900e7c70acf636d638c@o4511737932021760.ingest.de.sentry.io/4511737981108304",
  integrations: [Sentry.browserTracingIntegration()],
  tracesSampleRate: 0.1,          // capture 10% of transactions for performance
  environment: "production",
  // Session Replay deliberately disabled — financial data privacy (POPIA)
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 0,
  beforeSend(event) {
    if (window.location.hostname === "localhost") return null; // silence local dev noise
    return event;
  },
});

const C = {
  bg:"#FAF7F2", surface:"#FFFFFF", card:"#FDFCFA", border:"#E8E0D5",
  ink:"#1A1209", inkMid:"#6B5E4E", inkDim:"#B5A898",
  accent:"#C8401A", accentLt:"#C8401A12",
  green:"#2A6B3C", greenLt:"#2A6B3C12",
  gold:"#C4920A", goldLt:"#C4920A12",
  blue:"#1A3A6B", blueLt:"#1A3A6B12",
  red:"#C82A2A", redLt:"#C82A2A12",
  purple:"#6B2A8B", purpleLt:"#6B2A8B12",
};

const DEFAULT_DOC_TEMPLATE = {
  layout:       "classic",        // classic | modern | minimal | bold
  primaryColor: "#C8401A",
  fontFamily:   "Arial, sans-serif",
  invoiceTitle: "TAX INVOICE",
  quoteTitle:   "QUOTE / ESTIMATE",
  footerNote:   "",
  tableStyle:   "shaded",         // shaded | lines | clean
};

const BASE_URL = "https://zuzan-backend.onrender.com";
// audit fix 2026-09-16 — without maximumFractionDigits, "en-ZA" (which uses "," as
// the decimal separator, not a thousands separator) defaults to showing up to 3
// decimal digits for any value with more precision than 2dp (e.g. BCEA hourly
// rate = salary / 195, a repeating decimal). 6726/195 = 34.4923 was rendering as
// "R34,492/hr" — visually indistinguishable from thirty-four thousand rand, even
// though the correct value is R34.49/hr. Capping both min and max at 2 fixes every
// call site that passes an unrounded number through fmt(), not just this one.
const fmt = n => `R${Number(n).toLocaleString("en-ZA",{minimumFractionDigits:2,maximumFractionDigits:2})}`;
const fmtDate = d => { const x = d ? new Date(d) : null; return x && !isNaN(x) ? x.toLocaleDateString("en-ZA",{day:"2-digit",month:"short",year:"numeric"}) : "—"; };
// Latest invoices for the dashboards: the company's real invoices, newest first (demo rows only before data loads).
const recentInvoicesFrom = (live, n) => Array.isArray(live && live.invoices)
  ? live.invoices.slice(0, n).map(i => ({ id: i.invoice_number || ("INV-" + i.id), client: i.client_name || i.client || "",
      due: i.due_date, amount: Number(i.total_amount || i.amount || 0), status: String(i.status || "").replace("InvoiceStatus.", "") }))
  : MOCK_INVOICES.slice(0, n);
// Phone layout: phones in portrait (<=768px wide) AND phones in landscape (short and <=950px wide). Reactive, so
// rotating the phone (or resizing the window) switches layout without a reload.
// ZAR still owed on an invoice: full ZAR value less any part payment (foreign invoices: total x rate, never the amount received)
const owedZar = i => {
  const full = (i.currency && i.currency !== "ZAR") ? (i.total_amount||i.amount||0)*(i.exchange_rate||1) : (i.total_amount||i.amount||0);
  return Math.max(0, full - (i.status==="partial" ? (i.paid_amount_zar||0) : 0));
};
const MOBILE_QUERY = "(max-width: 768px), (max-height: 500px) and (max-width: 950px)";
function useIsMobile() {
  const get = () => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia(MOBILE_QUERY).matches;
  const [m, setM] = useState(get);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia(MOBILE_QUERY);
    const on = () => setM(mq.matches);
    on();
    if (mq.addEventListener) mq.addEventListener("change", on); else mq.addListener(on);
    return () => { if (mq.removeEventListener) mq.removeEventListener("change", on); else mq.removeListener(on); };
  }, []);
  return m;
}

// ── PAYFAST FORM SUBMIT ───────────────────────────────────────────────────────
// PayFast requires a POST form with all params as hidden inputs.
// A bare redirect (GET) doesn't carry the params, so the checkout fails.
function pfSubmit(payfast_url, payfast_data) {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = payfast_url;
  Object.entries(payfast_data).forEach(([k, v]) => {
    const inp = document.createElement("input");
    inp.type = "hidden"; inp.name = k; inp.value = v;
    form.appendChild(inp);
  });
  document.body.appendChild(form);
  form.submit();
}

// ── API HELPER ────────────────────────────────────────────────────────────────
async function api(path, options = {}) {
  const token = localStorage.getItem("zuzan_token");
  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token && !token.startsWith("demo_token") ? {"Authorization": `Bearer ${token}`} : {}),
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Request failed: ${res.status}`);
  }
  return res.json();
}

// ── LIVE DATA HOOK ────────────────────────────────────────────────────────────
function useLiveData() {
  const [dashboard, setDashboard] = useState(null);
  const [expenses,  setExpenses]  = useState(null);
  const [invoices,  setInvoices]  = useState(null);
  const [employees, setEmployees] = useState(null);
  const [trend,     setTrend]     = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [connected, setConnected] = useState(false);

  const loadAll = async () => {
    const token = localStorage.getItem("zuzan_token");
    if (!token || token.startsWith("demo_")) {
      setLoading(false);
      setConnected(false);
      return;
    }
    try {
      const [dash, exp, inv, emp, tr] = await Promise.all([
        api("/reports/dashboard").catch(() => null),
        api("/expenses/").catch(() => null),
        api("/invoices/").catch(() => null),
        api("/employees/?include_inactive=true").catch(() => null),
        api("/reports/monthly-trend").catch(() => null),
      ]);
      if (dash) { setDashboard(dash); setConnected(true); }
      if (exp)  setExpenses(exp);
      if (inv)  setInvoices(inv);
      if (emp)  setEmployees(emp);
      if (tr)   setTrend(tr);
    } catch(e) {
      console.warn("Live data error:", e.message);
      setConnected(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  return { dashboard, expenses, invoices, employees, trend, loading, connected, reload: loadAll };
}

const PLANS = [
  { id:"starter",      name:"Starter",      monthly:399,  annual:3990,  usdMonthly:22, users:2,  invoices:20,         color:C.blue,   icon:"🌱", features:["2 users","20 invoices/month","Invoicing & quotes","Expense tracking","Payroll add-on (in-app, no 3rd party)","Bank feed (all SA banks)","Double-entry general ledger","Trial balance & journal viewer","Basic reports","Email support"] },
  { id:"professional", name:"Professional", monthly:699,  annual:6990,  usdMonthly:39, users:5,  invoices:50,         color:C.accent, icon:"⚡", popular:true, features:["5 users","50 invoices/month","Everything in Starter","Balance sheet reconciliation","Purchase orders & inventory","Fixed assets & depreciation","Budgeting","Advanced reports & VAT201","Priority support"] },
  { id:"business",     name:"Business",     monthly:1299, annual:12990, usdMonthly:72, users:20, invoices:"Unlimited", color:C.green,  icon:"🏢", features:["20 users","Unlimited invoices","Everything in Professional","Budgeting vs actuals","Cash flow forecast","Department budgets","API access","Dedicated account manager"] },
];

const MOCK_INVOICES = [
  {id:"INV-001",client:"Example Client (Pty) Ltd",amount:10000,date:"2026-05-01",due:"2026-06-01",status:"sent",desc:"Add your first invoice using the + New Invoice button"},
];

const MOCK_EXPENSES = [
  {id:"EXP-001",vendor:"Example Supplier",amount:1150,date:"2026-05-01",category:"6360 - Bank Charges",desc:"Add your first expense using the + Add Expense button"},
];

const MOCK_EMPLOYEES = [
  {id:"EMP-001",name:"Example Employee",position:"Position",salary:25000,dept:"General"},
];

const REVENUE_DATA = [
  {month:"Jan",revenue:0,expenses:0,profit:0},
  {month:"Feb",revenue:0,expenses:0,profit:0},
  {month:"Mar",revenue:0,expenses:0,profit:0},
  {month:"Apr",revenue:0,expenses:0,profit:0},
  {month:"May",revenue:0,expenses:0,profit:0},
  {month:"Jun",revenue:0,expenses:0,profit:0},
];

const EXPENSE_PIE = [
  {name:"No data yet",value:1,color:C.inkDim},
];

const ALL_CATS = ["Revenue","Interest Income","Cost of Sales","Utilities","Telecoms","Office","Banking","Insurance","Tax","Equipment","Travel","Salaries","Rent","Marketing","Professional Fees","Other"];

const TAX_YEARS = {
  "2024/2025": {
    brackets: [{min:0,max:237100,rate:0.18,base:0},{min:237101,max:370500,rate:0.26,base:42678},{min:370501,max:512800,rate:0.31,base:77362},{min:512801,max:673000,rate:0.36,base:121475},{min:673001,max:857900,rate:0.39,base:179147},{min:857901,max:1817000,rate:0.41,base:251258},{min:1817001,max:9999999,rate:0.45,base:644489}],
    rebate: 17235, uifCeil: 17712,
  },
  "2025/2026": {
    brackets: [{min:0,max:237100,rate:0.18,base:0},{min:237101,max:370500,rate:0.26,base:42678},{min:370501,max:512800,rate:0.31,base:77362},{min:512801,max:673000,rate:0.36,base:121475},{min:673001,max:857900,rate:0.39,base:179147},{min:857901,max:1817000,rate:0.41,base:251258},{min:1817001,max:9999999,rate:0.45,base:644489}],
    rebate: 17235, uifCeil: 17712,
  },
  "2026/2027": {
    brackets: [{min:0,max:245100,rate:0.18,base:0},{min:245101,max:383100,rate:0.26,base:44118},{min:383101,max:530200,rate:0.31,base:79998},{min:530201,max:695800,rate:0.36,base:125599},{min:695801,max:887000,rate:0.39,base:185215},{min:887001,max:1878600,rate:0.41,base:259783},{min:1878601,max:9999999,rate:0.45,base:666339}],
    rebate: 17820, uifCeil: 17712,
  },
};
const CURRENT_TAX_YEAR = "2026/2027";

function calcPAYE(annual, taxYear) {
  const yr = TAX_YEARS[taxYear || CURRENT_TAX_YEAR] || TAX_YEARS[CURRENT_TAX_YEAR];
  const b = yr.brackets.find(b => annual >= b.min && annual <= b.max);
  if (!b) return 0;
  return Math.max(0, b.base + (annual - b.min) * b.rate - yr.rebate);
}

// BCEA overtime constants (mirrors backend payroll.py)
const BCEA_WEEKLY_HOURS    = 45;
const BCEA_WEEKS_PER_MONTH = 52 / 12;  // 4.3333
const BCEA_OT_WEEKDAY      = 1.5;      // s10 weekday/Saturday
const BCEA_OT_SUNDAY       = 2.0;      // s16
const BCEA_OT_PH           = 1.0;      // s18 public holiday: ADDITIONAL 1.0x (normal day's pay already in salary / normal hours)
// NBCPSS prescribed monthly hours (48h/week × 52/12), per NBCPSS Main Agreement —
// mirrors backend payroll.py's NBCPSS_PRESCRIBED_HOURS.
const NBCPSS_PRESCRIBED_HOURS = 208;
// NBCPSS minimum monthly wage by area + grade (1 Mar 2026 - 28 Feb 2027) -
// mirrors backend NBCPSS_MINIMUM_MONTHLY. "1_2" = Areas 1 & 2, "3" = Area 3.
const NBCPSS_MIN_BY_AREA = {
  "1_2": {A:8184, B:7607, C:7003, D:7003, E:7003},
  "3":   {A:7142, B:6726, C:6726, D:6726, E:6726},
};
const nbcpssMinMonthly = (grade, area) =>
  (NBCPSS_MIN_BY_AREA[area==="3" ? "3" : "1_2"][String(grade||"").toUpperCase()]) || 0;
// NBCPSS annual bonus (Main Agreement clause 8) - mirrors backend nbcpss_annual_bonus()/nbcpss_bonus_due():
//   bonus = ((A x 12) - (B x C)) / 12   A = monthly salary, B = hourly equivalent, C = UNAUTHORISED absence hours
// Paid on the start-date anniversary (after at least 12 completed months).
const nbcpssBonusAmount = (salary, hourly, unauthHrs) =>
  Math.max(0, Math.round((((+salary||0)*12 - (+hourly||0)*Math.max(0,+unauthHrs||0))/12)*100)/100);
const nbcpssBonusDue = (startDate, ym) => {
  if (!startDate || !ym) return false;
  const sy = +String(startDate).slice(0,4), sm = +String(startDate).slice(5,7);
  return sm === +ym.slice(5,7) && (+ym.slice(0,4) - sy) >= 1;
};
// sec = this employee's secData entry; bonusOn (true/false) is a manual override of the automatic anniversary rule
const nbcpssBonusOn = (emp, sec, ym) =>
  !!emp.security_grade && ((sec && sec.bonusOn != null) ? !!sec.bonusOn : nbcpssBonusDue(emp.start_date, ym));
const nbcpssBonusFor = (emp, sec, ym) => {
  if (!nbcpssBonusOn(emp, sec, ym)) return 0;
  const hr = emp.hourly_rate || nbcpssMinMonthly(emp.security_grade, emp.security_area||"1_2")/NBCPSS_PRESCRIBED_HOURS;
  return nbcpssBonusAmount(emp.salary||emp.gross_salary, hr, sec && sec.unauthHrs);
};
// NBCPSS constants (audit fix 2026-09-16) — mirror backend payroll.py so calcPayroll(),
// the client-side dashboard/batch-payment estimator, doesn't silently drop these for
// security employees the way it did before this fix (see [[project_nbcpss_208hr_bug]]).
const NBCPSS_NIGHT_SHIFT_PER_SHIFT   = 8.00;   // R/shift
const NBCPSS_SPECIAL_ALLOW_PER_SHIFT = 10.50;  // R/shift
const NBCPSS_CLEANING_ALLOWANCE      = 32.00;  // R/month — unconditional for every security employee
const NBCPSS_BC_LEVY                 = 7.00;   // R/employee/month, employer cost
const NBCPSS_PSIRA_FEE               = 4.00;   // R/SO/month, employer cost
const NBCPSS_MEDICAL_EMP             = 122.00; // R/month — PSSSBC prescribed hospital plan, employee share
const NBCPSS_MEDICAL_EMPR            = 172.50; // R/month — PSSSBC prescribed hospital plan, employer share
const NBCPSS_PROV_RATE               = 0.075;  // 7.5% of contract gross — PSSPF provident fund (employee + employer each)

// audit fix 2026-09-16 — this always divided by the general 45h/week BCEA default
// (195h/month) even for NBCPSS security employees, who are on a 48h/week schedule
// (208h/month) per the NBCPSS Main Agreement. Pass isSecurity=true for any employee
// with a security_grade at a private_security company.
function bceaHourlyRate(grossMonthly, explicitHourlyRate, isSecurity=false) {
  if (explicitHourlyRate) return explicitHourlyRate;
  const monthlyHours = isSecurity ? NBCPSS_PRESCRIBED_HOURS : (BCEA_WEEKLY_HOURS * BCEA_WEEKS_PER_MONTH);
  return grossMonthly / monthlyHours;
}

function calcOvertime(grossMonthly, otHours=0, sunHours=0, phHours=0, explicitHourlyRate=null, isSecurity=false, sundayOrdinary=false) {
  const hr = bceaHourlyRate(grossMonthly, explicitHourlyRate, isSecurity);
  const otAmt  = Math.round(otHours  * hr * BCEA_OT_WEEKDAY * 100) / 100;
  const sunAmt = Math.round(sunHours * hr * ((isSecurity || sundayOrdinary) ? 1.5 : BCEA_OT_SUNDAY) * 100) / 100;
  const phAmt  = Math.round(phHours  * hr * BCEA_OT_PH      * 100) / 100;
  return { hourlyRate:hr, otAmount:otAmt, sunAmount:sunAmt, phAmount:phAmt,
           otHours:otHours, sunHours:sunHours, phHours:phHours,
           total: Math.round((otAmt + sunAmt + phAmt)*100)/100 };
}

// Section 11F and Medical Tax Credit constants (2026/2027)
const S11F_RATE = 0.275;      // 27.5%
const S11F_CAP  = 430_000;    // R430,000/year (increased from R350,000, effective 1 March 2026)
const MTC_MAIN_FIRST = 376;   // R/month — main member + first dependant each
const MTC_ADDITIONAL = 254;   // R/month — each additional dependant

function calcMedicalTaxCredit(numDependants = 0) {
  // numDependants = people on the plan EXCLUDING the main member
  if (numDependants === 0) return MTC_MAIN_FIRST;
  if (numDependants === 1) return MTC_MAIN_FIRST * 2;
  return MTC_MAIN_FIRST * 2 + MTC_ADDITIONAL * (numDependants - 1);
}

function calcPayroll(
  salary,
  taxYear,
  otHours = 0,
  sunHours = 0,
  phHours = 0,
  explicitHourlyRate = null,
  pensionEmployeePct = 0,
  pensionEmployerPct = 0,
  medicalAidEmployee = 0,
  medicalAidEmployer = 0,
  medicalAidDependants = 0,
  pensionEmployeeFixed = 0,   // optional fixed ZAR/month voluntary top-up
  pensionEmployerFixed = 0,   // optional fixed ZAR/month employer add-on
  // audit fix 2026-09-16 — trailing options object (not positional, so every existing
  // call site that omits it keeps behaving exactly as before): NBCPSS security fields.
  // Mirrors backend calc_payroll's is_security/night_shift_shifts/special_allowance_shifts/
  // annual_bonus — this dashboard/batch-payment estimator previously ignored all of
  // these for security employees, unlike the Run Payroll modal (see [[project_nbcpss_208hr_bug]]).
  nbcpss = {},
) {
  const {
    isSecurity = false,
    nightShiftShifts = 0,
    specialAllowanceShifts = 0,
    annualBonus = 0,
  } = nbcpss;
  const yr = TAX_YEARS[taxYear || CURRENT_TAX_YEAR] || TAX_YEARS[CURRENT_TAX_YEAR];
  const ot = calcOvertime(salary, otHours, sunHours, phHours, explicitHourlyRate, isSecurity);
  // NBCPSS allowances — cleaning allowance is unconditional for every security employee
  // (this is the one most likely to matter for a dashboard/batch-payment total, since
  // it applies every period regardless of shifts worked).
  const nightAllow    = isSecurity ? nightShiftShifts * NBCPSS_NIGHT_SHIFT_PER_SHIFT : 0;
  const specialAllow  = isSecurity ? specialAllowanceShifts * NBCPSS_SPECIAL_ALLOW_PER_SHIFT : 0;
  const cleaningAllow = isSecurity ? NBCPSS_CLEANING_ALLOWANCE : 0;
  const bcLevyEmployer    = isSecurity ? NBCPSS_BC_LEVY : 0;
  const psiraLevyEmployer = isSecurity ? NBCPSS_PSIRA_FEE : 0;

  // NBCPSS medical aid — for security employees the PSSSBC prescribed plan IS the medical aid.
  // Zero out any general medical_aid_employee/employer that may have been set before NBCPSS
  // support was added, to prevent a double deduction on the payslip preview.
  const nbcpssMedicalEmployee = isSecurity ? NBCPSS_MEDICAL_EMP : 0;
  const nbcpssMedicalEmployer = isSecurity ? NBCPSS_MEDICAL_EMPR : 0;
  if (isSecurity) { medicalAidEmployee = 0; medicalAidEmployer = 0; }

  // NBCPSS PSSPF provident fund — 7.5% of basic contract salary (not taxable gross),
  // mirroring backend payroll.py: nbcpss_prov_emp = round(_contract_gross * _psspf_rate, 2).
  // Both employee and employer contribute 7.5%; only the employee share reduces net pay.
  const nbcpssProvidentEmployee = isSecurity ? salary * NBCPSS_PROV_RATE : 0;
  const nbcpssProvidentEmployer = isSecurity ? salary * NBCPSS_PROV_RATE : 0;

  const taxableGross = salary + ot.total + nightAllow + specialAllow + cleaningAllow + annualBonus;

  // Pension/provident: % of gross + optional fixed top-up (both add to total contribution)
  const pensionEmployee = salary * pensionEmployeePct + pensionEmployeeFixed;
  const pensionEmployer = salary * pensionEmployerPct + pensionEmployerFixed;

  // PAYE base: cash remuneration + employer medical aid fringe benefit (7th Schedule s2(i))
  // For security employees the employer share is the PSSSBC plan, not the general medical aid.
  const effectiveMedicalAidEmployer = medicalAidEmployer + nbcpssMedicalEmployer;
  const payeBaseMonthly = taxableGross + effectiveMedicalAidEmployer;
  const payeBaseAnnual  = payeBaseMonthly * 12;

  // Section 11F: min(total employee contributions, 27.5% × remuneration, R430,000)
  // Excess above the cap is still deducted from pay but gives no additional PAYE relief.
  const pensionEmployeeAnnual = pensionEmployee * 12;
  const s11fAnnual = Math.min(pensionEmployeeAnnual, S11F_RATE * payeBaseAnnual, S11F_CAP);
  const s11fMonthly = s11fAnnual / 12;
  const s11fExcessMonthly = Math.max(0, pensionEmployee - s11fMonthly); // post-tax excess

  // PAYE before MTC
  const payeBeforeMtc = calcPAYE(payeBaseAnnual - s11fAnnual, taxYear) / 12;

  // Medical Tax Credit (Section 6A) — applies if on any medical aid (general or NBCPSS)
  const onMedicalAid = (medicalAidEmployee > 0 || medicalAidEmployer > 0 || nbcpssMedicalEmployee > 0);
  const mtc = onMedicalAid ? calcMedicalTaxCredit(medicalAidDependants) : 0;
  const paye = Math.max(0, payeBeforeMtc - mtc);

  // UIF on base salary only (SARS excludes overtime and fringe benefits)
  const uifBase  = Math.min(salary, yr.uifCeil);
  const uifEmp   = uifBase * 0.01;
  const uifEmpr  = uifBase * 0.01;
  const sdl      = taxableGross * 0.01;

  // Net pay: cash earnings minus employee-side deductions (fringe benefit excluded from cash).
  // BC levy (bcLevyEmployer) is an employee deduction despite its name — mirrors backend
  // payroll.py net_pay formula which subtracts bc_levy_emp from net pay.
  const netPay = taxableGross - paye - uifEmp - pensionEmployee - medicalAidEmployee
               - nbcpssMedicalEmployee - nbcpssProvidentEmployee - bcLevyEmployer;

  // Total employer cost
  const totalCost = taxableGross + uifEmpr + sdl + pensionEmployer + medicalAidEmployer
                  + nbcpssMedicalEmployer + nbcpssProvidentEmployer + bcLevyEmployer + psiraLevyEmployer;

  return {
    gross: salary,
    overtime: ot,
    taxableGross:   Math.round(taxableGross),
    nightShiftAllowance:  Math.round(nightAllow),
    specialAllowanceAmount: Math.round(specialAllow),
    cleaningAllowance:    Math.round(cleaningAllow),
    annualBonus:          Math.round(annualBonus),
    bcLevyEmployer:       Math.round(bcLevyEmployer),
    psiraLevyEmployer:    Math.round(psiraLevyEmployer),
    pensionEmployee: Math.round(pensionEmployee),
    pensionEmployer: Math.round(pensionEmployer),
    s11fDeduction:      Math.round(s11fMonthly),
    s11fExcessMonthly:  Math.round(s11fExcessMonthly), // contribution above cap (post-tax, no extra relief)
    medicalAidEmployee: Math.round(medicalAidEmployee),
    medicalAidEmployer: Math.round(medicalAidEmployer),
    nbcpssMedicalEmployee: Math.round(nbcpssMedicalEmployee),
    nbcpssMedicalEmployer: Math.round(nbcpssMedicalEmployer),
    nbcpssProvidentEmployee: Math.round(nbcpssProvidentEmployee),
    nbcpssProvidentEmployer: Math.round(nbcpssProvidentEmployer),
    medicalTaxCredit:   Math.round(mtc),
    payeBeforeMtc:  Math.round(payeBeforeMtc),
    paye:           Math.round(paye),
    uifEmployee:    Math.round(uifEmp),
    uifEmployer:    Math.round(uifEmpr),
    sdl:            Math.round(sdl),
    netPay:         Math.round(netPay),
    totalCost:      Math.round(totalCost),
  };
}

const Badge = ({label,color,bg}) => (
  <span style={{fontSize:10,fontWeight:700,color,background:bg,padding:"3px 9px",borderRadius:20,letterSpacing:0.5,textTransform:"uppercase"}}>{label}</span>
);

const StatusBadge = ({status}) => {
  const m={
    paid:    [C.green,  C.greenLt,  "Paid"],
    sent:    [C.gold,   C.goldLt,   "Pending"],
    pending: [C.gold,   C.goldLt,   "Pending"],
    draft:   [C.inkMid, C.border,   "Draft"],
    overdue: [C.red,    C.redLt,    "Overdue"],
    partial: ["#b45309","#fef3c7",  "Partial"],
  };
  const [c,bg,l]=m[status]||m.pending;
  return <Badge label={l} color={c} bg={bg}/>;
};

const KPI = ({label,value,sub,color=C.ink,icon,onClick,active=false}) => (
  <div onClick={onClick} style={{background:C.surface,border:active?`2px solid ${color}`:`1px solid ${C.border}`,borderRadius:14,padding:active?"15px 17px":"16px 18px",flex:"1 1 140px",minWidth:0,cursor:onClick?"pointer":"default",transition:"box-shadow 0.15s, border 0.15s",boxShadow:active?"0 0 0 3px "+color+"18":"none"}}
    onMouseEnter={e=>{if(onClick)e.currentTarget.style.boxShadow=active?"0 0 0 3px "+color+"18":"0 4px 16px rgba(0,0,0,0.10)";}}
    onMouseLeave={e=>{e.currentTarget.style.boxShadow=active?"0 0 0 3px "+color+"18":"none";}}>
    <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
      <span style={{fontSize:10,color:C.inkMid,letterSpacing:0.5,textTransform:"uppercase",fontWeight:600}}>{label}</span>
      <span style={{fontSize:16}}>{icon}</span>
    </div>
    <div style={{fontSize:22,fontWeight:800,color,fontFamily:"'Playfair Display',serif",marginBottom:3}}>{value}</div>
    {sub && <div style={{fontSize:11,color:C.inkDim}}>{sub}</div>}
    {onClick && <div style={{fontSize:10,color:C.inkMid,marginTop:4,opacity:0.6}}>Tap to view breakdown ›</div>}
  </div>
);

const Tooltip2 = ({active,payload,label}) => {
  if (!active||!payload?.length) return null;
  return (
    <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,padding:"10px 14px",fontSize:12}}>
      <div style={{color:C.inkMid,marginBottom:6,fontWeight:600}}>{label}</div>
      {payload.map((p,i)=><div key={i} style={{color:p.color}}>{p.name}: {fmt(p.value)}</div>)}
    </div>
  );
};

const SectionHeader = ({title,sub,action,onAction}) => (
  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:20}}>
    <div>
      <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:26,color:C.ink,margin:0}}>{title}</h2>
      {sub&&<p style={{fontSize:12,color:C.inkMid,marginTop:3}}>{sub}</p>}
    </div>
    {action&&<button onClick={onAction} style={{background:C.accent,color:"#fff",border:"none",borderRadius:10,padding:"10px 20px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>{action}</button>}
  </div>
);
// ── COA DATA ──────────────────────────────────────────────────────────────────
const COA_GROUPS = ["Assets","Liabilities","Equity","Income","Cost of Sales","Expenses"];
const EXPENSE_COA_GROUPS = ["Cost of Sales","Expenses"];

const GROUP_COLORS = {
  "Assets":        {color:C.blue,  bg:C.blueLt,  icon:"🏛️"},
  "Liabilities":   {color:C.red,   bg:C.redLt,   icon:"📋"},
  "Equity":        {color:C.purple,bg:C.purpleLt, icon:"⚖️"},
  "Income":        {color:C.green, bg:C.greenLt,  icon:"💰"},
  "Cost of Sales": {color:C.gold,  bg:C.goldLt,   icon:"🏭"},
  "Expenses":      {color:C.accent,bg:C.accentLt, icon:"📤"},
};

const DEFAULT_COA = [
  {code:"1000",name:"ASSETS",type:"Header",group:"Assets",normal:"Debit",description:"All assets of the business"},
  {code:"1110",name:"Cash and Cash Equivalents",type:"Detail",group:"Assets",normal:"Debit",description:"Bank accounts and petty cash"},
  {code:"1111",name:"FNB Business Account",type:"Detail",group:"Assets",normal:"Debit",description:"Primary FNB business cheque account"},
  {code:"1112",name:"ABSA Business Account",type:"Detail",group:"Assets",normal:"Debit",description:"ABSA business account"},
  {code:"1113",name:"Petty Cash",type:"Detail",group:"Assets",normal:"Debit",description:"Cash on hand for small expenses"},
  {code:"1100",name:"Trade Receivables (Debtors)",type:"Detail",group:"Assets",normal:"Debit",description:"Amounts owed by customers — Debtors Control, matches journal account 1100"},
  {code:"1130",name:"Inventory",type:"Detail",group:"Assets",normal:"Debit",description:"Stock on hand"},
  {code:"1140",name:"Prepaid Expenses",type:"Detail",group:"Assets",normal:"Debit",description:"Expenses paid in advance"},
  {code:"1150",name:"VAT Input",type:"Detail",group:"Assets",normal:"Debit",description:"VAT claimable from SARS"},
  {code:"1160",name:"SARS Income Tax Receivable",type:"Detail",group:"Assets",normal:"Debit",description:"Income tax refund due from SARS"},
  {code:"1210",name:"Property Plant and Equipment",type:"Header",group:"Assets",normal:"Debit",description:"Tangible fixed assets"},
  {code:"1211",name:"Land and Buildings",type:"Detail",group:"Assets",normal:"Debit",description:"Owned property"},
  {code:"1212",name:"Vehicles",type:"Detail",group:"Assets",normal:"Debit",description:"Motor vehicles"},
  {code:"1213",name:"Computer Equipment",type:"Detail",group:"Assets",normal:"Debit",description:"Computers and laptops"},
  {code:"1214",name:"Office Furniture and Equipment",type:"Detail",group:"Assets",normal:"Debit",description:"Desks, chairs, office equipment"},
  {code:"1221",name:"Accum Dep - Vehicles",type:"Detail",group:"Assets",normal:"Credit",description:"Depreciation on vehicles"},
  {code:"1222",name:"Accum Dep - Computer Equipment",type:"Detail",group:"Assets",normal:"Credit",description:"Depreciation on computers"},
  {code:"2000",name:"LIABILITIES",type:"Header",group:"Liabilities",normal:"Credit",description:"All liabilities of the business"},
  {code:"2110",name:"Trade Payables (Creditors)",type:"Detail",group:"Liabilities",normal:"Credit",description:"Amounts owed to suppliers — Creditors Control, posted to journal account 2000 (code 2110 here only because 2000 is the Liabilities header)"},
  {code:"2121",name:"PAYE Payable",type:"Detail",group:"Liabilities",normal:"Credit",description:"Employee tax withheld - due to SARS by 7th"},
  {code:"2122",name:"UIF Payable",type:"Detail",group:"Liabilities",normal:"Credit",description:"UIF contributions due to SARS"},
  {code:"2123",name:"SDL Payable",type:"Detail",group:"Liabilities",normal:"Credit",description:"Skills Development Levy due to SARS"},
  {code:"2124",name:"VAT Output",type:"Detail",group:"Liabilities",normal:"Credit",description:"VAT collected on sales - due to SARS"},
  {code:"2125",name:"VAT Control",type:"Detail",group:"Liabilities",normal:"Credit",description:"Net VAT position"},
  {code:"2126",name:"Income Tax Payable",type:"Detail",group:"Liabilities",normal:"Credit",description:"Corporate income tax due to SARS"},
  {code:"2127",name:"Provisional Tax Payable",type:"Detail",group:"Liabilities",normal:"Credit",description:"Provisional tax IRP6 submissions"},
  {code:"2130",name:"Accrued Salaries",type:"Detail",group:"Liabilities",normal:"Credit",description:"Salaries earned but not yet paid"},
  {code:"2140",name:"Deferred Revenue",type:"Detail",group:"Liabilities",normal:"Credit",description:"Income received in advance"},
  {code:"2150",name:"Short-term Loan",type:"Detail",group:"Liabilities",normal:"Credit",description:"Bank overdraft or short-term borrowings"},
  {code:"2160",name:"Directors Loan Account",type:"Detail",group:"Liabilities",normal:"Credit",description:"Amounts owed to directors"},
  {code:"2210",name:"Long-term Loan",type:"Detail",group:"Liabilities",normal:"Credit",description:"Bank loans repayable after 12 months"},
  {code:"2220",name:"Mortgage Bond",type:"Detail",group:"Liabilities",normal:"Credit",description:"Property mortgage"},
  {code:"3000",name:"EQUITY",type:"Header",group:"Equity",normal:"Credit",description:"Owners interest in the business"},
  {code:"3110",name:"Ordinary Share Capital",type:"Detail",group:"Equity",normal:"Credit",description:"Ordinary shares issued"},
  {code:"3200",name:"Retained Income",type:"Detail",group:"Equity",normal:"Credit",description:"Accumulated profits not distributed"},
  {code:"3300",name:"Dividends Paid",type:"Detail",group:"Equity",normal:"Debit",description:"Dividends declared and paid to shareholders"},
  {code:"3400",name:"Drawings",type:"Detail",group:"Equity",normal:"Debit",description:"Amounts withdrawn by owners"},
  {code:"4000",name:"INCOME",type:"Header",group:"Income",normal:"Credit",description:"All revenue earned by the business"},
  {code:"4110",name:"Sales - Products",type:"Detail",group:"Income",normal:"Credit",description:"Revenue from sale of goods"},
  {code:"4120",name:"Sales - Services",type:"Detail",group:"Income",normal:"Credit",description:"Revenue from services rendered"},
  {code:"4130",name:"Consulting Fees",type:"Detail",group:"Income",normal:"Credit",description:"Professional consulting income"},
  {code:"4140",name:"Commission Income",type:"Detail",group:"Income",normal:"Credit",description:"Commission earned"},
  {code:"4150",name:"Rental Income",type:"Detail",group:"Income",normal:"Credit",description:"Income from property rental"},
  {code:"4160",name:"Subscription Income",type:"Detail",group:"Income",normal:"Credit",description:"Recurring subscription revenue"},
  {code:"4210",name:"Interest Income",type:"Detail",group:"Income",normal:"Credit",description:"Interest earned on bank accounts"},
  {code:"4220",name:"Discount Received",type:"Detail",group:"Income",normal:"Credit",description:"Discounts received from suppliers"},
  {code:"4230",name:"Government Grants",type:"Detail",group:"Income",normal:"Credit",description:"SEDA, SEFA or government grants"},
  {code:"5000",name:"COST OF SALES",type:"Header",group:"Cost of Sales",normal:"Debit",description:"Direct costs of generating revenue"},
  // Codes 5010-5050 (audit fix 2026-09-19 — previously 5110-5150, which silently
  // collided with unrelated ledger accounts of the same code in journal.py's
  // DEFAULT_ACCOUNTS, e.g. "5110 - Purchases" here vs "Payroll Levies (UIF/SDL)"
  // there — an expense categorised as Purchases was posting to the payroll
  // levies account. Renumbered to a free range; only affects new selections,
  // existing stored category strings on old expenses are unaffected.
  {code:"5010",name:"Purchases",type:"Detail",group:"Cost of Sales",normal:"Debit",description:"Goods purchased for resale"},
  {code:"5020",name:"Freight and Delivery",type:"Detail",group:"Cost of Sales",normal:"Debit",description:"Delivery costs for goods purchased"},
  {code:"5030",name:"Import Duties",type:"Detail",group:"Cost of Sales",normal:"Debit",description:"Customs and import duties"},
  {code:"5040",name:"Direct Labour",type:"Detail",group:"Cost of Sales",normal:"Debit",description:"Labour directly used in production"},
  {code:"5050",name:"Direct Materials",type:"Detail",group:"Cost of Sales",normal:"Debit",description:"Raw materials used in production"},
  {code:"6000",name:"OPERATING EXPENSES",type:"Header",group:"Expenses",normal:"Debit",description:"All operating expenses"},
  {code:"6110",name:"Salaries and Wages",type:"Detail",group:"Expenses",normal:"Debit",description:"Gross salaries paid to employees"},
  {code:"6120",name:"Employer UIF Contributions",type:"Detail",group:"Expenses",normal:"Debit",description:"Employer portion of UIF 1 percent of salary"},
  {code:"6130",name:"SDL Skills Development Levy",type:"Detail",group:"Expenses",normal:"Debit",description:"1 percent of payroll skills levy"},
  {code:"6140",name:"Medical Aid Contributions",type:"Detail",group:"Expenses",normal:"Debit",description:"Employer medical aid contributions"},
  {code:"6150",name:"Pension and Provident Fund",type:"Detail",group:"Expenses",normal:"Debit",description:"Employer retirement fund contributions"},
  {code:"6160",name:"Leave Pay Expense",type:"Detail",group:"Expenses",normal:"Debit",description:"Annual leave provision"},
  {code:"6210",name:"Rent - Office",type:"Detail",group:"Expenses",normal:"Debit",description:"Monthly office rental"},
  {code:"6220",name:"Electricity and Water",type:"Detail",group:"Expenses",normal:"Debit",description:"Eskom and municipal utilities"},
  {code:"6230",name:"Rates and Taxes",type:"Detail",group:"Expenses",normal:"Debit",description:"Municipal rates on owned property"},
  {code:"6240",name:"Cleaning and Hygiene",type:"Detail",group:"Expenses",normal:"Debit",description:"Office cleaning services"},
  {code:"6250",name:"Security",type:"Detail",group:"Expenses",normal:"Debit",description:"Security services and systems"},
  {code:"6260",name:"Repairs and Maintenance",type:"Detail",group:"Expenses",normal:"Debit",description:"Office and equipment maintenance"},
  {code:"6310",name:"Stationery and Office Supplies",type:"Detail",group:"Expenses",normal:"Debit",description:"Paper, pens, printing supplies"},
  {code:"6320",name:"Printing and Photocopying",type:"Detail",group:"Expenses",normal:"Debit",description:"Printing costs"},
  {code:"6340",name:"Subscriptions",type:"Detail",group:"Expenses",normal:"Debit",description:"Software and service subscriptions"},
  {code:"6350",name:"Computer Software",type:"Detail",group:"Expenses",normal:"Debit",description:"Software licences and SaaS"},
  {code:"6360",name:"Bank Charges",type:"Detail",group:"Expenses",normal:"Debit",description:"Monthly bank service fees"},
  {code:"6370",name:"Sundry Expenses",type:"Detail",group:"Expenses",normal:"Debit",description:"Miscellaneous small expenses"},
  {code:"6410",name:"Telephone Landline",type:"Detail",group:"Expenses",normal:"Debit",description:"Telkom and landline costs"},
  {code:"6420",name:"Cellphone Expenses",type:"Detail",group:"Expenses",normal:"Debit",description:"Mobile phone costs"},
  {code:"6430",name:"Internet Fibre",type:"Detail",group:"Expenses",normal:"Debit",description:"Fibre and internet connectivity"},
  {code:"6510",name:"Fuel and Oil",type:"Detail",group:"Expenses",normal:"Debit",description:"Petrol, diesel, oil"},
  {code:"6520",name:"Vehicle Maintenance",type:"Detail",group:"Expenses",normal:"Debit",description:"Servicing and repairs"},
  {code:"6530",name:"Vehicle Insurance",type:"Detail",group:"Expenses",normal:"Debit",description:"Motor vehicle insurance"},
  {code:"6540",name:"Toll Fees",type:"Detail",group:"Expenses",normal:"Debit",description:"E-tolls and toll gates"},
  {code:"6560",name:"Air Travel",type:"Detail",group:"Expenses",normal:"Debit",description:"Flights for business"},
  {code:"6570",name:"Accommodation",type:"Detail",group:"Expenses",normal:"Debit",description:"Hotels and lodging"},
  {code:"6610",name:"Advertising",type:"Detail",group:"Expenses",normal:"Debit",description:"Print and media advertising"},
  {code:"6620",name:"Digital Marketing",type:"Detail",group:"Expenses",normal:"Debit",description:"Google Ads, Facebook, Instagram"},
  {code:"6630",name:"Website Costs",type:"Detail",group:"Expenses",normal:"Debit",description:"Hosting, domain, web development"},
  {code:"6640",name:"Branding and Design",type:"Detail",group:"Expenses",normal:"Debit",description:"Logo, branding, graphic design"},
  {code:"6660",name:"Entertainment Clients",type:"Detail",group:"Expenses",normal:"Debit",description:"Client entertainment"},
  {code:"6710",name:"Accounting and Audit Fees",type:"Detail",group:"Expenses",normal:"Debit",description:"Accountant, bookkeeper, auditor fees"},
  {code:"6720",name:"Legal Fees",type:"Detail",group:"Expenses",normal:"Debit",description:"Attorney and legal costs"},
  {code:"6730",name:"Consulting Fees Expense",type:"Detail",group:"Expenses",normal:"Debit",description:"Business consultants"},
  {code:"6740",name:"IT Support",type:"Detail",group:"Expenses",normal:"Debit",description:"Technical support and IT services"},
  {code:"6750",name:"CIPC Annual Return",type:"Detail",group:"Expenses",normal:"Debit",description:"Annual return filing fee"},
  {code:"6810",name:"Business Insurance",type:"Detail",group:"Expenses",normal:"Debit",description:"General business insurance"},
  {code:"6820",name:"Professional Indemnity",type:"Detail",group:"Expenses",normal:"Debit",description:"PI insurance"},
  {code:"6910",name:"Income Tax Expense",type:"Detail",group:"Expenses",normal:"Debit",description:"Corporate income tax 27 percent of taxable income"},
  {code:"6930",name:"Penalties and Interest SARS",type:"Detail",group:"Expenses",normal:"Debit",description:"SARS penalties and interest charged"},
  {code:"7100",name:"Depreciation",type:"Detail",group:"Expenses",normal:"Debit",description:"Depreciation on fixed assets"},
  {code:"7200",name:"Interest Expense",type:"Detail",group:"Expenses",normal:"Debit",description:"Interest paid on loans and overdrafts"},
  {code:"7300",name:"Bad Debts Written Off",type:"Detail",group:"Expenses",normal:"Debit",description:"Uncollectable debts written off"},
  {code:"7600",name:"Donations",type:"Detail",group:"Expenses",normal:"Debit",description:"Charitable donations Section 18A"},
];

// BANK_FORMATS — these are now FALLBACK values only.
// The parser first auto-detects the data start row via date-pattern scan,
// then auto-detects column layout from the header row's text.
// Values here kick in only when header detection fails (e.g. headerless exports).
//
// Known SA bank header formats (for reference):
//  FNB Business:   Date | Amount | Balance | Description            → single Amount col
//  ABSA:           Date | Description | Debit | Credit | Balance    → separate Debit/Credit
//  Standard Bank:  Date | Description | Debit | Credit | Balance    → separate Debit/Credit
//  Nedbank:        Date | Transaction Description | Debit Amount | Credit Amount | Balance
//  Capitec:        Date | Transaction Description | Amount | Running Balance
//  Discovery Bank: Date | Description | Debit | Credit | Balance    → separate Debit/Credit
//  Investec:       Date | Transaction Description | Debit | Credit | Balance
//  TymeBank:       Date | Description | Amount | Balance
const BANK_FORMATS = {
  fnb:          {name:"FNB",           logo:"🟢", skipRows:5, dateCol:0, amtCol:1,  descCol:3},
  absa:         {name:"ABSA",          logo:"🔴", skipRows:1, dateCol:0, amtCol:2,  descCol:1},
  standardbank: {name:"Standard Bank", logo:"🔵", skipRows:1, dateCol:0, amtCol:2,  descCol:1},
  nedbank:      {name:"Nedbank",       logo:"🟩", skipRows:1, dateCol:0, amtCol:2,  descCol:1},
  capitec:      {name:"Capitec",       logo:"🟦", skipRows:1, dateCol:0, amtCol:2,  descCol:1},
  discovery:    {name:"Discovery Bank",logo:"🟣", skipRows:1, dateCol:0, amtCol:2,  descCol:1},
  investec:     {name:"Investec",      logo:"🔷", skipRows:1, dateCol:0, amtCol:2,  descCol:1},
  tymebank:     {name:"TymeBank",      logo:"🩵", skipRows:1, dateCol:0, amtCol:2,  descCol:1},
};

function autoCategory(desc, type) {
  const d = desc.toLowerCase();
  if (type === "credit") {
    // Income category auto-detection
    if (d.includes("interest")) return "Interest Income";
    if (d.includes("refund") || d.includes("reversal")) return "Other Income";
    if (d.includes("rental") || d.includes("rent receive")) return "Rental Income";
    return "Sales Revenue";  // default income account
  }
  // Expense category auto-detection
  if (d.includes("interest")) return "Interest Income";
  if (d.includes("eskom") || d.includes("electric") || d.includes("water")) return "Utilities";
  if (d.includes("telkom") || d.includes("vodacom") || d.includes("mtn") || d.includes("fibre")) return "Telecoms";
  if (d.includes("sars") || d.includes("tax") || d.includes("vat")) return "Tax";
  if (d.includes("salary") || d.includes("payroll") || d.includes("wages")) return "Salaries";
  if (d.includes("rent") || d.includes("lease")) return "Rent";
  if (d.includes("bank") || d.includes("charge") || d.includes("fee")) return "Banking";
  if (d.includes("insurance") || d.includes("assurance")) return "Insurance";
  if (d.includes("fuel") || d.includes("petrol") || d.includes("shell") || d.includes("engen")) return "Travel";
  if (d.includes("office") || d.includes("stationery")) return "Office";
  if (d.includes("advertis") || d.includes("google") || d.includes("facebook")) return "Marketing";
  if (d.includes("attorney") || d.includes("accountant") || d.includes("audit")) return "Professional Fees";
  return "Other";
}

// Checks company-learned rules first, then falls back to built-in patterns.
// `rules` is the array fetched from /category-rules/ (sorted by match_count desc).
function smartCategory(desc, type, rules) {
  if (rules && rules.length) {
    const d = desc.toLowerCase();
    const match = rules.find(r =>
      (r.txn_type === "any" || r.txn_type === type) && d.includes(r.keyword)
    );
    if (match) return match.category;
  }
  return autoCategory(desc, type);
}

function parseCSVLine(line) {
  const result = [];
  let current = "";
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    if (line[i] === '"') inQ = !inQ;
    else if (line[i] === ',' && !inQ) { result.push(current.trim().replace(/^"|"$/g,"")); current = ""; }
    else current += line[i];
  }
  result.push(current.trim().replace(/^"|"$/g,""));
  return result;
}

function parseAmt(str) {
  if (!str || !str.trim()) return 0;
  let s = str.trim()
    .replace(/^R\s*/i, "")        // strip leading R / r
    .replace(/ /g, "")       // non-breaking space
    .replace(/\s/g, "")           // all remaining whitespace (thousand-sep spaces)
    .replace(/[€$£]/g, "");       // other currency symbols
  // Parentheses = negative: (1234.56) → -1234.56
  if (s.startsWith("(") && s.endsWith(")")) s = "-" + s.slice(1, -1);
  // Handle European format: 1.234,56 → 1234.56
  if (/^\-?\d{1,3}(\.\d{3})+(,\d+)?$/.test(s)) s = s.replace(/\./g, "").replace(",", ".");
  // Remove thousands commas: 1,234.56 → 1234.56
  else s = s.replace(/,/g, "");
  return parseFloat(s) || 0;
}

function parseDt(str) {
  if (!str || !str.trim()) return new Date().toISOString().slice(0, 10);
  const s = str.trim();
  const MON = {jan:1,feb:2,mar:3,apr:4,may:5,jun:6,jul:7,aug:8,sep:9,oct:10,nov:11,dec:12};
  // YYYY/MM/DD or YYYY-MM-DD
  let m = s.match(/^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})$/);
  if (m) return `${m[1]}-${m[2].padStart(2,"0")}-${m[3].padStart(2,"0")}`;
  // DD/MM/YYYY or DD-MM-YYYY
  m = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (m) return `${m[3]}-${m[2].padStart(2,"0")}-${m[1].padStart(2,"0")}`;
  // DD Mon YYYY  or  DD Month YYYY
  m = s.match(/^(\d{1,2})\s+([A-Za-z]{3,})\s+(\d{4})$/);
  if (m) { const mo = MON[m[2].slice(0,3).toLowerCase()]; if (mo) return `${m[3]}-${String(mo).padStart(2,"0")}-${m[1].padStart(2,"0")}`; }
  // YYYYMMDD
  m = s.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  return new Date().toISOString().slice(0, 10);
}
// ── DASHBOARD ─────────────────────────────────────────────────────────────────
function CIPCBanner({cipc, poDupWarning}) {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;

  const banners = [];
  if (cipc && cipc.warning) {
    banners.push({
      msg:   cipc.message,
      color: cipc.overdue ? "#C82A2A" : "#C4920A",
      bg:    cipc.overdue ? "#FFF0F0" : "#FFFBF0",
      link:  "https://www.cipc.co.za",
      label: "File at CIPC ↗",
    });
  }
  if (poDupWarning) {
    banners.push({
      msg:   poDupWarning,
      color: "#C8401A",
      bg:    "#FFF5F0",
      link:  null,
      label: null,
    });
  }
  if (banners.length === 0) return null;

  return (
    <div style={{marginBottom:16}}>
      {banners.map((b,i) => (
        <div key={i} style={{
          display:"flex", alignItems:"center", justifyContent:"space-between",
          background:b.bg, border:`1px solid ${b.color}30`,
          borderLeft:`4px solid ${b.color}`, borderRadius:10,
          padding:"10px 16px", marginBottom:8, gap:12,
        }}>
          <span style={{fontSize:13, color:b.color, fontWeight:600, flex:1}}>{b.msg}</span>
          <div style={{display:"flex",gap:8,flexShrink:0}}>
            {b.link && (
              <a href={b.link} target="_blank" rel="noopener noreferrer"
                style={{fontSize:12,fontWeight:700,color:b.color,textDecoration:"none",
                  padding:"4px 12px",border:`1px solid ${b.color}`,borderRadius:6}}>
                {b.label}
              </a>
            )}
            <button onClick={()=>setDismissed(true)}
              style={{background:"none",border:"none",color:b.color,cursor:"pointer",
                fontSize:16,lineHeight:1,padding:"2px 4px",opacity:0.6}}>✕</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function Dashboard({live = {}}) {
  const d = live.dashboard;
  const revenueData = live.trend || REVENUE_DATA;
  const isLive = live.connected;
  const [drill, setDrill] = useState(null); // {type, title, rows, cols, total, color}
  const [drillLoading, setDrillLoading] = useState(false);


  const openDrill = async (type) => {
    setDrillLoading(true);
    setDrill({type, rows:[], cols:[], title:"", total:0, color:C.ink});
    try {
      if (type === "revenue") {
        // Same rows the Revenue card adds up (fully AND part paid invoices + bank-import income); VAT shown separately
        const det = await api(`/reports/management/revenue-detail?date_from=2000-01-01&date_to=${new Date().toISOString().slice(0,10)}`);
        const rows = (det.rows||[]).map(r => [r.invoice_number, r.client, r.status==="partial" ? "Part paid" : "Paid",
          fmt(r.received_zar), fmt(r.vat_zar), fmt(r.revenue_zar), r.paid_date ? new Date(r.paid_date).toLocaleDateString("en-ZA") : "—"]);
        if (det.bank_import_income) rows.push(["Bank import","Income posted from bank statements","","","",fmt(det.bank_import_income),""]);
        setDrill({type, title:"Revenue — cash received, excluding VAT", color:C.green, total: det.total,
          cols:["Invoice","Client","Status","Received","VAT","Revenue","Paid Date"], rows});
      } else if (type === "expenses") {
        const [exps, deprSchedule, allPOs] = await Promise.all([
          api("/expenses"),
          api("/fixed-assets/schedule").catch(()=>[]),
          api("/purchase-orders/").catch(()=>[]),
        ]);
        // Expense rows — VAT-exclusive
        const expRows = exps.map(e=>({
          _date: new Date(e.expense_date||e.created_at||0),
          cols: [e.description||"—", e.category||"—", fmt((e.amount||0)-(e.vat_amount||0)),
            e.expense_date ? new Date(e.expense_date).toLocaleDateString("en-ZA") : "—"],
          amount: (e.amount||0)-(e.vat_amount||0),
        }));
        // Depreciation rows — one row per DepreciationEntry
        const deprRows = (deprSchedule||[]).map(d=>({
          _date: new Date(d.period+"-01"),
          cols: [`${d.asset_name} — Depreciation`, "Depreciation (IAS 16)", fmt(d.amount||0),
            d.period ? new Date(d.period+"-01").toLocaleDateString("en-ZA",{year:"numeric",month:"short"}) : "—"],
          amount: d.amount||0,
        }));
        // PO COGS rows — received/partial/paid purchase orders (ex-VAT, matching backend)
        const poRows = (allPOs||[]).filter(po=>["received","partial","paid"].includes(po.status)).map(po=>({
          _date: new Date(po.received_date||po.created_at||0),
          cols: [po.description||po.po_number||"Purchase Order", "Cost of Sales (POs)",
            fmt((po.total_amount||0)-(po.vat_amount||0)),
            po.received_date ? new Date(po.received_date).toLocaleDateString("en-ZA") : "—"],
          amount: (po.total_amount||0)-(po.vat_amount||0),
        }));
        const allRows = [...expRows, ...deprRows, ...poRows].sort((a,b)=>b._date-a._date);
        setDrill({type, title:"Expenses — All Time (excl. VAT, incl. Depreciation & PO COGS)", color:C.red,
          total: allRows.reduce((s,r)=>s+r.amount,0),
          cols:["Description","Category","Amount (excl. VAT)","Date"],
          rows: allRows.map(r=>r.cols)});
      } else if (type === "outstanding") {
        const invs = await api("/invoices");
        const rows = invs.filter(i => ["sent","overdue","partial"].includes(i.status))
          .sort((a,b) => new Date(a.due_date||0)-new Date(b.due_date||0));
        const today = new Date();
        setDrill({type, title:"Outstanding — Pending & Overdue", color:C.gold,
          total: rows.reduce((s,i)=>s+owedZar(i),0),
          cols:["Invoice","Client","Amount (ZAR)","Due Date","Days Overdue"],
          rows: rows.map(i=>{
            const due = i.due_date ? new Date(i.due_date) : null;
            const overdue = due ? Math.max(0, Math.floor((today-due)/(1000*60*60*24))) : 0;
            return [i.invoice_number||i.id, i.client_name||i.client, fmt(owedZar(i)),
              due ? due.toLocaleDateString("en-ZA") : "—",
              overdue > 0 ? `${overdue}d` : "Current"];
          })});
      } else if (type === "payroll") {
        const emps = await api("/employees");
        const rows = emps.filter(e=>e.is_active!==false);
        // audit fix 2026-09-16 — this drill-down is a point-in-time snapshot, not tied
        // to a specific processed period, so OT/night-shift/bonus aren't applicable here;
        // but the always-on NBCPSS cleaning allowance + BC levy + PSIRA fee for security
        // employees were being silently dropped. See [[project_nbcpss_208hr_bug]].
        const calcRow = e => calcPayroll(e.gross_salary, null, 0,0,0,null, 0,0,0,0,0,0,0, {isSecurity: !!e.security_grade});
        setDrill({type, title:"Payroll — Active Employees", color:C.blue,
          total: rows.reduce((s,e)=>s+calcRow(e).totalCost,0),
          cols:["Employee","Position","Gross Salary","Net Pay","Employer Cost"],
          rows: rows.map(e=>{
            const p = calcRow(e);
            return [e.name, e.position||"—", fmt(e.gross_salary), fmt(p.netPay), fmt(p.totalCost)];
          })});
      } else if (type === "vat") {
        const invs = await api("/invoices");
        const exps = await api("/expenses");
        const outputVat = invs.reduce((s,i)=>s+(i.vat_amount||0),0);
        const inputVat  = exps.reduce((s,e)=>s+(e.vat_amount||0),0);
        const netVat    = outputVat - inputVat;
        setDrill({type, title:"VAT Position — All Time", color: netVat>=0?C.red:C.green,
          total: netVat,
          cols:["Description","Amount"],
          rows:[
            ["Output VAT (charged on invoices)", fmt(outputVat)],
            ["Input VAT (claimable on expenses)", `- ${fmt(inputVat)}`],
            [netVat>=0?"Net VAT payable to SARS":"Net VAT refund due from SARS", fmt(Math.abs(netVat))],
          ]});
      } else if (type === "profit") {
        const [exps, allPOs, deprSchedule, emps] = await Promise.all([
          api("/expenses"),
          api("/purchase-orders/").catch(()=>[]),
          api("/fixed-assets/schedule").catch(()=>[]),
          api("/employees").catch(()=>[]),
        ]);
        const rdet = await api(`/reports/management/revenue-detail?date_from=2000-01-01&date_to=${new Date().toISOString().slice(0,10)}`).catch(()=>null);
        const rev  = rdet ? rdet.total : 0;   // same figure as the Revenue card: cash received excluding VAT
        const exp  = exps.reduce((s,e)=>s+((e.amount||0)-(e.vat_amount||0)),0);
        const poCogs = (allPOs||[])
          .filter(po=>["received","partial","paid"].includes(po.status))
          .reduce((s,po)=>s+((po.total_amount||0)-(po.vat_amount||0)),0);
        const depr = (deprSchedule||[]).reduce((s,d)=>s+(d.amount||0),0);
        // audit fix 2026-09-16 — see [[project_nbcpss_208hr_bug]]
        const payrollCost = (emps||[]).filter(e=>e.is_active!==false)
          .reduce((s,e)=>s+calcPayroll(e.gross_salary, null, 0,0,0,null, 0,0,0,0,0,0,0, {isSecurity: !!e.security_grade}).totalCost,0);
        const netProfit = rev - exp - poCogs - depr - payrollCost;
        setDrill({type, title:"Net Profit Breakdown — All Time", color:C.accent,
          total: netProfit,
          cols:["Category","Amount"],
          rows:[
            ["Revenue (cash received, excl. VAT)", fmt(rev)],
            ["Expenses (excl. VAT)",             `- ${fmt(exp)}`],
            ["Cost of Sales — POs (excl. VAT)",  `- ${fmt(poCogs)}`],
            ["Depreciation (IAS 16)",            `- ${fmt(depr)}`],
            ["Payroll (employer total cost)",    `- ${fmt(payrollCost)}`],
            ["Net Profit",                        fmt(netProfit)],
          ]});
      }
    } catch(e) { setDrill(null); }
    setDrillLoading(false);
  };

  // Build expense pie from live expenses grouped by category, or fall back to placeholder
  const PIE_COLORS = [C.red, C.blue, C.green, C.accent, C.gold, "#8b5cf6", "#06b6d4", "#f97316"];
  const expensePieData = (() => {
    const liveExps = live.expenses;
    if (!liveExps || liveExps.length === 0) return EXPENSE_PIE;
    const grouped = {};
    for (const e of liveExps) {
      const key = e.category || "Other";
      grouped[key] = (grouped[key] || 0) + ((e.amount || 0) - (e.vat_amount || 0));
    }
    const entries = Object.entries(grouped).filter(([,v]) => v > 0).sort((a,b) => b[1]-a[1]);
    if (entries.length === 0) return EXPENSE_PIE;
    return entries.slice(0, 8).map(([name, value], i) => ({name, value, color: PIE_COLORS[i % PIE_COLORS.length]}));
  })();

  // Smart Y-axis formatter: avoids "R0.001k" for small values
  const fmtAxis = v => {
    if (v >= 1000000) return `R${(v/1000000).toFixed(1)}m`;
    if (v >= 1000)    return `R${(v/1000).toFixed(0)}k`;
    return `R${Math.round(v)}`;
  };

  // Use live data if available, otherwise fall back to mock
  const totalRevenue  = d ? d.total_revenue  : MOCK_INVOICES.filter(i => i.status === "paid").reduce((s,i) => s + i.amount, 0);
  const totalExpenses = d ? d.total_expenses : MOCK_EXPENSES.reduce((s,e) => s + e.amount, 0);
  const outstanding   = d ? d.total_outstanding : MOCK_INVOICES.filter(i => i.status !== "paid").reduce((s,i) => s + i.amount, 0);
  const profit        = d ? Math.round((d.net_profit - (d.tax_provision||0))*100)/100 : totalRevenue - totalExpenses;
  const totalPayroll  = d ? d.total_payroll  : MOCK_EMPLOYEES.reduce((s,e) => s + calcPayroll(e.salary).totalCost, 0);
  const netVatPayable = d ? d.net_vat_payable : null;
  const vatColor      = netVatPayable === null ? C.inkMid : netVatPayable >= 0 ? C.red : C.green;
  const vatSub        = netVatPayable === null ? "Output minus input VAT" : netVatPayable >= 0 ? "Payable to SARS" : "Refund due from SARS";
  return (
    <div>
      <CIPCBanner cipc={d?.cipc} poDupWarning={d?.po_duplicate_warning}/>
      <div style={{marginBottom:24,display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
        <div>
          <h1 style={{fontFamily:"serif",fontSize:28,color:C.ink,margin:"0 0 4px"}}>{(()=>{const h=new Date().getHours();return h<12?"Good morning":h<17?"Good afternoon":"Good evening";})()}</h1>
          <p style={{color:C.inkMid,fontSize:13}}>Financial overview - {d ? d.period : new Date().toLocaleDateString("en-ZA",{month:"long",year:"numeric"})}</p>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:6,padding:"6px 14px",borderRadius:20,background:isLive?C.greenLt:C.goldLt,border:`1px solid ${isLive?C.green:C.gold}`}}>
          <div style={{width:6,height:6,borderRadius:"50%",background:isLive?C.green:C.gold}}/>
          <span style={{fontSize:11,fontWeight:600,color:isLive?C.green:C.gold}}>{isLive?"Live Data":"Demo Mode"}</span>
        </div>
      </div>
      <div style={{display:"flex",gap:12,marginBottom:20,flexWrap:"wrap"}}>
        <KPI label="Revenue" value={fmt(totalRevenue)} sub="All paid invoices" color={C.green} icon="💰" onClick={()=>openDrill("revenue")}/>
        <KPI label="Expenses" value={fmt(totalExpenses)} sub="Excl. VAT, all time" color={C.red} icon="📤" onClick={()=>openDrill("expenses")}/>
        <KPI label="Net Profit" value={fmt(profit)} sub="After all costs &amp; 27% tax" color={C.accent} icon="📊" onClick={()=>openDrill("profit")}/>
        <KPI label="Outstanding" value={fmt(outstanding)} sub="Pending invoices" color={C.gold} icon="⏳" onClick={()=>openDrill("outstanding")}/>
        <KPI label="Payroll" value={fmt(totalPayroll)} sub={`${d?.employee_count||""} employees`} color={C.blue} icon="👥"/>
        <KPI label="Net VAT" value={netVatPayable !== null ? fmt(Math.abs(netVatPayable)) : "—"} sub={vatSub} color={vatColor} icon="🏛️" onClick={()=>openDrill("vat")}/>
      </div>

      {/* ── KPI Drill-down Modal ── */}
      {(drill || drillLoading) && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:900,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}
          onClick={e=>{if(e.target===e.currentTarget){setDrill(null);setDrillLoading(false);}}}>
          <div style={{background:C.surface,borderRadius:20,width:"100%",maxWidth:780,maxHeight:"80vh",display:"flex",flexDirection:"column",boxShadow:"0 8px 40px rgba(0,0,0,0.22)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"20px 24px 12px",borderBottom:`1px solid ${C.border}`}}>
              <div>
                <div style={{fontSize:15,fontWeight:800,color:C.ink}}>{drill?.title||"Loading…"}</div>
                {drill?.total !== undefined && <div style={{fontSize:12,color:drill.color,fontWeight:700,marginTop:2}}>Total: {fmt(drill.total)}</div>}
              </div>
              <button onClick={()=>{setDrill(null);setDrillLoading(false);}} style={{background:"none",border:"none",fontSize:20,cursor:"pointer",color:C.inkMid}}>✕</button>
            </div>
            <div style={{overflowY:"auto",padding:"0 24px 24px"}}>
              {drillLoading ? (
                <div style={{textAlign:"center",padding:40,color:C.inkMid,fontSize:13}}>Loading…</div>
              ) : drill?.rows?.length === 0 ? (
                <div style={{textAlign:"center",padding:40,color:C.inkMid,fontSize:13}}>No records found for this period.</div>
              ) : (
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:13,marginTop:12}}>
                  <thead><tr>{drill?.cols?.map(c=><th key={c} style={{textAlign:"left",padding:"8px 10px",color:C.inkMid,fontWeight:600,fontSize:11,textTransform:"uppercase",borderBottom:`1px solid ${C.border}`,whiteSpace:"nowrap"}}>{c}</th>)}</tr></thead>
                  <tbody>{drill?.rows?.map((row,ri)=>(
                    <tr key={ri} style={{borderBottom:`1px solid ${C.border}`}}>
                      {row.map((cell,ci)=><td key={ci} style={{padding:"10px 10px",color:ci===0?C.ink:C.inkMid,fontWeight:ci===0?600:400}}>{cell}</td>)}
                    </tr>
                  ))}</tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      <div style={{display:"grid",gridTemplateColumns:"2fr 1fr",gap:16,marginBottom:20}}>
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,padding:20}}>
          <div style={{fontSize:14,fontWeight:700,color:C.ink,marginBottom:16}}>Revenue vs Expenses - Last 6 months</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={revenueData} barGap={4}>
              <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false}/>
              <XAxis dataKey="month" tick={{fontSize:10,fill:C.inkMid}} tickLine={false} axisLine={false}/>
              <YAxis tick={{fontSize:10,fill:C.inkMid}} tickLine={false} axisLine={false} tickFormatter={fmtAxis} width={45}/>
              <Tooltip content={<Tooltip2/>}/>
              <Bar dataKey="revenue" name="Revenue" fill={C.green} radius={[4,4,0,0]}/>
              <Bar dataKey="expenses" name="Expenses" fill={C.accent} radius={[4,4,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,padding:20}}>
          <div style={{fontSize:14,fontWeight:700,color:C.ink,marginBottom:16}}>Expense Breakdown</div>
          <ResponsiveContainer width="100%" height={160}>
            <PieChart>
              <Pie data={expensePieData} dataKey="value" cx="50%" cy="50%" innerRadius={45} outerRadius={70}>
                {expensePieData.map((e,i) => <Cell key={i} fill={e.color}/>)}
              </Pie>
              <Tooltip formatter={v => fmt(v)}/>
            </PieChart>
          </ResponsiveContainer>
          {expensePieData.map((e,i) => (
            <div key={i} style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
              <div style={{display:"flex",gap:6,alignItems:"center"}}>
                <div style={{width:8,height:8,borderRadius:"50%",background:e.color}}/>
                <span style={{fontSize:10,color:C.inkMid}}>{e.name}</span>
              </div>
              <span style={{fontSize:10,fontWeight:600}}>{fmt(e.value)}</span>
            </div>
          ))}
        </div>
      </div>
      <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,padding:20,marginBottom:20}}>
        <div style={{fontSize:14,fontWeight:700,color:C.ink,marginBottom:4}}>Gross Profit Trend</div>
        <div style={{fontSize:11,color:C.inkMid,marginBottom:12}}>Revenue minus expenses (excl. payroll)</div>
        <ResponsiveContainer width="100%" height={130}>
          <AreaChart data={revenueData}>
            <defs>
              <linearGradient id="pg" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={C.green} stopOpacity={0.2}/>
                <stop offset="100%" stopColor={C.green} stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false}/>
            <XAxis dataKey="month" tick={{fontSize:10,fill:C.inkMid}} tickLine={false} axisLine={false}/>
            <YAxis tick={{fontSize:10,fill:C.inkMid}} tickLine={false} axisLine={false} tickFormatter={fmtAxis} width={45}/>
            <Tooltip content={<Tooltip2/>}/>
            <Area type="monotone" dataKey="gross_profit" name="Gross Profit" stroke={C.green} strokeWidth={2} fill="url(#pg)" dot={false}/>
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,overflow:"hidden"}}>
        <div style={{padding:"16px 20px",borderBottom:`1px solid ${C.border}`,fontWeight:700,fontSize:14,color:C.ink}}>Recent Invoices</div>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
          <thead>
            <tr style={{background:C.bg,borderBottom:`1px solid ${C.border}`}}>
              {["Invoice","Client","Amount","Due","Status"].map(h => <th key={h} style={{textAlign:"left",padding:"10px 16px",fontSize:10,color:C.inkMid,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase"}}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {recentInvoicesFrom(live,8).map((inv,i) => (
              <tr key={inv.id} style={{borderBottom:`1px solid ${C.border}30`}}>
                <td style={{padding:"12px 16px",fontWeight:700,color:C.accent}}>{inv.id}</td>
                <td style={{padding:"12px 16px"}}>{inv.client}</td>
                <td style={{padding:"12px 16px",fontWeight:700}}>{fmt(inv.amount)}</td>
                <td style={{padding:"12px 16px",color:C.inkMid}}>{fmtDate(inv.due)}</td>
                <td style={{padding:"12px 16px"}}><StatusBadge status={inv.status}/></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Defined outside Invoicing so it never remounts on parent re-render (fixes cursor-jump bug)
function InvFormFields({data, onChange, customers=[]}) {
  const [catalogItems, setCatalogItems] = useState([]);
  useEffect(() => {
    api("/service-items/").then(items => { if (Array.isArray(items)) setCatalogItems(items.filter(i => i.is_active)); }).catch(() => {});
  }, []);

  // Multi-line items state
  const EMPTY_LINE = {code:"", description:"", quantity:1, unit_price:0, vat_applicable:true};
  const [useLineItems, setUseLineItems] = useState(() => !!(data.items_json && data.items_json.length > 2));
  const [lineItems, setLineItems] = useState(() => {
    if (data.items_json) { try { return JSON.parse(data.items_json); } catch(_) {} }
    return [{ ...EMPTY_LINE }];
  });

  // Sync line items → parent data.items_json + auto-compute amount
  const syncLines = (lines) => {
    const enriched = lines.map(l => {
      const qty = +l.quantity || 0;
      const up  = +l.unit_price || 0;
      const vat = l.vat_applicable ? Math.round(qty * up * 0.15 * 100) / 100 : 0;
      return { ...l, quantity: qty, unit_price: up, vat_amount: vat, total: Math.round((qty*up + vat)*100)/100 };
    });
    const subtotal = enriched.reduce((s,l) => s + (l.unit_price * l.quantity), 0);
    setLineItems(enriched);
    onChange(d => ({
      ...d,
      items_json: JSON.stringify(enriched),
      amount: Math.round(subtotal * 100) / 100,
    }));
  };

  const is = {width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"};
  const lb = (txt) => <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>{txt}</label>;
  return (
    <>
      {catalogItems.length > 0 && (
        <div style={{marginBottom:12}}>
          <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Pick from Catalog</label>
          <select value="" onChange={e => {
            const item = catalogItems.find(i => String(i.id) === e.target.value);
            if (!item) return;
            onChange(d => ({...d, desc: item.description || item.name, amount: item.unit_price, vatApplicable: item.vat_applicable}));
          }} style={{...is, background: C.goldLt, color: C.gold, fontWeight: 600}}>
            <option value="">— Select a service to auto-fill —</option>
            {catalogItems.map(i => <option key={i.id} value={i.id}>[{i.code}] {i.name} — R{Number(i.unit_price).toFixed(2)} / {i.unit}</option>)}
          </select>
        </div>
      )}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
        <div>
          {lb("Client")}
          {customers.length>0 && (
            <select value="" onChange={e=>{if(e.target.value) onChange(d=>({...d,client:e.target.value}));}} style={{...is,marginBottom:6}}>
              <option value="">— Select from customers —</option>
              {customers.map(c=><option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          )}
          <input placeholder="Or type client name" value={data.client||""} onChange={e=>onChange(d=>({...d,client:e.target.value}))} style={is}/>
        </div>
        <div>{lb("Amount (excl. VAT)")}<input type="number" placeholder="50000" value={data.amount||""} onChange={e=>onChange(d=>({...d,amount:e.target.value}))} style={is}/></div>
        <div style={{gridColumn:"1 / -1"}}>
          {lb("Description / Invoice Body")}
          <textarea
            placeholder={"Type anything — services rendered, itemised list, free text...\n\nExample:\nCar Hire Extras — 16–19 Aug\nAirport transfers x2\nTravel insurance"}
            value={data.desc||""}
            onChange={e=>onChange(d=>({...d,desc:e.target.value}))}
            style={{...is, minHeight:90, resize:"vertical", lineHeight:1.5}}
          />
        </div>
        <div>{lb("Due Date")}<input type="date" value={data.due||""} onChange={e=>onChange(d=>({...d,due:e.target.value}))} style={is}/></div>
        <div>
          {lb("Currency")}
          <select value={data.currency||"ZAR"} onChange={e=>onChange(d=>({...d,currency:e.target.value}))} style={is}>
            <option value="ZAR">ZAR — South African Rand</option>
            <option value="USD">USD — US Dollar</option>
            <option value="EUR">EUR — Euro</option>
            <option value="GBP">GBP — British Pound</option>
            <option value="BWP">BWP — Botswana Pula</option>
            <option value="ZMW">ZMW — Zambian Kwacha</option>
            <option value="MZN">MZN — Mozambican Metical</option>
            <option value="NAD">NAD — Namibian Dollar</option>
            <option value="KES">KES — Kenyan Shilling</option>
            <option value="CNY">CNY — Chinese Yuan</option>
          </select>
        </div>
        {data.currency && data.currency!=="ZAR" && (
          <div>{lb(`Exchange Rate (1 ${data.currency} = R)`)}<input type="number" value={data.exchangeRate||"18.5"} onChange={e=>onChange(d=>({...d,exchangeRate:e.target.value}))} style={is}/></div>
        )}
      </div>
      {/* VAT — auto 15% for ZAR, manual amount for foreign currency */}
      {(!data.currency || data.currency === "ZAR") ? (
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
          <label style={{display:"flex",alignItems:"center",gap:8,fontSize:13,color:C.ink}}>
            VAT treatment
            <select value={data.supplyType || (data.vatApplicable !== false ? "standard" : "zero_rated")} onChange={e=>{const v=e.target.value; onChange(d=>({...d,supplyType:v,vatApplicable:v==="standard"}));}} style={{padding:"6px 8px",border:"1px solid "+C.border,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink}}>
              <option value="standard">Standard rate (15%)</option>
              <option value="zero_rated">Zero-rated (0%)</option>
              <option value="exempt">Exempt (no VAT)</option>
            </select>
          </label>
        </div>
      ) : (
        <div style={{marginBottom:12}}>
          <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>VAT Amount ({data.currency}) — enter 0 if not applicable</label>
          <input type="number" min="0" placeholder="0" value={data.vatAmount||"0"} onChange={e=>onChange(d=>({...d,vatAmount:e.target.value}))} style={{...is,width:"180px"}}/>
        </div>
      )}
      {data.amount && (
        <div style={{display:"flex",gap:16,fontSize:12,marginBottom:12,flexWrap:"wrap",background:C.bg,padding:"10px 14px",borderRadius:8}}>
          <span style={{color:C.inkMid}}>Excl. VAT: <strong>{fmtCurrency(+data.amount||0,data.currency||"ZAR")}</strong></span>
          {(!data.currency||data.currency==="ZAR")
            ? (data.vatApplicable !== false && <span style={{color:C.inkMid}}>VAT (15%): <strong style={{color:C.gold}}>{fmtCurrency((+data.amount||0)*0.15,"ZAR")}</strong></span>)
            : (+data.vatAmount > 0 && <span style={{color:C.inkMid}}>VAT: <strong style={{color:C.gold}}>{fmtCurrency(+data.vatAmount,data.currency)}</strong></span>)
          }
          <span style={{color:C.inkMid}}>Total: <strong style={{color:C.green}}>{fmtCurrency(
            (!data.currency||data.currency==="ZAR")
              ? (+data.amount||0)*(data.vatApplicable!==false?1.15:1)
              : (+data.amount||0)+(+data.vatAmount||0),
            data.currency||"ZAR"
          )}</strong></span>
          {data.currency&&data.currency!=="ZAR" && <span style={{color:C.blue}}>≈ {fmt(((+data.amount||0)+(+data.vatAmount||0))*(+data.exchangeRate||18.5))} ZAR</span>}
        </div>
      )}

      {/* ── REFERENCE FIELDS ──────────────────────────────────────────── */}
      <div style={{marginTop:8,padding:"12px 14px",background:C.bg,borderRadius:8,border:`1px solid ${C.border}`,marginBottom:12}}>
        <div style={{fontSize:11,fontWeight:700,color:C.inkMid,textTransform:"uppercase",letterSpacing:0.5,marginBottom:10}}>Travel / Reference Details</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:8}}>
          <div><label style={{fontSize:11,color:C.inkMid,display:"block",marginBottom:4}}>Passenger Name</label>
            <input value={data.passenger_name||""} onChange={e=>onChange(d=>({...d,passenger_name:e.target.value}))} placeholder="e.g. J. Smith" style={is}/></div>
          <div><label style={{fontSize:11,color:C.inkMid,display:"block",marginBottom:4}}>Tour Ref</label>
            <input value={data.tour_ref||""} onChange={e=>onChange(d=>({...d,tour_ref:e.target.value}))} placeholder="e.g. T-2024-001" style={is}/></div>
          <div><label style={{fontSize:11,color:C.inkMid,display:"block",marginBottom:4}}>Quote Ref</label>
            <input value={data.quote_ref||""} onChange={e=>onChange(d=>({...d,quote_ref:e.target.value}))} placeholder="e.g. Q-2024-001" style={is}/></div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
          <div><label style={{fontSize:11,color:C.inkMid,display:"block",marginBottom:4}}>Tax Ref</label>
            <input value={data.tax_ref||""} onChange={e=>onChange(d=>({...d,tax_ref:e.target.value}))} placeholder="SARS tax ref" style={is}/></div>
          <div><label style={{fontSize:11,color:C.inkMid,display:"block",marginBottom:4}}>Travel Date</label>
            <input type="date" value={data.travel_date||""} onChange={e=>onChange(d=>({...d,travel_date:e.target.value}))} style={is}/></div>
          <div><label style={{fontSize:11,color:C.inkMid,display:"block",marginBottom:4}}>Pax Count</label>
            <input type="number" min="1" value={data.pax_count||""} onChange={e=>onChange(d=>({...d,pax_count:e.target.value}))} placeholder="1" style={is}/></div>
        </div>
      </div>

      {/* ── LINE ITEMS ────────────────────────────────────────────────── */}
      <div style={{marginBottom:12}}>
        <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",fontSize:13,color:C.ink,marginBottom:8}}>
          <input type="checkbox" checked={useLineItems} onChange={e=>{
            setUseLineItems(e.target.checked);
            if (!e.target.checked) onChange(d=>({...d, items_json:null}));
            else syncLines(lineItems);
          }} style={{width:16,height:16,cursor:"pointer"}}/>
          Use line-item breakdown (code / description / qty / unit price)
        </label>
        {useLineItems && (
          <div style={{border:`1px solid ${C.border}`,borderRadius:8,overflow:"hidden"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
              <thead>
                <tr style={{background:C.bg}}>
                  {["Code","Description","Qty","Unit Price","VAT?","Total",""].map(h=>(
                    <th key={h} style={{padding:"7px 8px",fontWeight:600,color:C.inkMid,textAlign:h==="Total"||h==="Qty"||h==="Unit Price"?"right":"left",borderBottom:`1px solid ${C.border}`,fontSize:11}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lineItems.map((row, idx) => (
                  <tr key={idx} style={{borderBottom:`1px solid ${C.border}`}}>
                    <td style={{padding:"4px 6px",width:80}}>
                      <input value={row.code||""} onChange={e=>{const r=[...lineItems];r[idx]={...r[idx],code:e.target.value};syncLines(r);}} placeholder="SVC01" style={{...is,padding:"5px 7px",fontSize:12}}/>
                    </td>
                    <td style={{padding:"4px 6px"}}>
                      <input value={row.description||""} onChange={e=>{const r=[...lineItems];r[idx]={...r[idx],description:e.target.value};syncLines(r);}} placeholder="Description" style={{...is,padding:"5px 7px",fontSize:12}}/>
                    </td>
                    <td style={{padding:"4px 6px",width:60}}>
                      <input type="number" min="0" step="0.01" value={row.quantity||1} onChange={e=>{const r=[...lineItems];r[idx]={...r[idx],quantity:e.target.value};syncLines(r);}} style={{...is,padding:"5px 7px",fontSize:12,textAlign:"right"}}/>
                    </td>
                    <td style={{padding:"4px 6px",width:100}}>
                      <input type="number" min="0" step="0.01" value={row.unit_price||0} onChange={e=>{const r=[...lineItems];r[idx]={...r[idx],unit_price:e.target.value};syncLines(r);}} style={{...is,padding:"5px 7px",fontSize:12,textAlign:"right"}}/>
                    </td>
                    <td style={{padding:"4px 8px",textAlign:"center",width:40}}>
                      <input type="checkbox" checked={row.vat_applicable!==false} onChange={e=>{const r=[...lineItems];r[idx]={...r[idx],vat_applicable:e.target.checked};syncLines(r);}} style={{cursor:"pointer"}}/>
                    </td>
                    <td style={{padding:"4px 8px",textAlign:"right",width:80,fontWeight:600,color:C.ink}}>
                      {(((+row.quantity||0)*(+row.unit_price||0))*(row.vat_applicable!==false?1.15:1)).toFixed(2)}
                    </td>
                    <td style={{padding:"4px 6px",width:30,textAlign:"center"}}>
                      {lineItems.length > 1 && <button onClick={()=>{const r=lineItems.filter((_,i)=>i!==idx);syncLines(r);}} style={{background:"none",border:"none",color:C.red,cursor:"pointer",fontSize:16,lineHeight:1,padding:2}}>×</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{padding:"8px 10px",background:C.bg,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <button onClick={()=>{const r=[...lineItems,{...EMPTY_LINE}];syncLines(r);}} style={{padding:"6px 14px",background:C.accentLt,border:`1px solid ${C.accent}40`,borderRadius:6,color:C.accent,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>+ Add Line</button>
              <div style={{fontSize:12,color:C.inkMid}}>
                Subtotal: <strong>{lineItems.reduce((s,l)=>(s+(+l.quantity||0)*(+l.unit_price||0)),0).toFixed(2)}</strong>
              </div>
            </div>
            <div style={{fontSize:11,color:C.inkMid,marginTop:6}}>Stock: type an inventory item's SKU as the line Code and the invoice reduces that item's stock and posts its cost of sales automatically.</div>
          </div>
        )}
      </div>
    </>
  );
}

// ── CUSTOM HTML TEMPLATE RENDERER ────────────────────────────────────────────
// Replaces {{variable}} placeholders with live invoice data.
// Free text is escaped before it goes into a custom HTML template, and the finished HTML is stripped of anything
// active (scripts, frames, on* handlers, javascript: links) before it is shown: an imported or typed client name
// like <img src=x onerror=...> must never run in the owner's session.
const escHtml = v => String(v).replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;").replace(/'/g,"&#39;");
function sanitizeHtml(html) {
  try {
    const doc = new DOMParser().parseFromString(String(html || ""), "text/html");
    doc.querySelectorAll("script,iframe,frame,frameset,object,embed,applet,link,meta,base,form,noscript,template").forEach(n => n.remove());
    doc.querySelectorAll("*").forEach(el => {
      Array.from(el.attributes).forEach(a => {
        const n = a.name.toLowerCase(), v = (a.value || "").replace(/[\s\u0000-\u001f]+/g, "").toLowerCase();
        if (n.startsWith("on") || n === "srcdoc" || n === "formaction" ||
            (["href","src","xlink:href","action","poster","background"].includes(n) && /^(javascript|vbscript|data:text\/html)/.test(v))) el.removeAttribute(a.name);
      });
    });
    return doc.body ? doc.body.innerHTML : "";
  } catch (e) { return ""; }
}

function renderTemplate(html, doc, user) {
  const fmt = v => v !== undefined && v !== null ? escHtml(v) : "";
  const fmtAmt = v => isNaN(Number(v)) ? "0.00" : Number(v).toFixed(2);
  const fmtDate = v => v ? (v.includes("T") ? v.substring(0,10) : v) : "";

  // Build items table HTML if doc has items_json
  let itemsTableHtml = "";
  try {
    const items = doc.items_json ? JSON.parse(doc.items_json) : null;
    if (items && items.length) {
      const rows = items.map(it => `
        <tr>
          <td>${fmt(it.code)}</td>
          <td>${fmt(it.description)}</td>
          <td style="text-align:right">${fmt(it.quantity)}</td>
          <td style="text-align:right">${fmtAmt(it.unit_price)}</td>
          <td style="text-align:right">${fmtAmt(it.vat_amount)}</td>
          <td style="text-align:right">${fmtAmt(it.total)}</td>
        </tr>`).join("");
      itemsTableHtml = `<table border="1" cellpadding="5" cellspacing="0" style="width:100%;border-collapse:collapse">
        <thead><tr>
          <th>Code</th><th>Description</th><th>Qty</th><th>Unit Price</th><th>VAT</th><th>Total</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>`;
    }
  } catch(_) {}

  const vars = {
    company_name:        fmt(user.companyName),
    company_address:     fmt(user.address || user.companyAddress),
    company_vat:         fmt(user.vatNumber || user.vat_number),
    company_reg:         fmt(user.regNumber || user.reg_number),
    company_phone:       fmt(user.phone || user.companyPhone),
    company_email:       fmt(user.email || user.companyEmail),
    company_bank_name:   fmt(user.bankName),
    company_bank_account:fmt(user.bankAccount),
    company_bank_branch: fmt(user.branchCode),
    invoice_number:      fmt(doc.invoice_number),
    issue_date:          fmtDate(doc.issue_date || doc.created_at),
    due_date:            fmtDate(doc.due_date),
    client_name:         fmt(doc.client_name),
    client_email:        fmt(doc.client_email),
    tour_ref:            fmt(doc.tour_ref),
    quote_ref:           fmt(doc.quote_ref),
    tax_ref:             fmt(doc.tax_ref),
    travel_date:         fmt(doc.travel_date),
    pax_count:           fmt(doc.pax_count),
    passenger_name:      fmt(doc.passenger_name),
    subtotal:            fmtAmt(doc.amount),
    vat_amount:          fmtAmt(doc.vat_amount),
    total:               fmtAmt(doc.total_amount),
    description:         fmt(doc.description).replace(/\n/g, "<br>"),
    notes:               fmt(doc.notes).replace(/\n/g, "<br>"),
    items_table:         itemsTableHtml,
  };
  let out = html;
  Object.entries(vars).forEach(([k,v]) => {
    out = out.split(`{{${k}}}`).join(v);
  });
  return out;
}

// Starter template for the "Download" link in Settings → Templates
const SOLID_MATTER_HTML_TEMPLATE = `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<title>Invoice {{invoice_number}}</title>
<style>
  body { font-family: Arial, sans-serif; margin: 0; padding: 0; color: #222; }
  .header { background: #1a3a5c; color: #fff; padding: 24px 32px; display: flex; justify-content: space-between; align-items: center; }
  .header-left h1 { margin: 0; font-size: 22px; }
  .header-right { text-align: right; font-size: 13px; }
  .ref-bar { background: #f0f4f8; padding: 10px 32px; display: grid; grid-template-columns: repeat(6,1fr); gap: 8px; font-size: 12px; }
  .ref-bar div span { display: block; font-weight: 700; color: #1a3a5c; }
  .section { padding: 20px 32px; }
  h2 { font-size: 14px; color: #1a3a5c; border-bottom: 2px solid #1a3a5c; padding-bottom: 4px; margin-bottom: 10px; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; }
  th { background: #1a3a5c; color: #fff; padding: 8px; text-align: left; }
  td { padding: 7px 8px; border-bottom: 1px solid #e8ecf0; }
  .totals { text-align: right; padding: 8px 32px; }
  .totals table { width: 260px; margin-left: auto; }
  .totals td { padding: 4px 8px; }
  .totals .grand { font-weight: 800; font-size: 15px; color: #1a3a5c; }
  .footer { background: #1a3a5c; color: #fff; padding: 16px 32px; font-size: 12px; margin-top: 24px; }
</style>
</head>
<body>
<div class="header">
  <div class="header-left">
    <h1>{{company_name}}</h1>
    <div style="font-size:12px;margin-top:4px">{{company_address}}</div>
    <div style="font-size:12px">VAT: {{company_vat}} | Reg: {{company_reg}}</div>
    <div style="font-size:12px">{{company_phone}} | {{company_email}}</div>
  </div>
  <div class="header-right">
    <div style="font-size:20px;font-weight:800">TAX INVOICE</div>
    <div style="margin-top:6px"><strong>{{invoice_number}}</strong></div>
    <div>Date: {{issue_date}}</div>
    <div>Due: {{due_date}}</div>
  </div>
</div>
<div class="ref-bar">
  <div>Passenger<span>{{passenger_name}}</span></div>
  <div>Tour Ref<span>{{tour_ref}}</span></div>
  <div>Quote Ref<span>{{quote_ref}}</span></div>
  <div>Tax Ref<span>{{tax_ref}}</span></div>
  <div>Travel Date<span>{{travel_date}}</span></div>
  <div>Pax<span>{{pax_count}}</span></div>
</div>
<div class="section">
  <h2>Bill To</h2>
  <div><strong>{{client_name}}</strong></div>
  <div>{{client_email}}</div>
</div>
<div class="section">
  <h2>Services / Items</h2>
  {{items_table}}
  <div style="font-size:13px;line-height:1.7;white-space:pre-wrap;margin-top:6px">{{description}}</div>
</div>
<div class="totals">
  <table>
    <tr><td>Subtotal</td><td>{{subtotal}}</td></tr>
    <tr><td>VAT (15%)</td><td>{{vat_amount}}</td></tr>
    <tr class="grand"><td><strong>TOTAL</strong></td><td><strong>{{total}}</strong></td></tr>
  </table>
</div>
<div class="section">
  <h2>Banking Details</h2>
  <table style="width:auto">
    <tr><td><strong>Bank:</strong></td><td>{{company_bank_name}}</td></tr>
    <tr><td><strong>Account:</strong></td><td>{{company_bank_account}}</td></tr>
    <tr><td><strong>Branch:</strong></td><td>{{company_bank_branch}}</td></tr>
  </table>
</div>
<div class="footer">
  <div>{{notes}}</div>
  <div style="margin-top:8px">Thank you for your business.</div>
</div>
</body>
</html>`;

// ── DOCUMENT RENDERER ────────────────────────────────────────────────────────
// Renders invoice or quote in the chosen template layout.
// type: "invoice" | "quote"
// doc:  the invoice/quote object
// user: company info
// tmpl: document template config
function InvoiceDocument({type, doc, user, tmpl = DEFAULT_DOC_TEMPLATE}) {
  // If company has a custom HTML template, use it instead of built-in layouts
  if (user.invoiceTemplateHtml && type === "invoice") {
    const rendered = renderTemplate(user.invoiceTemplateHtml, doc, user);
    return <div dangerouslySetInnerHTML={{__html: sanitizeHtml(rendered)}} style={{fontFamily:"Arial,sans-serif"}}/>;
  }
  const t = {...DEFAULT_DOC_TEMPLATE, ...tmpl};
  const pc = t.primaryColor;
  const ff = t.fontFamily;
  const title = type === "quote" ? t.quoteTitle : t.invoiceTitle;
  const currency = doc.currency || "ZAR";
  const amount   = doc.amount || 0;
  const vatAmt   = doc.vatAmount || 0;
  const total    = doc.totalAmount || (amount + vatAmt) || amount;

  const logoBlock = user.logoUrl
    ? <img src={user.logoUrl} alt="logo" style={{height:56,maxWidth:180,objectFit:"contain",display:"block",marginBottom:2}}/>
    : <div style={{fontFamily:"serif",fontSize:22,fontWeight:800,color:pc}}>{user.companyName||"Your Company"}</div>;

  // Company details shown beneath logo/name on all built-in layouts
  const _addr  = user.address || user.companyAddress || "";
  const _vat   = user.vatNumber || user.vat_number || "";
  const _reg   = user.regNumber || user.reg_number || "";
  const _phone = user.phone || user.companyPhone || "";
  const _email = user.email || user.companyEmail || "";
  const companyDetailsLight = (_addr||_vat||_reg||_phone||_email) ? (
    <div style={{fontSize:10,color:"#666",marginTop:5,lineHeight:1.65,fontFamily:ff}}>
      {_addr  && <div>{_addr}</div>}
      {_vat   && <div>VAT No: {_vat}</div>}
      {_reg   && <div>Reg: {_reg}</div>}
      {(_phone||_email) && <div>{[_phone,_email].filter(Boolean).join(" · ")}</div>}
    </div>
  ) : null;
  const companyDetailsWhite = (_addr||_vat||_reg||_phone||_email) ? (
    <div style={{fontSize:10,color:"rgba(255,255,255,0.75)",marginTop:5,lineHeight:1.65,fontFamily:ff}}>
      {_addr  && <div>{_addr}</div>}
      {_vat   && <div>VAT No: {_vat}</div>}
      {_reg   && <div>Reg: {_reg}</div>}
      {(_phone||_email) && <div>{[_phone,_email].filter(Boolean).join(" · ")}</div>}
    </div>
  ) : null;

  const headerImageBlock = user.headerImageUrl
    ? <div style={{width:"100%",marginBottom:0,lineHeight:0}}>
        <img src={user.headerImageUrl} alt="header" style={{width:"100%",maxHeight:140,objectFit:"cover",display:"block",borderRadius:"8px 8px 0 0"}}/>
      </div>
    : null;

  const tableHeader = (
    <thead>
      <tr style={{
        background: t.tableStyle==="shaded" ? "#f4f4f4" : "transparent",
        borderBottom: `2px solid ${t.tableStyle==="lines"||t.tableStyle==="shaded" ? "#ddd" : "transparent"}`,
      }}>
        <th style={{padding:"9px 12px",textAlign:"left",fontSize:11,color:"#555",fontFamily:ff}}>Description</th>
        <th style={{padding:"9px 12px",textAlign:"right",fontSize:11,color:"#555",fontFamily:ff}}>Amount</th>
      </tr>
    </thead>
  );

  const tableBody = (
    <tbody>
      <tr style={{borderBottom:t.tableStyle!=="clean"?"1px solid #eee":"none"}}>
        <td style={{padding:"12px",color:"#1A1209",fontFamily:ff}}>{doc.desc}</td>
        <td style={{padding:"12px",textAlign:"right",fontWeight:600,fontFamily:ff}}>{fmtCurrency(amount, currency)}</td>
      </tr>
      {vatAmt > 0 && <tr style={{borderBottom:t.tableStyle!=="clean"?"1px solid #eee":"none"}}>
        <td style={{padding:"12px",color:"#888",fontSize:12,fontFamily:ff}}>VAT</td>
        <td style={{padding:"12px",textAlign:"right",color:"#C4920A",fontWeight:600,fontFamily:ff}}>{fmtCurrency(vatAmt, currency)}</td>
      </tr>}
    </tbody>
  );

  const bankingBlock = (t.tableStyle !== "clean" || type === "invoice") && user.bankingDetails
    ? <div style={{background:"#f9f9f9",borderRadius:8,padding:"10px 14px",marginTop:16,fontSize:11,color:"#666",fontFamily:ff}}>
        {user.bankingDetails}
      </div>
    : null;

  const footerBlock = t.footerNote
    ? <div style={{marginTop:12,fontSize:11,color:"#888",fontFamily:ff,borderTop:"1px solid #eee",paddingTop:10}}>{t.footerNote}</div>
    : null;

  // ── CLASSIC ──
  if (t.layout === "classic") return (
    <div style={{fontFamily:ff,color:"#1A1209"}}>
      {headerImageBlock}
      <div style={{display:"flex",justifyContent:"space-between",marginBottom:28,paddingBottom:16,borderBottom:`2px solid ${pc}`,marginTop:headerImageBlock?16:0}}>
        <div>{logoBlock}{user.logoUrl&&<div style={{fontSize:12,fontWeight:700,marginTop:2}}>{user.companyName||""}</div>}{companyDetailsLight}</div>
        <div style={{textAlign:"right"}}>
          <div style={{fontSize:20,fontWeight:800,color:"#1A1209"}}>{title}</div>
          <div style={{fontSize:13,color:"#888",marginTop:4}}>{doc.id}</div>
          {type==="quote"&&doc.validUntil&&<div style={{fontSize:12,color:"#888"}}>Valid until: {fmtDate(doc.validUntil)}</div>}
        </div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20,marginBottom:24}}>
        <div>
          <div style={{fontSize:10,color:"#888",fontWeight:600,textTransform:"uppercase",marginBottom:6}}>Bill To</div>
          <div style={{fontWeight:700}}>{doc.client}</div>
        </div>
        <div style={{textAlign:"right"}}>
          <div style={{fontSize:12,color:"#888"}}>Date: {fmtDate(doc.date)}</div>
          {doc.due&&<div style={{fontSize:12,color:"#888"}}>Due: {fmtDate(doc.due)}</div>}
        </div>
      </div>
      <table style={{width:"100%",borderCollapse:"collapse",marginBottom:24}}>{tableHeader}{tableBody}</table>
      <div style={{borderTop:`2px solid #ddd`,paddingTop:16}}>
        <div style={{display:"flex",justifyContent:"space-between",padding:"8px 0"}}>
          <span style={{fontWeight:800,fontSize:15}}>TOTAL {type==="invoice"?"DUE":""}</span>
          <span style={{fontWeight:800,fontSize:18,color:pc}}>{fmtCurrency(total, currency)}</span>
        </div>
      </div>
      {type==="invoice"&&bankingBlock}
      {footerBlock}
    </div>
  );

  // ── MODERN ──
  if (t.layout === "modern") return (
    <div style={{fontFamily:ff,color:"#1A1209"}}>
      {headerImageBlock}
      <div style={{background:pc,borderRadius:headerImageBlock?"0":"8px 8px 0 0",padding:"24px 28px",marginBottom:24,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div>
          {user.logoUrl
            ? <div style={{background:"#fff",borderRadius:8,padding:"6px 10px",display:"inline-block"}}>
                <img src={user.logoUrl} alt="logo" style={{height:42,maxWidth:150,objectFit:"contain",display:"block"}}/>
              </div>
            : <div style={{fontSize:22,fontWeight:800,color:"#fff"}}>{user.companyName||"Your Company"}</div>}
          {user.logoUrl&&<div style={{fontSize:11,color:"rgba(255,255,255,0.85)",marginTop:6}}>{user.companyName}</div>}
          {companyDetailsWhite}
        </div>
        <div style={{textAlign:"right"}}>
          <div style={{fontSize:22,fontWeight:800,color:"#fff"}}>{title}</div>
          <div style={{fontSize:13,color:"rgba(255,255,255,0.85)",marginTop:4}}>{doc.id}</div>
          {type==="quote"&&doc.validUntil&&<div style={{fontSize:11,color:"rgba(255,255,255,0.75)"}}>Valid until: {fmtDate(doc.validUntil)}</div>}
        </div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20,marginBottom:24,padding:"0 4px"}}>
        <div>
          <div style={{fontSize:10,color:"#888",fontWeight:600,textTransform:"uppercase",marginBottom:6}}>Bill To</div>
          <div style={{fontWeight:700,fontSize:14}}>{doc.client}</div>
        </div>
        <div style={{textAlign:"right"}}>
          <div style={{fontSize:12,color:"#888"}}>Date: {fmtDate(doc.date)}</div>
          {doc.due&&<div style={{fontSize:12,color:"#888"}}>Due: {fmtDate(doc.due)}</div>}
        </div>
      </div>
      <table style={{width:"100%",borderCollapse:"collapse",marginBottom:24}}>
        <thead><tr style={{background:pc}}>
          <th style={{padding:"10px 12px",textAlign:"left",fontSize:11,color:"#fff",fontFamily:ff}}>Description</th>
          <th style={{padding:"10px 12px",textAlign:"right",fontSize:11,color:"#fff",fontFamily:ff}}>Amount</th>
        </tr></thead>
        {tableBody}
      </table>
      <div style={{background:pc+"18",border:`1px solid ${pc}30`,borderRadius:8,padding:"14px 18px",display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
        <span style={{fontWeight:800,fontSize:15}}>TOTAL {type==="invoice"?"DUE":""}</span>
        <span style={{fontWeight:800,fontSize:20,color:pc}}>{fmtCurrency(total, currency)}</span>
      </div>
      {type==="invoice"&&bankingBlock}
      {footerBlock}
    </div>
  );

  // ── MINIMAL ──
  if (t.layout === "minimal") return (
    <div style={{fontFamily:ff,color:"#1A1209"}}>
      {headerImageBlock}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:32,marginTop:headerImageBlock?16:0}}>
        <div>
          {user.logoUrl
            ? <img src={user.logoUrl} alt="logo" style={{height:40,maxWidth:140,objectFit:"contain",display:"block",marginBottom:4}}/>
            : <div style={{fontSize:16,fontWeight:700,letterSpacing:2,textTransform:"uppercase",color:"#1A1209"}}>{user.companyName||"Your Company"}</div>}
          {user.logoUrl&&<div style={{fontSize:11,color:"#888",marginTop:2,letterSpacing:1}}>{user.companyName}</div>}
          {companyDetailsLight}
        </div>
        <div style={{textAlign:"right"}}>
          <div style={{fontSize:11,fontWeight:700,letterSpacing:2,textTransform:"uppercase",color:pc}}>{title}</div>
          <div style={{fontSize:18,fontWeight:800,color:"#1A1209",marginTop:4}}>{doc.id}</div>
          {type==="quote"&&doc.validUntil&&<div style={{fontSize:11,color:"#888",marginTop:2}}>Valid: {fmtDate(doc.validUntil)}</div>}
        </div>
      </div>
      <div style={{borderTop:"1px solid #eee",paddingTop:16,marginBottom:20,display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
        <div>
          <div style={{fontSize:10,color:"#888",fontWeight:600,textTransform:"uppercase",letterSpacing:1,marginBottom:4}}>Bill To</div>
          <div style={{fontWeight:600}}>{doc.client}</div>
        </div>
        <div style={{textAlign:"right",fontSize:12,color:"#888"}}>
          <div>Date: {fmtDate(doc.date)}</div>
          {doc.due&&<div>Due: {fmtDate(doc.due)}</div>}
        </div>
      </div>
      <table style={{width:"100%",borderCollapse:"collapse",marginBottom:24}}>
        <thead><tr style={{borderBottom:"1px solid #ddd"}}>
          <th style={{padding:"8px 0",textAlign:"left",fontSize:10,color:"#888",letterSpacing:1,fontWeight:600,textTransform:"uppercase",fontFamily:ff}}>Description</th>
          <th style={{padding:"8px 0",textAlign:"right",fontSize:10,color:"#888",letterSpacing:1,fontWeight:600,textTransform:"uppercase",fontFamily:ff}}>Amount</th>
        </tr></thead>
        <tbody>
          <tr><td style={{padding:"12px 0",color:"#1A1209",fontFamily:ff}}>{doc.desc}</td><td style={{padding:"12px 0",textAlign:"right",fontWeight:600,fontFamily:ff}}>{fmtCurrency(amount,currency)}</td></tr>
          {vatAmt>0&&<tr style={{borderTop:"1px solid #f0f0f0"}}><td style={{padding:"8px 0",color:"#888",fontSize:12,fontFamily:ff}}>VAT</td><td style={{padding:"8px 0",textAlign:"right",fontFamily:ff}}>{fmtCurrency(vatAmt,currency)}</td></tr>}
        </tbody>
      </table>
      <div style={{borderTop:"1px solid #ddd",paddingTop:12,display:"flex",justifyContent:"space-between",alignItems:"baseline"}}>
        <span style={{fontSize:11,fontWeight:700,letterSpacing:2,textTransform:"uppercase",color:"#888"}}>Total {type==="invoice"?"Due":""}</span>
        <span style={{fontSize:22,fontWeight:800,color:pc}}>{fmtCurrency(total,currency)}</span>
      </div>
      {type==="invoice"&&user.bankingDetails&&<div style={{marginTop:16,fontSize:11,color:"#888",fontFamily:ff}}>{user.bankingDetails}</div>}
      {footerBlock}
    </div>
  );

  // ── BOLD ──
  return (
    <div style={{fontFamily:ff,color:"#1A1209"}}>
      {headerImageBlock}
      <div style={{background:"#1A1209",padding:"28px 32px",marginBottom:0,display:"flex",justifyContent:"space-between",alignItems:"flex-end"}}>
        <div>
          {user.logoUrl
            ? <div style={{background:"#fff",borderRadius:8,padding:"6px 10px",display:"inline-block"}}>
                <img src={user.logoUrl} alt="logo" style={{height:42,maxWidth:150,objectFit:"contain",display:"block"}}/>
              </div>
            : <div style={{fontSize:20,fontWeight:800,color:"#fff"}}>{user.companyName||"Your Company"}</div>}
          {companyDetailsWhite}
        </div>
        <div style={{textAlign:"right"}}>
          <div style={{fontSize:11,fontWeight:700,letterSpacing:3,color:pc,textTransform:"uppercase"}}>{title}</div>
          <div style={{fontSize:28,fontWeight:900,color:"#fff",lineHeight:1.1,marginTop:4}}>{doc.id}</div>
        </div>
      </div>
      <div style={{background:pc,padding:"12px 32px",display:"flex",justifyContent:"space-between",marginBottom:24}}>
        <div style={{fontSize:12,color:"rgba(255,255,255,0.9)"}}>Bill To: <strong style={{color:"#fff"}}>{doc.client}</strong></div>
        <div style={{fontSize:12,color:"rgba(255,255,255,0.9)"}}>Date: {fmtDate(doc.date)}{doc.due?` · Due: ${fmtDate(doc.due)}`:""}</div>
      </div>
      <table style={{width:"100%",borderCollapse:"collapse",marginBottom:24}}>
        <thead><tr style={{background:"#f5f5f5"}}>
          <th style={{padding:"11px 16px",textAlign:"left",fontSize:11,fontWeight:700,letterSpacing:1,textTransform:"uppercase",color:"#555",fontFamily:ff}}>Description</th>
          <th style={{padding:"11px 16px",textAlign:"right",fontSize:11,fontWeight:700,letterSpacing:1,textTransform:"uppercase",color:"#555",fontFamily:ff}}>Amount</th>
        </tr></thead>
        <tbody>
          <tr style={{borderBottom:"1px solid #eee"}}><td style={{padding:"14px 16px",fontFamily:ff}}>{doc.desc}</td><td style={{padding:"14px 16px",textAlign:"right",fontWeight:700,fontFamily:ff}}>{fmtCurrency(amount,currency)}</td></tr>
          {vatAmt>0&&<tr style={{borderBottom:"1px solid #eee"}}><td style={{padding:"10px 16px",color:"#888",fontSize:12,fontFamily:ff}}>VAT</td><td style={{padding:"10px 16px",textAlign:"right",fontFamily:ff}}>{fmtCurrency(vatAmt,currency)}</td></tr>}
        </tbody>
      </table>
      <div style={{background:"#1A1209",borderRadius:8,padding:"16px 20px",display:"flex",justifyContent:"space-between",alignItems:"center",margin:"0 0 16px"}}>
        <span style={{fontWeight:800,fontSize:14,color:"#fff",letterSpacing:1,textTransform:"uppercase"}}>Total {type==="invoice"?"Due":""}</span>
        <span style={{fontWeight:900,fontSize:22,color:pc}}>{fmtCurrency(total,currency)}</span>
      </div>
      {type==="invoice"&&user.bankingDetails&&<div style={{background:"#f9f9f9",borderRadius:6,padding:"10px 14px",fontSize:11,color:"#666",fontFamily:ff}}>{user.bankingDetails}</div>}
      {footerBlock}
    </div>
  );
}

// ── DOCUMENT TEMPLATE SETTINGS ───────────────────────────────────────────────
function DocumentTemplateSettings({template, onChange}) {
  const t = {...DEFAULT_DOC_TEMPLATE, ...template};
  const set = (k, v) => onChange({...t, [k]: v});

  const LAYOUTS = [
    {id:"classic", label:"Classic",  desc:"Logo left, title right. Clean professional.",   preview:"🗒️"},
    {id:"modern",  label:"Modern",   desc:"Coloured header band. Bold and contemporary.",  preview:"🎨"},
    {id:"minimal", label:"Minimal",  desc:"Clean lines, elegant typography. Less is more.", preview:"✏️"},
    {id:"bold",    label:"Bold",     desc:"Dark header, large number. High impact.",        preview:"⚡"},
  ];
  const FONTS = [
    {id:"Arial, sans-serif",           label:"Arial (Sans-serif)"},
    {id:"Georgia, serif",              label:"Georgia (Serif)"},
    {id:"'Courier New', monospace",    label:"Courier (Monospace)"},
    {id:"Helvetica, Arial, sans-serif",label:"Helvetica"},
  ];
  const TABLE_STYLES = [
    {id:"shaded", label:"Shaded header"},
    {id:"lines",  label:"Lines only"},
    {id:"clean",  label:"Clean / no lines"},
  ];

  const inputStyle = {width:"100%",padding:"9px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"};
  const labelStyle = {fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5};

  // Sample doc for live preview
  const sampleDoc = {id:"INV-001",client:"Sample Client Pty Ltd",desc:"Professional services rendered",date:new Date().toISOString().slice(0,10),due:new Date(Date.now()+30*864e5).toISOString().slice(0,10),amount:10000,vatAmount:1500,totalAmount:11500,currency:"ZAR"};
  const sampleUser = {companyName:"Your Company",logoUrl:"",bankingDetails:"FNB Business · Acc: 123456789 · Branch: 250655",...(template||{})};

  return (
    <div style={{display:"grid",gridTemplateColumns:"1fr 420px",gap:24,alignItems:"start"}}>
      {/* ── Left: Controls ── */}
      <div>
        {/* Layout picker */}
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,padding:20,marginBottom:16}}>
          <div style={{fontSize:12,fontWeight:700,color:C.inkMid,letterSpacing:1,textTransform:"uppercase",marginBottom:14}}>Layout</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            {LAYOUTS.map(l=>(
              <div key={l.id} onClick={()=>set("layout",l.id)} style={{border:`2px solid ${t.layout===l.id?t.primaryColor:C.border}`,background:t.layout===l.id?t.primaryColor+"10":"transparent",borderRadius:12,padding:"14px 16px",cursor:"pointer",transition:"all 0.15s"}}>
                <div style={{fontSize:22,marginBottom:6}}>{l.preview}</div>
                <div style={{fontSize:13,fontWeight:700,color:C.ink}}>{l.label}</div>
                <div style={{fontSize:11,color:C.inkMid,marginTop:3}}>{l.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Colours + Font */}
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,padding:20,marginBottom:16}}>
          <div style={{fontSize:12,fontWeight:700,color:C.inkMid,letterSpacing:1,textTransform:"uppercase",marginBottom:14}}>Branding</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
            <div>
              <label style={labelStyle}>Primary Colour</label>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <input type="color" value={t.primaryColor} onChange={e=>set("primaryColor",e.target.value)} style={{width:44,height:36,border:`1px solid ${C.border}`,borderRadius:6,cursor:"pointer",padding:2}}/>
                <input value={t.primaryColor} onChange={e=>set("primaryColor",e.target.value)} style={{...inputStyle,flex:1}} placeholder="#C8401A"/>
              </div>
            </div>
            <div>
              <label style={labelStyle}>Font</label>
              <select value={t.fontFamily} onChange={e=>set("fontFamily",e.target.value)} style={inputStyle}>
                {FONTS.map(f=><option key={f.id} value={f.id}>{f.label}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Table style */}
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,padding:20,marginBottom:16}}>
          <div style={{fontSize:12,fontWeight:700,color:C.inkMid,letterSpacing:1,textTransform:"uppercase",marginBottom:14}}>Table Style</div>
          <div style={{display:"flex",gap:8}}>
            {TABLE_STYLES.map(s=>(
              <button key={s.id} onClick={()=>set("tableStyle",s.id)} style={{flex:1,padding:"9px 12px",borderRadius:8,border:`2px solid ${t.tableStyle===s.id?t.primaryColor:C.border}`,background:t.tableStyle===s.id?t.primaryColor+"10":"transparent",color:t.tableStyle===s.id?C.ink:C.inkMid,fontSize:12,fontWeight:t.tableStyle===s.id?700:400,cursor:"pointer",fontFamily:"inherit"}}>{s.label}</button>
            ))}
          </div>
        </div>

        {/* Titles + Footer */}
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,padding:20,marginBottom:16}}>
          <div style={{fontSize:12,fontWeight:700,color:C.inkMid,letterSpacing:1,textTransform:"uppercase",marginBottom:14}}>Document Titles &amp; Footer</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14}}>
            <div>
              <label style={labelStyle}>Invoice Title</label>
              <input value={t.invoiceTitle} onChange={e=>set("invoiceTitle",e.target.value)} style={inputStyle} placeholder="TAX INVOICE"/>
            </div>
            <div>
              <label style={labelStyle}>Quote Title</label>
              <input value={t.quoteTitle} onChange={e=>set("quoteTitle",e.target.value)} style={inputStyle} placeholder="QUOTE / ESTIMATE"/>
            </div>
          </div>
          <div>
            <label style={labelStyle}>Footer Note (appears at bottom of every document)</label>
            <textarea value={t.footerNote} onChange={e=>set("footerNote",e.target.value)} rows={3} style={{...inputStyle,resize:"vertical"}} placeholder="e.g. Payment terms: 30 days. Thank you for your business!"/>
          </div>
        </div>
      </div>

      {/* ── Right: Live Preview ── */}
      <div style={{position:"sticky",top:24}}>
        <div style={{fontSize:12,fontWeight:700,color:C.inkMid,letterSpacing:1,textTransform:"uppercase",marginBottom:10}}>Live Preview</div>
        <div style={{background:"#fff",border:`1px solid ${C.border}`,borderRadius:12,overflow:"hidden",boxShadow:"0 4px 24px rgba(0,0,0,0.08)"}}>
          <div style={{padding:24,transform:"scale(0.85)",transformOrigin:"top left",width:"117%"}}>
            <InvoiceDocument type="invoice" doc={sampleDoc} user={sampleUser} tmpl={t}/>
          </div>
        </div>
        <div style={{fontSize:11,color:C.inkMid,marginTop:8,textAlign:"center"}}>Preview updates in real time</div>
      </div>
    </div>
  );
}

// ── RECURRING INVOICES TAB ────────────────────────────────────────────────────
function RecurringInvoicesTab() {
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({client_name:"",client_email:"",description:"",amount:"",vat_applicable:true,currency:"ZAR",frequency:"monthly",start_date:new Date().toISOString().slice(0,10)});
  const [saving, setSaving] = useState(false);
  const load = () => { api("/recurring-invoices").then(setItems).catch(()=>{}); };
  useEffect(load, []);
  const FREQ = {weekly:"Weekly",monthly:"Monthly",quarterly:"Quarterly",annually:"Annually"};
  const handleCreate = async () => {
    if(!form.client_name||!form.amount){alert("Client name and amount required.");return;}
    setSaving(true);
    try { await api("/recurring-invoices",{method:"POST",body:JSON.stringify({...form,amount:+form.amount})}); setShowNew(false); setForm({client_name:"",client_email:"",description:"",amount:"",vat_applicable:true,currency:"ZAR",frequency:"monthly",start_date:new Date().toISOString().slice(0,10)}); load(); }
    catch(e){alert("Failed to create.");}
    finally{setSaving(false);}
  };
  const handleDelete = async (id) => {
    if(!window.confirm("Delete recurring invoice?"))return;
    await api(`/recurring-invoices/${id}`,{method:"DELETE"}).catch(()=>{});
    load();
  };
  const handleToggle = async (item) => {
    await api(`/recurring-invoices/${item.id}`,{method:"PUT",body:JSON.stringify({is_active:!item.is_active})}).catch(()=>{});
    load();
  };
  const handleRunNow = async (id) => {
    try { await api(`/recurring-invoices/${id}/run`,{method:"POST"}); alert("Invoice generated!"); load(); }
    catch(e){alert("Failed to run.");}
  };
  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
        <div>
          <div style={{fontSize:20,fontWeight:800,fontFamily:"serif",color:C.ink}}>Recurring Invoices</div>
          <div style={{fontSize:13,color:C.inkMid,marginTop:2}}>Auto-generate invoices on a schedule</div>
        </div>
        <button onClick={()=>setShowNew(true)} style={{background:C.accent,color:"#fff",border:"none",borderRadius:10,padding:"10px 20px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>+ New Recurring</button>
      </div>
      {showNew && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:250,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div style={{background:C.surface,borderRadius:20,width:"100%",maxWidth:600,maxHeight:"90vh",display:"flex",flexDirection:"column",boxShadow:"0 8px 40px #00000030"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"20px 28px 16px",borderBottom:`1px solid ${C.border}`,flexShrink:0}}>
              <h3 style={{fontFamily:"serif",fontSize:22,color:C.ink,margin:0}}>New Recurring Invoice</h3>
              <button onClick={()=>setShowNew(false)} style={{background:"none",border:"none",fontSize:24,cursor:"pointer",color:C.inkMid,lineHeight:1}}>×</button>
            </div>
            <div style={{overflowY:"auto",padding:"20px 28px",flex:1}}>
              {[{l:"Client Name",k:"client_name",p:"Client name"},{l:"Client Email",k:"client_email",p:"client@example.com"},{l:"Description",k:"description",p:"Monthly retainer"},{l:"Amount (excl. VAT)",k:"amount",p:"0.00",type:"number"},{l:"Start Date",k:"start_date",type:"date"}].map(f=>(
                <div key={f.k} style={{marginBottom:14}}>
                  <label style={{display:"block",fontSize:11,fontWeight:600,color:C.inkMid,marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>{f.l}</label>
                  <input type={f.type||"text"} placeholder={f.p||""} value={form[f.k]} onChange={e=>setForm({...form,[f.k]:e.target.value})} style={{width:"100%",padding:"11px 14px",border:`1.5px solid ${C.border}`,borderRadius:10,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}/>
                </div>
              ))}
              <div style={{marginBottom:14}}>
                <label style={{display:"block",fontSize:11,fontWeight:600,color:C.inkMid,marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Frequency</label>
                <select value={form.frequency} onChange={e=>setForm({...form,frequency:e.target.value})} style={{width:"100%",padding:"11px 14px",border:`1.5px solid ${C.border}`,borderRadius:10,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}>
                  <option value="weekly">Weekly</option><option value="monthly">Monthly</option><option value="quarterly">Quarterly</option><option value="annually">Annually</option>
                </select>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
                <label htmlFor="vat-recur" style={{fontSize:13,color:C.ink}}>VAT treatment </label>
                <select id="vat-recur" value={form.supply_type || (form.vat_applicable ? "standard" : "zero_rated")} onChange={e=>{const v=e.target.value; setForm({...form,supply_type:v,vat_applicable:v==="standard"});}} style={{padding:"6px 8px",border:"1px solid "+C.border,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink}}>
                  <option value="standard">Standard rate (15%)</option>
                  <option value="zero_rated">Zero-rated (0%)</option>
                  <option value="exempt">Exempt (no VAT)</option>
                </select>
              </div>
            </div>
            <div style={{display:"flex",gap:8,padding:"16px 28px",borderTop:`1px solid ${C.border}`,flexShrink:0,background:C.surface,borderRadius:"0 0 20px 20px"}}>
              <button onClick={handleCreate} disabled={saving} style={{background:C.accent,color:"#fff",border:"none",borderRadius:10,padding:"11px 24px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",opacity:saving?0.6:1}}>{saving?"Saving...":"Create"}</button>
              <button onClick={()=>setShowNew(false)} style={{background:"transparent",color:C.inkMid,border:`1px solid ${C.border}`,borderRadius:10,padding:"11px 20px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {items.length===0 ? (
        <div style={{textAlign:"center",padding:"60px 20px",color:C.inkMid}}>
          <div style={{fontSize:48,marginBottom:16}}>🔁</div>
          <div style={{fontSize:16,fontWeight:600,color:C.ink,marginBottom:8}}>No recurring invoices yet</div>
          <div style={{fontSize:13}}>Set up a recurring invoice to auto-generate invoices for retainers and subscriptions.</div>
        </div>
      ) : (
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,overflow:"hidden"}}>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead><tr style={{background:C.bg}}>
              {["Client","Description","Amount","Frequency","Next Run","Status","Actions"].map(h=>(
                <th key={h} style={{padding:"11px 16px",textAlign:"left",fontSize:11,fontWeight:700,color:C.inkMid,textTransform:"uppercase"}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {items.map((item,i)=>(
                <tr key={item.id} style={{borderTop:`1px solid ${C.border}`,background:i%2===0?C.surface:C.bg}}>
                  <td style={{padding:"12px 16px",fontWeight:600}}>{item.client_name}</td>
                  <td style={{padding:"12px 16px",color:C.inkMid,fontSize:12}}>{item.description||"—"}</td>
                  <td style={{padding:"12px 16px",fontWeight:700}}>{fmt(item.amount)}</td>
                  <td style={{padding:"12px 16px",textTransform:"capitalize"}}>{FREQ[item.frequency]||item.frequency}</td>
                  <td style={{padding:"12px 16px",color:C.inkMid}}>{item.next_run_date?new Date(item.next_run_date).toLocaleDateString("en-ZA"):"—"}</td>
                  <td style={{padding:"12px 16px"}}>
                    <span style={{padding:"3px 10px",borderRadius:20,fontSize:11,fontWeight:700,background:item.is_active?C.greenLt:C.border,color:item.is_active?C.green:C.inkMid}}>{item.is_active?"Active":"Paused"}</span>
                  </td>
                  <td style={{padding:"12px 16px"}}>
                    <div style={{display:"flex",gap:6}}>
                      <button onClick={()=>handleToggle(item)} style={{background:item.is_active?C.goldLt:C.greenLt,color:item.is_active?C.gold:C.green,border:"none",borderRadius:6,padding:"5px 10px",fontSize:11,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>{item.is_active?"Pause":"Resume"}</button>
                      <button onClick={()=>handleRunNow(item.id)} style={{background:C.blueLt,color:C.blue,border:"none",borderRadius:6,padding:"5px 10px",fontSize:11,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>Run Now</button>
                      <button onClick={()=>handleDelete(item.id)} style={{background:C.redLt,color:C.red,border:"none",borderRadius:6,padding:"5px 10px",fontSize:11,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── CREDIT NOTES TAB ──────────────────────────────────────────────────────────
function CreditNotesTab() {
  const today = () => new Date().toISOString().slice(0,10);
  const blank = () => ({invoice_id:"",client_name:"",description:"",amount:"",vat_rate:0.15,notes:"",issue_date:today(),currency:"ZAR"});
  const [items, setItems] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(blank());
  const [returnable, setReturnable] = useState([]);      // stock lines sold on the linked invoice that can still come back
  const [qty, setQty] = useState({});                    // code -> quantity coming back
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const load = () => { api("/credit-notes").then(setItems).catch(()=>{}); };
  useEffect(load, []);
  useEffect(() => { api("/invoices/").then(r => setInvoices(Array.isArray(r)?r:[])).catch(()=>{}); }, []);
  const creditable = invoices.filter(i => ["sent","overdue","partial","paid"].includes(String(i.status||"").toLowerCase().replace("invoicestatus.","")));
  const linked = creditable.find(i => String(i.id) === String(form.invoice_id));
  const pickInvoice = async (id) => {
    setErr(""); setQty({}); setReturnable([]);
    const inv = creditable.find(i => String(i.id) === String(id));
    if (!inv) { setForm({...form, invoice_id:"", currency:"ZAR"}); return; }
    const hasVat = (inv.vat_amount||0) > 0;
    setForm({...form, invoice_id:id, client_name:inv.client_name||form.client_name, currency:inv.currency||"ZAR", vat_rate:hasVat?0.15:0,
             description: form.description || `Credit against ${inv.invoice_number}`});
    try { setReturnable((await api(`/credit-notes/returnable/${id}`)).filter(r => r.returnable > 0)); } catch(e) { setReturnable([]); }
  };
  const handleCreate = async () => {
    setErr("");
    if(!form.client_name||!form.amount){setErr("Client name and amount are required.");return;}
    const restock = returnable.filter(r => +qty[r.code] > 0).map(r => ({code:r.code, quantity:+qty[r.code]}));
    for (const r of returnable) { if (+qty[r.code] > r.returnable) { setErr(`Only ${r.returnable} of ${r.name} can be taken back.`); return; } }
    setSaving(true);
    try {
      const body = {client_name:form.client_name, description:form.description, notes:form.notes, issue_date:form.issue_date,
                    amount:+form.amount, vat_rate:+form.vat_rate, currency:form.currency||"ZAR"};
      if (form.invoice_id) body.invoice_id = +form.invoice_id;
      if (restock.length) body.restock = restock;
      await api("/credit-notes",{method:"POST",body:JSON.stringify(body)});
      setShowNew(false); setForm(blank()); setReturnable([]); setQty({}); load();
    }
    catch(e){ setErr(e.message || "Failed to create credit note."); }
    finally{setSaving(false);}
  };
  const handleDelete = async (id) => {
    if(!window.confirm("Delete this credit note? Any stock it put back on the shelf will be taken out again."))return;
    try { await api(`/credit-notes/${id}`,{method:"DELETE"}); } catch(e) { alert(e.message || "Could not delete the credit note."); }
    load();
  };
  const inp = {width:"100%",padding:"11px 14px",border:`1.5px solid ${C.border}`,borderRadius:10,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"};
  const lbl = {display:"block",fontSize:11,fontWeight:600,color:C.inkMid,marginBottom:6,textTransform:"uppercase",letterSpacing:0.5};
  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
        <div>
          <div style={{fontSize:20,fontWeight:800,fontFamily:"serif",color:C.ink}}>Credit Notes</div>
          <div style={{fontSize:13,color:C.inkMid,marginTop:2}}>Issue credit notes to reduce amounts owed by clients, and put returned goods back in stock</div>
        </div>
        <button onClick={()=>{setErr("");setShowNew(true);}} style={{background:C.accent,color:"#fff",border:"none",borderRadius:10,padding:"10px 20px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>+ New Credit Note</button>
      </div>
      {showNew && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:250,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div style={{background:C.surface,borderRadius:20,width:"100%",maxWidth:560,maxHeight:"90vh",display:"flex",flexDirection:"column",boxShadow:"0 8px 40px #00000030"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"20px 28px 16px",borderBottom:`1px solid ${C.border}`,flexShrink:0}}>
              <h3 style={{fontFamily:"serif",fontSize:22,color:C.ink,margin:0}}>New Credit Note</h3>
              <button onClick={()=>setShowNew(false)} style={{background:"none",border:"none",fontSize:24,cursor:"pointer",color:C.inkMid,lineHeight:1}}>×</button>
            </div>
            <div style={{overflowY:"auto",padding:"20px 28px",flex:1}}>
              {err && <div style={{background:C.redLt,color:C.red,borderRadius:10,padding:"10px 14px",fontSize:13,fontWeight:600,marginBottom:14}}>{err}</div>}
              <div style={{marginBottom:14}}>
                <label style={lbl}>Credit against invoice (optional)</label>
                <select value={form.invoice_id} onChange={e=>pickInvoice(e.target.value)} style={inp}>
                  <option value="">— Not linked to an invoice —</option>
                  {creditable.map(i => <option key={i.id} value={i.id}>{i.invoice_number} · {i.client_name} · {i.currency&&i.currency!=="ZAR"?i.currency+" ":"R"}{Number(i.total_amount||0).toLocaleString("en-ZA",{minimumFractionDigits:2,maximumFractionDigits:2})}</option>)}
                </select>
                {linked && linked.currency && linked.currency!=="ZAR" && <div style={{fontSize:11,color:C.inkMid,marginTop:6}}>Foreign-currency invoice: enter the amount in {linked.currency}; it is converted to rand at the invoice's exchange rate.</div>}
              </div>
              {[{l:"Client Name",k:"client_name",p:"Client name"},{l:"Description",k:"description",p:"Reason for credit note"},{l:`Amount (excl. VAT)${linked&&linked.currency&&linked.currency!=="ZAR"?" in "+linked.currency:""}`,k:"amount",p:"0.00",type:"number"},{l:"Issue Date",k:"issue_date",type:"date"},{l:"Notes",k:"notes",p:"Optional additional notes"}].map(f=>(
                <div key={f.k} style={{marginBottom:14}}>
                  <label style={lbl}>{f.l}</label>
                  <input type={f.type||"text"} placeholder={f.p||""} value={form[f.k]} onChange={e=>setForm({...form,[f.k]:e.target.value})} style={inp}/>
                </div>
              ))}
              <div style={{marginBottom:14}}>
                <label style={lbl}>VAT Rate</label>
                <select value={form.vat_rate} onChange={e=>setForm({...form,vat_rate:+e.target.value})} style={inp}>
                  <option value={0.15}>15% VAT</option>
                  <option value={0}>No VAT (0%)</option>
                </select>
              </div>
              {returnable.length>0 && (
                <div style={{marginBottom:14,border:`1.5px solid ${C.border}`,borderRadius:12,padding:"12px 14px",background:C.bg}}>
                  <div style={{fontSize:12,fontWeight:700,color:C.ink,marginBottom:4}}>Goods coming back</div>
                  <div style={{fontSize:11,color:C.inkMid,marginBottom:10}}>Enter how many units the client is returning. They go back in stock at the cost they were sold at. Leave at 0 for a money-only credit.</div>
                  {returnable.map(r => (
                    <div key={r.code} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,marginBottom:8}}>
                      <div style={{fontSize:13,color:C.ink,flex:1}}>{r.name} <span style={{fontSize:11,color:C.inkMid}}>({r.code}) · sold {r.sold}{r.returned>0?`, ${r.returned} already returned`:""}</span></div>
                      <input type="number" min="0" max={r.returnable} step="any" placeholder="0" value={qty[r.code]||""} onChange={e=>setQty({...qty,[r.code]:e.target.value})} style={{...inp,width:90,padding:"8px 10px"}}/>
                    </div>
                  ))}
                </div>
              )}
              {linked && returnable.length===0 && <div style={{fontSize:11,color:C.inkMid,marginBottom:14}}>No stock items are left to take back on this invoice, so this will be a money-only credit note.</div>}
            </div>
            <div style={{display:"flex",gap:8,padding:"16px 28px",borderTop:`1px solid ${C.border}`,flexShrink:0,background:C.surface,borderRadius:"0 0 20px 20px"}}>
              <button onClick={handleCreate} disabled={saving} style={{background:C.accent,color:"#fff",border:"none",borderRadius:10,padding:"11px 24px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",opacity:saving?0.6:1}}>{saving?"Saving...":"Issue Credit Note"}</button>
              <button onClick={()=>setShowNew(false)} style={{background:"transparent",color:C.inkMid,border:`1px solid ${C.border}`,borderRadius:10,padding:"11px 20px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {items.length===0 ? (
        <div style={{textAlign:"center",padding:"60px 20px",color:C.inkMid}}>
          <div style={{fontSize:48,marginBottom:16}}>📋</div>
          <div style={{fontSize:16,fontWeight:600,color:C.ink,marginBottom:8}}>No credit notes yet</div>
          <div style={{fontSize:13}}>Issue a credit note to reduce the amount owed on an invoice.</div>
        </div>
      ) : (
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,overflow:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead><tr style={{background:C.bg}}>
              {["Number","Invoice","Client","Description","Amount","Total incl. VAT","Stock back","Date","Actions"].map(h=>(
                <th key={h} style={{padding:"11px 16px",textAlign:"left",fontSize:11,fontWeight:700,color:C.inkMid,textTransform:"uppercase"}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {items.map((cn,i)=>(
                <tr key={cn.id} style={{borderTop:`1px solid ${C.border}`,background:i%2===0?C.surface:C.bg}}>
                  <td style={{padding:"12px 16px",fontWeight:700,color:C.accent}}>{cn.credit_note_number}</td>
                  <td style={{padding:"12px 16px",color:C.inkMid,fontSize:12}}>{cn.invoice_number||"—"}</td>
                  <td style={{padding:"12px 16px",fontWeight:500}}>{cn.client_name}</td>
                  <td style={{padding:"12px 16px",color:C.inkMid,fontSize:12}}>{cn.description||"—"}</td>
                  <td style={{padding:"12px 16px",fontWeight:700,color:C.red}}>{fmt(cn.amount)}</td>
                  <td style={{padding:"12px 16px",fontWeight:700,color:C.red}}>{fmt(cn.total_amount)}</td>
                  <td style={{padding:"12px 16px",color:C.inkMid,fontSize:12}}>{(cn.stock_returns&&cn.stock_returns.length)?cn.stock_returns.map(r=>`${r.qty} × ${r.name}`).join(", "):"—"}</td>
                  <td style={{padding:"12px 16px",color:C.inkMid}}>{cn.issue_date?new Date(cn.issue_date).toLocaleDateString("en-ZA"):"—"}</td>
                  <td style={{padding:"12px 16px"}}>
                    <button onClick={()=>handleDelete(cn.id)} style={{background:C.redLt,color:C.red,border:"none",borderRadius:6,padding:"5px 10px",fontSize:11,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── INVOICING ─────────────────────────────────────────────────────────────────
function Invoicing({live = {}, user = {}, docTemplate}) {
  const liveInvoices = live.invoices;
  const [invoices, setInvoices] = useState([]);
  useEffect(() => { if (Array.isArray(liveInvoices)) setInvoices(liveInvoices.map(i => ({...i, _dbId: i.id, date: i.issue_date || i.date, due: i.due_date || i.due, client: i.client_name || i.client, desc: i.description, clientEmail: i.client_email || "", amount: i.total_amount || i.amount, portalToken: i.portal_token || null, id: i.invoice_number || `INV-${String(i.id).padStart(3,"0")}`}))); }, [liveInvoices]);

  const [showNew,      setShowNew]      = useState(false);
  const [preview,      setPreview]      = useState(null);   // view modal
  const [editInv,      setEditInv]      = useState(null);   // amend modal
  const [payModal,     setPayModal]     = useState(null);   // {inv, zarAmt, payDate, ref}
  const [filterStatus, setFilterStatus] = useState(null);   // null | "paid" | "sent" | "overdue"
  const [form, setForm] = useState({client:"",amount:"",desc:"",due:"",vatApplicable:true,currency:"ZAR",exchangeRate:"18.5",vatAmount:"0"});
  const [saving, setSaving] = useState(false);
  const [invTab, setInvTab] = useState("invoices");
  const [customers, setCustomers] = useState([]);
  useEffect(()=>{ api("/customers/").then(setCustomers).catch(()=>{}); },[]);
  // Cash actually received this calendar month, per invoice (so part payments count in the month they were received)
  const [payMonth, setPayMonth] = useState({});
  useEffect(()=>{ api("/invoices/payments-this-month").then(r=>setPayMonth((r&&r.by_invoice)||{})).catch(()=>{}); },[liveInvoices]);

  const toZar = i => (i.currency && i.currency !== "ZAR") ? (i.amount||0) * (i.exchange_rate||1) : (i.amount||0);
  const today = new Date(); today.setHours(0,0,0,0);
  const isOverdue = i => (i.status === "sent" || i.status === "partial") && i.due && new Date(i.due) < today;
  // Amount still owed on an unpaid / part-paid invoice (ZAR)
  const owedZar = i => i.status === "partial" ? Math.max(0, toZar(i) - (i.paid_amount_zar||0)) : toZar(i);
  // Paid invoices from the current calendar month are still "active" — only prior-month paid invoices are archived
  const isCurrentMonth = i => { const d = new Date(i.paid_date || i.date || 0); return d.getFullYear() === today.getFullYear() && d.getMonth() === today.getMonth(); };
  const totalPaid          = invoices.filter(i => i.status === "paid").reduce((s,i) => s + toZar(i), 0);
  const cashThisMonth = i => {
    const v = payMonth[i._dbId || i.id];
    if (v !== undefined) return v;
    return (i.status === "paid" && isCurrentMonth(i)) ? toZar(i) : 0;   // older invoices with no payment entry
  };
  const totalPaidThisMonth = invoices.reduce((s,i) => s + Math.max(0, cashThisMonth(i)), 0);
  const totalOverdue = invoices.filter(isOverdue).reduce((s,i) => s + owedZar(i), 0);
  const totalPending = invoices.filter(i => (i.status === "sent" || i.status === "partial") && !isOverdue(i)).reduce((s,i) => s + owedZar(i), 0);
  const displayedInvoices = filterStatus === "overdue"
    ? invoices.filter(isOverdue)
    : filterStatus === "sent"
    ? invoices.filter(i => (i.status === "sent" || i.status === "partial") && !isOverdue(i))
    : filterStatus === "paid"
    ? invoices.filter(i => i.status === "paid") // archive view: all paid
    : filterStatus === "paid_this_month"
    ? invoices.filter(i => cashThisMonth(i) > 0) // received this month (incl. part payments)
    : filterStatus
    ? invoices.filter(i => i.status === filterStatus)
    : invoices.filter(i => i.status !== "paid" || isCurrentMonth(i)); // default: show active + current-month paid; archive prior-month paid
  const toggleFilter = (status) => setFilterStatus(prev => prev === status ? null : status);

  const openPayModal = (inv) => {
    // For partial invoices, pre-fill with the outstanding balance; otherwise the full invoice amount
    const totalZar = inv.currency && inv.currency !== "ZAR"
      ? (inv.amount||0) * (inv.exchange_rate||inv.rate||18.5)
      : (inv.amount||0);
    const alreadyPaid = inv.status === "partial" ? (inv.paid_amount_zar||0) : 0;
    const outstanding = Math.max(0, totalZar - alreadyPaid);
    const zarAmt = outstanding > 0 ? outstanding.toFixed(2) : totalZar.toFixed(2);
    setPayModal({inv, zarAmt, payDate: new Date().toISOString().slice(0,10), ref:"", alreadyPaid});
  };

  const handleRecordPayment = async () => {
    const {inv, zarAmt, payDate, ref, alreadyPaid} = payModal;
    // Send the cumulative total so the backend can detect partial vs full payment
    const cumulative = Math.round(((alreadyPaid||0) + (+zarAmt||0)) * 100) / 100;
    const _isForeign = inv.currency && inv.currency !== "ZAR";
    const _fullZar = (inv.amount||0) * (_isForeign ? (inv.exchange_rate||inv.rate||1) : 1);
    const _isPart  = _isForeign ? payModal.settles === false : cumulative + 0.005 < _fullZar;
    setInvoices(prev => prev.map(i => i.id === inv.id ? {...i, status:_isPart?"partial":"paid", paid_amount_zar:cumulative} : i));
    setPayModal(null);
    try {
      await api(`/invoices/${inv._dbId || inv.id}`, { method:"PUT", body: JSON.stringify({
        status: "paid",
        paid_date: payDate || new Date().toISOString().slice(0,10),
        paid_amount_zar: cumulative || null,
        ...((inv.currency && inv.currency !== "ZAR") ? {final_payment: payModal.settles !== false} : {}),
        notes: ref || null,
      })});
      if (live && live.reload) live.reload();
    } catch(e) {
      console.warn("Record payment failed:", e.message);
      alert("The payment was NOT saved: " + e.message);
      if (live && live.reload) live.reload();
    }
  };

  const handleCreate = async () => {
    if(!form.client||!form.amount||!form.desc){alert("Client, description and amount are required.");return;}
    setSaving(true);
    try {
      const invBody = { client_name:form.client, description:form.desc, amount:+form.amount, vat_applicable:form.vatApplicable, supply_type:form.supplyType||null, due_date:form.due||null, currency:form.currency||"ZAR", exchange_rate:+form.exchangeRate||1.0 };
      if (form.currency && form.currency !== "ZAR") invBody.vat_amount_override = +form.vatAmount||0;
      if (form.items_json) invBody.items_json = form.items_json;
      if (form.passenger_name) invBody.passenger_name = form.passenger_name;
      if (form.tour_ref) invBody.tour_ref = form.tour_ref;
      if (form.quote_ref) invBody.quote_ref = form.quote_ref;
      if (form.tax_ref) invBody.tax_ref = form.tax_ref;
      if (form.travel_date) invBody.travel_date = form.travel_date;
      if (form.pax_count) invBody.pax_count = +form.pax_count;
      await api("/invoices/", { method:"POST", body: JSON.stringify(invBody) });
      if (live && live.reload) live.reload();
      setShowNew(false);
      setForm({client:"",amount:"",desc:"",due:"",vatApplicable:true});
    } catch(err) { alert(err.message || "Failed to create invoice. Please try again."); }
    finally { setSaving(false); }
  };

  const handleAmend = async () => {
    setInvoices(prev => prev.map(i => i.id === editInv.id ? {...editInv} : i));
    setEditInv(null);
    try {
      await api(`/invoices/${editInv._dbId || editInv.id}`, { method:"PUT", body: JSON.stringify({ client_name:editInv.client, description:editInv.desc, amount:+editInv.amount, due_date:editInv.due||null, status:editInv.status }) });
      if (live && live.reload) live.reload();
    } catch(err) { console.warn("Amend failed:", err.message); }
  };

  // InvFormFields moved outside component — see top-level definition below

  const isMobile = useIsMobile();
  const printInvoice = () => { const el=document.getElementById("invoice-preview-content"); if(!el)return; const w=window.open("","_blank"); if(!w){alert("Allow popups to print.");return;} w.document.write(`<html><head><title>Invoice ${preview.id}</title><style>body{font-family:Arial,sans-serif;padding:40px;color:#1A1209;}@media print{body{padding:20px;}}</style></head><body>${el.innerHTML}</body></html>`); w.document.close(); w.onload=()=>{w.focus();w.print();w.close();}; };

  return (
    <div>
      <div style={{display:"flex",gap:0,marginBottom:24,borderBottom:`1px solid ${C.border}`,alignItems:"center",flexWrap:"wrap"}}>
        <div style={{flex:isMobile?"1 0 100%":1,paddingBottom:10}}>
          <div style={{fontSize:22,fontWeight:800,fontFamily:"serif",color:C.ink}}>Invoicing</div>
        </div>
        {[{id:"invoices",label:"Invoices"},{id:"recurring",label:"Recurring"},{id:"credit_notes",label:"Credit Notes"}].map(t=>(
          <button key={t.id} onClick={()=>setInvTab(t.id)} style={{padding:"8px 18px 11px",border:"none",borderRadius:0,cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:invTab===t.id?700:400,background:"transparent",color:invTab===t.id?C.accent:C.inkMid,borderBottom:invTab===t.id?`2px solid ${C.accent}`:"2px solid transparent",marginBottom:-1}}>{t.label}</button>
        ))}
        {invTab==="invoices" && <button onClick={()=>setShowNew(true)} style={{marginLeft:16,background:C.accent,color:"#fff",border:"none",borderRadius:8,padding:"7px 16px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",marginBottom:8}}>+ New Invoice</button>}
      </div>
      {invTab==="invoices" && <>
      <div style={{display:"flex",gap:12,flexWrap:"wrap",marginBottom:filterStatus?12:24}}>
        <KPI label="Paid This Month" value={fmt(totalPaidThisMonth)} color={C.green} icon="✓" sub="Current month" onClick={()=>toggleFilter("paid_this_month")} active={filterStatus==="paid_this_month"}/>
        <KPI label="Pending" value={fmt(totalPending)} color={C.gold}  icon="⏳" onClick={()=>toggleFilter("sent")} active={filterStatus==="sent"}/>
        <KPI label="Overdue" value={fmt(totalOverdue)} color={C.red}   icon="⚠️" onClick={()=>toggleFilter("overdue")} active={filterStatus==="overdue"}/>
      </div>
      {filterStatus && (
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16,padding:"10px 16px",background:filterStatus==="paid"?"#f3f4f6":C.accentLt,border:`1px solid ${filterStatus==="paid"?C.border:C.accent}30`,borderRadius:10}}>
          {filterStatus==="paid"
            ? <span style={{fontSize:13,color:C.inkMid,fontWeight:600}}>📦 Archive — All Paid Invoices ({displayedInvoices.length} invoice{displayedInvoices.length!==1?"s":""})</span>
            : filterStatus==="paid_this_month"
            ? <span style={{fontSize:13,color:C.green,fontWeight:600}}>✓ Paid This Month ({displayedInvoices.length} invoice{displayedInvoices.length!==1?"s":""})</span>
            : <span style={{fontSize:13,color:C.accent,fontWeight:600,textTransform:"capitalize"}}>Showing: {filterStatus} ({displayedInvoices.length} invoice{displayedInvoices.length!==1?"s":""})</span>
          }
          <button onClick={()=>setFilterStatus(null)} style={{marginLeft:"auto",background:"none",border:`1px solid ${filterStatus==="paid"||filterStatus==="paid_this_month"?C.border:C.accent}40`,borderRadius:6,padding:"4px 10px",fontSize:12,color:filterStatus==="paid"||filterStatus==="paid_this_month"?C.inkMid:C.accent,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>{filterStatus==="paid"||filterStatus==="paid_this_month"?"← Active Invoices":"✕ Show All"}</button>
        </div>
      )}

      {/* New Invoice */}
      {showNew && (
        <div style={{background:C.surface,border:`2px solid ${C.accent}`,borderRadius:16,padding:24,marginBottom:20}}>
          <h3 style={{fontFamily:"serif",margin:"0 0 16px",color:C.ink}}>New Invoice</h3>
          <InvFormFields data={form} onChange={setForm} customers={customers}/>
          <div style={{display:"flex",gap:8}}>
            <button onClick={handleCreate} disabled={saving} style={{background:C.accent,color:"#fff",border:"none",borderRadius:10,padding:"10px 20px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",opacity:saving?0.6:1}}>{saving?"Saving...":"Create Invoice"}</button>
            <button onClick={() => setShowNew(false)} style={{background:"transparent",color:C.inkMid,border:`1px solid ${C.border}`,borderRadius:10,padding:"10px 20px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
          </div>
        </div>
      )}

      {/* ── MOBILE: Full-screen invoice detail ── */}
      {preview && isMobile && (
        <div style={{position:"fixed",inset:0,background:C.bg,zIndex:200,display:"flex",flexDirection:"column",overflowY:"hidden"}}>
          {/* Sticky header */}
          <div style={{display:"flex",alignItems:"center",gap:12,padding:"14px 16px",background:C.surface,borderBottom:`1px solid ${C.border}`,flexShrink:0}}>
            <button onClick={()=>setPreview(null)} style={{background:"none",border:"none",fontSize:22,cursor:"pointer",color:C.ink,lineHeight:1,padding:"0 4px"}}>←</button>
            <div style={{flex:1}}>
              <div style={{fontWeight:800,fontSize:16,color:C.accent}}>{preview.id}</div>
              <div style={{fontSize:12,color:C.inkMid}}>{preview.client}</div>
            </div>
            <StatusBadge status={preview.status}/>
          </div>
          {/* Scrollable document body */}
          <div style={{flex:1,overflowY:"auto",padding:"20px 16px",WebkitOverflowScrolling:"touch"}}>
            <div id="invoice-preview-content">
              <InvoiceDocument type="invoice" doc={preview} user={user} tmpl={docTemplate}/>
            </div>
            <SendInvoicePanel preview={preview} onSent={(token)=>{ setInvoices(p=>p.map(i=>i.id===preview.id?{...i,status:"sent",portalToken:token}:i)); }} isMobile={true} hasPayfast={!!user?.payfastMerchantId}/>
          </div>
          {/* Sticky action bar */}
          <div style={{flexShrink:0,background:C.surface,borderTop:`1px solid ${C.border}`,padding:"12px 16px",display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            {preview.status !== "paid" && preview.status !== "written_off" && (
              <button onClick={()=>{setPreview(null);openPayModal(preview);}} style={{gridColumn:"1/-1",background:C.green,color:"#fff",border:"none",borderRadius:12,padding:"14px",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>✓ {preview.status==="partial"?"Top Up Payment":"Record Payment"}</button>
            )}
            <button onClick={()=>{setPreview(null);setEditInv({...preview});}} style={{background:C.goldLt,color:C.gold,border:`1px solid ${C.gold}30`,borderRadius:12,padding:"13px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Amend</button>
            <button onClick={printInvoice} style={{background:C.accent,color:"#fff",border:"none",borderRadius:12,padding:"13px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Print / PDF</button>
            <button onClick={async()=>{if(!window.confirm(`Delete ${preview.id}?`))return;setInvoices(p=>p.filter(i=>i.id!==preview.id));setPreview(null);try{await api(`/invoices/${preview._dbId||preview.id}`,{method:"DELETE"});}catch(e){}}} style={{background:C.redLt,color:C.red,border:`1px solid ${C.red}30`,borderRadius:12,padding:"13px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Delete</button>
            <button onClick={()=>setPreview(null)} style={{background:"transparent",border:`1px solid ${C.border}`,borderRadius:12,padding:"13px",fontSize:13,cursor:"pointer",fontFamily:"inherit",color:C.inkMid}}>Close</button>
          </div>
        </div>
      )}

      {/* ── DESKTOP: floating modal ── */}
      {preview && !isMobile && (
        <div style={{position:"fixed",inset:0,background:"#00000060",zIndex:100,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={() => setPreview(null)}>
          <div style={{background:"#fff",borderRadius:20,padding:40,width:560,maxHeight:"80vh",overflow:"auto"}} onClick={e => e.stopPropagation()}>
            <div id="invoice-preview-content">
            <InvoiceDocument type="invoice" doc={preview} user={user} tmpl={docTemplate}/>
            </div>
            <SendInvoicePanel preview={preview} onSent={(token)=>{ setInvoices(p=>p.map(i=>i.id===preview.id?{...i,status:"sent",portalToken:token}:i)); }} isMobile={false} hasPayfast={!!user?.payfastMerchantId}/>
            <div style={{display:"flex",gap:8,marginTop:16,flexWrap:"wrap"}}>
              {preview.status !== "paid" && preview.status !== "written_off" && (
                <button onClick={() => { setPreview(null); openPayModal(preview); }} style={{flex:1,minWidth:140,background:C.green,color:"#fff",border:"none",borderRadius:10,padding:"11px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>✓ Record Payment</button>
              )}
              <button onClick={() => { setPreview(null); setEditInv({...preview}); }} style={{flex:1,background:C.goldLt,color:C.gold,border:`1px solid ${C.gold}40`,borderRadius:10,padding:"11px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Amend</button>
              <button onClick={printInvoice} style={{flex:1,background:C.accent,color:"#fff",border:"none",borderRadius:10,padding:"11px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Print / PDF</button>
              <button onClick={() => setPreview(null)} style={{flex:1,background:"transparent",border:`1px solid ${C.border}`,borderRadius:10,padding:"11px",fontSize:13,cursor:"pointer",fontFamily:"inherit",color:C.inkMid}}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Amend Modal */}
      {editInv && (
        <div style={{position:"fixed",inset:0,background:"#00000060",zIndex:100,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={() => setEditInv(null)}>
          <div style={{background:C.surface,borderRadius:20,padding:32,width:580,maxHeight:"90vh",overflow:"auto"}} onClick={e=>e.stopPropagation()}>
            <h3 style={{fontFamily:"serif",fontSize:22,color:C.ink,margin:"0 0 20px"}}>Amend Invoice — {editInv.id}</h3>
            <InvFormFields data={editInv} onChange={setEditInv} customers={customers}/>
            <div style={{marginBottom:16}}>
              <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Status</label>
              <select value={editInv.status} onChange={e=>setEditInv({...editInv,status:e.target.value})} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}>
                <option value="sent">Pending</option>
                <option value="paid">Paid</option>
                <option value="overdue">Overdue</option>
                <option value="draft">Draft</option>
              </select>
            </div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={handleAmend} style={{background:C.accent,color:"#fff",border:"none",borderRadius:10,padding:"10px 24px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Save Changes</button>
              <button onClick={() => setEditInv(null)} style={{background:"transparent",color:C.inkMid,border:`1px solid ${C.border}`,borderRadius:10,padding:"10px 20px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Record Payment Modal */}
      {payModal && (
        <div style={{position:"fixed",inset:0,background:"#00000060",zIndex:100,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={()=>setPayModal(null)}>
          <div style={{background:C.surface,borderRadius:20,padding:32,width:460}} onClick={e=>e.stopPropagation()}>
            <h3 style={{fontFamily:"serif",fontSize:22,color:C.ink,margin:"0 0 4px"}}>Record Payment</h3>
            <p style={{fontSize:13,color:C.inkMid,marginBottom:payModal.alreadyPaid>0?12:24}}>{payModal.inv.id} — {payModal.inv.client}</p>
            {payModal.alreadyPaid > 0 && (
              <div style={{background:"#fef3c7",border:"1px solid #f59e0b",borderRadius:8,padding:"10px 14px",marginBottom:16,fontSize:12,color:"#92400e"}}>
                <strong>Already received:</strong> {fmt(payModal.alreadyPaid)}<br/>
                <strong>Outstanding balance:</strong> {fmt(Math.max(0,toZar(payModal.inv)-payModal.alreadyPaid))}
              </div>
            )}
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              <div>
                <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>ZAR Amount Received</label>
                <input type="number" min="0" step="0.01" value={payModal.zarAmt}
                  onChange={e=>setPayModal(p=>({...p,zarAmt:e.target.value}))}
                  style={{width:"100%",padding:"11px 14px",border:`1.5px solid ${C.border}`,borderRadius:10,fontSize:15,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}/>
                {payModal.inv.currency&&payModal.inv.currency!=="ZAR" && (
                  <div style={{fontSize:11,color:C.blue,marginTop:4}}>Invoice is {payModal.inv.currency} — enter the ZAR actually received into your bank account</div>
                )}
                {payModal.inv.currency&&payModal.inv.currency!=="ZAR" && (
                  <label style={{display:"flex",alignItems:"center",gap:8,marginTop:10,fontSize:12,color:C.ink,cursor:"pointer"}}>
                    <input type="checkbox" checked={payModal.settles!==false} onChange={e=>setPayModal(p=>({...p,settles:e.target.checked}))}/>
                    This payment settles the invoice (any difference is booked as exchange gain/loss)
                  </label>
                )}
              </div>
              <div>
                <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Payment Date</label>
                <input type="date" value={payModal.payDate}
                  onChange={e=>setPayModal(p=>({...p,payDate:e.target.value}))}
                  style={{width:"100%",padding:"11px 14px",border:`1.5px solid ${C.border}`,borderRadius:10,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}/>
              </div>
              <div>
                <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Reference / Notes (optional)</label>
                <input placeholder="e.g. EFT Ref 12345" value={payModal.ref}
                  onChange={e=>setPayModal(p=>({...p,ref:e.target.value}))}
                  style={{width:"100%",padding:"11px 14px",border:`1.5px solid ${C.border}`,borderRadius:10,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}/>
              </div>
            </div>
            <div style={{display:"flex",gap:8,marginTop:24}}>
              <button onClick={handleRecordPayment} style={{flex:1,background:C.green,color:"#fff",border:"none",borderRadius:10,padding:"12px",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>✓ Record Payment</button>
              <button onClick={()=>setPayModal(null)} style={{background:"transparent",color:C.inkMid,border:`1px solid ${C.border}`,borderRadius:10,padding:"12px 20px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── MOBILE: card list ── */}
      {isMobile ? (
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {displayedInvoices.length === 0 && (
            <div style={{padding:"32px",textAlign:"center",color:C.inkMid,fontSize:13,background:C.surface,borderRadius:16,border:`1px solid ${C.border}`}}>No {filterStatus||""} invoices found.</div>
          )}
          {displayedInvoices.map(inv => (
            <div key={inv.id} onClick={()=>setPreview(inv)} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:14,padding:"16px",cursor:"pointer",WebkitTapHighlightColor:"transparent"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                <div style={{fontWeight:800,fontSize:15,color:C.accent}}>{inv.id}</div>
                <StatusBadge status={isOverdue(inv)?"overdue":inv.status}/>
              </div>
              <div style={{fontWeight:600,fontSize:14,color:C.ink,marginBottom:2}}>{inv.client}</div>
              <div style={{fontSize:12,color:C.inkMid,marginBottom:10,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{inv.desc}</div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline"}}>
                <div style={{fontWeight:800,fontSize:16,color:C.ink}}>
                  {inv.status === "partial"
                    ? filterStatus === "paid_this_month" ? fmt(cashThisMonth(inv)) : fmt(owedZar(inv))
                    : inv.status === "paid" && inv.paid_amount_zar
                    ? fmt(inv.paid_amount_zar)
                    : inv.currency && inv.currency!=="ZAR" ? fmt((inv.amount||0)*(inv.exchange_rate||1)) : fmt(inv.amount||0)}
                </div>
                <div style={{fontSize:11,color:isOverdue(inv)?C.red:C.inkMid}}>Due {fmtDate(inv.due)}</div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* ── DESKTOP: table ── */
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,overflow:"hidden"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
            <thead>
              <tr style={{background:C.bg,borderBottom:`1px solid ${C.border}`}}>
                {["Invoice #","Client","Description","Amount","Date","Due","Status","Actions"].map(h => <th key={h} style={{textAlign:"left",padding:"12px 16px",fontSize:10,color:C.inkMid,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase"}}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {displayedInvoices.length === 0 && (
                <tr><td colSpan={8} style={{padding:"32px",textAlign:"center",color:C.inkMid,fontSize:13}}>No {filterStatus} invoices found.</td></tr>
              )}
              {displayedInvoices.map((inv) => (
                <tr key={inv.id} style={{borderBottom:`1px solid ${C.border}30`}}>
                  <td style={{padding:"13px 16px",fontWeight:700,color:C.accent,cursor:"pointer"}} onClick={()=>setPreview(inv)}>{inv.id}</td>
                  <td style={{padding:"13px 16px",fontWeight:500}}>{inv.client}</td>
                  <td style={{padding:"13px 16px",color:C.inkMid,fontSize:12}}>{inv.desc}</td>
                  <td style={{padding:"13px 16px",fontWeight:700}}>
                    {inv.status === "partial"
                      ? filterStatus === "paid_this_month"
                        ? <div>{fmt(cashThisMonth(inv))}<div style={{fontSize:10,color:C.green,fontWeight:400,marginTop:2}}>received</div></div>
                        : <div>{fmt(owedZar(inv))}<div style={{fontSize:10,color:"#b45309",fontWeight:400,marginTop:2}}>outstanding</div></div>
                      : inv.status === "paid" && inv.paid_amount_zar
                      ? fmt(inv.paid_amount_zar)
                      : inv.currency && inv.currency!=="ZAR"
                      ? <>{fmt((inv.amount||0)*(inv.exchange_rate||1))}<div style={{fontSize:10,color:C.blue,fontWeight:400,marginTop:2}}>{fmtCurrency(inv.amount,inv.currency)}</div></>
                      : fmt(inv.amount||0)}
                  </td>
                  <td style={{padding:"13px 16px",color:C.inkMid}}>{fmtDate(inv.date)}</td>
                  <td style={{padding:"13px 16px",color:isOverdue(inv)?C.red:C.inkMid}}>{fmtDate(inv.due)}</td>
                  <td style={{padding:"13px 16px"}}>
                    {inv.status === "paid" && isCurrentMonth(inv)
                      ? <span style={{display:"inline-flex",alignItems:"center",gap:4,background:C.greenLt,color:C.green,border:`1.5px solid ${C.green}`,borderRadius:6,padding:"3px 10px",fontSize:11,fontWeight:800,letterSpacing:0.5,textTransform:"uppercase",boxShadow:`0 0 0 2px ${C.green}22`}}>✓ Paid</span>
                      : inv.status === "paid"
                      ? <span style={{display:"inline-flex",alignItems:"center",gap:4,background:C.surface,color:C.inkMid,border:`1px solid ${C.border}`,borderRadius:6,padding:"3px 10px",fontSize:11,fontWeight:600,letterSpacing:0.3}}>📦 Archived</span>
                      : <StatusBadge status={isOverdue(inv)?"overdue":inv.status}/>
                    }
                  </td>
                  <td style={{padding:"13px 16px"}}>
                    <div style={{display:"flex",gap:6}}>
                      <button onClick={() => setPreview(inv)} style={{background:C.blueLt,color:C.blue,border:"none",borderRadius:6,padding:"5px 10px",fontSize:11,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>View</button>
                      <button onClick={() => setEditInv({...inv})} style={{background:C.goldLt,color:C.gold,border:"none",borderRadius:6,padding:"5px 10px",fontSize:11,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>Amend</button>
                      {inv.status !== "paid" && inv.status !== "written_off" && inv.status !== "draft" && (
                        <button onClick={async()=>{ const reason=window.prompt(`Write off ${inv.id} as an irrecoverable bad debt?\n\nThe VAT on it is deducted on your VAT201.\nEnter the reason (kept as evidence for SARS):`); if(!reason||!reason.trim()) return; try{ await api(`/invoices/${inv._dbId || inv.id}/write-off`,{method:"POST",body:JSON.stringify({reason:reason.trim()})}); setInvoices(p=>p.map(i=>i.id===inv.id?{...i,status:"written_off"}:i)); }catch(e){ window.alert(e.message); } }} style={{background:C.redLt,color:C.red,border:"none",borderRadius:6,padding:"5px 10px",fontSize:11,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>Write off</button>
                      )}
                      {inv.status === "written_off" && (
                        <button onClick={async()=>{ const amt=window.prompt(`Record money received on written-off ${inv.id}.\nAmount received (incl. VAT) - leave blank for the full invoice:`); if(amt===null) return; try{ await api(`/invoices/${inv._dbId || inv.id}/bad-debt-recovery`,{method:"POST",body:JSON.stringify(amt.trim()?{amount:+amt}:{})}); window.location.reload(); }catch(e){ window.alert(e.message); } }} style={{background:C.greenLt,color:C.green,border:"none",borderRadius:6,padding:"5px 10px",fontSize:11,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>Record Recovery</button>
                      )}
                      {inv.status !== "paid" && inv.status !== "written_off" && (
                        <button onClick={() => openPayModal(inv)} style={{background:C.greenLt,color:C.green,border:"none",borderRadius:6,padding:"5px 10px",fontSize:11,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>Record Payment</button>
                      )}
                      <button onClick={async()=>{ if(!window.confirm(`Delete ${inv.id}?`))return; setInvoices(p=>p.filter(i=>i.id!==inv.id)); try{await api(`/invoices/${inv._dbId || inv.id}`,{method:"DELETE"});}catch(e){} }} style={{background:C.redLt,color:C.red,border:"none",borderRadius:6,padding:"5px 10px",fontSize:11,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {filterStatus !== "paid" && (
        <div style={{textAlign:"center",marginTop:8,marginBottom:4}}>
          <button onClick={()=>toggleFilter("paid")} style={{background:"none",border:"none",color:C.inkMid,fontSize:12,cursor:"pointer",fontFamily:"inherit",textDecoration:"underline",padding:"4px 8px"}}>
            📦 View archived invoices
          </button>
        </div>
      )}
      </>}
      {invTab==="recurring" && <RecurringInvoicesTab/>}
      {invTab==="credit_notes" && <CreditNotesTab/>}
    </div>
  );
}
// ── EXPENSES ──────────────────────────────────────────────────────────────────
// An expense is "posted" (categorised) when its category is a COA code e.g. "6220 - Electricity and Water"
const isPosted = cat => cat && /^\d{4}/.test(cat);

function Expenses({live = {}}) {
  const liveExpenses = live.expenses;
  const [expenses, setExpenses] = useState(MOCK_EXPENSES);
  const [pendingCats, setPendingCats] = useState({}); // {id: selectedCategory}
  const [view, setView] = useState("unposted"); // "unposted" | "posted"
  const [viewExp,  setViewExp]  = useState(null);
  const [editExp,  setEditExp]  = useState(null);

  // Merged COA: DEFAULT_COA + any custom accounts added via Chart of Accounts
  const [coaAccounts, setCoaAccounts] = useState(DEFAULT_COA);
  useEffect(() => {
    api("/coa/").then(rows => {
      if (!rows || !rows.length) return;
      const deletedCodes = new Set(rows.filter(r => r.is_deleted).map(r => r.code));
      const defaultCodes = new Set(DEFAULT_COA.map(a => a.code));
      const additions = rows.filter(r => !r.is_deleted && !defaultCodes.has(r.code));
      const merged = [...DEFAULT_COA.filter(a => !deletedCodes.has(a.code)), ...additions]
        .sort((a, b) => a.code.localeCompare(b.code));
      setCoaAccounts(merged);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const expRows = liveExpenses && liveExpenses.length > 0
      ? liveExpenses.map(e => ({...e, date: e.expense_date || e.date, desc: e.description, vendor: e.vendor, amount: e.amount, vat_amount: e.vat_amount||0, category: e.category || "", id: `EXP-${String(e.id).padStart(3,"0")}`}))
      : [];
    // Fetch depreciation entries and merge as auto-posted expense rows
    api("/fixed-assets/schedule").then(deprEntries => {
      const deprRows = (deprEntries||[]).map(d => ({
        id: `DEP-${d.id}`,
        date: d.period ? d.period+"-01" : null,
        desc: `${d.asset_name} — Monthly Depreciation`,
        vendor: "Fixed Assets",
        amount: d.amount||0,
        vat_amount: 0,
        category: "5800 - Depreciation",
        _readonly: true,
      }));
      const merged = [...expRows, ...deprRows].sort((a,b)=>new Date(b.date||0)-new Date(a.date||0));
      if (merged.length > 0) setExpenses(merged);
    }).catch(()=>{ if (expRows.length > 0) setExpenses(expRows); });
  }, [liveExpenses]);

  const [showNew,    setShowNew]    = useState(false);
  const [form,       setForm]       = useState({vendor:"",amount:"",category:"",desc:"",vatApplicable:true});
  const [autoNotice, setAutoNotice] = useState("");
  const [saving,     setSaving]     = useState(false);

  const unposted = expenses.filter(e => !isPosted(e.category));
  const posted   = expenses.filter(e => isPosted(e.category));
  const displayed = view === "unposted" ? unposted : posted;
  const total = displayed.reduce((s,e) => s + (e.amount||0) - (e.vat_amount||0), 0);

  // ── Auto-categorise engine ────────────────────────────────────────
  // Build vendor→category map from posted expenses
  const buildMemory = (expList) => {
    const mem = {};
    expList.filter(e => isPosted(e.category)).forEach(e => {
      const key = e.vendor.trim().toLowerCase();
      mem[key] = e.category; // last known category wins
      // also index first word of description
      if (e.desc) {
        const descKey = e.desc.trim().toLowerCase().split(" ").slice(0,3).join(" ");
        if (!mem[descKey]) mem[descKey] = e.category;
      }
    });
    return mem;
  };

  const findSuggestion = (exp, memory) => {
    const vendor = exp.vendor.trim().toLowerCase();
    if (memory[vendor]) return memory[vendor]; // exact vendor match
    // partial vendor match
    const partialKey = Object.keys(memory).find(k => vendor.includes(k) || k.includes(vendor));
    if (partialKey) return memory[partialKey];
    // description match
    if (exp.desc) {
      const descKey = exp.desc.trim().toLowerCase().split(" ").slice(0,3).join(" ");
      if (memory[descKey]) return memory[descKey];
    }
    return null;
  };

  // Run auto-categorise when expenses load — but NOT while form is open (avoids focus loss)
  useEffect(() => {
    if (showNew) return;
    const memory = buildMemory(expenses);
    if (Object.keys(memory).length === 0) return;
    const toPost = [];
    const suggestions = {};
    expenses.filter(e => !isPosted(e.category)).forEach(e => {
      const suggestion = findSuggestion(e, memory);
      if (suggestion) {
        suggestions[e.id] = suggestion;
        toPost.push({...e, category: suggestion});
      }
    });
    if (toPost.length === 0) return;
    // Auto-post exact matches and pre-fill suggestions
    setPendingCats(prev => ({...prev, ...suggestions}));
    // Auto-post all matched
    setExpenses(prev => prev.map(e => {
      const match = toPost.find(t => t.id === e.id);
      return match ? {...e, category: match.category} : e;
    }));
    setAutoNotice(`Auto-categorised ${toPost.length} expense${toPost.length>1?"s":""} based on past history.`);
    setTimeout(() => setAutoNotice(""), 5000);
    // Save to backend
    toPost.forEach(e => {
      api(`/expenses/${e.id}`, { method:"PUT", body: JSON.stringify({category: e.category}) }).catch(()=>{});
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expenses.length, posted.length]);

  const handleAmendExp = async () => {
    setExpenses(prev => prev.map(e => e.id === editExp.id ? {...editExp} : e));
    setEditExp(null);
    try {
      await api(`/expenses/${editExp.id}`, { method:"PUT", body: JSON.stringify({ vendor:editExp.vendor, description:editExp.desc, amount:+editExp.amount, category:editExp.category, expense_date:editExp.date }) });
      if (live && live.reload) live.reload();
    } catch(e) { console.warn("Amend expense failed:", e.message); }
  };

  const handlePost = async (exp) => {
    const cat = pendingCats[exp.id];
    if (!cat || !isPosted(cat)) return;
    setExpenses(prev => prev.map(e => e.id === exp.id ? {...e, category: cat} : e));
    setPendingCats(prev => { const n = {...prev}; delete n[exp.id]; return n; });
    try {
      await api(`/expenses/${exp.id}`, { method:"PUT", body: JSON.stringify({category: cat}) });
      if (live && live.reload) live.reload();
    } catch(err) { console.warn("Category save failed:", err.message); }
  };

  const handleAdd = async () => {
    if(!form.vendor||!form.amount){alert("Vendor and amount are required.");return;}
    setSaving(true);
    try {
      await api("/expenses/", { method:"POST", body: JSON.stringify({ vendor:form.vendor, description:form.desc, amount:+form.amount, vat_applicable:form.vatApplicable, vat_use:form.vatUse||null, category:form.category, expense_date:new Date().toISOString().slice(0,10), is_on_credit:form.onCredit||false }) });
      if (live && live.reload) live.reload();
      setShowNew(false);
      setForm({vendor:"",amount:"",category:"",desc:"",vatApplicable:true,onCredit:false});
    } catch(err) { alert("Failed to save expense. Please try again."); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <SectionHeader title="Expenses" sub="Categorise expenses to post them to the Income Statement" action="+ Add Expense" onAction={() => setShowNew(true)}/>

      {/* Summary bar */}
      <div style={{display:"flex",gap:12,marginBottom:16}}>
        <KPI label="To Categorise"     value={unposted.length} sub={fmt(unposted.reduce((s,e)=>s+(e.amount||0)-(e.vat_amount||0),0))} color={C.gold}  icon="⏳" onClick={()=>setView("unposted")} active={view==="unposted"}/>
        <KPI label="Posted to Accounts" value={posted.length}  sub={fmt(posted.reduce((s,e)=>s+(e.amount||0)-(e.vat_amount||0),0))}  color={C.green} icon="✅" onClick={()=>setView("posted")}   active={view==="posted"}/>
      </div>
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16,padding:"10px 16px",background:view==="unposted"?C.goldLt:C.greenLt,border:`1px solid ${view==="unposted"?C.gold:C.green}30`,borderRadius:10}}>
        <span style={{fontSize:13,fontWeight:600,color:view==="unposted"?C.gold:C.green}}>
          {view==="unposted"?"Showing uncategorised expenses":"Showing posted expenses"} — {displayed.length} record{displayed.length!==1?"s":""}, {fmt(total)}
        </span>
        <button onClick={()=>setView(view==="unposted"?"posted":"unposted")} style={{marginLeft:"auto",background:"none",border:`1px solid ${view==="unposted"?C.gold:C.green}40`,borderRadius:6,padding:"4px 10px",fontSize:12,color:view==="unposted"?C.gold:C.green,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>
          Switch to {view==="unposted"?"Posted":"Uncategorised"}
        </button>
      </div>

      {showNew && (
        <div style={{background:C.surface,border:`2px solid ${C.accent}`,borderRadius:16,padding:24,marginBottom:20}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <h3 style={{fontFamily:"serif",margin:0,color:C.ink}}>Add Expense</h3>
            <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",padding:"8px 14px",borderRadius:10,border:`1px solid ${C.border}`,background:C.bg,fontSize:12,color:C.blue,fontWeight:600}}>
              📷 Scan Receipt
              <input type="file" accept="image/*" capture="camera" style={{display:"none"}} onChange={async e=>{
                const file = e.target.files[0];
                if(!file) return;
                const reader = new FileReader();
                reader.onload = async ev => {
                  // Show receipt preview
                  setForm(f=>({...f,receiptImage:ev.target.result}));
                  // Try OCR via backend
                  try {
                    const res = await api("/expenses/scan-receipt",{method:"POST",body:JSON.stringify({image:ev.target.result.split(",")[1]})});
                    if(res.vendor)  setForm(f=>({...f,vendor:res.vendor}));
                    if(res.amount)  setForm(f=>({...f,amount:String(res.amount)}));
                    if(res.date)    setForm(f=>({...f,date:res.date}));
                    if(res.desc)    setForm(f=>({...f,desc:res.desc}));
                  } catch(err) { console.warn("OCR failed:",err.message); }
                };
                reader.readAsDataURL(file);
              }}/>
            </label>
          </div>
          {form.receiptImage && (
            <div style={{marginBottom:12,position:"relative"}}>
              <img src={form.receiptImage} alt="Receipt" style={{width:"100%",maxHeight:180,objectFit:"cover",borderRadius:8,border:`1px solid ${C.border}`}}/>
              <button onClick={()=>setForm(f=>({...f,receiptImage:null}))} style={{position:"absolute",top:6,right:6,background:"rgba(0,0,0,0.6)",color:"#fff",border:"none",borderRadius:"50%",width:24,height:24,cursor:"pointer",fontSize:12}}>✕</button>
              <div style={{background:C.greenLt,borderRadius:6,padding:"6px 10px",marginTop:6,fontSize:11,color:C.green}}>✓ Receipt captured — fields pre-filled where possible</div>
            </div>
          )}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
            <div>
              <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Vendor</label>
              <input placeholder="Eskom" value={form.vendor} onChange={e => setForm(f=>({...f,vendor:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}/>
            </div>
            <div>
              <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Amount (ZAR excl. VAT)</label>
              <input placeholder="1500" value={form.amount} onChange={e => setForm(f=>({...f,amount:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}/>
            </div>
            <div>
              <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Description</label>
              <input placeholder="Monthly bill" value={form.desc} onChange={e => setForm(f=>({...f,desc:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}/>
            </div>
            <div>
              <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Account (optional)</label>
              <select value={form.category} onChange={e => setForm({...form,category:e.target.value})} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}>
                <option value="">-- Categorise later --</option>
                {EXPENSE_COA_GROUPS.map(group => (
                  <optgroup key={group} label={group}>
                    {coaAccounts.filter(a => a.group === group && a.type === "Detail").map(a => (
                      <option key={a.code} value={`${a.code} - ${a.name}`}>{a.code} - {a.name}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:12,flexWrap:"wrap"}}>
            <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",fontSize:13,color:C.ink}}>
              <input type="checkbox" checked={form.vatApplicable} onChange={e=>setForm({...form,vatApplicable:e.target.checked})} style={{width:16,height:16,cursor:"pointer"}}/>
              Apply VAT @ 15%
            </label>
            {form.vatApplicable && <label style={{display:"flex",alignItems:"center",gap:8,fontSize:13,color:C.ink}} title="Only matters if the business also makes exempt supplies (e.g. residential rent, financial services)">
              VAT claim
              <select value={form.vatUse||""} onChange={e=>setForm({...form,vatUse:e.target.value})} style={{padding:"6px 8px",border:"1px solid "+C.border,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink}}>
                <option value="">Shared (apportioned)</option>
                <option value="taxable">Used for taxable supplies only</option>
                <option value="exempt">Used for exempt supplies only</option>
                <option value="denied">Not claimable (e.g. motor car purchase)</option>
              </select>
            </label>}
            <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",fontSize:13,color:C.ink}}>
              <input type="checkbox" checked={form.onCredit||false} onChange={e=>setForm({...form,onCredit:e.target.checked})} style={{width:16,height:16,cursor:"pointer"}}/>
              On Credit
            </label>
            {form.onCredit && (
              <span style={{fontSize:11,color:C.inkMid,background:C.surface,border:`1px solid ${C.border}`,borderRadius:6,padding:"3px 8px"}}>
                Posts to Accounts Payable — record payment separately when cash leaves the bank
              </span>
            )}
            {form.amount && (
              <div style={{marginLeft:"auto",display:"flex",gap:16,fontSize:12}}>
                <span style={{color:C.inkMid}}>Excl. VAT: <strong style={{color:C.ink}}>{fmt(+form.amount||0)}</strong></span>
                {form.vatApplicable && <span style={{color:C.inkMid}}>VAT: <strong style={{color:C.gold}}>{fmt((+form.amount||0)*0.15)}</strong></span>}
                <span style={{color:C.inkMid}}>Total: <strong style={{color:C.red}}>{fmt((+form.amount||0)*(form.vatApplicable?1.15:1))}</strong></span>
              </div>
            )}
          </div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={handleAdd} disabled={saving} style={{background:C.accent,color:"#fff",border:"none",borderRadius:10,padding:"10px 20px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",opacity:saving?0.6:1}}>{saving?"Saving...":"Add Expense"}</button>
            <button onClick={() => setShowNew(false)} style={{background:"transparent",color:C.inkMid,border:`1px solid ${C.border}`,borderRadius:10,padding:"10px 20px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
          </div>
        </div>
      )}

      {/* View Expense Modal */}
      {viewExp && (
        <div style={{position:"fixed",inset:0,background:"#00000060",zIndex:100,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={() => setViewExp(null)}>
          <div style={{background:C.surface,borderRadius:20,padding:32,width:480}} onClick={e=>e.stopPropagation()}>
            <h3 style={{fontFamily:"serif",fontSize:22,color:C.ink,margin:"0 0 20px"}}>Expense — {viewExp.id}</h3>
            {[["Vendor",viewExp.vendor],["Description",viewExp.desc],["Date",fmtDate(viewExp.date)],["Amount (excl. VAT)",fmt((viewExp.amount||0)-(viewExp.vat_amount||0))],["VAT",fmt(viewExp.vat_amount||0)],["Total (incl. VAT)",fmt(viewExp.amount||0)],["Account",viewExp.category||"Uncategorised"]].map(([l,v])=>(
              <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"10px 0",borderBottom:`1px solid ${C.border}30`,fontSize:13}}>
                <span style={{color:C.inkMid,fontWeight:600}}>{l}</span>
                <span style={{color:C.ink,fontWeight:l==="Amount"?700:400}}>{v}</span>
              </div>
            ))}
            <div style={{display:"flex",gap:8,marginTop:20}}>
              <button onClick={() => { setViewExp(null); setEditExp({...viewExp}); }} style={{flex:1,background:C.goldLt,color:C.gold,border:`1px solid ${C.gold}40`,borderRadius:10,padding:"10px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Amend</button>
              <button onClick={() => setViewExp(null)} style={{flex:1,background:"transparent",border:`1px solid ${C.border}`,borderRadius:10,padding:"10px",fontSize:13,cursor:"pointer",fontFamily:"inherit",color:C.inkMid}}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Amend Expense Modal */}
      {editExp && (
        <div style={{position:"fixed",inset:0,background:"#00000060",zIndex:100,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={() => setEditExp(null)}>
          <div style={{background:C.surface,borderRadius:20,padding:32,width:520}} onClick={e=>e.stopPropagation()}>
            <h3 style={{fontFamily:"serif",fontSize:22,color:C.ink,margin:"0 0 20px"}}>Amend Expense — {editExp.id}</h3>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
              {[{l:"Vendor",k:"vendor",t:"text"},{l:"Amount (ZAR)",k:"amount",t:"number"},{l:"Description",k:"desc",t:"text"},{l:"Date",k:"date",t:"date"}].map(f=>(
                <div key={f.k}>
                  <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>{f.l}</label>
                  <input type={f.t} value={editExp[f.k]||""} onChange={e=>setEditExp({...editExp,[f.k]:e.target.value})} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}/>
                </div>
              ))}
            </div>
            <div style={{marginBottom:16}}>
              <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Account</label>
              <select value={editExp.category||""} onChange={e=>setEditExp({...editExp,category:e.target.value})} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}>
                <option value="">-- Uncategorised --</option>
                {EXPENSE_COA_GROUPS.map(group=>(
                  <optgroup key={group} label={group}>
                    {coaAccounts.filter(a=>a.group===group&&a.type==="Detail").map(a=>(
                      <option key={a.code} value={`${a.code} - ${a.name}`}>{a.code} - {a.name}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={handleAmendExp} style={{background:C.accent,color:"#fff",border:"none",borderRadius:10,padding:"10px 24px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Save Changes</button>
              <button onClick={() => setEditExp(null)} style={{background:"transparent",color:C.inkMid,border:`1px solid ${C.border}`,borderRadius:10,padding:"10px 20px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Auto-categorise notice */}
      {autoNotice && (
        <div style={{background:C.greenLt,border:`1px solid ${C.green}40`,borderRadius:10,padding:"10px 16px",marginBottom:14,fontSize:13,color:C.green,display:"flex",alignItems:"center",gap:8}}>
          <span>✨</span> {autoNotice}
        </div>
      )}

      {/* View toggle */}
      <div style={{display:"flex",gap:8,marginBottom:16,alignItems:"center"}}>
        {[["unposted","⏳ Needs Categorising"],["posted","✅ Posted to Accounts"]].map(([v,l]) => (
          <button key={v} onClick={()=>setView(v)} style={{padding:"7px 16px",borderRadius:20,border:`1px solid ${view===v?C.accent:C.border}`,background:view===v?C.accentLt:"transparent",color:view===v?C.accent:C.inkMid,fontSize:12,fontWeight:view===v?700:400,cursor:"pointer",fontFamily:"inherit"}}>{l}</button>
        ))}
        <div style={{marginLeft:"auto",fontSize:13,fontWeight:700,color:C.ink}}>Total: {fmt(total)}</div>
      </div>

      <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
        {displayed.length === 0 ? (
          <div style={{padding:40,textAlign:"center",color:C.inkMid,fontSize:13}}>
            {view==="unposted" ? "All expenses have been categorised." : "No posted expenses yet."}
          </div>
        ) : (
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
            <thead>
              <tr style={{background:C.bg,borderBottom:`1px solid ${C.border}`}}>
                {["#","Vendor","Description","Date","Amount", view==="unposted"?"Assign Account":"Account","Actions"].map(h => <th key={h} style={{textAlign:"left",padding:"12px 16px",fontSize:10,color:C.inkMid,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase"}}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {displayed.map((exp) => (
                <tr key={exp.id} style={{borderBottom:`1px solid ${C.border}30`}}>
                  <td style={{padding:"13px 16px",fontWeight:600,color:C.inkMid,fontSize:11}}>{exp.id}</td>
                  <td style={{padding:"13px 16px",fontWeight:600}}>{exp.vendor}</td>
                  <td style={{padding:"13px 16px",color:C.inkMid,fontSize:12}}>{exp.desc}</td>
                  <td style={{padding:"13px 16px",color:C.inkMid}}>{fmtDate(exp.date)}</td>
                  <td style={{padding:"13px 16px",fontWeight:700,color:C.red}}>{fmt((exp.amount||0)-(exp.vat_amount||0))}</td>
                  <td style={{padding:"13px 16px"}}>
                    {view === "unposted" ? (
                      <select value={pendingCats[exp.id] || ""} onChange={e => setPendingCats(p=>({...p,[exp.id]:e.target.value}))}
                        style={{padding:"5px 8px",border:`1px solid ${C.border}`,borderRadius:6,fontSize:11,fontFamily:"inherit",background:C.bg,color:C.ink,maxWidth:220}}>
                        <option value="">-- Select Account --</option>
                        {EXPENSE_COA_GROUPS.map(group => (
                          <optgroup key={group} label={group}>
                            {coaAccounts.filter(a => a.group === group && a.type === "Detail").map(a => (
                              <option key={a.code} value={`${a.code} - ${a.name}`}>{a.code} - {a.name}</option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    ) : (
                      <Badge label={exp.category} color={C.green} bg={C.greenLt}/>
                    )}
                  </td>
                  <td style={{padding:"13px 16px"}}>
                    <div style={{display:"flex",gap:6}}>
                      <button onClick={() => setViewExp(exp)} style={{background:C.blueLt,color:C.blue,border:"none",borderRadius:6,padding:"5px 10px",fontSize:11,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>View</button>
                      <button onClick={() => setEditExp({...exp})} style={{background:C.goldLt,color:C.gold,border:"none",borderRadius:6,padding:"5px 10px",fontSize:11,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>Amend</button>
                      {view === "unposted" && (
                        <button onClick={() => handlePost(exp)}
                          disabled={!isPosted(pendingCats[exp.id])}
                          style={{background:isPosted(pendingCats[exp.id])?C.green:"transparent",color:isPosted(pendingCats[exp.id])?"#fff":C.inkDim,border:`1px solid ${isPosted(pendingCats[exp.id])?C.green:C.border}`,borderRadius:6,padding:"5px 10px",fontSize:11,cursor:isPosted(pendingCats[exp.id])?"pointer":"default",fontFamily:"inherit",fontWeight:700}}>
                          Post →
                        </button>
                      )}
                      <button onClick={async()=>{ if(!window.confirm(`Delete ${exp.id}?`))return; setExpenses(p=>p.filter(e=>e.id!==exp.id)); try{await api(`/expenses/${exp.id}`,{method:"DELETE"});}catch(e){} }} style={{background:C.redLt,color:C.red,border:"none",borderRadius:6,padding:"5px 10px",fontSize:11,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}


// ── PAYSLIP MODAL ─────────────────────────────────────────────────────────────
function PayslipModal({employee, payroll, period, company, logoUrl, onClose}) {
  const p = payroll;
  const today = new Date().toLocaleDateString("en-ZA", {day:"2-digit", month:"long", year:"numeric"});
  // Payment date: salaried → last day of current month; hourly / fixed pay → 15th
  const _empType = (employee.employment_type||"").toLowerCase().replace(/[\s_]/g,"");
  const _isHourly = _empType === "hourlypaid" || _empType === "hourly";
  const _now = new Date();
  const _payD = _isHourly
    ? new Date(_now.getFullYear(), _now.getMonth(), 15)
    : new Date(_now.getFullYear(), _now.getMonth() + 1, 0); // last day
  const paymentDate = _payD.toLocaleDateString("en-ZA", {day:"2-digit", month:"long", year:"numeric"});

  const handleDownloadPdf = async () => {
    const periodKey = new Date().toISOString().slice(0,7);   // payslips are generated for the current month
    try{
      const resp = await fetch(`${BASE_URL}/payroll/payslips/${periodKey}/employee/${employee.id}/pdf`,{headers:{Authorization:`Bearer ${localStorage.getItem("zuzan_token")}`}});
      if(!resp.ok){
        const msg = await resp.text().catch(()=>"");
        let detail = msg; try{ detail = JSON.parse(msg).detail || msg; }catch(e){}
        alert(`Payslip PDF failed (${resp.status}): ${detail || "payslip not found for this month — run payroll first."}`);
        return;
      }
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `payslip_${(employee.name||"employee").replace(/\s+/g,"_")}_${periodKey}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(()=>URL.revokeObjectURL(a.href),1000);
    }catch(err){ alert(`Payslip PDF error: ${err.message}`); }
  };

  const handlePrint = () => {
    const printContent = document.getElementById("payslip-content").innerHTML;
    const win = window.open("", "_blank");
    if (!win) {
      alert("Please allow popups for this site to print payslips.");
      return;
    }
    win.document.write(`
      <html><head><title>Payslip - ${employee.name}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 40px; color: #1A1209; }
        .header { display: flex; justify-content: space-between; margin-bottom: 30px; border-bottom: 2px solid #C8401A; padding-bottom: 16px; }
        .logo { font-size: 28px; font-weight: 800; color: #C8401A; }
        .title { font-size: 20px; font-weight: 700; text-align: right; }
        .section { margin-bottom: 20px; }
        .section-title { font-size: 11px; font-weight: 700; color: #6B5E4E; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 10px; border-bottom: 1px solid #E8E0D5; padding-bottom: 4px; }
        .row { display: flex; justify-content: space-between; padding: 6px 0; font-size: 13px; border-bottom: 1px solid #f0ebe3; }
        .row.total { font-weight: 800; font-size: 15px; border-top: 2px solid #1A1209; border-bottom: none; margin-top: 8px; padding-top: 10px; }
        .green { color: #2A6B3C; }
        .red { color: #C82A2A; }
        .blue { color: #1A3A6B; }
        .footer { margin-top: 40px; font-size: 11px; color: #B5A898; border-top: 1px solid #E8E0D5; padding-top: 16px; }
        @media print { body { padding: 20px; } }
      </style></head><body>${printContent}</body></html>
    `);
    win.document.close();
    win.onload = () => {
      win.focus();
      win.print();
      win.close();
    };
  };

  return (
    <div style={{position:"fixed",inset:0,background:"#00000070",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={onClose}>
      <div style={{background:"#fff",borderRadius:20,width:600,maxHeight:"90vh",overflow:"auto",boxShadow:"0 20px 60px rgba(0,0,0,0.3)"}} onClick={e=>e.stopPropagation()}>

        {/* Actions bar */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"16px 24px",borderBottom:`1px solid ${C.border}`,position:"sticky",top:0,background:"#fff",zIndex:10}}>
          <span style={{fontSize:14,fontWeight:700,color:C.ink}}>Payslip — {employee.name}</span>
          <div style={{display:"flex",gap:8}}>
            <button onClick={handleDownloadPdf} style={{background:C.accent,color:"#fff",border:"none",borderRadius:8,padding:"8px 18px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>⬇ Download PDF</button>
            <button onClick={handlePrint} style={{background:C.accentLt,color:C.accent,border:`1px solid ${C.accent}30`,borderRadius:8,padding:"8px 18px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>🖨️ Print</button>
            <button onClick={onClose} style={{background:C.bg,color:C.inkMid,border:`1px solid ${C.border}`,borderRadius:8,padding:"8px 16px",fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Close</button>
          </div>
        </div>

        {/* Payslip content */}
        <div id="payslip-content" style={{padding:"32px 40px"}}>

          {/* Header */}
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:28,paddingBottom:16,borderBottom:`2px solid ${C.accent}`}}>
            <div>
              {logoUrl
                ? <img src={logoUrl} alt="logo" style={{height:56,maxWidth:180,objectFit:"contain",display:"block",marginBottom:4}}/>
                : <div style={{fontFamily:"serif",fontSize:24,fontWeight:800,color:C.accent}}>{company||"Your Company"}</div>}
              {logoUrl && <div style={{fontSize:12,fontWeight:700,color:C.ink}}>{company||""}</div>}
            </div>
            <div style={{textAlign:"right"}}>
              <div style={{fontSize:18,fontWeight:800,color:C.ink}}>PAYSLIP</div>
              <div style={{fontSize:12,color:C.inkMid,marginTop:4}}>Period: {period || "Current Month"}</div>
              <div style={{fontSize:12,color:C.inkMid}}>Date: {today}</div>
              <div style={{fontSize:12,color:C.green,fontWeight:700}}>Payment Date: {paymentDate}</div>
            </div>
          </div>

          {/* Employee details */}
          <div style={{background:C.bg,borderRadius:12,padding:"16px 20px",marginBottom:24}}>
            <div style={{fontSize:11,fontWeight:700,color:C.inkMid,letterSpacing:1,textTransform:"uppercase",marginBottom:12}}>Employee Details</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              {[
                ["Employee Name", employee.name],
                ["Employee ID",   employee.employee_number || employee.id || "EMP-001"],
                ["Position",      employee.position || "Staff"],
                ["Department",    employee.dept || "General"],
                ...(employee.grade ? [["Grade / Pay Band", employee.grade]] : []),
                ...(employee.employment_type ? [["Employment Type", employee.employment_type === "hourly" ? "Hourly" : "Salaried"]] : []),
                ...(employee.security_grade ? [["Security Grade", `Grade ${employee.security_grade} — ${employee.security_area==="3"?"Area 3 (Rural)":"Area 1&2 (Urban)"}`]] : []),
                ...(employee.psira_number ? [["PSIRA Registration", employee.psira_number]] : []),
              ].map(([l,v]) => (
                <div key={l}>
                  <div style={{fontSize:10,color:C.inkMid,marginBottom:2}}>{l}</div>
                  <div style={{fontSize:13,fontWeight:600,color:C.ink}}>{v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Earnings */}
          {(()=>{
            const isSec = !!employee.security_grade;
            const row = (label, amount, sub) => amount > 0 ? (
              <div key={label} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",fontSize:13,borderBottom:`1px solid ${C.border}30`}}>
                <span style={{color:C.inkMid}}>{label}{sub ? <span style={{fontSize:11,color:C.inkDim,marginLeft:6}}>{sub}</span> : null}</span>
                <span style={{fontWeight:600,color:C.green}}>{fmt(amount)}</span>
              </div>
            ) : null;
            const taxGross = p.taxableGross || p.taxable_gross || p.gross;
            return (
              <div style={{marginBottom:20}}>
                <div style={{fontSize:11,fontWeight:700,color:C.inkMid,letterSpacing:1,textTransform:"uppercase",marginBottom:10,borderBottom:`1px solid ${C.border}`,paddingBottom:4}}>Earnings</div>
                {row("Basic Salary", p.gross)}
                {/* OT lines — calcOvertime() returns otAmount/sunAmount/phAmount (camelCase) */}
                {(p.overtime?.otAmount||p.overtime?.overtime_amount||0) > 0 && row(
                  `Weekday / Sat Overtime (${p.overtime.otHours||p.overtime.overtime_hours||""}h × 1.5)`,
                  p.overtime.otAmount||p.overtime.overtime_amount)}
                {(p.overtime?.sunAmount||p.overtime?.sunday_amount||0) > 0 && row(
                  `Sunday Work (${p.overtime.sunHours||p.overtime.sunday_hours||""}h × ${isSec?"1.5":"2.0"})`,
                  p.overtime.sunAmount||p.overtime.sunday_amount)}
                {(p.overtime?.phAmount||p.overtime?.ph_amount||0) > 0 && row(
                  `Public Holiday Additional (${p.overtime.phHours||p.overtime.ph_hours||""}h × 1.0)`,
                  p.overtime.phAmount||p.overtime.ph_amount, "normal day's pay already in salary")}
                {/* NBCPSS security allowances — calcPayroll uses camelCase; backend uses snake_case */}
                {(p.nightShiftAllowance||p.night_shift_allowance||0) > 0 && row(
                  `Night Shift Allowance (${p.nightShiftShifts||p.night_shift_shifts||""} shifts × R8.00)`,
                  p.nightShiftAllowance||p.night_shift_allowance)}
                {(p.specialAllowanceAmount||p.special_allowance_amount||0) > 0 && row(
                  `Special Duty Allowance (${p.specialAllowanceShifts||p.special_allowance_shifts||""} shifts × R10.50)`,
                  p.specialAllowanceAmount||p.special_allowance_amount)}
                {(p.cleaningAllowance||p.cleaning_allowance||0) > 0 && row("Cleaning Allowance", p.cleaningAllowance||p.cleaning_allowance)}
                {/* Uniform — non-taxable note */}
                {(p.uniform_allowance||0) > 0 && (
                  <div style={{display:"flex",justifyContent:"space-between",padding:"8px 0",fontSize:13,borderBottom:`1px solid ${C.border}30`}}>
                    <span style={{color:C.inkMid}}>Uniform Allowance <span style={{fontSize:11,color:C.inkDim}}>(non-taxable — s10(1)(nA))</span></span>
                    <span style={{fontWeight:600,color:C.green}}>{fmt(p.uniform_allowance)}</span>
                  </div>
                )}
                {/* MIBCO */}
                {(p.mibco_med_allow||p.mibcoMedAllow||0) > 0 && row("Medical Insurance Allowance (MIBCO — R19.62/wk)", p.mibco_med_allow||p.mibcoMedAllow)}
                {/* Once-off taxable */}
                {(p.once_off_allowance_taxable||p.onceOffAllowanceTaxable||0) > 0 && row("Once-off Taxable Allowance", p.once_off_allowance_taxable||p.onceOffAllowanceTaxable)}
                {/* Single taxable gross total */}
                <div style={{display:"flex",justifyContent:"space-between",padding:"10px 0",fontSize:13,fontWeight:800,borderTop:`2px solid ${C.border}`,marginTop:4}}>
                  <span style={{color:C.ink}}>Taxable Gross</span>
                  <span style={{color:C.green}}>{fmt(taxGross)}</span>
                </div>
              </div>
            );
          })()}

          {/* Deductions */}
          <div style={{marginBottom:20}}>
            <div style={{fontSize:11,fontWeight:700,color:C.inkMid,letterSpacing:1,textTransform:"uppercase",marginBottom:10,borderBottom:`1px solid ${C.border}`,paddingBottom:4}}>Deductions</div>
            {[
              p.s11fDeduction > 0 && ["Pension / Provident (s11F deductible)", p.pensionEmployee || p.pension_employee, C.red],
              p.medicalAidEmployee > 0 && ["Medical Aid (Employee Contribution)", p.medicalAidEmployee || p.medical_aid_employee_ded, C.red],
              (p.nbcpss_provident_employee > 0 || p.nbcpssProvidentEmployee > 0) && ["🔒 PSSPF Provident Fund (NBCPSS)", p.nbcpss_provident_employee||p.nbcpssProvidentEmployee, C.red],
              (p.nbcpss_medical_employee > 0 || p.nbcpssMedicalEmployee > 0) && ["🔒 PSSSBC Medical Aid (NBCPSS Prescribed)", p.nbcpss_medical_employee||p.nbcpssMedicalEmployee, C.red],
              (p.union_subscription_ded > 0 || p.unionSubscriptionDed > 0) && ["🤝 Union Subscription", p.union_subscription_ded||p.unionSubscriptionDed, C.red],
              (p.bcLevyEmployer||p.bc_levy_employer||0) > 0 && ["🔒 NBCPSS BC Levy (Bargaining Council — Employee)", p.bcLevyEmployer||p.bc_levy_employer, C.red],
              (p.advance_deduction > 0 || p.advanceDeduction > 0) && ["🏦 Salary Advance Repayment", p.advance_deduction||p.advanceDeduction, C.red],
              (p.garnishee_total > 0 || p.garnisheeTotal > 0) && ["⚖️ Garnishee / EAO", p.garnishee_total||p.garnisheeTotal, C.red],
              (p.once_off_deduction > 0 || p.onceOffDeduction > 0) && ["✂️ Once-off Deduction", p.once_off_deduction||p.onceOffDeduction, C.red],
              (p.once_off_allowance_taxable > 0 || p.onceOffAllowanceTaxable > 0) && ["＋ Once-off Taxable Allowance", -(p.once_off_allowance_taxable||p.onceOffAllowanceTaxable), C.green],
              (p.once_off_allowance_nontaxable > 0 || p.onceOffAllowanceNontaxable > 0) && ["＋ Once-off Non-taxable Allowance", -(p.once_off_allowance_nontaxable||p.onceOffAllowanceNontaxable), C.green],
              (p.expense_claim > 0 || p.expenseClaim > 0) && ["🧾 Expense Claim (Reimbursement)", -(p.expense_claim||p.expenseClaim), C.green],
              (p.on_maternity_leave || p.onMaternityLeave) && ["🤱 Maternity Leave (Zero Gross — Employee Claims UIF Directly)", 0, C.gold],
              ["PAYE (Income Tax)", p.paye, C.red],
              p.medicalTaxCredit > 0 && ["  ↳ Medical Tax Credit (s6A)", -(p.medicalTaxCredit || p.medical_tax_credit), C.green],
              ["UIF (Employee Contribution)", p.uifEmployee, C.gold],
              (p.mibco_scheme_employee > 0 || p.mibcoSchemeEmployee > 0) && ["⛽ MIBCO Health Scheme (Employee — Affinity Health)", p.mibco_scheme_employee||p.mibcoSchemeEmployee, C.red],
            ].filter(Boolean).map(([l,v,c]) => (
              <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",fontSize:13,borderBottom:`1px solid ${C.border}30`}}>
                <span style={{color:C.inkMid}}>{l}</span>
                <span style={{fontWeight:600,color:c}}>{v < 0 ? `+${fmt(Math.abs(v))}` : `(${fmt(v)})`}</span>
              </div>
            ))}
            <div style={{display:"flex",justifyContent:"space-between",padding:"10px 0",fontSize:14,fontWeight:800,borderTop:`2px solid ${C.border}`,marginTop:8}}>
              <span>Total Deductions</span>
              <span style={{color:C.red}}>({fmt((p.taxableGross||p.taxable_gross||0) - (p.netPay||p.net_pay||0))})</span>
            </div>
          </div>

          {/* Net Pay */}
          <div style={{background:`linear-gradient(135deg,${C.greenLt},transparent)`,border:`2px solid ${C.green}`,borderRadius:12,padding:"16px 20px",marginBottom:24}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <div style={{fontSize:12,color:C.inkMid,marginBottom:4}}>NET PAY THIS PERIOD</div>
                <div style={{fontSize:11,color:C.inkDim}}>Amount payable to employee</div>
              </div>
              <div style={{fontFamily:"serif",fontSize:32,fontWeight:800,color:C.green}}>{fmt(p.netPay||p.net_pay)}</div>
            </div>
          </div>

          {/* Employer contributions */}
          <div style={{marginBottom:20}}>
            <div style={{fontSize:11,fontWeight:700,color:C.inkMid,letterSpacing:1,textTransform:"uppercase",marginBottom:10,borderBottom:`1px solid ${C.border}`,paddingBottom:4}}>Employer Contributions (for info)</div>
            {[
              (p.pensionEmployer||p.pension_employer) > 0 && ["Pension / Provident (Employer)", p.pensionEmployer||p.pension_employer, C.blue],
              (p.medicalAidEmployer||p.medical_aid_employer_con) > 0 && ["Medical Aid (Employer Contribution)", p.medicalAidEmployer||p.medical_aid_employer_con, C.blue],
              ["UIF (Employer Contribution)", p.uifEmployer, C.blue],
              ["SDL (Skills Development Levy)", p.sdl, C.blue],
              (p.nbcpss_provident_employer > 0 || p.nbcpssProvidentEmployer > 0) && ["🔒 PSSPF Provident Fund (Employer)", p.nbcpss_provident_employer||p.nbcpssProvidentEmployer, "#c2410c"],
              (p.nbcpss_medical_employer > 0 || p.nbcpssMedicalEmployer > 0) && ["🔒 PSSSBC Medical Aid (Employer)", p.nbcpss_medical_employer||p.nbcpssMedicalEmployer, "#c2410c"],
              (p.bcLevyEmployer||p.bc_levy_employer||0) > 0 && ["NBCPSS BC Levy (Bargaining Council)", p.bcLevyEmployer||p.bc_levy_employer, "#c2410c"],
              (p.psiraLevyEmployer||p.psira_levy_employer||0) > 0 && ["PSIRA Registration Levy", p.psiraLevyEmployer||p.psira_levy_employer, "#c2410c"],
              (p.mibco_scheme_employer > 0 || p.mibcoSchemeEmployer > 0) && ["⛽ MIBCO Health Scheme (Employer — Affinity Health)", p.mibco_scheme_employer||p.mibcoSchemeEmployer, "#92400e"],
            ].filter(Boolean).map(([l,v,c]) => (
              <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",fontSize:13,borderBottom:`1px solid ${C.border}30`}}>
                <span style={{color:C.inkMid}}>{l}</span>
                <span style={{fontWeight:600,color:c}}>{fmt(v)}</span>
              </div>
            ))}
            <div style={{display:"flex",justifyContent:"space-between",padding:"10px 0",fontSize:13,fontWeight:700,borderTop:`1px solid ${C.border}`,marginTop:4}}>
              <span style={{color:C.inkMid}}>Total Employer Cost</span>
              <span style={{color:C.accent}}>{fmt(p.totalCost)}</span>
            </div>
          </div>

          {/* SARS info */}
          <div style={{background:C.bg,borderRadius:10,padding:"12px 16px",fontSize:11,color:C.inkMid,lineHeight:1.8}}>
            <strong style={{color:C.ink}}>SARS Reference (2026/2027)</strong><br/>
            PAYE calculated on annualised taxable gross of {fmt((p.taxableGross||p.gross) * 12)} — Primary rebate R17,820/year<br/>
            {(p.s11fDeduction||p.s11f_deduction) > 0 && <>Section 11F deduction: {fmt((p.s11fDeduction||p.s11f_deduction)*12)}/year (27.5% of remuneration, capped at R430,000)<br/></>}
            {(p.medicalTaxCredit||p.medical_tax_credit) > 0 && <>Section 6A Medical Tax Credit: R{p.medicalTaxCredit||p.medical_tax_credit}/month (direct PAYE reduction)<br/></>}
            UIF: 1% employee + 1% employer — capped at R17,712/month (on basic salary only)<br/>
            SDL: 1% of taxable gross payroll<br/>
            {p.overtime && p.overtime.total > 0 && `${employee?.security_grade ? "NBCPSS Overtime: Weekday/Sat 1.5× · Sunday 1.5× · Public Holiday 1.0× on top of salary" : "BCEA Overtime: Weekday/Sat 1.5× · Sunday 2× (1.5× if ordinarily works Sundays) · Public Holiday 1.0× on top of normal pay"} · Hourly rate: ${fmt(p.overtime.hourly_rate||p.overtime.hourlyRate)}/hr`}
          </div>

        </div>
      </div>
    </div>
  );
}

// ── BATCH PAYMENT HELPERS ─────────────────────────────────────────────────────
const SARS_BANKING = { bank:"ABSA", branch:"632005", account:"4048718850", type:"Cheque" };
const ACCOUNT_TYPE_CODE = { Cheque:"1", Savings:"2", Transmission:"3" };
const payDate = () => { const d = new Date(); d.setDate(d.getDate()+1); return d.toISOString().slice(0,10).replace(/-/g,""); };

function downloadCSV(filename, content) {
  const blob = new Blob([content], {type:"text/plain"});
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href = url; a.download = filename.replace(".csv",".txt"); a.click();
  URL.revokeObjectURL(url);
}

// Generic EFT format — accepted by FNB, Standard Bank, Nedbank, Capitec, TymeBank
function buildGenericEFT(rows) {
  const header = "Action,BranchCode,AccountNumber,AccountType,AccountName,Amount,OwnReference,BeneficiaryReference";
  const lines  = rows.map(r =>
    `CREDIT,${r.branch},${r.account},${ACCOUNT_TYPE_CODE[r.type]||"1"},"${r.name}",${r.amount.toFixed(2)},"${r.ownRef}","${r.benRef}"`
  );
  return [header, ...lines].join("\r\n");
}

// ABSA-specific format
function buildABSA(rows) {
  const header = "RecordIdentifier,DestinationBranch,DestinationAccount,AccountType,DestinationName,Amount,ActionDate,OwnStatement,DestinationStatement";
  const lines  = rows.map(r =>
    `10,${r.branch},${r.account},${ACCOUNT_TYPE_CODE[r.type]||"1"},"${r.name}",${Math.round(r.amount*100)},${payDate()},"${r.ownRef}","${r.benRef}"`
  );
  return [header, ...lines].join("\r\n");
}

// FNB-specific format (Pay & Clear)
function buildFNB(rows) {
  const header = "TransactionType,BranchCode,AccountNumber,AccountType,AccountName,Amount,Reference";
  const lines  = rows.map(r =>
    `EFT,${r.branch},${r.account},${ACCOUNT_TYPE_CODE[r.type]||"1"},"${r.name}",${r.amount.toFixed(2)},"${r.benRef}"`
  );
  return [header, ...lines].join("\r\n");
}

// Standard Bank format
function buildStdBank(rows) {
  const header = "SequenceNo,BranchCode,AccountNo,AccountType,BeneficiaryName,Amount,OurRef,TheirRef,Email";
  const lines  = rows.map((r,i) =>
    `${i+1},${r.branch},${r.account},${ACCOUNT_TYPE_CODE[r.type]||"1"},"${r.name}",${r.amount.toFixed(2)},"${r.ownRef}","${r.benRef}",""`
  );
  return [header, ...lines].join("\r\n");
}

function BatchPaymentModal({employees, payroll, period, ym, onClose}) {
  const periodStr = period || new Date().toLocaleDateString("en-ZA",{month:"long",year:"numeric"});
  // The bank files move real money, so they use ONLY the figures the server saved when
  // payroll was run (GET /payroll/payslips/{ym}/summary). The old client-side calcPayroll
  // estimate drifted from the server (overtime, NBCPSS allowances, bonus, PSSPF, UIF...).
  const [srv, setSrv] = useState(null);      // {employee_id: {net_pay, paye, ...}}
  const [srvErr, setSrvErr] = useState("");
  useEffect(() => {
    let dead = false;
    api(`/payroll/payslips/${ym}/summary`)
      .then(r => { if (!dead) setSrv(Object.fromEntries((r.payslips||[]).map(x => [x.employee_id, x]))); })
      .catch(err => { if (!dead) setSrvErr(err.message || "could not load processed payslips"); });
    return () => { dead = true; };
  }, [ym]);
  const missing = srv ? employees.filter(e => !srv[e.id]) : [];
  const ready = !!srv && missing.length === 0;
  const sum = (k) => employees.reduce((s,e) => s + ((srv && srv[e.id] && srv[e.id][k]) || 0), 0);
  const totalPAYE = sum("paye");
  const totalUIF  = sum("uif_employee") + sum("uif_employer");
  const totalSDL  = sum("sdl");
  const sarsTotal = totalPAYE + totalUIF + totalSDL;

  const empRows = employees.map(e => ({
    branch:  e.branchCode  || e.branch_code || "250655",
    account: e.accountNumber || e.account_number || "000000000",
    type:    e.accountType  || e.account_type || "Cheque",
    name:    e.name,
    amount:  (srv && srv[e.id] && srv[e.id].net_pay) || 0,
    ownRef:  `SALARY ${periodStr}`,
    benRef:  `SALARY ${periodStr}`,
  }));

  const sarsRow = [{
    branch:  SARS_BANKING.branch,
    account: SARS_BANKING.account,
    type:    SARS_BANKING.type,
    name:    "SARS",
    amount:  sarsTotal,
    ownRef:  `EMP201 ${periodStr}`,
    benRef:  `EMP201 ${periodStr}`,
  }];

  const FORMATS = [
    { label:"Generic EFT (FNB / Nedbank / Capitec / TymeBank / Discovery / Investec)", icon:"🏦", color:C.blue,
      dlSalary: () => downloadCSV(`salary-eft-${periodStr}.csv`,   buildGenericEFT(empRows)),
      dlSars:   () => downloadCSV(`sars-eft-${periodStr}.csv`,     buildGenericEFT(sarsRow)) },
    { label:"ABSA Batch Payment", icon:"🔴", color:"#B11116",
      dlSalary: () => downloadCSV(`salary-absa-${periodStr}.csv`,  buildABSA(empRows)),
      dlSars:   () => downloadCSV(`sars-absa-${periodStr}.csv`,    buildABSA(sarsRow)) },
    { label:"FNB Pay & Clear",    icon:"🟢", color:"#007A39",
      dlSalary: () => downloadCSV(`salary-fnb-${periodStr}.csv`,   buildFNB(empRows)),
      dlSars:   () => downloadCSV(`sars-fnb-${periodStr}.csv`,     buildFNB(sarsRow)) },
    { label:"Standard Bank",      icon:"🔵", color:"#0033A0",
      dlSalary: () => downloadCSV(`salary-std-${periodStr}.csv`,   buildStdBank(empRows)),
      dlSars:   () => downloadCSV(`sars-std-${periodStr}.csv`,     buildStdBank(sarsRow)) },
  ];

  return (
    <div style={{position:"fixed",inset:0,background:"#00000070",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={onClose}>
      <div style={{background:C.surface,borderRadius:20,width:660,maxHeight:"90vh",overflow:"auto",boxShadow:"0 20px 60px rgba(0,0,0,0.3)"}} onClick={e=>e.stopPropagation()}>
        <div style={{padding:"20px 28px",borderBottom:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div>
            <div style={{fontSize:16,fontWeight:800,color:C.ink}}>Batch Payment Files</div>
            <div style={{fontSize:12,color:C.inkMid,marginTop:2}}>{periodStr} — download and import into your bank</div>
          </div>
          <button onClick={onClose} style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:8,padding:"7px 14px",fontSize:12,cursor:"pointer",fontFamily:"inherit",color:C.inkMid}}>Close</button>
        </div>

        {/* Summary */}
        <div style={{padding:"16px 28px",background:C.bg,borderBottom:`1px solid ${C.border}`,display:"flex",gap:20}}>
          {[
            ["Salary Payments", fmt(empRows.reduce((s,r)=>s+r.amount,0)), `${employees.length} employees`, C.green],
            ["PAYE",  fmt(totalPAYE), "To SARS", C.red],
            ["UIF",   fmt(totalUIF),  "To SARS", C.gold],
            ["SDL",   fmt(totalSDL),  "To SARS", C.blue],
            ["SARS Total", fmt(sarsTotal), "EMP201", C.red],
          ].map(([l,v,s,c])=>(
            <div key={l} style={{flex:1}}>
              <div style={{fontSize:10,color:C.inkMid,textTransform:"uppercase",letterSpacing:0.5,marginBottom:3}}>{l}</div>
              <div style={{fontSize:15,fontWeight:800,color:c}}>{v}</div>
              <div style={{fontSize:10,color:C.inkDim}}>{s}</div>
            </div>
          ))}
        </div>

        {/* Format rows */}
        <div style={{padding:"20px 28px"}}>
          {!ready && <div style={{padding:"10px 14px",marginBottom:14,borderRadius:8,background:"#fff1f2",border:"1px solid #fca5a5",color:"#b91c1c",fontSize:12,fontWeight:600}}>
            {srvErr ? `Cannot build payment files: ${srvErr}`
              : !srv ? "Loading processed payslips from the server..."
              : `Payment files are disabled: no processed payslip for ${missing.map(m=>m.name).join(", ")} in ${ym}. Run payroll for this period first.`}
          </div>}
          <div style={{fontSize:11,fontWeight:700,color:C.inkMid,textTransform:"uppercase",letterSpacing:0.5,marginBottom:14}}>Select your bank format</div>
          {FORMATS.map((f,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 16px",borderRadius:12,border:`1px solid ${C.border}`,marginBottom:10,background:C.bg}}>
              <div style={{display:"flex",alignItems:"center",gap:12}}>
                <span style={{fontSize:22}}>{f.icon}</span>
                <span style={{fontSize:13,fontWeight:600,color:C.ink}}>{f.label}</span>
              </div>
              <div style={{display:"flex",gap:8}}>
                <button disabled={!ready} onClick={f.dlSalary} style={{opacity:ready?1:0.4,background:C.greenLt,color:C.green,border:`1px solid ${C.green}30`,borderRadius:8,padding:"7px 14px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>⬇ Salary EFT</button>
                <button disabled={!ready} onClick={f.dlSars} style={{opacity:ready?1:0.4,background:C.redLt,  color:C.red,  border:`1px solid ${C.red}30`,  borderRadius:8,padding:"7px 14px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>⬇ SARS EMP201</button>
              </div>
            </div>
          ))}
          <div style={{background:C.goldLt,border:`1px solid ${C.gold}40`,borderRadius:10,padding:"12px 16px",marginTop:8,fontSize:12,color:C.inkMid,lineHeight:1.7}}>
            <strong style={{color:C.ink}}>How to import:</strong> Log in to your bank's business banking portal → Payments → Batch Payments / EFT → Import File → select the downloaded CSV. SARS EMP201 payment due by the 7th of each month.
          </div>
        </div>
      </div>
    </div>
  );
}

// ── PAYROLL ───────────────────────────────────────────────────────────────────
// ── LEAVE MANAGEMENT ─────────────────────────────────────────────────────────
function LeaveManagement({employees = []}) {
  const [balances,  setBalances]  = useState([]);
  const [requests,  setRequests]  = useState([]);
  const [loadingBal, setLoadingBal] = useState(false);
  const [loadingReq, setLoadingReq] = useState(false);
  const [leaveSection, setLeaveSection] = useState("requests"); // requests | balances | submit
  const [submitForm, setSubmitForm] = useState({employee_id:"",leave_type:"annual",start_date:"",end_date:"",reason:""});
  const [submitMsg, setSubmitMsg]   = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchAll = async () => {
    setLoadingBal(true); setLoadingReq(true);
    try { const d = await api("/leave/balances"); setBalances(d); } catch(e) { console.warn("leave balances:", e); } finally { setLoadingBal(false); }
    try { const d = await api("/leave/requests"); setRequests(d); } catch(e) { console.warn("leave requests:", e); } finally { setLoadingReq(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleReview = async (reqId, status) => {
    try {
      await api(`/leave/requests/${reqId}`, {method:"PUT", body:JSON.stringify({status})});
      fetchAll();
    } catch(e) { alert("Failed: " + (e.message||"unknown error")); }
  };

  const handleSubmit = async () => {
    if (!submitForm.employee_id || !submitForm.start_date || !submitForm.end_date) {
      setSubmitMsg({type:"error", text:"Employee, start date and end date are required."}); return;
    }
    setSubmitting(true); setSubmitMsg(null);
    try {
      const res = await api("/leave/requests", {method:"POST", body:JSON.stringify({
        employee_id: +submitForm.employee_id,
        leave_type:  submitForm.leave_type,
        start_date:  submitForm.start_date,
        end_date:    submitForm.end_date,
        reason:      submitForm.reason || undefined,
      })});
      setSubmitMsg({type:"ok", text: res.message || "Leave request submitted."});
      setSubmitForm({employee_id:"",leave_type:"annual",start_date:"",end_date:"",reason:""});
      fetchAll();
    } catch(e) { setSubmitMsg({type:"error", text: e.message||"Submission failed."}); }
    setSubmitting(false);
  };

  const pending  = requests.filter(r => r.status === "pending").length;
  const approved = requests.filter(r => r.status === "approved").length;

  const statusColor = s => s === "approved" ? C.green : s === "pending" ? C.gold : s === "rejected" ? C.red : C.inkMid;
  const statusBg    = s => s === "approved" ? C.greenLt : s === "pending" ? C.goldLt : s === "rejected" ? C.redLt : C.bg;

  const tabs = [{id:"requests",label:"Requests"},{id:"balances",label:"Leave Balances"},{id:"submit",label:"Submit Leave"}];

  return (
    <div>
      <div style={{marginBottom:20}}>
        <h2 style={{fontFamily:"serif",fontSize:26,color:C.ink,margin:"0 0 4px"}}>Leave Management</h2>
        <p style={{fontSize:12,color:C.inkMid,margin:0}}>BCEA-compliant leave tracking — annual (15 days), sick (30/3yr), family responsibility (3 days)</p>
      </div>

      {/* KPI cards */}
      <div style={{display:"flex",gap:12,marginBottom:20,flexWrap:"wrap"}}>
        <KPI label="Pending Requests"  value={pending}  color={C.gold}  icon="⏳"/>
        <KPI label="Approved (all)"    value={approved} color={C.green} icon="✅"/>
        <KPI label="Total Employees"   value={balances.length} color={C.accent} icon="👥"/>
        <KPI label="Auto-approve in"   value="48 hrs"   color={C.blue}  icon="🕐"/>
      </div>

      {/* Sub-section tabs */}
      <div style={{display:"flex",gap:4,marginBottom:20,borderBottom:`1px solid ${C.border}`,paddingBottom:0}}>
        {tabs.map(t => (
          <button key={t.id} onClick={()=>setLeaveSection(t.id)} style={{background:"none",border:"none",borderBottom: leaveSection===t.id ? `2px solid ${C.accent}` : "2px solid transparent",padding:"8px 18px",fontSize:13,fontWeight:leaveSection===t.id?700:400,color:leaveSection===t.id?C.accent:C.inkMid,cursor:"pointer",fontFamily:"inherit",marginBottom:"-1px"}}>
            {t.label}
          </button>
        ))}
        <button onClick={fetchAll} style={{marginLeft:"auto",background:"none",border:`1px solid ${C.border}`,borderRadius:8,padding:"6px 14px",fontSize:12,color:C.inkMid,cursor:"pointer",fontFamily:"inherit"}}>↺ Refresh</button>
      </div>

      {/* ── REQUESTS TAB ── */}
      {leaveSection === "requests" && (
        <div>
          {loadingReq ? <div style={{color:C.inkMid,fontSize:13,padding:20}}>Loading...</div> : requests.length === 0 ? (
            <div style={{color:C.inkMid,fontSize:13,padding:20,textAlign:"center"}}>No leave requests yet.</div>
          ) : (
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,overflow:"hidden"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                <thead>
                  <tr style={{background:C.bg,borderBottom:`1px solid ${C.border}`}}>
                    {["Employee","Type","Dates","Days","Status","Reason","Action"].map(h=>(
                      <th key={h} style={{textAlign:"left",padding:"11px 14px",fontSize:9,color:C.inkMid,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase"}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {requests.map(r => (
                    <tr key={r.id} style={{borderBottom:`1px solid ${C.border}30`}}>
                      <td style={{padding:"12px 14px",fontWeight:600,color:C.ink}}>{r.employee_name}</td>
                      <td style={{padding:"12px 14px",textTransform:"capitalize"}}>{r.leave_type}</td>
                      <td style={{padding:"12px 14px",color:C.inkMid,whiteSpace:"nowrap"}}>{r.start_date} → {r.end_date}</td>
                      <td style={{padding:"12px 14px",fontWeight:700}}>{r.days_requested}</td>
                      <td style={{padding:"12px 14px"}}>
                        <span style={{background:statusBg(r.status),color:statusColor(r.status),borderRadius:6,padding:"3px 10px",fontSize:11,fontWeight:700,textTransform:"capitalize"}}>
                          {r.auto_approved ? "Auto-approved" : r.status}
                        </span>
                      </td>
                      <td style={{padding:"12px 14px",color:C.inkMid,maxWidth:160,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.reason||"—"}</td>
                      <td style={{padding:"12px 14px"}}>
                        {r.status === "pending" ? (
                          <div style={{display:"flex",gap:6}}>
                            <button onClick={()=>handleReview(r.id,"approved")} style={{background:C.greenLt,color:C.green,border:"none",borderRadius:6,padding:"5px 10px",fontSize:10,cursor:"pointer",fontFamily:"inherit",fontWeight:700}}>Approve</button>
                            <button onClick={()=>handleReview(r.id,"rejected")} style={{background:C.redLt,color:C.red,border:"none",borderRadius:6,padding:"5px 10px",fontSize:10,cursor:"pointer",fontFamily:"inherit",fontWeight:700}}>Reject</button>
                          </div>
                        ) : <span style={{color:C.inkMid,fontSize:11}}>{r.reviewed_by||"—"}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── BALANCES TAB ── */}
      {leaveSection === "balances" && (
        <div>
          {loadingBal ? <div style={{color:C.inkMid,fontSize:13,padding:20}}>Loading...</div> : balances.length === 0 ? (
            <div style={{color:C.inkMid,fontSize:13,padding:20,textAlign:"center"}}>No active employees found.</div>
          ) : (
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,overflow:"hidden"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                <thead>
                  <tr style={{background:C.bg,borderBottom:`1px solid ${C.border}`}}>
                    {["Employee","Position","Annual Balance","Annual Taken","Sick Balance","Sick Taken","Family Balance","Last Accrual"].map(h=>(
                      <th key={h} style={{textAlign:"left",padding:"11px 14px",fontSize:9,color:C.inkMid,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase",whiteSpace:"nowrap"}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {balances.map(b => (
                    <tr key={b.employee_id} style={{borderBottom:`1px solid ${C.border}30`}}>
                      <td style={{padding:"12px 14px",fontWeight:600,color:C.ink}}>{b.employee_name}</td>
                      <td style={{padding:"12px 14px",color:C.inkMid}}>{b.position||"—"}</td>
                      <td style={{padding:"12px 14px"}}>
                        <span style={{fontWeight:700,color:b.annual_balance<3?C.red:C.green}}>{b.annual_balance} days</span>
                      </td>
                      <td style={{padding:"12px 14px",color:C.inkMid}}>{b.annual_taken_ytd} days</td>
                      <td style={{padding:"12px 14px"}}>
                        <span style={{fontWeight:700,color:b.sick_balance<5?C.gold:C.ink}}>{b.sick_balance} days</span>
                      </td>
                      <td style={{padding:"12px 14px",color:C.inkMid}}>{b.sick_taken_ytd} days</td>
                      <td style={{padding:"12px 14px",fontWeight:600}}>{b.family_balance} days</td>
                      <td style={{padding:"12px 14px",color:C.inkMid,fontSize:11}}>{b.last_accrual_date||"Never"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div style={{marginTop:12,padding:"12px 16px",background:C.accentLt,border:`1px solid ${C.accent}30`,borderRadius:10,fontSize:12,color:C.inkMid}}>
            <strong style={{color:C.accent}}>Accrual note:</strong> Annual leave accrues at +1.25 days/month automatically when payroll is run. Maximum carry-over: 30 days.
          </div>
        </div>
      )}

      {/* ── SUBMIT LEAVE TAB ── */}
      {leaveSection === "submit" && (
        <div style={{background:C.surface,border:`2px solid ${C.accent}`,borderRadius:16,padding:28,maxWidth:560}}>
          <h3 style={{fontFamily:"serif",fontSize:20,margin:"0 0 20px",color:C.ink}}>Submit Leave Request</h3>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14}}>
            <div>
              <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Employee</label>
              <select value={submitForm.employee_id} onChange={e=>setSubmitForm(v=>({...v,employee_id:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}>
                <option value="">-- Select Employee --</option>
                {employees.map(emp=><option key={emp.id||emp.employee_id} value={emp.id||emp.employee_id}>{emp.name||emp.first_name+" "+emp.last_name}</option>)}
              </select>
            </div>
            <div>
              <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Leave Type</label>
              <select value={submitForm.leave_type} onChange={e=>setSubmitForm(v=>({...v,leave_type:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}>
                <option value="annual">Annual Leave</option>
                <option value="sick">Sick Leave</option>
                <option value="family">Family Responsibility</option>
                <option value="maternity">Maternity Leave</option>
                <option value="unpaid">Unpaid Leave</option>
              </select>
            </div>
            <div>
              <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Start Date</label>
              <input type="date" value={submitForm.start_date} onChange={e=>setSubmitForm(v=>({...v,start_date:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}/>
            </div>
            <div>
              <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>End Date</label>
              <input type="date" value={submitForm.end_date} onChange={e=>setSubmitForm(v=>({...v,end_date:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}/>
            </div>
            <div style={{gridColumn:"1 / -1"}}>
              <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Reason (optional)</label>
              <input placeholder="e.g. Scheduled medical appointment" value={submitForm.reason} onChange={e=>setSubmitForm(v=>({...v,reason:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}/>
            </div>
          </div>
          {submitMsg && (
            <div style={{background:submitMsg.type==="ok"?C.greenLt:C.redLt,border:`1px solid ${submitMsg.type==="ok"?C.green:C.red}40`,borderRadius:8,padding:"10px 14px",marginBottom:12,fontSize:13,color:submitMsg.type==="ok"?C.green:C.red}}>
              {submitMsg.text}
            </div>
          )}
          <div style={{display:"flex",gap:8}}>
            <button onClick={handleSubmit} disabled={submitting} style={{background:C.accent,color:"#fff",border:"none",borderRadius:10,padding:"10px 24px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",opacity:submitting?0.6:1}}>
              {submitting ? "Submitting..." : "Submit Request"}
            </button>
            <button onClick={()=>setLeaveSection("requests")} style={{background:"transparent",color:C.inkMid,border:`1px solid ${C.border}`,borderRadius:10,padding:"10px 20px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>View Requests</button>
          </div>
          <div style={{marginTop:16,fontSize:11,color:C.inkMid,lineHeight:1.8}}>
            <strong style={{color:C.ink}}>Approval flow:</strong> Requests go to manager for review. If no action is taken within 48 hours, the request is <em>automatically approved</em> and leave is deducted from the employee's balance.
          </div>
        </div>
      )}
    </div>
  );
}

// ── Patterson Grade market salary ranges (2025 SA general market, monthly CTC) ──
// Source: Peromnes/Patterson compensation surveys — use as indicative benchmarks.
// Ranges vary by industry, company size, and province.
const PATTERSON_RANGES = {
  A1: { min:  3500, median:  4500, max:  5500 },
  A2: { min:  5000, median:  6500, max:  8000 },
  B1: { min:  7000, median:  9000, max: 11000 },
  B2: { min:  9500, median: 12000, max: 15000 },
  B3: { min: 12000, median: 15500, max: 19000 },
  B4: { min: 15000, median: 19000, max: 23000 },
  B5: { min: 18000, median: 23000, max: 28000 },
  C1: { min: 20000, median: 26000, max: 32000 },
  C2: { min: 25000, median: 32000, max: 40000 },
  C3: { min: 30000, median: 39000, max: 48000 },
  C4: { min: 36000, median: 46000, max: 56000 },
  C5: { min: 43000, median: 54000, max: 65000 },
  D1: { min: 45000, median: 57000, max: 70000 },
  D2: { min: 55000, median: 70000, max: 85000 },
  D3: { min: 65000, median: 82000, max:100000 },
  D4: { min: 80000, median:102000, max:125000 },
  D5: { min: 95000, median:122000, max:150000 },
  E1: { min:110000, median:140000, max:170000 },
  E2: { min:130000, median:170000, max:210000 },
  E3: { min:160000, median:210000, max:260000 },
  E4: { min:200000, median:260000, max:320000 },
  E5: { min:240000, median:320000, max:400000 },
  F1: { min:280000, median:390000, max:500000 },
  F2: { min:380000, median:540000, max:700000 },
  F3: { min:500000, median:800000, max:1200000 },
};

// ── BENEFITS MANAGER ─────────────────────────────────────────────────────────
function BenefitsManager({ employees = [] }) {
  const BENEFIT_ICONS  = { pension:"🏦", medical:"🏥", group_life:"🛡️", other:"⚙️" };
  const BENEFIT_LABELS = { pension:"Pension / Provident Fund", medical:"Medical Aid", group_life:"Group Life / Risk Cover", other:"Other" };

  const [schemes,       setSchemes]       = useState([]);
  const [enrollments,   setEnrollments]   = useState({});   // schemeId → [{…}]
  const [summary,       setSummary]       = useState(null);
  const [selectedId,    setSelectedId]    = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [err,           setErr]           = useState("");
  const [showModal,     setShowModal]     = useState(false);
  const [editing,       setEditing]       = useState(null);  // null = create
  const [form,          setForm]          = useState({ name:"", benefit_type:"pension", description:"", employer_pct:"", employee_pct:"" });
  const [formErr,       setFormErr]       = useState("");
  const [saving,        setSaving]        = useState(false);
  const [enrollModal,   setEnrollModal]   = useState(false);
  const [enrollTarget,  setEnrollTarget]  = useState(null); // employee to enroll
  const [overrides,     setOverrides]     = useState({ employer_pct:"", employee_pct:"" });

  const fmtPct  = v => v == null ? "" : v + "%";
  const fmtZAR  = v => "R " + (v||0).toLocaleString("en-ZA", {minimumFractionDigits:2, maximumFractionDigits:2});

  const loadAll = async () => {
    setLoading(true); setErr("");
    try {
      const [sc, sm] = await Promise.all([
        api("/benefits/schemes"),
        api("/benefits/summary"),
      ]);
      setSchemes(sc);
      setSummary(sm);
      if (sc.length && !selectedId) setSelectedId(sc[0].id);
    } catch(e) { setErr(e.message); }
    finally    { setLoading(false); }
  };

  const loadEnrollments = async (schemeId) => {
    if (!schemeId) return;
    try {
      // Fetch all active employees' enrollments for this scheme
      const allEnrollments = await Promise.all(
        employees.map(emp => api(`/benefits/employee/${emp.id}`).catch(()=>[]))
      );
      const forScheme = allEnrollments.flat().filter(e => e.scheme_id === schemeId && e.is_active);
      setEnrollments(prev => ({ ...prev, [schemeId]: forScheme }));
    } catch(e) { /* silent */ }
  };

  useEffect(() => { loadAll(); }, []);
  useEffect(() => { if (selectedId) loadEnrollments(selectedId); }, [selectedId]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name:"", benefit_type:"pension", description:"", employer_pct:"", employee_pct:"" });
    setFormErr(""); setShowModal(true);
  };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ name:s.name, benefit_type:s.benefit_type, description:s.description||"", employer_pct:String(s.employer_pct), employee_pct:String(s.employee_pct) });
    setFormErr(""); setShowModal(true);
  };

  const saveScheme = async () => {
    const epct = parseFloat(form.employer_pct); const eepct = parseFloat(form.employee_pct);
    if (!form.name.trim())      { setFormErr("Name is required."); return; }
    if (isNaN(epct)||epct<0||epct>100)  { setFormErr("Employer % must be 0–100."); return; }
    if (isNaN(eepct)||eepct<0||eepct>100){ setFormErr("Employee % must be 0–100."); return; }
    setSaving(true); setFormErr("");
    try {
      const body = { name:form.name.trim(), benefit_type:form.benefit_type, description:form.description||null, employer_pct:epct, employee_pct:eepct };
      if (editing) {
        await api(`/benefits/schemes/${editing.id}`, { method:"PUT", body:JSON.stringify(body) });
      } else {
        const created = await api("/benefits/schemes", { method:"POST", body:JSON.stringify(body) });
        setSelectedId(created.id);
      }
      setShowModal(false);
      await loadAll();
    } catch(e) { setFormErr(e.message); }
    finally    { setSaving(false); }
  };

  const deactivateScheme = async (s) => {
    if (!window.confirm(`Deactivate "${s.name}"? It will no longer apply to payroll.`)) return;
    try {
      await api(`/benefits/schemes/${s.id}`, { method:"DELETE" });
      if (selectedId === s.id) setSelectedId(null);
      await loadAll();
    } catch(e) { setErr(e.message); }
  };

  const unenroll = async (enrollmentId) => {
    try {
      await api(`/benefits/enrollment/${enrollmentId}`, { method:"DELETE" });
      await loadEnrollments(selectedId);
      await loadAll();
    } catch(e) { setErr(e.message); }
  };

  const openEnroll = (emp) => {
    setEnrollTarget(emp);
    setOverrides({ employer_pct:"", employee_pct:"" });
    setEnrollModal(true);
  };

  const submitEnroll = async () => {
    const scheme = schemes.find(s => s.id === selectedId);
    if (!scheme) return;
    const body = {
      employee_id: enrollTarget.id,
      scheme_id:   selectedId,
      employer_pct_override: overrides.employer_pct !== "" ? parseFloat(overrides.employer_pct) : null,
      employee_pct_override: overrides.employee_pct !== "" ? parseFloat(overrides.employee_pct) : null,
    };
    try {
      await api("/benefits/enroll", { method:"POST", body:JSON.stringify(body) });
      setEnrollModal(false);
      await loadEnrollments(selectedId);
      await loadAll();
    } catch(e) { alert(e.message); }
  };

  const selectedScheme   = schemes.find(s => s.id === selectedId);
  const schemeEnrollments= (enrollments[selectedId] || []);
  const enrolledIds      = new Set(schemeEnrollments.map(e => e.employee_id));
  const unenrolledEmps   = employees.filter(e => !enrolledIds.has(e.id));

  const btnPrimary = { background:"#4361ee", color:"#fff", border:"none", borderRadius:6, padding:"7px 16px", cursor:"pointer", fontWeight:600, fontSize:13 };
  const btnDanger  = { background:"transparent", color:"#e53e3e", border:"1px solid #e53e3e", borderRadius:6, padding:"5px 10px", cursor:"pointer", fontSize:12 };
  const btnGhost   = { background:"transparent", color:"#4361ee", border:"1px solid #4361ee", borderRadius:6, padding:"5px 10px", cursor:"pointer", fontSize:12 };
  const card       = { background:"#fff", border:"1px solid #e8eaf0", borderRadius:10, padding:"16px 20px" };

  if (loading) return <div style={{padding:40,textAlign:"center",color:"#888"}}>Loading benefit schemes…</div>;
  if (err)     return <div style={{padding:40,textAlign:"center",color:"#e53e3e"}}>{err}</div>;

  return (
    <div>
      {/* ── Summary cards ── */}
      {summary && (
        <div style={{display:"flex",gap:16,marginBottom:24,flexWrap:"wrap"}}>
          <div style={{...card,flex:1,minWidth:180}}>
            <div style={{fontSize:12,color:"#888",marginBottom:4}}>Total Employer Cost / Month</div>
            <div style={{fontSize:22,fontWeight:700,color:"#4361ee"}}>{fmtZAR(summary.total_employer_monthly)}</div>
          </div>
          <div style={{...card,flex:1,minWidth:180}}>
            <div style={{fontSize:12,color:"#888",marginBottom:4}}>Total Employee Deductions / Month</div>
            <div style={{fontSize:22,fontWeight:700,color:"#e53e3e"}}>{fmtZAR(summary.total_employee_monthly)}</div>
          </div>
          <div style={{...card,flex:1,minWidth:180}}>
            <div style={{fontSize:12,color:"#888",marginBottom:4}}>Active Enrollments</div>
            <div style={{fontSize:22,fontWeight:700,color:"#333"}}>{summary.enrollment_count}</div>
          </div>
          {(summary.by_type||[]).map(t => (
            <div key={t.label} style={{...card,flex:1,minWidth:200}}>
              <div style={{fontSize:12,color:"#888",marginBottom:4}}>{t.icon} {t.label}</div>
              <div style={{fontSize:13}}><span style={{color:"#4361ee",fontWeight:600}}>{fmtZAR(t.employer_cost)}</span> employer · <span style={{color:"#e53e3e",fontWeight:600}}>{fmtZAR(t.employee_deductions)}</span> employee</div>
            </div>
          ))}
        </div>
      )}

      <div style={{display:"flex",gap:20,alignItems:"flex-start"}}>
        {/* ── Scheme list ── */}
        <div style={{width:260,flexShrink:0}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <div style={{fontWeight:700,fontSize:15}}>Benefit Schemes</div>
            <button style={btnPrimary} onClick={openCreate}>+ New</button>
          </div>
          {schemes.length === 0 && (
            <div style={{...card,color:"#888",fontSize:13,textAlign:"center",padding:24}}>No schemes yet. Create one to get started.</div>
          )}
          {schemes.map(s => (
            <div key={s.id} onClick={()=>setSelectedId(s.id)} style={{
              ...card, marginBottom:8, cursor:"pointer",
              borderColor: s.id===selectedId ? "#4361ee" : "#e8eaf0",
              background:  s.id===selectedId ? "#f0f3ff" : "#fff",
            }}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                <div>
                  <div style={{fontWeight:600,fontSize:13}}>{BENEFIT_ICONS[s.benefit_type]||"⚙️"} {s.name}</div>
                  <div style={{fontSize:11,color:"#888",marginTop:2}}>{BENEFIT_LABELS[s.benefit_type]||s.benefit_type}</div>
                  <div style={{fontSize:11,marginTop:4}}>
                    <span style={{color:"#4361ee"}}>Employer {s.employer_pct}%</span>
                    {" · "}
                    <span style={{color:"#e53e3e"}}>Employee {s.employee_pct}%</span>
                  </div>
                  <div style={{fontSize:11,color:"#888",marginTop:2}}>{s.enrollment_count} enrolled</div>
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:4}}>
                  <button style={{...btnGhost,padding:"3px 8px"}} onClick={e=>{e.stopPropagation();openEdit(s);}}>Edit</button>
                  <button style={{...btnDanger,padding:"3px 8px"}} onClick={e=>{e.stopPropagation();deactivateScheme(s);}}>Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ── Enrollment panel ── */}
        <div style={{flex:1}}>
          {!selectedScheme ? (
            <div style={{...card,textAlign:"center",color:"#888",padding:40}}>Select a scheme to manage enrollments.</div>
          ) : (
            <>
              <div style={{fontWeight:700,fontSize:15,marginBottom:12}}>
                {BENEFIT_ICONS[selectedScheme.benefit_type]||"⚙️"} {selectedScheme.name} — Enrollments
              </div>
              {selectedScheme.description && (
                <div style={{fontSize:13,color:"#555",marginBottom:12}}>{selectedScheme.description}</div>
              )}

              {/* Enrolled employees */}
              {schemeEnrollments.length > 0 && (
                <div style={{...card,marginBottom:16}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                    <thead>
                      <tr style={{borderBottom:"1px solid #e8eaf0",color:"#888"}}>
                        <th style={{textAlign:"left",padding:"6px 8px",fontWeight:600}}>Employee</th>
                        <th style={{textAlign:"right",padding:"6px 8px",fontWeight:600}}>Gross</th>
                        <th style={{textAlign:"right",padding:"6px 8px",fontWeight:600}}>Employer %</th>
                        <th style={{textAlign:"right",padding:"6px 8px",fontWeight:600}}>Employer / Mo</th>
                        <th style={{textAlign:"right",padding:"6px 8px",fontWeight:600}}>Employee %</th>
                        <th style={{textAlign:"right",padding:"6px 8px",fontWeight:600}}>Deduction / Mo</th>
                        <th style={{padding:"6px 8px"}}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {schemeEnrollments.map(en => (
                        <tr key={en.id} style={{borderBottom:"1px solid #f5f5f5"}}>
                          <td style={{padding:"7px 8px"}}>
                            <div style={{fontWeight:600}}>{en.employee_name}</div>
                            <div style={{fontSize:11,color:"#888"}}>{en.employee_number}</div>
                          </td>
                          <td style={{textAlign:"right",padding:"7px 8px"}}>{fmtZAR(en.gross_salary)}</td>
                          <td style={{textAlign:"right",padding:"7px 8px",color:"#4361ee"}}>
                            {en.employer_pct}%
                            {en.employer_pct_override!=null && <span style={{fontSize:10,color:"#888"}}> (override)</span>}
                          </td>
                          <td style={{textAlign:"right",padding:"7px 8px",color:"#4361ee",fontWeight:600}}>{fmtZAR(en.employer_monthly)}</td>
                          <td style={{textAlign:"right",padding:"7px 8px",color:"#e53e3e"}}>
                            {en.employee_pct}%
                            {en.employee_pct_override!=null && <span style={{fontSize:10,color:"#888"}}> (override)</span>}
                          </td>
                          <td style={{textAlign:"right",padding:"7px 8px",color:"#e53e3e",fontWeight:600}}>{fmtZAR(en.employee_monthly)}</td>
                          <td style={{padding:"7px 8px"}}>
                            <button style={btnDanger} onClick={()=>unenroll(en.id)}>Remove</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Enroll more */}
              {unenrolledEmps.length > 0 && (
                <div style={{...card}}>
                  <div style={{fontWeight:600,fontSize:13,marginBottom:10,color:"#555"}}>Enroll employees</div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                    {unenrolledEmps.map(emp => (
                      <button key={emp.id} style={btnGhost} onClick={()=>openEnroll(emp)}>
                        + {emp.first_name} {emp.last_name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {unenrolledEmps.length === 0 && schemeEnrollments.length > 0 && (
                <div style={{fontSize:13,color:"#888",marginTop:8}}>All active employees are enrolled in this scheme.</div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Create / Edit modal ── */}
      {showModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setShowModal(false)}>
          <div style={{background:"#fff",borderRadius:12,padding:28,width:420,maxWidth:"95vw",boxShadow:"0 8px 32px rgba(0,0,0,0.18)"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontWeight:700,fontSize:17,marginBottom:18}}>{editing?"Edit Scheme":"New Benefit Scheme"}</div>

            <div style={{marginBottom:14}}>
              <label style={{fontSize:13,fontWeight:600,display:"block",marginBottom:4}}>Scheme Name *</label>
              <input value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))}
                placeholder="e.g. Company Pension Plan"
                style={{width:"100%",border:"1px solid #ddd",borderRadius:6,padding:"8px 10px",fontSize:13,boxSizing:"border-box"}}/>
            </div>

            <div style={{marginBottom:14}}>
              <label style={{fontSize:13,fontWeight:600,display:"block",marginBottom:4}}>Benefit Type *</label>
              <select value={form.benefit_type} onChange={e=>setForm(p=>({...p,benefit_type:e.target.value}))}
                style={{width:"100%",border:"1px solid #ddd",borderRadius:6,padding:"8px 10px",fontSize:13,boxSizing:"border-box"}}>
                {Object.entries(BENEFIT_LABELS).map(([k,v])=>(
                  <option key={k} value={k}>{BENEFIT_ICONS[k]} {v}</option>
                ))}
              </select>
            </div>

            <div style={{display:"flex",gap:12,marginBottom:14}}>
              <div style={{flex:1}}>
                <label style={{fontSize:13,fontWeight:600,display:"block",marginBottom:4}}>Employer Contribution %</label>
                <input type="number" min="0" max="100" step="0.5"
                  value={form.employer_pct} onChange={e=>setForm(p=>({...p,employer_pct:e.target.value}))}
                  placeholder="e.g. 5"
                  style={{width:"100%",border:"1px solid #ddd",borderRadius:6,padding:"8px 10px",fontSize:13,boxSizing:"border-box"}}/>
                <div style={{fontSize:11,color:"#888",marginTop:2}}>% of gross salary paid by employer</div>
              </div>
              <div style={{flex:1}}>
                <label style={{fontSize:13,fontWeight:600,display:"block",marginBottom:4}}>Employee Contribution %</label>
                <input type="number" min="0" max="100" step="0.5"
                  value={form.employee_pct} onChange={e=>setForm(p=>({...p,employee_pct:e.target.value}))}
                  placeholder="e.g. 5"
                  style={{width:"100%",border:"1px solid #ddd",borderRadius:6,padding:"8px 10px",fontSize:13,boxSizing:"border-box"}}/>
                <div style={{fontSize:11,color:"#888",marginTop:2}}>% of gross salary deducted from employee</div>
              </div>
            </div>

            <div style={{marginBottom:18}}>
              <label style={{fontSize:13,fontWeight:600,display:"block",marginBottom:4}}>Description (optional)</label>
              <textarea value={form.description} onChange={e=>setForm(p=>({...p,description:e.target.value}))}
                placeholder="Any notes about this scheme…"
                rows={2}
                style={{width:"100%",border:"1px solid #ddd",borderRadius:6,padding:"8px 10px",fontSize:13,boxSizing:"border-box",resize:"vertical"}}/>
            </div>

            {formErr && <div style={{color:"#e53e3e",fontSize:13,marginBottom:12}}>{formErr}</div>}

            <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
              <button style={{...btnGhost,padding:"8px 18px"}} onClick={()=>setShowModal(false)}>Cancel</button>
              <button style={{...btnPrimary,padding:"8px 20px",opacity:saving?0.6:1}} onClick={saveScheme} disabled={saving}>
                {saving?"Saving…":"Save Scheme"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Enroll employee modal ── */}
      {enrollModal && enrollTarget && selectedScheme && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setEnrollModal(false)}>
          <div style={{background:"#fff",borderRadius:12,padding:28,width:380,maxWidth:"95vw",boxShadow:"0 8px 32px rgba(0,0,0,0.18)"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontWeight:700,fontSize:16,marginBottom:6}}>Enroll {enrollTarget.first_name} {enrollTarget.last_name}</div>
            <div style={{fontSize:13,color:"#555",marginBottom:18}}>Scheme: {BENEFIT_ICONS[selectedScheme.benefit_type]} {selectedScheme.name}</div>

            <div style={{fontSize:13,color:"#888",marginBottom:14}}>
              Default rates: Employer <strong style={{color:"#4361ee"}}>{selectedScheme.employer_pct}%</strong> · Employee <strong style={{color:"#e53e3e"}}>{selectedScheme.employee_pct}%</strong>.
              Leave overrides blank to use defaults.
            </div>

            <div style={{display:"flex",gap:12,marginBottom:18}}>
              <div style={{flex:1}}>
                <label style={{fontSize:13,fontWeight:600,display:"block",marginBottom:4}}>Employer % override</label>
                <input type="number" min="0" max="100" step="0.5"
                  value={overrides.employer_pct} onChange={e=>setOverrides(p=>({...p,employer_pct:e.target.value}))}
                  placeholder={String(selectedScheme.employer_pct)}
                  style={{width:"100%",border:"1px solid #ddd",borderRadius:6,padding:"8px 10px",fontSize:13,boxSizing:"border-box"}}/>
              </div>
              <div style={{flex:1}}>
                <label style={{fontSize:13,fontWeight:600,display:"block",marginBottom:4}}>Employee % override</label>
                <input type="number" min="0" max="100" step="0.5"
                  value={overrides.employee_pct} onChange={e=>setOverrides(p=>({...p,employee_pct:e.target.value}))}
                  placeholder={String(selectedScheme.employee_pct)}
                  style={{width:"100%",border:"1px solid #ddd",borderRadius:6,padding:"8px 10px",fontSize:13,boxSizing:"border-box"}}/>
              </div>
            </div>

            <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
              <button style={{...btnGhost,padding:"8px 18px"}} onClick={()=>setEnrollModal(false)}>Cancel</button>
              <button style={{...btnPrimary,padding:"8px 20px"}} onClick={submitEnroll}>Enroll</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Payroll({live = {}, user = {}}) {
  const liveEmployees = live.employees;
  const [employees, setEmployees] = useState(MOCK_EMPLOYEES);
  const [taxYear, setTaxYear] = useState(CURRENT_TAX_YEAR);

  // ── Payroll PIN gate ──────────────────────────────────────────────────────
  const [pinOk,     setPinOk]     = useState(false);
  const [pinInput,  setPinInput]  = useState("");
  const [pinSet,    setPinSet]    = useState(null);  // null=loading, true/false
  const [pinError,  setPinError]  = useState("");
  const [pinBusy,   setPinBusy]   = useState(false);
  const [payrollSection, setPayrollSection] = useState("payroll"); // payroll | leave | emp201
  const [emp201Data,    setEmp201Data]    = useState(null);
  const [emp201Loading, setEmp201Loading] = useState(false);
  const [emp201Period,  setEmp201Period]  = useState(new Date().toISOString().slice(0,7));
  // Annual returns (IRP5 / EMP501)
  const [annualView,    setAnnualView]    = useState("monthly"); // monthly | annual
  const [irp5Data,      setIrp5Data]      = useState(null);
  const [emp501Data,    setEmp501Data]    = useState(null);
  const [annualLoading, setAnnualLoading] = useState(false);
  const [annualTaxYear, setAnnualTaxYear] = useState(()=>{const n=new Date();return String(n.getMonth()>=2?n.getFullYear()+1:n.getFullYear());});

  useEffect(() => {
    const token = localStorage.getItem("zuzan_token");
    if (!token || token.startsWith("demo_")) { setPinSet(false); return; }
    fetch(`${BASE_URL}/companies/payroll-pin-status`, {
      headers: {"Authorization": "Bearer " + token}
    }).then(r => r.json()).then(d => setPinSet(d.pin_set)).catch(() => setPinSet(false));
  }, []);

  const handlePinVerify = async () => {
    if (!pinInput) { setPinError("Enter your PIN."); return; }
    setPinBusy(true); setPinError("");
    try {
      const token = localStorage.getItem("zuzan_token");
      const res = await fetch("https://zuzan-backend.onrender.com/companies/verify-payroll-pin", {
        method: "POST", headers: {"Content-Type":"application/json","Authorization":"Bearer "+token},
        body: JSON.stringify({pin: pinInput})
      });
      const d = await res.json();
      if (d.valid) { setPinOk(true); }
      else { setPinError("Incorrect PIN. Try again."); setPinInput(""); }
    } catch { setPinError("Could not verify PIN. Please try again."); }
    setPinBusy(false);
  };
  // ─────────────────────────────────────────────────────────────────────────

  // ── Company logo — fetch direct from API so payslips always have it ──────
  const [companyLogoUrl, setCompanyLogoUrl] = useState(user.logoUrl || "");
  useEffect(() => {
    if (user.logoUrl) { setCompanyLogoUrl(user.logoUrl); return; }
    api("/companies/me").then(d => { if (d.logo_url) setCompanyLogoUrl(d.logo_url); }).catch(() => {});
  }, [user.logoUrl]);
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => { if (liveEmployees && liveEmployees.length > 0) setEmployees(liveEmployees.map(e => ({...e, name: `${e.first_name} ${e.last_name}`, salary: e.gross_salary, dept: e.department || "General"}))); }, [liveEmployees]);
  const [showNew, setShowNew] = useState(false);
  // ── Dual pay schedule state ───────────────────────────────────────────────
  const [payScheduleTab, setPayScheduleTab] = useState("salaried"); // "security" | "salaried" — default to salaried so run-payroll button is immediately visible
  const [payrollRunBySchedule,    setPayrollRunBySchedule]    = useState({security:false, salaried:false});
  const [runNotes, setRunNotes] = useState(null); // {status,message,warnings[],skipped[]} from the last /payroll/run
  const [runError, setRunError] = useState(""); // set when /payroll/run fails, so a failed run is never shown as "Payroll Processed"
  const [payrollPeriodBySchedule, setPayrollPeriodBySchedule] = useState({security:null,  salaried:null});
  // Derived helpers for current tab
  const payrollRun    = payrollRunBySchedule[payScheduleTab];
  const payrollPeriod = payrollPeriodBySchedule[payScheduleTab];
  const setPayrollRun    = (v) => setPayrollRunBySchedule(p=>({...p,[payScheduleTab]:v}));
  const setPayrollPeriod = (v) => setPayrollPeriodBySchedule(p=>({...p,[payScheduleTab]:v}));
  // ─────────────────────────────────────────────────────────────────────────
  const [showOtModal, setShowOtModal] = useState(false);
  const [otData, setOtData] = useState({});  // {employeeId: {otHours, sunHours, phHours}}
  const [onceOffData, setOnceOffData] = useState({}); // {employeeId: {expenseClaim, onceOffDeduction, onceOffTaxable, onceOffNonTaxable}}
  const [secData, setSecData] = useState({}); // {employeeId: {nightShifts, specialShifts}} for NBCPSS
  const [secArea, setSecArea] = useState("3"); // NBCPSS rate area: Area 3 only
  const [form, setForm] = useState({name:"",position:"",salary:"",dept:"",empNo:"",grade:"",employmentType:"salaried",paySchedule:"salaried",hourlyRate:"",idNumber:"",taxNumber:"",dob:"",appointmentDate:"",address:"",bankName:"",accountNumber:"",branchCode:"",accountType:"Cheque",pensionEmployeePct:"",pensionEmployerPct:"",pensionEmployeeFixed:"",pensionEmployerFixed:"",medicalAidEmployee:"",medicalAidEmployer:"",medicalAidDependants:"",psiraNumber:"",securityGrade:"",securityArea:"3",shiftType:"day",specialAllowanceType:"none",mibcoRole:"",mibcoSchemeEnrolled:true,unionSubscription:0,advanceMonthlyDeduction:0,onMaternityLeave:false,employmentCategory:"permanent"});
  const [viewPayslip, setViewPayslip] = useState(null);
  const [showBatch,   setShowBatch]   = useState(false);
  const [editEmp,     setEditEmp]     = useState(null);
  const [editOpenSec, setEditOpenSec] = useState(new Set(["basic","personal","sector","bank"]));
  const toggleEditSec = k => setEditOpenSec(prev => { const s = new Set(prev); s.has(k) ? s.delete(k) : s.add(k); return s; });
  const [editForm,    setEditForm]    = useState({});
  const [garnishees,  setGarnishees]  = useState([]);  // active garnishee orders for editEmp
  const [showTerminated, setShowTerminated] = useState(false);
  const [termModal, setTermModal] = useState(null); // {emp} when open
  const [newGarnRef,  setNewGarnRef]  = useState("");
  const [newGarnAmt,  setNewGarnAmt]  = useState("");
  // Stored payslips for the current period — keyed by employee_id.
  // Fetched on mount and refreshed after each payroll run so the table shows
  // actuals (taxable_gross, net_pay, total_cost etc.) rather than the live
  // calcForSummary() estimate which only knows about basic salary.
  const [storedPayslips, setStoredPayslips] = useState({}); // {empId: {taxable_gross, net_pay, ...}}
  const currentPeriod = new Date().toISOString().slice(0,7); // "YYYY-MM"
  const fetchStoredPayslips = () => {
    api(`/payroll/payslips/${currentPeriod}/summary`)
      .then(d => {
        const map = {};
        (d.payslips || []).forEach(ps => { map[ps.employee_id] = ps; });
        setStoredPayslips(map);
      })
      .catch(() => {});
  };
  useEffect(() => { fetchStoredPayslips(); }, []);
  // audit fix 2026-09-16 — same NBCPSS gap as BatchPaymentModal: these drive the
  // "Payroll Processed" summary cards (Net Disbursed / PAYE / UIF+SDL) shown right
  // after a real payroll run, so they should reflect this period's actual OT/night-
  // shift/special-allowance/bonus inputs, not just bare salary. See [[project_nbcpss_208hr_bug]].
  const calcForSummary = (e) => {
    const isSecurityEmp = !!e.security_grade;
    const ot  = otData[e.id]  || {};
    const sec = secData[e.id] || {};
    const annualBonus = nbcpssBonusFor(e, sec, new Date().toISOString().slice(0,7));
    return calcPayroll(
      e.salary, taxYear,
      +ot.otHours||0, +ot.sunHours||0, +ot.phHours||0, e.hourly_rate||null,
      (e.pension_fund_employee_pct||0), (e.pension_fund_employer_pct||0),
      (e.medical_aid_employee||0), (e.medical_aid_employer||0), (e.medical_aid_dependants||0),
      (e.pension_employee_fixed||0), (e.pension_employer_fixed||0),
      { isSecurity: isSecurityEmp, nightShiftShifts: +sec.nightShifts||0,
        specialAllowanceShifts: +sec.specialShifts||0, annualBonus },
    );
  };
  const activeEmployees = employees.filter(e=>e.is_active!==false);
  // Filter by active schedule tab
  // Classification rule: security_grade always wins. For private security companies,
  // employees default to "security" unless pay_schedule is explicitly "salaried".
  const isSecurityCo = (user?.industry||"").toLowerCase().replace(/[\s-]/g,"_") === "private_security";
  const empSchedule = e => {
    if (e.security_grade) return "security";
    if (e.pay_schedule === "casual") return "casual";
    if (e.pay_schedule === "salaried") return "salaried"; // explicitly overridden
    if (isSecurityCo) return "security"; // company default for private security
    return e.pay_schedule || "salaried";
  };
  const scheduleEmployees = activeEmployees.filter(e => empSchedule(e) === payScheduleTab);
  const totalGross = scheduleEmployees.reduce((s,e) => s + e.salary, 0);
  const totalPAYE = scheduleEmployees.reduce((s,e) => s + calcForSummary(e).paye, 0);
  const totalNet = scheduleEmployees.reduce((s,e) => s + calcForSummary(e).netPay, 0);
  const totalCost = scheduleEmployees.reduce((s,e) => s + calcForSummary(e).totalCost, 0);
  const totalUIF = scheduleEmployees.reduce((s,e) => s + calcForSummary(e).uifEmployer, 0);
  const totalSDL = scheduleEmployees.reduce((s,e) => s + calcForSummary(e).sdl, 0);
  const zuZanFee = Math.max(99, activeEmployees.length * 15.5);
  const handleAdd = async () => {
    const nameParts = form.name.trim().split(" ");
    const firstName = nameParts[0] || form.name;
    const lastName  = nameParts.slice(1).join(" ") || "";
    const newEmp = {
      id: form.empNo || `EMP-${String(employees.length+1).padStart(3,"0")}`,
      name:form.name, position:form.position, salary:+form.salary, dept:form.dept,
      idNumber:form.idNumber, taxNumber:form.taxNumber, dob:form.dob,
      appointmentDate:form.appointmentDate, address:form.address,
      bankName:form.bankName, accountNumber:form.accountNumber,
      branchCode:form.branchCode, accountType:form.accountType,
    };
    setEmployees([...employees, newEmp]);
    setShowNew(false);
    setForm({name:"",position:"",salary:"",dept:"",empNo:"",grade:"",employmentType:"salaried",hourlyRate:"",idNumber:"",taxNumber:"",dob:"",appointmentDate:"",address:"",bankName:"",accountNumber:"",branchCode:"",accountType:"Cheque",pensionEmployeePct:"",pensionEmployerPct:"",pensionEmployeeFixed:"",pensionEmployerFixed:"",medicalAidEmployee:"",medicalAidEmployer:"",medicalAidDependants:""});
    try {
      const token = localStorage.getItem("zuzan_token");
      if (token && !token.startsWith("demo_")) {
        try { await api("/companies/me", {method:"PUT",body:JSON.stringify({payroll_enabled:true})}); } catch(e) {}
      }
      await api("/employees/", {
        method: "POST",
        body: JSON.stringify({
          first_name:                 firstName,
          last_name:                  lastName,
          position:                   form.position,
          department:                 form.dept,
          grade:                      form.grade || null,
          employment_type:            form.employmentType || "salaried",
          hourly_rate:                form.hourlyRate ? +form.hourlyRate : null,
          gross_salary:               +form.salary,
          employee_number:            form.empNo,
          id_number:                  form.idNumber,
          tax_number:                 form.taxNumber,
          date_of_birth:              form.dob || null,
          appointment_date:           form.appointmentDate || null,
          address:                    form.address,
          bank_name:                  form.bankName,
          account_number:             form.accountNumber,
          branch_code:                form.branchCode,
          account_type:               form.accountType,
          pension_fund_employee_pct:  form.pensionEmployeePct ? +form.pensionEmployeePct / 100 : 0,
          pension_fund_employer_pct:  form.pensionEmployerPct ? +form.pensionEmployerPct / 100 : 0,
          pension_employee_fixed:     form.pensionEmployeeFixed ? +form.pensionEmployeeFixed : 0,
          pension_employer_fixed:     form.pensionEmployerFixed ? +form.pensionEmployerFixed : 0,
          medical_aid_employee:       form.medicalAidEmployee ? +form.medicalAidEmployee : 0,
          medical_aid_employer:       form.medicalAidEmployer ? +form.medicalAidEmployer : 0,
          medical_aid_dependants:     form.medicalAidDependants ? +form.medicalAidDependants : 0,
          psira_number:               form.psiraNumber || null,
          security_grade:             form.securityGrade || null,
          security_area:              form.securityArea || "3",
          shift_type:                 form.shiftType || "day",
          special_allowance_type:     form.specialAllowanceType || "none",
          mibco_role:                 form.mibcoRole || null,
          mibco_scheme_enrolled:      form.mibcoSchemeEnrolled ?? true,
          psspf_exempt:               !!form.psspfExempt,
          sunday_ordinary:            !!form.sundayOrdinary,
          union_subscription:         form.unionSubscription || 0,
          advance_monthly_deduction:  form.advanceMonthlyDeduction || 0,
          on_maternity_leave:         form.onMaternityLeave || false,
          pay_schedule:               form.paySchedule || (form.securityGrade ? "security" : "salaried"),
          employment_category:        form.employmentCategory || "permanent",
        }),
      });
      if (live && live.reload) live.reload();
    } catch(err) {
      console.warn("Employee save failed:", err.message);
    }
  };
  // Fetch garnishee orders whenever a different employee is opened for editing
  useEffect(() => {
    if (!editEmp) { setGarnishees([]); setNewGarnRef(""); setNewGarnAmt(""); return; }
    const tok = localStorage.getItem("zuzan_token");
    fetch(`${BASE_URL}/employees/${editEmp.id}/garnishees`, {headers:{Authorization:`Bearer ${tok}`}})
      .then(r=>r.ok?r.json():[])
      .then(setGarnishees)
      .catch(()=>setGarnishees([]));
  }, [editEmp?.id]);

  const addGarnishee = async () => {
    if (!newGarnRef.trim() || !parseFloat(newGarnAmt)) return;
    const tok = localStorage.getItem("zuzan_token");
    const r = await fetch(`${BASE_URL}/employees/${editEmp.id}/garnishees`, {
      method:"POST",headers:{"Content-Type":"application/json",Authorization:`Bearer ${tok}`},
      body:JSON.stringify({reference:newGarnRef.trim(),amount:parseFloat(newGarnAmt)})
    });
    if (r.ok) { const g = await r.json(); setGarnishees(v=>[...v,g]); setNewGarnRef(""); setNewGarnAmt(""); }
  };

  const removeGarnishee = async (gid) => {
    const tok = localStorage.getItem("zuzan_token");
    const r = await fetch(`${BASE_URL}/employees/${editEmp.id}/garnishees/${gid}`, {
      method:"DELETE",headers:{Authorization:`Bearer ${tok}`}
    });
    if (r.ok) setGarnishees(v=>v.filter(g=>g.id!==gid));
  };

  const handleEditSave = async () => {
    if (!editEmp) return;
    const nameParts = (editForm.name || "").trim().split(" ");
    const firstName = nameParts[0] || "";
    const lastName  = nameParts.slice(1).join(" ") || "";
    const updated = {
      ...editEmp,
      name:                       editForm.name,
      position:                   editForm.position,
      salary:                     +editForm.salary || editEmp.salary,
      dept:                       editForm.dept,
      grade:                      editForm.grade,
      employment_type:            editForm.employmentType,
      hourly_rate:                editForm.hourlyRate ? +editForm.hourlyRate : null,
      id_number:                  editForm.idNumber,
      tax_number:                 editForm.taxNumber,
      date_of_birth:              editForm.dob,
      appointment_date:           editForm.appointmentDate,
      address:                    editForm.address,
      bank_name:                  editForm.bankName,
      account_number:             editForm.accountNumber,
      branch_code:                editForm.branchCode,
      account_type:               editForm.accountType,
      pension_fund_employee_pct:  editForm.pensionEmployeePct ? +editForm.pensionEmployeePct / 100 : 0,
      pension_fund_employer_pct:  editForm.pensionEmployerPct ? +editForm.pensionEmployerPct / 100 : 0,
      pension_employee_fixed:     editForm.pensionEmployeeFixed ? +editForm.pensionEmployeeFixed : 0,
      pension_employer_fixed:     editForm.pensionEmployerFixed ? +editForm.pensionEmployerFixed : 0,
      medical_aid_employee:       editForm.medicalAidEmployee ? +editForm.medicalAidEmployee : 0,
      medical_aid_employer:       editForm.medicalAidEmployer ? +editForm.medicalAidEmployer : 0,
      medical_aid_dependants:     editForm.medicalAidDependants ? +editForm.medicalAidDependants : 0,
      pay_schedule:               editForm.paySchedule || (editForm.securityGrade ? "security" : "salaried"),
      employment_category:        editForm.employmentCategory || "permanent",
    };
    setEmployees(employees.map(e => e.id === editEmp.id ? updated : e));
    setEditEmp(null);
    try {
      const token = localStorage.getItem("zuzan_token");
      if (token && !token.startsWith("demo_")) {
        await api(`/employees/${editEmp.id}`, {
          method: "PUT",
          body: JSON.stringify({
            first_name:                 firstName,
            last_name:                  lastName,
            position:                   editForm.position,
            department:                 editForm.dept,
            grade:                      editForm.grade || null,
            employment_type:            editForm.employmentType || "salaried",
            hourly_rate:                editForm.hourlyRate ? +editForm.hourlyRate : null,
            gross_salary:               +editForm.salary,
            id_number:                  editForm.idNumber,
            tax_number:                 editForm.taxNumber,
            date_of_birth:              editForm.dob || null,
            appointment_date:           editForm.appointmentDate || null,
            address:                    editForm.address,
            bank_name:                  editForm.bankName,
            account_number:             editForm.accountNumber,
            branch_code:                editForm.branchCode,
            account_type:               editForm.accountType,
            pension_fund_employee_pct:  editForm.pensionEmployeePct ? +editForm.pensionEmployeePct / 100 : 0,
            pension_fund_employer_pct:  editForm.pensionEmployerPct ? +editForm.pensionEmployerPct / 100 : 0,
            pension_employee_fixed:     editForm.pensionEmployeeFixed ? +editForm.pensionEmployeeFixed : 0,
            pension_employer_fixed:     editForm.pensionEmployerFixed ? +editForm.pensionEmployerFixed : 0,
            medical_aid_employee:       editForm.medicalAidEmployee ? +editForm.medicalAidEmployee : 0,
            medical_aid_employer:       editForm.medicalAidEmployer ? +editForm.medicalAidEmployer : 0,
            medical_aid_dependants:     editForm.medicalAidDependants ? +editForm.medicalAidDependants : 0,
            psira_number:               editForm.psiraNumber || null,
            security_grade:             editForm.securityGrade || null,
            security_area:              editForm.securityArea || "3",
            shift_type:                 editForm.shiftType || "day",
            special_allowance_type:     editForm.specialAllowanceType || "none",
            mibco_role:                 editForm.mibcoRole || null,
            mibco_scheme_enrolled:      editForm.mibcoSchemeEnrolled ?? true,
            psspf_exempt:               !!editForm.psspfExempt,
            sunday_ordinary:            !!editForm.sundayOrdinary,
            union_subscription:         editForm.unionSubscription || 0,
            advance_monthly_deduction:  editForm.advanceMonthlyDeduction || 0,
            on_maternity_leave:         editForm.onMaternityLeave || false,
            pay_schedule:               editForm.paySchedule || (editForm.securityGrade ? "security" : "salaried"),
            employment_category:        editForm.employmentCategory || "permanent",
          }),
        });
        if (live && live.reload) live.reload();
      }
    } catch(err) {
      console.error("Employee update failed:", err.message);
      alert("Save failed: " + (err.message || "unknown error") + "\nYour changes were not saved to the server.");
    }
  };

  // Show PIN gate if not yet verified
  if (!pinOk) {
    return (
      <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",minHeight:340,padding:40}}>
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:20,padding:40,width:"100%",maxWidth:380,textAlign:"center",boxShadow:"0 4px 24px #0000000a"}}>
          <div style={{fontSize:36,marginBottom:12}}>🔒</div>
          <h2 style={{fontFamily:"serif",fontSize:24,color:C.ink,margin:"0 0 8px"}}>Payroll is Protected</h2>
          {pinSet === null && <p style={{color:C.inkMid,fontSize:13}}>Checking security settings...</p>}
          {pinSet === false && (
            <div>
              <div style={{background:C.redLt,border:`1px solid ${C.red}40`,borderRadius:10,padding:"12px 16px",marginBottom:16}}>
                <p style={{color:C.red,fontSize:13,margin:0,fontWeight:600}}>No Payroll PIN is set.</p>
                <p style={{color:C.inkMid,fontSize:12,margin:"6px 0 0"}}>A PIN must be configured before payroll data can be accessed. Go to <strong>Settings → Payroll PIN</strong> to set one.</p>
              </div>
            </div>
          )}
          {pinSet === true && (
            <div>
              <p style={{color:C.inkMid,fontSize:13,marginBottom:20}}>Enter your payroll PIN to access salary data.</p>
              <input
                type="password" inputMode="numeric" maxLength={8} placeholder="Enter PIN"
                value={pinInput} onChange={e=>setPinInput(e.target.value.replace(/\D/g,""))}
                onKeyDown={e=>e.key==="Enter"&&handlePinVerify()}
                style={{width:"100%",padding:"12px",border:`1px solid ${pinError?C.red:C.border}`,borderRadius:10,fontSize:20,textAlign:"center",letterSpacing:8,fontFamily:"inherit",outline:"none",boxSizing:"border-box",marginBottom:8}}
                autoFocus
              />
              {pinError && <div style={{color:C.red,fontSize:12,marginBottom:8}}>{pinError}</div>}
              <button onClick={handlePinVerify} disabled={pinBusy} style={{background:C.accent,color:"#fff",border:"none",borderRadius:10,padding:"11px",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit",width:"100%",opacity:pinBusy?0.6:1}}>
                {pinBusy ? "Verifying..." : "Unlock Payroll"}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  const SECTION_LABELS = {payroll:"Payroll", leave:"Leave Management", benefits:"Benefits", emp201:"EMP201 / IRP5"};
  const SectionTabs = () => (
    <div style={{display:"flex",alignItems:"center",gap:0,marginBottom:24,borderBottom:"1px solid "+C.border}}>
      {["payroll","leave","benefits","emp201"].map(s => (
        <button key={s} onClick={()=>setPayrollSection(s)}
          style={{background:"none",border:"none",borderBottom:payrollSection===s?`2px solid ${C.accent}`:"2px solid transparent",padding:"10px 18px",fontSize:13,fontWeight:payrollSection===s?700:400,color:payrollSection===s?C.accent:C.inkMid,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>
          {SECTION_LABELS[s]}
        </button>
      ))}
    </div>
  );

  // Leave management sub-tab (accessible after PIN gate)
  if (payrollSection === "leave") {
    return (
      <div>
        <SectionTabs/>
        <LeaveManagement employees={employees.filter(e=>e.is_active!==false)} />
      </div>
    );
  }

  // Benefits sub-tab
  if (payrollSection === "benefits") {
    return (
      <div>
        <SectionTabs/>
        <BenefitsManager employees={employees.filter(e=>e.is_active!==false)}/>
      </div>
    );
  }

  // EMP201 / IRP5 sub-tab — confidential, behind PIN gate
  if (payrollSection === "emp201") {
    // ── Monthly EMP201 helpers ─────────────────────────────────────────────
    const loadEmp201 = async () => {
      setEmp201Loading(true);
      try {
        const data = await api(`/reports/emp201?date_from=${emp201Period}-01&date_to=${emp201Period}-28`);
        setEmp201Data(data);
      } catch(err) { setEmp201Data(null); }
      setEmp201Loading(false);
    };
    const e         = emp201Data;
    const nowD      = new Date();
    // audit fix 2026-09-16 — fallback path only (real numbers normally come from
    // /reports/emp201); switched to calcForSummary for NBCPSS consistency with the
    // rest of this component. See [[project_nbcpss_208hr_bug]].
    const activeEmps = employees.filter(emp=>emp.is_active!==false);
    const totalPaye = e ? e.total_paye : activeEmps.reduce((s,emp)=>s+calcForSummary({...emp, salary: emp.salary||emp.gross_salary}).paye,0);
    const totalUif  = e ? e.total_uif  : activeEmps.reduce((s,emp)=>{const p=calcForSummary({...emp, salary: emp.salary||emp.gross_salary});return s+p.uifEmployee+p.uifEmployer;},0);
    const totalSdl  = e ? e.total_sdl  : activeEmps.reduce((s,emp)=>s+calcForSummary({...emp, salary: emp.salary||emp.gross_salary}).sdl,0);
    const totalDue  = e ? e.total_due_sars : totalPaye+totalUif+totalSdl;
    const dueDate   = e ? e.due_date : "7 "+new Date(nowD.getFullYear(),nowD.getMonth()+1).toLocaleDateString("en-ZA",{month:"long",year:"numeric"});
    const empList   = e ? e.employees : activeEmps.map(emp=>{const p=calcForSummary({...emp, salary: emp.salary||emp.gross_salary});return {employee_name:emp.name,employee_number:emp.id,gross_salary:p.gross,paye:p.paye,uif_employee:p.uifEmployee,uif_employer:p.uifEmployer,sdl:p.sdl,net_pay:p.netPay};});

    // ── Annual IRP5 / EMP501 helpers ──────────────────────────────────────
    const loadAnnual = async () => {
      setAnnualLoading(true);
      try {
        const [irp5, e501] = await Promise.all([
          api(`/payroll/irp5?tax_year=${annualTaxYear}`),
          api(`/payroll/emp501?tax_year=${annualTaxYear}`),
        ]);
        setIrp5Data(irp5);
        setEmp501Data(e501);
      } catch(err) { /* silently keep old data */ }
      setAnnualLoading(false);
    };

    // Inner view toggle
    const InnerToggle = () => (
      <div style={{display:"flex",gap:0,marginBottom:20,background:C.bg,borderRadius:8,border:"1px solid "+C.border,width:"fit-content"}}>
        {[["monthly","Monthly EMP201"],["annual","Annual IRP5 / EMP501"]].map(([v,l])=>(
          <button key={v} onClick={()=>setAnnualView(v)}
            style={{background:annualView===v?C.accent:"transparent",color:annualView===v?"#fff":C.inkMid,border:"none",borderRadius:7,padding:"7px 18px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit",transition:"all .15s"}}>
            {l}
          </button>
        ))}
      </div>
    );

    // ── ANNUAL VIEW ────────────────────────────────────────────────────────
    if (annualView === "annual") {
      const irp5 = irp5Data;
      const e501 = emp501Data;
      const irp5Emps = irp5 ? irp5.employees : [];
      return (
        <div>
          <SectionTabs/>
          <InnerToggle/>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
            <div>
              <h2 style={{fontFamily:"serif",fontSize:22,color:C.ink,margin:0}}>Annual Returns — IRP5 &amp; EMP501</h2>
              <p style={{fontSize:12,color:C.inkMid,marginTop:3}}>
                Tax Year {annualTaxYear}: {irp5?irp5.period_from:""} – {irp5?irp5.period_to:""}
              </p>
            </div>
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              <select value={annualTaxYear} onChange={ev=>{setAnnualTaxYear(ev.target.value);setIrp5Data(null);setEmp501Data(null);}}
                style={{padding:"7px 12px",border:"1px solid "+C.border,borderRadius:8,fontSize:12,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",fontWeight:700}}>
                {[String(new Date().getFullYear()+2),String(new Date().getFullYear()+1),String(new Date().getFullYear()),String(new Date().getFullYear()-1)].map(y=>(
                  <option key={y} value={y}>Tax Year {y}</option>
                ))}
              </select>
              <button onClick={loadAnnual} style={{background:C.accent,color:"#fff",border:"none",borderRadius:8,padding:"8px 16px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                {annualLoading?"Loading…":"Load"}
              </button>
              {irp5Emps.length>0 && (
                <ExcelBtn filename={`irp5_${annualTaxYear}.csv`} data={irp5Emps.map(r=>({
                  Employee:`${r.first_name} ${r.last_name}`, EmpNo:r.employee_number, IDNo:r.id_number, TaxNo:r.tax_number,
                  Gross:r.gross_remuneration, "3601_Salary":r.code_3601_salary, "3810_MedFringe":r.code_3810_med_fringe,
                  "3607_Overtime":r.code_3607_overtime, "4102_PAYE":r.code_4102_paye, "4141_UIF_Emp":r.code_4141_uif_employee,
                  "4001_Pension":r.code_4001_pension, "MedTaxCredit":r.medical_tax_credit, NetPay:r.net_pay_annual,
                  "4141_UIF_Empr":r.code_4141_uif_employer, "4142_SDL":r.code_4142_sdl, "PensionEmpr":r.pension_employer_contribution,
                }))}/>
              )}
              {irp5Emps.length>0 && (
                <button onClick={async()=>{
                  try {
                    const token = localStorage.getItem("zuzan_token");
                    const res = await fetch(`https://zuzan-backend.onrender.com/payroll/easyfile-export?tax_year=${annualTaxYear}&mode=TEST`, {
                      headers:{Authorization:`Bearer ${token}`}
                    });
                    if (!res.ok) { const e=await res.json(); alert("e@syFile export failed: "+(e.detail||res.status)); return; }
                    const blob = await res.blob();
                    const url  = URL.createObjectURL(blob);
                    const a    = document.createElement("a");
                    a.href = url; a.download = `easyfile_emp501_${annualTaxYear}_annual_TEST.txt`; a.click();
                    URL.revokeObjectURL(url);
                  } catch(err) { alert("Download failed. Make sure PAYE Reference is saved in Settings → Company."); }
                }} style={{background:"#1a7f37",color:"#fff",border:"none",borderRadius:8,padding:"8px 14px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>
                  ⬇ Annual EMP501 (.txt)
                </button>
              )}
              {irp5Emps.length>0 && (
                <button onClick={async()=>{
                  try {
                    const token = localStorage.getItem("zuzan_token");
                    const res = await fetch(`https://zuzan-backend.onrender.com/payroll/easyfile-export?tax_year=${annualTaxYear}&mode=TEST&interim=true`, {
                      headers:{Authorization:`Bearer ${token}`}
                    });
                    if (!res.ok) { const e=await res.json(); alert("Interim EMP501 export failed: "+(e.detail||res.status)); return; }
                    const blob = await res.blob();
                    const url  = URL.createObjectURL(blob);
                    const a    = document.createElement("a");
                    a.href = url; a.download = `easyfile_emp501_${annualTaxYear}_interim_mar_aug_TEST.txt`; a.click();
                    URL.revokeObjectURL(url);
                  } catch(err) { alert("Download failed. Make sure PAYE Reference is saved in Settings → Company."); }
                }} style={{background:"#b45309",color:"#fff",border:"none",borderRadius:8,padding:"8px 14px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>
                  ⬇ Interim EMP501 Mar–Aug (.txt)
                </button>
              )}
              <PrintBtn onClick={()=>{const el=document.getElementById("annual-print");if(!el)return;const w=window.open("","_blank");w.document.write("<html><body>"+el.innerHTML+"</body></html>");w.document.close();w.print();}}/>
            </div>
          </div>

          <div id="annual-print">
            {/* EMP501 Summary */}
            {e501 && (
              <div style={{marginBottom:24}}>
                <div style={{fontSize:13,fontWeight:700,color:C.ink,marginBottom:12}}>EMP501 — Employer Reconciliation Declaration</div>
                <div style={{display:"flex",gap:12,marginBottom:16,flexWrap:"wrap"}}>
                  <KPI label="Total Employees"    value={e501.total_employees}            color={C.blue}   icon="👥" sub={`${e501.months_submitted} months submitted`}/>
                  <KPI label="Total Remuneration" value={fmt(e501.total_gross_remuneration)} color={C.ink}    icon="💰" sub="Gross payroll for year"/>
                  <KPI label="Total PAYE"         value={fmt(e501.total_paye)}            color={C.red}    icon="💼" sub="Income tax withheld"/>
                  <KPI label="Total UIF"          value={fmt(e501.total_uif)}             color={C.gold}   icon="🛡️" sub="Emp + employer"/>
                  <KPI label="Total SDL"          value={fmt(e501.total_sdl)}             color={C.blue}   icon="📚" sub="Skills levy"/>
                  <KPI label="Total Due SARS"     value={fmt(e501.total_due_sars)}        color={C.accent} icon="🏛️" sub="PAYE + UIF + SDL"/>
                </div>
                {/* Monthly breakdown */}
                <div style={{background:C.surface,border:"1px solid "+C.border,borderRadius:12,overflow:"hidden",marginBottom:20}}>
                  <div style={{padding:"12px 18px",borderBottom:"1px solid "+C.border,fontSize:12,fontWeight:700,color:C.ink}}>Monthly EMP201 Reconciliation</div>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                    <thead>
                      <tr style={{background:C.bg}}>
                        {["Period","Employees","Gross","PAYE","UIF","SDL","Total SARS"].map(h=>(
                          <th key={h} style={{padding:"9px 14px",textAlign:"left",fontSize:9,color:C.inkMid,fontWeight:600,letterSpacing:.5,textTransform:"uppercase"}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {e501.monthly_breakdown.map((m,i)=>(
                        <tr key={i} style={{borderBottom:"1px solid "+C.border+"20"}}>
                          <td style={{padding:"9px 14px",fontWeight:600,color:C.ink}}>{m.period}</td>
                          <td style={{padding:"9px 14px",color:C.inkMid}}>{m.employee_count}</td>
                          <td style={{padding:"9px 14px"}}>{fmt(m.gross)}</td>
                          <td style={{padding:"9px 14px",color:C.red}}>{fmt(m.paye)}</td>
                          <td style={{padding:"9px 14px",color:C.gold}}>{fmt(m.uif)}</td>
                          <td style={{padding:"9px 14px",color:C.blue}}>{fmt(m.sdl)}</td>
                          <td style={{padding:"9px 14px",fontWeight:700}}>{fmt(m.paye+m.uif+m.sdl)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{background:C.bg,borderTop:"2px solid "+C.border}}>
                        <td colSpan={2} style={{padding:"9px 14px",fontWeight:800,color:C.ink}}>ANNUAL TOTAL</td>
                        <td style={{padding:"9px 14px",fontWeight:800}}>{fmt(e501.total_gross_remuneration)}</td>
                        <td style={{padding:"9px 14px",fontWeight:800,color:C.red}}>{fmt(e501.total_paye)}</td>
                        <td style={{padding:"9px 14px",fontWeight:800,color:C.gold}}>{fmt(e501.total_uif)}</td>
                        <td style={{padding:"9px 14px",fontWeight:800,color:C.blue}}>{fmt(e501.total_sdl)}</td>
                        <td style={{padding:"9px 14px",fontWeight:800,color:C.accent}}>{fmt(e501.total_due_sars)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <div style={{background:C.redLt,border:"1px solid "+C.red+"30",borderRadius:10,padding:"12px 16px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div>
                    <div style={{fontSize:12,fontWeight:700,color:C.red}}>Submit EMP501 on SARS eFiling</div>
                    <div style={{fontSize:11,color:C.inkMid,marginTop:2}}>Interim reconciliation: Aug–Sep. Annual reconciliation: May–Jun. Use the figures above to complete your eFiling submission.</div>
                  </div>
                  <a href="https://efiling.sars.gov.za" target="_blank" rel="noreferrer" style={{background:C.red,color:"#fff",padding:"8px 14px",borderRadius:8,fontSize:12,fontWeight:700,textDecoration:"none",whiteSpace:"nowrap"}}>Open eFiling</a>
                </div>
              </div>
            )}

            {/* IRP5 per employee */}
            <div style={{background:C.surface,border:"1px solid "+C.border,borderRadius:16,overflow:"hidden"}}>
              <div style={{padding:"14px 20px",borderBottom:"1px solid "+C.border,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div style={{fontSize:13,fontWeight:700,color:C.ink}}>IRP5 Employee Tax Certificates — Tax Year {annualTaxYear}</div>
                <div style={{fontSize:11,color:C.inkMid}}>Give each employee their IRP5 by 31 May. Codes follow SARS IT3(a) format.</div>
              </div>
              {irp5Emps.length === 0 ? (
                <div style={{padding:32,textAlign:"center",color:C.inkMid,fontSize:13}}>
                  {irp5 ? "No payslips found for this tax year." : "Click Load to generate IRP5 certificates."}
                </div>
              ) : (
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                  <thead>
                    <tr style={{background:C.bg}}>
                      {["Employee","ID / Tax No","Months","3601 Salary","3810 Med Fringe","3607 OT","4102 PAYE","4141 UIF","4001 Pension","MTC","Net Pay (Annual)","4142 SDL*"].map(h=>(
                        <th key={h} style={{padding:"9px 12px",textAlign:"left",fontSize:9,color:C.inkMid,fontWeight:600,letterSpacing:.4,textTransform:"uppercase",whiteSpace:"nowrap"}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {irp5Emps.map((emp,i)=>(
                      <tr key={i} style={{borderBottom:"1px solid "+C.border+"20"}}>
                        <td style={{padding:"10px 12px",fontWeight:600,color:C.ink,whiteSpace:"nowrap"}}>{emp.first_name} {emp.last_name}</td>
                        <td style={{padding:"10px 12px",color:C.inkMid,fontSize:10}}>{emp.id_number||"—"} / {emp.tax_number||"—"}</td>
                        <td style={{padding:"10px 12px",textAlign:"center",color:C.inkMid}}>{emp.periods_count}</td>
                        <td style={{padding:"10px 12px"}}>{fmt(emp.code_3601_salary)}</td>
                        <td style={{padding:"10px 12px",color:emp.code_3810_med_fringe>0?C.gold:"inherit"}}>{fmt(emp.code_3810_med_fringe)}</td>
                        <td style={{padding:"10px 12px",color:emp.code_3607_overtime>0?C.blue:"inherit"}}>{fmt(emp.code_3607_overtime)}</td>
                        <td style={{padding:"10px 12px",color:C.red,fontWeight:600}}>{fmt(emp.code_4102_paye)}</td>
                        <td style={{padding:"10px 12px",color:C.gold}}>{fmt(emp.code_4141_uif_employee)}</td>
                        <td style={{padding:"10px 12px"}}>{fmt(emp.code_4001_pension)}</td>
                        <td style={{padding:"10px 12px",color:C.green}}>{fmt(emp.medical_tax_credit)}</td>
                        <td style={{padding:"10px 12px",fontWeight:700,color:C.green}}>{fmt(emp.net_pay_annual)}</td>
                        <td style={{padding:"10px 12px",color:C.inkMid,fontSize:10}}>{fmt(emp.code_4142_sdl)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{background:C.bg,borderTop:"2px solid "+C.border}}>
                      <td colSpan={3} style={{padding:"10px 12px",fontWeight:800,color:C.ink}}>TOTALS</td>
                      <td style={{padding:"10px 12px",fontWeight:800}}>{fmt(irp5Emps.reduce((s,r)=>s+(r.code_3601_salary||0),0))}</td>
                      <td style={{padding:"10px 12px",fontWeight:800,color:C.gold}}>{fmt(irp5Emps.reduce((s,r)=>s+(r.code_3810_med_fringe||0),0))}</td>
                      <td style={{padding:"10px 12px",fontWeight:800,color:C.blue}}>{fmt(irp5Emps.reduce((s,r)=>s+(r.code_3607_overtime||0),0))}</td>
                      <td style={{padding:"10px 12px",fontWeight:800,color:C.red}}>{fmt(irp5Emps.reduce((s,r)=>s+(r.code_4102_paye||0),0))}</td>
                      <td style={{padding:"10px 12px",fontWeight:800,color:C.gold}}>{fmt(irp5Emps.reduce((s,r)=>s+(r.code_4141_uif_employee||0),0))}</td>
                      <td style={{padding:"10px 12px",fontWeight:800}}>{fmt(irp5Emps.reduce((s,r)=>s+(r.code_4001_pension||0),0))}</td>
                      <td style={{padding:"10px 12px",fontWeight:800,color:C.green}}>{fmt(irp5Emps.reduce((s,r)=>s+(r.medical_tax_credit||0),0))}</td>
                      <td style={{padding:"10px 12px",fontWeight:800,color:C.green}}>{fmt(irp5Emps.reduce((s,r)=>s+(r.net_pay_annual||0),0))}</td>
                      <td style={{padding:"10px 12px",fontWeight:800,color:C.inkMid}}>{fmt(irp5Emps.reduce((s,r)=>s+(r.code_4142_sdl||0),0))}</td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
            <p style={{fontSize:10,color:C.inkMid,marginTop:8}}>* SDL (code 4142) is an employer cost — shown for reconciliation, not deducted from employee net pay. MTC = Medical Tax Credit (s6A).</p>
          </div>
        </div>
      );
    }

    // ── MONTHLY EMP201 VIEW ────────────────────────────────────────────────
    return (
      <div>
        <SectionTabs/>
        <InnerToggle/>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20}}>
          <div>
            <h2 style={{fontFamily:"serif",fontSize:22,color:C.ink,margin:0}}>EMP201 Monthly Employer Return</h2>
            <p style={{fontSize:12,color:C.inkMid,marginTop:3}}>Period: {e?e.period:emp201Period} — Due: <strong style={{color:C.red}}>{dueDate}</strong></p>
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <input type="month" value={emp201Period} onChange={ev=>{ setEmp201Period(ev.target.value); setEmp201Data(null); }}
              style={{padding:"7px 12px",border:"1px solid "+C.border,borderRadius:8,fontSize:12,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}/>
            <button onClick={loadEmp201} style={{background:C.accent,color:"#fff",border:"none",borderRadius:8,padding:"8px 16px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
              {emp201Loading?"Loading…":"Load"}
            </button>
            <ExcelBtn filename="emp201.csv" data={empList.map(emp=>({Name:emp.employee_name,Number:emp.employee_number,Gross:emp.gross_salary,PAYE:emp.paye,UIF_Emp:emp.uif_employee,UIF_Empr:emp.uif_employer,SDL:emp.sdl,Net_Pay:emp.net_pay}))}/>
            <PrintBtn onClick={()=>{const el=document.getElementById("emp201-print");if(!el)return;const w=window.open("","_blank");w.document.write("<html><body>"+el.innerHTML+"</body></html>");w.document.close();w.print();}}/>
          </div>
        </div>
        <div id="emp201-print">
          <div style={{display:"flex",gap:12,marginBottom:20,flexWrap:"wrap"}}>
            <KPI label="PAYE Due"       value={fmt(totalPaye)} color={C.red}    icon="💼" sub="Income tax withheld"/>
            <KPI label="UIF Due"        value={fmt(totalUif)}  color={C.gold}   icon="🛡️" sub="Emp + employer 1%"/>
            <KPI label="SDL Due"        value={fmt(totalSdl)}  color={C.blue}   icon="📚" sub="Skills levy 1%"/>
            <KPI label="Total Due SARS" value={fmt(totalDue)}  color={C.accent} icon="🏛️" sub={"By "+dueDate}/>
          </div>
          <div style={{background:C.redLt,border:"1px solid "+C.red+"30",borderRadius:12,padding:"12px 18px",marginBottom:20,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <div style={{fontSize:13,fontWeight:700,color:C.red}}>SARS eFiling Reminder</div>
              <div style={{fontSize:11,color:C.inkMid,marginTop:3}}>EMP201 due by the 7th. Late submission incurs a 10% penalty.</div>
            </div>
            <a href="https://efiling.sars.gov.za" target="_blank" rel="noreferrer" style={{background:C.red,color:"#fff",padding:"8px 16px",borderRadius:8,fontSize:12,fontWeight:700,textDecoration:"none",whiteSpace:"nowrap"}}>Open eFiling</a>
          </div>
          <div style={{background:C.surface,border:"1px solid "+C.border,borderRadius:16,overflow:"hidden"}}>
            <div style={{padding:"14px 20px",borderBottom:"1px solid "+C.border,fontSize:13,fontWeight:700,color:C.ink}}>Employee Breakdown</div>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
              <thead>
                <tr style={{background:C.bg,borderBottom:"1px solid "+C.border}}>
                  {["Employee","Emp #","Gross","PAYE","UIF (Emp)","UIF (Empr)","SDL","Net Pay"].map(h=>(
                    <th key={h} style={{padding:"10px 14px",textAlign:"left",fontSize:9,color:C.inkMid,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase",whiteSpace:"nowrap"}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {empList.map((emp,i)=>(
                  <tr key={i} style={{borderBottom:"1px solid "+C.border+"20"}}>
                    <td style={{padding:"11px 14px",fontWeight:600,color:C.ink}}>{emp.employee_name}</td>
                    <td style={{padding:"11px 14px",color:C.inkMid,fontSize:11}}>{emp.employee_number}</td>
                    <td style={{padding:"11px 14px",fontWeight:600}}>{fmt(emp.gross_salary)}</td>
                    <td style={{padding:"11px 14px",color:C.red}}>{fmt(emp.paye)}</td>
                    <td style={{padding:"11px 14px",color:C.gold}}>{fmt(emp.uif_employee)}</td>
                    <td style={{padding:"11px 14px",color:C.blue}}>{fmt(emp.uif_employer)}</td>
                    <td style={{padding:"11px 14px",color:C.blue}}>{fmt(emp.sdl)}</td>
                    <td style={{padding:"11px 14px",fontWeight:700,color:C.green}}>{fmt(emp.net_pay)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{background:C.bg,borderTop:"2px solid "+C.border}}>
                  <td colSpan={2} style={{padding:"11px 14px",fontWeight:800,color:C.ink}}>TOTALS</td>
                  <td style={{padding:"11px 14px",fontWeight:800}}>{fmt(empList.reduce((s,e)=>s+(e.gross_salary||0),0))}</td>
                  <td style={{padding:"11px 14px",fontWeight:800,color:C.red}}>{fmt(totalPaye)}</td>
                  <td style={{padding:"11px 14px",fontWeight:800,color:C.gold}}>{fmt(empList.reduce((s,e)=>s+(e.uif_employee||0),0))}</td>
                  <td style={{padding:"11px 14px",fontWeight:800,color:C.blue}}>{fmt(empList.reduce((s,e)=>s+(e.uif_employer||0),0))}</td>
                  <td style={{padding:"11px 14px",fontWeight:800,color:C.blue}}>{fmt(totalSdl)}</td>
                  <td style={{padding:"11px 14px",fontWeight:800,color:C.green}}>{fmt(empList.reduce((s,e)=>s+(e.net_pay||0),0))}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
        <div>
          <div style={{display:"flex",gap:4,marginBottom:0}}>
            {["payroll","leave","benefits","emp201"].map(s => (
              <button key={s} onClick={()=>setPayrollSection(s)} style={{background:"none",border:"none",borderBottom:payrollSection===s?`2px solid ${C.accent}`:"2px solid transparent",padding:"4px 14px",fontSize:13,fontWeight:payrollSection===s?700:400,color:payrollSection===s?C.accent:C.inkMid,cursor:"pointer",fontFamily:"inherit"}}>
                {SECTION_LABELS[s]}
              </button>
            ))}
          </div>
          <p style={{fontSize:12,color:C.inkMid,marginTop:4}}>SARS-compliant PAYE, UIF, SDL, EMP201, IRP5</p>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <span style={{fontSize:12,color:C.inkMid,fontWeight:600}}>Tax Year:</span>
            <select value={taxYear} onChange={e=>setTaxYear(e.target.value)} style={{padding:"7px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:12,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",fontWeight:700}}>
              {Object.keys(TAX_YEARS).reverse().map(y=>(
                <option key={y} value={y}>{y}{y===CURRENT_TAX_YEAR?" (current)":""}</option>
              ))}
            </select>
          </div>
          <button onClick={()=>setShowNew(true)} style={{background:C.accent,color:"#fff",border:"none",borderRadius:10,padding:"10px 20px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>+ Add Employee</button>
        </div>
      </div>
      <div style={{background:C.goldLt,border:`1px solid ${C.gold}40`,borderRadius:12,padding:"12px 18px",marginBottom:20,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div style={{fontSize:12,color:C.inkMid}}>ZuZan Payroll Module - {employees.length} employees x R15.50 = <strong style={{color:C.accent}}>{fmt(zuZanFee)}/month</strong></div>
        <Badge label="Active" color={C.green} bg={C.greenLt}/>
      </div>
      {/* ── Pay Schedule Tabs ──────────────────────────────────────────────── */}
      <div style={{display:"flex",gap:0,marginBottom:16,borderBottom:`1px solid ${C.border}`}}>
        {[["security","🛡️ Security Officers"],["salaried","💼 Salaried Staff"],["casual","👷 Casual / Temp"]].map(([key,label])=>(
          <button key={key} onClick={()=>setPayScheduleTab(key)}
            style={{background:"none",border:"none",borderBottom:payScheduleTab===key?`2px solid ${C.accent}`:"2px solid transparent",
              padding:"8px 20px",fontSize:13,fontWeight:payScheduleTab===key?700:400,
              color:payScheduleTab===key?C.accent:C.inkMid,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>
            {label}
            <span style={{marginLeft:6,background:payScheduleTab===key?C.accent:"#e5e7eb",color:payScheduleTab===key?"#fff":C.inkMid,
              borderRadius:10,padding:"1px 7px",fontSize:11,fontWeight:700}}>
              {activeEmployees.filter(e=>empSchedule(e)===key).length}
            </span>
          </button>
        ))}
      </div>
      {scheduleEmployees.length === 0 && (
        <div style={{background:C.surface,border:`1px dashed ${C.border}`,borderRadius:12,padding:"32px 24px",textAlign:"center",marginBottom:20}}>
          <div style={{fontSize:28,marginBottom:8}}>{payScheduleTab==="security"?"🛡️":payScheduleTab==="casual"?"👷":"💼"}</div>
          <div style={{fontWeight:700,fontSize:15,color:C.ink,marginBottom:4}}>
            No {payScheduleTab==="security"?"security officer":payScheduleTab==="casual"?"casual / temp":"salaried"} employees found
          </div>
          <div style={{fontSize:12,color:C.inkMid}}>
            Add employees and set their pay schedule to <strong>{payScheduleTab==="security"?"Security":payScheduleTab==="casual"?"Casual / Temp":"Salaried"}</strong> to see them here.
          </div>
        </div>
      )}
      {scheduleEmployees.length > 0 && <>
      <div style={{display:"flex",gap:12,marginBottom:20,flexWrap:"wrap"}}>
        <KPI label="Gross Payroll" value={fmt(totalGross)} color={C.ink} icon="💼"/>
        <KPI label="PAYE to SARS" value={fmt(totalPAYE)} color={C.red} icon="🏛️"/>
        <KPI label="UIF and SDL" value={fmt(totalUIF+totalSDL)} color={C.gold} icon="🛡️"/>
        <KPI label="Total Cost" value={fmt(totalCost)} color={C.accent} icon="📊"/>
      </div>
      <div style={{background:`linear-gradient(135deg,${C.accentLt},transparent)`,border:`1px solid ${C.accent}40`,borderRadius:16,padding:"16px 24px",marginBottom:20,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div>
          <div style={{fontWeight:700,fontSize:15,color:C.ink}}>
            {new Date().toLocaleDateString("en-ZA",{month:"long",year:"numeric"})} — {payScheduleTab==="security"?"Security Officers":payScheduleTab==="casual"?"Casual / Temp":"Salaried Staff"} Payroll
            {payrollRun && <span style={{marginLeft:10,background:C.greenLt,color:C.green,borderRadius:8,padding:"2px 10px",fontSize:11,fontWeight:700}}>✓ Run</span>}
          </div>
          <div style={{fontSize:12,color:C.inkMid,marginTop:3}}>
            {scheduleEmployees.length} employees · Net pay: {fmt(totalNet)} · SARS due: 7 {new Date(new Date().getFullYear(),new Date().getMonth()+1,7).toLocaleDateString("en-ZA",{month:"long",year:"numeric"})}
          </div>
        </div>
        <button onClick={() => {
          // Initialise overtime entries for current schedule's employees only
          const init = {};
          scheduleEmployees.forEach(e => { init[e.id] = payScheduleTab==="casual" ? {daysWorked:""} : {otHours:0, sunHours:0, phHours:0}; });
          setOtData(prev => ({...prev, ...init}));
          setSecArea("3"); // NBCPSS Area 3 rates only
          setShowOtModal(true);
        }} style={{background:C.accent,color:"#fff",border:"none",borderRadius:10,padding:"10px 20px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
          Run {payScheduleTab==="security"?"Security":payScheduleTab==="casual"?"Casual":"Salaried"} Payroll
        </button>
      </div>
      </>}
      {/* ── BCEA Overtime Entry Modal ────────────────────────────────────── */}
      {showOtModal && (
        <div style={{position:"fixed",inset:0,background:"#00000070",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
          <div style={{background:C.surface,borderRadius:20,padding:32,width:"100%",maxWidth:760,maxHeight:"90vh",overflowY:"auto",boxShadow:"0 8px 40px #00000030"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
              <h3 style={{fontFamily:"serif",fontSize:22,color:C.ink,margin:0}}>Run Payroll — {payScheduleTab==="casual"?"Days Worked Entry":(user?.industry||"").toLowerCase().replace(/[\s-]/g,"_")==="private_security"?"Overtime & Security Allowances":"Overtime Entry"}</h3>
              <button onClick={()=>setShowOtModal(false)} style={{background:"none",border:"none",fontSize:22,cursor:"pointer",color:C.inkMid}}>×</button>
            </div>
            <p style={{fontSize:12,color:C.inkMid,marginBottom:16}}>Enter hours per employee for this pay period. <strong>Weekday/Sat OT</strong> = extra hours above normal shift (×1.5). {(user?.industry||"").toLowerCase().replace(/[\s-]/g,"_")==="private_security"
              ? <><strong>Sunday Hrs Worked</strong> = total hours worked on Sundays, paid at 1.5× the hourly rate (NBCPSS rate for officers who ordinarily work Sundays). Enter 12 per 12-hour Sunday shift, 24 for two Sundays, etc. <strong>Public Holiday Hrs Worked</strong> = hours actually worked on a public holiday, paid at 1.0× on top of salary (the normal day&apos;s pay is already in the monthly salary). Night shifts and special-allowance shifts are entered in the columns on the right.</>
              : <><strong>Sunday Hrs Worked</strong> = total hours worked on Sunday (×2, all hours). Default shift = 12 h; enter 12 per Sunday, 24 for two Sundays, etc. <strong>Public Holiday Hrs Worked</strong> = hours worked on a PH, paid at 1.0× ADDITIONAL to normal pay (the day is already in the monthly salary, so the total for the day is 2×). Hourly/casual staff: also include these hours in Normal Hours.</>}</p>

            {/* ── NBCPSS rate area info (Area 3 only) ── */}
            {(user?.industry||"").toLowerCase().replace(/[\s-]/g,"_")==="private_security" && (
              <div style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:12,padding:"12px 18px",marginBottom:16,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                <span style={{fontSize:12,fontWeight:700,color:C.ink}}>🔒 NBCPSS Area 3 Rates</span>
                <span style={{fontSize:11,color:C.inkMid}}>Grade A: R7,142 · Grade B/C/D/E: R6,726 per month</span>
              </div>
            )}

            {/* ── MIBCO Sector 5 rate info (fuel station companies only) ── */}
            {(user?.industry||"").toLowerCase().replace(/[\s-]/g,"_")==="fuel_station" && (
              <div style={{background:"#fffbeb",border:"1px solid #fbbf24",borderRadius:12,padding:"14px 18px",marginBottom:16,display:"flex",alignItems:"center",gap:14,flexWrap:"wrap"}}>
                <span style={{fontSize:12,fontWeight:700,color:"#92400e"}}>⛽ MIBCO Sector 5 — Minimum Hourly Rates (eff. 22 Dec 2025)</span>
                <span style={{fontSize:11,color:"#78350f"}}>
                  Forecourt Attendant: R45.79/hr (R8,929/mo) · Cashier: R45.30/hr (R8,834/mo) · Char/Cleaner: R34.64/hr (R6,755/mo)
                </span>
                <span style={{fontSize:10,color:"#a16207"}}>Medical Insurance Allowance R19.62/wk added automatically. Health scheme (Affinity Health via MIBCO) contributions applied if enrolled.</span>
              </div>
            )}

            {/* ── CSV / Excel upload strip ── */}
            <div style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:12,padding:"14px 18px",marginBottom:20}}>
              <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                <span style={{fontSize:12,fontWeight:700,color:C.ink}}>📂 Upload overtime from file</span>
                <button onClick={() => {
                  // Generate and download a template CSV/XLSX with employee list pre-filled
                  const isSec = (user?.industry||"").toLowerCase().replace(/[\s-]/g,"_")==="private_security";
                  const rows = scheduleEmployees.map(e => ({
                    "Employee Number":      e.employee_number || e.id,
                    "Employee Name":        e.name,
                    ...(isSec ? {"Normal_Hours": 208} : {}),
                    "Weekday_Sat_OT_Hours": 0,
                    "Sunday_Hours":         0,
                    "Public_Holiday_Hours": 0,
                    ...(isSec ? {"Night_Shift_Shifts": 0, "Special_Allow_Shifts": 0} : {}),
                  }));
                  const ws = XLSX.utils.json_to_sheet(rows);
                  ws["!cols"] = [{wch:18},{wch:28},{wch:22},{wch:16},{wch:22},{wch:20},{wch:22}];
                  const wb = XLSX.utils.book_new();
                  XLSX.utils.book_append_sheet(wb, ws, "Overtime");
                  XLSX.writeFile(wb, `overtime_${new Date().toISOString().slice(0,7)}.xlsx`);
                }} style={{background:C.greenLt,color:C.green,border:`1px solid ${C.green}40`,borderRadius:8,padding:"7px 14px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                  ⬇ Download Template
                </button>
                <label style={{background:C.blueLt,color:C.blue,border:`1px solid ${C.blue}40`,borderRadius:8,padding:"7px 14px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                  ⬆ {Object.values(otData).some(o=>o.otHours||o.sunHours||o.phHours||o.normalHours) ? "Change File" : "Upload CSV / Excel"}
                  <input type="file" accept=".csv,.xlsx,.xls" style={{display:"none"}} onChange={e => {
                    const file = e.target.files[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = ev => {
                      try {
                        const wb = XLSX.read(ev.target.result, {type:"binary"});
                        const ws = wb.Sheets[wb.SheetNames[0]];
                        const rows = XLSX.utils.sheet_to_json(ws, {defval:0});
                        let matched = 0, unmatched = [];
                        const newOt = {...otData};
                        const newSec = {};
                        rows.forEach(row => {
                          // Accept employee_number, employee name, or id as lookup key
                          const empNum = String(row["Employee Number"] || row["employee_number"] || row["EmpNo"] || "").trim();
                          const empName = String(row["Employee Name"]   || row["employee_name"]   || row["Name"]  || "").trim().toLowerCase();
                          // Skip legend/notes/header rows that are clearly not employee records
                          const rawKey = empNum || empName;
                          if (!rawKey) return; // blank row
                          if (/^notes?[:\s]|special_allow|=|^[0-9\s,]+$/.test(rawKey)) return; // notes / column labels
                          const emp = employees.find(e =>
                            (empNum && (String(e.employee_number||"").trim() === empNum || String(e.id) === empNum)) ||
                            (empName && e.name.toLowerCase() === empName)
                          );
                          if (!emp) { unmatched.push(rawKey); return; }
                          matched++;
                          newOt[emp.id] = {
                            normalHours: +(row["Normal_Hours"] || row["normal_hours"] || row["Hours Worked"] || row["Ordinary Hours"] || 0),
                            otHours:  +(row["Weekday_Sat_OT_Hours"]  || row["overtime_hours"]  || row["OT Hours"]        || 0),
                            sunHours: +(row["Sunday_Hours"]           || row["sunday_hours"]    || row["Sunday OT Hours"] || 0),
                            phHours:  +(row["Public_Holiday_Hours"]   || row["ph_hours"]        || row["PH Hours"]        || 0),
                          };
                          // NBCPSS security columns
                          const ns = +(row["Night_Shift_Shifts"] || row["Night Shift Shifts"] || row["night_shifts"] || 0);
                          const ss = +(row["Special_Allow_Shifts"] || row["Special Allow Shifts"] || row["special_shifts"] || 0);
                          if (ns || ss) newSec[emp.id] = {nightShifts: ns, specialShifts: ss};
                        });
                        setOtData(newOt);
                        if (Object.keys(newSec).length) setSecData(prev => ({...prev, ...newSec}));
                        if (unmatched.length) alert(`Imported ${matched} employees.\n\nCould not match ${unmatched.length} row(s):\n${unmatched.join(", ")}\n\nCheck that Employee Number in your file matches the payroll list.`);
                        else alert(`✅ Imported ${matched} employee${matched!==1?"s":""} from file.`);
                      } catch(err) {
                        alert("Could not read file: " + err.message);
                      }
                    };
                    reader.readAsBinaryString(file);
                    e.target.value = "";   // allow re-upload of same file
                  }}/>
                </label>
                <button onClick={async () => {
                  // ── Load hours directly from the clocking system ──────────
                  try {
                    const today     = new Date().toISOString().slice(0,10);
                    const monthStart= today.slice(0,8) + '01';
                    const from = prompt("Clocking period FROM (yyyy-mm-dd):", monthStart);
                    if (!from) return;
                    const to   = prompt("Clocking period TO (yyyy-mm-dd):", today);
                    if (!to) return;
                    const token = localStorage.getItem("zuzan_token");
                    const resp = await fetch(`${BASE_URL}/clocking/payroll-hours?date_from=${from}&date_to=${to}&daily_threshold=8&round_minutes=15`, {
                      headers: { Authorization: `Bearer ${token||""}` }
                    });
                    if (!resp.ok) throw new Error(await resp.text());
                    const rows = await resp.json();
                    let matched = 0, unmatched = [];
                    const clockIssues = [];
                    const newOt = {...otData};
                    const newSec = {};
                    rows.forEach(row => {
                      const empNum  = String(row.employee_number || "").trim();
                      const empName = String(row.name || "").trim().toLowerCase();
                      const emp = employees.find(e =>
                        (empNum && (String(e.employee_number||"").trim() === empNum || String(e.id) === empNum)) ||
                        (empName && e.name.toLowerCase() === empName)
                      );
                      if (!emp) { unmatched.push(row.name || empNum || "(unknown)"); return; }
                      matched++;
                      // Security officers are paid their contracted monthly salary: never send clocked
                      // "normal hours" for them (payroll would rebuild basic pay as hours x rate).
                      newOt[emp.id] = {
                        ...(newOt[emp.id] || {}),
                        ...(row.apply_normal_hours === false ? {} : { normalHours: row.normal_hours || 0 }),
                        otHours:     row.ot_hours     || 0,
                        sunHours:    row.sunday_hours || 0,
                        phHours:     row.ph_hours     || 0,
                      };
                      // Merge, never replace: keep special-allowance shifts, unauthorised hours and the bonus tick.
                      if (emp.security_grade) newSec[emp.id] = { nightShifts: row.night_shift_shifts || 0 };
                      (row.exceptions || []).forEach(x => clockIssues.push(`${row.name}: ${x.message}`));
                    });
                    setOtData(newOt);
                    if (Object.keys(newSec).length) setSecData(prev => {
                      const next = {...prev};
                      Object.keys(newSec).forEach(id => { next[id] = {...(prev[id] || {}), ...newSec[id]}; });
                      return next;
                    });
                    let msg = `✅ Loaded ${matched} employee${matched!==1?"s":""} from clocking (${from} → ${to}).`;
                    if (unmatched.length) msg += `\n\nCould not match ${unmatched.length}: ${unmatched.join(", ")}`;
                    if (clockIssues.length) msg += `\n\n⚠️ ${clockIssues.length} clocking problem(s) - fix in the clocking app BEFORE running payroll (affected shifts are not paid):\n` + clockIssues.slice(0, 15).join("\n") + (clockIssues.length > 15 ? `\n…and ${clockIssues.length - 15} more` : "");
                    alert(msg);
                  } catch(err) {
                    alert("Could not load from clocking: " + err.message);
                  }
                }} style={{background:"#f0fdf4",color:"#16a34a",border:"1px solid #86efac",borderRadius:8,padding:"7px 14px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                  📍 Load from Clocking
                </button>
                {Object.values(otData).some(o=>o.otHours||o.sunHours||o.phHours||o.normalHours) && (
                  <button onClick={()=>{setOtData({});setSecData({});}}
                    style={{background:C.redLt,color:C.red,border:`1px solid ${C.red}40`,borderRadius:8,padding:"7px 14px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                    🗑 Clear Upload
                  </button>
                )}
                <span style={{fontSize:11,color:C.inkMid}}>{(user?.industry||"").toLowerCase().replace(/[\s-]/g,"_")==="private_security" ? "Columns: Employee Number · Employee Name · Normal_Hours · Weekday_Sat_OT_Hours · Sunday_Hours · Public_Holiday_Hours · Night_Shift_Shifts · Special_Allow_Shifts" : "Columns: Employee Number · Employee Name · Weekday_Sat_OT_Hours · Sunday_Hours · Public_Holiday_Hours"}</span>
              </div>
            </div>

            {/* ── NBCPSS Annual Bonus banner: officers whose employment anniversary falls in this pay month ── */}
            {(user?.industry||"").toLowerCase().replace(/[\s-]/g,"_")==="private_security" && (()=>{
              const ym = new Date().toISOString().slice(0,7);
              const dueEmps = scheduleEmployees.filter(e=>e.security_grade && nbcpssBonusDue(e.start_date, ym));
              const noStart = scheduleEmployees.filter(e=>e.security_grade && !e.start_date).length;
              if (dueEmps.length === 0 && noStart === 0) return null;
              const bonusTotal = scheduleEmployees.reduce((t,e)=>t+nbcpssBonusFor(e, secData[e.id], ym),0);
              return (
                <div style={{background:"#fefce8",border:"1px solid #fbbf24",borderRadius:12,padding:"14px 18px",marginBottom:16,display:"flex",alignItems:"center",gap:14,flexWrap:"wrap"}}>
                  <span style={{fontSize:20}}>🎁</span>
                  <div style={{flex:1}}>
                    <div style={{fontSize:12,fontWeight:700,color:C.ink}}>NBCPSS Annual Bonus — {dueEmps.length} officer{dueEmps.length===1?"":"s"} reach{dueEmps.length===1?"es":""} their employment anniversary this month</div>
                    <div style={{fontSize:11,color:C.inkMid}}>Clause 8: one month's salary less pay for unauthorised absence hours ((A×12 − B×C) ÷ 12). Tick or untick each officer in the Annual Bonus column, and enter any unauthorised absence hours. Total included in this run: <strong>{fmt(bonusTotal)}</strong>{noStart>0 && <> · {noStart} officer{noStart===1?" has":"s have"} no start date, so the bonus can only be added by ticking it manually.</>}</div>
                  </div>
                </div>
              );
            })()}

            {/* ── Casual / Temp: days-worked table ── */}
            {payScheduleTab === "casual" ? (
              <div style={{overflowX:"auto",overflowY:"auto",maxHeight:480,border:`1px solid ${C.border}`,borderRadius:12,marginBottom:20}}>
                <div style={{background:"#fffbeb",border:"1px solid #fbbf24",borderRadius:"10px 10px 0 0",padding:"10px 16px",fontSize:12,color:"#92400e"}}>
                  <strong>Casual / Temp payroll</strong> — enter the number of days each worker worked this period. Pay = Days × Daily Rate. UIF and PAYE apply as normal.
                </div>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                  <thead><tr style={{background:C.bg}}>
                    {["Employee","Daily Rate","Days Worked","Gross Preview","Est. PAYE","Est. UIF","Est. Net"].map(h=>(
                      <th key={h} style={{padding:"10px 12px",textAlign:"left",fontSize:10,color:C.inkMid,fontWeight:700,textTransform:"uppercase",letterSpacing:0.4,borderBottom:`1px solid ${C.border}`}}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {scheduleEmployees.map(emp=>{
                      const days = +otData[emp.id]?.daysWorked || 0;
                      const dailyRate = emp.hourly_rate ? emp.hourly_rate * 8 : (emp.salary || 0) / 26;
                      const grossPreview = days * dailyRate;
                      // Rough PAYE/UIF preview using calc engine at actual gross
                      const pc = grossPreview > 0 ? calcPayroll(grossPreview, taxYear, 0, 0, 0, emp.hourly_rate||null, 0, 0, 0, 0, 0, 0, 0, {isSecurity:false}) : null;
                      const inpStyle = {width:"70px",padding:"6px 8px",border:`1px solid ${C.border}`,borderRadius:6,fontSize:12,fontFamily:"inherit",textAlign:"center",background:C.bg,color:C.ink,outline:"none"};
                      return (
                        <tr key={emp.id} style={{borderBottom:`1px solid ${C.border}30`}}>
                          <td style={{padding:"10px 12px"}}>
                            <div style={{fontWeight:600,color:C.ink}}>{emp.name}</div>
                            <div style={{fontSize:10,color:C.inkMid}}>{emp.employee_number||emp.id}</div>
                          </td>
                          <td style={{padding:"10px 12px",color:C.inkMid}}>{fmt(dailyRate)}/day</td>
                          <td style={{padding:"10px 12px"}}>
                            <input style={inpStyle} type="number" min="0" max="31" step="1"
                              value={otData[emp.id]?.daysWorked ?? ""}
                              placeholder="0"
                              onChange={e=>setOtData(prev=>({...prev,[emp.id]:{...prev[emp.id],daysWorked:e.target.value}}))}/>
                          </td>
                          <td style={{padding:"10px 12px",fontWeight:700,color:grossPreview>0?C.green:C.inkMid}}>{grossPreview>0?fmt(grossPreview):"—"}</td>
                          <td style={{padding:"10px 12px",color:C.red}}>{pc?fmt(pc.paye):"—"}</td>
                          <td style={{padding:"10px 12px",color:C.gold}}>{pc?fmt(pc.uifEmployee):"—"}</td>
                          <td style={{padding:"10px 12px",fontWeight:600,color:C.accent}}>{pc?fmt(pc.netPay):"—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
            <div style={{overflowX:"auto",overflowY:"auto",maxHeight:480,border:`1px solid ${C.border}`,borderRadius:12,marginBottom:20,scrollbarWidth:"thin",scrollbarColor:`${C.border} transparent`}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:12,minWidth:800}}>
              <thead style={{position:"sticky",top:0,zIndex:2}}>
                <tr style={{background:C.bg}}>
                  {[
                    "Employee","Grade","BCEA Hourly Rate",
                    ...((user?.industry||"").toLowerCase().replace(/[\s-]/g,"_")==="private_security" ? ["Normal Hrs (hourly)"] : []),
                    ...((user?.industry||"").toLowerCase().replace(/[\s-]/g,"_")==="fuel_station" ? ["MIBCO Role"] : []),
                    "Weekday/Sat OT hrs","Sunday Hrs Worked","Public Holiday Hrs Worked","OT Pay Preview",
                    ...((user?.industry||"").toLowerCase().replace(/[\s-]/g,"_")==="private_security" ? ["Night Shift Shifts","Special Allow. Shifts","Unauth. Absence Hrs","Annual Bonus","Security Preview"] : [])
                  ].map(h=>(
                    <th key={h} style={{padding:"10px 12px",textAlign:"left",fontSize:10,color:C.inkMid,fontWeight:700,textTransform:"uppercase",letterSpacing:0.4,borderBottom:`1px solid ${C.border}`}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {scheduleEmployees.map(emp => {
                  const ot = otData[emp.id] || {normalHours:0,otHours:0,sunHours:0,phHours:0};
                  const isSecurity = (user?.industry||"").toLowerCase().replace(/[\s-]/g,"_")==="private_security";
                  const isFuelStation = (user?.industry||"").toLowerCase().replace(/[\s-]/g,"_")==="fuel_station";
                  // For security employees, effective monthly = max(contracted salary, NBCPSS area+grade minimum)
                  const _gradeKey = ((emp.security_grade||"C").toUpperCase().match(/[A-E]/)||["C"])[0];
                  // area-aware (matches backend: an employee with no area is treated as Areas 1 & 2)
                  const _areaMin = nbcpssMinMonthly(_gradeKey, emp.security_area||"1_2") || nbcpssMinMonthly("C", emp.security_area||"1_2");
                  // For fuel station employees, effective monthly = max(contracted salary, MIBCO role minimum)
                  const _MIBCO_HR = {forecourt_attendant:45.79,cashier:45.30,char:34.64};
                  const _mibcoHr = _MIBCO_HR[(emp.mibco_role||"").toLowerCase()] || 0;
                  const _mibcoMonthlyMin = _mibcoHr > 0 ? Math.round(_mibcoHr * 45 * (52/12) * 100) / 100 : 0;
                  const effectiveSalary = isSecurity ? Math.max(emp.salary||0, _areaMin) : isFuelStation && _mibcoMonthlyMin > 0 ? Math.max(emp.salary||0, _mibcoMonthlyMin) : (emp.salary||0);
                  // audit fix 2026-09-16 — gate on the employee's own security_grade
                  // (matches backend is_sec = is_security_co and bool(sec_grade)), not
                  // just the company's industry, so non-security staff at a private
                  // security company still use the general 195h/month BCEA default.
                  const isSecurityEmp = isSecurity && !!emp.security_grade;
                  // NBCPSS OT rate is always the grade minimum ÷ 208h, not contracted salary ÷ 208h.
                  // Grade B/C/D/E → R6,726 ÷ 208 = R32.34/hr; Grade A → R7,142 ÷ 208 = R34.34/hr.
                  const secHrOverride = isSecurity ? (_areaMin / NBCPSS_PRESCRIBED_HOURS) : null;
                  const hrRate = emp.hourly_rate || secHrOverride || null;
                  const hr = bceaHourlyRate(effectiveSalary, hrRate, isSecurityEmp);
                  const preview = calcOvertime(effectiveSalary, +ot.otHours||0, +ot.sunHours||0, +ot.phHours||0, hrRate, isSecurityEmp, !!emp.sunday_ordinary);
                  const inpStyle = {width:"60px",padding:"6px 8px",border:`1px solid ${C.border}`,borderRadius:6,fontSize:12,fontFamily:"inherit",textAlign:"center",background:C.bg,color:C.ink,outline:"none"};
                  const setOt = (k,v) => setOtData(prev=>({...prev,[emp.id]:{...prev[emp.id],[k]:v}}));
                  return (
                    <tr key={emp.id} style={{borderBottom:`1px solid ${C.border}30`}}>
                      <td style={{padding:"10px 12px"}}>
                        <div style={{fontWeight:600,color:C.ink}}>{emp.name}</div>
                        <div style={{fontSize:10,color:C.inkMid}}>{emp.employee_number||emp.id}</div>
                      </td>
                      <td style={{padding:"10px 12px"}}>
                        {(()=>{const g=isSecurityEmp?emp.security_grade:emp.grade;return g?<Badge label={g} color={C.blue} bg={C.blueLt}/>:<span style={{color:C.inkMid}}>—</span>;})()}
                      </td>
                      <td style={{padding:"10px 12px",color:C.inkMid}}>{fmt(hr)}/hr</td>
                      {isFuelStation && (
                        <td style={{padding:"10px 12px"}}>
                          {emp.mibco_role
                            ? <span style={{fontSize:10,fontWeight:600,color:"#92400e",background:"#fffbeb",border:"1px solid #fbbf24",borderRadius:5,padding:"2px 6px"}}>{{"forecourt_attendant":"Forecourt","cashier":"Cashier","char":"Char"}[emp.mibco_role]||emp.mibco_role}</span>
                            : <span style={{color:C.inkMid,fontSize:11}}>—</span>}
                          {_mibcoHr > 0 && <div style={{fontSize:9,color:"#a16207",marginTop:2}}>min R{_mibcoHr.toFixed(2)}/hr</div>}
                        </td>
                      )}
                      {isSecurity && (
                        <td style={{padding:"10px 12px"}}>
                          {(emp.employment_type||"").toLowerCase().replace(/[\s_]/g,"") === "hourlypaid" || (emp.employment_type||"").toLowerCase() === "hourly"
                            ? <input style={{...inpStyle,width:"68px",borderColor:(+ot.normalHours||208)<208?"#f59e0b":C.border}} type="number" min="0" max="250" step="1"
                                value={ot.normalHours!=null&&ot.normalHours!==""?ot.normalHours:208} placeholder="208"
                                onChange={e=>setOt("normalHours",e.target.value)}/>
                            : <span style={{color:C.inkMid,fontSize:11}}>{fmt(emp.salary)}</span>}
                        </td>
                      )}
                      <td style={{padding:"10px 12px"}}>
                        <input style={inpStyle} type="number" min="0" max="10" step="0.5" value={ot.otHours||""} placeholder="0" onChange={e=>setOt("otHours",e.target.value)}/>
                      </td>
                      <td style={{padding:"10px 12px"}}>
                        <input style={inpStyle} type="number" min="0" max="96" step="1" value={ot.sunHours||""} placeholder="12" onChange={e=>setOt("sunHours",e.target.value)}/>
                      </td>
                      <td style={{padding:"10px 12px"}}>
                        <input style={inpStyle} type="number" min="0" step="0.5" value={ot.phHours||""} placeholder="0" onChange={e=>setOt("phHours",e.target.value)}/>
                      </td>
                      <td style={{padding:"10px 12px",fontWeight:700,color:preview.total>0?C.green:C.inkMid}}>
                        {preview.total > 0 ? `+ ${fmt(preview.total)}` : "—"}
                      </td>
                      {(user?.industry||"").toLowerCase().replace(/[\s-]/g,"_")==="private_security" && (()=>{
                        const sec = secData[emp.id] || {nightShifts:0,specialShifts:0};
                        const setSec = (k,v) => setSecData(prev=>({...prev,[emp.id]:{...prev[emp.id],[k]:v}}));
                        const nightPrev = (+sec.nightShifts||0) * 8.00;
                        const specPrev  = (+sec.specialShifts||0) * 10.50;
                        const secTotal  = nightPrev + specPrev + (emp.security_grade ? 32.00 : 0);
                        return (<>
                          <td style={{padding:"10px 12px"}}>
                            <input style={inpStyle} type="number" min="0" step="1" value={sec.nightShifts||""} placeholder="0" onChange={e=>setSec("nightShifts",e.target.value)}/>
                          </td>
                          <td style={{padding:"10px 12px"}}>
                            <input style={inpStyle} type="number" min="0" step="1" value={sec.specialShifts||""} placeholder="0" onChange={e=>setSec("specialShifts",e.target.value)}/>
                          </td>
                          <td style={{padding:"10px 12px"}}>
                            <input style={inpStyle} type="number" min="0" step="0.5" value={sec.unauthHrs||""} placeholder="0" title="Hours lost to UNAUTHORISED absence or late coming. Approved leave never counts." onChange={e=>setSec("unauthHrs",e.target.value)}/>
                          </td>
                          <td style={{padding:"10px 12px",whiteSpace:"nowrap"}}>
                            {emp.security_grade ? (()=>{
                              const _ym = new Date().toISOString().slice(0,7);
                              const _on = nbcpssBonusOn(emp, sec, _ym);
                              const _due = nbcpssBonusDue(emp.start_date, _ym);
                              return (<label style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:11,color:_on?"#15803d":C.inkMid,fontWeight:_on?700:400}}>
                                <input type="checkbox" checked={_on} onChange={e=>setSec("bonusOn", e.target.checked)}/>
                                {_on ? `+ ${fmt(nbcpssBonusFor(emp, sec, _ym))}` : (_due ? "skipped" : "not due")}
                              </label>);
                            })() : <span style={{color:C.inkMid}}>—</span>}
                          </td>
                          <td style={{padding:"10px 12px",fontWeight:700,color:secTotal>0?"#c2410c":C.inkMid}}>
                            {secTotal > 0 ? `+ ${fmt(secTotal)}` : "—"}
                          </td>
                        </>);
                      })()}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
            )} {/* end casual/else ternary */}

            {/* ── Once-off items (shown for all pay schedule tabs) ────────── */}
            <div style={{marginTop:16,borderTop:`1px solid ${C.border}`,paddingTop:14}}>
              <div style={{fontSize:12,fontWeight:700,color:C.inkMid,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8}}>Once-off Items (this pay run only)</div>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                <thead>
                  <tr style={{background:C.surface}}>
                    <th style={{padding:"8px 10px",textAlign:"left",fontWeight:600,color:C.inkMid,fontSize:11}}>Employee</th>
                    <th style={{padding:"8px 10px",textAlign:"left",fontWeight:600,color:C.inkMid,fontSize:11}}>Expense Claim (R)</th>
                    <th style={{padding:"8px 10px",textAlign:"left",fontWeight:600,color:C.inkMid,fontSize:11}}>Once-off Deduction (R)</th>
                    <th style={{padding:"8px 10px",textAlign:"left",fontWeight:600,color:C.inkMid,fontSize:11}}>Once-off Allow. Taxable (R)</th>
                    <th style={{padding:"8px 10px",textAlign:"left",fontWeight:600,color:C.inkMid,fontSize:11}}>Once-off Allow. Non-taxable (R)</th>
                  </tr>
                </thead>
                <tbody>
                  {scheduleEmployees.map(emp => {
                    const oo = onceOffData[emp.id] || {};
                    const setOo = (k,v) => setOnceOffData(prev=>({...prev,[emp.id]:{...prev[emp.id],[k]:v}}));
                    const inpSt = {padding:"5px 8px",border:`1px solid ${C.border}`,borderRadius:6,fontSize:12,width:90,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"};
                    return (
                      <tr key={emp.id} style={{borderBottom:`1px solid ${C.border}`}}>
                        <td style={{padding:"7px 10px",fontWeight:600,color:C.ink}}>{emp.name}</td>
                        <td style={{padding:"7px 10px"}}><input type="number" min="0" step="0.01" placeholder="0" value={oo.expenseClaim||""} onChange={e=>setOo("expenseClaim",e.target.value)} style={inpSt}/></td>
                        <td style={{padding:"7px 10px"}}><input type="number" min="0" step="0.01" placeholder="0" value={oo.onceOffDeduction||""} onChange={e=>setOo("onceOffDeduction",e.target.value)} style={inpSt}/></td>
                        <td style={{padding:"7px 10px"}}><input type="number" min="0" step="0.01" placeholder="0" value={oo.onceOffTaxable||""} onChange={e=>setOo("onceOffTaxable",e.target.value)} style={inpSt}/></td>
                        <td style={{padding:"7px 10px"}}><input type="number" min="0" step="0.01" placeholder="0" value={oo.onceOffNonTaxable||""} onChange={e=>setOo("onceOffNonTaxable",e.target.value)} style={inpSt}/></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
              <button onClick={()=>setShowOtModal(false)} style={{background:"transparent",color:C.inkMid,border:`1px solid ${C.border}`,borderRadius:10,padding:"10px 20px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
              <button onClick={async () => {
                setShowOtModal(false);
                let runRes = null;
                try {
                  const otPayload = payScheduleTab === "casual"
                    ? scheduleEmployees.map(emp => {
                        const days = +otData[emp.id]?.daysWorked || 0;
                        return { employee_id: emp.id, normal_hours: days * 8, overtime_hours: 0, sunday_hours: 0, ph_hours: 0 };
                      }).filter(e => e.normal_hours > 0)
                    : scheduleEmployees.map(emp => {
                        const ot = otData[emp.id] || {};
                        return { employee_id: emp.id, normal_hours: +ot.normalHours||0, overtime_hours: +ot.otHours||0, sunday_hours: +ot.sunHours||0, ph_hours: +ot.phHours||0 };
                      }).filter(e => e.normal_hours > 0 || e.overtime_hours > 0 || e.sunday_hours > 0 || e.ph_hours > 0);
                  const _bonusYm = new Date().toISOString().slice(0,7);
                  const secPayload = scheduleEmployees.map(emp => {
                    const sec = secData[emp.id] || {};
                    // pay_annual_bonus is sent explicitly (true/false) for every officer so the backend pays exactly what the screen shows
                    return { employee_id: emp.id, night_shift_shifts: +sec.nightShifts||0, special_allowance_shifts: +sec.specialShifts||0,
                             unauthorised_absence_hours: +sec.unauthHrs||0,
                             pay_annual_bonus: emp.security_grade ? nbcpssBonusOn(emp, sec, _bonusYm) : null };
                  }).filter(e => e.night_shift_shifts > 0 || e.special_allowance_shifts > 0 || e.pay_annual_bonus !== null);
                  const onceOffPayload = scheduleEmployees.map(emp => {
                    const oo = onceOffData[emp.id] || {};
                    return { employee_id: emp.id, expense_claim: +oo.expenseClaim||0, once_off_deduction: +oo.onceOffDeduction||0, once_off_allowance_taxable: +oo.onceOffTaxable||0, once_off_allowance_nontaxable: +oo.onceOffNonTaxable||0 };
                  }).filter(e => e.expense_claim > 0 || e.once_off_deduction > 0 || e.once_off_allowance_taxable > 0 || e.once_off_allowance_nontaxable > 0);
                  const isSec = (user?.industry||"").toLowerCase().replace(/[\s-]/g,"_")==="private_security";
                  runRes = await api("/payroll/run", {method:"POST", body: JSON.stringify({pay_schedule: payScheduleTab, overtime: otPayload, security: secPayload, once_off: onceOffPayload, ...(isSec ? {area_override: secArea} : {})})});
                  if (live && live.reload) live.reload();
                } catch(err) { console.warn("Payroll run failed:", err.message); setRunError(`Payroll was NOT saved - the server rejected the run: ${err.message}. No payslips or journal entries were created for this run.`); return; }
                setRunError("");
                setRunNotes(runRes ? {status: runRes.status, message: runRes.message, warnings: runRes.warnings||[], skipped: runRes.skipped||[]} : null);
                if (runRes && runRes.status === "no_change") return;
                setPayrollRun(true);
                setPayrollPeriod(new Date().toISOString().slice(0,7));
                fetchStoredPayslips(); // refresh table with actuals from the run we just completed
              }} style={{background:C.accent,color:"#fff",border:"none",borderRadius:10,padding:"10px 24px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                Confirm &amp; Run Payroll
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Persistent payslip download — always accessible ─────────────────── */}
      {scheduleEmployees.length > 0 && (() => {
        const dlPeriodKey = `dlPeriod_${payScheduleTab}`;
        return (
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:"12px 16px",marginBottom:16,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
            <span style={{fontSize:13,fontWeight:600,color:C.ink}}>⬇ Download Payslips</span>
            <input type="month" defaultValue={new Date().toISOString().slice(0,7)}
              id={dlPeriodKey}
              style={{border:`1px solid ${C.border}`,borderRadius:8,padding:"6px 10px",fontSize:13,fontFamily:"inherit",color:C.ink,background:C.bg}}/>
            <button onClick={async()=>{
              const period = document.getElementById(dlPeriodKey)?.value || new Date().toISOString().slice(0,7);
              try{
                const resp=await fetch(`${BASE_URL}/payroll/payslips/${period}/download-all`,{headers:{Authorization:`Bearer ${localStorage.getItem("zuzan_token")}`}});
                if(!resp.ok){
                  const msg=await resp.text().catch(()=>"");
                  alert(`No payslips found for ${period}. Run payroll for this period first.`);
                  return;
                }
                const blob=await resp.blob();
                const a=document.createElement("a");
                a.href=URL.createObjectURL(blob);
                a.download=`payslips_${period}.zip`;
                document.body.appendChild(a); a.click(); document.body.removeChild(a);
                setTimeout(()=>URL.revokeObjectURL(a.href),1000);
              }catch(err){alert(`Download error: ${err.message}`);}
            }} style={{background:C.accent,color:"#fff",border:"none",borderRadius:8,padding:"7px 18px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Download ZIP</button>
            <span style={{fontSize:11,color:C.inkMid}}>One PDF payslip per employee, in a ZIP</span>
          </div>
        );
      })()}

      {runError && (
        <div style={{background:"#fef2f2",border:"2px solid #dc2626",borderRadius:16,padding:"14px 18px",marginBottom:20,display:"flex",alignItems:"center",gap:12}}>
          <span style={{fontSize:20}}>⚠️</span>
          <div style={{flex:1,fontSize:13,fontWeight:600,color:"#991b1b"}}>{runError}</div>
          <button onClick={()=>setRunError("")} style={{background:"transparent",border:"1px solid #dc2626",color:"#991b1b",borderRadius:8,padding:"5px 12px",fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Dismiss</button>
        </div>
      )}

      {runNotes && (runNotes.status === "no_change" || runNotes.warnings.length > 0 || runNotes.skipped.length > 0) && (
        <div style={{background:"#fffbeb",border:"2px solid #d97706",borderRadius:16,padding:"14px 18px",marginBottom:20}}>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <span style={{fontSize:20}}>ℹ️</span>
            <div style={{flex:1,fontSize:13,fontWeight:600,color:"#92400e"}}>{runNotes.status === "no_change" ? (runNotes.message || "Nothing new was processed - payslips for this period already exist.") : "Payroll processed - please review these notes:"}</div>
            <button onClick={()=>setRunNotes(null)} style={{background:"transparent",border:"1px solid #d97706",color:"#92400e",borderRadius:8,padding:"5px 12px",fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Dismiss</button>
          </div>
          {runNotes.warnings.length > 0 && <ul style={{margin:"8px 0 0 32px",padding:0,fontSize:12,color:"#92400e"}}>{runNotes.warnings.map((w,i)=><li key={"w"+i}>{typeof w==="string"?w:JSON.stringify(w)}</li>)}</ul>}
          {runNotes.skipped.length > 0 && <div style={{margin:"8px 0 0 32px",fontSize:12,color:"#92400e"}}><b>Skipped:</b><ul style={{margin:"2px 0 0 0",padding:"0 0 0 18px"}}>{runNotes.skipped.map((w,i)=><li key={"s"+i}>{typeof w==="string"?w:JSON.stringify(w)}</li>)}</ul></div>}
        </div>
      )}

      {payrollRun && (
        <div style={{background:C.surface,border:`2px solid ${C.green}`,borderRadius:16,padding:20,marginBottom:20}}>
          <div style={{fontSize:16,fontWeight:700,color:C.green,marginBottom:12}}>Payroll Processed - {new Date().toLocaleDateString("en-ZA",{month:"long",year:"numeric"})}</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:12}}>
            {[["Net Disbursed",fmt(totalNet),C.green],["PAYE to SARS",fmt(totalPAYE),C.red],["UIF and SDL",fmt(totalUIF+totalSDL),C.gold],["ZuZan Fee",fmt(zuZanFee),C.accent]].map(([l,v,c]) => (
              <div key={l} style={{background:C.bg,borderRadius:10,padding:14}}>
                <div style={{fontSize:10,color:C.inkMid,marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>{l}</div>
                <div style={{fontSize:18,fontWeight:700,color:c}}>{v}</div>
              </div>
            ))}
          </div>
          <div style={{marginTop:16,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <div style={{fontSize:12,color:C.inkMid}}>EMP201 generated · Payslips ready · SARS eFiling export ready</div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={async()=>{
                const period = payrollPeriod || new Date().toISOString().slice(0,7);
                try{
                  const resp=await fetch(`${BASE_URL}/payroll/payslips/${period}/download-all`,{headers:{Authorization:`Bearer ${localStorage.getItem("zuzan_token")}`}});
                  if(!resp.ok){
                    const msg = await resp.text().catch(()=>"");
                    alert(`Payslip download failed (${resp.status}): ${msg||"No payslips found for this period."}`);
                    return;
                  }
                  const blob=await resp.blob();
                  const a=document.createElement("a");
                  a.href=URL.createObjectURL(blob);
                  a.download=`payslips_${period}.zip`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  setTimeout(()=>URL.revokeObjectURL(a.href),1000);
                }catch(err){alert(`Payslip download error: ${err.message}`);}
              }} style={{background:C.accentLt,color:C.accent,border:`1px solid ${C.accent}30`,borderRadius:10,padding:"10px 20px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>⬇ Download All Payslips</button>
              {payScheduleTab==="security" && <button onClick={async()=>{
                try{
                  const ym=payrollPeriod||new Date().toISOString().slice(0,7);
                  const r=await api(`/payroll/nbcpss-remittance/${ym}`);
                  const q=v=>`"${String(v??"").replace(/"/g,'""')}"`;
                  const lines=[
                    ["NBCPSS consolidated remittance (cl 35A)",ym,"Due",r.due_date].map(q).join(","),
                    ["Levy",r.levy,"PSSPF",r.psspf,"Health insurance",r.health_insurance,"TOTAL",r.total_clause_35a].map(q).join(","),
                    ["PSIRA fee (separate, not cl 35A)",r.psira_fee_separate].map(q).join(","),
                    "",
                    ["Employee No","Name","Grade","Area","Levy","PSSPF","Health","Total"].map(q).join(","),
                    ...r.employees.map(e=>[e.employee_number,e.name,e.grade,e.area,e.levy,e.psspf,e.health,e.total].map(q).join(","))
                  ];
                  downloadCSV(`nbcpss-remittance-${ym}.csv`,lines.join("\n"));
                }catch(err){alert(`Remittance schedule failed: ${err.message}`);}
              }} style={{background:C.accentLt,color:C.accent,border:`1px solid ${C.accent}30`,borderRadius:10,padding:"10px 20px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>⬇ NBCPSS Remittance (due 7th)</button>}
              <button onClick={()=>setShowBatch(true)} style={{background:C.ink,color:"#fff",border:"none",borderRadius:10,padding:"10px 20px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>⬇ Download Payment Files</button>
            </div>
          </div>
        </div>
      )}
      {showBatch && (
        <BatchPaymentModal
          employees={scheduleEmployees}
          period={new Date((payrollPeriod||new Date().toISOString().slice(0,7))+"-01T00:00:00").toLocaleDateString("en-ZA",{month:"long",year:"numeric"})}
          ym={payrollPeriod||new Date().toISOString().slice(0,7)}
          onClose={()=>setShowBatch(false)}
        />
      )}
      {showNew && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:250,display:"flex",alignItems:"flex-start",justifyContent:"center",padding:"20px 16px",overflowY:"auto"}}>
          <div style={{background:C.surface,borderRadius:20,width:"100%",maxWidth:700,boxShadow:"0 8px 40px #00000030",margin:"auto",display:"flex",flexDirection:"column",maxHeight:"90vh"}}>
          {/* Pinned header */}
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"20px 28px 16px",borderBottom:`1px solid ${C.border}`,flexShrink:0}}>
            <h3 style={{fontFamily:"serif",fontSize:22,color:C.ink,margin:0}}>New Employee</h3>
            <button onClick={()=>setShowNew(false)} style={{background:"none",border:"none",fontSize:24,cursor:"pointer",color:C.inkMid,lineHeight:1}}>×</button>
          </div>
          {/* Scrollable body */}
          <div style={{overflowY:"auto",padding:"20px 28px",flex:1}}>

          {/* Employment Details */}
          <div style={{fontSize:11,fontWeight:700,color:C.accent,textTransform:"uppercase",letterSpacing:1,marginBottom:10}}>Employment Details</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:16}}>
            {[
              {l:"Employee #",           k:"empNo",           p:"EMP-001",     t:"text"},
              {l:"Full Name",            k:"name",            p:"Sipho Dlamini",t:"text"},
              {l:"Position / Title",     k:"position",        p:"Developer",   t:"text"},
              {l:"Department",           k:"dept",            p:"Tech",        t:"text"},
              {l:"Monthly Salary (ZAR)", k:"salary",          p:"35000",       t:"number"},
              {l:"Date of Appointment",  k:"appointmentDate", p:"",            t:"date"},
            ].map(f => (
              <div key={f.k}>
                <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>{f.l}</label>
                <input type={f.t} placeholder={f.p} value={form[f.k]} onChange={e=>setForm(v=>({...v,[f.k]:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}/>
              </div>
            ))}
            {/* Patterson Grade dropdown + market salary hint */}
            <div style={{gridColumn:"1 / -1"}}>
              <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Patterson Grade</label>
              <select value={form.grade} onChange={e=>setForm(v=>({...v,grade:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}>
                <option value="">— Select grade —</option>
                <optgroup label="Band A — Unskilled / Elementary">
                  <option value="A1">A1 — Basic unskilled (cleaners, general workers)</option>
                  <option value="A2">A2 — Elementary semi-skilled</option>
                </optgroup>
                <optgroup label="Band B — Semi-Skilled">
                  <option value="B1">B1 — Routine semi-skilled</option>
                  <option value="B2">B2 — Defined semi-skilled</option>
                  <option value="B3">B3 — Skilled semi-skilled</option>
                  <option value="B4">B4 — Advanced semi-skilled</option>
                  <option value="B5">B5 — Expert semi-skilled</option>
                </optgroup>
                <optgroup label="Band C — Skilled / Technical / Clerical">
                  <option value="C1">C1 — Junior skilled (bookkeepers, admin clerks)</option>
                  <option value="C2">C2 — Skilled technician / senior clerk</option>
                  <option value="C3">C3 — Senior skilled / team leader</option>
                  <option value="C4">C4 — Specialist / senior technician</option>
                  <option value="C5">C5 — Expert specialist</option>
                </optgroup>
                <optgroup label="Band D — Junior Management / Professional">
                  <option value="D1">D1 — Junior professional / supervisor</option>
                  <option value="D2">D2 — Professional / junior manager</option>
                  <option value="D3">D3 — Senior professional / manager</option>
                  <option value="D4">D4 — Senior manager / department head</option>
                  <option value="D5">D5 — Divisional head / senior specialist</option>
                </optgroup>
                <optgroup label="Band E — Middle Management">
                  <option value="E1">E1 — Junior middle manager</option>
                  <option value="E2">E2 — Middle manager</option>
                  <option value="E3">E3 — Senior middle manager</option>
                  <option value="E4">E4 — General manager</option>
                  <option value="E5">E5 — Senior general manager</option>
                </optgroup>
                <optgroup label="Band F — Senior / Executive Management">
                  <option value="F1">F1 — Director / senior executive</option>
                  <option value="F2">F2 — Executive director</option>
                  <option value="F3">F3 — Chief executive / top management</option>
                </optgroup>
              </select>
              {/* Market salary hint — appears when a grade is selected */}
              {form.grade && PATTERSON_RANGES[form.grade] && (() => {
                const r = PATTERSON_RANGES[form.grade];
                const fmt = n => "R" + n.toLocaleString("en-ZA");
                const pct = form.salary
                  ? Math.round(((+form.salary - r.min) / (r.max - r.min)) * 100)
                  : null;
                const position = pct === null ? null : Math.max(0, Math.min(100, pct));
                return (
                  <div style={{marginTop:10,padding:"12px 14px",background:"#f0f7ff",border:"1px solid #b8d4f0",borderRadius:10}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                      <div style={{fontSize:12,fontWeight:700,color:"#1a4a7a"}}>
                        📊 Market range for {form.grade} — 2025 SA general market (monthly CTC)
                      </div>
                      <button
                        type="button"
                        onClick={()=>setForm(v=>({...v,salary:String(r.median)}))}
                        style={{padding:"4px 12px",background:"#1a6fcc",border:"none",borderRadius:6,color:"#fff",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}
                      >
                        Use median
                      </button>
                    </div>
                    {/* Range bar */}
                    <div style={{position:"relative",height:8,background:"#d0e4f8",borderRadius:4,marginBottom:6}}>
                      <div style={{position:"absolute",left:"33%",right:"33%",top:0,height:"100%",background:"#6baee8",borderRadius:4}}/>
                      {position !== null && (
                        <div style={{position:"absolute",top:-3,left:`${position}%`,transform:"translateX(-50%)",width:14,height:14,borderRadius:7,background:position < 33 ? "#e8a020" : position > 67 ? "#1a6fcc" : "#2a9d3f",border:"2px solid #fff",boxShadow:"0 1px 3px rgba(0,0,0,0.2)"}}/>
                      )}
                    </div>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:"#3a6a9a"}}>
                      <span>Min: <strong>{fmt(r.min)}</strong></span>
                      <span>Median: <strong style={{color:"#1a4a7a"}}>{fmt(r.median)}</strong></span>
                      <span>Max: <strong>{fmt(r.max)}</strong></span>
                    </div>
                    {position !== null && (
                      <div style={{marginTop:6,fontSize:11,color: position < 25 ? "#b85c00" : position > 75 ? "#1a4a7a" : "#2a7d3f", fontWeight:600}}>
                        {position < 25 ? "⚠️ Below market — risk of turnover"
                          : position > 75 ? "💡 Above median — strong retention"
                          : "✓ Within market range"}
                      </div>
                    )}
                    <div style={{marginTop:4,fontSize:10,color:"#6a8aaa"}}>Indicative only — varies by industry, province &amp; experience. Source: Peromnes/Patterson surveys.</div>
                  </div>
                );
              })()}
            </div>
            <div>
              <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Employment Type</label>
              <select value={form.employmentType} onChange={e=>setForm(v=>({...v,employmentType:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}>
                <option value="salaried">Salaried (fixed monthly)</option>
                <option value="hourly">Hourly rate</option>
              </select>
            </div>
            <div>
              <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Pay Schedule</label>
              <select value={form.paySchedule||"salaried"} onChange={e=>setForm(v=>({...v,paySchedule:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}>
                <option value="salaried">💼 Salaried Staff</option>
                <option value="security">🛡️ Security Officers</option>
                <option value="casual">👷 Casual / Temp</option>
              </select>
            </div>
            <div>
              <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Employment Category</label>
              <select value={form.employmentCategory||"permanent"} onChange={e=>setForm(v=>({...v,employmentCategory:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}>
                <option value="permanent">Permanent — full UIF + SDL</option>
                <option value="fixed_term">Fixed-Term Contract — full UIF + SDL</option>
                <option value="temp_labour">Casual / Temporary (own staff) — full UIF + SDL</option>
                <option value="learnership">Learnership — UIF applies</option>
                <option value="public_servant">Public Servant — no UIF</option>
                <option value="foreign_national">Foreign National — UIF applies</option>
              </select>
              <div style={{marginTop:6,fontSize:11,color:"#475569",background:"#f1f5f9",borderRadius:6,padding:"6px 10px"}}>ℹ️ Directors: executive/shareholder-directors paid a salary through payroll are employees (use Permanent — UIF applies, regardless of age). Non-executive directors paid fees only are generally not employees — do not add them to payroll.</div>
            </div>
            {form.employmentType === "hourly" && (
              <div>
                <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Hourly Rate (ZAR)</label>
                <input type="number" placeholder="e.g. 150" value={form.hourlyRate} onChange={e=>setForm(v=>({...v,hourlyRate:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}/>
              </div>
            )}
          </div>
          {form.salary && (() => {
            // audit fix 2026-09-16 — same 208 vs 195 hours fix as the Run Payroll
            // modal: an NBCPSS security employee's implied hourly rate must use
            // the 208h/month prescribed hours, not the general 195h/month BCEA default.
            const _isSecurityEmp = (user?.industry||"").toLowerCase().replace(/[\s-]/g,"_")==="private_security" && !!form.securityGrade;
            const _rate = bceaHourlyRate(+form.salary, form.hourlyRate ? +form.hourlyRate : null, _isSecurityEmp);
            return (
              <div style={{background:C.blueLt,border:`1px solid ${C.blue}30`,borderRadius:8,padding:"10px 14px",marginBottom:16,fontSize:12,color:C.inkMid}}>
                <strong style={{color:C.blue}}>BCEA hourly rate: </strong>
                {fmt(_rate)}/hr
                <span style={{marginLeft:16}}>Weekday OT: {fmt(_rate * 1.5)}/hr · Sunday: {fmt(_rate * (_isSecurityEmp ? 1.5 : 2))}/hr</span>
              </div>
            );
          })()}

          {/* Personal Details */}
          <div style={{fontSize:11,fontWeight:700,color:C.accent,textTransform:"uppercase",letterSpacing:1,marginBottom:10}}>Personal Details</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:20}}>
            {[
              {l:"SA ID Number",  k:"idNumber",   p:"8001015009087", t:"text"},
              {l:"Tax Number",    k:"taxNumber",  p:"1234567890",    t:"text"},
              {l:"Date of Birth", k:"dob",        p:"",              t:"date"},
            ].map(f => (
              <div key={f.k}>
                <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>{f.l}</label>
                <input type={f.t} placeholder={f.p} value={form[f.k]} onChange={e=>setForm(v=>({...v,[f.k]:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}/>
              </div>
            ))}
            <div style={{gridColumn:"1 / -1"}}>
              <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Residential Address</label>
              <input placeholder="123 Main St, Johannesburg, 2000" value={form.address} onChange={e=>setForm(v=>({...v,address:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}/>
            </div>
          </div>

          {/* Benefits */}
          <div style={{fontSize:11,fontWeight:700,color:C.accent,textTransform:"uppercase",letterSpacing:1,marginBottom:10}}>Pension / Provident Fund &amp; Medical Aid</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:12}}>
            {[
              {l:"Employee Pension %",             k:"pensionEmployeePct",   p:"7.5",  t:"number", tip:"% of gross salary — standard/mandatory rate"},
              {l:"Employee Pension Fixed (ZAR/mo)",k:"pensionEmployeeFixed",  p:"500",  t:"number", tip:"Optional voluntary top-up (ZAR/month, added to % above). No limit — excess above s11F cap is post-tax."},
              {l:"Employer Pension %",             k:"pensionEmployerPct",   p:"7.5",  t:"number", tip:"% of gross salary contributed by employer"},
              {l:"Employer Pension Fixed (ZAR/mo)",k:"pensionEmployerFixed",  p:"500",  t:"number", tip:"Optional employer add-on (ZAR/month). Contribution has no SARS cap — only s11F employee relief is capped."},
              {l:"Medical Aid — Employee (ZAR/mo)",k:"medicalAidEmployee",   p:"1500", t:"number", tip:"Employee's own monthly medical aid contribution"},
              {l:"Medical Aid — Employer (ZAR/mo)",k:"medicalAidEmployer",   p:"1500", t:"number", tip:"Employer's monthly contribution (taxable fringe benefit — added to PAYE base)"},
              {l:"Medical Aid Dependants",          k:"medicalAidDependants", p:"1",    t:"number", tip:"Dependants EXCLUDING the main member (s6A MTC: R376 main+first, R254 each additional)"},
            ].map(f=>(
              <div key={f.k}>
                <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:0.5}}>{f.l}</label>
                <input type={f.t} placeholder={f.p} value={form[f.k]} onChange={e=>setForm(v=>({...v,[f.k]:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}/>
                <div style={{fontSize:10,color:C.inkDim,marginTop:3}}>{f.tip}</div>
              </div>
            ))}
          </div>
          {/* Live pension/medical preview */}
          {form.salary && (form.pensionEmployeePct || form.pensionEmployeeFixed || form.medicalAidEmployee || form.medicalAidEmployer) && (()=>{
            const p = calcPayroll(+form.salary, null, 0, 0, 0, null,
              form.pensionEmployeePct ? +form.pensionEmployeePct/100 : 0,
              form.pensionEmployerPct ? +form.pensionEmployerPct/100 : 0,
              form.medicalAidEmployee ? +form.medicalAidEmployee : 0,
              form.medicalAidEmployer ? +form.medicalAidEmployer : 0,
              form.medicalAidDependants ? +form.medicalAidDependants : 0,
              form.pensionEmployeeFixed ? +form.pensionEmployeeFixed : 0,
              form.pensionEmployerFixed ? +form.pensionEmployerFixed : 0,
              // audit fix 2026-09-16 — see [[project_nbcpss_208hr_bug]]
              { isSecurity: (user?.industry||"").toLowerCase().replace(/[\s-]/g,"_")==="private_security" && !!form.securityGrade },
            );
            const hasExcess = p.s11fExcessMonthly > 0;
            return (
              <div style={{background:C.blueLt,border:`1px solid ${hasExcess?C.gold:C.blue}30`,borderRadius:8,padding:"10px 14px",marginBottom:16,fontSize:12}}>
                <strong style={{color:C.blue}}>Live preview: </strong>
                <span style={{color:C.ink}}>
                  PAYE {fmt(p.paye)} · s11F relief {fmt(p.s11fDeduction)}/mo · MTC R{p.medicalTaxCredit} · Net Pay {fmt(p.netPay)} · Employer Cost {fmt(p.totalCost)}
                </span>
                {hasExcess && (
                  <div style={{marginTop:6,fontSize:11,color:C.gold,fontWeight:600}}>
                    ⚠️ {fmt(p.s11fExcessMonthly)}/month ({fmt(p.s11fExcessMonthly*12)}/year) of employee contributions exceed the s11F cap — this portion is deducted from after-tax income with no additional PAYE relief. Contributions are still processed in full.
                  </div>
                )}
              </div>
            );
          })()}

          {/* MIBCO Fuel Station Section — only shown for fuel_station industry */}
          {(user?.industry||"").toLowerCase().replace(/[\s-]/g,"_")==="fuel_station" && (()=>{
            const MIBCO_ROLES = {forecourt_attendant:"Forecourt Attendant",cashier:"Cashier",char:"Char / Cleaner"};
            const MIBCO_HR = {forecourt_attendant:45.79,cashier:45.30,char:34.64};
            const mibcoHr = MIBCO_HR[form.mibcoRole] || 0;
            const mibcoMonthMin = mibcoHr > 0 ? Math.round(mibcoHr * 45 * (52/12) * 100) / 100 : 0;
            const belowMibco = form.mibcoRole && form.salary && mibcoMonthMin > 0 && +form.salary < mibcoMonthMin;
            return (
              <div style={{marginBottom:16}}>
                <div style={{fontSize:11,fontWeight:700,color:"#92400e",textTransform:"uppercase",letterSpacing:1,marginBottom:10}}>⛽ MIBCO Sector 5 — Fuel Retail (eff. 22 Dec 2025)</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:8}}>
                  <div>
                    <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:0.5}}>MIBCO Role</label>
                    <select value={form.mibcoRole} onChange={e=>setForm(v=>({...v,mibcoRole:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}>
                      <option value="">-- Select Role --</option>
                      {Object.entries(MIBCO_ROLES).map(([k,v])=><option key={k} value={k}>{v} (min R{MIBCO_HR[k].toFixed(2)}/hr)</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:0.5}}>Health Scheme (Affinity Health via MIBCO)</label>
                    <div style={{display:"flex",alignItems:"center",gap:10,height:42}}>
                      <input type="checkbox" id="mibcoScheme" checked={!!form.mibcoSchemeEnrolled} onChange={e=>setForm(v=>({...v,mibcoSchemeEnrolled:e.target.checked}))} style={{width:16,height:16,cursor:"pointer"}}/>
                      <label htmlFor="mibcoScheme" style={{fontSize:12,color:C.ink,cursor:"pointer"}}>Enrolled — Employee: R174/mo deduction · Employer: R90/mo</label>
                    </div>
                  </div>
                </div>
                {form.mibcoRole && <div style={{padding:"8px 12px",background:belowMibco?"#fff1f2":"#fffbeb",border:`1px solid ${belowMibco?"#fca5a5":"#fbbf24"}`,borderRadius:8,fontSize:12}}>
                  {belowMibco
                    ? <span style={{color:"#b91c1c",fontWeight:600}}>⚠️ Salary R{(+form.salary).toLocaleString("en-ZA")} is below the MIBCO minimum for {MIBCO_ROLES[form.mibcoRole]}: <strong>R{mibcoMonthMin.toLocaleString("en-ZA")}/month</strong> (R{mibcoHr.toFixed(2)}/hr × 45h × 4.33 wks). Adjust before saving.</span>
                    : <span style={{color:"#78350f"}}>✓ {MIBCO_ROLES[form.mibcoRole]} minimum: <strong>R{mibcoMonthMin.toLocaleString("en-ZA")}/month</strong>. Medical Insurance Allowance R85.00/mo added to gross. Health scheme and BC levy applied automatically.</span>
                  }
                </div>}
              </div>
            );
          })()}

          {/* NBCPSS Private Security Section — only shown for private_security industry */}
          {user?.industry === "private_security" && (()=>{
            const minWage = nbcpssMinMonthly(form.securityGrade, form.securityArea||"3");
            const belowMin = form.salary && form.securityGrade && +form.salary < minWage;
            const _medEmp  = form.securityArea==="1_2" ? 122 : 122;
            const _medEmpr = form.securityArea==="1_2" ? 172.50 : 172.50;
            const _areaLabel = form.securityArea==="1_2" ? "Area 1 & 2 (Urban/Metro)" : "Area 3 (Rural / All Other)";
            return (
              <div style={{marginBottom:16}}>
                <div style={{fontSize:11,fontWeight:700,color:"#c2410c",textTransform:"uppercase",letterSpacing:1,marginBottom:10}}>🔒 NBCPSS — Private Security Sector (1 Mar 2026 – 28 Feb 2027)</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:8}}>
                  <div>
                    <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:0.5}}>PSIRA Number</label>
                    <input placeholder="e.g. 1234567890" value={form.psiraNumber} onChange={e=>setForm(v=>({...v,psiraNumber:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}/>
                  </div>
                  <div>
                    <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:0.5}}>Security Grade</label>
                    <select value={form.securityGrade} onChange={e=>{const g=e.target.value;setForm(v=>({...v,securityGrade:g,pensionEmployeePct:g?"7.5":v.pensionEmployeePct,pensionEmployerPct:g?"7.5":v.pensionEmployerPct}));}} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}>
                      <option value="">-- Select --</option>
                      <option value="A">Grade A — Armed/Supervisory</option>
                      <option value="B">Grade B — Armed Response/Supervisor</option>
                      <option value="C">Grade C — Armed Guard</option>
                      <option value="D">Grade D — Unarmed/Access Control</option>
                      <option value="E">Grade E — Entry Level/Car Guard</option>
                    </select>
                  </div>
                  <div>
                    <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:0.5}}>Area (NBCPSS)</label>
                    <select value={form.securityArea||"3"} onChange={e=>setForm(v=>({...v,securityArea:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}>
                      <option value="3">Area 3 — Rural / All Other Districts</option>
                      <option value="1_2">Area 1 & 2 — Urban / Metro</option>
                    </select>
                  </div>
                  <div>
                    <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:0.5}}>Shift Type</label>
                    <select value={form.shiftType||"day"} onChange={e=>setForm(v=>({...v,shiftType:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}>
                      <option value="day">Day Shift</option>
                      <option value="night">Night Shift (R8.00/shift allowance)</option>
                      <option value="rotating">Rotating (Day &amp; Night)</option>
                    </select>
                  </div>
                  <div>
                    <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:0.5}}>Special Allowance</label>
                    <select value={form.specialAllowanceType||"none"} onChange={e=>setForm(v=>({...v,specialAllowanceType:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}>
                      <option value="none">None</option>
                      <option value="armed">Armed Security Officer (R10.50/shift)</option>
                      <option value="armed_response">Armed Response Officer (R10.50/shift)</option>
                      <option value="nkp">National Key Point Officer (R10.50/shift)</option>
                      <option value="control_centre">Control/Comm Centre Operator (R10.50/shift)</option>
                      <option value="canine">Canine / Dog Handler (R10.50/shift)</option>
                      <option value="mobile_supervisor">Mobile Supervisor (R10.50/shift)</option>
                    </select>
                  </div>
                  <div>
                    <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:0.5}}>Union Subscription (R/month)</label>
                    <input type="number" min="0" step="0.01" placeholder="0.00" value={form.unionSubscription||""} onChange={e=>setForm(v=>({...v,unionSubscription:parseFloat(e.target.value)||0}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}/>
                  </div>
                  <div>
                    <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:0.5}}>Advance Repayment (R/month)</label>
                    <input type="number" min="0" step="0.01" placeholder="0.00" value={form.advanceMonthlyDeduction||""} onChange={e=>setForm(v=>({...v,advanceMonthlyDeduction:parseFloat(e.target.value)||0}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}/>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0"}}><input type="checkbox" id="addPsspfEx" checked={form.psspfExempt||false} onChange={e=>setForm(v=>({...v,psspfExempt:e.target.checked}))} style={{width:16,height:16,cursor:"pointer"}}/><label htmlFor="addPsspfEx" style={{fontSize:13,color:C.ink,cursor:"pointer",fontWeight:600}}>Not a PSSPF member <span style={{fontSize:11,color:C.inkMid,fontWeight:400}}>(tick only if exempt - otherwise PSSPF provident is applied automatically and any pension fields above are ignored)</span></label></div><div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0"}}><input type="checkbox" id="addSunOrd" checked={form.sundayOrdinary||false} onChange={e=>setForm(v=>({...v,sundayOrdinary:e.target.checked}))} style={{width:16,height:16,cursor:"pointer"}}/><label htmlFor="addSunOrd" style={{fontSize:13,color:C.ink,cursor:"pointer",fontWeight:600}}>Ordinarily works Sundays <span style={{fontSize:11,color:C.inkMid,fontWeight:400}}>(BCEA s16: Sunday work paid at 1.5× instead of 2×. Security officers always get 1.5×)</span></label></div>
                  <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0"}}>
                    <input type="checkbox" id="addMatLeave" checked={form.onMaternityLeave||false} onChange={e=>setForm(v=>({...v,onMaternityLeave:e.target.checked}))} style={{width:16,height:16,cursor:"pointer"}}/>
                    <label htmlFor="addMatLeave" style={{fontSize:13,color:C.ink,cursor:"pointer",fontWeight:600}}>On Maternity Leave <span style={{fontSize:11,color:C.inkMid,fontWeight:400}}>(zero gross this pay run; employee claims UIF directly)</span></label>
                  </div>
                </div>
                {form.securityGrade && <div style={{padding:"8px 12px",background:belowMin?"#fff1f2":"#f0fdf4",border:`1px solid ${belowMin?"#fca5a5":"#86efac"}`,borderRadius:8,fontSize:12}}>
                  {belowMin
                    ? <span style={{color:"#b91c1c",fontWeight:600}}>⚠️ Salary R{(+form.salary).toLocaleString("en-ZA")} is below the NBCPSS minimum for Grade {form.securityGrade} ({_areaLabel}): <strong>R{minWage.toLocaleString("en-ZA")}/month</strong>. Adjust before saving.</span>
                    : <span style={{color:"#166534"}}>✓ Grade {form.securityGrade} · {_areaLabel} · Min R{minWage.toLocaleString("en-ZA")}/mo · Med aid R{_medEmp}/R{_medEmpr} (emp/empr) · Cleaning R32 · BC levy R7 · PSIRA R4 · PSSPF provident (employee + employer).</span>
                  }
                </div>}
              </div>
            );
          })()}

          {/* Bank Details */}
          <div style={{fontSize:11,fontWeight:700,color:C.accent,textTransform:"uppercase",letterSpacing:1,marginBottom:10}}>Bank Details</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:12,marginBottom:20}}>
            <div>
              <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Bank Name</label>
              <select value={form.bankName} onChange={e=>setForm(v=>({...v,bankName:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}>
                <option value="">-- Select Bank --</option>
                {["ABSA","Capitec","Discovery Bank","FNB","Investec","Nedbank","Standard Bank","TymeBank","African Bank"].map(b=><option key={b}>{b}</option>)}
              </select>
            </div>
            {[
              {l:"Account Number", k:"accountNumber", p:"62123456789"},
              {l:"Branch Code",    k:"branchCode",    p:"250655"},
            ].map(f=>(
              <div key={f.k}>
                <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>{f.l}</label>
                <input placeholder={f.p} value={form[f.k]} onChange={e=>setForm(v=>({...v,[f.k]:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}/>
              </div>
            ))}
            <div>
              <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Account Type</label>
              <select value={form.accountType} onChange={e=>setForm(v=>({...v,accountType:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}>
                {["Cheque","Savings","Transmission"].map(t=><option key={t}>{t}</option>)}
              </select>
            </div>
          </div>

          </div>{/* end scrollable body */}
          {/* Pinned footer */}
          <div style={{display:"flex",gap:8,padding:"16px 28px",borderTop:`1px solid ${C.border}`,flexShrink:0,background:C.surface,borderRadius:"0 0 20px 20px"}}>
            <button onClick={handleAdd} style={{background:C.accent,color:"#fff",border:"none",borderRadius:10,padding:"10px 24px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Add Employee</button>
            <button onClick={() => setShowNew(false)} style={{background:"transparent",color:C.inkMid,border:`1px solid ${C.border}`,borderRadius:10,padding:"10px 20px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
          </div>
          </div>{/* end modal card */}
        </div>
      )}
      <div key={taxYear} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,overflow:"hidden",marginBottom:16}}>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
          <thead>
            <tr style={{background:C.bg,borderBottom:`1px solid ${C.border}`}}>
              {["Employee","Grade","Position","Dept","Gross","PAYE","UIF","SDL","Net Pay","Employer Cost",""].map(h => <th key={h} style={{textAlign:"left",padding:"12px 14px",fontSize:9,color:C.inkMid,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase",whiteSpace:"nowrap"}}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {scheduleEmployees.map((emp) => {
              // audit fix 2026-09-16 — was calcPayroll(emp.salary, taxYear) with no
              // NBCPSS awareness; calcForSummary (defined above) applies the same
              // period's OT/night-shift/special-allowance/bonus inputs and the
              // 208h basis, matching what the Run Payroll modal and the actual
              // backend run would compute. See [[project_nbcpss_208hr_bug]].
              // Use actuals from DB when this period's payslip exists; fall back to live estimate.
              const _stored = storedPayslips[emp.id];
              const p = _stored ? {
                gross:       _stored.taxable_gross,
                paye:        _stored.paye,
                uifEmployee: _stored.uif_employee,
                uifEmployer: _stored.uif_employer,
                sdl:         _stored.sdl,
                netPay:      _stored.net_pay,
                totalCost:   _stored.total_cost,
              } : calcForSummary(emp);
              return (
                <tr key={emp.id} style={{borderBottom:`1px solid ${C.border}30`}}>
                  <td style={{padding:"13px 14px"}}>
                    <div style={{fontWeight:600,color:C.ink}}>{emp.name}</div>
                    <div style={{fontSize:10,color:C.inkMid}}>{emp.employee_number||emp.id}</div>
                    {user?.industry==="private_security" && emp.security_grade && (()=>{
                      const minW=nbcpssMinMonthly(emp.security_grade, emp.security_area||"1_2");
                      const below=minW>0&&emp.salary<minW;
                      const areaLbl=!emp.security_area?"area not set (Areas 1 & 2 assumed)":emp.security_area==="3"?"Area 3":"Areas 1 & 2";
                      return <div style={{fontSize:9,marginTop:2,color:below?"#b91c1c":"#166534",fontWeight:600}}>{below?`⚠️ Below NBCPSS min (R${minW.toLocaleString("en-ZA")})`:`🔒 Grade ${emp.security_grade} · ${areaLbl} · R${minW.toLocaleString("en-ZA")} min`}</div>;
                    })()}
                    {(user?.industry||"").toLowerCase().replace(/[\s-]/g,"_")==="fuel_station" && emp.mibco_role && (()=>{
                      const MIBCO_HR={forecourt_attendant:45.79,cashier:45.30,char:34.64};
                      const hr=MIBCO_HR[emp.mibco_role]||0;
                      const minW=hr>0?Math.round(hr*45*(52/12)*100)/100:0;
                      const below=minW>0&&emp.salary<minW;
                      const roleLabel={forecourt_attendant:"Forecourt",cashier:"Cashier",char:"Char"}[emp.mibco_role]||emp.mibco_role;
                      return <div style={{fontSize:9,marginTop:2,color:below?"#b91c1c":"#92400e",fontWeight:600}}>{below?`⚠️ Below MIBCO min (R${minW.toLocaleString("en-ZA")})`:`⛽ ${roleLabel} · R${hr.toFixed(2)}/hr min`}</div>;
                    })()}
                  </td>
                  <td style={{padding:"13px 14px"}}>{(()=>{const g=(user?.industry==="private_security"&&emp.security_grade)?emp.security_grade:emp.grade;return g?<Badge label={g} color={C.blue} bg={C.blueLt}/>:<span style={{color:C.inkMid,fontSize:11}}>—</span>;})()}</td>
                  <td style={{padding:"13px 14px",color:C.inkMid}}>{emp.position}</td>
                  <td style={{padding:"13px 14px"}}><Badge label={emp.dept||"General"} color={C.blue} bg={C.blueLt}/></td>
                  <td style={{padding:"13px 14px",fontWeight:600}}>{fmt(p.gross)}</td>
                  <td style={{padding:"13px 14px",color:C.red}}>{fmt(p.paye)}</td>
                  <td style={{padding:"13px 14px",color:C.gold}}>{fmt(p.uifEmployee)}</td>
                  <td style={{padding:"13px 14px",color:C.blue}}>{fmt(p.sdl)}</td>
                  <td style={{padding:"13px 14px",fontWeight:700,color:C.green}}>{fmt(p.netPay)}</td>
                  <td style={{padding:"13px 14px",fontWeight:700,color:C.accent}}>{fmt(p.totalCost)}</td>
                  <td style={{padding:"13px 14px"}}>
                    <div style={{display:"flex",gap:6}}>
                      <button onClick={()=>{setEditEmp(emp);setEditForm({name:emp.name||"",position:emp.position||"",salary:String(emp.salary||""),dept:emp.dept||emp.department||"",grade:emp.grade||"",employmentType:emp.employment_type||"salaried",hourlyRate:String(emp.hourly_rate||""),idNumber:emp.id_number||"",taxNumber:emp.tax_number||"",dob:emp.date_of_birth||"",appointmentDate:emp.appointment_date||"",address:emp.address||"",bankName:emp.bank_name||"",accountNumber:emp.account_number||"",branchCode:emp.branch_code||"",accountType:emp.account_type||"Cheque",pensionEmployeePct:emp.pension_fund_employee_pct ? String(Math.round(emp.pension_fund_employee_pct*100)) : "",pensionEmployerPct:emp.pension_fund_employer_pct ? String(Math.round(emp.pension_fund_employer_pct*100)) : "",pensionEmployeeFixed:emp.pension_employee_fixed ? String(emp.pension_employee_fixed) : "",pensionEmployerFixed:emp.pension_employer_fixed ? String(emp.pension_employer_fixed) : "",medicalAidEmployee:emp.medical_aid_employee ? String(emp.medical_aid_employee) : "",medicalAidEmployer:emp.medical_aid_employer ? String(emp.medical_aid_employer) : "",medicalAidDependants:emp.medical_aid_dependants ? String(emp.medical_aid_dependants) : "",psiraNumber:emp.psira_number||"",securityGrade:emp.security_grade||"",securityArea:emp.security_area||"3",shiftType:emp.shift_type||"day",specialAllowanceType:emp.special_allowance_type||"none",mibcoRole:emp.mibco_role||"",mibcoSchemeEnrolled:emp.mibco_scheme_enrolled!=null?emp.mibco_scheme_enrolled:true,psspfExempt:!!emp.psspf_exempt,sundayOrdinary:!!emp.sunday_ordinary,unionSubscription:emp.union_subscription||0,advanceMonthlyDeduction:emp.advance_monthly_deduction||0,onMaternityLeave:emp.on_maternity_leave||false,paySchedule:emp.pay_schedule||(emp.security_grade?"security":"salaried"),employmentCategory:emp.employment_category||"permanent"});}} style={{background:C.accentLt,color:C.accent,border:"none",borderRadius:6,padding:"5px 10px",fontSize:10,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>Edit</button>
                      <button onClick={()=>setViewPayslip({employee:emp,payroll:p,taxYear})} style={{background:C.blueLt,color:C.blue,border:"none",borderRadius:6,padding:"5px 10px",fontSize:10,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>Payslip</button>
                      <button onClick={()=>setTermModal({emp,reason:"resignation",date:new Date().toISOString().split("T")[0]})} style={{background:"#fee2e2",color:"#b91c1c",border:"none",borderRadius:6,padding:"5px 10px",fontSize:10,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>Terminate</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{background:C.bg,borderTop:`2px solid ${C.border}`}}>
              <td colSpan={4} style={{padding:"13px 14px",fontWeight:800,color:C.ink}}>TOTALS</td>
              <td style={{padding:"13px 14px",fontWeight:800}}>{fmt(totalGross)}</td>
              <td style={{padding:"13px 14px",fontWeight:800,color:C.red}}>{fmt(totalPAYE)}</td>
              <td style={{padding:"13px 14px",fontWeight:800,color:C.gold}}>{fmt(scheduleEmployees.reduce((s,e)=>s+calcForSummary(e).uifEmployee,0))}</td>
              <td style={{padding:"13px 14px",fontWeight:800,color:C.blue}}>{fmt(totalSDL)}</td>
              <td style={{padding:"13px 14px",fontWeight:800,color:C.green}}>{fmt(totalNet)}</td>
              <td style={{padding:"13px 14px",fontWeight:800,color:C.accent}}>{fmt(totalCost)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <div style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:12,padding:16,fontSize:12,color:C.inkMid,lineHeight:1.8}}>
        <strong style={{color:C.ink}}>SARS &amp; BCEA Compliance Notes {taxYear}</strong><br/>
        PAYE: 2026/2027 tax tables · Primary rebate R17,820/year · Due 7th of each month<br/>
        UIF: 1% employee + 1% employer · Capped at R17,712/month · Overtime excluded from UIF base<br/>
        SDL: 1% of gross payroll · Payable if annual payroll ≥ R500,000<br/>
        <strong style={{color:C.ink}}>BCEA Overtime (s9/s10/s16/s18):</strong> Normal week = 45 hrs · OT max 10 hrs/week · Weekday/Sat OT = 1.5× · Sunday = 2× · Public holiday = 2× · Hourly rate derived from salary ÷ 195 hrs/month
        {user?.industry==="private_security" && <><br/><strong style={{color:"#c2410c"}}>🔒 NBCPSS (1 Mar 2026 – 28 Feb 2027):</strong> Night shift R8.00/shift · Special allowance R10.50/shift · Cleaning R32.00/month · BC levy R9.40/month (employer) · PSIRA fee R5.00/month (employer) · PSSPF provident (employee + employer) · All allowances are taxable income.</>}
      </div>

      {/* ── Terminated Employees ──────────────────────────────────────────────── */}
      {employees.filter(e=>e.is_active===false).length > 0 && (
        <div style={{marginTop:8}}>
          <button onClick={()=>setShowTerminated(v=>!v)} style={{background:"none",border:"none",color:C.inkMid,fontSize:12,cursor:"pointer",padding:"4px 0",display:"flex",alignItems:"center",gap:6,fontFamily:"inherit"}}>
            <span style={{fontSize:10}}>{showTerminated?"▼":"▶"}</span>
            Terminated Employees ({employees.filter(e=>e.is_active===false).length})
          </button>
          {showTerminated && (
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,overflow:"hidden",marginTop:8}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                <thead>
                  <tr style={{background:C.bg,borderBottom:`1px solid ${C.border}`}}>
                    {["Employee","Position","Reason","Date",""].map(h=><th key={h} style={{textAlign:"left",padding:"10px 14px",fontSize:9,color:C.inkMid,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase"}}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {employees.filter(e=>e.is_active===false).map(emp=>{
                    const UIF_REASONS={resignation:"Resignation",retrenchment:"Retrenchment",dismissal_misconduct:"Dismissal — misconduct",dismissal_performance:"Dismissal — poor performance",dismissal_operational:"Dismissal — operational requirements",retirement:"Retirement",death:"Death",end_of_contract:"End of fixed-term contract",mutual_agreement:"Mutual agreement",ill_health:"Ill health / incapacity"};
                    return (
                    <tr key={emp.id} style={{borderBottom:`1px solid ${C.border}30`,opacity:0.7}}>
                      <td style={{padding:"11px 14px"}}>
                        <div style={{fontWeight:600,color:C.ink}}>{emp.name}</div>
                        <div style={{fontSize:10,color:C.inkMid}}>{emp.employee_number||emp.id}</div>
                      </td>
                      <td style={{padding:"11px 14px",color:C.inkMid}}>{emp.position||"—"}</td>
                      <td style={{padding:"11px 14px",color:C.inkMid}}>{emp.termination_reason ? (UIF_REASONS[emp.termination_reason]||emp.termination_reason) : "—"}</td>
                      <td style={{padding:"11px 14px",color:C.inkMid}}>{emp.termination_date ? new Date(emp.termination_date).toLocaleDateString("en-ZA") : "—"}</td>
                      <td style={{padding:"11px 14px"}}>
                        <button onClick={async()=>{if(!window.confirm(`Reinstate ${emp.name}? They will be included in future payroll runs.`))return;try{const d=await api(`/employees/${emp.id}/reinstate`,{method:"POST"});setEmployees(employees.map(e=>e.id===emp.id?{...e,...d,is_active:true,termination_reason:null,termination_date:null}:e));}catch(err){alert("Could not reinstate employee. Please try again.");}}} style={{background:"#dcfce7",color:"#166534",border:"none",borderRadius:6,padding:"5px 10px",fontSize:10,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>Reinstate</button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Termination Modal ─────────────────────────────────────────────────── */}
      {termModal && (()=>{
        const UIF_REASONS=[
          {value:"resignation",        label:"Resignation"},
          {value:"retrenchment",       label:"Retrenchment"},
          {value:"dismissal_misconduct",label:"Dismissal — misconduct"},
          {value:"dismissal_performance",label:"Dismissal — poor performance"},
          {value:"dismissal_operational",label:"Dismissal — operational requirements"},
          {value:"retirement",         label:"Retirement"},
          {value:"death",              label:"Death"},
          {value:"end_of_contract",    label:"End of fixed-term contract"},
          {value:"mutual_agreement",   label:"Mutual agreement"},
          {value:"ill_health",         label:"Ill health / incapacity"},
        ];
        return (
          <div style={{position:"fixed",inset:0,background:"#0008",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={e=>{if(e.target===e.currentTarget)setTermModal(null);}}>
            <div style={{background:C.surface,borderRadius:16,padding:28,width:420,maxWidth:"94vw",boxShadow:"0 20px 60px #0003"}}>
              <div style={{fontWeight:700,fontSize:16,color:C.ink,marginBottom:4}}>Terminate Employee</div>
              <div style={{fontSize:13,color:C.inkMid,marginBottom:20}}>{termModal.emp.name} — {termModal.emp.position||"No position"}</div>
              <div style={{marginBottom:14}}>
                <label style={{fontSize:11,fontWeight:600,color:C.inkMid,letterSpacing:0.5,textTransform:"uppercase",display:"block",marginBottom:6}}>Reason for Termination (UIF UI-19)</label>
                <select value={termModal.reason} onChange={e=>setTermModal(m=>({...m,reason:e.target.value}))} style={{width:"100%",padding:"10px 12px",borderRadius:8,border:`1.5px solid ${C.border}`,fontSize:13,color:C.ink,background:C.bg,fontFamily:"inherit",cursor:"pointer"}}>
                  {UIF_REASONS.map(r=><option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              <div style={{marginBottom:20}}>
                <label style={{fontSize:11,fontWeight:600,color:C.inkMid,letterSpacing:0.5,textTransform:"uppercase",display:"block",marginBottom:6}}>Termination Date</label>
                <input type="date" value={termModal.date} onChange={e=>setTermModal(m=>({...m,date:e.target.value}))} style={{width:"100%",padding:"10px 12px",borderRadius:8,border:`1.5px solid ${C.border}`,fontSize:13,color:C.ink,background:C.bg,fontFamily:"inherit",boxSizing:"border-box"}}/>
              </div>
              <div style={{fontSize:11,color:C.inkMid,background:C.bg,borderRadius:8,padding:"10px 12px",marginBottom:20,lineHeight:1.6}}>
                ⚠️ This employee will be excluded from future payroll runs. You can reinstate them at any time from the Terminated Employees section.
              </div>
              <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
                <button onClick={()=>setTermModal(null)} style={{background:C.bg,color:C.inkMid,border:`1px solid ${C.border}`,borderRadius:8,padding:"10px 18px",fontSize:13,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>Cancel</button>
                <button onClick={async()=>{
                  try{
                    const d=await api(`/employees/${termModal.emp.id}/terminate`,{method:"POST",body:JSON.stringify({reason:termModal.reason,date:termModal.date})});
                    setEmployees(employees.map(e=>e.id===termModal.emp.id?{...e,...d,is_active:false}:e));
                    setTermModal(null);
                  }catch(err){alert("Could not terminate employee. Please try again.");}
                }} style={{background:"#b91c1c",color:"#fff",border:"none",borderRadius:8,padding:"10px 18px",fontSize:13,cursor:"pointer",fontFamily:"inherit",fontWeight:700}}>Confirm Termination</button>
              </div>
            </div>
          </div>
        );
      })()}

      {viewPayslip && (
        <PayslipModal
          employee={viewPayslip.employee}
          payroll={viewPayslip.payroll}
          period={new Date().toLocaleDateString("en-ZA",{month:"long",year:"numeric"})}
          company={user.companyName || "Your Company"}
          logoUrl={companyLogoUrl}
          onClose={()=>setViewPayslip(null)}
        />
      )}

      {/* ── Edit Employee Modal ─────────────────────────────────────────────── */}
      {editEmp && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:300,display:"flex",alignItems:"flex-start",justifyContent:"center",padding:"20px 16px",overflowY:"auto"}}>
          <div style={{background:C.surface,borderRadius:20,width:"100%",maxWidth:920,boxShadow:"0 8px 40px #00000030",margin:"auto",display:"flex",flexDirection:"column",maxHeight:"calc(100vh - 40px)"}}>
            {/* Sticky header */}
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"24px 32px 16px",borderBottom:`1px solid ${C.border}`,flexShrink:0}}>
              <h3 style={{fontFamily:"serif",fontSize:22,color:C.ink,margin:0}}>Edit Employee — {editEmp.first_name} {editEmp.last_name}</h3>
              <button onClick={()=>setEditEmp(null)} style={{background:"none",border:"none",fontSize:22,cursor:"pointer",color:C.inkMid}}>×</button>
            </div>
            {/* Scrollable body */}
            <div style={{overflowY:"auto",padding:"24px 32px",flex:1}}>

            {/* ── Section toggle helper ── */}
            {/* Basic Info */}
            <div onClick={()=>toggleEditSec("basic")} style={{display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer",marginBottom:editOpenSec.has("basic")?10:16,padding:"8px 0",borderBottom:`1px solid ${C.border}`}}>
              <span style={{fontSize:11,fontWeight:700,color:C.accent,textTransform:"uppercase",letterSpacing:1}}>Basic Information</span>
              <span style={{fontSize:14,color:C.inkMid,transition:"transform 0.2s",display:"inline-block",transform:editOpenSec.has("basic")?"rotate(180deg)":"rotate(0deg)"}}>▾</span>
            </div>
            {editOpenSec.has("basic") && <>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:20}}>
              {[
                {l:"Full Name",             k:"name",     p:"Jane Smith",    t:"text"},
                {l:"Job Title / Position",  k:"position", p:"Accountant",    t:"text"},
                {l:"Department",            k:"dept",     p:"Finance",       t:"text"},
                {l:"Monthly Salary (ZAR)",  k:"salary",   p:"25000",         t:"number"},
              ].map(f=>(
                <div key={f.k}>
                  <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>{f.l}</label>
                  <input type={f.t} placeholder={f.p} value={editForm[f.k]} onChange={e=>setEditForm(v=>({...v,[f.k]:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}/>
                </div>
              ))}
              {/* Patterson Grade */}
              <div style={{gridColumn:"1 / -1"}}>
                <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Patterson Grade</label>
                <select value={editForm.grade} onChange={e=>setEditForm(v=>({...v,grade:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}>
                  <option value="">-- Select Grade --</option>
                  <optgroup label="Band A — Unskilled / entry-level operational">
                    <option value="A1">A1 — Basic unskilled work</option>
                    <option value="A2">A2 — Semi-skilled / entry operational</option>
                  </optgroup>
                  <optgroup label="Band B — Semi-skilled / skilled operational">
                    <option value="B1">B1 — Skilled trades / junior admin</option>
                    <option value="B2">B2 — Skilled technical / admin</option>
                    <option value="B3">B3 — Senior skilled / specialist</option>
                    <option value="B4">B4 — Senior skilled / team lead</option>
                    <option value="B5">B5 — Senior specialist / supervisor</option>
                  </optgroup>
                  <optgroup label="Band C — Highly skilled / junior professional">
                    <option value="C1">C1 — Junior professional</option>
                    <option value="C2">C2 — Professional / technical specialist</option>
                    <option value="C3">C3 — Senior professional</option>
                    <option value="C4">C4 — Lead professional</option>
                    <option value="C5">C5 — Senior specialist / section manager</option>
                  </optgroup>
                  <optgroup label="Band D — Middle management / senior professional">
                    <option value="D1">D1 — Junior / first-line manager</option>
                    <option value="D2">D2 — Middle manager</option>
                    <option value="D3">D3 — Senior manager (department)</option>
                    <option value="D4">D4 — Senior manager (multi-dept)</option>
                    <option value="D5">D5 — Divisional manager</option>
                  </optgroup>
                  <optgroup label="Band E — Senior management">
                    <option value="E1">E1 — Junior senior manager</option>
                    <option value="E2">E2 — Senior manager / head of function</option>
                    <option value="E3">E3 — Senior middle manager</option>
                    <option value="E4">E4 — General manager</option>
                    <option value="E5">E5 — Senior general manager</option>
                  </optgroup>
                  <optgroup label="Band F — Senior / Executive Management">
                    <option value="F1">F1 — Director / senior executive</option>
                    <option value="F2">F2 — Executive director</option>
                    <option value="F3">F3 — Chief executive / top management</option>
                  </optgroup>
                </select>
                {editForm.grade && PATTERSON_RANGES[editForm.grade] && (() => {
                  const r = PATTERSON_RANGES[editForm.grade];
                  const fmtR = n => "R" + n.toLocaleString("en-ZA");
                  const pct = editForm.salary ? Math.round(((+editForm.salary - r.min) / (r.max - r.min)) * 100) : null;
                  const pos = pct === null ? null : Math.max(0, Math.min(100, pct));
                  return (
                    <div style={{marginTop:10,padding:"12px 14px",background:"#f0f7ff",border:"1px solid #b8d4f0",borderRadius:10}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                        <div style={{fontSize:12,fontWeight:700,color:"#1a4a7a"}}>📊 Market range for {editForm.grade} — 2025 SA general market (monthly CTC)</div>
                        <button type="button" onClick={()=>setEditForm(v=>({...v,salary:String(r.median)}))} style={{padding:"4px 12px",background:"#1a6fcc",border:"none",borderRadius:6,color:"#fff",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>Use median</button>
                      </div>
                      <div style={{position:"relative",height:8,background:"#d0e4f8",borderRadius:4,marginBottom:6}}>
                        <div style={{position:"absolute",left:"33%",right:"33%",top:0,height:"100%",background:"#6baee8",borderRadius:4}}/>
                        {pos !== null && <div style={{position:"absolute",top:-3,left:`${pos}%`,transform:"translateX(-50%)",width:14,height:14,borderRadius:7,background:pos < 33 ? "#e8a020" : pos > 67 ? "#1a6fcc" : "#2a9d3f",border:"2px solid #fff",boxShadow:"0 1px 3px rgba(0,0,0,0.2)"}}/>}
                      </div>
                      <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:"#3a6a9a"}}>
                        <span>Min: <strong>{fmtR(r.min)}</strong></span>
                        <span>Median: <strong style={{color:"#1a4a7a"}}>{fmtR(r.median)}</strong></span>
                        <span>Max: <strong>{fmtR(r.max)}</strong></span>
                      </div>
                      {pos !== null && <div style={{marginTop:6,fontSize:11,fontWeight:600,color:pos < 25 ? "#b85c00" : pos > 75 ? "#1a4a7a" : "#2a7d3f"}}>{pos < 25 ? "⚠️ Below market — risk of turnover" : pos > 75 ? "💡 Above median — strong retention" : "✓ Within market range"}</div>}
                      <div style={{marginTop:4,fontSize:10,color:"#6a8aaa"}}>Indicative only — varies by industry, province &amp; experience. Source: Peromnes/Patterson surveys.</div>
                    </div>
                  );
                })()}
              </div>
              {/* Employment Type */}
              <div>
                <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Employment Type</label>
                <select value={editForm.employmentType} onChange={e=>setEditForm(v=>({...v,employmentType:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}>
                  <option value="salaried">Salaried (fixed monthly)</option>
                  <option value="hourly">Hourly rate</option>
                </select>
              </div>
              {/* Pay Schedule */}
              <div>
                <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Pay Schedule</label>
                <select value={editForm.paySchedule||"salaried"} onChange={e=>setEditForm(v=>({...v,paySchedule:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}>
                  <option value="salaried">💼 Salaried Staff</option>
                  <option value="security">🛡️ Security Officers</option>
                  <option value="casual">👷 Casual / Temp</option>
                </select>
              </div>
              {/* Employment Category (UIF/SDL exemption) */}
              <div style={{gridColumn:"1 / -1"}}>
                <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Employment Category</label>
                <select value={editForm.employmentCategory||"permanent"} onChange={e=>setEditForm(v=>({...v,employmentCategory:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}>
                  <option value="permanent">Permanent — full UIF + SDL</option>
                  <option value="fixed_term">Fixed-Term Contract — full UIF + SDL</option>
                  <option value="temp_labour">Casual / Temporary (own staff) — full UIF + SDL</option>
                  <option value="learnership">Learnership — UIF applies</option>
                  <option value="public_servant">Public Servant — no UIF</option>
                  <option value="foreign_national">Foreign National — UIF applies</option>
                </select>
                <div style={{marginTop:6,fontSize:11,color:"#475569",background:"#f1f5f9",borderRadius:6,padding:"6px 10px"}}>ℹ️ Directors: executive/shareholder-directors paid a salary through payroll are employees (use Permanent — UIF applies, regardless of age). Non-executive directors paid fees only are generally not employees — do not add them to payroll.</div>
                {editForm.employmentCategory==="public_servant" && (
                  <div style={{marginTop:6,fontSize:11,color:"#b45309",background:"#fef3c7",borderRadius:6,padding:"6px 10px"}}>
                    ⚠️ UIF will not be deducted for this employee (public servants are excluded from UIF). Independent contractors and labour-broker workers should not be captured as employees — add them under Suppliers.
                  </div>
                )}
              </div>
              {editForm.employmentType === "hourly" && (
                <div>
                  <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Hourly Rate (ZAR)</label>
                  <input type="number" placeholder="e.g. 150" value={editForm.hourlyRate} onChange={e=>setEditForm(v=>({...v,hourlyRate:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}/>
                </div>
              )}
            </div>

            </>}

            {/* Personal Details */}
            <div onClick={()=>toggleEditSec("personal")} style={{display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer",marginBottom:editOpenSec.has("personal")?10:16,padding:"8px 0",borderBottom:`1px solid ${C.border}`}}>
              <span style={{fontSize:11,fontWeight:700,color:C.accent,textTransform:"uppercase",letterSpacing:1}}>Personal Details</span>
              <span style={{fontSize:14,color:C.inkMid,display:"inline-block",transform:editOpenSec.has("personal")?"rotate(180deg)":"rotate(0deg)"}}>▾</span>
            </div>
            {editOpenSec.has("personal") && <>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:20}}>
              {[
                {l:"SA ID Number",      k:"idNumber",        p:"8001015009087", t:"text"},
                {l:"Tax Number",        k:"taxNumber",        p:"1234567890",    t:"text"},
                {l:"Date of Birth",     k:"dob",              p:"",              t:"date"},
                {l:"Appointment Date",  k:"appointmentDate",  p:"",              t:"date"},
              ].map(f=>(
                <div key={f.k}>
                  <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>{f.l}</label>
                  <input type={f.t} placeholder={f.p} value={editForm[f.k]} onChange={e=>setEditForm(v=>({...v,[f.k]:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}/>
                </div>
              ))}
              <div style={{gridColumn:"1 / -1"}}>
                <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Residential Address</label>
                <input placeholder="123 Main St, Johannesburg, 2000" value={editForm.address} onChange={e=>setEditForm(v=>({...v,address:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}/>
              </div>
            </div>

            </>}

            {/* Benefits */}
            <div onClick={()=>toggleEditSec("benefits")} style={{display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer",marginBottom:editOpenSec.has("benefits")?10:16,padding:"8px 0",borderBottom:`1px solid ${C.border}`}}>
              <span style={{fontSize:11,fontWeight:700,color:C.accent,textTransform:"uppercase",letterSpacing:1}}>Pension / Provident Fund &amp; Medical Aid</span>
              <span style={{fontSize:14,color:C.inkMid,display:"inline-block",transform:editOpenSec.has("benefits")?"rotate(180deg)":"rotate(0deg)"}}>▾</span>
            </div>
            {editOpenSec.has("benefits") && <>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:12}}>
              {[
                {l:"Employee Pension %",              k:"pensionEmployeePct",   p:"7.5",  t:"number"},
                {l:"Employee Fixed Top-up (ZAR/mo)", k:"pensionEmployeeFixed",  p:"500",  t:"number"},
                {l:"Employer Pension %",              k:"pensionEmployerPct",   p:"7.5",  t:"number"},
                {l:"Employer Fixed Add-on (ZAR/mo)", k:"pensionEmployerFixed",  p:"500",  t:"number"},
                {l:"Medical Aid — Employee (ZAR/mo)",k:"medicalAidEmployee",   p:"1500", t:"number"},
                {l:"Medical Aid — Employer (ZAR/mo)",k:"medicalAidEmployer",   p:"1500", t:"number"},
                {l:"Medical Aid Dependants",          k:"medicalAidDependants", p:"1",    t:"number"},
              ].map(f=>(
                <div key={f.k}>
                  <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>{f.l}</label>
                  <input type={f.t} placeholder={f.p} value={editForm[f.k]||""} onChange={e=>setEditForm(v=>({...v,[f.k]:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}/>
                </div>
              ))}
            </div>
            {editForm.salary && (editForm.pensionEmployeePct || editForm.pensionEmployeeFixed || editForm.medicalAidEmployee || editForm.medicalAidEmployer) && (()=>{
              const p = calcPayroll(+editForm.salary, null, 0, 0, 0, null,
                editForm.pensionEmployeePct ? +editForm.pensionEmployeePct/100 : 0,
                editForm.pensionEmployerPct ? +editForm.pensionEmployerPct/100 : 0,
                editForm.medicalAidEmployee ? +editForm.medicalAidEmployee : 0,
                editForm.medicalAidEmployer ? +editForm.medicalAidEmployer : 0,
                editForm.medicalAidDependants ? +editForm.medicalAidDependants : 0,
                editForm.pensionEmployeeFixed ? +editForm.pensionEmployeeFixed : 0,
                editForm.pensionEmployerFixed ? +editForm.pensionEmployerFixed : 0,
                // audit fix 2026-09-16 — see [[project_nbcpss_208hr_bug]]
                { isSecurity: (user?.industry||"").toLowerCase().replace(/[\s-]/g,"_")==="private_security" && !!editForm.securityGrade },
              );
              const hasExcess = p.s11fExcessMonthly > 0;
              return (
                <div style={{background:C.blueLt,border:`1px solid ${hasExcess?C.gold:C.blue}30`,borderRadius:8,padding:"10px 14px",marginBottom:16,fontSize:12}}>
                  <strong style={{color:C.blue}}>Live preview: </strong>
                  <span style={{color:C.ink}}>
                    PAYE {fmt(p.paye)} · s11F relief {fmt(p.s11fDeduction)}/mo · MTC R{p.medicalTaxCredit} · Net Pay {fmt(p.netPay)} · Employer Cost {fmt(p.totalCost)}
                  </span>
                  {hasExcess && (
                    <div style={{marginTop:6,fontSize:11,color:C.gold,fontWeight:600}}>
                      ⚠️ {fmt(p.s11fExcessMonthly)}/month above s11F cap — deducted from after-tax income (contribution is still processed in full, no additional PAYE relief on the excess).
                    </div>
                  )}
                </div>
              );
            })()}

            </>}

            {/* Sector Section (MIBCO / NBCPSS) */}
            {((user?.industry||"").toLowerCase().replace(/[\s-]/g,"_")==="fuel_station" || user?.industry==="private_security") && <>
            <div onClick={()=>toggleEditSec("sector")} style={{display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer",marginBottom:editOpenSec.has("sector")?10:16,padding:"8px 0",borderBottom:`1px solid ${C.border}`}}>
              <span style={{fontSize:11,fontWeight:700,color:C.accent,textTransform:"uppercase",letterSpacing:1}}>
                {(user?.industry||"").toLowerCase().replace(/[\s-]/g,"_")==="fuel_station" ? "⛽ MIBCO Sector 5 — Fuel Retail" : "🔒 NBCPSS — Private Security Sector"}
              </span>
              <span style={{fontSize:14,color:C.inkMid,display:"inline-block",transform:editOpenSec.has("sector")?"rotate(180deg)":"rotate(0deg)"}}>▾</span>
            </div>
            {editOpenSec.has("sector") && <>
            {/* MIBCO Fuel Station Section — edit employee */}
            {(user?.industry||"").toLowerCase().replace(/[\s-]/g,"_")==="fuel_station" && (()=>{
              const MIBCO_ROLES = {forecourt_attendant:"Forecourt Attendant",cashier:"Cashier",char:"Char / Cleaner"};
              const MIBCO_HR = {forecourt_attendant:45.79,cashier:45.30,char:34.64};
              const mibcoHr = MIBCO_HR[editForm.mibcoRole] || 0;
              const mibcoMonthMin = mibcoHr > 0 ? Math.round(mibcoHr * 45 * (52/12) * 100) / 100 : 0;
              const belowMibco = editForm.mibcoRole && editForm.salary && mibcoMonthMin > 0 && +editForm.salary < mibcoMonthMin;
              return (
                <div style={{marginBottom:16}}>
                  <div style={{fontSize:11,fontWeight:700,color:"#92400e",textTransform:"uppercase",letterSpacing:1,marginBottom:10}}>⛽ MIBCO Sector 5 — Fuel Retail (eff. 22 Dec 2025)</div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:8}}>
                    <div>
                      <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:0.5}}>MIBCO Role</label>
                      <select value={editForm.mibcoRole||""} onChange={e=>setEditForm(v=>({...v,mibcoRole:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}>
                        <option value="">-- Select Role --</option>
                        {Object.entries(MIBCO_ROLES).map(([k,v])=><option key={k} value={k}>{v} (min R{MIBCO_HR[k].toFixed(2)}/hr)</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:0.5}}>Health Scheme (Affinity Health via MIBCO)</label>
                      <div style={{display:"flex",alignItems:"center",gap:10,height:42}}>
                        <input type="checkbox" id="editMibcoScheme" checked={editForm.mibcoSchemeEnrolled!=null?!!editForm.mibcoSchemeEnrolled:true} onChange={e=>setEditForm(v=>({...v,mibcoSchemeEnrolled:e.target.checked}))} style={{width:16,height:16,cursor:"pointer"}}/>
                        <label htmlFor="editMibcoScheme" style={{fontSize:12,color:C.ink,cursor:"pointer"}}>Enrolled — Employee: R174/mo deduction · Employer: R90/mo</label>
                      </div>
                    </div>
                  </div>
                  {editForm.mibcoRole && <div style={{padding:"8px 12px",background:belowMibco?"#fff1f2":"#fffbeb",border:`1px solid ${belowMibco?"#fca5a5":"#fbbf24"}`,borderRadius:8,fontSize:12,marginBottom:8}}>
                    {belowMibco
                      ? <span style={{color:"#b91c1c",fontWeight:600}}>⚠️ Salary R{(+editForm.salary).toLocaleString("en-ZA")} is below the MIBCO minimum for {MIBCO_ROLES[editForm.mibcoRole]}: <strong>R{mibcoMonthMin.toLocaleString("en-ZA")}/month</strong> (R{mibcoHr.toFixed(2)}/hr × 45h × 4.33 wks). Adjust before saving.</span>
                      : <span style={{color:"#78350f"}}>✓ {MIBCO_ROLES[editForm.mibcoRole]} minimum: <strong>R{mibcoMonthMin.toLocaleString("en-ZA")}/month</strong>. Medical Insurance Allowance R85.00/mo added to gross. Health scheme and BC levy applied automatically.</span>
                    }
                  </div>}
                </div>
              );
            })()}

            {/* NBCPSS Private Security Section — edit employee */}
            {user?.industry === "private_security" && (()=>{
              const minWage = nbcpssMinMonthly(editForm.securityGrade, editForm.securityArea||"3");
              const belowMin = editForm.salary && editForm.securityGrade && +editForm.salary < minWage;
              const _medEmp  = editForm.securityArea==="1_2" ? 122 : 122;
              const _medEmpr = editForm.securityArea==="1_2" ? 172.50 : 172.50;
              const _areaLabel = editForm.securityArea==="1_2" ? "Area 1 & 2 (Urban/Metro)" : "Area 3 (Rural / All Other)";
              return (
                <div style={{marginBottom:16}}>
                  <div style={{fontSize:11,fontWeight:700,color:"#c2410c",textTransform:"uppercase",letterSpacing:1,marginBottom:10}}>🔒 NBCPSS — Private Security Sector (1 Mar 2026 – 28 Feb 2027)</div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:8}}>
                    <div>
                      <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:0.5}}>PSIRA Number</label>
                      <input placeholder="e.g. 1234567890" value={editForm.psiraNumber||""} onChange={e=>setEditForm(v=>({...v,psiraNumber:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}/>
                    </div>
                    <div>
                      <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:0.5}}>Security Grade</label>
                      <select value={editForm.securityGrade||""} onChange={e=>{const g=e.target.value;setEditForm(v=>({...v,securityGrade:g,pensionEmployeePct:g?"7.5":v.pensionEmployeePct,pensionEmployerPct:g?"7.5":v.pensionEmployerPct}));}} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}>
                        <option value="">-- Select --</option>
                        <option value="A">Grade A — Armed/Supervisory</option>
                        <option value="B">Grade B — Armed Response/Supervisor</option>
                        <option value="C">Grade C — Armed Guard</option>
                        <option value="D">Grade D — Unarmed/Access Control</option>
                        <option value="E">Grade E — Entry Level/Car Guard</option>
                      </select>
                    </div>
                    <div>
                      <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:0.5}}>Area (NBCPSS)</label>
                      <select value={editForm.securityArea||"3"} onChange={e=>setEditForm(v=>({...v,securityArea:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}>
                        <option value="3">Area 3 — Rural / All Other Districts</option>
                        <option value="1_2">Area 1 & 2 — Urban / Metro</option>
                      </select>
                    </div>
                    <div>
                      <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:0.5}}>Shift Type</label>
                      <select value={editForm.shiftType||"day"} onChange={e=>setEditForm(v=>({...v,shiftType:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}>
                        <option value="day">Day Shift</option>
                        <option value="night">Night Shift (R8.00/shift allowance)</option>
                        <option value="rotating">Rotating (Day &amp; Night)</option>
                      </select>
                    </div>
                    <div>
                      <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:0.5}}>Special Allowance</label>
                      <select value={editForm.specialAllowanceType||"none"} onChange={e=>setEditForm(v=>({...v,specialAllowanceType:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}>
                        <option value="none">None</option>
                        <option value="armed">Armed Security Officer (R10.50/shift)</option>
                        <option value="armed_response">Armed Response Officer (R10.50/shift)</option>
                        <option value="nkp">National Key Point Officer (R10.50/shift)</option>
                        <option value="control_centre">Control/Comm Centre Operator (R10.50/shift)</option>
                        <option value="canine">Canine / Dog Handler (R10.50/shift)</option>
                        <option value="mobile_supervisor">Mobile Supervisor (R10.50/shift)</option>
                      </select>
                    </div>
                    <div>
                      <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:0.5}}>Union Subscription (R/month)</label>
                      <input type="number" min="0" step="0.01" placeholder="0.00" value={editForm.unionSubscription||""} onChange={e=>setEditForm(v=>({...v,unionSubscription:parseFloat(e.target.value)||0}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}/>
                    </div>
                    <div>
                      <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:0.5}}>Advance Repayment (R/month)</label>
                      <input type="number" min="0" step="0.01" placeholder="0.00" value={editForm.advanceMonthlyDeduction||""} onChange={e=>setEditForm(v=>({...v,advanceMonthlyDeduction:parseFloat(e.target.value)||0}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}/>
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0"}}><input type="checkbox" id="editPsspfEx" checked={editForm.psspfExempt||false} onChange={e=>setEditForm(v=>({...v,psspfExempt:e.target.checked}))} style={{width:16,height:16,cursor:"pointer"}}/><label htmlFor="editPsspfEx" style={{fontSize:13,color:C.ink,cursor:"pointer",fontWeight:600}}>Not a PSSPF member <span style={{fontSize:11,color:C.inkMid,fontWeight:400}}>(tick only if exempt - otherwise PSSPF provident is applied automatically and any pension fields above are ignored)</span></label></div><div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0"}}><input type="checkbox" id="editSunOrd" checked={editForm.sundayOrdinary||false} onChange={e=>setEditForm(v=>({...v,sundayOrdinary:e.target.checked}))} style={{width:16,height:16,cursor:"pointer"}}/><label htmlFor="editSunOrd" style={{fontSize:13,color:C.ink,cursor:"pointer",fontWeight:600}}>Ordinarily works Sundays <span style={{fontSize:11,color:C.inkMid,fontWeight:400}}>(BCEA s16: Sunday work paid at 1.5× instead of 2×. Security officers always get 1.5×)</span></label></div>
                    <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0"}}>
                      <input type="checkbox" id="editMatLeave" checked={editForm.onMaternityLeave||false} onChange={e=>setEditForm(v=>({...v,onMaternityLeave:e.target.checked}))} style={{width:16,height:16,cursor:"pointer"}}/>
                      <label htmlFor="editMatLeave" style={{fontSize:13,color:C.ink,cursor:"pointer",fontWeight:600}}>On Maternity Leave <span style={{fontSize:11,color:C.inkMid,fontWeight:400}}>(zero gross this pay run; employee claims UIF directly)</span></label>
                    </div>
                  </div>
                  {editForm.securityGrade && <div style={{padding:"8px 12px",background:belowMin?"#fff1f2":"#f0fdf4",border:`1px solid ${belowMin?"#fca5a5":"#86efac"}`,borderRadius:8,fontSize:12,marginBottom:8}}>
                    {belowMin
                      ? <span style={{color:"#b91c1c",fontWeight:600}}>⚠️ Salary R{(+editForm.salary).toLocaleString("en-ZA")} is below the NBCPSS minimum for Grade {editForm.securityGrade} ({_areaLabel}): <strong>R{minWage.toLocaleString("en-ZA")}/month</strong>. Adjust before saving.</span>
                      : <span style={{color:"#166534"}}>✓ Grade {editForm.securityGrade} · {_areaLabel} · Min R{minWage.toLocaleString("en-ZA")}/mo · Med aid R{_medEmp}/R{_medEmpr} (emp/empr) · Cleaning R32 · BC levy R7 · PSIRA R4 · PSSPF provident (employee + employer).</span>
                    }
                  </div>}
                </div>
              );
            })()}

            </>}{/* end sector content */}
            </>}{/* end sector wrapper */}

            {/* Bank Details */}
            <div onClick={()=>toggleEditSec("bank")} style={{display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer",marginBottom:editOpenSec.has("bank")?10:16,padding:"8px 0",borderBottom:`1px solid ${C.border}`}}>
              <span style={{fontSize:11,fontWeight:700,color:C.accent,textTransform:"uppercase",letterSpacing:1}}>Bank Details</span>
              <span style={{fontSize:14,color:C.inkMid,display:"inline-block",transform:editOpenSec.has("bank")?"rotate(180deg)":"rotate(0deg)"}}>▾</span>
            </div>
            {editOpenSec.has("bank") && <>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:12,marginBottom:24}}>
              <div>
                <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Bank Name</label>
                <select value={editForm.bankName} onChange={e=>setEditForm(v=>({...v,bankName:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}>
                  <option value="">-- Select Bank --</option>
                  {["ABSA","Capitec","Discovery Bank","FNB","Investec","Nedbank","Standard Bank","TymeBank","African Bank"].map(b=><option key={b}>{b}</option>)}
                </select>
              </div>
              {[
                {l:"Account Number", k:"accountNumber", p:"62123456789"},
                {l:"Branch Code",    k:"branchCode",    p:"250655"},
              ].map(f=>(
                <div key={f.k}>
                  <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>{f.l}</label>
                  <input placeholder={f.p} value={editForm[f.k]} onChange={e=>setEditForm(v=>({...v,[f.k]:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}/>
                </div>
              ))}
              <div>
                <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Account Type</label>
                <select value={editForm.accountType} onChange={e=>setEditForm(v=>({...v,accountType:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}>
                  {["Cheque","Savings","Transmission"].map(t=><option key={t}>{t}</option>)}
                </select>
              </div>
            </div>

            </>}

            {/* ── Garnishee orders ─────────────────────────────────────────── */}
            <div onClick={()=>toggleEditSec("garnishee")} style={{display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer",marginBottom:editOpenSec.has("garnishee")?10:16,padding:"8px 0",borderBottom:`1px solid ${C.border}`}}>
              <span style={{fontSize:11,fontWeight:700,color:C.accent,textTransform:"uppercase",letterSpacing:1}}>Garnishee / Emolument Attachment Orders</span>
              <span style={{fontSize:14,color:C.inkMid,display:"inline-block",transform:editOpenSec.has("garnishee")?"rotate(180deg)":"rotate(0deg)"}}>▾</span>
            </div>
            {editOpenSec.has("garnishee") && <div style={{padding:"14px 16px",background:C.surfaceAlt||C.bg,border:`1px solid ${C.border}`,borderRadius:12,marginBottom:16}}>
              {garnishees.length===0 && <div style={{fontSize:12,color:C.inkMid,marginBottom:8}}>No active garnishee orders.</div>}
              {garnishees.map(g=>(
                <div key={g.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"6px 10px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:8,marginBottom:6}}>
                  <span style={{fontSize:13,color:C.ink}}>{g.reference}</span>
                  <span style={{fontSize:13,fontWeight:700,color:C.ink}}>{fmt(g.amount)}/month</span>
                  <button onClick={()=>removeGarnishee(g.id)} style={{background:"#fff1f2",color:"#b91c1c",border:"1px solid #fca5a5",borderRadius:6,padding:"3px 10px",fontSize:11,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>Remove</button>
                </div>
              ))}
              <div style={{display:"flex",gap:8,marginTop:8}}>
                <input placeholder="Order reference (e.g. court case no.)" value={newGarnRef} onChange={e=>setNewGarnRef(e.target.value)} style={{flex:2,padding:"8px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}/>
                <input type="number" min="0" step="0.01" placeholder="Amount R" value={newGarnAmt} onChange={e=>setNewGarnAmt(e.target.value)} style={{flex:1,padding:"8px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}/>
                <button onClick={addGarnishee} style={{background:C.accent,color:"#fff",border:"none",borderRadius:8,padding:"8px 14px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>+ Add</button>
              </div>
            </div>}

            </div>{/* end scrollable body */}

            {/* Sticky footer */}
            <div style={{display:"flex",gap:8,padding:"16px 32px",borderTop:`1px solid ${C.border}`,flexShrink:0,background:C.surface,borderRadius:"0 0 20px 20px"}}>
              <button onClick={handleEditSave} style={{background:C.accent,color:"#fff",border:"none",borderRadius:10,padding:"10px 24px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Save Changes</button>
              <button onClick={()=>setEditEmp(null)} style={{background:"transparent",color:C.inkMid,border:`1px solid ${C.border}`,borderRadius:10,padding:"10px 20px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
// REPORTS
function ReportRow({label,value,bold,border,large,indent,color}) {
  const isNeg = value < 0;
  const displayVal = isNeg ? "("+fmt(Math.abs(value))+")" : fmt(value);
  return (
    <div style={{display:"flex",justifyContent:"space-between",padding:(large?"14px":"9px")+" 0",borderTop:border?"2px solid "+C.border:"1px solid "+C.border+"20"}}>
      <span style={{fontSize:large?15:13,fontWeight:bold?700:400,color:C.ink,paddingLeft:indent?20:0}}>{label}</span>
      <span style={{fontSize:large?18:14,fontWeight:bold?800:500,color:color||(isNeg?C.red:C.green)}}>{displayVal}</span>
    </div>
  );
}

function PrintBtn({onClick}) {
  return <button onClick={onClick} style={{background:C.accent,color:"#fff",border:"none",borderRadius:8,padding:"8px 18px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Print PDF</button>;
}

function ExcelBtn({data,filename}) {
  const download = () => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    XLSX.writeFile(wb, filename.replace(".csv",".xlsx"));
  };
  return <button onClick={download} style={{background:C.greenLt,color:C.green,border:"1px solid "+C.green+"40",borderRadius:8,padding:"8px 18px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Export Excel</button>;
}

function Reports({live = {}, user = null}) {
  const [activeTab, setActiveTab] = useState("mgmt");
  const [bsData,  setBsData]  = useState(null);
  const [cfData,  setCfData]  = useState(null);
  // emp201 moved to Payroll component
  const [mgmt,     setMgmt]     = useState(null);
  const [provTax,  setProvTax]  = useState(null);
  const [vat201,   setVat201]   = useState(null);
  const [recon,    setRecon]    = useState(null);
  const [trialBal, setTrialBal] = useState(null);
  const [journal,  setJournal]  = useState(null);
  const [jnlExpanded, setJnlExpanded] = useState(new Set());
  const [jnlAccounts, setJnlAccounts] = useState([]);
  const [jnlForm, setJnlForm] = useState(null); // null = hidden, object = visible
  const JNL_EMPTY_FORM = {date:new Date().toISOString().slice(0,10),description:"",reference:"",auto_reverse:false,reversal_date:"",lines:[{account_id:"",debit:"",credit:"",description:""},{account_id:"",debit:"",credit:"",description:""}]};

  const [vatPeriod,setVatPeriod]= useState(new Date().toISOString().slice(0,7));
  const [vatMonths,setVatMonths]= useState(1);
  const [loading,  setLoading]  = useState(false);
  const [mgmtDrill, setMgmtDrill] = useState(null); // {type,title,cols,rows,total,color}
  const [mgmtDrillLoading, setMgmtDrillLoading] = useState(false);
  const [aiInsights,    setAiInsights]    = useState(null);  // [{text}] or null
  const [aiLoading,     setAiLoading]     = useState(false);
  const [aiError,       setAiError]       = useState(null);
  const [reclassLoading, setReclassLoading] = useState(false);
  const [reclassResult,  setReclassResult]  = useState(null); // null | {fixed, message}

  // ── Date range ──────────────────────────────────────────────────
  const now = new Date();
  const firstOfMonth  = new Date(now.getFullYear(), now.getMonth(), 1);
  const firstOfLast   = new Date(now.getFullYear(), now.getMonth()-1, 1);
  const lastOfLast    = new Date(now.getFullYear(), now.getMonth(), 0);
  // SA financial year: 1 Mar – last Feb
  const fyStart = now.getMonth() >= 2
    ? new Date(now.getFullYear(), 2, 1)
    : new Date(now.getFullYear()-1, 2, 1);
  const fyEnd   = new Date(fyStart.getFullYear()+1, 1, 28);
  const toISO   = d => d.toISOString().slice(0,10);
  const PRESETS = [
    {label:"Current Month",  from:toISO(firstOfMonth), to:toISO(now)},
    {label:"Last Month",     from:toISO(firstOfLast),  to:toISO(lastOfLast)},
    {label:"Financial Year", from:toISO(fyStart),      to:toISO(fyEnd)},
    {label:"All Time",       from:"2000-01-01",         to:toISO(now)},
    {label:"Custom",         from:"",                  to:""},
  ];
  const [preset,   setPreset]   = useState("All Time");
  const [dateFrom, setDateFrom] = useState("2000-01-01");
  const [dateTo,   setDateTo]   = useState(toISO(now));
  const handlePreset = (label) => {
    setPreset(label);
    const p = PRESETS.find(p => p.label === label);
    if (label !== "Custom") { setDateFrom(p.from); setDateTo(p.to); }
  };
  const periodLabel = preset === "Custom"
    ? `${dateFrom} to ${dateTo}`
    : preset === "Financial Year"
      ? `FY ${fyStart.getFullYear()}/${fyEnd.getFullYear()}`
      : preset;
  // ────────────────────────────────────────────────────────────────

  const d   = live.dashboard;
  const period = d ? d.period : periodLabel;
  const totalRevenue  = d ? d.total_revenue  : MOCK_INVOICES.filter(i=>i.status==="paid").reduce((s,i)=>s+i.amount,0);
  const totalExpenses = d ? d.total_expenses : MOCK_EXPENSES.reduce((s,e)=>s+e.amount,0);
  const totalPayroll  = d ? d.total_payroll  : MOCK_EMPLOYEES.reduce((s,e)=>s+calcPayroll(e.salary).totalCost,0);
  const grossProfit   = d ? d.gross_profit   : totalRevenue - totalExpenses;
  const netProfit     = d ? d.net_profit     : grossProfit - totalPayroll;
  const taxProvision  = d ? d.tax_provision  : Math.max(0, netProfit * 0.27);
  const netAfterTax   = netProfit - taxProvision;
  const dateParams = `date_from=${dateFrom}&date_to=${dateTo}`;
  const loadTab = async (tab, forceReload=false) => {
    setActiveTab(tab);
    setLoading(true);
    try {
      if (tab==="bs")    setBsData(await api(`/reports/balance-sheet`).catch(()=>null));
      if (tab==="recon") setRecon(await api(`/reports/reconciliation`).catch(()=>null));
      if (tab==="tb")    setTrialBal(await api(`/journal/trial-balance`).catch(()=>null));
      if (tab==="jnl") {
        setJournal(await api(`/journal/?limit=500&${dateParams}`).catch(()=>null));
        if (!jnlAccounts.length) api("/journal/accounts").then(setJnlAccounts).catch(()=>null);
      }
      if (tab==="cf")   setCfData(await api(`/reports/cash-flow?${dateParams}`).catch(()=>null));
      // emp201 now lives in Payroll component
      if (tab==="pl")   setMgmt(await api(`/reports/management?${dateParams}`).catch(()=>null));
      if (tab==="mgmt") setMgmt(await api(`/reports/management?${dateParams}`).catch(()=>null));
      if (tab==="prov") setProvTax(await api(`/reports/provisional-tax?${dateParams}`).catch(()=>null));
      if (tab==="vat")  setVat201(await api(`/reports/vat201?period=${vatPeriod}&months=${vatMonths}&${dateParams}`).catch(()=>null));
    } finally { setLoading(false); }
  };
  // Reload current tab when date range changes
  useEffect(() => { loadTab(activeTab, true); }, [dateFrom, dateTo]);
  const RTABS = [
    {id:"mgmt", label:"Management Pack"},
    {id:"pl",   label:"Income Statement"},
    {id:"bs",   label:"Balance Sheet"},
    {id:"cf",   label:"Cash Flow"},
    {id:"recon",label:"Reconciliation"},
    {id:"tb",   label:"Trial Balance"},
    {id:"jnl",  label:"Journal"},
    {id:"prov", label:"Provisional Tax"},
    {id:"vat",  label:"VAT201"},
  ];
  const renderBS = () => {
    const bs = bsData;
    if (!bs) return <div style={{color:C.inkMid,fontSize:13,padding:20}}>Loading balance sheet…</div>;
    const balanced = bs.balanced;
    return (
      <div>
        <div style={{display:"flex",justifyContent:"space-between",marginBottom:16}}>
          <div><div style={{fontFamily:"serif",fontSize:20,fontWeight:800,color:C.ink}}>Balance Sheet</div><div style={{fontSize:12,color:C.inkMid,marginTop:4}}>As at {bs.date}</div></div>
          <ExcelBtn filename="balance-sheet.csv" data={[
            {Item:"Cash & Equivalents",Amount:bs.assets.cash_and_equivalents},
            {Item:"Trade Receivables",Amount:bs.assets.trade_receivables},
            {Item:"Inventory at Cost",Amount:bs.assets.inventory_at_cost},
            {Item:"VAT Input Recoverable",Amount:bs.assets.vat_input_recoverable},
            {Item:"Fixed Assets at Cost",Amount:bs.assets.fixed_assets_cost||0},
            {Item:"Accumulated Depreciation",Amount:bs.assets.accum_depreciation||0},
            {Item:"Fixed Assets (Net)",Amount:bs.assets.fixed_assets_net||0},
            {Item:"Total Assets",Amount:bs.assets.total},
            {Item:"Accounts Payable",Amount:bs.liabilities.accounts_payable},
            {Item:"VAT Payable",Amount:bs.liabilities.vat_payable},
            {Item:"PAYE Payable",Amount:bs.liabilities.paye_payable},
            {Item:"UIF Payable",Amount:bs.liabilities.uif_payable},
            {Item:"SDL Payable",Amount:bs.liabilities.sdl_payable},
            {Item:"Total Liabilities",Amount:bs.liabilities.total},
            {Item:"Retained Income",Amount:bs.equity.retained_income},
            {Item:"Total Equity",Amount:bs.equity.total},
            {Item:"Total Liabilities & Equity",Amount:bs.total_liabilities_and_equity},
          ]}/>
        </div>
        {/* Balance check banner */}
        <div style={{background:balanced?C.greenLt:"#FFF3F3",border:`1px solid ${balanced?C.green:C.red}`,borderRadius:10,padding:"10px 16px",marginBottom:20,display:"flex",alignItems:"center",gap:10}}>
          <span style={{fontSize:18}}>{balanced?"✅":"❌"}</span>
          <span style={{fontSize:13,fontWeight:600,color:balanced?C.green:C.red}}>
            {balanced
              ? "Balance sheet is balanced — Assets = Liabilities + Equity"
              : `Balance sheet imbalance of R ${Math.abs(bs.imbalance).toLocaleString("en-ZA",{minimumFractionDigits:2})} — check the Reconciliation tab for details`}
          </span>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:24}}>
          <div>
            <div style={{fontSize:11,fontWeight:700,color:C.blue,textTransform:"uppercase",letterSpacing:1,marginBottom:12}}>Assets</div>
            <ReportRow label="Cash and Cash Equivalents"   value={bs.assets.cash_and_equivalents} color={C.blue}/>
            <ReportRow label="Trade Receivables (Debtors)" value={bs.assets.trade_receivables}    color={C.blue}/>
            <ReportRow label="Inventory at Cost"           value={bs.assets.inventory_at_cost}    color={C.blue}/>
            {bs.assets.vat_input_recoverable > 0 && <ReportRow label="VAT Input Recoverable" value={bs.assets.vat_input_recoverable} color={C.blue}/>}
            {(bs.assets.fixed_assets_cost||0) > 0 && <>
              <ReportRow label="Fixed Assets at Cost"       value={bs.assets.fixed_assets_cost}   color={C.blue} indent/>
              <ReportRow label="Accumulated Depreciation"   value={bs.assets.accum_depreciation}  color={C.red}  indent/>
              <ReportRow label="Fixed Assets (Net)"         value={bs.assets.fixed_assets_net}    color={C.blue}/>
            </>}
            {(bs.assets.other_assets||0) !== 0 && <ReportRow label="Other Assets (imported)" value={bs.assets.other_assets} color={C.blue}/>}
            <ReportRow label="Total Assets"                value={bs.assets.total} bold border color={C.blue}/>
          </div>
          <div>
            <div style={{fontSize:11,fontWeight:700,color:C.red,textTransform:"uppercase",letterSpacing:1,marginBottom:12}}>Liabilities</div>
            <ReportRow label="Accounts Payable (Suppliers)" value={bs.liabilities.accounts_payable} indent color={C.red}/>
            <ReportRow label="VAT Payable (SARS)"           value={bs.liabilities.vat_payable}      indent color={C.red}/>
            <ReportRow label="PAYE Payable (SARS)"          value={bs.liabilities.paye_payable}     indent color={C.red}/>
            <ReportRow label="UIF Payable (SARS)"           value={bs.liabilities.uif_payable}      indent color={C.red}/>
            <ReportRow label="SDL Payable (SARS)"           value={bs.liabilities.sdl_payable}      indent color={C.red}/>
            {(bs.liabilities.income_tax_payable||0) !== 0 && <ReportRow label="Income Tax Payable (SARS)" value={bs.liabilities.income_tax_payable} indent color={C.red}/>}
            {(bs.liabilities.prov_tax_payable||0) !== 0 && <ReportRow label="Provisional Tax Payable (SARS)" value={bs.liabilities.prov_tax_payable} indent color={C.red}/>}
            {(bs.liabilities.other_liabilities||0) !== 0 && <ReportRow label="Other Liabilities (imported)" value={bs.liabilities.other_liabilities} indent color={C.red}/>}
            <ReportRow label="Total Liabilities"            value={bs.liabilities.total} bold border color={C.red}/>
            <div style={{height:12}}/>
            <div style={{fontSize:11,fontWeight:700,color:C.green,textTransform:"uppercase",letterSpacing:1,marginBottom:12}}>Equity</div>
            <ReportRow label="Retained Income" value={bs.equity.retained_income} color={C.green}/>
            {(bs.equity.opening_balance_equity||0) !== 0 && <ReportRow label="Opening Balance Equity (imported)" value={bs.equity.opening_balance_equity} color={C.green}/>}
            {(bs.equity.imported_retained_earnings||0) !== 0 && <ReportRow label="Retained Earnings (imported)" value={bs.equity.imported_retained_earnings} color={C.green}/>}
            {(bs.equity.other_equity||0) !== 0 && <ReportRow label="Other Equity" value={bs.equity.other_equity} color={C.green}/>}
            <ReportRow label="Total Equity"    value={bs.equity.total} bold color={C.green}/>
            <div style={{height:12}}/>
            <ReportRow label="Total Liabilities and Equity" value={bs.total_liabilities_and_equity} bold border large color={balanced?C.ink:C.red}/>
          </div>
        </div>
      </div>
    );
  };

  const renderReconciliation = () => {
    if (!recon) return <div style={{color:C.inkMid,fontSize:13,padding:20}}>Loading reconciliation…</div>;
    const { summary, checks } = recon;
    const statusColor = s => s==="pass"?C.green:s==="warn"?C.gold:C.red;
    const statusBg    = s => s==="pass"?C.greenLt:s==="warn"?"#FFFBEB":"#FFF3F3";
    const statusIcon  = s => s==="pass"?"✅":s==="warn"?"⚠️":"❌";
    return (
      <div>
        <div style={{fontFamily:"serif",fontSize:20,fontWeight:800,color:C.ink,marginBottom:4}}>Reconciliation Report</div>
        <div style={{fontSize:12,color:C.inkMid,marginBottom:20}}>As at {recon.date}</div>

        {/* Summary pills */}
        <div style={{display:"flex",gap:12,marginBottom:24}}>
          <div style={{background:C.greenLt,borderRadius:10,padding:"10px 18px",textAlign:"center"}}>
            <div style={{fontSize:22,fontWeight:800,color:C.green}}>{summary.passed}</div>
            <div style={{fontSize:11,color:C.green,fontWeight:600}}>PASSED</div>
          </div>
          <div style={{background:"#FFFBEB",borderRadius:10,padding:"10px 18px",textAlign:"center"}}>
            <div style={{fontSize:22,fontWeight:800,color:C.gold}}>{summary.warned}</div>
            <div style={{fontSize:11,color:C.gold,fontWeight:600}}>WARNINGS</div>
          </div>
          <div style={{background:"#FFF3F3",borderRadius:10,padding:"10px 18px",textAlign:"center"}}>
            <div style={{fontSize:22,fontWeight:800,color:C.red}}>{summary.failed}</div>
            <div style={{fontSize:11,color:C.red,fontWeight:600}}>FAILED</div>
          </div>
          <div style={{background:C.surface,borderRadius:10,padding:"10px 18px",textAlign:"center",border:`1px solid ${C.border}`}}>
            <div style={{fontSize:22,fontWeight:800,color:C.ink}}>{summary.total}</div>
            <div style={{fontSize:11,color:C.inkMid,fontWeight:600}}>TOTAL CHECKS</div>
          </div>
        </div>

        {/* Per-check cards */}
        {checks.map((check, i) => (
          <div key={i} style={{background:statusBg(check.status),border:`1px solid ${statusColor(check.status)}40`,borderLeft:`4px solid ${statusColor(check.status)}`,borderRadius:10,padding:"14px 16px",marginBottom:12}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12}}>
              <div style={{flex:1}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                  <span style={{fontSize:16}}>{statusIcon(check.status)}</span>
                  <span style={{fontSize:14,fontWeight:700,color:C.ink}}>{check.rule}</span>
                  <span style={{fontSize:11,fontWeight:700,color:statusColor(check.status),background:`${statusColor(check.status)}20`,padding:"2px 8px",borderRadius:20,textTransform:"uppercase"}}>
                    {check.status}
                  </span>
                </div>
                <div style={{fontSize:13,color:C.inkMid,lineHeight:1.5}}>{check.detail}</div>
              </div>
              {check.amount !== null && check.amount !== undefined &&
                <div style={{textAlign:"right",flexShrink:0}}>
                  <div style={{fontSize:16,fontWeight:700,color:statusColor(check.status)}}>
                    R {Math.abs(check.amount).toLocaleString("en-ZA",{minimumFractionDigits:2})}
                  </div>
                </div>}
            </div>
            {/* Drill-down items */}
            {check.items && check.items.length > 0 && (
              <div style={{marginTop:10,borderTop:`1px solid ${statusColor(check.status)}30`,paddingTop:10}}>
                {check.items.slice(0,5).map((item,j)=>(
                  <div key={j} style={{display:"flex",justifyContent:"space-between",fontSize:12,color:C.inkMid,padding:"3px 0",borderBottom:`1px solid ${C.border}`}}>
                    <span style={{fontWeight:500}}>{item.id || item.sku || item.name} {item.client?"— "+item.client:""}{item.supplier?"— "+item.supplier:""}</span>
                    <span>{item.amount!=null?"R "+Number(item.amount).toLocaleString("en-ZA",{minimumFractionDigits:2}):""} {item.days?"("+item.days+" days)":""}{item.qty!=null?" qty: "+item.qty:""}</span>
                  </div>
                ))}
                {check.items.length > 5 && <div style={{fontSize:11,color:C.inkMid,marginTop:6}}>…and {check.items.length-5} more</div>}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };
  const renderTrialBalance = () => {
    if (!trialBal) return <div style={{color:C.inkMid,fontSize:13,padding:20}}>Loading trial balance…</div>;
    const TYPE_COLOR = {asset:C.blue,liability:C.red,equity:C.green,revenue:C.green,expense:"#c17d00"};
    const TYPE_ORDER = ["asset","liability","equity","revenue","expense"];
    const grouped = TYPE_ORDER.reduce((acc,t)=>({...acc,[t]:trialBal.accounts.filter(a=>a.type===t)}),[]);
    return (
      <div>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
          <div>
            <div style={{fontFamily:"serif",fontSize:20,fontWeight:800,color:C.ink}}>Trial Balance</div>
            <div style={{fontSize:12,color:C.inkMid,marginTop:4}}>All accounts — debits must equal credits</div>
          </div>
          <div style={{textAlign:"right"}}>
            <div style={{fontSize:11,color:C.inkMid}}>Check</div>
            <div style={{fontSize:15,fontWeight:700,color:trialBal.balanced?C.green:C.red}}>
              {trialBal.balanced ? "✅ Balanced" : `❌ Off by R ${Math.abs(trialBal.imbalance).toLocaleString("en-ZA",{minimumFractionDigits:2})}`}
            </div>
          </div>
        </div>
        {/* ── Fix Account Types (Xero reclassify) ─────────────────────────── */}
        <div style={{marginBottom:16,padding:"12px 16px",background:"#fffbeb",border:"1px solid #f59e0b",borderRadius:10,display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
          <div>
            <div style={{fontSize:13,fontWeight:700,color:"#92400e"}}>⚠️ Imported from Xero?</div>
            <div style={{fontSize:12,color:"#78350f",marginTop:2}}>If accounts appear under the wrong section (e.g. expenses under Revenues), click <strong>Fix Account Types</strong> to reclassify them using Xero's chart-of-accounts codes.</div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
            {reclassResult && (
              <span style={{fontSize:12,color:reclassResult.fixed>0?C.green:C.inkMid,fontWeight:600}}>
                {reclassResult.fixed>0 ? `✅ ${reclassResult.fixed} account(s) reclassified — refresh to see updated figures` : "✅ All accounts already correctly classified"}
              </span>
            )}
            <button
              disabled={reclassLoading}
              onClick={async()=>{
                setReclassLoading(true);
                setReclassResult(null);
                try {
                  const res = await api("/csv-import/reclassify-gl-accounts",{method:"POST"});
                  setReclassResult(res);
                  if (res.fixed>0) {
                    // Reload trial balance to show corrected types
                    const tb = await api("/journal/trial-balance");
                    setTrialBal(tb);
                  }
                } catch(e) {
                  setReclassResult({fixed:0,message:"Error: "+e.message});
                } finally {
                  setReclassLoading(false);
                }
              }}
              style={{background:"#f59e0b",color:"#fff",border:"none",borderRadius:8,padding:"8px 16px",fontSize:12,fontWeight:700,cursor:reclassLoading?"not-allowed":"pointer",fontFamily:"inherit",opacity:reclassLoading?0.6:1,whiteSpace:"nowrap"}}
            >
              {reclassLoading ? "Fixing…" : "Fix Account Types"}
            </button>
          </div>
        </div>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
          <thead>
            <tr style={{background:C.surface}}>
              <th style={{padding:"8px 12px",textAlign:"left",fontWeight:600,color:C.inkMid,border:`1px solid ${C.border}`}}>Code</th>
              <th style={{padding:"8px 12px",textAlign:"left",fontWeight:600,color:C.inkMid,border:`1px solid ${C.border}`}}>Account Name</th>
              <th style={{padding:"8px 12px",textAlign:"right",fontWeight:600,color:C.inkMid,border:`1px solid ${C.border}`}}>Debits</th>
              <th style={{padding:"8px 12px",textAlign:"right",fontWeight:600,color:C.inkMid,border:`1px solid ${C.border}`}}>Credits</th>
              <th style={{padding:"8px 12px",textAlign:"right",fontWeight:600,color:C.inkMid,border:`1px solid ${C.border}`}}>Balance</th>
            </tr>
          </thead>
          <tbody>
            {TYPE_ORDER.map(type => {
              const rows = trialBal.accounts.filter(a=>a.type===type);
              if (!rows.length) return null;
              return [
                <tr key={`hdr-${type}`}>
                  <td colSpan={5} style={{padding:"8px 12px",fontWeight:700,fontSize:11,color:TYPE_COLOR[type]||C.ink,background:`${TYPE_COLOR[type]}15`,textTransform:"uppercase",letterSpacing:0.5,border:`1px solid ${C.border}`}}>
                    {type.charAt(0).toUpperCase()+type.slice(1)}s
                  </td>
                </tr>,
                ...rows.map(row=>(
                  <tr key={row.code}>
                    <td style={{padding:"8px 12px",border:`1px solid ${C.border}`,color:C.inkMid,fontFamily:"monospace",fontSize:12}}>{row.code}</td>
                    <td style={{padding:"8px 12px",border:`1px solid ${C.border}`,color:C.ink}}>{row.name}</td>
                    <td style={{padding:"8px 12px",border:`1px solid ${C.border}`,textAlign:"right",color:C.ink}}>{row.total_debits?`R ${row.total_debits.toLocaleString("en-ZA",{minimumFractionDigits:2})}`:"—"}</td>
                    <td style={{padding:"8px 12px",border:`1px solid ${C.border}`,textAlign:"right",color:C.ink}}>{row.total_credits?`R ${row.total_credits.toLocaleString("en-ZA",{minimumFractionDigits:2})}`:"—"}</td>
                    <td style={{padding:"8px 12px",border:`1px solid ${C.border}`,textAlign:"right",fontWeight:600,color:row.balance>=0?C.ink:C.red}}>R {Math.abs(row.balance).toLocaleString("en-ZA",{minimumFractionDigits:2})}</td>
                  </tr>
                )),
              ];
            })}
            <tr style={{background:C.surface,fontWeight:700}}>
              <td colSpan={2} style={{padding:"10px 12px",border:`1px solid ${C.border}`}}>TOTALS</td>
              <td style={{padding:"10px 12px",border:`1px solid ${C.border}`,textAlign:"right",color:C.blue}}>R {trialBal.total_debits.toLocaleString("en-ZA",{minimumFractionDigits:2})}</td>
              <td style={{padding:"10px 12px",border:`1px solid ${C.border}`,textAlign:"right",color:C.red}}>R {trialBal.total_credits.toLocaleString("en-ZA",{minimumFractionDigits:2})}</td>
              <td style={{padding:"10px 12px",border:`1px solid ${C.border}`,textAlign:"right",color:trialBal.balanced?C.green:C.red}}>{trialBal.balanced?"✅ Balanced":`Off by R ${Math.abs(trialBal.imbalance).toLocaleString("en-ZA",{minimumFractionDigits:2})}`}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  const renderJournal = () => {
    if (!journal) return <div style={{color:C.red,fontSize:13,padding:20}}>⚠️ Could not load journal entries — the server returned an error. Try refreshing the page. If the problem persists, please contact support.</div>;
    const SOURCE_ICON = {invoice:"🧾",invoice_payment:"💰",invoice_reversal:"↩️",expense:"💳",expense_reversal:"↩️",payroll:"👥",purchase_order:"📦",po_payment:"🏭",purchase_order_reversal:"↩️",po_payment_reversal:"↩️",manual:"✏️",manual_reversal:"↩️"};
    const toggle = id => setJnlExpanded(prev=>{const n=new Set(prev);n.has(id)?n.delete(id):n.add(id);return n;});

    // ── Manual entry form helpers ─────────────────────────────────────────────
    const addLine = () => setJnlForm(f=>({...f,lines:[...f.lines,{account_id:"",debit:"",credit:"",description:""}]}));
    const removeLine = i => setJnlForm(f=>({...f,lines:f.lines.filter((_,j)=>j!==i)}));
    const updateLine = (i,field,val) => setJnlForm(f=>{const ls=[...f.lines];ls[i]={...ls[i],[field]:val};return{...f,lines:ls};});

    const submitManual = async () => {
      const lines = jnlForm.lines
        .filter(l=>l.account_id)
        .map(l=>({account_id:parseInt(l.account_id),debit:parseFloat(l.debit)||0,credit:parseFloat(l.credit)||0,description:l.description||undefined}));
      const dr = lines.reduce((s,l)=>s+l.debit,0);
      const cr = lines.reduce((s,l)=>s+l.credit,0);
      if (Math.abs(dr-cr)>0.01) { alert(`Entry is unbalanced: DR R${dr.toFixed(2)} ≠ CR R${cr.toFixed(2)}`); return; }
      if (!jnlForm.description) { alert("Description is required."); return; }
      if (jnlForm.auto_reverse && !jnlForm.reversal_date) { alert("Reversal date is required when auto-reverse is on."); return; }
      try {
        await api("/journal/manual",{method:"POST",body:JSON.stringify({
          date:jnlForm.date, description:jnlForm.description, reference:jnlForm.reference||undefined, lines,
          auto_reverse:jnlForm.auto_reverse, reversal_date:jnlForm.auto_reverse?jnlForm.reversal_date:undefined,
        })});
        setJnlForm(null);
        setJournal(await api(`/journal/?limit=500&${dateParams}`));
      } catch(e) { alert("Failed to save: "+(e.message||e)); }
    };

    return (
      <div>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
          <div>
            <div style={{fontFamily:"serif",fontSize:20,fontWeight:800,color:C.ink}}>General Journal</div>
            <div style={{fontSize:12,color:C.inkMid,marginTop:4}}>Every transaction as debit/credit pairs — click any entry to expand</div>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={()=>setJnlForm(jnlForm?null:{...JNL_EMPTY_FORM,date:new Date().toISOString().slice(0,10)})}
              style={{background:jnlForm?C.surface:C.accent,color:jnlForm?C.inkMid:"#fff",border:`1px solid ${jnlForm?C.border:C.accent}`,borderRadius:8,padding:"7px 14px",fontSize:12,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>
              {jnlForm ? "✕ Cancel" : "+ New Entry"}
            </button>
            <button onClick={async()=>{setLoading(true);try{await api("/journal/backfill",{method:"POST"});setJournal(await api(`/journal/?limit=500&${dateParams}`));}catch(e){alert("Backfill failed");}finally{setLoading(false);}}}
              style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:8,padding:"7px 14px",fontSize:12,cursor:"pointer",fontFamily:"inherit",color:C.inkMid}}>
              ↻ Re-run backfill
            </button>
          </div>
        </div>

        {/* ── Manual entry form ──────────────────────────────────────────────── */}
        {jnlForm && (
          <div style={{background:C.surface,border:`2px solid ${C.accent}`,borderRadius:14,padding:24,marginBottom:20}}>
            <div style={{fontWeight:700,fontSize:15,color:C.ink,marginBottom:16}}>New Manual Journal Entry</div>

            {/* Header fields */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:16}}>
              <div>
                <div style={{fontSize:11,color:C.inkMid,marginBottom:4,fontWeight:600}}>DATE *</div>
                <input type="date" value={jnlForm.date} onChange={e=>setJnlForm(f=>({...f,date:e.target.value}))}
                  style={{width:"100%",border:`1px solid ${C.border}`,borderRadius:8,padding:"8px 10px",fontSize:13,fontFamily:"inherit"}}/>
              </div>
              <div>
                <div style={{fontSize:11,color:C.inkMid,marginBottom:4,fontWeight:600}}>DESCRIPTION *</div>
                <input placeholder="e.g. Month-end accrual" value={jnlForm.description} onChange={e=>setJnlForm(f=>({...f,description:e.target.value}))}
                  style={{width:"100%",border:`1px solid ${C.border}`,borderRadius:8,padding:"8px 10px",fontSize:13,fontFamily:"inherit"}}/>
              </div>
              <div>
                <div style={{fontSize:11,color:C.inkMid,marginBottom:4,fontWeight:600}}>REFERENCE</div>
                <input placeholder="e.g. JNL-001" value={jnlForm.reference} onChange={e=>setJnlForm(f=>({...f,reference:e.target.value}))}
                  style={{width:"100%",border:`1px solid ${C.border}`,borderRadius:8,padding:"8px 10px",fontSize:13,fontFamily:"inherit"}}/>
              </div>
            </div>

            {/* Auto-reverse toggle */}
            <div style={{background:jnlForm.auto_reverse?"#EEF6FF":"#f9f9f9",border:`1px solid ${jnlForm.auto_reverse?C.blue:C.border}`,borderRadius:10,padding:"12px 16px",marginBottom:16,display:"flex",alignItems:"center",gap:16,flexWrap:"wrap"}}>
              <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",userSelect:"none"}}>
                <input type="checkbox" checked={jnlForm.auto_reverse} onChange={e=>setJnlForm(f=>({...f,auto_reverse:e.target.checked,reversal_date:""}))}
                  style={{width:16,height:16,accentColor:C.blue}}/>
                <span style={{fontSize:13,fontWeight:600,color:jnlForm.auto_reverse?C.blue:C.inkMid}}>
                  🔄 Auto-reverse this entry
                </span>
              </label>
              {jnlForm.auto_reverse && (
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:12,color:C.inkMid}}>Reverse on:</span>
                  <input type="date" value={jnlForm.reversal_date} onChange={e=>setJnlForm(f=>({...f,reversal_date:e.target.value}))}
                    style={{border:`1px solid ${C.blue}`,borderRadius:6,padding:"5px 8px",fontSize:12,fontFamily:"inherit",color:C.ink}}/>
                  <span style={{fontSize:11,color:C.inkMid}}>The mirror entry will be posted automatically on this date.</span>
                </div>
              )}
              {!jnlForm.auto_reverse && (
                <span style={{fontSize:11,color:C.inkMid}}>Use for accruals or prepayments that need to unwind in a future period.</span>
              )}
            </div>

            {/* Line items */}
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:13,marginBottom:12}}>
              <thead>
                <tr style={{background:"#f5f5f5"}}>
                  {["Account","Debit (R)","Credit (R)","Memo",""].map(h=>(
                    <th key={h} style={{padding:"7px 10px",textAlign:"left",fontSize:10,color:C.inkMid,fontWeight:700,letterSpacing:0.5,textTransform:"uppercase",borderBottom:`1px solid ${C.border}`}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {jnlForm.lines.map((line,i)=>(
                  <tr key={i}>
                    <td style={{padding:"5px 6px",width:"40%"}}>
                      <select value={line.account_id} onChange={e=>updateLine(i,"account_id",e.target.value)}
                        style={{width:"100%",border:`1px solid ${C.border}`,borderRadius:6,padding:"6px 8px",fontSize:12,fontFamily:"inherit"}}>
                        <option value="">— select account —</option>
                        {jnlAccounts.map(a=>(
                          <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
                        ))}
                      </select>
                    </td>
                    <td style={{padding:"5px 6px",width:"15%"}}>
                      <input type="number" min="0" step="0.01" placeholder="0.00" value={line.debit}
                        onChange={e=>updateLine(i,"debit",e.target.value)}
                        style={{width:"100%",border:`1px solid ${C.border}`,borderRadius:6,padding:"6px 8px",fontSize:12,fontFamily:"inherit",textAlign:"right"}}/>
                    </td>
                    <td style={{padding:"5px 6px",width:"15%"}}>
                      <input type="number" min="0" step="0.01" placeholder="0.00" value={line.credit}
                        onChange={e=>updateLine(i,"credit",e.target.value)}
                        style={{width:"100%",border:`1px solid ${C.border}`,borderRadius:6,padding:"6px 8px",fontSize:12,fontFamily:"inherit",textAlign:"right"}}/>
                    </td>
                    <td style={{padding:"5px 6px",width:"25%"}}>
                      <input placeholder="Optional memo" value={line.description}
                        onChange={e=>updateLine(i,"description",e.target.value)}
                        style={{width:"100%",border:`1px solid ${C.border}`,borderRadius:6,padding:"6px 8px",fontSize:12,fontFamily:"inherit"}}/>
                    </td>
                    <td style={{padding:"5px 6px",textAlign:"center"}}>
                      {jnlForm.lines.length > 2 &&
                        <button onClick={()=>removeLine(i)} style={{background:"none",border:"none",color:C.red,cursor:"pointer",fontSize:16,padding:"0 4px"}}>✕</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Running totals + actions */}
            {(()=>{
              const dr=jnlForm.lines.reduce((s,l)=>s+(parseFloat(l.debit)||0),0);
              const cr=jnlForm.lines.reduce((s,l)=>s+(parseFloat(l.credit)||0),0);
              const balanced=Math.abs(dr-cr)<0.01;
              return (
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12}}>
                  <button onClick={addLine}
                    style={{background:"none",border:`1px dashed ${C.border}`,borderRadius:8,padding:"6px 14px",fontSize:12,cursor:"pointer",color:C.inkMid,fontFamily:"inherit"}}>
                    + Add line
                  </button>
                  <div style={{display:"flex",alignItems:"center",gap:16}}>
                    <span style={{fontSize:12,color:balanced?C.green:C.red,fontWeight:600}}>
                      {balanced?"✅ Balanced":`DR R${dr.toFixed(2)} ≠ CR R${cr.toFixed(2)}`}
                    </span>
                    <button onClick={submitManual} disabled={!balanced}
                      style={{background:balanced?C.accent:"#ccc",color:"#fff",border:"none",borderRadius:8,padding:"8px 20px",fontSize:13,cursor:balanced?"pointer":"not-allowed",fontFamily:"inherit",fontWeight:700}}>
                      Post Entry{jnlForm.auto_reverse?" + Schedule Reversal":""}
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {journal.length === 0 && !jnlForm && (
          <div style={{textAlign:"center",padding:"40px 20px",color:C.inkMid}}>
            <div style={{fontSize:32,marginBottom:12}}>📒</div>
            <div style={{fontSize:15,fontWeight:600,marginBottom:6}}>No journal entries yet</div>
            <div style={{fontSize:13}}>Journal entries are created automatically when you add invoices, expenses or process payroll.</div>
          </div>
        )}

        {journal.map(entry => {
          const isReversal = entry.source && entry.source.endsWith("_reversal");
          const hasScheduledReversal = entry.auto_reverse && entry.reversal_date;
          const alreadyReversed = journal.some(e=>e.is_reversal_of===entry.id);
          return (
            <div key={entry.id} style={{border:`1px solid ${isReversal?"#FFC107":entry.is_reversal_of?C.border:C.border}`,borderLeft:`3px solid ${isReversal?C.gold:hasScheduledReversal?C.blue:C.border}`,borderRadius:10,marginBottom:8,overflow:"hidden",opacity:isReversal?0.85:1}}>
              <div onClick={()=>toggle(entry.id)} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 14px",cursor:"pointer",background:jnlExpanded.has(entry.id)?C.accentLt:C.surface}}>
                <span style={{fontSize:18}}>{SOURCE_ICON[entry.source]||"📝"}</span>
                <div style={{flex:1}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                    <span style={{fontSize:13,fontWeight:600,color:C.ink}}>{entry.description}</span>
                    {isReversal && <span style={{fontSize:10,background:"#FFF3CD",color:"#856404",borderRadius:20,padding:"2px 8px",fontWeight:700}}>REVERSAL</span>}
                    {hasScheduledReversal && !alreadyReversed && <span style={{fontSize:10,background:"#EEF6FF",color:C.blue,borderRadius:20,padding:"2px 8px",fontWeight:700}}>🔄 Reverses {entry.reversal_date}</span>}
                    {hasScheduledReversal && alreadyReversed && <span style={{fontSize:10,background:C.greenLt,color:C.green,borderRadius:20,padding:"2px 8px",fontWeight:700}}>✓ Reversed</span>}
                    {entry.is_reversal_of && <span style={{fontSize:10,background:"#f5f5f5",color:C.inkMid,borderRadius:20,padding:"2px 8px"}}>reversal of #{entry.is_reversal_of}</span>}
                  </div>
                  <div style={{fontSize:11,color:C.inkMid,marginTop:2}}>{entry.date} · {entry.reference||"—"} · {entry.source}</div>
                </div>
                <div style={{fontSize:11,color:C.inkMid}}>{jnlExpanded.has(entry.id)?"▲":"▼"}</div>
              </div>
              {jnlExpanded.has(entry.id) && (
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                  <thead>
                    <tr style={{background:"#f8f8f8"}}>
                      <th style={{padding:"6px 14px",textAlign:"left",color:C.inkMid,fontWeight:600,borderTop:`1px solid ${C.border}`}}>Account</th>
                      <th style={{padding:"6px 14px",textAlign:"right",color:C.inkMid,fontWeight:600,borderTop:`1px solid ${C.border}`}}>Debit</th>
                      <th style={{padding:"6px 14px",textAlign:"right",color:C.inkMid,fontWeight:600,borderTop:`1px solid ${C.border}`}}>Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entry.lines.map((line,i)=>(
                      <tr key={i} style={{borderTop:`1px solid ${C.border}`}}>
                        <td style={{padding:"6px 14px",color:C.ink}}><span style={{fontFamily:"monospace",color:C.inkMid,marginRight:8}}>{line.account_code}</span>{line.account_name}{line.description?<span style={{color:C.inkMid}}> — {line.description}</span>:""}</td>
                        <td style={{padding:"6px 14px",textAlign:"right",color:C.blue,fontWeight:line.debit?600:400}}>{line.debit?`R ${line.debit.toLocaleString("en-ZA",{minimumFractionDigits:2})}`:"—"}</td>
                        <td style={{padding:"6px 14px",textAlign:"right",color:C.red,fontWeight:line.credit?600:400}}>{line.credit?`R ${line.credit.toLocaleString("en-ZA",{minimumFractionDigits:2})}`:"—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderCF = () => {
    const cf = cfData || {period,operating:{cash_receipts_from_customers:0,cash_paid_to_suppliers:0,payroll_net_pay:0,sars_paye_uif_sdl:0,net_cash_from_operations:0},investing:{net_cash_from_investing:0},financing:{net_cash_from_financing:0},net_increase_in_cash:0};
    const op = cf.operating;
    return (
      <div>
        <div style={{display:"flex",justifyContent:"space-between",marginBottom:24}}>
          <div><div style={{fontFamily:"serif",fontSize:20,fontWeight:800,color:C.ink}}>Cash Flow Statement</div><div style={{fontSize:12,color:C.inkMid,marginTop:4}}>For the period {cf.period}</div></div>
          <ExcelBtn filename="cash-flow.csv" data={[{Item:"Cash Receipts",Amount:op.cash_receipts_from_customers},{Item:"Cash Paid",Amount:op.cash_paid_to_suppliers},{Item:"Payroll",Amount:op.payroll_net_pay},{Item:"SARS",Amount:op.sars_paye_uif_sdl},{Item:"Net Operating",Amount:op.net_cash_from_operations},{Item:"Net Investing",Amount:cf.investing.net_cash_from_investing},{Item:"Net Financing",Amount:cf.financing.net_cash_from_financing},{Item:"Net Change",Amount:cf.net_increase_in_cash}]}/>
        </div>
        <div style={{fontSize:12,fontWeight:700,color:C.ink,marginBottom:8}}>Operating Activities</div>
        <ReportRow label="Cash receipts from customers"       value={op.cash_receipts_from_customers}  color={C.green} indent/>
        <ReportRow label="Cash paid to suppliers"             value={op.cash_paid_to_suppliers}        indent/>
        <ReportRow label="Payroll net pay"                    value={op.payroll_net_pay}               indent/>
        <ReportRow label="SARS PAYE, UIF and SDL"             value={op.sars_paye_uif_sdl}            indent/>
        <ReportRow label="Net cash from operating activities" value={op.net_cash_from_operations} bold border color={op.net_cash_from_operations>=0?C.green:C.red}/>
        <div style={{fontSize:12,fontWeight:700,color:C.ink,marginTop:16,marginBottom:8}}>Investing Activities</div>
        <ReportRow label="Net cash from investing" value={cf.investing.net_cash_from_investing} bold color={C.inkMid}/>
        <div style={{fontSize:12,fontWeight:700,color:C.ink,marginTop:16,marginBottom:8}}>Financing Activities</div>
        <ReportRow label="Net cash from financing" value={cf.financing.net_cash_from_financing} bold color={C.inkMid}/>
        <ReportRow label="Net increase in cash"    value={cf.net_increase_in_cash} bold border large color={cf.net_increase_in_cash>=0?C.accent:C.red}/>
      </div>
    );
  };
  const openMgmtDrill = async (type) => {
    if (mgmtDrill && mgmtDrill.type === type) { setMgmtDrill(null); return; } // toggle off
    setMgmtDrillLoading(true);
    setMgmtDrill({type, title:"", cols:[], rows:[], total:0, color:C.ink});
    try {
      const m = mgmt;
      const pl = m ? m.pl : null;
      if (type === "revenue") {
        const det = await api(`/reports/management/revenue-detail?${dateParams}`);
        const rows = (det.rows||[]).map(r => [r.invoice_number, r.client, r.status==="partial" ? "Part paid" : "Paid",
          fmt(r.received_zar), fmt(r.vat_zar), fmt(r.revenue_zar), r.paid_date ? new Date(r.paid_date).toLocaleDateString("en-ZA") : "—"]);
        if (det.bank_import_income) rows.push(["Bank import","Income posted from bank statements","","","",fmt(det.bank_import_income),""]);
        setMgmtDrill({type, title:"Revenue — cash received, excluding VAT (period)", color:C.green, total: det.total,
          cols:["Invoice","Client","Status","Received","VAT","Revenue","Paid Date"], rows});
      } else if (type === "profit") {
        // Use management accounts P&L breakdown
        const bd = pl ? (pl.expense_breakdown || {}) : {};
        const cogs = pl ? (pl.po_cogs||0) : 0;
        const rows = [
          ["Revenue", fmt(pl ? pl.revenue : 0), ""],
          ["Cost of Sales (POs)", fmt(cogs), "deduct"],
          ["Gross Profit", fmt(pl ? pl.revenue - cogs : 0), "subtotal"],
          ...Object.entries(bd).filter(([k])=>k!=="Cost of Sales (POs)").sort((a,b)=>b[1]-a[1])
            .map(([k,v])=>[k, fmt(v), "deduct"]),
          ["Total Operating Expenses", fmt(Object.entries(bd).filter(([k])=>k!=="Cost of Sales (POs)").reduce((s,[,v])=>s+v,0)), "subtotal"],
          ["Payroll Cost", fmt(pl ? pl.payroll_cost : 0), "deduct"],
          ["Net Profit Before Tax", fmt(pl ? pl.net_profit + (pl.tax_provision||0) : 0), "subtotal"],
          ["Tax Provision (27%)", fmt(pl ? pl.tax_provision : 0), "deduct"],
          ["Net Profit After Tax", fmt(pl ? pl.net_profit : 0), "total"],
        ];
        setMgmtDrill({type, title:"Net Profit — P&L Breakdown (period)", color:pl&&pl.net_profit>=0?C.accent:C.red,
          total: pl ? pl.net_profit : 0,
          cols:["Line Item","Amount",""],
          rows});
      } else if (type === "outstanding") {
        const invs = await api("/invoices");
        const today = new Date();
        const rows = invs.filter(i=>["sent","overdue","partial"].includes(i.status))
          .sort((a,b)=>new Date(a.due_date||0)-new Date(b.due_date||0));
        setMgmtDrill({type, title:"Outstanding — Pending & Overdue Invoices", color:C.gold,
          total: rows.reduce((s,i)=>s+owedZar(i),0),
          cols:["Invoice","Client","Amount (ZAR)","Due Date","Status"],
          rows: rows.map(i=>{
            const due = i.due_date ? new Date(i.due_date) : null;
            const overdue = due ? Math.max(0,Math.floor((today-due)/(1000*60*60*24))) : 0;
            return [i.invoice_number||i.id, i.client_name||i.client, fmt(owedZar(i)),
              due ? due.toLocaleDateString("en-ZA") : "—",
              overdue > 0 ? `${overdue}d overdue` : i.status];
          })});
      } else if (type === "payroll") {
        const emps = await api("/employees");
        const active = emps.filter(e=>e.is_active!==false);
        // audit fix 2026-09-16 — see [[project_nbcpss_208hr_bug]]
        const calcRow2 = e => calcPayroll(e.gross_salary, null, 0,0,0,null, 0,0,0,0,0,0,0, {isSecurity: !!e.security_grade});
        setMgmtDrill({type, title:"Payroll — Active Employees", color:C.blue,
          total: pl ? pl.payroll_cost : active.reduce((s,e)=>s+calcRow2(e).totalCost,0),
          cols:["Employee","Position","Gross Salary","Net Pay","Total Cost"],
          rows: active.map(e=>{
            const p = calcRow2(e);
            return [e.name, e.position||"—", fmt(e.gross_salary), fmt(p.netPay), fmt(p.totalCost)];
          })});
      }
    } catch(err) { setMgmtDrill(null); }
    setMgmtDrillLoading(false);
  };

  const renderMgmt = () => {
    const m    = mgmt;
    const pl   = m ? m.pl : {revenue:totalRevenue,total_expenses:totalExpenses,gross_profit:grossProfit,payroll_cost:totalPayroll,ebit:netProfit,tax_provision:taxProvision,net_profit:netAfterTax,gross_margin_pct:totalRevenue?Math.round(grossProfit/totalRevenue*100):0,net_margin_pct:totalRevenue?Math.round(netAfterTax/totalRevenue*100):0,expense_breakdown:{}};
    const kpis = m ? m.kpis : {revenue:totalRevenue,net_profit:netAfterTax,outstanding:0,overdue_count:0,employee_count:MOCK_EMPLOYEES.length,payroll_cost:totalPayroll,gross_margin_pct:0,net_margin_pct:0};
    const trend = m ? m.trend : live.trend || REVENUE_DATA;
    const expBreakdown = pl.expense_breakdown || {};
    return (
      <div>
        {m && <div style={{fontSize:11,color:C.inkMid,marginBottom:10,fontWeight:500}}>Period: {m.period}</div>}
        <div style={{display:"flex",gap:12,marginBottom:mgmtDrill?12:20,flexWrap:"wrap"}}>
          <KPI label="Revenue"     value={fmt(kpis.revenue)}      color={C.green}                           icon="💰" active={mgmtDrill?.type==="revenue"}     onClick={()=>openMgmtDrill("revenue")}     sub={"Gross margin "+kpis.gross_margin_pct+"%"}/>
          <KPI label="Net Profit"  value={fmt(kpis.net_profit)}   color={kpis.net_profit>=0?C.accent:C.red} icon="📊" active={mgmtDrill?.type==="profit"}      onClick={()=>openMgmtDrill("profit")}      sub={"Net margin "+kpis.net_margin_pct+"%"}/>
          <KPI label="Outstanding" value={fmt(kpis.outstanding)}  color={C.gold}                            icon="⏳" active={mgmtDrill?.type==="outstanding"} onClick={()=>openMgmtDrill("outstanding")} sub={kpis.overdue_count+" overdue"}/>
          <KPI label="Payroll"     value={fmt(kpis.payroll_cost)} color={C.blue}                            icon="👥" active={mgmtDrill?.type==="payroll"}     onClick={()=>openMgmtDrill("payroll")}     sub={kpis.employee_count+" employees"}/>
        </div>

        {/* Drill-down panel */}
        {(mgmtDrill || mgmtDrillLoading) && (
          <div style={{background:C.surface,border:"2px solid "+(mgmtDrill?.color||C.accent)+"40",borderRadius:16,padding:20,marginBottom:20}}>
            {mgmtDrillLoading ? (
              <div style={{textAlign:"center",padding:24,color:C.inkMid,fontSize:13}}>Loading…</div>
            ) : mgmtDrill && (
              <>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
                  <div style={{fontSize:13,fontWeight:700,color:mgmtDrill.color}}>{mgmtDrill.title}</div>
                  <div style={{display:"flex",alignItems:"center",gap:12}}>
                    {mgmtDrill.total !== undefined && mgmtDrill.type !== "profit" && (
                      <div style={{fontSize:15,fontWeight:800,color:mgmtDrill.color}}>{fmt(mgmtDrill.total)}</div>
                    )}
                    <button onClick={()=>setMgmtDrill(null)} style={{background:"none",border:"none",fontSize:18,cursor:"pointer",color:C.inkMid,lineHeight:1}}>✕</button>
                  </div>
                </div>
                <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                    <thead>
                      <tr style={{background:C.bg}}>
                        {mgmtDrill.cols.map((c,i)=>(
                          <th key={i} style={{padding:"8px 12px",textAlign:"left",fontSize:10,color:C.inkMid,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase",whiteSpace:"nowrap",borderBottom:"1px solid "+C.border}}>{c}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {mgmtDrill.rows.length === 0
                        ? <tr><td colSpan={mgmtDrill.cols.length} style={{padding:"20px 12px",textAlign:"center",color:C.inkMid}}>No data for this period</td></tr>
                        : mgmtDrill.rows.map((row,ri)=>{
                            const tag = Array.isArray(row) ? row[row.length-1] : "";
                            const isCols = !["deduct","subtotal","total",""].includes(tag) || mgmtDrill.type !== "profit";
                            const displayRow = mgmtDrill.type === "profit" ? row.slice(0,-1) : row;
                            const bg = tag==="total" ? C.accentLt : tag==="subtotal" ? C.bg : "transparent";
                            const fw = (tag==="total"||tag==="subtotal") ? 700 : 400;
                            return (
                              <tr key={ri} style={{borderBottom:"1px solid "+C.border+"20",background:bg}}>
                                {displayRow.map((cell,ci)=>(
                                  <td key={ci} style={{padding:"10px 12px",color:ci===1&&tag==="deduct"?C.red:C.ink,fontWeight:ci===0?fw:ci===1?fw:400}}>{cell}</td>
                                ))}
                              </tr>
                            );
                          })
                      }
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        <div style={{background:C.surface,border:"1px solid "+C.border,borderRadius:16,padding:20,marginBottom:20}}>
          <div style={{fontSize:13,fontWeight:700,color:C.ink,marginBottom:16}}>6-Month Revenue vs Expenses</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={trend} barGap={4}>
              <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false}/>
              <XAxis dataKey="month" tick={{fontSize:10,fill:C.inkMid}} tickLine={false} axisLine={false}/>
              <YAxis tick={{fontSize:10,fill:C.inkMid}} tickLine={false} axisLine={false} width={45}/>
              <Tooltip content={<Tooltip2/>}/>
              <Bar dataKey="revenue"  name="Revenue"  fill={C.green}  radius={[4,4,0,0]}/>
              <Bar dataKey="expenses" name="Expenses" fill={C.accent} radius={[4,4,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
          <div style={{background:C.surface,border:"1px solid "+C.border,borderRadius:16,padding:20}}>
            <div style={{fontSize:13,fontWeight:700,color:C.ink,marginBottom:16}}>Profit and Loss</div>
            <ReportRow label="Revenue"              value={pl.revenue}         bold color={C.green}/>
            <ReportRow label="Operating Expenses"  value={-pl.total_expenses} indent/>
            <ReportRow label="Operating Profit"    value={pl.gross_profit}    bold border/>
            <ReportRow label="Payroll Cost"        value={-pl.payroll_cost}   indent/>
            <ReportRow label="Net Profit Before Tax" value={pl.ebit}          bold border color={pl.ebit>=0?C.ink:C.red}/>
            <ReportRow label="Tax Provision 27%"   value={-pl.tax_provision}  indent/>
            <ReportRow label="Net Profit After Tax" value={pl.net_profit}     bold border large color={pl.net_profit>=0?C.accent:C.red}/>
          </div>
          <div style={{background:C.surface,border:"1px solid "+C.border,borderRadius:16,padding:20}}>
            <div style={{fontSize:13,fontWeight:700,color:C.ink,marginBottom:16}}>Expense Breakdown</div>
            {Object.keys(expBreakdown).length > 0
              ? Object.entries(expBreakdown).sort((a,b)=>b[1]-a[1]).map(([cat,amt])=>(
                  <div key={cat} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:"1px solid "+C.border+"20"}}>
                    <span style={{fontSize:12,color:C.ink}}>{cat}</span>
                    <span style={{fontSize:12,fontWeight:700,color:C.red}}>{fmt(amt)}</span>
                  </div>
                ))
              : <div style={{fontSize:12,color:C.inkDim,padding:"20px 0",textAlign:"center"}}>No expense data for this period</div>
            }
          </div>
        </div>

        {/* AI Insights Panel */}
        {canAccess(user, "business") ? (
          <div style={{background:C.surface,border:"1px solid "+C.accent+"40",borderRadius:16,padding:20,marginTop:20}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:aiInsights||aiLoading?16:0}}>
              <div>
                <div style={{fontSize:13,fontWeight:700,color:C.ink}}>✨ AI Insights</div>
                {!aiInsights && !aiLoading && <div style={{fontSize:11,color:C.inkMid,marginTop:4}}>Analyse your revenue, margins, payroll, and cash flow.</div>}
              </div>
              <button
                onClick={async()=>{
                  setAiLoading(true); setAiError(null); setAiInsights(null);
                  try {
                    const res = await api("/reports/ai-insights",{method:"POST",body:JSON.stringify({
                      period: m ? m.period : "",
                      revenue: kpis.revenue,
                      total_expenses: pl.total_expenses,
                      gross_profit: pl.gross_profit,
                      payroll_cost: kpis.payroll_cost,
                      net_profit: kpis.net_profit,
                      gross_margin_pct: kpis.gross_margin_pct,
                      net_margin_pct: kpis.net_margin_pct,
                      outstanding: kpis.outstanding,
                      overdue_count: kpis.overdue_count,
                      employee_count: kpis.employee_count,
                      expense_breakdown: expBreakdown,
                      trend
                    })});
                    setAiInsights(res);
                  } catch(e){ setAiError(e.message||"Failed to generate insights"); }
                  finally { setAiLoading(false); }
                }}
                disabled={aiLoading}
                style={{background:C.accent,color:"#fff",border:"none",borderRadius:8,padding:"8px 18px",fontSize:12,fontWeight:600,cursor:aiLoading?"wait":"pointer",opacity:aiLoading?0.7:1,whiteSpace:"nowrap",flexShrink:0}}
              >{aiLoading?"Analysing…":aiInsights?"Regenerate":"Generate Insights"}</button>
            </div>
            {aiError && <div style={{fontSize:12,color:C.red,marginTop:8}}>{aiError}</div>}
            {aiInsights && (
              <div>
                <div style={{fontSize:11,color:C.inkMid,marginBottom:12}}>Period: {aiInsights.period}</div>
                {(aiInsights.insights||"").split("\n").filter(l=>l.trim()).map((line,i)=>{
                  const match = line.match(/^\*\*(.+?)\*\*\s*(.*)/);
                  return (
                    <div key={i} style={{display:"flex",gap:12,padding:"12px 0",borderBottom:"1px solid "+C.border+"30"}}>
                      {match ? (
                        <>
                          <div style={{minWidth:100,fontSize:11,fontWeight:700,color:C.accent,paddingTop:2,flexShrink:0}}>{match[1]}</div>
                          <div style={{fontSize:12,color:C.ink,lineHeight:1.65}}>{match[2]}</div>
                        </>
                      ) : (
                        <div style={{fontSize:12,color:C.ink,lineHeight:1.65}}>{line.replace(/\*\*/g,"")}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div style={{background:C.surface,border:"1px dashed "+C.border,borderRadius:16,padding:20,marginTop:20}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
              <div style={{fontSize:13,fontWeight:700,color:C.ink}}>✨ AI Insights</div>
              <span style={{fontSize:10,background:C.accentLt,color:C.accent,borderRadius:6,padding:"2px 8px",fontWeight:700,letterSpacing:0.3}}>BUSINESS</span>
            </div>
            <div style={{fontSize:12,color:C.inkMid,marginBottom:16,lineHeight:1.65}}>AI Insights analyse your revenue, margins, payroll efficiency, and cash flow to surface actionable recommendations.</div>
            <div style={{background:C.bg,borderRadius:10,padding:16,marginBottom:16,filter:"blur(3px)",pointerEvents:"none",userSelect:"none"}}>
              {[["Revenue","Strong month with 12% YoY growth. Consider reviewing pricing on core services to improve margin."],["Payroll","Payroll at 38% of revenue — within healthy range for service businesses. Monitor if headcount grows."],["Cash Flow","3 invoices overdue >30 days totalling R24 500. Follow up promptly to protect working capital."],["Expenses","Admin costs rising 8% MoM. Review subscriptions and recurring charges to identify savings."]].map(([cat,text],i)=>(
                <div key={i} style={{display:"flex",gap:12,padding:"10px 0",borderBottom:"1px solid "+C.border+"30"}}>
                  <div style={{minWidth:100,fontSize:11,fontWeight:700,color:C.accent,flexShrink:0}}>{cat}</div>
                  <div style={{fontSize:12,color:C.ink}}>{text}</div>
                </div>
              ))}
            </div>
            <UpgradeWall requiredPlan="business" onNavigateSettings={undefined}/>
          </div>
        )}
      </div>
    );
  };
  const renderVat201 = () => {
    const v = vat201;
    const now = new Date();
    const out  = v ? v.output : {field_1a_standard_supplies_excl_vat:totalRevenue*100/115,field_4a_output_vat:totalRevenue*15/115,invoice_count:0,total_invoiced_incl_vat:totalRevenue,vat_recorded_on_invoices:0,field_1b_zero_rated_supplies:0};
    const inp  = v ? v.input  : {field_14_input_vat:totalExpenses*15/115,total_expenses_incl_vat:totalExpenses,total_expenses_excl_vat:totalExpenses*100/115,expense_count:0,expense_breakdown:{},note:""};
    const net  = v ? v.net    : {field_15_net_vat:(totalRevenue-totalExpenses)*15/115,status:(totalRevenue>totalExpenses?"payable":"refundable"),amount:Math.abs((totalRevenue-totalExpenses)*15/115)};
    const periodLabel = v ? v.period : now.toLocaleDateString("en-ZA",{month:"long",year:"numeric"});
    const dueDate     = v ? v.due_date : "";
    const isPayable   = net.status === "payable";
    return (
      <div>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20}}>
          <div>
            <h2 style={{fontFamily:"serif",fontSize:22,color:C.ink,margin:0}}>VAT201 Return</h2>
            <p style={{fontSize:12,color:C.inkMid,marginTop:3}}>VAT @ 15% — Period: {periodLabel}{dueDate?" — Due: ":""}<strong style={{color:C.red}}>{dueDate}</strong></p>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <span style={{fontSize:12,color:C.inkMid,fontWeight:600}}>Period:</span>
              <input type="month" value={vatPeriod} onChange={e=>{setVatPeriod(e.target.value);setVat201(null);}} style={{padding:"7px 12px",border:"1px solid "+C.border,borderRadius:8,fontSize:12,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}/>
              <select value={vatMonths} onChange={e=>{setVatMonths(+e.target.value);setVat201(null);}} title="Monthly, or bi-monthly (this month and the next)" style={{padding:"7px 8px",border:"1px solid "+C.border,borderRadius:8,fontSize:12,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}><option value={1}>Monthly</option><option value={2}>Bi-monthly</option></select>
              <button onClick={()=>api("/reports/vat201?period="+vatPeriod+"&months="+vatMonths).then(setVat201).catch(()=>null)} style={{background:C.accent,color:"#fff",border:"none",borderRadius:8,padding:"8px 16px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Load</button>
            </div>
            <a href="https://efiling.sars.gov.za" target="_blank" rel="noreferrer" style={{background:C.blue,color:"#fff",padding:"8px 14px",borderRadius:8,fontSize:12,fontWeight:700,textDecoration:"none"}}>Submit on eFiling</a>
          </div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:24}}>
          <div style={{background:C.greenLt,border:"1px solid "+C.green+"30",borderRadius:14,padding:20}}>
            <div style={{fontSize:10,color:C.inkMid,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8}}>Output VAT (Field 4A)</div>
            <div style={{fontFamily:"serif",fontSize:28,fontWeight:800,color:C.green}}>{fmt(out.field_4a_output_vat)}</div>
            <div style={{fontSize:11,color:C.inkMid,marginTop:4}}>VAT collected on {out.invoice_count} invoice{out.invoice_count!==1?"s":""}</div>
          </div>
          <div style={{background:C.accentLt,border:"1px solid "+C.accent+"30",borderRadius:14,padding:20}}>
            <div style={{fontSize:10,color:C.inkMid,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8}}>Input VAT (Field 14)</div>
            <div style={{fontFamily:"serif",fontSize:28,fontWeight:800,color:C.accent}}>{fmt(inp.field_14_input_vat)}</div>
            <div style={{fontSize:11,color:C.inkMid,marginTop:4}}>VAT on {inp.expense_count} expense{inp.expense_count!==1?"s":""} (est.)</div>
          </div>
          <div style={{background:isPayable?C.redLt:C.greenLt,border:"1px solid "+(isPayable?C.red:C.green)+"30",borderRadius:14,padding:20}}>
            <div style={{fontSize:10,color:C.inkMid,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8}}>Net VAT (Field 15)</div>
            <div style={{fontFamily:"serif",fontSize:28,fontWeight:800,color:isPayable?C.red:C.green}}>{fmt(net.amount)}</div>
            <div style={{fontSize:11,color:isPayable?C.red:C.green,marginTop:4,fontWeight:700}}>{isPayable?"Payable to SARS":"Refund due from SARS"}</div>
          </div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:20}}>
          <div style={{background:C.surface,border:"1px solid "+C.border,borderRadius:16,padding:24}}>
            <div style={{fontSize:13,fontWeight:700,color:C.green,marginBottom:16}}>Section A — Output Tax</div>
            <div style={{display:"flex",justifyContent:"space-between",padding:"10px 0",borderBottom:"1px solid "+C.border+"20",fontSize:13}}>
              <span style={{color:C.inkMid}}>Field 1(a) Standard rated supplies (excl. VAT)</span>
              <span style={{fontWeight:700}}>{fmt(out.field_1a_standard_supplies_excl_vat)}</span>
            </div>
            <div style={{display:"flex",justifyContent:"space-between",padding:"10px 0",borderBottom:"1px solid "+C.border+"20",fontSize:13}}>
              <span style={{color:C.inkMid}}>Field 1(b) Zero-rated supplies</span>
              <span style={{fontWeight:700}}>{fmt(out.field_1b_zero_rated_supplies)}</span>
            </div>
            {(out.field_3_exempt_supplies||0)>0.005 && <div style={{display:"flex",justifyContent:"space-between",padding:"10px 0",borderBottom:"1px solid "+C.border+"20",fontSize:13}}>
              <span style={{color:C.inkMid}}>Field 3 Exempt supplies (excl. VAT)</span>
              <span style={{fontWeight:700}}>{fmt((out.field_3_exempt_supplies||0))}</span>
            </div>}
            {(out.credit_notes_vat_deducted||0)>0.005 && <div style={{display:"flex",justifyContent:"space-between",padding:"10px 0",borderBottom:"1px solid "+C.border+"20",fontSize:13}}>
              <span style={{color:C.inkMid}}>Less: credit notes (VAT deducted)</span>
              <span style={{fontWeight:700}}>{fmt((out.credit_notes_vat_deducted||0))}</span>
            </div>}
            {(out.bank_import_output_vat||0)>0.005 && <div style={{display:"flex",justifyContent:"space-between",padding:"10px 0",borderBottom:"1px solid "+C.border+"20",fontSize:13}}>
              <span style={{color:C.inkMid}}>Add: VAT on bank-import income</span>
              <span style={{fontWeight:700}}>{fmt((out.bank_import_output_vat||0))}</span>
            </div>}
            {(out.bad_debt_recovered_vat||0)>0.005 && <div style={{display:"flex",justifyContent:"space-between",padding:"10px 0",borderBottom:"1px solid "+C.border+"20",fontSize:13}}>
              <span style={{color:C.inkMid}}>Add: VAT on bad debts recovered (s22)</span>
              <span style={{fontWeight:700}}>{fmt((out.bad_debt_recovered_vat||0))}</span>
            </div>}
            <div style={{display:"flex",justifyContent:"space-between",padding:"10px 0",borderBottom:"2px solid "+C.border,fontSize:13}}>
              <span style={{color:C.inkMid}}>Total invoiced (incl. VAT)</span>
              <span style={{fontWeight:700}}>{fmt(out.total_invoiced_incl_vat)}</span>
            </div>
            <div style={{display:"flex",justifyContent:"space-between",padding:"12px 0",fontSize:14,fontWeight:800}}>
              <span style={{color:C.green}}>Field 4(a) Output VAT</span>
              <span style={{color:C.green}}>{fmt(out.field_4a_output_vat)}</span>
            </div>
          </div>
          <div style={{background:C.surface,border:"1px solid "+C.border,borderRadius:16,padding:24}}>
            <div style={{fontSize:13,fontWeight:700,color:C.accent,marginBottom:16}}>Section B — Input Tax</div>
            <div style={{display:"flex",justifyContent:"space-between",padding:"10px 0",borderBottom:"1px solid "+C.border+"20",fontSize:13}}>
              <span style={{color:C.inkMid}}>Total expenses (incl. VAT)</span>
              <span style={{fontWeight:700}}>{fmt(inp.total_expenses_incl_vat)}</span>
            </div>
            <div style={{display:"flex",justifyContent:"space-between",padding:"10px 0",borderBottom:"1px solid "+C.border+"20",fontSize:13}}>
              <span style={{color:C.inkMid}}>Total expenses (excl. VAT)</span>
              <span style={{fontWeight:700}}>{fmt(inp.total_expenses_excl_vat)}</span>
            </div>
            {(inp.input_vat_before_apportionment||0)>0.005 && <div style={{display:"flex",justifyContent:"space-between",padding:"10px 0",borderBottom:"1px solid "+C.border+"20",fontSize:13}}>
              <span style={{color:C.inkMid}}>Input VAT before apportionment</span>
              <span style={{fontWeight:700}}>{fmt((inp.input_vat_before_apportionment||0))}</span>
            </div>}
            {(inp.apportionment&&inp.apportionment.non_deductible_input_vat||0)>0.005 && <div style={{display:"flex",justifyContent:"space-between",padding:"10px 0",borderBottom:"1px solid "+C.border+"20",fontSize:13}}>
              <span style={{color:C.inkMid}}>Less: non-deductible (exempt-supply apportionment)</span>
              <span style={{fontWeight:700}}>{fmt((inp.apportionment&&inp.apportionment.non_deductible_input_vat||0))}</span>
            </div>}
            {(inp.bad_debt_vat_deduction||0)>0.005 && <div style={{display:"flex",justifyContent:"space-between",padding:"10px 0",borderBottom:"1px solid "+C.border+"20",fontSize:13}}>
              <span style={{color:C.inkMid}}>Add: VAT on bad debts written off (s22)</span>
              <span style={{fontWeight:700}}>{fmt((inp.bad_debt_vat_deduction||0))}</span>
            </div>}
            <div style={{display:"flex",justifyContent:"space-between",padding:"10px 0",borderBottom:"2px solid "+C.border,fontSize:13}}>
              <span style={{color:C.inkMid}}>Estimated input VAT (15/115)</span>
              <span style={{fontWeight:700}}>{fmt(inp.field_14_input_vat)}</span>
            </div>
            {inp.apportionment && inp.apportionment.applies && <div style={{fontSize:11,color:C.inkMid,padding:"8px 0"}}>Apportionment ratio {((inp.apportionment.ratio||0)*100).toFixed(2)}% (provisional). {inp.apportionment.note}</div>}
            <div style={{display:"flex",justifyContent:"space-between",padding:"12px 0",fontSize:14,fontWeight:800}}>
              <span style={{color:C.accent}}>Field 14 Input VAT claimed</span>
              <span style={{color:C.accent}}>{fmt(inp.field_14_input_vat)}</span>
            </div>
          </div>
        </div>
        <div style={{background:isPayable?C.redLt:C.greenLt,border:"2px solid "+(isPayable?C.red:C.green),borderRadius:16,padding:24,marginBottom:20}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <div style={{fontSize:14,fontWeight:700,color:isPayable?C.red:C.green,marginBottom:4}}>Field 15 — Net VAT {isPayable?"Payable":"Refundable"}</div>
              <div style={{fontSize:12,color:C.inkMid}}>Output VAT {fmt(out.field_4a_output_vat)} {isPayable?"minus":"minus"} Input VAT {fmt(inp.field_14_input_vat)}</div>
              {dueDate && <div style={{fontSize:12,fontWeight:700,color:isPayable?C.red:C.green,marginTop:8}}>Due date: {dueDate}</div>}
            </div>
            <div style={{fontFamily:"serif",fontSize:36,fontWeight:800,color:isPayable?C.red:C.green}}>{fmt(net.amount)}</div>
          </div>
        </div>
        {Object.keys(inp.expense_breakdown||{}).length > 0 && (
          <div style={{background:C.surface,border:"1px solid "+C.border,borderRadius:16,padding:24,marginBottom:20}}>
            <div style={{fontSize:13,fontWeight:700,color:C.ink,marginBottom:16}}>Expense Breakdown (for VAT audit trail)</div>
            {Object.entries(inp.expense_breakdown).sort((a,b)=>b[1]-a[1]).map(([cat,amt])=>(
              <div key={cat} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:"1px solid "+C.border+"20",fontSize:13}}>
                <span style={{color:C.ink}}>{cat}</span>
                <div style={{textAlign:"right"}}>
                  <span style={{fontWeight:600}}>{fmt(amt)}</span>
                  <span style={{color:C.inkDim,fontSize:11,marginLeft:12}}>VAT est. {fmt(amt*15/115)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
        <div style={{background:C.goldLt,border:"1px solid "+C.gold+"40",borderRadius:12,padding:16,fontSize:12,color:C.inkMid,lineHeight:1.8}}>
          <strong style={{color:C.ink}}>VAT201 Notes</strong><br/>
          Input VAT is estimated at 15/115 of total expenses assuming all are VAT-inclusive. Exclude zero-rated and exempt purchases before submitting.<br/>
          VAT is due by the 25th of the month following the tax period. Late submission incurs a 10% penalty plus interest.<br/>
          Ensure your VAT registration number appears on all tax invoices issued and received.
        </div>
      </div>
    );
  };

  const renderProvTax = () => {
    const p = provTax;
    const now = new Date();
    const ytd   = p ? p.ytd   : {revenue:totalRevenue, expenses:totalExpenses, payroll_cost:totalPayroll, taxable_income:totalRevenue-totalExpenses-totalPayroll};
    const ann   = p ? p.annualised : {taxable_income:Math.max(0,(totalRevenue-totalExpenses-totalPayroll)/now.getMonth()*12), estimated_tax:0};
    const irp6  = p ? p.irp6  : {first_payment:0, first_due:"31 August "+now.getFullYear(), second_payment:0, second_due:"28 February "+(now.getFullYear()+1)};
    const notes = p ? p.notes : [];
    const taxable = ytd.taxable_income;
    const estTax  = ann.estimated_tax || Math.max(0, ann.taxable_income * 0.27);
    return (
      <div>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20}}>
          <div>
            <h2 style={{fontFamily:"serif",fontSize:22,color:C.ink,margin:0}}>Provisional Tax (IRP6)</h2>
            <p style={{fontSize:12,color:C.inkMid,marginTop:3}}>Corporate tax @ 27% — Tax year {p?p.tax_year:now.getFullYear()+"/"+(now.getFullYear()+1)}</p>
          </div>
          <a href="https://efiling.sars.gov.za" target="_blank" rel="noreferrer" style={{background:C.accent,color:"#fff",padding:"8px 16px",borderRadius:8,fontSize:12,fontWeight:700,textDecoration:"none"}}>Submit IRP6 on eFiling</a>
        </div>
        <div style={{display:"flex",gap:12,marginBottom:20,flexWrap:"wrap"}}>
          <KPI label="YTD Revenue"         value={fmt(ytd.revenue)}          color={C.green}  icon="Revenue" sub="Year to date"/>
          <KPI label="YTD Expenses"        value={fmt(ytd.expenses+ytd.payroll_cost)} color={C.red} icon="Expenses" sub="Incl. payroll"/>
          <KPI label="Taxable Income"      value={fmt(Math.max(0,taxable))}  color={taxable>=0?C.ink:C.red} icon="Tax" sub="Est. annual basis"/>
          <KPI label="Estimated Tax (27%)" value={fmt(estTax)}               color={C.accent} icon="SARS" sub="Before deductions"/>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:20}}>
          <div style={{background:C.surface,border:"1px solid "+C.border,borderRadius:16,padding:24}}>
            <div style={{fontSize:13,fontWeight:700,color:C.ink,marginBottom:16}}>YTD Income Statement</div>
            <ReportRow label="Revenue (invoiced + paid)"  value={ytd.revenue}       bold color={C.green}/>
            <ReportRow label="Less: Operating Expenses"   value={-ytd.expenses}     indent/>
            <ReportRow label="Less: Payroll Cost"         value={-ytd.payroll_cost} indent/>
            <ReportRow label="YTD Taxable Income"         value={taxable} bold border color={taxable>=0?C.ink:C.red}/>
            <ReportRow label="Annualised Taxable Income"  value={ann.taxable_income} bold color={C.inkMid}/>
            <ReportRow label="Estimated Annual Tax (27%)" value={estTax} bold border large color={C.accent}/>
          </div>
          <div>
            <div style={{background:C.surface,border:"2px solid "+C.accent,borderRadius:16,padding:24,marginBottom:12}}>
              <div style={{fontSize:11,fontWeight:700,color:C.inkMid,textTransform:"uppercase",letterSpacing:0.5,marginBottom:12}}>First IRP6 Payment</div>
              <div style={{fontFamily:"serif",fontSize:28,fontWeight:800,color:C.accent,marginBottom:6}}>{fmt(irp6.first_payment)}</div>
              <div style={{fontSize:12,color:C.inkMid}}>50% of estimated annual tax</div>
              <div style={{marginTop:12,padding:"8px 12px",background:C.redLt,borderRadius:8,fontSize:12,fontWeight:700,color:C.red}}>Due: {irp6.first_due}</div>
            </div>
            <div style={{background:C.surface,border:"1px solid "+C.border,borderRadius:16,padding:24}}>
              <div style={{fontSize:11,fontWeight:700,color:C.inkMid,textTransform:"uppercase",letterSpacing:0.5,marginBottom:12}}>Second IRP6 Payment</div>
              <div style={{fontFamily:"serif",fontSize:28,fontWeight:800,color:C.ink,marginBottom:6}}>{fmt(irp6.second_payment)}</div>
              <div style={{fontSize:12,color:C.inkMid}}>Balance of estimated annual tax</div>
              <div style={{marginTop:12,padding:"8px 12px",background:C.goldLt,borderRadius:8,fontSize:12,fontWeight:700,color:C.gold}}>Due: {irp6.second_due}</div>
            </div>
          </div>
        </div>
        {notes.length > 0 && (
          <div style={{background:C.bg,border:"1px solid "+C.border,borderRadius:12,padding:16}}>
            <div style={{fontSize:12,fontWeight:700,color:C.ink,marginBottom:10}}>Important Notes</div>
            {notes.map((n,i)=>(
              <div key={i} style={{fontSize:12,color:C.inkMid,marginBottom:6,display:"flex",gap:8}}>
                <span style={{color:C.gold}}>•</span>{n}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:20}}>
        <div>
          <h2 style={{fontFamily:"serif",fontSize:26,color:C.ink,margin:0}}>Reports</h2>
          <p style={{fontSize:12,color:C.inkMid,marginTop:3}}>SARS-ready financial statements — {periodLabel}</p>
        </div>
        <PrintBtn onClick={()=>{const el=document.getElementById("report-print-area");if(!el)return;const w=window.open("","_blank");w.document.write("<html><body>"+el.innerHTML+"</body></html>");w.document.close();w.print();}}/>
      </div>

      {/* Date range selector */}
      <div style={{background:C.surface,border:"1px solid "+C.border,borderRadius:12,padding:"14px 18px",marginBottom:20,display:"flex",gap:12,alignItems:"center",flexWrap:"wrap"}}>
        <span style={{fontSize:11,fontWeight:700,color:C.inkMid,textTransform:"uppercase",letterSpacing:0.5}}>Period</span>
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          {PRESETS.map(p => (
            <button key={p.label} onClick={()=>handlePreset(p.label)}
              style={{padding:"6px 14px",borderRadius:20,border:"1px solid "+(preset===p.label?C.accent:C.border),background:preset===p.label?C.accentLt:"transparent",color:preset===p.label?C.accent:C.inkMid,fontSize:12,fontWeight:preset===p.label?700:400,cursor:"pointer",fontFamily:"inherit"}}>
              {p.label}
            </button>
          ))}
        </div>
        {preset === "Custom" && (
          <div style={{display:"flex",gap:8,alignItems:"center",marginLeft:4}}>
            <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)}
              style={{padding:"6px 10px",border:"1px solid "+C.border,borderRadius:8,fontSize:12,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}/>
            <span style={{fontSize:12,color:C.inkMid}}>to</span>
            <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)}
              style={{padding:"6px 10px",border:"1px solid "+C.border,borderRadius:8,fontSize:12,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}/>
          </div>
        )}
        <div style={{marginLeft:"auto",fontSize:12,color:C.inkMid,fontStyle:"italic"}}>{periodLabel}</div>
      </div>

      <div style={{display:"flex",gap:4,marginBottom:24,background:C.surface,border:"1px solid "+C.border,borderRadius:12,padding:5,width:"fit-content",flexWrap:"wrap"}}>
        {RTABS.map(t => (
          <button key={t.id} onClick={()=>loadTab(t.id)} style={{padding:"8px 16px",borderRadius:8,border:"none",cursor:"pointer",fontSize:12,fontWeight:activeTab===t.id?700:400,background:activeTab===t.id?C.accent:"transparent",color:activeTab===t.id?"#fff":C.inkMid,fontFamily:"inherit",whiteSpace:"nowrap"}}>
            {t.label}
          </button>
        ))}
      </div>
      <div id="report-print-area">
        {activeTab==="pl" && (
          <div style={{background:C.surface,border:"1px solid "+C.border,borderRadius:16,padding:28}}>
            {loading ? <div style={{textAlign:"center",padding:40,color:C.inkMid}}>Loading...</div> : (() => {
              // Use management accounts data (loaded alongside this tab) for full breakdown
              const pl = mgmt ? mgmt.pl : null;
              const plRev     = pl ? pl.revenue          : totalRevenue;
              const plCogs    = pl ? (pl.po_cogs || 0)   : 0;
              // True gross profit = revenue minus cost of sales only
              const plTrueGP  = Math.round((plRev - plCogs)*100)/100;
              // Operating expenses = all non-COGS expenses (incl. depreciation)
              const breakdown = pl ? pl.expense_breakdown : {};
              const opExEntries = Object.entries(breakdown)
                .filter(([k]) => k !== "Cost of Sales (POs)")
                .sort(([a],[b]) => a.localeCompare(b));
              const plTotalOpEx = opExEntries.reduce((s,[,v])=>s+v, 0);
              const plEbit      = Math.round((plTrueGP - plTotalOpEx)*100)/100;
              const plPayroll   = pl ? pl.payroll_cost    : totalPayroll;
              const plNBT       = Math.round((plEbit - plPayroll)*100)/100;
              const plTax       = Math.round(Math.max(0, plNBT * 0.27)*100)/100;
              const plNAT       = Math.round((plNBT - plTax)*100)/100;
              const plGM        = plRev ? Math.round(plTrueGP/plRev*100) : 0;
              const plNM        = plRev ? Math.round(plNAT/plRev*100)   : 0;
              return (
                <>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:24}}>
                    <div>
                      <div style={{fontFamily:"serif",fontSize:20,fontWeight:800,color:C.ink}}>Income Statement</div>
                      <div style={{fontSize:12,color:C.inkMid,marginTop:4}}>{pl ? `For the period: ${mgmt.period}` : `As at ${period}`}</div>
                    </div>
                    <ExcelBtn filename="income-statement.csv" data={[
                      {Item:"Revenue",Amount:plRev},
                      {Item:"Cost of Sales",Amount:-plCogs},
                      {Item:"Gross Profit",Amount:plTrueGP},
                      ...opExEntries.map(([k,v])=>({Item:k,Amount:-v})),
                      {Item:"Total Operating Expenses",Amount:-plTotalOpEx},
                      {Item:"EBIT",Amount:plEbit},
                      {Item:"Payroll",Amount:-plPayroll},
                      {Item:"Net Profit Before Tax",Amount:plNBT},
                      {Item:"Tax 27%",Amount:-plTax},
                      {Item:"Net Profit After Tax",Amount:plNAT},
                    ]}/>
                  </div>

                  {/* Revenue */}
                  <ReportRow label="Revenue" value={plRev} bold color={C.green}/>

                  {/* Cost of Sales — separate section above gross profit */}
                  {plCogs > 0 && (
                    <>
                      <div style={{fontSize:11,fontWeight:700,color:C.inkMid,letterSpacing:0.5,textTransform:"uppercase",padding:"12px 0 4px",borderTop:"1px solid "+C.border+"50"}}>Cost of Sales</div>
                      <ReportRow label="Purchase Orders (received)" value={-plCogs} indent color={C.red}/>
                      <ReportRow label="Total Cost of Sales" value={-plCogs} bold/>
                    </>
                  )}

                  {/* True Gross Profit = Revenue − COGS */}
                  <ReportRow label="Gross Profit" value={plTrueGP} bold border color={plTrueGP>=0?C.ink:C.red}/>

                  {/* Operating Expenses (incl. depreciation as its own line) */}
                  {opExEntries.length > 0 ? (
                    <>
                      <div style={{fontSize:11,fontWeight:700,color:C.inkMid,letterSpacing:0.5,textTransform:"uppercase",padding:"12px 0 4px",borderTop:"1px solid "+C.border+"50"}}>Operating Expenses</div>
                      {opExEntries.map(([account, amount]) => (
                        <ReportRow key={account} label={account} value={-amount} indent color={C.red}/>
                      ))}
                      <ReportRow label="Total Operating Expenses" value={-plTotalOpEx} bold/>
                    </>
                  ) : (
                    plTotalOpEx > 0 && <ReportRow label="Less: Operating Expenses" value={-plTotalOpEx} indent/>
                  )}

                  {/* EBIT */}
                  <ReportRow label="Earnings Before Interest & Tax (EBIT)" value={plEbit} bold border color={plEbit>=0?C.ink:C.red}/>

                  {/* Payroll & Net Profit */}
                  <ReportRow label="Less: Payroll Costs" value={-plPayroll} indent/>
                  <ReportRow label="Net Profit Before Tax" value={plNBT} bold border color={plNBT>=0?C.ink:C.red}/>
                  <ReportRow label="Less: Tax Provision (27%)" value={-plTax} indent/>
                  <ReportRow label="Net Profit After Tax" value={plNAT} bold border large color={plNAT>=0?C.accent:C.red}/>

                  {/* KPI pills */}
                  <div style={{marginTop:24,display:"flex",gap:12}}>
                    {[["Gross Margin",plGM,C.green,C.greenLt],["Net Margin",plNM,C.accent,C.accentLt],["Tax Rate",27,C.gold,C.goldLt]].map(([l,v,c,bg])=>(
                      <div key={l} style={{flex:1,background:bg,border:"1px solid "+c+"30",borderRadius:12,padding:16,textAlign:"center"}}>
                        <div style={{fontSize:10,color:C.inkMid,textTransform:"uppercase",letterSpacing:0.5,marginBottom:6}}>{l}</div>
                        <div style={{fontSize:22,fontWeight:800,color:c}}>{v}%</div>
                      </div>
                    ))}
                  </div>
                </>
              );
            })()}
          </div>
        )}
        {activeTab==="bs"   && <div style={{background:C.surface,border:"1px solid "+C.border,borderRadius:16,padding:28}}>{loading ? <div style={{textAlign:"center",padding:40,color:C.inkMid}}>Loading...</div> : renderBS()}</div>}
        {activeTab==="recon"&& <div style={{background:C.surface,border:"1px solid "+C.border,borderRadius:16,padding:28}}>{loading ? <div style={{textAlign:"center",padding:40,color:C.inkMid}}>Loading...</div> : renderReconciliation()}</div>}
        {activeTab==="tb"   && <div style={{background:C.surface,border:"1px solid "+C.border,borderRadius:16,padding:28}}>{loading ? <div style={{textAlign:"center",padding:40,color:C.inkMid}}>Loading...</div> : renderTrialBalance()}</div>}
        {activeTab==="jnl"  && <div style={{background:C.surface,border:"1px solid "+C.border,borderRadius:16,padding:28}}>{loading ? <div style={{textAlign:"center",padding:40,color:C.inkMid}}>Loading...</div> : renderJournal()}</div>}
        {activeTab==="cf"   && <div style={{background:C.surface,border:"1px solid "+C.border,borderRadius:16,padding:28}}>{loading ? <div style={{textAlign:"center",padding:40,color:C.inkMid}}>Loading...</div> : renderCF()}</div>}
        {activeTab==="mgmt" && <div>{loading ? <div style={{background:C.surface,border:"1px solid "+C.border,borderRadius:16,padding:40,textAlign:"center",color:C.inkMid}}>Loading...</div> : renderMgmt()}</div>}
        {/* EMP201/IRP5 moved to Payroll component */}
        {activeTab==="prov" && <div style={{background:C.surface,border:"1px solid "+C.border,borderRadius:16,padding:28}}>{loading ? <div style={{textAlign:"center",padding:40,color:C.inkMid}}>Loading...</div> : renderProvTax()}</div>}
        {activeTab==="vat"  && <div style={{background:C.surface,border:"1px solid "+C.border,borderRadius:16,padding:28}}>{renderVat201()}</div>}
      </div>
    </div>
  );
}


// ── BUDGETING ─────────────────────────────────────────────────────────────────
function Budgeting({live = {}}) {
  const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const EXPENSE_CATS = ["Cost of Sales","Utilities","Telecoms","Office","Banking","Insurance","Tax","Equipment","Travel","Salaries","Rent","Marketing","Professional Fees","Other"];
  const INCOME_CATS  = ["Revenue"];
  const ALL_CATS     = [...INCOME_CATS, ...EXPENSE_CATS];
  const DEPTS        = ["General","Sales","Operations","Finance","HR","IT","Marketing","Admin"];

  const [view,    setView]    = useState("monthly");   // monthly | annual | cashflow | departments
  const [year,    setYear]    = useState(new Date().getFullYear());
  const [month,   setMonth]   = useState(new Date().getMonth() + 1);
  const [budgets, setBudgets] = useState([]);
  const [actuals, setActuals] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [editCell, setEditCell] = useState(null); // {cat, month, type, dept}
  const [editVal,  setEditVal]  = useState("");
  const [dept,     setDept]     = useState("General");
  const [csvPreview, setCsvPreview] = useState(null); // parsed rows awaiting confirm
  const [csvError,   setCsvError]   = useState(null);
  const csvInputRef = useRef(null);

  // ── 13-WEEK FORECAST STATE ──────────────────────────────────────────────────
  const [forecastData,    setForecastData]    = useState(null);
  const [forecastLoading, setForecastLoading] = useState(false);
  const [openingBalance,  setOpeningBalance]  = useState(0);
  const [fcOverrides,     setFcOverrides]     = useState({});   // {`${wk}-${field}`: number}

  const loadForecast = async (ob) => {
    setForecastLoading(true);
    try {
      const bal = ob !== undefined ? ob : openingBalance;
      const data = await api(`/reports/cash-flow-13week?opening_balance=${bal}`);
      setForecastData(data);
      setFcOverrides({}); // reset manual overrides on refresh
    } catch(e) { console.warn("Forecast load error", e); }
    finally { setForecastLoading(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [b, a, s] = await Promise.all([
        api(`/budgets/?year=${year}`),
        api(`/budgets/actuals?year=${year}`),
        api(`/budgets/summary?year=${year}`),
      ]);
      setBudgets(b); setActuals(a); setSummary(s);
    } catch(e) { console.warn("Budget load error", e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [year]);

  const getBudget = (cat, m, type="expense", d=null) => {
    const b = budgets.find(b => b.category===cat && b.month===m && b.type===type && (d ? b.department===d : true));
    return b ? b.amount : 0;
  };

  const getActual = (cat, m, type="expense") => {
    const a = actuals.find(a => a.category===cat && a.month===m && a.type===type);
    return a ? a.actual : 0;
  };

  const saveCell = async (cat, m, type, amount, d=null) => {
    setSaving(true);
    try {
      await api("/budgets/", {method:"POST", body: JSON.stringify({
        year, month: m, category: cat, amount: parseFloat(amount)||0, type, department: d||null,
      })});
      await load();
    } catch(e) { alert("Save failed: "+(e.message||"")); }
    finally { setSaving(false); setEditCell(null); }
  };

  const fmtR = (n) => `R ${Number(n||0).toLocaleString("en-ZA",{minimumFractionDigits:0,maximumFractionDigits:0})}`;
  const pct  = (actual, budget) => budget > 0 ? Math.min(Math.round((actual/budget)*100), 200) : 0;
  const over = (actual, budget) => budget > 0 && actual > budget;

  // ── CSV IMPORT ──────────────────────────────────────────────────────────────
  const MONTH_NAMES = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];

  const downloadTemplate = () => {
    const header = "category,type,department,jan,feb,mar,apr,may,jun,jul,aug,sep,oct,nov,dec";
    const rows = [
      "Revenue,income,,100000,100000,110000,110000,120000,120000,130000,130000,140000,140000,150000,150000",
      "Salaries,expense,,50000,50000,50000,50000,50000,50000,50000,50000,50000,50000,50000,50000",
      "Rent,expense,,12000,12000,12000,12000,12000,12000,12000,12000,12000,12000,12000,12000",
      "Marketing,expense,Sales,5000,5000,6000,6000,7000,7000,8000,8000,9000,9000,10000,10000",
    ];
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], {type:"text/csv"});
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url; a.download = `zuzan-budget-template-${year}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const parseCsv = (text) => {
    const lines = text.trim().split(/\r?\n/).filter(l => l.trim());
    if (lines.length < 2) throw new Error("CSV must have a header row and at least one data row.");
    const headers = lines[0].split(",").map(h => h.trim().toLowerCase());
    const catIdx  = headers.indexOf("category");
    const typeIdx = headers.indexOf("type");
    const deptIdx = headers.indexOf("department");
    if (catIdx === -1) throw new Error("Missing required column: category");

    // Detect format: monthly columns (jan…dec) or explicit month column
    const monthCols = MONTH_NAMES.map(m => headers.indexOf(m));
    const hasMonthCols = monthCols.some(i => i !== -1);
    const monthIdx  = headers.indexOf("month");
    const amountIdx = headers.indexOf("amount");
    const yearIdx   = headers.indexOf("year");

    const entries = [];
    for (let i = 1; i < lines.length; i++) {
      const cols = lines[i].split(",").map(c => c.trim());
      if (cols.every(c => !c)) continue; // skip blank rows
      const category   = cols[catIdx] || "";
      const type       = typeIdx !== -1 ? (cols[typeIdx]||"expense").toLowerCase() : (category==="Revenue"?"income":"expense");
      const department = deptIdx !== -1 ? (cols[deptIdx]||null) : null;
      const rowYear    = yearIdx !== -1 ? parseInt(cols[yearIdx]) : year;
      if (!category) continue;

      if (hasMonthCols) {
        // Wide format: one row per category, one column per month
        MONTH_NAMES.forEach((m, idx) => {
          const colIdx = monthCols[idx];
          if (colIdx === -1) return;
          const amount = parseFloat((cols[colIdx]||"0").replace(/[^0-9.\-]/g,"")) || 0;
          if (amount !== 0) entries.push({year: rowYear, month: idx+1, category, amount, type, department: department||null});
        });
      } else {
        // Long format: explicit year/month/amount columns
        if (monthIdx === -1 || amountIdx === -1) throw new Error("Long format requires columns: year, month, category, amount, type");
        const month  = parseInt(cols[monthIdx]);
        const amount = parseFloat((cols[amountIdx]||"0").replace(/[^0-9.\-]/g,"")) || 0;
        if (month >= 1 && month <= 12 && amount !== 0)
          entries.push({year: rowYear, month, category, amount, type, department: department||null});
      }
    }
    if (!entries.length) throw new Error("No valid budget entries found in CSV.");
    return entries;
  };

  const handleCsvFile = (e) => {
    setCsvError(null); setCsvPreview(null);
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const entries = parseCsv(ev.target.result);
        setCsvPreview(entries);
      } catch(err) {
        setCsvError(err.message);
      }
    };
    reader.readAsText(file);
    e.target.value = ""; // allow re-upload of same file
  };

  const confirmImport = async () => {
    if (!csvPreview) return;
    setSaving(true);
    try {
      await api("/budgets/bulk", {method:"POST", body: JSON.stringify({entries: csvPreview})});
      setCsvPreview(null);
      await load();
    } catch(e) {
      setCsvError("Import failed: "+(e.message||""));
    } finally { setSaving(false); }
  };

  const CellInput = ({cat, m, type, dept: d}) => {
    const key = `${cat}-${m}-${type}`;
    const cur = getBudget(cat, m, type, d);
    return editCell === key
      ? <input autoFocus type="number" value={editVal}
          onChange={e=>setEditVal(e.target.value)}
          onBlur={()=>saveCell(cat,m,type,editVal,d)}
          onKeyDown={e=>{if(e.key==="Enter")saveCell(cat,m,type,editVal,d);if(e.key==="Escape")setEditCell(null);}}
          style={{width:80,padding:"2px 4px",border:`1px solid ${C.accent}`,borderRadius:4,fontSize:12,textAlign:"right",fontFamily:"inherit"}}/>
      : <span onClick={()=>{setEditCell(key);setEditVal(cur||"");}}
          style={{cursor:"text",fontSize:12,color:cur?C.ink:C.inkDim,display:"block",textAlign:"right",minWidth:60,padding:"2px 4px",borderRadius:4,border:`1px dashed ${C.border}`}}>
          {cur ? fmtR(cur) : "Set…"}
        </span>;
  };

  // ── MONTHLY VIEW ────────────────────────────────────────────────────────────
  const MonthlyView = () => {
    const [catFilter, setCatFilter] = useState("all"); // all | over | on_track | no_budget
    const totalBudget = ALL_CATS.reduce((s,c)=>s+getBudget(c,month,c==="Revenue"?"income":"expense"),0);
    const totalActual = actuals.filter(a=>a.month===month).reduce((s,a)=>s+a.actual,0);
    const remaining   = totalBudget - totalActual;
    const overBudgetCount = ALL_CATS.filter(c=>{
      const t=c==="Revenue"?"income":"expense";
      return over(getActual(c,month,t), getBudget(c,month,t));
    }).length;

    const FILTERS = [
      {id:"all",       label:"All",          color:C.ink},
      {id:"over",      label:`Over Budget (${overBudgetCount})`, color:C.red},
      {id:"on_track",  label:"On Track",     color:C.green},
      {id:"no_budget", label:"No Budget Set",color:C.gold},
    ];

    return (
      <div>
        <div style={{display:"flex",gap:8,marginBottom:20,alignItems:"center"}}>
          <select value={month} onChange={e=>setMonth(+e.target.value)} style={{padding:"8px 12px",borderRadius:8,border:`1px solid ${C.border}`,fontSize:13,background:C.bg,color:C.ink,fontFamily:"inherit"}}>
            {MONTHS.map((m,i)=><option key={i} value={i+1}>{m}</option>)}
          </select>
          <span style={{fontSize:12,color:C.inkMid}}>vs actual spend</span>
        </div>

        {/* Summary KPIs — clickable to filter categories below */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:12}}>
          {[
            {id:"all",      label:"Total Budgeted", value:fmtR(totalBudget), color:C.ink,   active:catFilter==="all"},
            {id:"over",     label:"Actual Spend",   value:fmtR(totalActual), color:totalActual>totalBudget?C.red:C.ink, active:catFilter==="over"},
            {id:"on_track", label:"Remaining",      value:fmtR(Math.abs(remaining)), color:remaining>=0?C.green:C.red, active:catFilter==="on_track"},
          ].map(card=>(
            <div key={card.id} onClick={()=>setCatFilter(f=>f===card.id?"all":card.id)}
              style={{background:card.active?card.color:C.surface,borderRadius:12,padding:"14px 16px",border:`2px solid ${card.active?card.color:C.border}`,cursor:"pointer",transition:"all 0.15s"}}
              onMouseEnter={e=>{if(!card.active)e.currentTarget.style.borderColor=card.color;}}
              onMouseLeave={e=>{if(!card.active)e.currentTarget.style.borderColor=C.border;}}>
              <div style={{fontSize:10,color:card.active?"rgba(255,255,255,0.7)":C.inkMid,fontWeight:600,textTransform:"uppercase",letterSpacing:0.5,marginBottom:4}}>{card.label}</div>
              <div style={{fontSize:18,fontWeight:700,color:card.active?"#fff":card.color}}>{card.value}</div>
              {card.id==="on_track" && <div style={{fontSize:10,color:card.active?"rgba(255,255,255,0.6)":C.inkMid,marginTop:2}}>{remaining<0?"Over budget":"Under budget"}</div>}
              {card.active && <div style={{fontSize:10,color:"rgba(255,255,255,0.6)",marginTop:2}}>▼ Filtering</div>}
            </div>
          ))}
        </div>

        {/* Filter pills */}
        <div style={{display:"flex",gap:8,marginBottom:20,flexWrap:"wrap"}}>
          {FILTERS.map(f=>(
            <button key={f.id} onClick={()=>setCatFilter(f.id)}
              style={{padding:"5px 14px",borderRadius:20,border:`1px solid ${catFilter===f.id?f.color:C.border}`,background:catFilter===f.id?f.color+"18":"transparent",color:catFilter===f.id?f.color:C.inkMid,fontSize:12,cursor:"pointer",fontFamily:"inherit",fontWeight:catFilter===f.id?600:400}}>
              {f.label}
            </button>
          ))}
        </div>

        {/* Per-category rows */}
        {ALL_CATS.map(cat => {
          const type   = cat==="Revenue" ? "income" : "expense";
          const budget = getBudget(cat, month, type);
          const actual = getActual(cat, month, type);
          const p      = pct(actual, budget);
          const isOver = over(actual, budget);
          // Always skip empty rows (no budget AND no actual)
          if (!budget && !actual) {
            if (catFilter !== "no_budget") return null;
            // In no_budget mode, show categories with no budget but don't skip all
          }
          // Apply filter
          if (catFilter === "over"      && !isOver)            return null;
          if (catFilter === "on_track"  && (isOver || !budget)) return null;
          if (catFilter === "no_budget" && budget)              return null;
          if (catFilter === "no_budget" && !budget && !actual)  return null; // genuinely empty, skip
          return (
            <div key={cat} style={{background:C.surface,border:`1px solid ${isOver&&catFilter!=="on_track"?C.red+"60":C.border}`,borderRadius:12,padding:"12px 16px",marginBottom:10}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                <div style={{fontSize:13,fontWeight:600,color:C.ink}}>{cat}</div>
                <div style={{display:"flex",gap:16,alignItems:"center"}}>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:10,color:C.inkMid}}>Budget</div>
                    <CellInput cat={cat} m={month} type={type}/>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:10,color:C.inkMid}}>Actual</div>
                    <div style={{fontSize:12,fontWeight:600,color:isOver?C.red:C.ink}}>{fmtR(actual)}</div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:10,color:C.inkMid}}>%</div>
                    <div style={{fontSize:12,fontWeight:700,color:isOver?C.red:C.green}}>{p}%</div>
                  </div>
                </div>
              </div>
              <div style={{height:6,background:C.border,borderRadius:3}}>
                <div style={{height:"100%",width:`${Math.min(p,100)}%`,background:isOver?C.red:p>80?C.gold:C.green,borderRadius:3,transition:"width 0.3s"}}/>
              </div>
              {isOver && <div style={{fontSize:10,color:C.red,marginTop:4}}>⚠ Over budget by {fmtR(actual-budget)}</div>}
            </div>
          );
        })}

        {/* Prompt to set budgets */}
        {ALL_CATS.every(cat=>{const type=cat==="Revenue"?"income":"expense";return !getBudget(cat,month,type)&&!getActual(cat,month,type);}) && catFilter==="all" && (
          <div style={{textAlign:"center",padding:"40px 20px",color:C.inkMid}}>
            <div style={{fontSize:32,marginBottom:12}}>🎯</div>
            <div style={{fontSize:15,fontWeight:600,marginBottom:6}}>No budget set for {MONTHS[month-1]}</div>
            <div style={{fontSize:13}}>Switch to Annual view to plan the full year, or click "Set…" next to any category above after adding some expenses.</div>
          </div>
        )}
      </div>
    );
  };

  // ── ANNUAL VIEW ─────────────────────────────────────────────────────────────
  const AnnualView = () => {
    const [pendingSaves, setPendingSaves] = useState({});
    const setPending = (cat, m, type, val) => setPendingSaves(p=>({...p,[`${cat}-${m}-${type}`]:val}));
    const getCellVal = (cat, m, type) => {
      const key = `${cat}-${m}-${type}`;
      return pendingSaves[key] !== undefined ? pendingSaves[key] : (getBudget(cat,m,type)||"");
    };
    const saveAll = async () => {
      if (!Object.keys(pendingSaves).length) return;
      setSaving(true);
      try {
        const entries = Object.entries(pendingSaves).map(([key,val])=>{
          const [cat,...rest] = key.split("-");
          const type = rest.pop();
          const m = parseInt(rest.join("-"));
          return {year, month:m, category:cat, amount:parseFloat(val)||0, type};
        });
        await api("/budgets/bulk",{method:"POST",body:JSON.stringify({entries})});
        setPendingSaves({});
        await load();
      } catch(e){alert("Save failed");}
      finally{setSaving(false);}
    };

    return (
      <div>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <div style={{fontSize:13,color:C.inkMid}}>Click any cell to edit. Save all when done.</div>
          {Object.keys(pendingSaves).length>0 &&
            <button onClick={saveAll} disabled={saving} style={{background:C.accent,color:"#fff",border:"none",borderRadius:8,padding:"8px 16px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",opacity:saving?0.6:1}}>
              {saving?"Saving…":`Save ${Object.keys(pendingSaves).length} changes`}
            </button>}
        </div>
        <div style={{overflowX:"auto"}}>
          <table style={{borderCollapse:"collapse",fontSize:12,tableLayout:"fixed",width:"1139px"}}>
            <colgroup>
              <col style={{width:155}}/>
              {MONTHS.map((_,i)=><col key={i} style={{width:72}}/>)}
              <col style={{width:95}}/>
            </colgroup>
            <thead>
              <tr style={{background:C.surface}}>
                <th style={{padding:"8px 10px",textAlign:"left",fontWeight:600,color:C.inkMid,border:`1px solid ${C.border}`,position:"sticky",left:0,background:C.surface,zIndex:1,whiteSpace:"nowrap"}}>Category</th>
                {MONTHS.map((m,i)=><th key={i} style={{padding:"8px 4px",textAlign:"center",fontWeight:600,color:C.inkMid,border:`1px solid ${C.border}`,whiteSpace:"nowrap"}}>{m}</th>)}
                <th style={{padding:"8px 6px",textAlign:"right",fontWeight:600,color:C.inkMid,border:`1px solid ${C.border}`,whiteSpace:"nowrap"}}>Total</th>
              </tr>
            </thead>
            <tbody>
              {/* Income section */}
              <tr><td colSpan={14} style={{background:C.greenLt,padding:"5px 10px",fontSize:11,fontWeight:700,color:C.green,letterSpacing:0.5,textTransform:"uppercase"}}>Income</td></tr>
              {INCOME_CATS.map(cat=>(
                <tr key={cat}>
                  <td style={{padding:"4px 10px",border:`1px solid ${C.border}`,fontWeight:500,color:C.ink,position:"sticky",left:0,background:"#fff",zIndex:1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{cat}</td>
                  {MONTHS.map((_,i)=>{
                    const m=i+1;
                    const cur=getCellVal(cat,m,"income");
                    return <td key={m} style={{border:`1px solid ${C.border}`,padding:"1px 2px"}}>
                      <input type="number" value={cur} placeholder="0"
                        onChange={e=>setPending(cat,m,"income",e.target.value)}
                        style={{width:"100%",border:"none",background:"transparent",textAlign:"right",fontSize:11,fontFamily:"inherit",color:cur?C.ink:C.inkDim,outline:"none",padding:"4px 3px",boxSizing:"border-box"}}/>
                    </td>;
                  })}
                  <td style={{border:`1px solid ${C.border}`,padding:"4px 6px",textAlign:"right",fontWeight:600,color:C.green,fontSize:11,whiteSpace:"nowrap"}}>
                    {fmtR(MONTHS.reduce((s,_,i)=>s+(parseFloat(getCellVal(cat,i+1,"income"))||0),0))}
                  </td>
                </tr>
              ))}
              {/* Expense section */}
              <tr><td colSpan={14} style={{background:"#FFF3F3",padding:"5px 10px",fontSize:11,fontWeight:700,color:C.red,letterSpacing:0.5,textTransform:"uppercase"}}>Expenses</td></tr>
              {EXPENSE_CATS.map(cat=>(
                <tr key={cat}>
                  <td style={{padding:"4px 10px",border:`1px solid ${C.border}`,fontWeight:500,color:C.ink,position:"sticky",left:0,background:"#fff",zIndex:1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{cat}</td>
                  {MONTHS.map((_,i)=>{
                    const m=i+1;
                    const cur=getCellVal(cat,m,"expense");
                    return <td key={m} style={{border:`1px solid ${C.border}`,padding:"1px 2px"}}>
                      <input type="number" value={cur} placeholder="0"
                        onChange={e=>setPending(cat,m,"expense",e.target.value)}
                        style={{width:"100%",border:"none",background:"transparent",textAlign:"right",fontSize:11,fontFamily:"inherit",color:cur?C.ink:C.inkDim,outline:"none",padding:"4px 3px",boxSizing:"border-box"}}/>
                    </td>;
                  })}
                  <td style={{border:`1px solid ${C.border}`,padding:"4px 6px",textAlign:"right",fontWeight:600,color:C.red,fontSize:11,whiteSpace:"nowrap"}}>
                    {fmtR(MONTHS.reduce((s,_,i)=>s+(parseFloat(getCellVal(cat,i+1,"expense"))||0),0))}
                  </td>
                </tr>
              ))}
              {/* Net row */}
              <tr style={{background:C.surface,fontWeight:700}}>
                <td style={{padding:"6px 10px",border:`1px solid ${C.border}`,position:"sticky",left:0,background:C.surface,zIndex:1,whiteSpace:"nowrap",fontSize:11}}>Net (Income − Expenses)</td>
                {MONTHS.map((_,i)=>{
                  const m=i+1;
                  const inc=(parseFloat(getCellVal("Revenue",m,"income"))||0);
                  const exp=EXPENSE_CATS.reduce((s,c)=>s+(parseFloat(getCellVal(c,m,"expense"))||0),0);
                  const net=inc-exp;
                  return <td key={m} style={{border:`1px solid ${C.border}`,padding:"6px 4px",textAlign:"right",fontSize:11,color:net>=0?C.green:C.red,whiteSpace:"nowrap"}}>{net?fmtR(net):""}</td>;
                })}
                <td style={{border:`1px solid ${C.border}`,padding:"6px 6px",textAlign:"right",color:C.ink,fontSize:11,whiteSpace:"nowrap"}}>
                  {fmtR(MONTHS.reduce((s,_,i)=>{
                    const m=i+1;
                    const inc=(parseFloat(getCellVal("Revenue",m,"income"))||0);
                    const exp=EXPENSE_CATS.reduce((s2,c)=>s2+(parseFloat(getCellVal(c,m,"expense"))||0),0);
                    return s+(inc-exp);
                  },0))}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ── CASH FLOW VIEW ──────────────────────────────────────────────────────────
  const CashFlowView = () => {
    return (
      <div>
        <p style={{fontSize:13,color:C.inkMid,marginBottom:16}}>Budgeted vs actual net cash position per month for {year}.</p>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
            <thead>
              <tr style={{background:C.surface}}>
                <th style={{padding:"10px 14px",textAlign:"left",fontWeight:600,color:C.inkMid,border:`1px solid ${C.border}`}}>Month</th>
                <th style={{padding:"10px 14px",textAlign:"right",fontWeight:600,color:C.inkMid,border:`1px solid ${C.border}`}}>Budgeted Income</th>
                <th style={{padding:"10px 14px",textAlign:"right",fontWeight:600,color:C.inkMid,border:`1px solid ${C.border}`}}>Budgeted Expense</th>
                <th style={{padding:"10px 14px",textAlign:"right",fontWeight:600,color:C.inkMid,border:`1px solid ${C.border}`}}>Budgeted Net</th>
                <th style={{padding:"10px 14px",textAlign:"right",fontWeight:600,color:C.inkMid,border:`1px solid ${C.border}`}}>Actual Income</th>
                <th style={{padding:"10px 14px",textAlign:"right",fontWeight:600,color:C.inkMid,border:`1px solid ${C.border}`}}>Actual Expense</th>
                <th style={{padding:"10px 14px",textAlign:"right",fontWeight:600,color:C.inkMid,border:`1px solid ${C.border}`}}>Actual Net</th>
                <th style={{padding:"10px 14px",textAlign:"right",fontWeight:600,color:C.inkMid,border:`1px solid ${C.border}`}}>Variance</th>
              </tr>
            </thead>
            <tbody>
              {summary.map(row=>{
                const variance = row.actual_net - row.budgeted_net;
                const now = new Date(); const isFuture = row.month > now.getMonth()+1 && year >= now.getFullYear();
                return (
                  <tr key={row.month} style={{background:isFuture?"#fafafa":"#fff"}}>
                    <td style={{padding:"10px 14px",border:`1px solid ${C.border}`,fontWeight:600,color:C.ink}}>{MONTHS[row.month-1]}{isFuture?" (forecast)":""}</td>
                    <td style={{padding:"10px 14px",border:`1px solid ${C.border}`,textAlign:"right",color:C.green}}>{row.budgeted_income?fmtR(row.budgeted_income):"—"}</td>
                    <td style={{padding:"10px 14px",border:`1px solid ${C.border}`,textAlign:"right",color:C.red}}>{row.budgeted_expense?fmtR(row.budgeted_expense):"—"}</td>
                    <td style={{padding:"10px 14px",border:`1px solid ${C.border}`,textAlign:"right",fontWeight:600,color:row.budgeted_net>=0?C.green:C.red}}>{row.budgeted_net?fmtR(row.budgeted_net):"—"}</td>
                    <td style={{padding:"10px 14px",border:`1px solid ${C.border}`,textAlign:"right",color:C.green,opacity:isFuture?0.3:1}}>{row.actual_income?fmtR(row.actual_income):"—"}</td>
                    <td style={{padding:"10px 14px",border:`1px solid ${C.border}`,textAlign:"right",color:C.red,opacity:isFuture?0.3:1}}>{row.actual_expense?fmtR(row.actual_expense):"—"}</td>
                    <td style={{padding:"10px 14px",border:`1px solid ${C.border}`,textAlign:"right",fontWeight:600,color:row.actual_net>=0?C.green:C.red,opacity:isFuture?0.3:1}}>{row.actual_net?fmtR(row.actual_net):"—"}</td>
                    <td style={{padding:"10px 14px",border:`1px solid ${C.border}`,textAlign:"right",fontWeight:600,color:variance>=0?C.green:C.red,opacity:isFuture?0.3:1}}>{!isFuture&&row.actual_net?`${variance>=0?"+":""}${fmtR(variance)}`:"—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ── DEPARTMENTS VIEW ─────────────────────────────────────────────────────────
  const DepartmentsView = () => {
    const [pendingSaves, setPendingSaves] = useState({});
    const setPending = (cat, m, val) => setPendingSaves(p=>({...p,[`${cat}-${m}`]:val}));
    const getCellVal = (cat, m) => {
      const key=`${cat}-${m}`;
      if(pendingSaves[key]!==undefined) return pendingSaves[key];
      const b=budgets.find(b=>b.category===cat&&b.month===m&&b.department===dept);
      return b?b.amount:"";
    };
    const saveAll = async () => {
      if(!Object.keys(pendingSaves).length) return;
      setSaving(true);
      try {
        const entries=Object.entries(pendingSaves).map(([key,val])=>{
          const [cat,m]=key.split("-");
          return {year,month:parseInt(m),category:cat,amount:parseFloat(val)||0,type:"expense",department:dept};
        });
        await api("/budgets/bulk",{method:"POST",body:JSON.stringify({entries})});
        setPendingSaves({});
        await load();
      } catch(e){alert("Save failed");}
      finally{setSaving(false);}
    };

    return (
      <div>
        <div style={{display:"flex",gap:12,alignItems:"center",marginBottom:16,flexWrap:"wrap"}}>
          <select value={dept} onChange={e=>setDept(e.target.value)} style={{padding:"8px 12px",borderRadius:8,border:`1px solid ${C.border}`,fontSize:13,background:C.bg,color:C.ink,fontFamily:"inherit"}}>
            {DEPTS.map(d=><option key={d} value={d}>{d}</option>)}
          </select>
          <span style={{fontSize:12,color:C.inkMid}}>department budget for {year}</span>
          {Object.keys(pendingSaves).length>0 &&
            <button onClick={saveAll} disabled={saving} style={{background:C.accent,color:"#fff",border:"none",borderRadius:8,padding:"8px 16px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",opacity:saving?0.6:1}}>
              {saving?"Saving…":`Save ${Object.keys(pendingSaves).length} changes`}
            </button>}
        </div>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12,minWidth:900}}>
            <thead>
              <tr style={{background:C.surface}}>
                <th style={{padding:"8px 12px",textAlign:"left",fontWeight:600,color:C.inkMid,border:`1px solid ${C.border}`,position:"sticky",left:0,background:C.surface}}>Category</th>
                {MONTHS.map((m,i)=><th key={i} style={{padding:"8px 8px",textAlign:"center",fontWeight:600,color:C.inkMid,border:`1px solid ${C.border}`,minWidth:80}}>{m}</th>)}
                <th style={{padding:"8px 8px",textAlign:"right",fontWeight:600,color:C.inkMid,border:`1px solid ${C.border}`}}>Total</th>
              </tr>
            </thead>
            <tbody>
              {EXPENSE_CATS.map(cat=>(
                <tr key={cat}>
                  <td style={{padding:"6px 12px",border:`1px solid ${C.border}`,fontWeight:500,color:C.ink,position:"sticky",left:0,background:"#fff"}}>{cat}</td>
                  {MONTHS.map((_,i)=>{
                    const m=i+1; const cur=getCellVal(cat,m);
                    return <td key={m} style={{border:`1px solid ${C.border}`,padding:"2px 4px",textAlign:"right"}}>
                      <input type="number" value={cur} placeholder="0"
                        onChange={e=>setPending(cat,m,e.target.value)}
                        style={{width:"100%",border:"none",background:"transparent",textAlign:"right",fontSize:12,fontFamily:"inherit",outline:"none",padding:"4px 2px"}}/>
                    </td>;
                  })}
                  <td style={{border:`1px solid ${C.border}`,padding:"6px 8px",textAlign:"right",fontWeight:600,color:C.red}}>
                    {fmtR(MONTHS.reduce((s,_,i)=>s+(parseFloat(getCellVal(cat,i+1))||0),0))}
                  </td>
                </tr>
              ))}
              <tr style={{background:C.surface,fontWeight:700}}>
                <td style={{padding:"8px 12px",border:`1px solid ${C.border}`,position:"sticky",left:0,background:C.surface}}>Total — {dept}</td>
                {MONTHS.map((_,i)=>{
                  const m=i+1;
                  const total=EXPENSE_CATS.reduce((s,c)=>s+(parseFloat(getCellVal(c,m))||0),0);
                  return <td key={m} style={{border:`1px solid ${C.border}`,padding:"6px 8px",textAlign:"right",color:C.accent,fontWeight:700}}>{total?fmtR(total):""}</td>;
                })}
                <td style={{border:`1px solid ${C.border}`,padding:"6px 8px",textAlign:"right",color:C.accent,fontWeight:700}}>
                  {fmtR(EXPENSE_CATS.reduce((s,c)=>s+MONTHS.reduce((s2,_,i)=>s2+(parseFloat(getCellVal(c,i+1))||0),0),0))}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div>
      <SectionHeader title="Budgeting" sub="Plan, track and forecast your finances"/>

      {/* Year selector + view tabs */}
      <div style={{display:"flex",gap:12,alignItems:"center",marginBottom:20,flexWrap:"wrap"}}>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <button onClick={()=>setYear(y=>y-1)} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:6,padding:"5px 10px",cursor:"pointer",fontSize:13,fontFamily:"inherit"}}>◀</button>
          <span style={{fontSize:15,fontWeight:700,color:C.ink,minWidth:48,textAlign:"center"}}>{year}</span>
          <button onClick={()=>setYear(y=>y+1)} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:6,padding:"5px 10px",cursor:"pointer",fontSize:13,fontFamily:"inherit"}}>▶</button>
        </div>
        {[
          {id:"monthly",     label:"Monthly"},
          {id:"annual",      label:"Annual Plan"},
          {id:"cashflow",    label:"Cash Flow"},
          {id:"departments", label:"Departments"},
          {id:"forecast",    label:"13-Week Forecast"},
        ].map(v=>(
          <button key={v.id} onClick={()=>setView(v.id)} style={{
            padding:"7px 14px",borderRadius:8,border:`1px solid ${view===v.id?C.accent:C.border}`,
            background:view===v.id?C.accentLt:"transparent",color:view===v.id?C.accent:C.inkMid,
            fontSize:13,fontWeight:view===v.id?700:400,cursor:"pointer",fontFamily:"inherit",
          }}>{v.label}</button>
        ))}
        {loading && <span style={{fontSize:12,color:C.inkMid}}>Loading…</span>}
        <div style={{marginLeft:"auto",display:"flex",gap:8}}>
          <button onClick={downloadTemplate} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:8,padding:"7px 14px",fontSize:12,cursor:"pointer",fontFamily:"inherit",color:C.inkMid}}>
            ⬇ Template
          </button>
          <button onClick={()=>csvInputRef.current?.click()} style={{background:C.accent,border:"none",borderRadius:8,padding:"7px 16px",fontSize:12,cursor:"pointer",fontFamily:"inherit",color:"#fff",fontWeight:600}}>
            📂 Import CSV
          </button>
          <input ref={csvInputRef} type="file" accept=".csv,text/csv" style={{display:"none"}} onChange={handleCsvFile}/>
        </div>
      </div>

      {/* CSV error banner */}
      {csvError && (
        <div style={{background:"#fff0ee",border:`1px solid ${C.red}`,borderRadius:10,padding:"12px 16px",marginBottom:16,display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
          <div>
            <div style={{fontWeight:700,color:C.red,fontSize:13,marginBottom:4}}>⚠ CSV Import Error</div>
            <div style={{fontSize:12,color:C.red}}>{csvError}</div>
          </div>
          <button onClick={()=>setCsvError(null)} style={{background:"none",border:"none",fontSize:16,cursor:"pointer",color:C.red,padding:0}}>✕</button>
        </div>
      )}

      {/* CSV preview modal */}
      {csvPreview && (
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:14,padding:20,marginBottom:20}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
            <div>
              <div style={{fontWeight:700,fontSize:15,color:C.ink}}>📋 Preview — {csvPreview.length} budget entries</div>
              <div style={{fontSize:12,color:C.inkMid,marginTop:2}}>Review before importing. Existing entries for the same category/month will be overwritten.</div>
            </div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>setCsvPreview(null)} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:8,padding:"7px 14px",fontSize:12,cursor:"pointer",fontFamily:"inherit",color:C.inkMid}}>Cancel</button>
              <button onClick={confirmImport} disabled={saving} style={{background:C.green,border:"none",borderRadius:8,padding:"7px 18px",fontSize:12,cursor:"pointer",fontFamily:"inherit",color:"#fff",fontWeight:700}}>
                {saving ? "Importing…" : `✓ Import ${csvPreview.length} entries`}
              </button>
            </div>
          </div>
          <div style={{maxHeight:260,overflowY:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
              <thead>
                <tr style={{background:"#f5f5f5",position:"sticky",top:0}}>
                  {["Year","Month","Category","Type","Amount","Department"].map(h=>(
                    <th key={h} style={{padding:"6px 10px",textAlign:"left",color:C.inkMid,fontWeight:600,borderBottom:`1px solid ${C.border}`}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {csvPreview.slice(0,100).map((r,i)=>(
                  <tr key={i} style={{borderBottom:`1px solid ${C.border}40`}}>
                    <td style={{padding:"5px 10px",color:C.ink}}>{r.year}</td>
                    <td style={{padding:"5px 10px",color:C.ink}}>
                      {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][r.month-1]}
                    </td>
                    <td style={{padding:"5px 10px",color:C.ink,fontWeight:500}}>{r.category}</td>
                    <td style={{padding:"5px 10px",color:r.type==="income"?C.green:C.inkMid}}>{r.type}</td>
                    <td style={{padding:"5px 10px",color:C.ink,textAlign:"right"}}>R {r.amount.toLocaleString("en-ZA")}</td>
                    <td style={{padding:"5px 10px",color:C.inkMid}}>{r.department||"—"}</td>
                  </tr>
                ))}
                {csvPreview.length > 100 && (
                  <tr><td colSpan={6} style={{padding:"8px 10px",color:C.inkMid,fontSize:11,textAlign:"center"}}>…and {csvPreview.length - 100} more rows</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {view==="monthly"     && <MonthlyView/>}
      {view==="annual"      && <AnnualView/>}
      {view==="cashflow"    && <CashFlowView/>}
      {view==="departments" && <DepartmentsView/>}
      {view==="forecast"    && <ForecastView/>}
    </div>
  );

  // ── 13-WEEK FORECAST VIEW ─────────────────────────────────────────────────
  function ForecastView() {
    // Trigger initial load when tab is first opened
    useEffect(() => {
      if (!forecastData && !forecastLoading) loadForecast(openingBalance);
    }, []); // eslint-disable-line

    const fcGet = (i, field) => {
      const key = `${i}-${field}`;
      return fcOverrides[key] !== undefined ? fcOverrides[key] : (forecastData?.weeks?.[i]?.[field] ?? 0);
    };
    const fcSet = (i, field, val) => {
      setFcOverrides(p => ({...p, [`${i}-${field}`]: parseFloat(val) || 0}));
    };

    // Calculate running balances across all 13 weeks
    let running = parseFloat(openingBalance) || 0;
    const calc = (forecastData?.weeks || []).map((_, i) => {
      const opening   = running;
      const receipts  = fcGet(i,"invoice_receipts") + fcGet(i,"recurring_income");
      const payments  = fcGet(i,"payroll") + fcGet(i,"operating_expenses") + fcGet(i,"creditor_payments") + fcGet(i,"other_payments") + fcGet(i,"vat_payment") + fcGet(i,"provisional_tax");
      const net       = receipts - payments;
      const closing   = opening + net;
      running = closing;
      return { opening, receipts, payments, net, closing };
    });

    const exportCsv = () => {
      const rows = [
        ["Week","Period","Opening Balance","Invoice Receipts","Recurring Income","Total Receipts","Payroll","Operating Expenses","Creditor/PO Payments","Other Payments","VAT/Tax","Provisional Tax","Total Payments","Net Cash Flow","Closing Balance"],
        ...(forecastData?.weeks || []).map((w,i) => {
          const c = calc[i];
          return [w.week, w.label, c.opening.toFixed(2), fcGet(i,"invoice_receipts").toFixed(2), fcGet(i,"recurring_income").toFixed(2), c.receipts.toFixed(2), fcGet(i,"payroll").toFixed(2), fcGet(i,"operating_expenses").toFixed(2), fcGet(i,"creditor_payments").toFixed(2), fcGet(i,"other_payments").toFixed(2), fcGet(i,"vat_payment").toFixed(2), fcGet(i,"provisional_tax").toFixed(2), c.payments.toFixed(2), c.net.toFixed(2), c.closing.toFixed(2)];
        }),
      ];
      const csv = rows.map(r=>r.join(",")).join("\n");
      const blob = new Blob([csv],{type:"text/csv"});
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a"); a.href=url; a.download="cash-flow-forecast-13week.csv"; a.click();
      URL.revokeObjectURL(url);
    };

    const EDIT_ROWS = [
      {field:"invoice_receipts",   label:"Invoice Receipts",    color:C.green,  dir:"in"},
      {field:"recurring_income",   label:"Recurring Income",    color:C.green,  dir:"in"},
      {field:"payroll",            label:"Payroll",             color:C.red,    dir:"out"},
      {field:"operating_expenses", label:"Operating Expenses",  color:C.red,    dir:"out"},
      {field:"creditor_payments",  label:"Creditor / PO Payments", color:C.red, dir:"out"},
      {field:"other_payments",     label:"Other Payments",      color:C.red,    dir:"out"},
      {field:"vat_payment",        label:"VAT / Tax",           color:C.accent, dir:"out"},
      {field:"provisional_tax",    label:"Provisional Tax (IRP6)", color:C.accent, dir:"out"},
    ];

    const cellStyle = (color, editable=false) => ({
      padding:"6px 8px", border:`1px solid ${C.border}`, textAlign:"right",
      fontSize:11, color, cursor:editable?"text":"default",
      background:editable?"transparent":"#fafafa",
      minWidth:90,
    });

    const balColor = (v) => v > 0 ? C.green : v < 0 ? C.red : C.inkMid;
    const warningBg = (v) => {
      if (v > 0) return "#f0fff4";
      if (v < 0) return "#fff0f0";
      return "#fafafa";
    };

    if (forecastLoading) return (
      <div style={{textAlign:"center",padding:60,color:C.inkMid}}>
        <div style={{fontSize:24,marginBottom:12}}>📊</div>
        <div style={{fontSize:14,fontWeight:600}}>Building your 13-week forecast…</div>
        <div style={{fontSize:12,marginTop:6}}>Pulling outstanding invoices, expenses and payroll data</div>
      </div>
    );

    if (!forecastData) return (
      <div style={{textAlign:"center",padding:60,color:C.inkMid}}>
        <button onClick={()=>loadForecast(openingBalance)} style={{background:C.accent,color:"#fff",border:"none",borderRadius:10,padding:"12px 28px",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
          Generate 13-Week Forecast
        </button>
      </div>
    );

    return (
      <div>
        {/* Top controls */}
        <div style={{display:"flex",gap:12,alignItems:"center",marginBottom:20,flexWrap:"wrap"}}>
          <div style={{display:"flex",alignItems:"center",gap:8,background:C.surface,padding:"8px 14px",borderRadius:10,border:`1px solid ${C.border}`}}>
            <span style={{fontSize:12,color:C.inkMid,fontWeight:600}}>Opening Balance (R)</span>
            <input type="number" value={openingBalance}
              onChange={e=>setOpeningBalance(parseFloat(e.target.value)||0)}
              style={{width:120,padding:"4px 8px",border:`1px solid ${C.border}`,borderRadius:6,fontSize:13,fontFamily:"inherit",textAlign:"right"}}/>
          </div>
          <button onClick={()=>loadForecast(openingBalance)} style={{background:C.accent,color:"#fff",border:"none",borderRadius:8,padding:"9px 18px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
            ↻ Refresh
          </button>
          <button onClick={exportCsv} style={{background:C.surface,color:C.inkMid,border:`1px solid ${C.border}`,borderRadius:8,padding:"9px 16px",fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>
            ⬇ Export CSV
          </button>
          <div style={{marginLeft:"auto",display:"flex",gap:16,fontSize:11,color:C.inkMid}}>
            <span>📋 Expenses: avg <strong style={{color:C.ink}}>R {Number(forecastData.avg_weekly_expenses).toLocaleString("en-ZA",{maximumFractionDigits:0})}/wk</strong></span>
            <span>👥 Payroll: avg <strong style={{color:C.ink}}>R {Number(forecastData.avg_monthly_payroll).toLocaleString("en-ZA",{maximumFractionDigits:0})}/mo</strong></span>
          </div>
        </div>

        {/* Summary KPI strip */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:20}}>
          {[
            {label:"Total Receipts (13 wks)", value: calc.reduce((s,c)=>s+c.receipts,0), color:C.green},
            {label:"Total Payments (13 wks)", value: calc.reduce((s,c)=>s+c.payments,0), color:C.red},
            {label:"Net Cash Flow (13 wks)",  value: calc.reduce((s,c)=>s+c.net,0),      color:calc.reduce((s,c)=>s+c.net,0)>=0?C.green:C.red},
            {label:"Closing Balance (Wk 13)", value: calc[12]?.closing ?? 0,             color:balColor(calc[12]?.closing ?? 0)},
          ].map(k=>(
            <div key={k.label} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:"12px 14px"}}>
              <div style={{fontSize:10,color:C.inkMid,fontWeight:600,textTransform:"uppercase",letterSpacing:0.5,marginBottom:4}}>{k.label}</div>
              <div style={{fontSize:17,fontWeight:700,color:k.color}}>R {Number(k.value).toLocaleString("en-ZA",{minimumFractionDigits:0,maximumFractionDigits:0})}</div>
            </div>
          ))}
        </div>

        {/* Instruction note */}
        <div style={{fontSize:11,color:C.inkMid,marginBottom:12,padding:"8px 12px",background:C.surface,borderRadius:8,border:`1px solid ${C.border}`}}>
          💡 Click any green or red cell to override the projected value. Totals and closing balances update automatically.
        </div>

        {/* Main table */}
        <div style={{overflowX:"auto"}}>
          <table style={{borderCollapse:"collapse",fontSize:11,minWidth:forecastData.weeks.length*95+160}}>
            <thead>
              <tr style={{background:C.surface}}>
                <th style={{padding:"8px 14px",textAlign:"left",fontWeight:600,color:C.inkMid,border:`1px solid ${C.border}`,minWidth:160,position:"sticky",left:0,background:C.surface,zIndex:2}}>
                  Category
                </th>
                {forecastData.weeks.map(w=>(
                  <th key={w.week} style={{padding:"6px 8px",textAlign:"center",fontWeight:700,color:C.ink,border:`1px solid ${C.border}`,minWidth:90,fontSize:10}}>
                    <div>Wk {w.week}</div>
                    <div style={{fontWeight:400,color:C.inkMid,fontSize:9}}>{w.label}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Opening Balance */}
              <tr style={{background:"#f8f8f8"}}>
                <td style={{padding:"7px 14px",border:`1px solid ${C.border}`,fontWeight:700,color:C.ink,position:"sticky",left:0,background:"#f8f8f8",zIndex:1,fontSize:12}}>
                  Opening Balance
                </td>
                {calc.map((c,i)=>(
                  <td key={i} style={{...cellStyle(balColor(c.opening)),fontWeight:700}}>
                    {fmtR(c.opening)}
                  </td>
                ))}
              </tr>

              {/* Separator: Receipts */}
              <tr>
                <td colSpan={forecastData.weeks.length+1} style={{padding:"4px 14px",background:C.green+"18",border:`1px solid ${C.border}`,fontSize:10,fontWeight:700,color:C.green,textTransform:"uppercase",letterSpacing:0.5}}>
                  💰 Cash Receipts
                </td>
              </tr>

              {/* Editable receipt rows */}
              {EDIT_ROWS.filter(r=>r.dir==="in").map(row=>(
                <EditableRow key={row.field} row={row} calc={calc} fcGet={fcGet} fcSet={fcSet} weeks={forecastData.weeks}/>
              ))}

              {/* Total Receipts */}
              <tr style={{background:C.green+"12"}}>
                <td style={{padding:"7px 14px",border:`1px solid ${C.border}`,fontWeight:700,color:C.green,position:"sticky",left:0,background:C.green+"12",zIndex:1}}>
                  Total Receipts
                </td>
                {calc.map((c,i)=>(
                  <td key={i} style={{...cellStyle(C.green),fontWeight:700}}>
                    {fmtR(c.receipts)}
                  </td>
                ))}
              </tr>

              {/* Separator: Payments */}
              <tr>
                <td colSpan={forecastData.weeks.length+1} style={{padding:"4px 14px",background:C.red+"18",border:`1px solid ${C.border}`,fontSize:10,fontWeight:700,color:C.red,textTransform:"uppercase",letterSpacing:0.5}}>
                  💸 Cash Payments
                </td>
              </tr>

              {/* Editable payment rows */}
              {EDIT_ROWS.filter(r=>r.dir==="out").map(row=>(
                <EditableRow key={row.field} row={row} calc={calc} fcGet={fcGet} fcSet={fcSet} weeks={forecastData.weeks}/>
              ))}

              {/* Total Payments */}
              <tr style={{background:C.red+"12"}}>
                <td style={{padding:"7px 14px",border:`1px solid ${C.border}`,fontWeight:700,color:C.red,position:"sticky",left:0,background:C.red+"12",zIndex:1}}>
                  Total Payments
                </td>
                {calc.map((c,i)=>(
                  <td key={i} style={{...cellStyle(C.red),fontWeight:700}}>
                    {fmtR(c.payments)}
                  </td>
                ))}
              </tr>

              {/* Net Cash Flow */}
              <tr style={{background:C.surface}}>
                <td style={{padding:"8px 14px",border:`1px solid ${C.border}`,fontWeight:700,color:C.ink,position:"sticky",left:0,background:C.surface,zIndex:1,fontSize:12}}>
                  Net Cash Flow
                </td>
                {calc.map((c,i)=>(
                  <td key={i} style={{...cellStyle(c.net>=0?C.green:C.red),fontWeight:700,background:c.net>=0?"#f0fff4":"#fff0f0"}}>
                    {c.net>=0?"+":""}{fmtR(c.net)}
                  </td>
                ))}
              </tr>

              {/* Closing Balance */}
              <tr>
                <td style={{padding:"8px 14px",border:`1px solid ${C.border}`,fontWeight:800,color:C.ink,position:"sticky",left:0,background:C.surface,zIndex:1,fontSize:12}}>
                  Closing Balance
                </td>
                {calc.map((c,i)=>(
                  <td key={i} style={{padding:"6px 8px",border:`1px solid ${C.border}`,textAlign:"right",fontSize:12,fontWeight:800,color:balColor(c.closing),background:warningBg(c.closing)}}>
                    {fmtR(c.closing)}
                    {c.closing < 0 && <div style={{fontSize:9,color:C.red}}>⚠ Deficit</div>}
                    {c.closing >= 0 && c.closing < 5000 && <div style={{fontSize:9,color:C.gold}}>⚡ Low</div>}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );

    function EditableRow({row, calc, fcGet, fcSet, weeks}) {
      const [editing, setEditing] = useState(null); // week index being edited
      const [editVal, setEditVal] = useState("");
      return (
        <tr>
          <td style={{padding:"6px 14px",border:`1px solid ${C.border}`,color:row.color,fontWeight:600,position:"sticky",left:0,background:"#fff",zIndex:1,fontSize:12}}>
            {row.dir==="in" ? "↑ " : "↓ "}{row.label}
          </td>
          {weeks.map((_,i)=>{
            const val = fcGet(i, row.field);
            const isEditing = editing===i;
            return (
              <td key={i} style={{padding:"2px 4px",border:`1px solid ${C.border}`,textAlign:"right"}}>
                {isEditing
                  ? <input autoFocus type="number" value={editVal}
                      onChange={e=>setEditVal(e.target.value)}
                      onBlur={()=>{fcSet(i,row.field,editVal);setEditing(null);}}
                      onKeyDown={e=>{if(e.key==="Enter"){fcSet(i,row.field,editVal);setEditing(null);}if(e.key==="Escape")setEditing(null);}}
                      style={{width:80,padding:"2px 4px",border:`1px solid ${C.accent}`,borderRadius:4,fontSize:11,textAlign:"right",fontFamily:"inherit"}}/>
                  : <span onClick={()=>{setEditing(i);setEditVal(val.toString());}}
                      style={{cursor:"text",fontSize:11,color:val?row.color:C.inkDim,display:"block",textAlign:"right",padding:"4px 6px",borderRadius:4,border:`1px dashed ${C.border}`}}>
                      {val ? fmtR(val) : "—"}
                    </span>
                }
              </td>
            );
          })}
        </tr>
      );
    }
  }
}

// ── DEBTORS ───────────────────────────────────────────────────────────────────
function Debtors({live = {}}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    api("/reports/debtors-aging").then(setData).catch(()=>null).finally(()=>setLoading(false));
  }, []);

  // Refresh when an invoice payment is recorded
  useEffect(() => {
    if (live.invoices) {
      api("/reports/debtors-aging").then(setData).catch(()=>null);
    }
  }, [live.invoices]); // eslint-disable-line

  const BUCKETS = [
    {key:"not_due", label:"Not Yet Due",  color:C.blue},
    {key:"current", label:"0 – 30 Days",  color:C.green},
    {key:"31_60",   label:"31 – 60 Days", color:C.gold},
    {key:"61_90",   label:"61 – 90 Days", color:C.accent},
    {key:"over_90", label:"90+ Days",     color:C.red},
  ];

  // Mock debtors for demo mode
  const mockBuckets = {
    not_due: [{id:"INV-003",client:"SA Retail Group",amount:67200,due_date:"2025-06-15",days_overdue:0,status:"sent"}],
    current: [{id:"INV-002",client:"BuildRight CC",amount:28500,due_date:"2025-05-10",days_overdue:20,status:"sent"}],
    "31_60": [{id:"INV-004",client:"Cape Town Motors",amount:15800,due_date:"2025-04-20",days_overdue:40,status:"overdue"}],
    "61_90": [],
    over_90: [],
  };
  const mockTotals = {not_due:67200,current:28500,"31_60":15800,"61_90":0,over_90:0,grand:111500};

  const buckets = data ? data.buckets : mockBuckets;
  const totals  = data ? data.totals  : mockTotals;
  const asAt    = data ? data.as_at   : new Date().toLocaleDateString("en-ZA",{day:"2-digit",month:"long",year:"numeric"});

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:20}}>
        <div>
          <h2 style={{fontFamily:"serif",fontSize:26,color:C.ink,margin:0}}>Debtors Book</h2>
          <p style={{fontSize:12,color:C.inkMid,marginTop:3}}>Accounts receivable — as at {asAt}</p>
        </div>
        <div style={{display:"flex",gap:8}}>
          {!data && <Badge label="Demo Mode" color={C.gold} bg={C.goldLt}/>}
          <KPI label="Total Outstanding" value={fmt(totals.grand)} color={C.accent} icon="Total"/>
        </div>
      </div>
      {loading && <div style={{textAlign:"center",padding:40,color:C.inkMid}}>Loading debtors...</div>}
      {!loading && (
        <div>
          <div style={{display:"flex",gap:12,marginBottom:24,flexWrap:"wrap"}}>
            {BUCKETS.map(b => (
              <div key={b.key} onClick={()=>setExpanded(expanded===b.key?null:b.key)}
                style={{flex:1,minWidth:140,background:C.surface,border:`2px solid ${expanded===b.key?b.color:C.border}`,borderRadius:14,padding:16,cursor:"pointer"}}>
                <div style={{fontSize:10,color:C.inkMid,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8}}>{b.label}</div>
                <div style={{fontSize:20,fontWeight:800,color:b.color,marginBottom:4}}>{fmt(totals[b.key])}</div>
                <div style={{fontSize:11,color:C.inkDim}}>{(buckets[b.key]||[]).length} invoice{(buckets[b.key]||[]).length!==1?"s":""}</div>
              </div>
            ))}
          </div>
          {BUCKETS.map(b => expanded===b.key && (buckets[b.key]||[]).length>0 && (
            <div key={b.key} style={{background:C.surface,border:`1px solid ${b.color}40`,borderRadius:16,overflow:"hidden",marginBottom:16}}>
              <div style={{padding:"14px 20px",borderBottom:`1px solid ${C.border}`,fontSize:13,fontWeight:700,color:b.color}}>{b.label} — {fmt(totals[b.key])}</div>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                <thead>
                  <tr style={{background:C.bg,borderBottom:`1px solid ${C.border}`}}>
                    {["Invoice","Client","Amount","Due Date","Days Overdue","Status"].map(h=>(
                      <th key={h} style={{padding:"10px 16px",textAlign:"left",fontSize:9,color:C.inkMid,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase"}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(buckets[b.key]||[]).map((inv,i)=>(
                    <tr key={i} style={{borderBottom:`1px solid ${C.border}20`}}>
                      <td style={{padding:"12px 16px",fontWeight:700,color:C.accent}}>{inv.id}</td>
                      <td style={{padding:"12px 16px",fontWeight:500}}>{inv.client}</td>
                      <td style={{padding:"12px 16px",fontWeight:700}}>{fmt(inv.amount)}</td>
                      <td style={{padding:"12px 16px",color:C.inkMid}}>{inv.due_date ? new Date(inv.due_date).toLocaleDateString("en-ZA",{day:"2-digit",month:"short",year:"numeric"}) : "—"}</td>
                      <td style={{padding:"12px 16px"}}>
                        <Badge label={inv.days_overdue===0?"Current":inv.days_overdue+" days"} color={b.color} bg={b.color+"15"}/>
                      </td>
                      <td style={{padding:"12px 16px"}}><StatusBadge status={inv.status}/></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,overflow:"hidden"}}>
            <div style={{padding:"14px 20px",borderBottom:`1px solid ${C.border}`,fontSize:13,fontWeight:700,color:C.ink}}>Aging Summary</div>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
              <thead>
                <tr style={{background:C.bg,borderBottom:`1px solid ${C.border}`}}>
                  {["Age Bucket","Invoices","Amount","% of Total"].map(h=>(
                    <th key={h} style={{padding:"10px 16px",textAlign:"left",fontSize:9,color:C.inkMid,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase"}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {BUCKETS.map(b=>(
                  <tr key={b.key} style={{borderBottom:`1px solid ${C.border}20`}}>
                    <td style={{padding:"12px 16px"}}><Badge label={b.label} color={b.color} bg={b.color+"15"}/></td>
                    <td style={{padding:"12px 16px",color:C.inkMid}}>{(buckets[b.key]||[]).length}</td>
                    <td style={{padding:"12px 16px",fontWeight:700,color:b.color}}>{fmt(totals[b.key])}</td>
                    <td style={{padding:"12px 16px",color:C.inkMid}}>{totals.grand?Math.round(totals[b.key]/totals.grand*100):0}%</td>
                  </tr>
                ))}
                <tr style={{background:C.bg,borderTop:`2px solid ${C.border}`}}>
                  <td style={{padding:"12px 16px",fontWeight:800}}>TOTAL</td>
                  <td style={{padding:"12px 16px",fontWeight:800}}>{Object.values(buckets).reduce((s,b)=>s+(b||[]).length,0)}</td>
                  <td style={{padding:"12px 16px",fontWeight:800,color:C.accent}}>{fmt(totals.grand)}</td>
                  <td style={{padding:"12px 16px",fontWeight:800}}>100%</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── CREDITORS ─────────────────────────────────────────────────────────────────
function Creditors({live = {}}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [bucketFilter, setBucketFilter] = useState("all");

  useEffect(() => {
    api("/reports/creditors-aging").then(setData).catch(()=>null).finally(()=>setLoading(false));
  }, []);

  // Refresh when a PO is received or paid, or when an on-credit expense is paid
  useEffect(() => {
    if (live.purchaseOrders || live.expenses) {
      api("/reports/creditors-aging").then(setData).catch(()=>null);
    }
  }, [live.purchaseOrders, live.expenses]); // eslint-disable-line

  const BUCKETS = [
    {key:"not_due",  label:"Not Yet Due",   color:C.blue},
    {key:"current",  label:"0 – 30 Days",   color:C.green},
    {key:"31_60",    label:"31 – 60 Days",  color:C.gold},
    {key:"61_90",    label:"61 – 90 Days",  color:C.accent},
    {key:"over_90",  label:"90+ Days",      color:C.red},
  ];

  const mockVendors = [
    {vendor:"Eskom",     total:3200, invoices:[{id:"PO-001",description:"Electricity",amount:3200,received_date:"2026-05-20",due_date:"2026-06-19",days_overdue:0, bucket:"not_due"}]},
    {vendor:"Telkom",    total:1850, invoices:[{id:"PO-002",description:"Fibre + phone",amount:1850,received_date:"2026-04-15",due_date:"2026-05-15",days_overdue:32,bucket:"31_60"}]},
    {vendor:"Discovery", total:4200, invoices:[{id:"PO-003",description:"Business insurance",amount:4200,received_date:"2026-04-22",due_date:"2026-05-22",days_overdue:25,bucket:"current"}]},
  ];
  const mockTotals = {not_due:3200,current:4200,"31_60":1850,"61_90":0,over_90:0,grand:9250};

  const allVendors = data ? data.vendors : mockVendors;
  const totals     = data ? data.totals  : mockTotals;
  const asAt       = data ? data.as_at   : new Date().toLocaleDateString("en-ZA",{day:"2-digit",month:"long",year:"numeric"});

  // Filter vendors by selected bucket — only show vendors that have items in that bucket
  const vendors = bucketFilter === "all"
    ? allVendors
    : allVendors
        .map(v => ({...v, invoices: v.invoices.filter(i => i.bucket === bucketFilter)}))
        .filter(v => v.invoices.length > 0)
        .map(v => ({...v, total: v.invoices.reduce((s,i) => s+i.amount, 0)}));

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:20}}>
        <div>
          <h2 style={{fontFamily:"serif",fontSize:26,color:C.ink,margin:0}}>Creditors Book</h2>
          <p style={{fontSize:12,color:C.inkMid,marginTop:3}}>Accounts payable by vendor — as at {asAt}</p>
        </div>
        {!data && <Badge label="Demo Mode" color={C.gold} bg={C.goldLt}/>}
      </div>
      {loading && <div style={{textAlign:"center",padding:40,color:C.inkMid}}>Loading creditors...</div>}
      {!loading && (
        <div>
          {/* Summary cards — click to filter table */}
          <div style={{display:"flex",gap:12,marginBottom:24,flexWrap:"wrap"}}>
            {BUCKETS.map(b=>{
              const active = bucketFilter === b.key;
              return (
                <div key={b.key} onClick={()=>setBucketFilter(active?"all":b.key)}
                  style={{flex:1,minWidth:130,background:active?b.color:C.surface,border:`2px solid ${active?b.color:C.border}`,borderRadius:14,padding:16,cursor:"pointer",transition:"all 0.15s"}}
                  onMouseEnter={e=>{if(!active)e.currentTarget.style.borderColor=b.color;}}
                  onMouseLeave={e=>{if(!active)e.currentTarget.style.borderColor=C.border;}}>
                  <div style={{fontSize:10,color:active?"rgba(255,255,255,0.75)":C.inkMid,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8}}>{b.label}</div>
                  <div style={{fontSize:20,fontWeight:800,color:active?"#fff":b.color,marginBottom:4}}>{fmt(totals[b.key]||0)}</div>
                  {active && <div style={{fontSize:10,color:"rgba(255,255,255,0.75)",marginTop:2}}>▼ Filtering</div>}
                </div>
              );
            })}
            <div onClick={()=>setBucketFilter("all")}
              style={{flex:1,minWidth:130,background:bucketFilter==="all"?C.ink:C.surface,border:`2px solid ${bucketFilter==="all"?C.ink:C.border}`,borderRadius:14,padding:16,cursor:"pointer",transition:"all 0.15s"}}>
              <div style={{fontSize:10,color:bucketFilter==="all"?"rgba(255,255,255,0.5)":C.inkMid,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8}}>Total Creditors</div>
              <div style={{fontSize:20,fontWeight:800,color:bucketFilter==="all"?"#fff":C.ink,marginBottom:4}}>{fmt(totals.grand||0)}</div>
              {bucketFilter==="all" && <div style={{fontSize:10,color:"rgba(255,255,255,0.5)",marginTop:2}}>All shown</div>}
            </div>
          </div>

          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,overflow:"hidden"}}>
            <div style={{padding:"14px 20px",borderBottom:`1px solid ${C.border}`,fontSize:13,fontWeight:700,color:C.ink,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span>Creditor Ledger by Vendor</span>
              {bucketFilter !== "all" && (
                <span style={{fontSize:12,fontWeight:400,color:C.inkMid}}>
                  Showing: <strong style={{color:BUCKETS.find(b=>b.key===bucketFilter)?.color}}>{BUCKETS.find(b=>b.key===bucketFilter)?.label}</strong>
                  {" "}<button onClick={()=>setBucketFilter("all")} style={{marginLeft:8,fontSize:11,color:C.accent,background:"none",border:"none",cursor:"pointer",padding:0,fontFamily:"inherit"}}>✕ Clear</button>
                </span>
              )}
            </div>
            {vendors.length === 0 ? (
              <div style={{textAlign:"center",padding:"40px 0",color:C.inkDim}}>
                <div style={{fontSize:32,marginBottom:8}}>✅</div>
                <div style={{fontSize:14,fontWeight:600}}>No creditors in this bucket</div>
              </div>
            ) : vendors.map((v,vi)=>(
              <div key={vi}>
                <div onClick={()=>setExpanded(expanded===vi?null:vi)}
                  style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 20px",borderBottom:`1px solid ${C.border}20`,cursor:"pointer",background:expanded===vi?C.accentLt:"transparent"}}>
                  <div style={{display:"flex",alignItems:"center",gap:12}}>
                    <span style={{fontSize:14,fontWeight:700,color:C.ink}}>{v.vendor}</span>
                    <Badge label={v.invoices.length+" PO"+(v.invoices.length!==1?"s":"")} color={C.blue} bg={C.blueLt}/>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:16}}>
                    <span style={{fontWeight:700,color:C.red}}>{fmt(v.total)}</span>
                    <span style={{fontSize:12,color:C.inkDim}}>{expanded===vi?"▼":"▶"}</span>
                  </div>
                </div>
                {expanded===vi && (
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:12,background:C.bg}}>
                    <thead>
                      <tr style={{borderBottom:`1px solid ${C.border}`}}>
                        {["PO Ref","Description","Received","Due Date","Overdue","Amount (incl. VAT)"].map(h=>(
                          <th key={h} style={{padding:"9px 20px",textAlign:"left",fontSize:9,color:C.inkMid,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase"}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {v.invoices.map((exp,i)=>{
                        const bc = BUCKETS.find(b=>b.key===exp.bucket)||BUCKETS[1];
                        return (
                          <tr key={i} style={{borderBottom:`1px solid ${C.border}20`}}>
                            <td style={{padding:"10px 20px",color:C.inkMid,fontSize:11}}>{exp.id}</td>
                            <td style={{padding:"10px 20px"}}>{exp.description}</td>
                            <td style={{padding:"10px 20px",color:C.inkMid}}>{exp.received_date?new Date(exp.received_date).toLocaleDateString("en-ZA",{day:"2-digit",month:"short",year:"numeric"}):"—"}</td>
                            <td style={{padding:"10px 20px",color:C.inkMid}}>{exp.due_date?new Date(exp.due_date).toLocaleDateString("en-ZA",{day:"2-digit",month:"short",year:"numeric"}):"—"}</td>
                            <td style={{padding:"10px 20px"}}>
                              <Badge
                                label={exp.bucket==="not_due" ? `Due in ${exp.days_until_due} day${exp.days_until_due===1?"":"s"}` : `${exp.days_overdue} days`}
                                color={bc.color} bg={bc.color+"15"}/>
                            </td>
                            <td style={{padding:"10px 20px",fontWeight:700,color:C.red}}>{fmt(exp.amount)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{borderTop:`1px solid ${C.border}`}}>
                        <td colSpan={5} style={{padding:"10px 20px",fontWeight:700,color:C.ink}}>Vendor Total</td>
                        <td style={{padding:"10px 20px",fontWeight:800,color:C.red}}>{fmt(v.total)}</td>
                      </tr>
                    </tfoot>
                  </table>
                )}
              </div>
            ))}
            <div style={{display:"flex",justifyContent:"space-between",padding:"14px 20px",borderTop:`2px solid ${C.border}`,fontWeight:800,fontSize:14}}>
              <span>{bucketFilter==="all"?"TOTAL CREDITORS":`TOTAL — ${BUCKETS.find(b=>b.key===bucketFilter)?.label||""}`}</span>
              <span style={{color:C.red}}>{fmt(bucketFilter==="all"?totals.grand||0:vendors.reduce((s,v)=>s+v.total,0))}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── CHART OF ACCOUNTS ─────────────────────────────────────────────────────────
function ChartOfAccounts() {
  // Merge DEFAULT_COA with server-persisted custom accounts on every load
  const [customRows, setCustomRows] = useState([]);   // [{code, name, type, group, normal, description, is_deleted, custom:true}]
  const [loading,    setLoading]    = useState(true);
  const [saving,     setSaving]     = useState(false);
  const [search, setSearch] = useState("");
  const [groupFilter, setGF] = useState("All");
  const [showNew, setShowNew] = useState(false);
  const [showEdit, setShowEdit] = useState(null);
  const [expanded, setExpanded] = useState(new Set(COA_GROUPS));
  const [form, setForm] = useState({code:"",name:"",type:"Detail",group:"Expenses",normal:"Debit",description:""});

  // Build merged account list: DEFAULT_COA + custom additions, minus soft-deleted codes
  const accounts = useMemo(() => {
    const deletedCodes = new Set(customRows.filter(r => r.is_deleted).map(r => r.code));
    // Default accounts not hidden
    const defaults = DEFAULT_COA.filter(a => !deletedCodes.has(a.code));
    // Custom additions (not deleted, not already a default code)
    const defaultCodes = new Set(DEFAULT_COA.map(a => a.code));
    const additions = customRows.filter(r => !r.is_deleted && !defaultCodes.has(r.code));
    return [...defaults, ...additions].sort((a,b) => a.code.localeCompare(b.code));
  }, [customRows]);

  // Load custom rows from server
  const loadCoa = async () => {
    setLoading(true);
    try {
      const rows = await api("/coa/");
      setCustomRows(rows || []);
    } catch(e) { console.warn("COA load error", e); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadCoa(); }, []);

  const filtered = accounts.filter(a => {
    const ms = a.name.toLowerCase().includes(search.toLowerCase()) || a.code.includes(search) || (a.description||"").toLowerCase().includes(search.toLowerCase());
    const mg = groupFilter === "All" || a.group === groupFilter;
    return ms && mg;
  });

  const toggleGroup = g => setExpanded(prev => { const n = new Set(prev); n.has(g) ? n.delete(g) : n.add(g); return n; });

  const handleAdd = async () => {
    if (!form.code || !form.name) return;
    if (accounts.find(a => a.code === form.code.trim())) { alert("Account code already exists."); return; }
    setSaving(true);
    try {
      await api("/coa/", { method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify(form) });
      await loadCoa();
      setShowNew(false);
      setForm({code:"",name:"",type:"Detail",group:"Expenses",normal:"Debit",description:""});
    } catch(e) { alert("Failed to save account: " + (e.message||e)); }
    finally { setSaving(false); }
  };

  const handleEdit = async () => {
    setSaving(true);
    try {
      await api(`/coa/${encodeURIComponent(showEdit.code)}`, { method:"PUT", headers:{"Content-Type":"application/json"}, body: JSON.stringify(showEdit) });
      await loadCoa();
      setShowEdit(null);
    } catch(e) { alert("Failed to update account: " + (e.message||e)); }
    finally { setSaving(false); }
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`Remove account ${code} from the chart of accounts?`)) return;
    try {
      await api(`/coa/${encodeURIComponent(code)}`, { method:"DELETE" });
      await loadCoa();
    } catch(e) { alert("Failed to delete account: " + (e.message||e)); }
  };

  const grouped = COA_GROUPS.reduce((acc,g) => { acc[g] = filtered.filter(a => a.group === g); return acc; }, {});

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:24,flexWrap:"wrap",gap:12}}>
        <div>
          <h2 style={{fontFamily:"serif",fontSize:26,color:C.ink,margin:0}}>Chart of Accounts</h2>
          <p style={{fontSize:12,color:C.inkMid,marginTop:3}}>IFRS for SMEs · SA-compliant · {loading ? "Loading…" : `${accounts.length} accounts`}</p>
        </div>
        <button onClick={() => setShowNew(true)} disabled={loading} style={{padding:"9px 18px",border:"none",borderRadius:8,background:C.accent,color:"#fff",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit",opacity:loading?0.6:1}}>+ Add Account</button>
      </div>
      <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:"12px 16px",marginBottom:16,display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
        <input type="text" placeholder="Search by code, name or description..." value={search} onChange={e => setSearch(e.target.value)} style={{flex:1,minWidth:200,padding:"8px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}/>
        <select value={groupFilter} onChange={e => setGF(e.target.value)} style={{padding:"8px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:12,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}>
          <option value="All">All Groups</option>
          {COA_GROUPS.map(g => <option key={g}>{g}</option>)}
        </select>
        <button onClick={() => setExpanded(new Set(COA_GROUPS))} style={{padding:"7px 12px",border:`1px solid ${C.border}`,borderRadius:8,background:"transparent",color:C.inkMid,fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Expand All</button>
        <button onClick={() => setExpanded(new Set())} style={{padding:"7px 12px",border:`1px solid ${C.border}`,borderRadius:8,background:"transparent",color:C.inkMid,fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Collapse All</button>
      </div>
      {showNew && (
        <div style={{background:C.surface,border:`2px solid ${C.accent}`,borderRadius:16,padding:24,marginBottom:20}}>
          <h3 style={{fontFamily:"serif",fontSize:20,color:C.ink,marginBottom:18}}>Add New Account</h3>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:12}}>
            {[{l:"Account Code",k:"code",p:"e.g. 6999"},{l:"Account Name",k:"name",p:"e.g. Staff Welfare"}].map(f => (
              <div key={f.k}>
                <label style={{display:"block",fontSize:11,fontWeight:600,color:C.inkMid,marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>{f.l}</label>
                <input placeholder={f.p} value={form[f.k]} onChange={e => setForm({...form,[f.k]:e.target.value})} style={{width:"100%",padding:"9px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}/>
              </div>
            ))}
            <div>
              <label style={{display:"block",fontSize:11,fontWeight:600,color:C.inkMid,marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Group</label>
              <select value={form.group} onChange={e => setForm({...form,group:e.target.value})} style={{width:"100%",padding:"9px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}>
                {COA_GROUPS.map(g => <option key={g}>{g}</option>)}
              </select>
            </div>
            <div>
              <label style={{display:"block",fontSize:11,fontWeight:600,color:C.inkMid,marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Type</label>
              <select value={form.type} onChange={e => setForm({...form,type:e.target.value})} style={{width:"100%",padding:"9px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}>
                <option value="Header">Header</option>
                <option value="Detail">Detail</option>
              </select>
            </div>
            <div>
              <label style={{display:"block",fontSize:11,fontWeight:600,color:C.inkMid,marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Normal Balance</label>
              <select value={form.normal} onChange={e => setForm({...form,normal:e.target.value})} style={{width:"100%",padding:"9px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}>
                <option value="Debit">Debit</option>
                <option value="Credit">Credit</option>
              </select>
            </div>
          </div>
          <div style={{marginBottom:16}}>
            <label style={{display:"block",fontSize:11,fontWeight:600,color:C.inkMid,marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Description</label>
            <input placeholder="Brief description" value={form.description} onChange={e => setForm({...form,description:e.target.value})} style={{width:"100%",padding:"9px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}/>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={handleAdd} disabled={saving} style={{background:C.accent,color:"#fff",border:"none",borderRadius:8,padding:"10px 20px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",opacity:saving?0.6:1}}>{saving ? "Saving…" : "Add Account"}</button>
            <button onClick={() => setShowNew(false)} style={{background:"transparent",color:C.inkMid,border:`1px solid ${C.border}`,borderRadius:8,padding:"10px 20px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
          </div>
        </div>
      )}
      {showEdit && (
        <div style={{position:"fixed",inset:0,background:"#00000050",zIndex:100,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={() => setShowEdit(null)}>
          <div style={{background:C.surface,borderRadius:18,padding:32,width:560}} onClick={e => e.stopPropagation()}>
            <h3 style={{fontFamily:"serif",fontSize:20,color:C.ink,marginBottom:20}}>Edit Account - {showEdit.code}</h3>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
              <div>
                <label style={{display:"block",fontSize:11,fontWeight:600,color:C.inkMid,marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Account Name</label>
                <input value={showEdit.name} onChange={e => setShowEdit({...showEdit,name:e.target.value})} style={{width:"100%",padding:"9px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}/>
              </div>
              <div>
                <label style={{display:"block",fontSize:11,fontWeight:600,color:C.inkMid,marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Group</label>
                <select value={showEdit.group} onChange={e => setShowEdit({...showEdit,group:e.target.value})} style={{width:"100%",padding:"9px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}>
                  {COA_GROUPS.map(g => <option key={g}>{g}</option>)}
                </select>
              </div>
            </div>
            <div style={{marginBottom:16}}>
              <label style={{display:"block",fontSize:11,fontWeight:600,color:C.inkMid,marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Description</label>
              <input value={showEdit.description} onChange={e => setShowEdit({...showEdit,description:e.target.value})} style={{width:"100%",padding:"9px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}/>
            </div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={handleEdit} disabled={saving} style={{background:C.accent,color:"#fff",border:"none",borderRadius:8,padding:"10px 20px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",opacity:saving?0.6:1}}>{saving ? "Saving…" : "Save Changes"}</button>
              <button onClick={() => setShowEdit(null)} style={{background:"transparent",color:C.inkMid,border:`1px solid ${C.border}`,borderRadius:8,padding:"10px 20px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {COA_GROUPS.map(group => {
        const groupAccounts = grouped[group];
        if (!groupAccounts || !groupAccounts.length) return null;
        const gc = GROUP_COLORS[group];
        const isExpanded = expanded.has(group);
        return (
          <div key={group} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,marginBottom:12,overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
            <div onClick={() => toggleGroup(group)} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 20px",cursor:"pointer",background:gc.bg,borderBottom:isExpanded ? `1px solid ${C.border}` : "none"}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <span style={{fontSize:20}}>{gc.icon}</span>
                <span style={{fontFamily:"serif",fontSize:16,fontWeight:700,color:gc.color}}>{group}</span>
                <span style={{fontSize:11,background:gc.color+"20",color:gc.color,padding:"2px 8px",borderRadius:10,fontWeight:600}}>{groupAccounts.length} accounts</span>
              </div>
              <span style={{color:gc.color,fontSize:16}}>{isExpanded ? "v" : ">"}</span>
            </div>
            {isExpanded && (
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                <thead>
                  <tr style={{background:C.bg,borderBottom:`1px solid ${C.border}`}}>
                    {["Code","Account Name","Type","Normal Balance","Description",""].map(h => <th key={h} style={{padding:"10px 16px",textAlign:"left",fontSize:9,color:C.inkMid,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase",whiteSpace:"nowrap"}}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {groupAccounts.map((acc) => (
                    <tr key={acc.code} style={{borderBottom:`1px solid ${C.border}20`,background:acc.type === "Header" ? C.bg+"80" : "transparent"}}>
                      <td style={{padding:"10px 16px",fontFamily:"monospace",fontWeight:700,color:gc.color,fontSize:12}}>{acc.code}</td>
                      <td style={{padding:"10px 16px",color:C.ink,fontWeight:acc.type === "Header" ? 700 : 400,paddingLeft:acc.type === "Header" ? 16 : 28}}>
                        {acc.type === "Header" ? acc.name : `- ${acc.name}`}
                        {acc.custom && <span style={{fontSize:9,background:C.accentLt,color:C.accent,padding:"1px 6px",borderRadius:8,marginLeft:6,fontWeight:700}}>CUSTOM</span>}
                      </td>
                      <td style={{padding:"10px 16px"}}><span style={{fontSize:10,background:acc.type === "Header" ? C.inkDim+"20" : C.blueLt,color:acc.type === "Header" ? C.inkMid : C.blue,padding:"2px 8px",borderRadius:8,fontWeight:600}}>{acc.type}</span></td>
                      <td style={{padding:"10px 16px"}}><span style={{fontSize:10,background:acc.normal === "Debit" ? C.accentLt : C.greenLt,color:acc.normal === "Debit" ? C.accent : C.green,padding:"2px 8px",borderRadius:8,fontWeight:600}}>{acc.normal}</span></td>
                      <td style={{padding:"10px 16px",color:C.inkMid,fontSize:11,maxWidth:280}}><div style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{acc.description}</div></td>
                      <td style={{padding:"10px 16px",whiteSpace:"nowrap"}}>
                        <button onClick={() => setShowEdit({...acc})} style={{background:C.blueLt,color:C.blue,border:"none",borderRadius:6,padding:"4px 10px",fontSize:10,cursor:"pointer",fontFamily:"inherit",fontWeight:600,marginRight:4}}>Edit</button>
                        {acc.custom && <button onClick={() => handleDelete(acc.code)} style={{background:C.redLt,color:C.red,border:"none",borderRadius:6,padding:"4px 10px",fontSize:10,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>Delete</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })}
    </div>
  );
}
// ── DIRECT BANK FEEDS ─────────────────────────────────────────────────────────
const SA_BANKS = [
  {id:"fnb",          name:"FNB",           logo:"🟢",color:"#007A39"},
  {id:"absa",         name:"ABSA",          logo:"🔴",color:"#C8102E"},
  {id:"standardbank", name:"Standard Bank", logo:"🔵",color:"#0033A0"},
  {id:"nedbank",      name:"Nedbank",       logo:"🟩",color:"#009A44"},
  {id:"capitec",      name:"Capitec",       logo:"🟦",color:"#1E4A8C"},
  {id:"discovery",    name:"Discovery Bank",logo:"🟣",color:"#6B2A8B"},
  {id:"tymebank",     name:"TymeBank",      logo:"🩵",color:"#00B4D8"},
  {id:"africanbank",  name:"African Bank",  logo:"🟠",color:"#E85C0D"},
];

// ── Shared sub-component: BankFeedPanel ──────────────────────────────────────
// Renders accounts + transactions for one bank (absa | nedbank)
function BankFeedPanel({ bank }) {
  const prefix  = `/banking/${bank}`;
  const [status,     setStatus]    = useState(null);
  const [accounts,   setAccounts]  = useState([]);
  const [txns,       setTxns]      = useState([]);
  const [txnTab,     setTxnTab]    = useState("unmatched");
  const [syncing,    setSyncing]   = useState(false);
  const [connecting, setConnecting]= useState(false);
  const [banner,     setBanner]    = useState(null);
  const [matchModal, setMatchModal]= useState(null);
  const [invoices,   setInvoices]  = useState([]);
  const [expenses,   setExpenses]  = useState([]);
  const [requested,  setRequested] = useState(false);

  const BANK_META = {
    absa:         {label:"ABSA",          color:"#CC0000", icon:"🔴"},
    nedbank:      {label:"Nedbank",        color:"#009A44", icon:"🟩"},
    investec:     {label:"Investec",       color:"#1A1A1A", icon:"⬛"},
    standardbank: {label:"Standard Bank",  color:"#0033A0", icon:"🔵"},
    fnb:          {label:"FNB",            color:"#007A39", icon:"🟢"},
    africanbank:  {label:"African Bank",   color:"#E85C0D", icon:"🟠"},
    discovery:    {label:"Discovery Bank", color:"#6B2A8B", icon:"🟣"},
  };
  const bankLabel = BANK_META[bank]?.label || bank;
  const bankColor = BANK_META[bank]?.color || C.accent;
  const bankIcon  = BANK_META[bank]?.icon  || "🏦";

  // Detect OAuth callback redirect (?absa=callback or ?nedbank=callback)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get(bank) === "callback") {
      window.history.replaceState({}, "", window.location.pathname + "#banking");
      if (params.get("success")) { setBanner("success"); loadAll(); }
      else { setBanner("error"); }
    }
  }, []); // eslint-disable-line

  const loadAll = async () => {
    const st = await api(`${prefix}/status`).catch(() => ({connected: false, configured: false}));
    setStatus(st);
    if (st.connected) {
      const [accts, t] = await Promise.all([
        api(`${prefix}/accounts`),
        api(`${prefix}/transactions?match_status=unmatched&limit=200`),
      ]);
      setAccounts(accts || []);
      setTxns(t || []);
    }
  };

  useEffect(() => { loadAll(); }, []); // eslint-disable-line

  useEffect(() => {
    if (!status?.connected) return;
    api(`${prefix}/transactions?match_status=${txnTab}&limit=200`).then(t=>setTxns(t||[])).catch(()=>{});
  }, [txnTab, status?.connected]); // eslint-disable-line

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res = await api(`${prefix}/connect`);
      window.location.href = res.connect_url;
    } catch(e) { alert(`Could not initiate ${bankLabel} connection: ${e.message}`); setConnecting(false); }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await api(`${prefix}/sync`, {method:"POST"});
      await loadAll();
      alert(`Synced ${res.accounts_synced} account(s), ${res.transactions_new} new transaction(s).`);
    } catch(e) { alert("Sync failed: " + e.message); }
    setSyncing(false);
  };

  const handleDisconnect = async () => {
    if (!window.confirm(`Disconnect ${bankLabel}? Transaction history will be kept.`)) return;
    try {
      await api(`${prefix}/disconnect`, {method:"DELETE"});
      setStatus({connected:false, configured:status?.configured}); setAccounts([]); setTxns([]);
    } catch(e) { alert("Disconnect failed: " + e.message); }
  };

  const openMatchModal = async (txn) => {
    let inv = invoices, exp = expenses;
    if (!inv.length) { inv = await api("/invoices/"); setInvoices(inv); }
    if (!exp.length) { exp = await api("/expenses/"); setExpenses(exp); }
    setMatchModal({txn, invoices: inv, expenses: exp});
  };

  const handleMatch = async (txn, invoiceId, expenseId) => {
    try {
      await api(`${prefix}/transactions/${txn.id}/match`, {
        method:"POST", body: JSON.stringify({invoice_id: invoiceId||null, expense_id: expenseId||null}),
      });
      setMatchModal(null);
      const t = await api(`${prefix}/transactions?match_status=${txnTab}&limit=200`);
      setTxns(t || []);
    } catch(e) { alert("Match failed: " + e.message); }
  };

  const handleExclude = async (txn) => {
    try {
      await api(`${prefix}/transactions/${txn.id}/exclude`, {method:"POST"});
      const t = await api(`${prefix}/transactions?match_status=${txnTab}&limit=200`);
      setTxns(t || []);
    } catch(e) { alert(e.message); }
  };

  const handleUnmatch = async (txn) => {
    try {
      await api(`${prefix}/transactions/${txn.id}/unmatch`, {method:"POST"});
      const t = await api(`${prefix}/transactions?match_status=${txnTab}&limit=200`);
      setTxns(t || []);
    } catch(e) { alert(e.message); }
  };

  const fmtAmt = (n) => {
    const abs = Math.abs(n).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g,",");
    return n >= 0 ? `+R${abs}` : `-R${abs}`;
  };

  const confBadge = (c) => {
    if (!c) return null;
    const pct = Math.round(c * 100);
    const col = c >= 0.9 ? C.green : c >= 0.7 ? C.gold : C.red;
    const bg  = c >= 0.9 ? C.greenLt : c >= 0.7 ? C.goldLt : C.redLt;
    return <span style={{background:bg,color:col,borderRadius:4,padding:"1px 6px",fontSize:10,fontWeight:700}}>{pct}%</span>;
  };

  const txnDate = (t) => (t.txn_date || t.date || "").slice(0,10);

  if (status === null) return <div style={{padding:20,color:C.inkMid,fontSize:12}}>Loading {bankLabel} connection…</div>;

  return (
    <div>
      {/* Banners */}
      {banner === "success" && (
        <div style={{background:C.greenLt,border:`1px solid ${C.green}`,borderRadius:10,padding:"12px 16px",marginBottom:12,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <span style={{color:C.green,fontWeight:700,fontSize:13}}>✓ {bankLabel} connected! Transactions imported and matched.</span>
          <button onClick={()=>setBanner(null)} style={{background:"transparent",border:"none",cursor:"pointer",color:C.inkMid,fontSize:16}}>×</button>
        </div>
      )}
      {banner === "error" && (
        <div style={{background:C.redLt,border:`1px solid ${C.red}`,borderRadius:10,padding:"12px 16px",marginBottom:12,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <span style={{color:C.red,fontWeight:700,fontSize:13}}>{bankLabel} connection failed. Please try again.</span>
          <button onClick={()=>setBanner(null)} style={{background:"transparent",border:"none",cursor:"pointer",color:C.inkMid,fontSize:16}}>×</button>
        </div>
      )}

      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <div>
          <div style={{fontSize:14,fontWeight:700,color:C.ink}}>{bankIcon} {bankLabel} Live Feed</div>
          <div style={{fontSize:11,color:C.inkMid,marginTop:2}}>
            {status?.connected
              ? `${status.account_count ?? accounts.length} account(s) · Last synced: ${status.last_synced ? status.last_synced.slice(0,16).replace("T"," ") : "never"}`
              : "Connect your " + bankLabel + " account for automatic transaction imports"}
          </div>
        </div>
        {status?.connected && (
          <div style={{display:"flex",gap:8}}>
            <button onClick={handleSync} disabled={syncing} style={{background:bankColor,color:"#fff",border:"none",borderRadius:8,padding:"7px 14px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit",opacity:syncing?0.6:1}}>
              {syncing ? "Syncing…" : "↻ Sync Now"}
            </button>
            <button onClick={handleDisconnect} style={{background:C.redLt,color:C.red,border:`1px solid ${C.red}40`,borderRadius:8,padding:"7px 12px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
              Disconnect
            </button>
          </div>
        )}
      </div>

      {!status?.connected ? (
        <div style={{background:C.surface,border:`1px dashed ${C.border}`,borderRadius:12,padding:28,textAlign:"center"}}>
          <div style={{fontSize:28,marginBottom:10}}>{bankIcon}</div>
          {status?.configured ? (<>
            <div style={{fontSize:13,fontWeight:700,color:C.ink,marginBottom:6}}>Connect your {bankLabel} account</div>
            <div style={{fontSize:11,color:C.inkMid,lineHeight:1.7,maxWidth:380,margin:"0 auto 16px"}}>
              You'll be redirected to <strong>{bankLabel}'s secure login page</strong> to authorise ZuZan.
              ZuZan never sees your banking password — only read-only transaction data is shared.
            </div>
            <button onClick={handleConnect} disabled={connecting} style={{background:bankColor,color:"#fff",border:"none",borderRadius:10,padding:"10px 24px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",opacity:connecting?0.6:1}}>
              {connecting ? "Redirecting to " + bankLabel + "…" : "🔗 Connect " + bankLabel}
            </button>
          </>) : (<>
            <div style={{fontSize:13,fontWeight:700,color:C.ink,marginBottom:6}}>{bankLabel} — Coming Soon</div>
            <div style={{fontSize:11,color:C.inkMid,lineHeight:1.7,maxWidth:380,margin:"0 auto 14px"}}>
              We're completing our partnership with {bankLabel}. Once live, you'll be able to connect your account and import transactions automatically.
            </div>
            {requested ? (
              <div style={{fontSize:12,color:"#16a34a",fontWeight:600}}>✓ Interest registered — we'll notify you when {bankLabel} goes live.</div>
            ) : (
              <button onClick={()=>{ api(`/banking/${bank}/request-interest`,{method:"POST"}).catch(()=>{}); setRequested(true); }} style={{background:"#f8fafc",color:C.ink,border:`1px solid ${C.border}`,borderRadius:8,padding:"8px 20px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>
                🔔 Notify me when {bankLabel} is available
              </button>
            )}
          </>)}
        </div>
      ) : (
        <div>
          {accounts.length > 0 && (
            <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:16}}>
              {accounts.map(a=>(
                <div key={a.id} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:"10px 14px",minWidth:180,flex:"0 0 auto"}}>
                  <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:4}}>
                    <span style={{fontSize:18}}>{bankIcon}</span>
                    <div>
                      <div style={{fontSize:12,fontWeight:700,color:C.ink}}>{a.account_name || "Account"}</div>
                      <div style={{fontSize:10,color:C.inkMid}}>{a.account_type || a.currency || "ZAR"}</div>
                    </div>
                  </div>
                  <div style={{fontSize:13,fontWeight:700,color:C.ink}}>
                    R {((a.current_balance) || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g,",")}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div style={{display:"flex",borderBottom:`2px solid ${C.border}`,marginBottom:12}}>
            {[["unmatched","Unmatched"],["matched","Matched"],["excluded","Excluded"]].map(([id,label])=>(
              <button key={id} onClick={()=>setTxnTab(id)} style={{
                padding:"7px 16px",fontSize:12,fontWeight:txnTab===id?700:400,cursor:"pointer",
                border:"none",background:"none",fontFamily:"inherit",
                color:txnTab===id?bankColor:C.inkMid,
                borderBottom:`3px solid ${txnTab===id?bankColor:"transparent"}`,marginBottom:-2,
              }}>{label}</button>
            ))}
          </div>

          {txns.length === 0 ? (
            <div style={{padding:24,textAlign:"center",color:C.inkMid,fontSize:12,background:C.surface,borderRadius:12,border:`1px dashed ${C.border}`}}>
              {txnTab === "unmatched" ? "All transactions matched — great work!" : `No ${txnTab} transactions.`}
            </div>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:5}}>
              {txns.map(t=>(
                <div key={t.id} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,padding:"9px 13px",display:"flex",justifyContent:"space-between",alignItems:"center",gap:12}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:2}}>
                      <span style={{fontSize:12,fontWeight:700,color:t.amount>=0?C.green:C.red}}>{fmtAmt(t.amount)}</span>
                      <span style={{fontSize:11,color:C.inkMid}}>{txnDate(t)}</span>
                      {t.match_confidence && txnTab==="matched" && confBadge(t.match_confidence)}
                    </div>
                    <div style={{fontSize:12,color:C.ink,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{t.description || t.reference || "—"}</div>
                  </div>
                  <div style={{display:"flex",gap:5,flexShrink:0}}>
                    {txnTab === "unmatched" && (
                      <>
                        <button onClick={()=>openMatchModal(t)} style={{background:bankColor,color:"#fff",border:"none",borderRadius:6,padding:"5px 10px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>Match</button>
                        <button onClick={()=>handleExclude(t)} style={{background:C.surface,color:C.inkMid,border:`1px solid ${C.border}`,borderRadius:6,padding:"5px 8px",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Ignore</button>
                      </>
                    )}
                    {(txnTab === "matched" || txnTab === "excluded") && (
                      <button onClick={()=>handleUnmatch(t)} style={{background:C.surface,color:C.inkMid,border:`1px solid ${C.border}`,borderRadius:6,padding:"5px 8px",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Undo</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Match Modal */}
      {matchModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div style={{background:C.bg,borderRadius:16,padding:24,width:"100%",maxWidth:480,maxHeight:"80vh",overflowY:"auto",boxShadow:"0 8px 40px rgba(0,0,0,0.2)"}}>
            <div style={{fontWeight:700,fontSize:15,color:C.ink,marginBottom:4}}>Match Transaction</div>
            <div style={{fontSize:12,color:C.inkMid,marginBottom:18}}>
              <strong>{fmtAmt(matchModal.txn.amount)}</strong> · {txnDate(matchModal.txn)} · {matchModal.txn.description||"—"}
            </div>
            {matchModal.txn.amount > 0 && (
              <>
                <div style={{fontSize:11,fontWeight:700,color:C.inkMid,textTransform:"uppercase",marginBottom:8}}>Match to Invoice</div>
                <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:16}}>
                  {matchModal.invoices.filter(i=>["sent","overdue"].includes(i.status)).length === 0
                    ? <div style={{fontSize:12,color:C.inkMid}}>No open invoices.</div>
                    : matchModal.invoices.filter(i=>["sent","overdue"].includes(i.status)).map(inv=>(
                      <button key={inv.id} onClick={()=>handleMatch(matchModal.txn, inv._dbId||inv.id, null)}
                        style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:8,padding:"8px 12px",textAlign:"left",cursor:"pointer",fontFamily:"inherit"}}>
                        <div style={{fontSize:12,fontWeight:700,color:C.ink}}>INV #{inv.invoiceNumber||inv.invoice_number} — {inv.clientName||inv.client_name}</div>
                        <div style={{fontSize:11,color:C.inkMid}}>R {(inv.totalAmount||inv.total_amount||0).toFixed(2)} · {inv.status}</div>
                      </button>
                    ))
                  }
                </div>
              </>
            )}
            {matchModal.txn.amount < 0 && (
              <>
                <div style={{fontSize:11,fontWeight:700,color:C.inkMid,textTransform:"uppercase",marginBottom:8}}>Match to Expense</div>
                <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:16}}>
                  {matchModal.expenses.slice(0,20).map(exp=>(
                    <button key={exp.id} onClick={()=>handleMatch(matchModal.txn, null, exp.id)}
                      style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:8,padding:"8px 12px",textAlign:"left",cursor:"pointer",fontFamily:"inherit"}}>
                      <div style={{fontSize:12,fontWeight:700,color:C.ink}}>{exp.vendor}</div>
                      <div style={{fontSize:11,color:C.inkMid}}>R {(exp.amount||0).toFixed(2)} · {exp.category}</div>
                    </button>
                  ))}
                </div>
              </>
            )}
            <button onClick={()=>setMatchModal(null)} style={{width:"100%",background:"transparent",color:C.inkMid,border:`1px solid ${C.border}`,borderRadius:8,padding:"9px",fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function BankFeeds() {
  const [bankTab, setBankTab] = useState("fnb");

  const BANKS = [
    {id:"fnb",          label:"🟢 FNB",            color:"#007A39"},
    {id:"absa",         label:"🔴 ABSA",           color:"#CC0000"},
    {id:"nedbank",      label:"🟩 Nedbank",         color:"#009A44"},
    {id:"investec",     label:"⬛ Investec",         color:"#1A1A1A"},
    {id:"standardbank", label:"🔵 Standard Bank",   color:"#0033A0"},
    {id:"africanbank",  label:"🟠 African Bank",    color:"#E85C0D"},
    {id:"discovery",    label:"🟣 Discovery Bank",  color:"#6B2A8B"},
  ];

  const active = BANKS.find(b=>b.id===bankTab);

  return (
    <div>
      <div style={{background:"#fffbeb",border:"1px solid #fde68a",borderRadius:10,padding:"12px 16px",marginBottom:20,display:"flex",alignItems:"flex-start",gap:10}}>
        <span style={{fontSize:16}}>🚧</span>
        <div style={{fontSize:12,color:"#92400e"}}>
          <strong>Work in progress.</strong> Automatic bank feeds are coming soon. In the meantime, use the <strong>Bank Import</strong> tab to upload your bank statement and categorise transactions manually.
        </div>
      </div>

      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:18}}>
        <div>
          <div style={{fontSize:15,fontWeight:700,color:C.ink}}>Live Bank Feeds</div>
          <div style={{fontSize:12,color:C.inkMid,marginTop:2}}>Direct OAuth connection — your bank credentials never touch ZuZan</div>
        </div>
      </div>

      {/* Bank selector tabs */}
      <div style={{display:"flex",gap:8,marginBottom:20,flexWrap:"wrap"}}>
        {BANKS.map(({id,label,color})=>(
          <button key={id} onClick={()=>setBankTab(id)} style={{
            padding:"9px 18px",fontSize:12,fontWeight:bankTab===id?700:500,cursor:"pointer",
            border:`2px solid ${bankTab===id?color:C.border}`,
            borderRadius:10,background:bankTab===id?`${color}12`:C.surface,
            fontFamily:"inherit",color:bankTab===id?color:C.inkMid,
          }}>{label}</button>
        ))}
      </div>

      <BankFeedPanel key={bankTab} bank={bankTab} />

      {/* Info footer */}
      <div style={{marginTop:24,background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,padding:"12px 16px",fontSize:11,color:C.inkMid,lineHeight:1.7}}>
        <strong style={{color:C.ink}}>How it works:</strong> When you click "Connect", you are redirected to your bank's own secure login.
        You authorise ZuZan for read-only access. The bank sends transactions directly to ZuZan — your banking password is never shared.
        ZuZan automatically matches transactions to your open invoices and expenses.
      </div>
    </div>
  );
}

// ── BANK IMPORT ───────────────────────────────────────────────────────────────
function BankImport({live = {}, onNavigate}) {
  const [step, setStep] = useState("select");
  const [bank, setBank] = useState(null);
  const [txns, setTxns] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [fileName, setFileName] = useState("");
  const [error, setError] = useState("");
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [search, setSearch] = useState("");
  const [filterType, setFilter] = useState("all");
  const [companyRules, setCompanyRules] = useState([]);
  const fileRef = useRef(null);

  // Fetch learned category rules from the backend on mount
  useEffect(() => {
    const token = localStorage.getItem("zuzan_token");
    if (!token || token.startsWith("demo_")) return;
    fetch(`${BASE_URL}/category-rules/`, {
      headers: {"Authorization": `Bearer ${token}`}
    }).then(r => r.ok ? r.json() : []).then(data => {
      if (Array.isArray(data)) setCompanyRules(data);
    }).catch(() => {});
  }, []);

  // Re-apply learned rules to parsed transactions when rules load after the file
  useEffect(() => {
    if (!companyRules.length || !txns.length) return;
    setTxns(prev => prev.map(t => {
      const d = t.description.toLowerCase();
      const match = companyRules.find(r =>
        (r.txn_type === "any" || r.txn_type === t.type) && d.includes(r.keyword)
      );
      return match ? {...t, category: match.category} : t;
    }));
  }, [companyRules]); // eslint-disable-line

  const processFile = f => {
    if (!bank) { setError("Please select your bank first."); return; }
    if (!f.name.endsWith(".csv")) { setError("Please upload a CSV file."); return; }
    setError(""); setFileName(f.name);
    const bfmt = BANK_FORMATS[bank];
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const rows = e.target.result.split("\n").filter(l => l.trim()).map(l => parseCSVLine(l));
        // Auto-detect first real data row: col[0] must look like a date (YYYY/MM/DD or DD/MM/YYYY etc.)
        const dateRx = /^\d{4}[\/\-]\d{1,2}[\/\-]\d{1,2}$|^\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}$/;
        let dataStart = bfmt.skipRows; // fallback to configured value
        for (let i = 0; i < Math.min(rows.length, 25); i++) {
          if (rows[i] && rows[i][0] && dateRx.test(rows[i][0].trim())) {
            dataStart = i;
            break;
          }
        }
        // ── Column auto-detection from the header row (row just before data start) ──
        // Falls back to bank-specific config if header names aren't recognised.
        let dateCol  = bfmt.dateCol;
        let amtCol   = bfmt.amtCol;
        let descCol  = bfmt.descCol;
        let debitCol  = -1;   // Standard Bank / Nedbank: separate Debit column
        let creditCol = -1;   // Standard Bank / Nedbank: separate Credit column

        if (dataStart > 0) {
          const hdr = rows[dataStart - 1].map(h => (h || "").trim().toLowerCase());
          // Date column
          const di = hdr.findIndex(h => /^date$|trans.*date|value.*date|^datum$/.test(h));
          if (di >= 0) dateCol = di;
          // Description column
          const xi = hdr.findIndex(h => /descr|narrat|particular|reference|^details?$|^memo$|^remarks?$|^information$/.test(h));
          if (xi >= 0) descCol = xi;
          // Single signed-amount column (FNB Business, Capitec, TymeBank, etc.)
          const ai = hdr.findIndex(h => /^amount$|^amt$|^bedrag$/.test(h));
          if (ai >= 0) amtCol = ai;
          // Separate debit + credit columns (Standard Bank, Nedbank, ABSA, Investec)
          const dbi = hdr.findIndex(h => /^debit$|^debit.*amount|^withdrawals?$/.test(h));
          const cri = hdr.findIndex(h => /^credit$|^credit.*amount|^deposits?$/.test(h));
          if (dbi >= 0 && cri >= 0) { debitCol = dbi; creditCol = cri; }
        }

        const minCols = debitCol >= 0
          ? Math.max(dateCol, descCol, debitCol, creditCol) + 1
          : Math.max(dateCol, descCol, amtCol) + 1;

        const data = rows.slice(dataStart);
        const parsed = data
          .filter(r => r.length >= minCols)
          .map((r, i) => {
            let amt, type;
            if (debitCol >= 0) {
              // Banks that export separate Debit / Credit columns (Standard Bank, Nedbank, ABSA, Investec)
              const dv = parseAmt(r[debitCol]);
              const cv = parseAmt(r[creditCol]);
              if (dv > 0)      { amt = dv; type = "debit"; }
              else if (cv > 0) { amt = cv; type = "credit"; }
              else return null; // empty row (e.g. running balance summary line)
            } else {
              // Banks that export a single signed Amount column (FNB, Capitec, TymeBank, etc.)
              amt = parseAmt(r[amtCol]);
              type = amt < 0 ? "debit" : "credit";
              amt = Math.abs(amt);
            }
            const desc = (r[descCol] || "").trim() || "Unknown";
            return {id:i+1, date:parseDt(r[dateCol]), description:desc, amount:amt, type,
                    category:smartCategory(desc, type, companyRules), hasVat:false, vatAmount:0};
          })
          .filter(t => t && t.amount > 0);
        if (!parsed.length) { setError("No transactions found. Check you selected the correct bank."); return; }
        setTxns(parsed);
        setSelected(new Set(parsed.map(t => t.id)));  // pre-select all (expenses + income)
        setStep("preview");
      } catch(err) { setError("Could not read file. Check it is a valid CSV bank statement."); }
    };
    reader.readAsText(f);
  };

  const toggleRow = id => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const updateCat = (id, cat) => {
    const txn = txns.find(t => t.id === id);
    setTxns(prev => prev.map(t => t.id === id ? {...t, category: cat} : t));
    // Persist the correction as a learned rule so future imports auto-apply it
    if (txn && cat) {
      const token = localStorage.getItem("zuzan_token");
      if (token && !token.startsWith("demo_")) {
        fetch(`${BASE_URL}/category-rules/`, {
          method: "POST",
          headers: {"Content-Type": "application/json", "Authorization": `Bearer ${token}`},
          body: JSON.stringify({
            keyword: txn.description.toLowerCase().trim().slice(0, 100),
            category: cat,
            txn_type: txn.type,
          }),
        }).then(r => r.ok ? r.json() : null).then(rule => {
          if (rule && rule.id) {
            setCompanyRules(prev => {
              const idx = prev.findIndex(r => r.id === rule.id);
              if (idx >= 0) { const u = [...prev]; u[idx] = rule; return u; }
              return [rule, ...prev];
            });
          }
        }).catch(() => {});
      }
    }
  };
  const filtered = txns.filter(t => t.description.toLowerCase().includes(search.toLowerCase()) && (filterType === "all" || t.type === filterType));
  const selectedTxns = txns.filter(t => selected.has(t.id));
  const totalDebits = selectedTxns.filter(t => t.type === "debit").reduce((s,t) => s + t.amount, 0);

  const handleImport = async () => {
    setImporting(true);
    setError("");
    try {
      const token = localStorage.getItem("zuzan_token");
      const payload = {
        bank,
        transactions: selectedTxns.map(t => ({
          date:        t.date,
          description: t.description,
          amount:      t.amount,
          type:        t.type,
          category:    t.category,
          has_vat:     t.hasVat || false,
          vat_amount:  t.vatAmount || 0,
        }))
      };

      // If no token or demo token, skip backend and save locally
      if (!token || token.startsWith("demo_")) {
        await new Promise(r => setTimeout(r, 1000));
        setResult({
          expenses_created: selectedTxns.filter(t => t.type === "debit").length,
          expenses_skipped: 0,
          credits_recorded: selectedTxns.filter(t => t.type === "credit").length,
          message: "Saved locally (demo mode)",
        });
        setStep("done");
        return;
      }

      const res = await fetch(`${BASE_URL}/bank/import`, {
        method:  "POST",
        headers: {"Content-Type":"application/json", "Authorization":`Bearer ${token}`},
        body:    JSON.stringify(payload),
      });
      const data = await res.json();

      // Session expired — remove token and prompt re-login (do NOT write a demo_ token)
      if (res.status === 401) {
        localStorage.removeItem("zuzan_token");
        throw new Error("Your session has expired. Please log in again.");
      }

      if (!res.ok) throw new Error(data.detail || "Import failed");
      setResult(data);
      setStep("done");
    } catch(err) {
      // Never show a fake "Import Complete" - if the request did not reach the server, nothing was saved.
      setError(`Import failed - nothing was saved. ${err.message}`);
    } finally {
      setImporting(false);
    }
  };

  const reset = () => { setStep("select"); setTxns([]); setBank(null); setFileName(""); setSelected(new Set()); setResult(null); setError(""); };

  // Reload live data and navigate when import completes
  useEffect(() => {
    if (step === "done") {
      if (live && typeof live.reload === "function") live.reload();
    }
  }, [step]); // eslint-disable-line

  return (
    <div>
      <div style={{marginBottom:20}}>
        <h2 style={{fontFamily:"serif",fontSize:26,color:C.ink,margin:0}}>Bank Import</h2>
        <p style={{fontSize:12,color:C.inkMid,marginTop:3,margin:"3px 0 0"}}>Upload and categorise your bank statement</p>
      </div>
      <div>
      {/* Step progress bar */}
      <div style={{display:"flex",background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,overflow:"hidden",width:"fit-content",marginBottom:20}}>
        {[["select","1. Select Bank"],["preview","2. Preview"],["categorise","3. Categorise"],["done","4. Done"]].map(([id,label],i) => {
          const steps = ["select","preview","categorise","done"];
          const done = steps.indexOf(id) < steps.indexOf(step);
          const active = id === step;
          return <div key={id} style={{padding:"10px 18px",fontSize:12,fontWeight:active?700:400,color:active?C.accent:done?C.green:C.inkDim,background:active?C.accentLt:"transparent",borderRight:i<3?`1px solid ${C.border}`:"none"}}>{done ? "✓ " : ""}{label}</div>;
        })}
      </div>
      {step === "select" && (
        <div>
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,padding:24,marginBottom:16}}>
            <div style={{fontSize:14,fontWeight:700,color:C.ink,marginBottom:16}}>Select Your Bank</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12}}>
              {Object.entries(BANK_FORMATS).map(([key,b]) => (
                <div key={key} onClick={() => setBank(key)} style={{padding:"14px 12px",border:`2px solid ${bank===key?C.accent:C.border}`,borderRadius:12,cursor:"pointer",textAlign:"center",background:bank===key?C.accentLt:C.bg}}>
                  <div style={{fontSize:26,marginBottom:6}}>{b.logo}</div>
                  <div style={{fontSize:12,fontWeight:700,color:bank===key?C.accent:C.ink}}>{b.name}</div>
                </div>
              ))}
            </div>
          </div>
          <div style={{background:C.surface,border:`2px dashed ${C.border}`,borderRadius:16,padding:48,textAlign:"center",cursor:"pointer"}}
            onClick={() => fileRef.current && fileRef.current.click()}
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); processFile(e.dataTransfer.files[0]); }}>
            <div style={{fontSize:48,marginBottom:12}}>📂</div>
            <div style={{fontSize:15,fontWeight:700,color:C.ink,marginBottom:8}}>Upload Bank Statement</div>
            <div style={{fontSize:13,color:C.inkMid,marginBottom:20}}>Drag and drop your CSV file here or click to browse</div>
            <button style={{background:C.accent,color:"#fff",border:"none",borderRadius:10,padding:"10px 24px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Browse Files</button>
            <div style={{fontSize:11,color:C.inkDim,marginTop:14}}>Supported: CSV from FNB, ABSA, Standard Bank, Nedbank, Capitec, Discovery Bank, Investec, TymeBank</div>
            <input ref={fileRef} type="file" accept=".csv" style={{display:"none"}} onChange={e => processFile(e.target.files[0])}/>
          </div>
          {error && <div style={{background:C.redLt,border:`1px solid ${C.red}40`,borderRadius:10,padding:"12px 16px",marginTop:14,fontSize:13,color:C.red}}>Warning: {error}</div>}
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:14,padding:22,marginTop:16}}>
            <div style={{fontSize:13,fontWeight:700,color:C.ink,marginBottom:14}}>How to download your bank statement CSV</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              {[
                ["FNB","Online Banking - Accounts - Transaction History - Export - CSV"],
                ["ABSA","Online Banking - Accounts - View Statements - Download - CSV"],
                ["Standard Bank","Online Banking - Accounts - Transaction History - Export CSV"],
                ["Nedbank","Online Banking - Accounts - Account Activity - Export - CSV"],
                ["Capitec","Capitec App or Online - Transactions - Export - CSV format"],
                ["Discovery Bank","Discovery Bank App - Accounts - Statements - Download CSV"],
                ["Investec","Investec Online - Accounts - Transaction History - Export CSV"],
                ["TymeBank","TymeBank App - Accounts - Transactions - Export - CSV"],
              ].map(([b,s]) => (
                <div key={b} style={{background:C.bg,borderRadius:8,padding:"10px 14px"}}>
                  <div style={{fontSize:12,fontWeight:700,color:C.ink,marginBottom:4}}>{b}</div>
                  <div style={{fontSize:11,color:C.inkMid,lineHeight:1.6}}>{s}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {step === "preview" && (
        <div>
          <div style={{display:"flex",gap:12,marginBottom:16}}>
            <KPI label="Transactions" value={txns.length} color={C.ink} icon="📊"/>
            <KPI label="Expenses" value={fmt(totalDebits)} color={C.red} icon="📤" sub={`${selectedTxns.filter(t=>t.type==="debit").length} selected`}/>
            <KPI label="Bank" value={BANK_FORMATS[bank] ? BANK_FORMATS[bank].name : ""} color={C.blue} icon="🏦" sub={fileName}/>
          </div>
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:14,padding:"12px 16px",marginBottom:14,display:"flex",gap:10,alignItems:"center"}}>
            <input type="text" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} style={{flex:1,padding:"8px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}/>
            {["all","debit","credit"].map(f => <button key={f} onClick={() => setFilter(f)} style={{padding:"7px 14px",borderRadius:8,border:`1px solid ${filterType===f?C.accent:C.border}`,background:filterType===f?C.accentLt:"transparent",color:filterType===f?C.accent:C.inkMid,fontSize:12,fontWeight:filterType===f?700:400,cursor:"pointer",fontFamily:"inherit"}}>{f==="all"?"All":f==="debit"?"Expenses":"Income"}</button>)}
            <span style={{fontSize:12,color:C.inkMid}}>{selected.size} selected</span>
          </div>
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:14,overflow:"hidden",marginBottom:16,maxHeight:400,overflowY:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
              <thead style={{position:"sticky",top:0}}>
                <tr style={{background:C.bg,borderBottom:`1px solid ${C.border}`}}>
                  <th style={{padding:"11px 14px",textAlign:"left",width:40}}>
                    <input type="checkbox" onChange={() => { if (selected.size === filtered.length) setSelected(new Set()); else setSelected(new Set(filtered.map(t => t.id))); }} checked={selected.size === filtered.length && filtered.length > 0}/>
                  </th>
                  {["Date","Description","Type","Amount","Account","VAT","VAT Amt"].map(h => <th key={h} style={{padding:"11px 14px",textAlign:"left",fontSize:10,color:C.inkMid,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase"}}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {filtered.slice(0,100).map(t => (
                  <tr key={t.id} style={{borderBottom:`1px solid ${C.border}20`,opacity:selected.has(t.id)?1:0.4}}>
                    <td style={{padding:"10px 14px"}}><input type="checkbox" checked={selected.has(t.id)} onChange={() => toggleRow(t.id)}/></td>
                    <td style={{padding:"10px 14px",color:C.inkMid,fontSize:11,fontFamily:"monospace"}}>{t.date}</td>
                    <td style={{padding:"10px 14px",color:C.ink,maxWidth:240}}><div style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.description}</div></td>
                    <td style={{padding:"10px 14px"}}><Badge label={t.type==="debit"?"Expense":"Income"} color={t.type==="debit"?C.red:C.green} bg={t.type==="debit"?C.redLt:C.greenLt}/></td>
                    <td style={{padding:"10px 14px",fontWeight:700,color:t.type==="debit"?C.red:C.green}}>{t.type==="debit"?"-":"+"}{fmt(t.amount)}</td>
                    <td style={{padding:"10px 14px"}}>
                      <select value={t.category} onChange={e => updateCat(t.id,e.target.value)} style={{padding:"4px 8px",border:`1px solid ${C.border}`,borderRadius:6,fontSize:11,fontFamily:"inherit",background:C.bg,color:C.ink,minWidth:220,maxWidth:"100%"}}>
                        <option value="">-- Select Account --</option>
                        {COA_GROUPS.map(group => (
                          <optgroup key={group} label={group}>
                            {DEFAULT_COA.filter(a => a.group === group && a.type === "Detail").map(a => (
                              <option key={a.code} value={`${a.code} - ${a.name}`}>{a.code} - {a.name}</option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </td>
                    <td style={{padding:"10px 14px",textAlign:"center"}}>
                      <input type="checkbox" checked={t.hasVat} title="This transaction includes VAT (15%)"
                        onChange={e => setTxns(prev => prev.map(tx => tx.id === t.id ? {...tx, hasVat:e.target.checked, vatAmount:e.target.checked ? Math.round((t.amount / 115) * 15 * 100) / 100 : 0} : tx))}
                        style={{cursor:"pointer",width:16,height:16}}/>
                    </td>
                    <td style={{padding:"10px 14px",fontSize:11,color:t.hasVat?C.accent:C.inkDim,fontWeight:t.hasVat?700:400}}>
                      {t.hasVat ? fmt(t.vatAmount) : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{display:"flex",gap:10}}>
            <button onClick={() => setStep("select")} style={{padding:"11px 22px",border:`1px solid ${C.border}`,borderRadius:10,background:"transparent",color:C.inkMid,fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Back</button>
            <button onClick={() => setStep("categorise")} style={{flex:1,padding:"11px",border:"none",borderRadius:10,background:C.accent,color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Review Categories ({selected.size} transactions)</button>
          </div>
        </div>
      )}
      {step === "categorise" && (() => {
        const debits  = selectedTxns.filter(t => t.type === "debit");
        const credits = selectedTxns.filter(t => t.type === "credit");
        const totalCredits = credits.reduce((s,t) => s + t.amount, 0);
        const TH = h => <th key={h} style={{padding:"10px 14px",textAlign:"left",fontSize:10,color:C.inkMid,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase"}}>{h}</th>;
        const vatToggle = t => e => setTxns(prev => prev.map(tx => tx.id === t.id ? {...tx, hasVat:e.target.checked, vatAmount:e.target.checked ? Math.round((t.amount/1.15)*0.15*100)/100 : 0} : tx));
        const renderRow = (t, isDebit) => (
          <tr key={t.id} style={{borderBottom:`1px solid ${C.border}20`}}>
            <td style={{padding:"10px 14px",color:C.inkMid,fontSize:11,fontFamily:"monospace"}}>{t.date}</td>
            <td style={{padding:"10px 14px",color:C.ink}}>{t.description}</td>
            <td style={{padding:"10px 14px",fontWeight:700,color:isDebit?C.red:C.green}}>{isDebit?"-":"+"}  {fmt(t.amount)}</td>
            <td style={{padding:"10px 14px"}}>
              <select value={t.category} onChange={e => updateCat(t.id,e.target.value)} style={{padding:"5px 8px",border:`1px solid ${C.border}`,borderRadius:6,fontSize:11,fontFamily:"inherit",background:C.bg,color:C.ink,minWidth:220,maxWidth:"100%"}}>
                <option value="">-- Select Account --</option>
                {isDebit ? (
                  COA_GROUPS.map(group => (
                    <optgroup key={group} label={group}>
                      {DEFAULT_COA.filter(a => a.group === group && a.type === "Detail").map(a => (
                        <option key={a.code} value={`${a.code} - ${a.name}`}>{a.code} - {a.name}</option>
                      ))}
                    </optgroup>
                  ))
                ) : (
                  <optgroup label="Revenue">
                    {DEFAULT_COA.filter(a => a.group === "Revenue" && a.type === "Detail").map(a => (
                      <option key={a.code} value={`${a.code} - ${a.name}`}>{a.code} - {a.name}</option>
                    ))}
                  </optgroup>
                )}
              </select>
            </td>
            <td style={{padding:"10px 14px",textAlign:"center"}}>
              <input type="checkbox" checked={t.hasVat||false} title="Includes VAT at 15%" onChange={vatToggle(t)} style={{cursor:"pointer",width:16,height:16}}/>
            </td>
            <td style={{padding:"10px 14px",fontSize:11,color:t.hasVat?C.accent:C.inkDim,fontWeight:t.hasVat?700:400}}>
              {t.hasVat ? fmt(t.vatAmount||0) : "-"}
            </td>
          </tr>
        );
        return (
          <div>
            {/* EXPENSES section */}
            {debits.length > 0 && (
              <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,padding:24,marginBottom:16}}>
                <div style={{fontSize:13,fontWeight:700,color:C.ink,marginBottom:12}}>📤 Expenses ({debits.length})</div>
                <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                    <thead><tr style={{background:C.bg,borderBottom:`1px solid ${C.border}`}}>{["Date","Description","Amount","Account","VAT","VAT Amt"].map(TH)}</tr></thead>
                    <tbody>{debits.map(t => renderRow(t, true))}</tbody>
                  </table>
                </div>
              </div>
            )}
            {/* INCOME section */}
            {credits.length > 0 && (
              <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,padding:24,marginBottom:16}}>
                <div style={{fontSize:13,fontWeight:700,color:C.ink,marginBottom:6}}>📥 Income ({credits.length})</div>
                <div style={{fontSize:11,color:C.inkMid,marginBottom:12}}>Posted as DR Bank / CR Revenue — select the income account for each.</div>
                <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                    <thead><tr style={{background:C.bg,borderBottom:`1px solid ${C.border}`}}>{["Date","Description","Amount","Income Account","VAT","VAT Amt"].map(TH)}</tr></thead>
                    <tbody>{credits.map(t => renderRow(t, false))}</tbody>
                  </table>
                </div>
              </div>
            )}
            {error && <div style={{background:C.redLt,border:`1px solid ${C.red}40`,borderRadius:10,padding:"12px 16px",marginBottom:14,fontSize:13,color:C.red}}>Warning: {error}</div>}
            <div style={{background:C.ink,borderRadius:14,padding:"18px 24px",marginBottom:16,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",marginBottom:4}}>IMPORT SUMMARY</div>
                <div style={{fontSize:14,color:"#fff",fontWeight:600}}>
                  {debits.length > 0 && <span>{debits.length} expenses {fmt(totalDebits)}</span>}
                  {debits.length > 0 && credits.length > 0 && <span style={{color:"rgba(255,255,255,0.4)"}}> · </span>}
                  {credits.length > 0 && <span style={{color:C.greenLt||"#86efac"}}>{credits.length} income {fmt(totalCredits)}</span>}
                </div>
              </div>
              <button onClick={handleImport} disabled={importing} style={{background:importing?C.inkDim:C.accent,color:"#fff",border:"none",borderRadius:10,padding:"12px 24px",fontSize:13,fontWeight:700,cursor:importing?"wait":"pointer",fontFamily:"inherit"}}>{importing ? "Importing..." : "Import to ZuZan"}</button>
            </div>
            <button onClick={() => setStep("preview")} style={{padding:"10px 22px",border:`1px solid ${C.border}`,borderRadius:10,background:"transparent",color:C.inkMid,fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Back</button>
          </div>
        );
      })()}
      {step === "done" && (
        <div style={{textAlign:"center",maxWidth:520,margin:"40px auto"}}>
          <div style={{fontSize:64,marginBottom:16}}>🎉</div>
          <h2 style={{fontFamily:"serif",fontSize:30,color:C.ink,marginBottom:12}}>Import Complete!</h2>
          <p style={{color:C.inkMid,fontSize:14,lineHeight:1.7,marginBottom:28}}>
            {result && result.expenses_created > 0 && <span>Imported <strong style={{color:C.ink}}>{result.expenses_created} expenses</strong> ({fmt(totalDebits)}). </span>}
            {result && result.credits_recorded > 0 && <span>Recorded <strong style={{color:C.green}}>{result.credits_recorded} income entries</strong> to your P&amp;L. </span>}
            {result && result.credits_matched_to_invoices > 0 && <span>Matched <strong style={{color:C.green}}>{result.credits_matched_to_invoices} receipts</strong> to open invoices and marked them paid. </span>}
            {result && result.credits_skipped > 0 && <span style={{color:C.gold}}>{result.credits_skipped} entries skipped as already imported. </span>}
            {result && result.ignored > 0 && <span style={{color:C.gold}}>{result.ignored} line(s) ignored (zero or negative amount). </span>}
            {result && result.possible_invoice_matches && result.possible_invoice_matches.length > 0 && <span style={{display:"block",color:C.gold,marginTop:8}}>⚠ {result.possible_invoice_matches.map(m=>`${m.description} (${fmt(m.amount)}) has the same amount as open invoice ${m.possible_invoices.join(", ")}`).join("; ")}. These were posted as NEW income - if they are invoice payments, the revenue is now counted twice: reverse the income entry and mark the invoice paid instead.</span>}
            {(!result || (result.expenses_created === 0 && result.credits_recorded === 0 && !(result.credits_matched_to_invoices>0))) && <span>Nothing new was imported (all transactions already exist).</span>}
          </p>
          <div style={{display:"flex",gap:10,justifyContent:"center"}}>
            <button onClick={reset} style={{padding:"11px 22px",border:`1px solid ${C.border}`,borderRadius:10,background:"transparent",color:C.inkMid,fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Upload Another</button>
            <button onClick={() => { if (typeof onNavigate === "function") onNavigate("expenses"); }} style={{padding:"11px 24px",border:"none",borderRadius:10,background:C.accent,color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>View Expenses</button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}


// ── INVOICE PORTAL (public — no auth) ─────────────────────────────────────────
function InvoicePortal({token}) {
  const [inv,  setInv]  = useState(null);
  const [err,  setErr]  = useState("");
  const [busy, setBusy] = useState(false);
  const [paid, setPaid] = useState(false);
  const urlParams = new URLSearchParams(window.location.search);
  const justPaid = urlParams.get("paid") === "1";

  useEffect(() => {
    if (!token) { setErr("Invalid payment link."); return; }
    fetch(`${BASE_URL}/portal/invoice/${token}`)
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(data => { setInv(data); if (data.status === "paid") setPaid(true); if (justPaid) setPaid(true); })
      .catch(() => setErr("Invoice not found or link has expired."));
  }, [token]);

  const handlePay = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${BASE_URL}/portal/invoice/${token}/pay`, {method:"POST"});
      if (!res.ok) { const d = await res.json(); setErr(d.detail || "Payment error"); setBusy(false); return; }
      const data = await res.json();
      // Build PayFast form and submit
      const form = document.createElement("form");
      form.method = "POST";
      form.action = data.payfast_url;
      Object.entries(data.payfast_data).forEach(([k,v]) => {
        const inp = document.createElement("input");
        inp.type = "hidden"; inp.name = k; inp.value = v;
        form.appendChild(inp);
      });
      document.body.appendChild(form);
      form.submit();
    } catch(e) { setErr("Payment error — please try again."); setBusy(false); }
  };

  const C2 = {bg:"#FAF7F4",surface:"#fff",accent:"#C8401A",ink:"#1a1a1a",inkMid:"#666",border:"#e5e0d8",green:"#1a7a4a",greenLt:"#edf7f0",gold:"#B7791F",goldLt:"#FEF9E7"};

  if (err) return (
    <div style={{minHeight:"100vh",background:C2.bg,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"system-ui,sans-serif"}}>
      <div style={{textAlign:"center",padding:40}}>
        <div style={{fontSize:48,marginBottom:16}}>⚠️</div>
        <div style={{fontFamily:"serif",fontSize:24,color:C2.ink,marginBottom:8}}>Link Not Found</div>
        <div style={{color:C2.inkMid,fontSize:14}}>{err}</div>
      </div>
    </div>
  );

  if (!inv) return (
    <div style={{minHeight:"100vh",background:C2.bg,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"system-ui,sans-serif"}}>
      <div style={{textAlign:"center"}}><div style={{fontFamily:"serif",fontSize:36,color:C2.ink}}><span style={{color:C2.accent}}>Zu</span>Zan</div><div style={{color:C2.inkMid,marginTop:8}}>Loading invoice...</div></div>
    </div>
  );

  const curSym = ({ZAR:"R",USD:"$",EUR:"€",GBP:"£"})[inv.currency] || inv.currency + " ";
  const isPaid = paid || inv.status === "paid";

  return (
    <div style={{minHeight:"100vh",background:C2.bg,fontFamily:"system-ui,sans-serif",padding:"24px 16px"}}>
      <div style={{maxWidth:560,margin:"0 auto"}}>
        {/* Header */}
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:32}}>
          {inv.company_logo && <img src={inv.company_logo} alt="" style={{height:44,objectFit:"contain"}}/>}
          <div>
            <div style={{fontFamily:"serif",fontSize:20,fontWeight:800,color:C2.ink}}>{inv.company_name || <span><span style={{color:C2.accent}}>Zu</span>Zan</span>}</div>
            {inv.company_email && <div style={{fontSize:12,color:C2.inkMid}}>{inv.company_email}</div>}
          </div>
        </div>

        {/* Invoice card */}
        <div style={{background:C2.surface,borderRadius:20,padding:32,boxShadow:"0 4px 24px rgba(0,0,0,0.06)",marginBottom:24}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:24}}>
            <div>
              <div style={{fontSize:11,fontWeight:700,color:C2.inkMid,textTransform:"uppercase",letterSpacing:1}}>Invoice</div>
              <div style={{fontFamily:"serif",fontSize:26,fontWeight:800,color:C2.ink,marginTop:2}}>{inv.invoice_number}</div>
            </div>
            <div style={{background:isPaid?C2.greenLt:C2.goldLt,color:isPaid?C2.green:C2.gold,padding:"6px 14px",borderRadius:20,fontSize:12,fontWeight:700}}>
              {isPaid ? "✓ PAID" : "PAYMENT DUE"}
            </div>
          </div>

          <div style={{borderTop:`1px solid ${C2.border}`,paddingTop:20,marginBottom:20}}>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:10}}>
              <span style={{color:C2.inkMid,fontSize:13}}>To</span>
              <span style={{fontWeight:600,color:C2.ink,fontSize:13}}>{inv.client_name}</span>
            </div>
            {inv.description && (
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:10}}>
                <span style={{color:C2.inkMid,fontSize:13}}>Description</span>
                <span style={{color:C2.ink,fontSize:13,textAlign:"right",maxWidth:280}}>{inv.description}</span>
              </div>
            )}
            {inv.due_date && (
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:10}}>
                <span style={{color:C2.inkMid,fontSize:13}}>Due Date</span>
                <span style={{fontWeight:600,color:isPaid?C2.green:C2.accent,fontSize:13}}>{new Date(inv.due_date).toLocaleDateString("en-ZA",{day:"numeric",month:"short",year:"numeric"})}</span>
              </div>
            )}
            {inv.currency !== "ZAR" && (
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:10}}>
                <span style={{color:C2.inkMid,fontSize:13}}>Currency</span>
                <span style={{color:C2.ink,fontSize:13}}>{inv.currency} (Rate: {inv.exchange_rate})</span>
              </div>
            )}
          </div>

          <div style={{background:C2.bg,borderRadius:12,padding:"16px 20px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span style={{fontSize:13,color:C2.inkMid,fontWeight:600}}>{inv.status==="partial"?"Balance Due":"Total Due"}</span>
            <span style={{fontFamily:"serif",fontSize:28,fontWeight:800,color:isPaid?C2.green:C2.ink}}>{inv.status==="partial"&&!isPaid?"R"+(inv.balance_due||0).toLocaleString("en-ZA",{minimumFractionDigits:2,maximumFractionDigits:2}):curSym+(inv.total_amount||0).toLocaleString("en-ZA",{minimumFractionDigits:2,maximumFractionDigits:2})}</span>
          </div>
          {inv.status==="partial"&&!isPaid&&(
            <div style={{textAlign:"right",fontSize:11,color:C2.inkMid,marginTop:6}}>Invoice total {curSym}{(inv.total_amount||0).toLocaleString("en-ZA",{minimumFractionDigits:2})} · already paid R{(inv.paid_so_far||0).toLocaleString("en-ZA",{minimumFractionDigits:2})}</div>
          )}
          {inv.currency !== "ZAR" && (
            <div style={{textAlign:"right",fontSize:11,color:C2.inkMid,marginTop:6}}>≈ R{(inv.zar_total||0).toLocaleString("en-ZA",{minimumFractionDigits:2})} ZAR (charged via PayFast)</div>
          )}
        </div>

        {/* Action area */}
        {isPaid ? (
          <div style={{background:C2.greenLt,border:`1px solid ${C2.green}30`,borderRadius:16,padding:24,textAlign:"center"}}>
            <div style={{fontSize:40,marginBottom:8}}>✅</div>
            <div style={{fontWeight:700,color:C2.green,fontSize:17}}>Payment Received</div>
            <div style={{color:C2.inkMid,fontSize:13,marginTop:4}}>Thank you! This invoice has been paid.</div>
          </div>
        ) : (
          <div style={{background:C2.surface,borderRadius:16,padding:24}}>
            {err && <div style={{color:C2.accent,marginBottom:12,fontSize:13}}>{err}</div>}
            <button onClick={handlePay} disabled={busy}
              style={{width:"100%",background:C2.accent,color:"#fff",border:"none",borderRadius:12,padding:"16px",fontSize:16,fontWeight:700,cursor:busy?"not-allowed":"pointer",fontFamily:"inherit",opacity:busy?0.7:1}}>
              {busy ? "Redirecting to PayFast..." : (inv.status==="partial" ? `Pay R${(inv.balance_due||0).toLocaleString("en-ZA",{minimumFractionDigits:2})} Balance Securely` : `Pay ${curSym}${(inv.total_amount||0).toLocaleString("en-ZA",{minimumFractionDigits:2})} Securely`)}
            </button>
            <div style={{textAlign:"center",marginTop:10,fontSize:11,color:C2.inkMid}}>🔒 Secured by PayFast — South Africa's trusted payment gateway</div>
          </div>
        )}

        {inv.notes && (
          <div style={{marginTop:16,background:C2.surface,borderRadius:12,padding:"16px 20px"}}>
            <div style={{fontSize:11,fontWeight:700,color:C2.inkMid,marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Notes</div>
            <div style={{fontSize:13,color:C2.inkMid}}>{inv.notes}</div>
          </div>
        )}

        <div style={{textAlign:"center",marginTop:24,fontSize:11,color:C2.inkMid}}>
          Powered by <a href="https://zuzan.co.za" style={{color:C2.accent,textDecoration:"none",fontWeight:700}}><span style={{color:C2.accent}}>Zu</span>Zan</a>
        </div>
      </div>
    </div>
  );
}

// ── SEND INVOICE PANEL (inside invoice preview modal) ─────────────────────────
function SendInvoicePanel({preview, onSent, isMobile, hasPayfast}) {
  const [sending,    setSending]    = useState(false);
  const [portalUrl,  setPortalUrl]  = useState(preview?.portalToken ? `${window.location.origin}/portal/${preview.portalToken}` : null);
  const [copied,     setCopied]     = useState(false);
  const [sentMsg,    setSentMsg]    = useState("");

  const handleSend = async () => {
    if (!preview?._dbId) return;
    setSending(true); setSentMsg("");
    try {
      const res = await fetch(`${BASE_URL}/invoices/${preview._dbId}/send`, {method:"POST", headers:{"Authorization":"Bearer "+localStorage.getItem("zuzan_token")}});
      const data = await res.json();
      if (res.ok) {
        setPortalUrl(data.portal_url);
        if (onSent) onSent(data.portal_token);
        setSentMsg(data.email_sent ? "✓ Invoice emailed to client" : "✓ Portal link ready (no client email on file)");
      } else {
        setSentMsg(data.detail || "Error — please try again");
      }
    } catch(e) { setSentMsg("Network error"); }
    setSending(false);
  };

  const handleCopy = () => {
    if (!portalUrl) return;
    navigator.clipboard.writeText(portalUrl).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  const C2 = {accent:"#C8401A",green:"#1a7a4a",greenLt:"#edf7f0",gold:"#B7791F",goldLt:"#FEF9E7",inkMid:"#666",border:"#e5e0d8",surface:"#fff",bg:"#FAF7F4",blue:"#1a5fa8",blueLt:"#EBF3FF"};

  return (
    <div style={{background:C2.blueLt,border:`1px solid ${C2.blue}30`,borderRadius:12,padding:"14px 16px",marginTop:16}}>
      <div style={{fontSize:12,fontWeight:700,color:C2.blue,marginBottom:8}}>📧 Send to Client</div>
      {portalUrl ? (
        <div>
          <div style={{display:"flex",gap:6,marginBottom:8}}>
            <input readOnly value={portalUrl} style={{flex:1,fontSize:11,padding:"7px 10px",border:`1px solid ${C2.border}`,borderRadius:8,background:C2.surface,color:C2.inkMid,fontFamily:"monospace",outline:"none"}}/>
            <button onClick={handleCopy} style={{background:copied?C2.green:C2.blue,color:"#fff",border:"none",borderRadius:8,padding:"7px 12px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>{copied?"✓ Copied":"Copy Link"}</button>
          </div>
          <button onClick={()=>window.open(portalUrl,"_blank")} style={{fontSize:11,color:C2.blue,background:"transparent",border:"none",cursor:"pointer",fontFamily:"inherit",padding:0,textDecoration:"underline"}}>Open portal page →</button>
          {sentMsg && <div style={{fontSize:11,color:C2.green,marginTop:6}}>{sentMsg}</div>}
        </div>
      ) : (
        <div>
          <div style={{fontSize:11,color:C2.inkMid,marginBottom:10}}>Generate a secure payment link and optionally email it to the client.</div>
          {!hasPayfast && (
            <div style={{fontSize:11,color:"#c2410c",background:"#fff7ed",border:"1px solid #fed7aa",borderRadius:8,padding:"8px 10px",marginBottom:10}}>
              ⚠️ <strong>PayFast not set up</strong> — clients won't be able to pay online. <a href="#settings" style={{color:"#c2410c",fontWeight:700}} onClick={()=>window.dispatchEvent(new CustomEvent("zuzan-nav",{detail:"settings"}))}>Go to Settings →</a>
            </div>
          )}
          <button onClick={handleSend} disabled={sending} style={{background:C2.blue,color:"#fff",border:"none",borderRadius:8,padding:"8px 16px",fontSize:12,fontWeight:700,cursor:sending?"not-allowed":"pointer",fontFamily:"inherit",opacity:sending?0.7:1}}>
            {sending ? "Sending..." : "Send Invoice"}
          </button>
          {sentMsg && <div style={{fontSize:11,color:C2.accent,marginTop:6}}>{sentMsg}</div>}
        </div>
      )}
    </div>
  );
}

// ── ACCEPT INVITE ─────────────────────────────────────────────────────────────
function AcceptInvite({token, onLogin, onSignIn}) {
  const [info,    setInfo]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");
  const [form,    setForm]    = useState({firstName:"", lastName:"", password:"", confirm:""});
  const [saving,  setSaving]  = useState(false);
  const [done,    setDone]    = useState(false);

  useEffect(() => {
    if (!token) { setError("Invalid invite link."); setLoading(false); return; }
    fetch(`${BASE_URL}/auth/invite/${token}`)
      .then(r => r.json().then(d => r.ok ? d : Promise.reject(d.detail)))
      .then(d => { setInfo(d); setLoading(false); })
      .catch(e => { setError(typeof e === "string" ? e : "This invite link is invalid or has expired."); setLoading(false); });
  }, [token]);

  const handleAccept = async () => {
    if (!form.firstName.trim() || !form.lastName.trim()) { setError("Please enter your name."); return; }
    if (!form.password || form.password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (form.password !== form.confirm) { setError("Passwords do not match."); return; }
    setSaving(true); setError("");
    try {
      const res = await fetch(`${BASE_URL}/auth/accept-invite`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({token, first_name:form.firstName.trim(), last_name:form.lastName.trim(), password:form.password}),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Could not accept invite.");
      localStorage.setItem("zuzan_token", data.access_token);
      setDone(true);
      setTimeout(() => {
        onLogin({
          firstName: data.user.first_name, lastName: data.user.last_name,
          email: data.user.email, companyName: data.company.name,
          logoUrl: data.company.logo_url||"", headerImageUrl: data.company.invoice_header_url||"", invoiceTemplateHtml: data.company.invoice_template_html||"", plan:{name:data.company.plan,id:data.company.plan},
          access_token: data.access_token, trialEnds: data.company.trial_ends, subscriptionStatus: data.company.subscription_status||"trial",
          role: data.user.role||"owner", payrollEnabled: data.company.payroll_enabled||false, afsEnabled: data.company.afs_enabled||false,
          billingExempt: data.company.billing_exempt||false,
        });
      }, 1200);
    } catch(e) { setError(e.message); }
    setSaving(false);
  };

  const card = {maxWidth:420, margin:"80px auto", background:C.surface, border:`1px solid ${C.border}`, borderRadius:16, padding:36, fontFamily:"sans-serif"};
  const inp = {width:"100%", padding:"11px 14px", borderRadius:9, border:`1px solid ${C.border}`, fontSize:14, fontFamily:"inherit", background:C.bg, color:C.ink, outline:"none", boxSizing:"border-box"};

  if (loading) return <div style={{...card, textAlign:"center", color:C.inkMid}}>Checking invite...</div>;

  if (error && !info) return (
    <div style={{...card, textAlign:"center"}}>
      <div style={{fontFamily:"serif",fontSize:28,fontWeight:800,color:C.ink,marginBottom:24}}><span style={{color:C.accent}}>Zu</span>Zan</div>
      <div style={{fontSize:14,color:"#c00",marginBottom:20}}>{error}</div>
      <button onClick={onSignIn} style={{fontSize:13,color:C.accent,background:"none",border:"none",cursor:"pointer",fontFamily:"inherit"}}>Go to sign in →</button>
    </div>
  );

  if (done) return (
    <div style={{...card, textAlign:"center"}}>
      <div style={{fontSize:40,marginBottom:12}}>✅</div>
      <div style={{fontSize:16,fontWeight:700,color:C.ink}}>Welcome to {info?.company_name}!</div>
      <div style={{fontSize:13,color:C.inkMid,marginTop:8}}>Signing you in...</div>
    </div>
  );

  const roleLabels = {admin:"Admin", accountant:"Accountant", payroll:"Payroll Manager", employee:"Employee"};

  return (
    <div style={{minHeight:"100vh",background:C.bg,display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={card}>
        <div style={{textAlign:"center",marginBottom:28}}>
          <div style={{fontFamily:"serif",fontSize:28,fontWeight:800,color:C.ink,marginBottom:4}}><span style={{color:C.accent}}>Zu</span>Zan</div>
          <div style={{fontSize:15,fontWeight:600,color:C.ink}}>You've been invited to join</div>
          <div style={{fontSize:20,fontWeight:700,color:C.accent,marginTop:4}}>{info?.company_name}</div>
          <div style={{display:"inline-block",marginTop:10,padding:"4px 14px",borderRadius:20,background:C.accentLt,color:C.accent,fontSize:12,fontWeight:700}}>
            Role: {roleLabels[info?.role] || info?.role}
          </div>
        </div>

        <div style={{marginBottom:16}}>
          <div style={{display:"flex",gap:10,marginBottom:12}}>
            <div style={{flex:1}}>
              <div style={{fontSize:11,color:C.inkMid,marginBottom:5,fontWeight:600}}>First Name</div>
              <input value={form.firstName} onChange={e=>setForm(v=>({...v,firstName:e.target.value}))} placeholder="First name" style={inp}/>
            </div>
            <div style={{flex:1}}>
              <div style={{fontSize:11,color:C.inkMid,marginBottom:5,fontWeight:600}}>Last Name</div>
              <input value={form.lastName} onChange={e=>setForm(v=>({...v,lastName:e.target.value}))} placeholder="Last name" style={inp}/>
            </div>
          </div>
          <div style={{marginBottom:12}}>
            <div style={{fontSize:11,color:C.inkMid,marginBottom:5,fontWeight:600}}>Email (from invite)</div>
            <input value={info?.email||""} disabled style={{...inp,color:C.inkMid,cursor:"not-allowed"}}/>
          </div>
          <div style={{marginBottom:12}}>
            <div style={{fontSize:11,color:C.inkMid,marginBottom:5,fontWeight:600}}>Password</div>
            <input type="password" value={form.password} onChange={e=>setForm(v=>({...v,password:e.target.value}))} placeholder="Choose a password (6+ chars)" style={inp}/>
          </div>
          <div style={{marginBottom:12}}>
            <div style={{fontSize:11,color:C.inkMid,marginBottom:5,fontWeight:600}}>Confirm Password</div>
            <input type="password" value={form.confirm} onChange={e=>setForm(v=>({...v,confirm:e.target.value}))}
              onKeyDown={e=>e.key==="Enter"&&handleAccept()} placeholder="Confirm password" style={inp}/>
          </div>
        </div>

        {error && <div style={{color:"#c00",fontSize:12,marginBottom:12}}>{error}</div>}

        <button onClick={handleAccept} disabled={saving} style={{width:"100%",padding:"13px",border:"none",borderRadius:10,
          background:saving?C.inkDim:C.accent,color:"#fff",fontSize:15,fontWeight:700,cursor:saving?"wait":"pointer",fontFamily:"inherit",marginBottom:16}}>
          {saving ? "Creating account..." : "Accept & Create Account"}
        </button>
        <div style={{textAlign:"center",fontSize:12,color:C.inkMid}}>
          Already have an account? <span onClick={onSignIn} style={{color:C.accent,cursor:"pointer",fontWeight:600}}>Sign in</span>
        </div>
      </div>
    </div>
  );
}

// ── TEAM SETTINGS ─────────────────────────────────────────────────────────────
function TeamSettings({user}) {
  const [members,    setMembers]    = useState([]);
  const [invites,    setInvites]    = useState([]);
  const [auditLog,   setAuditLog]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [inviteForm, setInviteForm] = useState({email:"", role:"accountant"});
  const [inviting,   setInviting]   = useState(false);
  const [msg,        setMsg]        = useState("");
  const [activeView, setActiveView] = useState("members"); // members | log

  const load = async () => {
    setLoading(true);
    try {
      const [m, inv, log] = await Promise.all([
        api("/auth/team"),
        api("/auth/team/invites"),
        api("/auth/audit-log?limit=30"),
      ]);
      setMembers(m); setInvites(inv); setAuditLog(log);
    } catch(e) { console.warn("Team load error:", e.message); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const sendInvite = async () => {
    if (!inviteForm.email.trim()) { setMsg("Please enter an email address."); return; }
    setInviting(true); setMsg("");
    try {
      await api("/auth/team/invite", {method:"POST", body:JSON.stringify({email:inviteForm.email.trim(), role:inviteForm.role})});
      setMsg(`✓ Invitation sent to ${inviteForm.email}`);
      setInviteForm({email:"", role:"accountant"});
      load();
    } catch(e) { setMsg("Error: " + e.message); }
    setInviting(false);
  };

  const changeRole = async (memberId, newRole) => {
    try {
      await api(`/auth/team/${memberId}`, {method:"PATCH", body:JSON.stringify({role:newRole})});
      load();
    } catch(e) { alert(e.message); }
  };

  const removeMember = async (memberId, email) => {
    if (!window.confirm(`Remove ${email} from your team?`)) return;
    try {
      await api(`/auth/team/${memberId}`, {method:"DELETE"});
      load();
    } catch(e) { alert(e.message); }
  };

  const cancelInvite = async (inviteId) => {
    try {
      await api(`/auth/team/invites/${inviteId}`, {method:"DELETE"});
      load();
    } catch(e) { alert(e.message); }
  };

  const isOwnerOrAdmin = user?.role === "owner" || user?.role === "admin";
  const roleBadge = (role) => {
    const map = {owner:["#1B3A6B","#E8EEF8"],admin:["#C8401A","#FBF0EC"],accountant:["#00878A","#E0F4F5"],payroll:["#7B2D8B","#F5EAF8"],employee:["#888","#F5F5F5"]};
    const [fg, bg] = map[role] || ["#888","#F5F5F5"];
    return <span style={{padding:"2px 9px",borderRadius:20,fontSize:10,fontWeight:700,color:fg,background:bg}}>{role.charAt(0).toUpperCase()+role.slice(1)}</span>;
  };

  const inp = {padding:"9px 12px",borderRadius:8,border:`1px solid ${C.border}`,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"};

  const actionLabel = (action) => {
    const map = {"team.invite_sent":"Invite sent","team.invite_accepted":"Member joined","team.member_removed":"Member removed","team.role_changed":"Role changed","invoice.created":"Invoice created","expense.created":"Expense added"};
    return map[action] || action;
  };

  if (loading) return <div style={{textAlign:"center",padding:40,color:C.inkMid}}>Loading team...</div>;

  return (
    <div>
      {/* View toggle */}
      <div style={{display:"flex",gap:6,marginBottom:20}}>
        {[["members","👥 Members & Invites"],["log","📋 Activity Log"]].map(([id,lbl])=>(
          <button key={id} onClick={()=>setActiveView(id)} style={{padding:"7px 16px",borderRadius:8,border:"none",cursor:"pointer",fontSize:12,fontWeight:activeView===id?700:400,background:activeView===id?C.surface:"transparent",color:activeView===id?C.ink:C.inkMid,fontFamily:"inherit",boxShadow:activeView===id?"0 1px 4px rgba(0,0,0,0.08)":"none"}}>{lbl}</button>
        ))}
      </div>

      {activeView === "members" && <>
        {/* Current members */}
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:14,padding:20,marginBottom:16}}>
          <div style={{fontSize:11,fontWeight:700,color:C.inkMid,letterSpacing:1,textTransform:"uppercase",marginBottom:14}}>Team Members ({members.length})</div>
          {members.map(m=>(
            <div key={m.id} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 0",borderBottom:`1px solid ${C.border}`,last:{borderBottom:"none"}}}>
              <div style={{width:36,height:36,borderRadius:"50%",background:C.accentLt,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:700,color:C.accent}}>
                {(m.first_name||"?")[0].toUpperCase()}
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:600,color:C.ink}}>{m.first_name} {m.last_name} {m.is_self && <span style={{fontSize:10,color:C.inkMid}}>(you)</span>}</div>
                <div style={{fontSize:11,color:C.inkMid,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{m.email}</div>
              </div>
              {roleBadge(m.role)}
              {isOwnerOrAdmin && !m.is_self && m.role !== "owner" && (
                <div style={{display:"flex",gap:6}}>
                  <select value={m.role} onChange={e=>changeRole(m.id,e.target.value)}
                    style={{...inp,fontSize:11,padding:"5px 8px",cursor:"pointer"}}>
                    <option value="admin">Admin</option>
                    <option value="accountant">Accountant</option>
                    <option value="employee">Employee</option>
                  </select>
                  <button onClick={()=>removeMember(m.id,m.email)}
                    style={{padding:"5px 10px",borderRadius:7,border:`1px solid ${C.border}`,background:"transparent",color:"#c00",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Remove</button>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Pending invites */}
        {invites.length > 0 && (
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:14,padding:20,marginBottom:16}}>
            <div style={{fontSize:11,fontWeight:700,color:C.inkMid,letterSpacing:1,textTransform:"uppercase",marginBottom:14}}>Pending Invitations ({invites.length})</div>
            {invites.map(inv=>(
              <div key={inv.id} style={{display:"flex",alignItems:"center",gap:12,padding:"8px 0",borderBottom:`1px solid ${C.border}`}}>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,color:C.ink}}>{inv.email}</div>
                  <div style={{fontSize:11,color:C.inkMid}}>Expires {new Date(inv.expires_at).toLocaleDateString()}</div>
                </div>
                {roleBadge(inv.role)}
                {isOwnerOrAdmin && (
                  <button onClick={()=>cancelInvite(inv.id)} style={{padding:"5px 10px",borderRadius:7,border:`1px solid ${C.border}`,background:"transparent",color:C.inkMid,fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Invite form */}
        {isOwnerOrAdmin && (
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:14,padding:20}}>
            <div style={{fontSize:11,fontWeight:700,color:C.inkMid,letterSpacing:1,textTransform:"uppercase",marginBottom:14}}>Invite a Team Member</div>
            <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"flex-end"}}>
              <div style={{flex:2,minWidth:200}}>
                <div style={{fontSize:11,color:C.inkMid,marginBottom:5}}>Email address</div>
                <input value={inviteForm.email} onChange={e=>setInviteForm(v=>({...v,email:e.target.value}))}
                  onKeyDown={e=>e.key==="Enter"&&sendInvite()}
                  placeholder="colleague@example.com" style={{...inp,width:"100%",boxSizing:"border-box"}}/>
              </div>
              <div style={{flex:1,minWidth:140}}>
                <div style={{fontSize:11,color:C.inkMid,marginBottom:5}}>Role</div>
                <select value={inviteForm.role} onChange={e=>setInviteForm(v=>({...v,role:e.target.value}))} style={{...inp,width:"100%"}}>
                  <option value="admin">Admin</option>
                  <option value="accountant">Accountant</option>
                  <option value="payroll">Payroll Manager</option>
                  <option value="employee">Employee</option>
                </select>
              </div>
              <button onClick={sendInvite} disabled={inviting} style={{padding:"9px 20px",borderRadius:8,border:"none",background:C.accent,color:"#fff",fontSize:13,fontWeight:700,cursor:inviting?"wait":"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>
                {inviting ? "Sending..." : "Send Invite"}
              </button>
            </div>
            {msg && <div style={{fontSize:12,color:msg.startsWith("✓")?C.teal:"#c00",marginTop:10}}>{msg}</div>}
          </div>
        )}
      </>}

      {activeView === "log" && (
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:14,padding:20}}>
          <div style={{fontSize:11,fontWeight:700,color:C.inkMid,letterSpacing:1,textTransform:"uppercase",marginBottom:14}}>Recent Activity</div>
          {auditLog.length === 0 && <div style={{fontSize:13,color:C.inkMid,textAlign:"center",padding:24}}>No activity yet.</div>}
          {auditLog.map(entry=>(
            <div key={entry.id} style={{display:"flex",gap:12,padding:"8px 0",borderBottom:`1px solid ${C.border}`}}>
              <div style={{width:8,height:8,borderRadius:"50%",background:C.accent,marginTop:5,flexShrink:0}}/>
              <div style={{flex:1}}>
                <div style={{fontSize:12,color:C.ink,fontWeight:500}}>{actionLabel(entry.action)}{entry.target_type ? ` — ${entry.target_type} #${entry.target_id||""}` : ""}</div>
                {entry.detail && <div style={{fontSize:11,color:C.inkMid}}>{entry.detail}</div>}
              </div>
              <div style={{fontSize:10,color:C.inkDim,whiteSpace:"nowrap",paddingTop:2}}>
                {entry.user_email && <div>{entry.user_email}</div>}
                <div>{new Date(entry.created_at).toLocaleString()}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── SERVICE CATALOG (Settings tab) ────────────────────────────────────────────
function ServiceCatalog() {
  const EMPTY = {code:"", name:"", description:"", unit_price:"", vat_applicable:true, unit:"each", is_active:true};
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const is = {width:"100%",padding:"9px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"};
  const lb = t => <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:5,textTransform:"uppercase",letterSpacing:0.5}}>{t}</label>;

  const load = () => { setLoading(true); api("/service-items/").then(d => { setItems(Array.isArray(d)?d:[]); }).catch(()=>{}).finally(()=>setLoading(false)); };
  useEffect(load, []);

  const handleSave = async () => {
    if (!form.code || !form.name) { alert("Code and name are required."); return; }
    setSaving(true);
    try {
      const body = {...form, unit_price: parseFloat(form.unit_price)||0};
      if (editId) { await api(`/service-items/${editId}`, {method:"PUT", body:JSON.stringify(body)}); }
      else { await api("/service-items/", {method:"POST", body:JSON.stringify(body)}); }
      setShowForm(false); setForm(EMPTY); setEditId(null); load();
    } catch(e) { alert(e.message || "Save failed."); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this service item?")) return;
    try { await api(`/service-items/${id}`, {method:"DELETE"}); load(); }
    catch(e) { alert("Delete failed."); }
  };

  const startEdit = (item) => {
    setForm({...item, unit_price: String(item.unit_price)});
    setEditId(item.id);
    setShowForm(true);
  };

  const UNITS = ["each","hour","day","month","km","kg","litre","project"];

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
        <div>
          <div style={{fontSize:16,fontWeight:700,color:C.ink}}>Service Catalogue</div>
          <div style={{fontSize:13,color:C.inkMid,marginTop:2}}>Pre-defined services with codes and prices — pick from these when creating invoices.</div>
        </div>
        <button onClick={()=>{setForm(EMPTY);setEditId(null);setShowForm(true);}} style={{padding:"10px 20px",background:C.accent,color:"#fff",border:"none",borderRadius:10,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>+ Add Service</button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,padding:24,marginBottom:20}}>
          <div style={{fontSize:14,fontWeight:700,color:C.ink,marginBottom:16}}>{editId ? "Edit Service" : "New Service"}</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:14,marginBottom:14}}>
            <div>{lb("Code")}<input value={form.code} onChange={e=>setForm(f=>({...f,code:e.target.value.toUpperCase()}))} placeholder="e.g. CONS-001" style={is}/></div>
            <div>{lb("Name")}<input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="Consulting Fee" style={is}/></div>
            <div>{lb("Unit Price (excl. VAT)")}<input type="number" min="0" step="0.01" value={form.unit_price} onChange={e=>setForm(f=>({...f,unit_price:e.target.value}))} placeholder="1500.00" style={is}/></div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr",gap:14,marginBottom:14}}>
            <div>{lb("Description (used on invoice)")}<input value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} placeholder="Professional consulting services" style={is}/></div>
            <div>{lb("Unit")}<select value={form.unit} onChange={e=>setForm(f=>({...f,unit:e.target.value}))} style={is}>{UNITS.map(u=><option key={u} value={u}>{u}</option>)}</select></div>
            <div style={{display:"flex",flexDirection:"column",gap:10,paddingTop:20}}>
              <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",fontSize:13,color:C.ink}}>
                <input type="checkbox" checked={form.vat_applicable} onChange={e=>setForm(f=>({...f,vat_applicable:e.target.checked}))} style={{width:16,height:16}}/>
                VAT @ 15%
              </label>
              <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",fontSize:13,color:C.ink}}>
                <input type="checkbox" checked={form.is_active} onChange={e=>setForm(f=>({...f,is_active:e.target.checked}))} style={{width:16,height:16}}/>
                Active
              </label>
            </div>
          </div>
          <div style={{display:"flex",gap:10}}>
            <button onClick={handleSave} disabled={saving} style={{padding:"10px 22px",background:C.accent,color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",opacity:saving?0.6:1}}>{saving?"Saving…":(editId?"Update":"Create")}</button>
            <button onClick={()=>{setShowForm(false);setForm(EMPTY);setEditId(null);}} style={{padding:"10px 18px",background:"transparent",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,cursor:"pointer",fontFamily:"inherit",color:C.inkMid}}>Cancel</button>
          </div>
        </div>
      )}

      {/* Items table */}
      <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,overflow:"hidden"}}>
        {loading ? <div style={{padding:32,textAlign:"center",color:C.inkMid}}>Loading…</div> : items.length === 0 ? (
          <div style={{padding:48,textAlign:"center",color:C.inkMid}}>
            <div style={{fontSize:32,marginBottom:10}}>📋</div>
            <div style={{fontWeight:600,marginBottom:6}}>No services yet</div>
            <div style={{fontSize:13}}>Add your first service to speed up invoice creation.</div>
          </div>
        ) : (
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
            <thead>
              <tr style={{background:C.bg,borderBottom:`1px solid ${C.border}`}}>
                {["Code","Name","Description","Unit Price","Unit","VAT","Active",""].map(h=><th key={h} style={{textAlign:"left",padding:"10px 16px",fontSize:10,color:C.inkMid,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase"}}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {items.map((item,i) => (
                <tr key={item.id} style={{borderBottom:`1px solid ${C.border}30`,background:i%2===0?C.surface:C.bg}}>
                  <td style={{padding:"11px 16px",fontWeight:700,color:C.accent,fontFamily:"monospace"}}>{item.code}</td>
                  <td style={{padding:"11px 16px",fontWeight:600}}>{item.name}</td>
                  <td style={{padding:"11px 16px",color:C.inkMid,maxWidth:200,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{item.description||"—"}</td>
                  <td style={{padding:"11px 16px",fontWeight:600}}>R{Number(item.unit_price).toFixed(2)}</td>
                  <td style={{padding:"11px 16px",color:C.inkMid}}>{item.unit}</td>
                  <td style={{padding:"11px 16px"}}>{item.vat_applicable ? <span style={{color:C.green,fontWeight:600}}>Yes</span> : <span style={{color:C.inkMid}}>No</span>}</td>
                  <td style={{padding:"11px 16px"}}>{item.is_active ? <span style={{color:C.green,fontWeight:600}}>✓</span> : <span style={{color:C.inkMid}}>—</span>}</td>
                  <td style={{padding:"11px 16px"}}>
                    <div style={{display:"flex",gap:6}}>
                      <button onClick={()=>startEdit(item)} style={{padding:"5px 12px",background:C.goldLt,border:`1px solid ${C.gold}30`,borderRadius:6,color:C.gold,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Edit</button>
                      <button onClick={()=>handleDelete(item.id)} style={{padding:"5px 12px",background:C.redLt,border:`1px solid ${C.red}30`,borderRadius:6,color:C.red,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── SETTINGS ──────────────────────────────────────────────────────────────────
function AppSettings({user, onLogout, onUserUpdate, docTemplate, onTemplateChange, onNavigate, onSubscribeFlow}) {
  const [settingsTab, setSettingsTab] = useState("subscription");
  // Clear Trial Data panel state — hooks must live at component top level,
  // not inside the render-time IIFE below (react-hooks/rules-of-hooks).
  const [showClear, setShowClear] = useState(false);
  const [clearSt, setClearSt] = useState({
    sales:false,expenses:false,customers:false,suppliers:false,
    employees:false,inventory:false,banking:false,budgets_assets:false,documents:false,
  });
  const [clearing, setClearing] = useState(false);
  const [clearDone, setClearDone] = useState(false);
  const [form, setForm] = useState({
    companyName:          user?.companyName          || "",
    regNumber:            user?.regNumber            || "",
    industry:             user?.industry             || "",
    vatNumber:            user?.vatNumber            || "",
    vatRegistered:        user?.vatRegistered !== false,
    bankName:             user?.bankName             || "",
    bankAccount:          user?.bankAccount          || "",
    branchCode:           user?.branchCode           || "",
    bankingDetails:       user?.bankingDetails       || "",
    payfastMerchantId:    user?.payfastMerchantId    || "",
    payfastMerchantKey:   user?.payfastMerchantKey   || "",
    payfastPassphrase:    user?.payfastPassphrase    || "",
    logoUrl:              user?.logoUrl              || "",
    invoiceHeaderUrl:     user?.headerImageUrl       || "",
    invoiceTemplateHtml:  user?.invoiceTemplateHtml  || "",
    cipcRegistrationDate: user?.cipcRegistrationDate || "",
    financialYearEnd:     user?.financialYearEnd     || "02-28",
    // SARS e@syFile fields
    payeRef:     "",
    sdlRef:      "",
    uifRef:      "",
    sic7Code:    "",
    contactName: "",
  });
  const [saved, setSaved] = useState(false);
  const [subInfo, setSubInfo] = useState({status: user?.subscriptionStatus || "trial", trialEnds: user?.trialEnds || null});
  const [showPayrollForm, setShowPayrollForm] = useState(false);
  const [payrollEmpCount, setPayrollEmpCount] = useState(5);
  const [activatingPayroll, setActivatingPayroll] = useState(false);
  const [myClientsData, setMyClientsData] = useState(null);  // full accountant fee response
  const [myClientsLoading, setMyClientsLoading] = useState(false);
  const [overdueReminders, setOverdueReminders] = useState(true);
  const [overdueRemindersSaving, setOverdueRemindersSaving] = useState(false);

  // Fetch client accounts for consolidated billing display
  useEffect(() => {
    setMyClientsLoading(true);
    api("/companies/my-clients").then(data => {
      // API returns either the new object shape {clients,discount_pct,...} or legacy array
      setMyClientsData(Array.isArray(data) ? {clients: data, num_clients: data.length, discount_pct: 0} : data);
    }).catch(() => { setMyClientsData(null); }).finally(() => setMyClientsLoading(false));
  }, []);

  // Pre-fill Settings form from API on mount so credentials persist across logins
  useEffect(() => {
    api("/companies/me").then(data => {
      setForm(f => ({
        ...f,
        companyName:          data.name             || f.companyName,
        regNumber:            data.reg_number        || f.regNumber,
        industry:             data.industry          || f.industry,
        vatNumber:            data.vat_number        || f.vatNumber,
        vatRegistered:        data.vat_registered !== false,
        bankName:             data.bank_name         || f.bankName,
        bankAccount:          data.bank_account      || f.bankAccount,
        branchCode:           data.bank_branch       || f.branchCode,
        payfastMerchantId:    data.payfast_merchant_id  || f.payfastMerchantId,
        payfastMerchantKey:   data.payfast_merchant_key || f.payfastMerchantKey,
        payfastPassphrase:    data.payfast_passphrase   || f.payfastPassphrase,
        logoUrl:              data.logo_url          || f.logoUrl,
        invoiceHeaderUrl:     data.invoice_header_url || f.invoiceHeaderUrl,
        invoiceTemplateHtml:  data.invoice_template_html || f.invoiceTemplateHtml,
        cipcRegistrationDate: data.cipc_registration_date ? data.cipc_registration_date.substring(0,10) : f.cipcRegistrationDate,
        financialYearEnd:     data.financial_year_end    || f.financialYearEnd,
      }));
      setOverdueReminders(data.overdue_reminders_enabled !== false);
      setForm(f => ({
        ...f,
        payeRef:     data.paye_ref     || f.payeRef,
        sdlRef:      data.sdl_ref      || f.sdlRef,
        uifRef:      data.uif_ref      || f.uifRef,
        sic7Code:    data.sic7_code    || f.sic7Code,
        contactName: data.contact_name || f.contactName,
      }));
      setSubInfo({status: data.subscription_status, trialEnds: data.trial_ends, plan: data.plan, billingCycle: data.billing_cycle});
    }).catch(() => {});
  }, []);
  const [profileForm, setProfileForm] = useState({firstName: user?.firstName||user?.first_name||"", lastName: user?.lastName||user?.last_name||""});
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState("");
  const handleProfileSave = async () => {
    if(!profileForm.firstName.trim()||!profileForm.lastName.trim()){setProfileMsg("Both fields are required.");return;}
    setProfileSaving(true); setProfileMsg("");
    try {
      const updated = await api("/auth/me",{method:"PATCH",body:JSON.stringify({first_name:profileForm.firstName.trim(),last_name:profileForm.lastName.trim()})});
      if(onUserUpdate) onUserUpdate({...user, firstName:updated.first_name, lastName:updated.last_name, first_name:updated.first_name, last_name:updated.last_name});
      setProfileMsg("✓ Name updated");
    } catch(e){ setProfileMsg("Failed to save. Try again."); }
    setProfileSaving(false);
  };
  const [showUpgrade,  setShowUpgrade]  = useState(false);
  const [showBilling,  setShowBilling]  = useState(false);
  const [upgradeBilling, setUpgradeBilling] = useState("monthly");
  const [upgrading,    setUpgrading]    = useState(false);
  const [upgradeMsg,   setUpgradeMsg]   = useState("");

  // subscribeWithDataChoice is lifted to ZuZanApp and passed as onSubscribeFlow prop
  // so banners outside AppSettings can also trigger the modal.
  const subscribeWithDataChoice = onSubscribeFlow || pfSubmit;

  const handleUpgrade = async (planId) => {
    setUpgrading(true); setUpgradeMsg("");
    try {
      // 1. Save the chosen plan and billing cycle to the company record
      await api("/companies/me", {method:"PUT", body: JSON.stringify({plan: planId, billing_cycle: upgradeBilling})});
      setUpgradeMsg("Plan selected — redirecting to payment…");
      // 2. Build the PayFast subscription form for this plan and redirect
      const res = await api("/billing/subscribe", {method:"POST"});
      if (res.payfast_url) {
        subscribeWithDataChoice(res.payfast_url, res.payfast_data);
      } else {
        setUpgradeMsg("✓ Plan updated. Use Subscribe Now to complete payment.");
        setTimeout(() => { setShowUpgrade(false); setUpgradeMsg(""); }, 2000);
      }
    } catch(e) { setUpgradeMsg("Failed: " + (e.message||"Please try again.")); }
    setUpgrading(false);
  };

  const handleCancelSub = async () => {
    if (!window.confirm("Are you sure you want to cancel your subscription?\n\nYour PayFast recurring charge will be stopped and your account will revert to the free tier at the end of your current billing period.")) return;
    try {
      await api("/companies/cancel-subscription", {method:"POST"});
      alert("Subscription cancelled. Your PayFast recurring charge has been stopped. You retain full access until the end of your current billing period.");
      onUserUpdate && onUserUpdate();
    } catch(e) {
      alert("Could not cancel subscription: " + (e.message || "Please email support@solutha.co.za."));
    }
  };
  const [pinForm, setPinForm] = useState({newPin:"", confirmPin:""});
  const [pinMsg,  setPinMsg]  = useState("");
  const [pinSaving, setPinSaving] = useState(false);

  const handleSetPin = async () => {
    if (!pinForm.newPin || pinForm.newPin.length < 4) { setPinMsg("PIN must be at least 4 digits."); return; }
    if (pinForm.newPin !== pinForm.confirmPin) { setPinMsg("PINs do not match."); return; }
    setPinSaving(true); setPinMsg("");
    try {
      await api("/companies/payroll-pin", {method:"PUT", body: JSON.stringify({pin: pinForm.newPin})});
      setPinMsg("✓ Payroll PIN updated successfully."); setPinForm({newPin:"", confirmPin:""});
    } catch(e) { setPinMsg("Failed to save PIN. " + (e.message||"")); }
    setPinSaving(false);
  };

  const handleSave = async () => {
    try {
      await api("/companies/me", { method:"PUT", body: JSON.stringify({
        name:                   form.companyName,
        reg_number:             form.regNumber,
        industry:               form.industry,
        vat_number:             form.vatNumber,
        vat_registered:         form.vatRegistered !== false,
        bank_name:              form.bankName,
        bank_account:           form.bankAccount,
        branch_code:            form.branchCode,
        logo_url:               form.logoUrl || null,
        invoice_header_url:     form.invoiceHeaderUrl || null,
        invoice_template_html:  form.invoiceTemplateHtml || null,
        cipc_registration_date: form.cipcRegistrationDate || null,
        financial_year_end:     form.financialYearEnd    || null,
        payfast_merchant_id:    form.payfastMerchantId  || null,
        payfast_merchant_key:   form.payfastMerchantKey || null,
        payfast_passphrase:     form.payfastPassphrase  || null,
        paye_ref:     form.payeRef     || null,
        sdl_ref:      form.sdlRef      || null,
        uif_ref:      form.uifRef      || null,
        sic7_code:    form.sic7Code    || null,
        contact_name: form.contactName || null,
      })});
    } catch(e) { console.warn("Settings save failed:", e.message); }
    // Update local user context
    if (onUserUpdate) onUserUpdate({
      ...user,
      companyName:    form.companyName,
      regNumber:      form.regNumber,
      industry:       form.industry,
      vatNumber:      form.vatNumber,
      vatRegistered:  form.vatRegistered !== false,
      bankName:       form.bankName,
      bankAccount:    form.bankAccount,
      branchCode:     form.branchCode,
      bankingDetails:    `${form.bankName} - Acc: ${form.bankAccount} - Branch: ${form.branchCode}`,
      payfastMerchantId: form.payfastMerchantId,
      payfastMerchantKey:form.payfastMerchantKey,
      logoUrl:              form.logoUrl,
      headerImageUrl:       form.invoiceHeaderUrl,
      invoiceTemplateHtml:  form.invoiceTemplateHtml,
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const inputStyle = {width:"100%",padding:"9px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"};
  const labelStyle = {fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5};

  // ── Settings tab definitions ─────────────────────────────────────────────
  const STABS = [
    {id:"subscription", label:"Subscription", icon:"💳"},
    {id:"profile",      label:"Profile",      icon:"👤"},
    {id:"company",      label:"Company",      icon:"🏢"},
    {id:"security",     label:"Security",     icon:"🔒"},
    {id:"templates",    label:"Templates",    icon:"🎨"},
    {id:"catalog",      label:"Services",     icon:"📋"},
    {id:"developer",    label:"Developer",    icon:"🔑"},
    ...(["owner","admin"].includes(user?.role) ? [{id:"team", label:"Team", icon:"👥"}] : []),
  ];

  return (
    <div>
      {/* Header */}
      <div style={{marginBottom:28}}>
        <h2 style={{fontFamily:"serif",fontSize:26,color:C.ink,margin:"0 0 4px"}}>Settings</h2>
        <p style={{fontSize:13,color:C.inkMid,margin:0}}>Manage your Zuzan account</p>
      </div>

      {/* Tab bar — large, easy to click */}
      <div style={{display:"flex",gap:4,marginBottom:28,background:C.bg,border:`1px solid ${C.border}`,borderRadius:14,padding:5,flexWrap:"wrap"}}>
        {STABS.map(t => (
          <button key={t.id} onClick={()=>setSettingsTab(t.id)} style={{
            flex:"1 1 auto", padding:"11px 18px", borderRadius:10, border:"none",
            cursor:"pointer", fontFamily:"inherit", fontSize:14, fontWeight:settingsTab===t.id?700:500,
            background:settingsTab===t.id?C.surface:"transparent",
            color:settingsTab===t.id?C.ink:C.inkMid,
            boxShadow:settingsTab===t.id?"0 1px 6px rgba(0,0,0,0.09)":"none",
            transition:"all 0.15s", whiteSpace:"nowrap",
          }}>
            {t.icon}&nbsp; {t.label}
          </button>
        ))}
      </div>

      {/* ── SUBSCRIPTION TAB ─────────────────────────────────────────────────── */}
      {settingsTab === "subscription" && <>
      {user?.billingExempt && (
        <div style={{background:"#e8f5e9",border:"1px solid #a5d6a7",borderRadius:16,padding:24,marginBottom:16,display:"flex",alignItems:"center",gap:16}}>
          <div style={{fontSize:32}}>🤝</div>
          <div>
            <div style={{fontSize:16,fontWeight:800,color:"#1b5e20",marginBottom:4}}>Partner Account</div>
            <div style={{fontSize:13,color:"#2e7d32"}}>This is a Zuzan partner account. Billing and subscription limits do not apply.</div>
          </div>
        </div>
      )}
      {!user?.billingExempt && <>
      <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,padding:28,marginBottom:16}}>
        <div style={{fontSize:11,fontWeight:700,color:C.inkMid,letterSpacing:1,textTransform:"uppercase",marginBottom:16}}>Your Plan</div>

        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <div>
            <div style={{fontSize:20,fontWeight:800,color:C.ink,textTransform:"capitalize"}}>
              {subInfo.plan || user?.plan?.name || "Starter"} Plan
              {subInfo.billingCycle && <span style={{fontSize:12,fontWeight:400,color:C.inkMid,marginLeft:8}}>({subInfo.billingCycle})</span>}
            </div>
            <div style={{fontSize:13,color:C.inkMid,marginTop:4}}>
              {subInfo.status === "trial" && subInfo.trialEnds
                ? `Trial ends ${new Date(subInfo.trialEnds).toLocaleDateString("en-ZA",{day:"numeric",month:"long",year:"numeric"})}`
                : subInfo.status === "cancelled" ? "Cancelled — access continues until end of billing period"
                : subInfo.status === "active"    ? "Active subscription"
                : subInfo.status === "expired"   ? "Subscription expired" : ""}
            </div>
          </div>
          <Badge
            label={subInfo.status === "trial" ? "Trial" : subInfo.status === "active" ? "Active" : subInfo.status === "cancelled" ? "Cancelled" : "Expired"}
            color={subInfo.status === "active" ? C.green : subInfo.status === "trial" ? C.gold : C.red}
            bg={subInfo.status === "active" ? C.greenLt : subInfo.status === "trial" ? C.goldLt : C.redLt}
          />
        </div>

        <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:20}}>
          {(subInfo.status === "trial" || subInfo.status === "expired") && (
            <button onClick={async()=>{
              try {
                const res = await api("/billing/subscribe", {method:"POST"});
                if (res.payfast_url) subscribeWithDataChoice(res.payfast_url, res.payfast_data);
              } catch(e) { alert("Could not start subscription. " + (e.message||"")); }
            }} style={{padding:"10px 22px",background:C.green,border:"none",borderRadius:10,color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
              {subInfo.status === "trial" ? "🔑 Subscribe Now" : "🔑 Reactivate Subscription"}
            </button>
          )}
          <button onClick={()=>setShowUpgrade(true)} style={{padding:"10px 22px",background:C.accent,border:"none",borderRadius:10,color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Change Plan</button>
          <button onClick={async()=>{
            try {
              const res = await api("/billing/subscribe", {method:"POST"});
              if (res.payfast_url) pfSubmit(res.payfast_url, res.payfast_data);
            } catch(e) { alert("Could not open payment page. " + (e.message||"")); }
          }} style={{padding:"10px 22px",background:"transparent",border:`1px solid ${C.border}`,borderRadius:10,color:C.inkMid,fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>
            {subInfo.status === "active" ? "💳 Update Payment Method" : "💳 Add Payment Method"}
          </button>
        </div>

        {/* Auto-renewal toggle */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 18px",background:C.bg,borderRadius:12,border:`1px solid ${C.border}`,marginBottom:10}}>
          <div>
            <div style={{fontSize:14,fontWeight:600,color:C.ink}}>Auto-renewal</div>
            <div style={{fontSize:12,color:C.inkMid,marginTop:3}}>
              {subInfo.status === "cancelled"
                ? "Your subscription will not renew. Re-enable before your period ends to keep access."
                : "Renews automatically. You'll be notified 7 days before each renewal."}
            </div>
          </div>
          <button
            onClick={async () => {
              const turningOff = subInfo.status !== "cancelled";
              if (turningOff) {
                if (!window.confirm("Turn off auto-renewal? Your PayFast recurring charge will be stopped and access continues until the end of your current period.")) return;
                try {
                  await api("/companies/cancel-subscription", {method:"POST"});
                  setSubInfo(s => ({...s, status: "cancelled"}));
                  onUserUpdate && onUserUpdate();
                } catch(e) { alert("Could not cancel: " + (e.message || "Please try again.")); }
              } else {
                // Re-enable: just update status locally — they'll need to re-subscribe via PayFast
                try {
                  await api("/companies/me", {method:"PUT", body: JSON.stringify({subscription_status: "active"})});
                  setSubInfo(s => ({...s, status: "active"}));
                } catch(e) { alert("Could not update. Please re-subscribe via the Subscribe button."); }
              }
            }}
            style={{width:48,height:26,borderRadius:13,border:"none",cursor:"pointer",
              background:subInfo.status==="cancelled"?C.border:C.green,
              position:"relative",flexShrink:0,transition:"background 0.2s"}}>
            <span style={{position:"absolute",top:4,width:18,height:18,borderRadius:9,background:"#fff",
              boxShadow:"0 1px 3px rgba(0,0,0,0.2)",transition:"left 0.2s",
              left:subInfo.status==="cancelled"?4:26}}/>
          </button>
        </div>

        {subInfo.status !== "cancelled" && subInfo.status !== "expired" && (
          <button onClick={async()=>{
            if(!window.confirm("Cancel your subscription? Access continues until end of billing period.")) return;
            try {
              await api("/companies/me",{method:"PUT",body:JSON.stringify({subscription_status:"cancelled"})});
              setSubInfo(s=>({...s,status:"cancelled"}));
            } catch(e){ alert("Could not cancel. Email support@solutha.co.za."); }
          }} style={{fontSize:13,color:C.red,background:"none",border:"none",cursor:"pointer",padding:"6px 0",textDecoration:"underline",fontFamily:"inherit"}}>
            Cancel subscription
          </button>
        )}
      </div>

      {/* Add-ons */}
      <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,padding:28,marginBottom:16}}>
        <div style={{fontSize:11,fontWeight:700,color:C.inkMid,letterSpacing:1,textTransform:"uppercase",marginBottom:16}}>Add-ons</div>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"18px 20px",border:`1px solid ${user?.payrollEnabled?C.green:C.border}`,borderRadius:12,background:user?.payrollEnabled?C.greenLt:C.bg}}>
            <div style={{display:"flex",alignItems:"center",gap:16}}>
              <div style={{fontSize:32}}>👥</div>
              <div>
                <div style={{fontSize:15,fontWeight:700,color:C.ink}}>Payroll</div>
                <div style={{fontSize:12,color:C.inkMid,marginTop:2}}>PAYE, UIF &amp; SDL calculations · Payslips · EMP201 reports</div>
                <div style={{fontSize:12,color:C.inkMid,marginTop:2}}>R15.50 per employee · min <strong style={{color:C.ink}}>R99/month</strong></div>
              </div>
            </div>
            {user?.payrollEnabled
              ? <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:6}}>
                  <span style={{padding:"7px 16px",background:C.green,color:"#fff",borderRadius:8,fontSize:13,fontWeight:700}}>✓ Active</span>
                  <button onClick={async()=>{
                    if(!window.confirm("Cancel the Payroll add-on? Payroll data will be retained but payroll features will be disabled immediately.")) return;
                    try{
                      await api("/companies/me",{method:"PUT",body:JSON.stringify({payroll_enabled:false})});
                      if(onUserUpdate) onUserUpdate({...user,payrollEnabled:false});
                    }catch(e){ alert("Could not cancel payroll. Please email support@solutha.co.za."); }
                  }} style={{fontSize:11,color:C.red,background:"none",border:"none",cursor:"pointer",textDecoration:"underline",fontFamily:"inherit",padding:0}}>
                    Cancel Payroll
                  </button>
                </div>
              : showPayrollForm
                ? <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <div>
                      <div style={{fontSize:11,color:C.inkMid,marginBottom:4}}>Employees</div>
                      <input type="number" min={1} max={500} value={payrollEmpCount} onChange={e=>setPayrollEmpCount(+e.target.value)} style={{width:70,padding:"8px 10px",border:`1.5px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",textAlign:"center"}}/>
                    </div>
                    <div>
                      <div style={{fontSize:11,color:C.inkMid,marginBottom:4}}>Cost/mo</div>
                      <div style={{fontSize:13,fontWeight:700,color:C.green,padding:"8px 10px"}}>R{Math.max(99,Math.round(payrollEmpCount*15.5))}/mo</div>
                    </div>
                    <button
                      disabled={activatingPayroll}
                      onClick={async()=>{
                        setActivatingPayroll(true);
                        try{
                          await api("/companies/me",{method:"PUT",body:JSON.stringify({payroll_enabled:true,payroll_employees:payrollEmpCount})});
                          if(onUserUpdate)onUserUpdate({...user,payrollEnabled:true,payrollEmployees:payrollEmpCount});
                          setShowPayrollForm(false);
                        }catch(e){alert("Could not activate payroll.");}
                        setActivatingPayroll(false);
                      }}
                      style={{padding:"9px 16px",background:activatingPayroll?C.inkDim:C.green,border:"none",borderRadius:8,color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>
                      {activatingPayroll?"Activating…":"Confirm"}
                    </button>
                    <button onClick={()=>setShowPayrollForm(false)} style={{padding:"9px 12px",background:"transparent",border:`1px solid ${C.border}`,borderRadius:8,color:C.inkMid,fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>✕</button>
                  </div>
                : <button onClick={()=>setShowPayrollForm(true)} style={{padding:"9px 20px",background:C.green,border:"none",borderRadius:8,color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>+ Add Payroll</button>
            }
          </div>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"18px 20px",border:`1px solid ${user?.afsEnabled?"#7C3AED":C.border}`,borderRadius:12,background:user?.afsEnabled?"#F5F3FF":C.bg}}>
            <div style={{display:"flex",alignItems:"center",gap:16}}>
              <div style={{fontSize:32}}>📑</div>
              <div>
                <div style={{fontSize:15,fontWeight:700,color:C.ink}}>Annual Financial Statements</div>
                <div style={{fontSize:12,color:C.inkMid,marginTop:2}}>Income Statement · Balance Sheet · Cash Flow · Changes in Equity</div>
                <div style={{fontSize:12,color:C.inkMid,marginTop:2}}><strong style={{color:C.ink}}>R1,999/year</strong> — IFRS-compliant, ready for your accountant</div>
              </div>
            </div>
            <button onClick={()=>onNavigate&&onNavigate("fin_statements")} style={{padding:"9px 20px",background:"#7C3AED",border:"none",borderRadius:8,color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>Open AFS →</button>
          </div>
        </div>
      </div>

      {/* ── CLIENT ACCOUNTS (consolidated billing + accountant fee structure) ─── */}
      {(myClientsLoading || (myClientsData && myClientsData.num_clients > 0)) && (
      <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,padding:28,marginBottom:16}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <div style={{fontSize:11,fontWeight:700,color:C.inkMid,letterSpacing:1,textTransform:"uppercase"}}>Client Accounts</div>
          {myClientsData?.discount_pct > 0 && (
            <span style={{fontSize:12,fontWeight:700,color:C.green,background:C.greenLt,borderRadius:20,padding:"4px 12px"}}>
              {myClientsData.discount_pct}% volume discount active
            </span>
          )}
        </div>

        {myClientsLoading ? (
          <div style={{fontSize:13,color:C.inkMid}}>Loading…</div>
        ) : myClientsData && (() => {
          const d = myClientsData;
          const clientTotal = (d.clients||[]).reduce((s,c) => s + (c.discounted_cost ?? c.monthly_cost ?? 0), 0);
          const grandTotal  = (d.own_discounted_cost ?? d.own_base_cost ?? 0) + clientTotal;
          const totalSaving = (d.own_plan_saving ?? 0) + (d.clients||[]).reduce((s,c) => s + ((c.monthly_cost||0) - (c.discounted_cost||c.monthly_cost||0)), 0);

          return (
            <>
              {/* Own plan row */}
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 0",borderBottom:`1px solid ${C.border}`}}>
                <div>
                  <div style={{fontSize:14,fontWeight:600,color:C.ink}}>Your firm</div>
                  <div style={{fontSize:12,color:C.inkMid,marginTop:2,textTransform:"capitalize"}}>
                    {d.own_plan||"starter"} Plan · {d.own_cycle||"monthly"}
                    {d.own_plan_saving > 0 && <span style={{color:C.green,marginLeft:8}}>−R{d.own_plan_saving.toLocaleString("en-ZA")} ({d.num_clients} clients × R20)</span>}
                  </div>
                </div>
                <div style={{textAlign:"right"}}>
                  {d.own_plan_saving > 0 && <div style={{fontSize:11,color:C.inkMid,textDecoration:"line-through"}}>R{(d.own_base_cost||0).toLocaleString("en-ZA")}</div>}
                  <div style={{fontSize:14,fontWeight:700,color:d.own_discounted_cost===0?C.green:C.ink}}>
                    {d.own_discounted_cost===0?"FREE":`R${(d.own_discounted_cost||0).toLocaleString("en-ZA")}/mo`}
                  </div>
                </div>
              </div>

              {/* Client rows */}
              {(d.clients||[]).map(c => {
                const hasDis = c.discount_pct > 0;
                return (
                  <div key={c.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 0",borderBottom:`1px solid ${C.border}`}}>
                    <div>
                      <div style={{fontSize:14,fontWeight:600,color:C.ink}}>{c.name}</div>
                      <div style={{fontSize:12,color:C.inkMid,marginTop:2,textTransform:"capitalize"}}>
                        {c.plan} Plan · {c.billing_cycle}
                        {hasDis && <span style={{color:C.green,marginLeft:6}}>{c.discount_pct}% off</span>}
                      </div>
                    </div>
                    <div style={{textAlign:"right"}}>
                      {hasDis && <div style={{fontSize:11,color:C.inkMid,textDecoration:"line-through"}}>R{(c.monthly_cost||0).toLocaleString("en-ZA")}</div>}
                      <div style={{fontSize:14,fontWeight:700,color:C.ink}}>R{(c.discounted_cost||c.monthly_cost||0).toLocaleString("en-ZA")}/mo</div>
                    </div>
                  </div>
                );
              })}

              {/* Total + saving */}
              <div style={{paddingTop:14}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div style={{fontSize:14,fontWeight:700,color:C.ink}}>Total monthly billing</div>
                  <div style={{fontSize:18,fontWeight:800,color:C.accent}}>R{grandTotal.toLocaleString("en-ZA")}/mo</div>
                </div>
                {totalSaving > 0 && (
                  <div style={{display:"flex",justifyContent:"flex-end",marginTop:4}}>
                    <span style={{fontSize:12,color:C.green,fontWeight:600}}>You save R{totalSaving.toLocaleString("en-ZA")}/mo with the accountant pricing model</span>
                  </div>
                )}
                <div style={{fontSize:12,color:C.inkMid,marginTop:8}}>
                  One consolidated invoice covers your firm and all {d.num_clients} client account{d.num_clients===1?"":"s"}.
                  {d.num_clients < 3 && <span> Add {3-d.num_clients} more client{3-d.num_clients===1?"":"s"} to unlock a 5% volume discount on client fees.</span>}
                  {d.num_clients >= 3 && d.num_clients < 6 && <span> Add {6-d.num_clients} more client{6-d.num_clients===1?"":"s"} to upgrade to 15% off.</span>}
                  {d.num_clients >= 6 && d.num_clients < 10 && <span> Add {10-d.num_clients} more client{10-d.num_clients===1?"":"s"} to unlock 25% off.</span>}
                </div>
              </div>
            </>
          );
        })()}
      </div>
      )}
      </>}
      {/* Clear trial data */}
      {(()=>{
        const OPTS = [
          {key:"sales",          label:"Sales",                 sub:"Invoices, Quotes, Credit Notes, Recurring"},
          {key:"expenses",       label:"Expenses",              sub:"All expense records"},
          {key:"customers",      label:"Customers",             sub:"Customer contact records"},
          {key:"suppliers",      label:"Suppliers & Purchases", sub:"Suppliers and Purchase Orders"},
          {key:"employees",      label:"Employees & Payroll",   sub:"Employees, Payslips, Leave, Clocking"},
          {key:"inventory",      label:"Inventory",             sub:"Stock items and quantities"},
          {key:"banking",        label:"Bank Transactions",     sub:"Imported transactions and connections"},
          {key:"budgets_assets", label:"Budgets & Fixed Assets",sub:"Budget entries and asset register"},
          {key:"documents",      label:"Documents",             sub:"Uploaded files in Document Repository"},
        ];
        const anyTicked = Object.values(clearSt).some(Boolean);
        return (
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,padding:28,marginBottom:16}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:showClear?16:0}}>
              <div>
                <div style={{fontSize:14,fontWeight:700,color:C.ink}}>Clear Trial Data</div>
                <div style={{fontSize:12,color:C.inkMid,marginTop:2}}>Remove test records created during your trial before you start using ZuZan for real.</div>
              </div>
              <button onClick={()=>setShowClear(s=>!s)} style={{padding:"8px 16px",borderRadius:8,border:`1px solid ${C.border}`,background:showClear?C.bg:"transparent",fontSize:12,fontWeight:600,color:C.inkMid,cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>
                {showClear ? "Cancel" : "Clear Data…"}
              </button>
            </div>
            {clearDone && (
              <div style={{background:"#e8f5e9",border:"1px solid #a5d6a7",borderRadius:8,padding:"10px 14px",marginBottom:12,fontSize:12,color:"#1b5e20",fontWeight:600}}>
                ✅ Done — selected trial data has been cleared.
              </div>
            )}
            {showClear && (<>
              {OPTS.map(({key,label,sub})=>(
                <label key={key} style={{display:"flex",alignItems:"flex-start",gap:12,marginBottom:10,cursor:"pointer"}}>
                  <input type="checkbox" checked={clearSt[key]} onChange={e=>setClearSt(p=>({...p,[key]:e.target.checked}))}
                    style={{marginTop:3,width:15,height:15,accentColor:C.red,flexShrink:0}}/>
                  <div>
                    <div style={{fontSize:13,fontWeight:600,color:clearSt[key]?C.red:C.ink}}>{label}</div>
                    <div style={{fontSize:11,color:C.inkDim}}>{sub}</div>
                  </div>
                </label>
              ))}
              {anyTicked && (
                <div style={{background:"#FFF3CD",border:"1px solid #FFC107",borderRadius:8,padding:"9px 12px",fontSize:12,color:"#856404",marginBottom:12}}>
                  ⚠️ Ticked data will be <strong>permanently deleted</strong>. This cannot be undone.
                </div>
              )}
              <button
                disabled={!anyTicked || clearing}
                onClick={async()=>{
                  if(!window.confirm("Delete the selected data permanently? This cannot be undone.")) return;
                  setClearing(true);
                  try {
                    const toDelete = Object.keys(clearSt).filter(k=>clearSt[k]);
                    await api("/companies/clear-data",{method:"POST",body:JSON.stringify({categories:toDelete,confirm:true})});
                    setClearDone(true);
                    setShowClear(false);
                    setClearSt({sales:false,expenses:false,customers:false,suppliers:false,employees:false,inventory:false,banking:false,budgets_assets:false,documents:false});
                    setTimeout(()=>setClearDone(false), 4000);
                  } catch(e) {
                    alert("Error clearing data: " + (e.message||"Please try again."));
                  }
                  setClearing(false);
                }}
                style={{padding:"10px 22px",borderRadius:10,border:"none",
                  background:anyTicked?C.red:"#ccc",color:"#fff",fontSize:13,fontWeight:700,
                  cursor:anyTicked&&!clearing?"pointer":"not-allowed",fontFamily:"inherit",
                  opacity:clearing?0.6:1}}>
                {clearing ? "Clearing…" : "Delete Selected"}
              </button>
            </>)}
          </div>
        );
      })()}

      {/* Account actions */}
      <div style={{background:C.surface,border:`1px solid ${C.red}30`,borderRadius:16,padding:28}}>
        <div style={{fontSize:11,fontWeight:700,color:C.red,letterSpacing:1,textTransform:"uppercase",marginBottom:16}}>Account</div>
        <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
          <button onClick={onLogout} style={{padding:"11px 22px",background:C.redLt,border:`1px solid ${C.red}40`,borderRadius:10,color:C.red,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Sign Out</button>
          {!user?.billingExempt && <button onClick={handleCancelSub} style={{padding:"11px 22px",background:"transparent",border:`1px solid ${C.red}40`,borderRadius:10,color:C.red,fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Cancel Subscription</button>}
        </div>
      </div>
      </>}

      {/* ── PROFILE TAB ──────────────────────────────────────────────────────── */}
      {settingsTab === "profile" && <>
      <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,padding:28,marginBottom:16}}>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:8}}>
          <div style={{fontSize:28}}>👤</div>
          <div>
            <div style={{fontSize:16,fontWeight:700,color:C.ink}}>Your Profile</div>
            <div style={{fontSize:13,color:C.inkMid,marginTop:2}}>Update the name on your account. Your email address cannot be changed here.</div>
          </div>
        </div>
        <div style={{height:1,background:C.border,margin:"16px 0"}}/>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20,marginBottom:20,maxWidth:480}}>
          <div>
            <label style={labelStyle}>First Name</label>
            <input type="text" value={profileForm.firstName} onChange={e=>setProfileForm(f=>({...f,firstName:e.target.value}))} style={inputStyle} placeholder="First name"/>
          </div>
          <div>
            <label style={labelStyle}>Last Name</label>
            <input type="text" value={profileForm.lastName} onChange={e=>setProfileForm(f=>({...f,lastName:e.target.value}))} style={inputStyle} placeholder="Last name"/>
          </div>
        </div>
        <div style={{marginBottom:16,maxWidth:480}}>
          <label style={labelStyle}>Email</label>
          <input type="text" value={user?.email||""} readOnly style={{...inputStyle,background:C.bg,color:C.inkMid,cursor:"default"}}/>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:14}}>
          <button onClick={handleProfileSave} disabled={profileSaving} style={{padding:"11px 26px",background:C.accent,border:"none",borderRadius:10,color:"#fff",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit",opacity:profileSaving?0.6:1}}>
            {profileSaving ? "Saving…" : "Save Name"}
          </button>
          {profileMsg && <span style={{fontSize:13,color:profileMsg.startsWith("✓")?C.green:C.red,fontWeight:600}}>{profileMsg}</span>}
        </div>
      </div>
      </>}

      {/* ── COMPANY TAB ──────────────────────────────────────────────────────── */}
      {settingsTab === "company" && <>
      {/* Logo */}
      <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,padding:28,marginBottom:16}}>
        <div style={{fontSize:11,fontWeight:700,color:C.inkMid,letterSpacing:1,textTransform:"uppercase",marginBottom:16}}>Company Logo</div>
        <div style={{display:"flex",alignItems:"center",gap:20,marginBottom:4}}>
          {form.logoUrl
            ? <img src={form.logoUrl} alt="Logo" style={{height:72,maxWidth:220,objectFit:"contain",borderRadius:10,border:`1px solid ${C.border}`,background:"#fff",padding:6}}/>
            : <div style={{width:120,height:72,borderRadius:10,border:`2px dashed ${C.border}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,color:C.inkMid}}>No logo</div>
          }
          <div>
            <input type="file" accept="image/*" id="logo-upload" style={{display:"none"}} onChange={e=>{
              const file=e.target.files[0]; if(!file)return;
              if(file.size>500000){alert("Logo must be under 500KB.");return;}
              const reader=new FileReader();
              reader.onload=ev=>setForm(v=>({...v,logoUrl:ev.target.result}));
              reader.readAsDataURL(file);
            }}/>
            <label htmlFor="logo-upload" style={{display:"inline-block",padding:"9px 18px",background:C.accentLt,border:`1px solid ${C.accent}40`,borderRadius:8,color:C.accent,fontSize:13,fontWeight:700,cursor:"pointer"}}>Upload Logo</label>
            {form.logoUrl && <button onClick={()=>setForm(v=>({...v,logoUrl:""}))} style={{marginLeft:10,padding:"9px 16px",background:C.redLt,border:`1px solid ${C.red}30`,borderRadius:8,color:C.red,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Remove</button>}
            <div style={{fontSize:12,color:C.inkMid,marginTop:8}}>PNG, JPG or SVG · Max 500 KB · Appears on invoices &amp; quotes</div>
          </div>
        </div>
      </div>

      {/* Invoice Header Image */}
      <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,padding:28,marginBottom:16}}>
        <div style={{fontSize:11,fontWeight:700,color:C.inkMid,letterSpacing:1,textTransform:"uppercase",marginBottom:16}}>Invoice Header Image</div>
        <div style={{fontSize:12,color:C.inkMid,marginBottom:14}}>A wide banner/letterhead image that appears at the top of every invoice and quote. Ideal size: 1200×200 px. JPG or PNG, max 1 MB.</div>
        <div style={{display:"flex",alignItems:"flex-start",gap:20,marginBottom:4}}>
          {form.invoiceHeaderUrl
            ? <img src={form.invoiceHeaderUrl} alt="Invoice header" style={{maxWidth:320,maxHeight:90,objectFit:"cover",borderRadius:8,border:`1px solid ${C.border}`,background:"#fff"}}/>
            : <div style={{width:260,height:70,borderRadius:8,border:`2px dashed ${C.border}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,color:C.inkMid}}>No header image</div>
          }
          <div>
            <input type="file" accept="image/*" id="header-upload" style={{display:"none"}} onChange={e=>{
              const file=e.target.files[0]; if(!file)return;
              if(file.size>1100000){alert("Header image must be under 1 MB.");return;}
              const reader=new FileReader();
              reader.onload=ev=>setForm(v=>({...v,invoiceHeaderUrl:ev.target.result}));
              reader.readAsDataURL(file);
            }}/>
            <label htmlFor="header-upload" style={{display:"inline-block",padding:"9px 18px",background:C.accentLt,border:`1px solid ${C.accent}40`,borderRadius:8,color:C.accent,fontSize:13,fontWeight:700,cursor:"pointer"}}>Upload Header</label>
            {form.invoiceHeaderUrl && <button onClick={()=>setForm(v=>({...v,invoiceHeaderUrl:""}))} style={{marginLeft:10,padding:"9px 16px",background:C.redLt,border:`1px solid ${C.red}30`,borderRadius:8,color:C.red,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Remove</button>}
          </div>
        </div>
      </div>

      {/* Company Details */}
      <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,padding:28,marginBottom:16}}>
        <div style={{fontSize:11,fontWeight:700,color:C.inkMid,letterSpacing:1,textTransform:"uppercase",marginBottom:20}}>Company Details</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:20}}>
          {[{l:"Company Name",k:"companyName"},{l:"Registration Number",k:"regNumber"},{l:"VAT Number",k:"vatNumber"},{l:"CIPC Registration Date",k:"cipcRegistrationDate",type:"date",hint:"Used for Annual Return reminders"}].map(f=>(
            <div key={f.k}>
              <label style={labelStyle}>{f.l}{f.hint&&<span style={{fontWeight:400,textTransform:"none",color:C.inkDim,marginLeft:6}}>{f.hint}</span>}</label>
              <input type={f.type||"text"} value={form[f.k]||""} onChange={e=>setForm(v=>({...v,[f.k]:e.target.value}))} style={inputStyle}/>
            </div>
          ))}
          <div style={{gridColumn:"1 / -1",display:"flex",alignItems:"center",gap:10}}><input type="checkbox" id="coVatReg" checked={form.vatRegistered!==false} onChange={e=>setForm(v=>({...v,vatRegistered:e.target.checked}))} style={{width:16,height:16,cursor:"pointer"}}/><label htmlFor="coVatReg" style={{fontSize:13,color:C.ink,cursor:"pointer",fontWeight:600}}>Registered for VAT <span style={{fontSize:11,color:C.inkMid,fontWeight:400}}>(untick if this business is NOT a VAT vendor: no VAT is then charged on sales or claimed on purchases)</span></label></div>
          <div>
            <label style={labelStyle}>Financial Year End</label>
            <select value={form.financialYearEnd||"02-28"} onChange={e=>setForm(v=>({...v,financialYearEnd:e.target.value}))} style={inputStyle}>
              {[["01-31","31 January"],["02-28","28 February"],["03-31","31 March"],["04-30","30 April"],["05-31","31 May"],["06-30","30 June"],["07-31","31 July"],["08-31","31 August"],["09-30","30 September"],["10-31","31 October"],["11-30","30 November"],["12-31","31 December"]].map(([v,l])=>(
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Industry</label>
            <select value={form.industry||""} onChange={e=>setForm(v=>({...v,industry:e.target.value}))} style={inputStyle}>
              <option value="">General</option>
              <option value="private_security">Private Security (NBCPSS)</option>
              <option value="fuel_station">Fuel Retail (MIBCO Sector 5)</option>
              <option value="construction">Construction</option>
              <option value="retail">Retail</option>
              <option value="hospitality">Hospitality</option>
              <option value="transport">Transport &amp; Logistics</option>
              <option value="healthcare">Healthcare</option>
              <option value="agriculture">Agriculture</option>
              <option value="mining">Mining</option>
              <option value="manufacturing">Manufacturing</option>
              <option value="financial_services">Financial Services</option>
              <option value="it_technology">IT &amp; Technology</option>
              <option value="professional_services">Professional Services</option>
              <option value="other">Other</option>
            </select>
            {form.industry==="private_security" && <div style={{marginTop:6,padding:"8px 12px",background:"#fff7ed",border:"1px solid #fed7aa",borderRadius:8,fontSize:12,color:"#c2410c"}}>🔒 NBCPSS mode active — payroll enforces National Bargaining Council for the Private Security Sector requirements (minimum wages, allowances, levies). Rates effective 1 March 2026 – 28 February 2027.</div>}
            {form.industry==="fuel_station" && <div style={{marginTop:6,padding:"8px 12px",background:"#fffbeb",border:"1px solid #fbbf24",borderRadius:8,fontSize:12,color:"#92400e"}}>⛽ MIBCO Sector 5 mode active — payroll enforces Motor Industry Bargaining Council (Fuel Retail) requirements (minimum hourly rates, medical insurance allowance, Affinity Health scheme). Rates effective 22 December 2025.</div>}
          </div>
        </div>

        {(()=>{
          if(!form.cipcRegistrationDate) return null;
          const reg=new Date(form.cipcRegistrationDate);
          const now=new Date();
          const anniv=new Date(now.getFullYear(),reg.getMonth(),reg.getDate());
          if(anniv<now) anniv.setFullYear(anniv.getFullYear()+1);
          // add 30 business days
          let bd=0,dl=new Date(anniv);
          while(bd<30){dl.setDate(dl.getDate()+1);if(dl.getDay()!==0&&dl.getDay()!==6)bd++;}
          const daysLeft=Math.round((dl-now)/(1000*60*60*24));
          const dlStr=dl.toLocaleDateString("en-ZA",{day:"numeric",month:"long",year:"numeric"});
          const urgent=daysLeft<=30;
          return(
            <div style={{background:urgent?"#fff7ed":C.accentLt,border:`1px solid ${urgent?"#fed7aa":C.accent+"40"}`,borderRadius:10,padding:"10px 16px",marginBottom:16,fontSize:12,color:urgent?"#c2410c":C.inkMid,display:"flex",alignItems:"center",gap:10}}>
              <span style={{fontSize:16}}>{urgent?"⚠️":"📅"}</span>
              <span><strong>CIPC Annual Return deadline:</strong> {dlStr} ({daysLeft} days away) — due within 30 business days of your incorporation anniversary ({reg.toLocaleDateString("en-ZA",{day:"numeric",month:"long"})})</span>
            </div>
          );
        })()}
        <div style={{fontSize:11,fontWeight:700,color:C.inkMid,letterSpacing:1,textTransform:"uppercase",marginBottom:14}}>Banking Details <span style={{fontWeight:400,textTransform:"none",letterSpacing:0}}>(shown on invoices)</span></div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:16,marginBottom:20}}>
          {[{l:"Bank Name",k:"bankName"},{l:"Account Number",k:"bankAccount"},{l:"Branch Code",k:"branchCode"}].map(f=>(
            <div key={f.k}>
              <label style={labelStyle}>{f.l}</label>
              <input value={form[f.k]} onChange={e=>setForm(v=>({...v,[f.k]:e.target.value}))} style={inputStyle}/>
            </div>
          ))}
        </div>

        <div style={{fontSize:11,fontWeight:700,color:C.inkMid,letterSpacing:1,textTransform:"uppercase",marginBottom:14}}>PayFast Online Payments</div>
        {!form.payfastMerchantId && (
          <div style={{background:"#fff7ed",border:"1px solid #fed7aa",borderRadius:10,padding:"12px 16px",marginBottom:14,fontSize:12,color:"#c2410c",display:"flex",alignItems:"flex-start",gap:10}}>
            <span style={{fontSize:16}}>⚠️</span>
            <div><strong>Setup required to receive online payments.</strong> Without your PayFast credentials, clients cannot pay invoices online. <a href="https://www.payfast.co.za/registration" target="_blank" rel="noreferrer" style={{color:"#c2410c",fontWeight:700}}>Create a free PayFast account →</a> then enter your Merchant ID and Key below.</div>
          </div>
        )}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:14}}>
          {[{l:"Merchant ID",k:"payfastMerchantId",p:"10000100"},{l:"Merchant Key",k:"payfastMerchantKey",p:"46f0cd694581a"}].map(f=>(
            <div key={f.k}>
              <label style={labelStyle}>{f.l}</label>
              <input placeholder={f.p} value={form[f.k]||""} onChange={e=>setForm(v=>({...v,[f.k]:e.target.value}))} style={inputStyle}/>
            </div>
          ))}
        </div>
        <div style={{marginBottom:16}}>
          <label style={labelStyle}>Passphrase <span style={{fontWeight:400,textTransform:"none"}}>(optional)</span></label>
          <input type="password" placeholder="Leave blank if not set in PayFast" value={form.payfastPassphrase||""} onChange={e=>setForm(v=>({...v,payfastPassphrase:e.target.value}))} style={inputStyle}/>
        </div>
        <div style={{background:C.goldLt,borderRadius:10,padding:"12px 16px",marginBottom:20,fontSize:12,color:C.inkMid}}>
          Get your credentials at <strong>payfast.co.za → Account → Settings → API Credentials</strong>. Each client uses their own PayFast account — Zuzan never touches your funds.
        </div>

        <div style={{fontSize:11,fontWeight:700,color:C.inkMid,letterSpacing:1,textTransform:"uppercase",marginBottom:14}}>SARS Returns <span style={{fontWeight:400,textTransform:"none",letterSpacing:0}}>(IRP5 / EMP501 / e@syFile export)</span></div>
        <div style={{background:C.accentLt,borderRadius:10,padding:"10px 14px",marginBottom:14,fontSize:12,color:C.inkMid}}>
          Required to generate e@syFile-compatible IRP5 import files. PAYE Reference is mandatory — SDL and UIF refs only needed if registered. SIC7 code: your industry classification (e.g. <strong>85100</strong> for accounting). Contact name appears on the EMP501.
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}}>
          {[
            {l:"PAYE Reference Number",k:"payeRef",  p:"7000000000",   hint:"10 digits, starts with 7 — mandatory"},
            {l:"Contact Name (SARS)",   k:"contactName",p:"Jane Smith",hint:"Person responsible for payroll"},
            {l:"SDL Reference Number",  k:"sdlRef",   p:"L000000000",   hint:"Starts with L — if registered"},
            {l:"UIF Reference Number",  k:"uifRef",   p:"U000000000",   hint:"Starts with U — if registered"},
            {l:"SIC7 Industry Code",    k:"sic7Code", p:"85100",        hint:"5-char SARS industry code"},
          ].map(f=>(
            <div key={f.k}>
              <label style={labelStyle}>{f.l} <span style={{fontWeight:400,textTransform:"none",color:C.inkDim}}>{f.hint}</span></label>
              <input placeholder={f.p} value={form[f.k]||""} onChange={e=>setForm(v=>({...v,[f.k]:e.target.value}))} style={inputStyle}/>
            </div>
          ))}
        </div>

        {/* Overdue Invoice Reminders toggle */}
        <div style={{borderTop:`1px solid ${C.border}`,paddingTop:20,marginTop:4,marginBottom:20}}>
          <div style={{fontSize:11,fontWeight:700,color:C.inkMid,letterSpacing:1,textTransform:"uppercase",marginBottom:12}}>Automated Email Reminders</div>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",background:overdueReminders?C.accentLt:"#f5f5f5",border:`1px solid ${overdueReminders?C.accent+"40":C.border}`,borderRadius:12,padding:"14px 18px"}}>
            <div>
              <div style={{fontSize:14,fontWeight:600,color:C.ink}}>Overdue invoice reminders</div>
              <div style={{fontSize:12,color:C.inkMid,marginTop:2}}>Automatically email clients at 7, 14 and 30 days past due date</div>
            </div>
            <button
              disabled={overdueRemindersSaving}
              onClick={async()=>{
                const next=!overdueReminders;
                setOverdueRemindersSaving(true);
                try{
                  await api("/billing/overdue-reminders",{method:"PATCH",body:JSON.stringify({enabled:next})});
                  setOverdueReminders(next);
                }catch(e){alert("Could not update setting. Please try again.");}
                finally{setOverdueRemindersSaving(false);}
              }}
              style={{flexShrink:0,width:52,height:28,borderRadius:14,border:"none",cursor:overdueRemindersSaving?"wait":"pointer",background:overdueReminders?C.accent:"#ccc",transition:"background 0.2s",position:"relative",padding:0}}
            >
              <span style={{position:"absolute",top:3,left:overdueReminders?26:3,width:22,height:22,borderRadius:"50%",background:"#fff",transition:"left 0.2s",boxShadow:"0 1px 4px rgba(0,0,0,0.2)"}}/>
            </button>
          </div>
        </div>

        <div style={{display:"flex",alignItems:"center",gap:14}}>
          <button onClick={handleSave} style={{padding:"11px 26px",background:C.accent,border:"none",borderRadius:10,color:"#fff",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Save Changes</button>
          {saved && <span style={{fontSize:13,color:C.green,fontWeight:600}}>✓ Saved</span>}
        </div>
      </div>
      </>}

      {/* ── SECURITY TAB ─────────────────────────────────────────────────────── */}
      {settingsTab === "security" && <>
      {/* Payroll PIN */}
      <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,padding:28,marginBottom:16}}>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:8}}>
          <div style={{fontSize:28}}>🔒</div>
          <div>
            <div style={{fontSize:16,fontWeight:700,color:C.ink}}>Payroll PIN</div>
            <div style={{fontSize:13,color:C.inkMid,marginTop:2}}>Restrict access to salary data. Anyone opening Payroll must enter this PIN first.</div>
          </div>
        </div>
        <div style={{height:1,background:C.border,margin:"16px 0"}}/>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20,marginBottom:20,maxWidth:480}}>
          <div>
            <label style={labelStyle}>New PIN <span style={{fontWeight:400,textTransform:"none"}}>(4–8 digits)</span></label>
            <input type="password" inputMode="numeric" maxLength={8} placeholder="••••" value={pinForm.newPin}
              onChange={e=>setPinForm(f=>({...f,newPin:e.target.value.replace(/\D/g,"")}))}
              style={{...inputStyle,fontSize:20,letterSpacing:8,textAlign:"center",padding:"12px"}}/>
          </div>
          <div>
            <label style={labelStyle}>Confirm PIN</label>
            <input type="password" inputMode="numeric" maxLength={8} placeholder="••••" value={pinForm.confirmPin}
              onChange={e=>setPinForm(f=>({...f,confirmPin:e.target.value.replace(/\D/g,"")}))}
              style={{...inputStyle,fontSize:20,letterSpacing:8,textAlign:"center",padding:"12px"}}/>
          </div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:14}}>
          <button onClick={handleSetPin} disabled={pinSaving} style={{padding:"11px 26px",background:C.accent,border:"none",borderRadius:10,color:"#fff",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit",opacity:pinSaving?0.6:1}}>
            {pinSaving ? "Saving…" : "Set PIN"}
          </button>
          {pinMsg && <span style={{fontSize:13,color:pinMsg.startsWith("✓")?C.green:C.red,fontWeight:600}}>{pinMsg}</span>}
        </div>
        <div style={{marginTop:16,fontSize:12,color:C.inkMid,background:C.bg,borderRadius:8,padding:"10px 14px"}}>
          💡 Forgot your PIN? Simply set a new one here — no old PIN required.
        </div>
      </div>

      </>}

      {/* ── TEMPLATES TAB ────────────────────────────────────────────────────── */}
      {settingsTab === "templates" && (
        <div>
          {/* Built-in layout picker */}
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,padding:28,marginBottom:20}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
              <div>
                <div style={{fontSize:16,fontWeight:700,color:C.ink,marginBottom:4}}>Document Templates</div>
                <div style={{fontSize:13,color:C.inkMid}}>Customise how your invoices and quotes look when printed or shared.</div>
              </div>
              <button onClick={()=>{if(onTemplateChange)onTemplateChange(DEFAULT_DOC_TEMPLATE);}} style={{padding:"9px 18px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,color:C.inkMid,cursor:"pointer",fontFamily:"inherit"}}>Reset to Default</button>
            </div>
            <DocumentTemplateSettings template={docTemplate} onChange={tmpl=>{if(onTemplateChange)onTemplateChange(tmpl);}}/>
          </div>
          {/* Custom HTML template upload */}
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,padding:28}}>
            <div style={{fontSize:16,fontWeight:700,color:C.ink,marginBottom:4}}>Custom HTML Invoice Template</div>
            <div style={{fontSize:13,color:C.inkMid,marginBottom:16}}>
              Upload your own HTML invoice layout. Use <code style={{background:C.bg,padding:"1px 5px",borderRadius:4,fontSize:12}}>{"{{variable}}"}</code> placeholders — Zuzan will fill them in automatically.
              {" "}<a href="#" onClick={e=>{e.preventDefault(); const tpl=document.createElement("a");tpl.href="data:text/html;charset=utf-8,"+encodeURIComponent(SOLID_MATTER_HTML_TEMPLATE);tpl.download="InvoiceTemplate.html";tpl.click();}} style={{color:C.accent,textDecoration:"none",fontWeight:600}}>Download starter template ↓</a>
            </div>
            <div style={{marginBottom:12}}>
              <div style={{display:"flex",gap:10,marginBottom:10}}>
                <label style={{display:"inline-flex",alignItems:"center",gap:6,padding:"9px 18px",background:C.accentLt,border:`1px solid ${C.accent}40`,borderRadius:8,color:C.accent,fontSize:13,fontWeight:700,cursor:"pointer"}}>
                  📂 Upload HTML File
                  <input type="file" accept=".html,text/html" style={{display:"none"}} onChange={e=>{
                    const file=e.target.files&&e.target.files[0];
                    if(!file)return;
                    const reader=new FileReader();
                    reader.onload=ev=>setForm(v=>({...v,invoiceTemplateHtml:ev.target.result}));
                    reader.readAsText(file);
                    e.target.value="";
                  }}/>
                </label>
                {form.invoiceTemplateHtml && <button onClick={()=>setForm(v=>({...v,invoiceTemplateHtml:""}))} style={{padding:"9px 16px",background:C.redLt,border:`1px solid ${C.red}30`,borderRadius:8,color:C.red,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>✕ Remove</button>}
              </div>
              <div style={{fontSize:12,color:C.inkMid,marginBottom:6}}>Or paste HTML directly:</div>
              <textarea
                value={form.invoiceTemplateHtml}
                onChange={e=>setForm(v=>({...v,invoiceTemplateHtml:e.target.value}))}
                placeholder={"<!DOCTYPE html>\n<html>...</html>"}
                style={{width:"100%",height:160,padding:"9px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:12,fontFamily:"monospace",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box",resize:"vertical"}}
              />
            </div>
            {form.invoiceTemplateHtml && (
              <div style={{padding:"10px 14px",background:C.accentLt,border:`1px solid ${C.accent}30`,borderRadius:8,fontSize:13,color:C.accent,marginBottom:4}}>
                ✓ Custom template loaded ({Math.round(form.invoiceTemplateHtml.length/1024)} KB). Click <strong>Save Changes</strong> in Company Settings to activate.
              </div>
            )}
            <div style={{marginTop:12,fontSize:12,color:C.inkMid,lineHeight:1.6}}>
              <strong>Available placeholders:</strong>{" "}
              {["{{company_name}}","{{company_address}}","{{company_vat}}","{{company_reg}}","{{company_phone}}","{{company_email}}","{{company_bank_name}}","{{company_bank_account}}","{{company_bank_branch}}",
                "{{invoice_number}}","{{issue_date}}","{{due_date}}","{{client_name}}","{{client_email}}",
                "{{tour_ref}}","{{quote_ref}}","{{tax_ref}}","{{travel_date}}","{{pax_count}}","{{passenger_name}}",
                "{{subtotal}}","{{vat_amount}}","{{total}}","{{description}}","{{notes}}","{{items_table}}"].map(p=>(
                <code key={p} style={{background:C.bg,padding:"1px 4px",borderRadius:3,fontSize:11,marginRight:4,display:"inline-block",marginBottom:2}}>{p}</code>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── SERVICE CATALOG TAB ──────────────────────────────────────────────── */}
      {settingsTab === "catalog" && <ServiceCatalog/>}

      {/* ── DEVELOPER TAB ────────────────────────────────────────────────────── */}
      {settingsTab === "developer" && <Developer/>}

      {/* ── TEAM TAB ─────────────────────────────────────────────────────────── */}
      {settingsTab === "team" && <TeamSettings user={user}/>}
      {/* ── Upgrade Plan Modal ── */}
      {showUpgrade && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:900,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
          <div style={{background:C.surface,borderRadius:20,padding:32,width:"100%",maxWidth:520,boxShadow:"0 8px 40px rgba(0,0,0,0.18)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:24}}>
              <div style={{fontSize:18,fontWeight:800,color:C.ink}}>Change Plan</div>
              <button onClick={()=>{setShowUpgrade(false);setUpgradeMsg("");}} style={{background:"none",border:"none",cursor:"pointer",fontSize:20,color:C.inkMid}}>✕</button>
            </div>
            <div style={{display:"flex",gap:8,marginBottom:24}}>
              {["monthly","annual"].map(t=>(
                <button key={t} onClick={()=>setUpgradeBilling(t)} style={{flex:1,padding:"7px 0",borderRadius:8,border:`1px solid ${C.border}`,background:upgradeBilling===t?C.accent:"transparent",color:upgradeBilling===t?"#fff":C.inkMid,fontWeight:700,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>
                  {t==="monthly"?"Monthly":"Annual (2 months free)"}
                </button>
              ))}
            </div>
            {PLANS.map((p, pIdx)=>{
              const price = upgradeBilling==="annual" ? Math.round(p.annual/12) : p.monthly;
              const currentPlanId = user?.plan?.id||"starter";
              const isCurrent = currentPlanId === p.id;
              const currentIdx = PLANS.findIndex(x => x.id === currentPlanId);
              const isDowngrade = pIdx < currentIdx;
              const btnBg    = isCurrent ? C.border : isDowngrade ? C.red    : C.green;
              const btnColor = isCurrent ? C.inkMid : "#fff";
              const btnLabel = isCurrent ? "Current Plan" : upgrading ? "Updating…" : isDowngrade ? "Downgrade" : "Upgrade";
              return (
                <div key={p.id} style={{border:`2px solid ${isCurrent?C.accent:isDowngrade?C.red+"40":C.border}`,borderRadius:12,padding:16,marginBottom:12,background:isCurrent?C.accentLt:"transparent"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div>
                      <div style={{fontSize:14,fontWeight:800,color:C.ink}}>{p.icon} {p.name}</div>
                      <div style={{fontSize:11,color:C.inkMid,marginTop:3}}>{p.features[0]} · {p.features[1]}</div>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontSize:18,fontWeight:800,color:p.color||C.accent}}>R{price.toLocaleString()}<span style={{fontSize:11,fontWeight:400,color:C.inkMid}}>/mo</span></div>
                      {upgradeBilling==="annual"&&<div style={{fontSize:10,color:C.inkMid}}>billed R{(p.annual).toLocaleString()}/yr</div>}
                    </div>
                  </div>
                  <button
                    onClick={async()=>{
                      if(isDowngrade && !window.confirm(`Downgrade to ${p.name}? Some features may become unavailable immediately.`)) return;
                      handleUpgrade(p.id);
                    }}
                    disabled={upgrading||isCurrent}
                    style={{marginTop:12,width:"100%",padding:"9px 0",borderRadius:8,border:"none",background:btnBg,color:btnColor,fontWeight:700,fontSize:12,cursor:isCurrent?"default":"pointer",fontFamily:"inherit",opacity:upgrading?0.6:1}}
                  >{btnLabel}</button>
                </div>
              );
            })}
            {upgradeMsg && <div style={{marginTop:12,fontSize:13,color:upgradeMsg.startsWith("✓")?C.green:C.red,fontWeight:600,textAlign:"center"}}>{upgradeMsg}</div>}
          </div>
        </div>
      )}

      {/* ── Manage Billing Modal ── */}
      {showBilling && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:900,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
          <div style={{background:C.surface,borderRadius:20,padding:32,width:"100%",maxWidth:440,boxShadow:"0 8px 40px rgba(0,0,0,0.18)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:24}}>
              <div style={{fontSize:18,fontWeight:800,color:C.ink}}>Billing</div>
              <button onClick={()=>setShowBilling(false)} style={{background:"none",border:"none",cursor:"pointer",fontSize:20,color:C.inkMid}}>✕</button>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:20}}>
              <div style={{background:C.bg,borderRadius:10,padding:14}}>
                <div style={{fontSize:10,fontWeight:700,color:C.inkMid,textTransform:"uppercase",letterSpacing:0.5,marginBottom:4}}>Current Plan</div>
                <div style={{fontSize:15,fontWeight:800,color:C.ink}}>{user?.plan?.name||"Professional"}</div>
              </div>
              <div style={{background:C.bg,borderRadius:10,padding:14}}>
                <div style={{fontSize:10,fontWeight:700,color:C.inkMid,textTransform:"uppercase",letterSpacing:0.5,marginBottom:4}}>Status</div>
                <div style={{fontSize:15,fontWeight:800,color:C.gold}}>Trial Active</div>
              </div>
              <div style={{background:C.bg,borderRadius:10,padding:14}}>
                <div style={{fontSize:10,fontWeight:700,color:C.inkMid,textTransform:"uppercase",letterSpacing:0.5,marginBottom:4}}>Billing Cycle</div>
                <div style={{fontSize:15,fontWeight:800,color:C.ink}}>Monthly</div>
              </div>
              <div style={{background:C.bg,borderRadius:10,padding:14}}>
                <div style={{fontSize:10,fontWeight:700,color:C.inkMid,textTransform:"uppercase",letterSpacing:0.5,marginBottom:4}}>Trial Ends</div>
                <div style={{fontSize:15,fontWeight:800,color:C.ink}}>9 days</div>
              </div>
            </div>
            <div style={{background:C.accentLt,border:`1px solid ${C.accent}30`,borderRadius:10,padding:14,marginBottom:20,fontSize:12,color:C.ink,lineHeight:1.6}}>
              Payments are processed securely via <strong>PayFast</strong>. For billing queries, invoice copies, or to update your payment method, email <strong>billing@solutha.co.za</strong>.
            </div>
            <button onClick={()=>setShowBilling(false)} style={{width:"100%",padding:"10px 0",borderRadius:8,border:"none",background:C.accent,color:"#fff",fontWeight:700,fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Close</button>
          </div>
        </div>
      )}

    </div>
  );
}


// ── ACCOUNTANT DASHBOARD ──────────────────────────────────────────────────────
// Full-screen client portfolio view for accountants/bookkeepers.
// Shows all companies the user has access to with invoice stats, overdue alerts,
// quick "Open" switch button, and an "Add Client" onboarding modal.
function AccountantDashboard({user, onSelectCompany, onLogout}) {
  const [clients,    setClients]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [err,        setErr]        = useState("");
  const [showModal,  setShowModal]  = useState(false);
  const [switching,  setSwitching]  = useState(null);
  // Onboarding modal fields
  const [oName,    setOName]    = useState("");
  const [oContact, setOContact] = useState("");
  const [oEmail,   setOEmail]   = useState("");
  const [oPhone,   setOPhone]   = useState("");
  const [oIndustry,setOIndustry]= useState("");
  const [oErr,     setOErr]     = useState("");
  const [oLoading, setOLoading] = useState(false);
  const [oSuccess, setOSuccess] = useState("");

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const data = await api("/accountant/clients");
      setClients(data);
    } catch(e) { setErr(e.message || "Could not load clients."); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleOpen = async (id) => {
    if (switching !== null) return;
    setSwitching(id);
    await onSelectCompany(id);
    setSwitching(null);
  };

  const handleOnboard = async () => {
    if (!oName.trim()) { setOErr("Company name is required."); return; }
    if (!oEmail.trim()) { setOErr("Contact email is required."); return; }
    setOLoading(true); setOErr("");
    try {
      const res = await api("/accountant/onboard-client", {
        method: "POST",
        body: JSON.stringify({ company_name: oName.trim(), contact_name: oContact.trim(), contact_email: oEmail.trim(), contact_phone: oPhone.trim(), industry: oIndustry.trim() }),
      });
      setOSuccess(`${res.name || oName} created! Invite${res.email_sent?" sent to "+oEmail+".":": invite link generated."}`);
      setOName(""); setOContact(""); setOEmail(""); setOPhone(""); setOIndustry("");
      await load();
    } catch(e) { setOErr(e.message || "Could not onboard client."); }
    setOLoading(false);
  };

  const subColor  = (s) => s === "active" ? C.green : s === "trial" ? C.gold : C.red;
  const subBg     = (s) => s === "active" ? C.greenLt : s === "trial" ? C.goldLt : C.redLt;

  return (
    <div style={{minHeight:"100vh",background:C.bg,fontFamily:"sans-serif"}}>
      {/* Header */}
      <div style={{background:C.surface,borderBottom:`1px solid ${C.border}`,padding:"18px 32px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div>
          <div style={{fontFamily:"serif",fontSize:26,fontWeight:800,color:C.ink}}><span style={{color:C.accent}}>Zu</span>Zan</div>
          <div style={{fontSize:11,color:C.inkMid,marginTop:2}}>Accountant Practice Dashboard</div>
        </div>
        <div style={{display:"flex",gap:10,alignItems:"center"}}>
          <button onClick={()=>{setShowModal(true);setOSuccess("");setOErr("");}} style={{padding:"10px 20px",background:C.accent,border:"none",borderRadius:10,color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>+ Add Client</button>
          <button onClick={load} style={{padding:"10px 16px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:10,fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>↻ Refresh</button>
          <button onClick={onLogout} style={{padding:"10px 16px",background:"transparent",border:`1px solid ${C.border}`,borderRadius:10,fontSize:13,color:C.inkMid,cursor:"pointer",fontFamily:"inherit"}}>Sign Out</button>
        </div>
      </div>

      <div style={{padding:"28px 32px"}}>
        {/* Summary stat bar */}
        {clients.length > 0 && (
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:12,marginBottom:28}}>
            {[
              {label:"Total Clients",   val: clients.length},
              {label:"Unpaid Invoices", val: clients.reduce((s,c)=>s+(c.unpaid_count||0),0)},
              {label:"Overdue",         val: clients.reduce((s,c)=>s+(c.overdue_count||0),0), alert:true},
              {label:"Outstanding",     val: `R${clients.reduce((s,c)=>s+(c.unpaid_total||0),0).toLocaleString("en-ZA",{minimumFractionDigits:2})}`},
            ].map(st=>(
              <div key={st.label} style={{background:C.surface,border:`1px solid ${st.alert&&clients.reduce((s,c)=>s+(c.overdue_count||0),0)>0?C.red+"40":C.border}`,borderRadius:12,padding:18,textAlign:"center"}}>
                <div style={{fontSize:22,fontWeight:800,color:st.alert&&clients.reduce((s,c)=>s+(c.overdue_count||0),0)>0?C.red:C.ink}}>{st.val}</div>
                <div style={{fontSize:11,color:C.inkMid,marginTop:4}}>{st.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Error / loading */}
        {loading && <div style={{textAlign:"center",padding:60,color:C.inkMid}}>Loading clients…</div>}
        {err && <div style={{color:C.red,padding:20,textAlign:"center"}}>{err}</div>}

        {/* Client cards grid */}
        {!loading && !err && (
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))",gap:16}}>
            {clients.length === 0 && (
              <div style={{gridColumn:"1/-1",textAlign:"center",padding:60,color:C.inkMid}}>
                <div style={{fontSize:40,marginBottom:12}}>🏢</div>
                <div style={{fontSize:16,fontWeight:700,marginBottom:8}}>No clients yet</div>
                <div style={{fontSize:13}}>Click "+ Add Client" to onboard your first client.</div>
              </div>
            )}
            {clients.map(c => (
              <div key={c.id} style={{background:C.surface,border:`1.5px solid ${c.overdue_count>0?C.red+"40":C.border}`,borderRadius:16,padding:22,display:"flex",flexDirection:"column",gap:0}}>
                {/* Company name + plan badge */}
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
                  <div>
                    <div style={{fontSize:15,fontWeight:800,color:C.ink}}>{c.name||"Unnamed"}</div>
                    <div style={{fontSize:11,color:C.inkMid,marginTop:2}}>{c.email||""}</div>
                  </div>
                  <div style={{display:"flex",gap:4,flexWrap:"wrap",justifyContent:"flex-end"}}>
                    {c.billing_exempt && <span style={{fontSize:10,fontWeight:700,background:"#e8f5e9",color:"#1b5e20",borderRadius:20,padding:"2px 8px"}}>Partner</span>}
                    <span style={{fontSize:10,fontWeight:700,background:subBg(c.subscription_status),color:subColor(c.subscription_status),borderRadius:20,padding:"2px 8px",textTransform:"capitalize"}}>{c.subscription_status||"trial"}</span>
                    <span style={{fontSize:10,fontWeight:600,color:C.inkMid,background:C.bg,borderRadius:20,padding:"2px 8px",textTransform:"capitalize"}}>{c.role}</span>
                  </div>
                </div>

                {/* Invoice stats */}
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:6,marginBottom:14}}>
                  {[
                    {label:"Unpaid",  val:c.unpaid_count,  color:c.unpaid_count>0?C.gold:C.inkDim},
                    {label:"Overdue", val:c.overdue_count, color:c.overdue_count>0?C.red:C.inkDim},
                    {label:"Drafts",  val:c.draft_count,   color:C.inkDim},
                  ].map(st=>(
                    <div key={st.label} style={{textAlign:"center",background:C.bg,borderRadius:8,padding:"8px 4px"}}>
                      <div style={{fontSize:16,fontWeight:800,color:st.color}}>{st.val}</div>
                      <div style={{fontSize:9,color:C.inkDim,marginTop:2}}>{st.label}</div>
                    </div>
                  ))}
                </div>
                <div style={{fontSize:11,color:C.inkMid,marginBottom:14}}>
                  Outstanding: <strong style={{color:C.ink}}>R{(c.unpaid_total||0).toLocaleString("en-ZA",{minimumFractionDigits:2})}</strong>
                </div>

                {/* Open button */}
                <button onClick={()=>handleOpen(c.id)} disabled={switching!==null} style={{
                  marginTop:"auto",padding:"10px",background:C.accent,border:"none",borderRadius:10,
                  color:"#fff",fontSize:13,fontWeight:700,cursor:switching!==null?"default":"pointer",
                  fontFamily:"inherit",opacity:switching!==null&&switching!==c.id?0.5:1,
                }}>
                  {switching===c.id ? "Opening…" : "Open →"}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Onboarding Modal ── */}
      {showModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:900,display:"flex",alignItems:"center",justifyContent:"center",padding:24}} onClick={e=>{if(e.target===e.currentTarget){setShowModal(false);setOSuccess("");}}}>
          <div style={{background:C.surface,borderRadius:20,padding:32,width:"100%",maxWidth:480,boxShadow:"0 8px 40px rgba(0,0,0,0.18)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
              <div style={{fontSize:18,fontWeight:800,color:C.ink}}>Add New Client</div>
              <button onClick={()=>{setShowModal(false);setOSuccess("");}} style={{background:"none",border:"none",cursor:"pointer",fontSize:20,color:C.inkMid}}>✕</button>
            </div>
            {oSuccess ? (
              <div style={{textAlign:"center",padding:"20px 0"}}>
                <div style={{fontSize:40,marginBottom:12}}>✅</div>
                <div style={{fontSize:15,fontWeight:700,color:C.ink,marginBottom:8}}>{oSuccess}</div>
                <button onClick={()=>setOSuccess("")} style={{padding:"10px 24px",background:C.accent,border:"none",borderRadius:10,color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Add Another</button>
              </div>
            ) : (
              <>
                {[
                  {label:"Company Name *",     val:oName,     set:setOName,     ph:"e.g. ABC Trading (Pty) Ltd"},
                  {label:"Contact Name",        val:oContact,  set:setOContact,  ph:"e.g. Jane Smith"},
                  {label:"Contact Email *",     val:oEmail,    set:setOEmail,    ph:"jane@abctrading.co.za", type:"email"},
                  {label:"Phone",               val:oPhone,    set:setOPhone,    ph:"+27 82 000 0000"},
                  {label:"Industry",            val:oIndustry, set:setOIndustry, ph:"e.g. Retail, Construction…"},
                ].map(f=>(
                  <div key={f.label} style={{marginBottom:12}}>
                    <label style={{display:"block",fontSize:11,fontWeight:700,color:C.inkMid,marginBottom:4,textTransform:"uppercase",letterSpacing:0.5}}>{f.label}</label>
                    <input type={f.type||"text"} value={f.val} onChange={e=>f.set(e.target.value)} placeholder={f.ph}
                      style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:14,fontFamily:"inherit",outline:"none"}}/>
                  </div>
                ))}
                {oErr && <div style={{fontSize:12,color:C.red,marginBottom:12}}>{oErr}</div>}
                <div style={{marginTop:4,fontSize:11,color:C.inkMid,marginBottom:16}}>The client will receive an invite email to set up their account. You'll be added as their accountant automatically.</div>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={handleOnboard} disabled={oLoading} style={{flex:1,padding:"12px",background:C.accent,border:"none",borderRadius:10,color:"#fff",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit",opacity:oLoading?0.6:1}}>
                    {oLoading?"Creating…":"Create & Invite"}
                  </button>
                  <button onClick={()=>{setShowModal(false);setOErr("");}} style={{padding:"12px 18px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:10,fontSize:14,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── LOGIN ─────────────────────────────────────────────────────────────────────
// ── CLIENT PICKER (Accountant Practice / multi-client) ─────────────────────────
// Shown after login when a user has access to more than one company — lets
// an accountant/bookkeeper pick which client to work in before landing on
// the dashboard. Single-company users never see this screen.
function ClientPicker({companies, onSelect, onLogout, onNewCompany}) {
  const [switching, setSwitching] = useState(null); // id of company being switched into
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState("");
  const [newPlan, setNewPlan] = useState("starter");
  const [creating, setCreating] = useState(false);
  const [newErr, setNewErr] = useState("");

  const pick = async (id) => {
    if (switching !== null) return;
    setSwitching(id);
    await onSelect(id);
    setSwitching(null);
  };

  const handleCreate = async () => {
    if (!newName.trim()) { setNewErr("Enter a company name."); return; }
    setCreating(true); setNewErr("");
    try {
      await onNewCompany(newName.trim(), newPlan);
      setShowNew(false); setNewName(""); setNewPlan("starter");
    } catch(e) { setNewErr(e.message || "Could not create company."); }
    setCreating(false);
  };

  const roleColor = (role) => {
    if (role === "owner") return C.accent;
    if (role === "admin") return C.blue;
    if (role === "accountant") return C.green;
    return C.inkMid;
  };

  return (
    <div style={{minHeight:"100vh",background:C.bg,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
      <div style={{width:"100%",maxWidth:640}}>
        <div style={{textAlign:"center",marginBottom:32}}>
          <div style={{fontFamily:"serif",fontSize:40,fontWeight:800,color:C.ink,marginBottom:8}}><span style={{color:C.accent}}>Zu</span>Zan</div>
          <h2 style={{fontFamily:"serif",fontSize:22,color:C.ink,margin:"0 0 6px"}}>Select a client</h2>
          <p style={{fontSize:13,color:C.inkMid}}>You have access to {companies.length} companies. Choose one to continue.</p>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(260px, 1fr))",gap:14}}>
          {companies.map(c => (
            <button key={c.id} onClick={()=>pick(c.id)} disabled={switching!==null} style={{
              textAlign:"left", background:C.surface, border:`1.5px solid ${C.border}`, borderRadius:16,
              padding:20, cursor: switching!==null ? "default" : "pointer", fontFamily:"inherit",
              opacity: switching!==null && switching!==c.id ? 0.5 : 1,
              transition:"opacity 0.15s",
            }}>
              <div style={{fontSize:15,fontWeight:700,color:C.ink,marginBottom:8}}>{c.name}</div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
                <span style={{fontSize:10,fontWeight:700,color:"#fff",background:C.inkDim,borderRadius:20,padding:"3px 10px",textTransform:"capitalize"}}>{c.plan||"starter"}</span>
                {c.role && <span style={{fontSize:10,fontWeight:700,color:roleColor(c.role),background:roleColor(c.role)+"1A",borderRadius:20,padding:"3px 10px",textTransform:"capitalize"}}>{c.role}</span>}
                {c.is_home && <span style={{fontSize:10,fontWeight:600,color:C.inkDim}}>Home</span>}
              </div>
              {switching===c.id && <div style={{fontSize:11,color:C.inkMid,marginTop:10}}>Switching…</div>}
            </button>
          ))}
        </div>
        <div style={{textAlign:"center",marginTop:24,display:"flex",flexDirection:"column",alignItems:"center",gap:12}}>
          {showNew ? (
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:14,padding:20,width:"100%",maxWidth:360,textAlign:"left"}}>
              <div style={{fontSize:14,fontWeight:700,color:C.ink,marginBottom:12}}>New Client Company</div>
              <input autoFocus type="text" placeholder="Company name" value={newName} onChange={e=>setNewName(e.target.value)}
                style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:14,fontFamily:"inherit",marginBottom:8,outline:"none"}}/>
              <label style={{display:"block",fontSize:11,fontWeight:600,color:"#888",marginBottom:4,textTransform:"uppercase",letterSpacing:0.5}}>Plan</label>
              <div style={{display:"flex",gap:6,marginBottom:8}}>
                {[{v:"starter",l:"Starter",p:"R399"},{v:"professional",l:"Professional",p:"R699"},{v:"business",l:"Business",p:"R1 299"}].map(opt=>(
                  <button key={opt.v} type="button" onClick={()=>setNewPlan(opt.v)} style={{flex:1,padding:"8px 4px",border:`1.5px solid ${newPlan===opt.v?C.accent:C.border}`,borderRadius:8,background:newPlan===opt.v?C.accentLt:"transparent",color:newPlan===opt.v?C.accent:"#666",fontSize:12,fontWeight:newPlan===opt.v?700:500,cursor:"pointer",fontFamily:"inherit"}}>
                    <div>{opt.l}</div><div style={{fontSize:10,opacity:0.8}}>{opt.p}/mo</div>
                  </button>
                ))}
              </div>
              {newErr && <div style={{fontSize:12,color:C.red,marginBottom:8}}>{newErr}</div>}
              <div style={{display:"flex",gap:8}}>
                <button onClick={handleCreate} disabled={creating} style={{flex:1,padding:"10px",background:C.accent,border:"none",borderRadius:8,color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",opacity:creating?0.6:1}}>
                  {creating?"Creating…":"Create"}
                </button>
                <button onClick={()=>{setShowNew(false);setNewName("");setNewErr("");}} style={{padding:"10px 16px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
              </div>
            </div>
          ) : (
            <button onClick={()=>setShowNew(true)} style={{padding:"11px 24px",background:C.accent,border:"none",borderRadius:10,color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
              + Add Client
            </button>
          )}
          <button onClick={onLogout} style={{fontSize:12,color:C.inkDim,background:"none",border:"none",cursor:"pointer",fontFamily:"inherit"}}>Sign out</button>
        </div>
      </div>
    </div>
  );
}

function Login({onLogin, onRegister}) {
  const [view,    setView]    = useState("login");   // login | forgot | reset
  const [form,    setForm]    = useState({email:"", password:""});
  const [forgot,  setForgot]  = useState({email:""});
  const [reset,   setReset]   = useState({code:"", password:"", confirm:""});
  const [msg,     setMsg]     = useState("");
  const [resetCode, setResetCode] = useState("");
  const [error,   setError]   = useState("");
  const [loading, setLoading] = useState(false);
  const [serverStatus, setServerStatus] = useState("checking"); // checking | ready | sleeping
  const [showPassword, setShowPassword] = useState(false);

  // Ping backend on mount to wake it up before user clicks Sign In
  useEffect(() => {
    const ping = async () => {
      try {
        const res = await fetch("https://zuzan-backend.onrender.com/health", {method:"GET"});
        if (res.ok) { setServerStatus("ready"); return; }
      } catch(e) {}
      setServerStatus("sleeping");
      // Retry every 5s until awake
      const interval = setInterval(async () => {
        try {
          const res = await fetch("https://zuzan-backend.onrender.com/health", {method:"GET"});
          if (res.ok) { setServerStatus("ready"); clearInterval(interval); }
        } catch(e) {}
      }, 5000);
      setTimeout(() => clearInterval(interval), 90000); // give up after 90s
    };
    ping();
  }, []);

  const handleLogin = async () => {
    if (!form.email || !form.password) { setError("Please enter your email and password."); return; }
    setLoading(true); setError("");
    const attempt = async () => {
      const res = await fetch("https://zuzan-backend.onrender.com/auth/login", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({email:form.email, password:form.password}),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Invalid email or password.");
      return data;
    };
    try {
      let data;
      try {
        data = await attempt();
      } catch(e) {
        if (e.message.includes("Invalid email") || e.message.includes("password")) throw e;
        // Server likely sleeping — show message and retry once after 8s
        setError("Server is waking up, please wait...");
        await new Promise(r => setTimeout(r, 8000));
        data = await attempt();
      }
      localStorage.setItem("zuzan_token", data.access_token);
      const _rawSub = data.company.subscription_status || "trial";
      const _subStatus = (_rawSub === "expired" && data.company.trial_ends && new Date(data.company.trial_ends) > new Date()) ? "trial" : _rawSub;
      onLogin({firstName:data.user.first_name, lastName:data.user.last_name, email:data.user.email,
        companyName:data.company.name, logoUrl:data.company.logo_url||"", headerImageUrl:data.company.invoice_header_url||"", invoiceTemplateHtml:data.company.invoice_template_html||"", plan:{name:data.company.plan, id:data.company.plan}, access_token:data.access_token, trialEnds:data.company.trial_ends, subscriptionStatus:_subStatus, role:data.user.role||"owner", payrollEnabled:data.company.payroll_enabled||false, afsEnabled:data.company.afs_enabled||false, billingExempt:data.company.billing_exempt||false});
    } catch(e) { setError(e.message.includes("fetch") || e.message.includes("network") ? "Could not connect to server. Please try again." : e.message); }
    finally { setLoading(false); }
  };

  const handleForgot = async () => {
    if (!forgot.email) { setError("Please enter your email address."); return; }
    setLoading(true); setError(""); setMsg("");
    try {
      const res = await fetch("https://zuzan-backend.onrender.com/auth/forgot-password", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({email: forgot.email}),
      });
      const data = await res.json();
      setMsg(data.message);
      if (res.ok) { setView("reset"); }
    } catch(e) { setError("Could not connect to server."); }
    finally { setLoading(false); }
  };

  const handleReset = async () => {
    if (!reset.code) { setError("Please enter the reset code."); return; }
    if (!reset.password || reset.password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (reset.password !== reset.confirm) { setError("Passwords do not match."); return; }
    setLoading(true); setError(""); setMsg("");
    try {
      const res = await fetch("https://zuzan-backend.onrender.com/auth/reset-password", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({token: reset.code, new_password: reset.password}),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || "Reset failed."); return; }
      setMsg(data.message);
      setTimeout(() => { setView("login"); setMsg(""); setReset({code:"",password:"",confirm:""}); }, 2000);
    } catch(e) { setError("Could not connect to server."); }
    finally { setLoading(false); }
  };

  const inputStyle = {width:"100%",padding:"12px 14px",border:"1.5px solid "+C.border,borderRadius:10,fontSize:14,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"};
  const labelStyle = {display:"block",fontSize:11,fontWeight:600,color:C.inkMid,marginBottom:6,textTransform:"uppercase",letterSpacing:0.5};

  return (
    <div style={{minHeight:"100vh",background:C.bg,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
      <div style={{width:"100%",maxWidth:420}}>
        <div style={{textAlign:"center",marginBottom:40}}>
          <div style={{fontFamily:"serif",fontSize:40,fontWeight:800,color:C.ink,marginBottom:8}}><span style={{color:C.accent}}>Zu</span>Zan</div>
          <div style={{fontSize:13,color:C.inkMid}}>SA Bookkeeping Platform</div>
        </div>
        <div style={{background:C.surface,border:"1px solid "+C.border,borderRadius:20,padding:36,boxShadow:"0 4px 24px rgba(0,0,0,0.06)"}}>

          {view === "login" && (
            <>
              <h2 style={{fontFamily:"serif",fontSize:26,color:C.ink,margin:"0 0 8px"}}>Welcome back</h2>
              <p style={{fontSize:13,color:C.inkMid,marginBottom:16}}>Sign in to your ZuZan account</p>
              {serverStatus === "sleeping" && (
                <div style={{background:C.goldLt,border:`1px solid ${C.gold}40`,borderRadius:8,padding:"10px 14px",marginBottom:16,fontSize:12,color:C.gold,display:"flex",alignItems:"center",gap:8}}>
                  <span>⏳</span> Server is starting up, please wait a moment...
                </div>
              )}
              {serverStatus === "ready" && (
                <div style={{background:C.greenLt,border:`1px solid ${C.green}40`,borderRadius:8,padding:"10px 14px",marginBottom:16,fontSize:12,color:C.green,display:"flex",alignItems:"center",gap:8}}>
                  <span>🟢</span> Server is ready
                </div>
              )}
              <div style={{marginBottom:16}}>
                <label style={labelStyle}>Email Address</label>
                <input type="email" placeholder="you@company.co.za" value={form.email}
                  onChange={e=>setForm({...form,email:e.target.value})}
                  onKeyDown={e=>e.key==="Enter"&&handleLogin()} style={inputStyle}/>
              </div>
              <div style={{marginBottom:8}}>
                <label style={labelStyle}>Password</label>
                <div style={{position:"relative"}}>
                  <input type={showPassword?"text":"password"} placeholder="Your password" value={form.password}
                    onChange={e=>setForm({...form,password:e.target.value})}
                    onKeyDown={e=>e.key==="Enter"&&handleLogin()} style={{...inputStyle,paddingRight:44}}/>
                  <button type="button" onClick={()=>setShowPassword(v=>!v)}
                    style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",padding:0,color:C.inkMid,fontSize:16,lineHeight:1}}>
                    {showPassword ? "🙈" : "👁️"}
                  </button>
                </div>
              </div>
              <div style={{textAlign:"right",marginBottom:20}}>
                <span onClick={()=>{setView("forgot");setError("");setMsg("");}} style={{fontSize:12,color:C.accent,cursor:"pointer",fontWeight:600}}>Forgot password?</span>
              </div>
              {error && <div style={{background:C.redLt,border:"1px solid "+C.red+"30",borderRadius:10,padding:"10px 14px",marginBottom:16,fontSize:13,color:C.red}}>{error}</div>}
              <button onClick={handleLogin} disabled={loading} style={{width:"100%",padding:"13px",border:"none",borderRadius:10,background:loading?C.inkDim:C.accent,color:"#fff",fontSize:15,fontWeight:700,cursor:loading?"wait":"pointer",fontFamily:"inherit",marginBottom:16}}>
                {loading ? "Signing in..." : "Sign In"}
              </button>
              <div style={{textAlign:"center",fontSize:13,color:C.inkMid}}>
                Don't have an account?{" "}
                <span onClick={onRegister} style={{color:C.accent,fontWeight:700,cursor:"pointer"}}>Start free trial</span>
              </div>
            </>
          )}

          {view === "forgot" && (
            <>
              <h2 style={{fontFamily:"serif",fontSize:24,color:C.ink,margin:"0 0 8px"}}>Reset Password</h2>
              <p style={{fontSize:13,color:C.inkMid,marginBottom:28}}>Enter your email address and we'll generate a reset code.</p>
              <div style={{marginBottom:20}}>
                <label style={labelStyle}>Email Address</label>
                <input type="email" placeholder="you@company.co.za" value={forgot.email}
                  onChange={e=>setForgot({...forgot,email:e.target.value})}
                  onKeyDown={e=>e.key==="Enter"&&handleForgot()} style={inputStyle}/>
              </div>
              {error && <div style={{background:C.redLt,border:"1px solid "+C.red+"30",borderRadius:10,padding:"10px 14px",marginBottom:16,fontSize:13,color:C.red}}>{error}</div>}
              {msg && <div style={{background:C.goldLt,border:"1px solid "+C.gold+"30",borderRadius:10,padding:"10px 14px",marginBottom:16,fontSize:13,color:C.gold}}>{msg}</div>}
              <button onClick={handleForgot} disabled={loading} style={{width:"100%",padding:"13px",border:"none",borderRadius:10,background:loading?C.inkDim:C.accent,color:"#fff",fontSize:15,fontWeight:700,cursor:loading?"wait":"pointer",fontFamily:"inherit",marginBottom:16}}>
                {loading ? "Sending..." : "Get Reset Code"}
              </button>
              <div style={{textAlign:"center",fontSize:13,color:C.inkMid}}>
                <span onClick={()=>{setView("login");setError("");setMsg("");}} style={{color:C.accent,fontWeight:700,cursor:"pointer"}}>Back to Sign In</span>
              </div>
            </>
          )}

          {view === "reset" && (
            <>
              <h2 style={{fontFamily:"serif",fontSize:24,color:C.ink,margin:"0 0 8px"}}>Set New Password</h2>
              <p style={{fontSize:13,color:C.inkMid,marginBottom:20}}>Check your email for the reset code, then enter it below with your new password.</p>
              <div style={{marginBottom:14}}>
                <label style={labelStyle}>Reset Code</label>
                <input placeholder="e.g. A3F9B21C" value={reset.code}
                  onChange={e=>setReset({...reset,code:e.target.value.toUpperCase()})}
                  style={{...inputStyle,fontFamily:"monospace",letterSpacing:2,fontSize:16}}/>
              </div>
              <div style={{marginBottom:14}}>
                <label style={labelStyle}>New Password</label>
                <input type="password" placeholder="Min 6 characters" value={reset.password}
                  onChange={e=>setReset({...reset,password:e.target.value})} style={inputStyle}/>
              </div>
              <div style={{marginBottom:20}}>
                <label style={labelStyle}>Confirm New Password</label>
                <input type="password" placeholder="Repeat password" value={reset.confirm}
                  onChange={e=>setReset({...reset,confirm:e.target.value})}
                  onKeyDown={e=>e.key==="Enter"&&handleReset()} style={inputStyle}/>
              </div>
              {error && <div style={{background:C.redLt,border:"1px solid "+C.red+"30",borderRadius:10,padding:"10px 14px",marginBottom:16,fontSize:13,color:C.red}}>{error}</div>}
              {msg   && <div style={{background:C.greenLt,border:"1px solid "+C.green+"30",borderRadius:10,padding:"10px 14px",marginBottom:16,fontSize:13,color:C.green,fontWeight:700}}>{msg}</div>}
              <button onClick={handleReset} disabled={loading} style={{width:"100%",padding:"13px",border:"none",borderRadius:10,background:loading?C.inkDim:C.accent,color:"#fff",fontSize:15,fontWeight:700,cursor:loading?"wait":"pointer",fontFamily:"inherit",marginBottom:16}}>
                {loading ? "Updating..." : "Set New Password"}
              </button>
              <div style={{textAlign:"center",fontSize:13,color:C.inkMid}}>
                <span onClick={()=>{setView("forgot");setError("");setMsg("");setResetCode("");}} style={{color:C.accent,fontWeight:700,cursor:"pointer"}}>Request new code</span>
                {" · "}
                <span onClick={()=>{setView("login");setError("");setMsg("");}} style={{color:C.accent,fontWeight:700,cursor:"pointer"}}>Back to Sign In</span>
              </div>
            </>
          )}

        </div>
        <div style={{textAlign:"center",marginTop:20,fontSize:11,color:C.inkDim}}>
          Secure 256-bit SSL — Powered by PayFast — SARS compliant
        </div>
      </div>
    </div>
  );
}

// ── REGISTRATION ──────────────────────────────────────────────────────────────
function Registration({onComplete, onLogin}) {
  const [step, setStep] = useState(1);
  const [billing, setBilling] = useState("monthly");
  const [selectedPlan, setPlan] = useState(null);
  const [payrollEnabled, setPayroll] = useState(false);
  const [empCount, setEmpCount] = useState(5);
  const [form, setForm] = useState({companyName:"",regNumber:"",industry:"",financialYearEnd:"02-28",cipcRegistrationDate:"",firstName:"",lastName:"",email:"",phone:"",password:"",confirm:"",userType:"business_owner"});
  const [mandate, setMandate] = useState({accountHolder:"",bank:"",accountNumber:"",branchCode:"",accountType:"current",collectionDay:"1",signedName:"",mandateAccepted:false});
  const SA_BANK_BRANCHES = {"FNB":"250655","ABSA":"632005","Standard Bank":"051001","Nedbank":"198765","Capitec":"470010","African Bank":"430000","Investec":"580105","TymeBank":"678910","Discovery Bank":"679000","Bidvest":"462005"};
  const [processing, setProcessing] = useState(false);
  const [complete, setComplete] = useState(false);
  const [tcAccepted, setTcAccepted] = useState(false);
  const [errors, setErrors] = useState({});

  const planPrice = selectedPlan ? (billing === "monthly" ? selectedPlan.monthly : Math.round(selectedPlan.annual / 12)) : 0;
  const payrollCost = payrollEnabled ? Math.max(99, Math.round(empCount * 15.5)) : 0;
  const totalMonthly = planPrice + payrollCost;

  const validateStep2 = () => {
    const errs = {};
    if (!form.companyName.trim()) errs.companyName = "Company name is required";
    if (!form.firstName.trim()) errs.firstName = "First name is required";
    if (!form.lastName.trim()) errs.lastName = "Last name is required";
    if (!form.email.trim()) {
      errs.email = "Email address is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      errs.email = "Enter a valid email address";
    }
    if (!form.password) {
      errs.password = "Password is required";
    } else if (form.password.length < 8) {
      errs.password = "Password must be at least 8 characters";
    }
    if (!form.confirm) {
      errs.confirm = "Please confirm your password";
    } else if (form.confirm !== form.password) {
      errs.confirm = "Passwords do not match";
    }
    return errs;
  };

  const handlePayment = async () => {
    const errs = validateStep2();
    if (Object.keys(errs).length > 0) { setErrors(errs); setStep(2); return; }
    setProcessing(true);
    let token = null;
    let userData = {...form, plan:selectedPlan, billing, payroll:payrollEnabled, employees:empCount};
    try {
      // Register with real backend
      console.log("Registering with backend...", form.email);
      const res = await fetch("https://zuzan-backend.onrender.com/auth/register", {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify({
          company_name:    form.companyName.trim(),
          reg_number:              form.regNumber.trim(),
          industry:                form.industry.trim(),
          financial_year_end:      form.financialYearEnd || null,
          cipc_registration_date:  form.cipcRegistrationDate || null,
          first_name:      form.firstName.trim(),
          last_name:       form.lastName.trim(),
          email:           form.email.trim(),
          phone:                   form.phone.trim(),
          password:                form.password.slice(0, 50),
          plan:                    selectedPlan.id,
          billing_cycle:           billing,
          payroll_enabled:         payrollEnabled,
          employee_count:          empCount,
          mandate_account_holder:  mandate.accountHolder || `${form.firstName} ${form.lastName}`.trim(),
          mandate_bank:            mandate.bank,
          mandate_account_number:  mandate.accountNumber,
          mandate_branch_code:     mandate.branchCode,
          mandate_account_type:    mandate.accountType,
          mandate_collection_day:  mandate.collectionDay,
          mandate_signed_name:     mandate.signedName,
          mandate_signed:          mandate.mandateAccepted,
          user_type:               form.userType,
        }),
      });
      const data = await res.json();
      console.log("Registration response:", res.status, data);
      if (res.ok && data.access_token) {
        token = data.access_token;
        userData = {...userData, ...data.user, company: data.company};
      } else {
        // Email already registered — try login instead
        if (data.detail && data.detail.includes("already registered")) {
          const loginRes = await fetch("https://zuzan-backend.onrender.com/auth/login", {
            method: "POST",
            headers: {"Content-Type": "application/json"},
            body: JSON.stringify({email: form.email, password: (form.password || "").slice(0, 50)}),
          });
          const loginData = await loginRes.json();
          if (loginRes.ok && loginData.access_token) {
            token = loginData.access_token;
            userData = {...userData, ...loginData.user};
          }
        }
      }
    } catch(err) {
      console.error("Backend registration error:", err.message);
    }
    // Save token (real or demo)
    console.log("Token obtained:", token ? "REAL TOKEN" : "NO TOKEN - demo mode");
    if (token) {
      localStorage.setItem("zuzan_token", token);
      console.log("Real token saved!");
    } else {
      localStorage.setItem("zuzan_token", "demo_" + Date.now());
      console.log("Demo token saved - backend unreachable");
    }
    setProcessing(false);
    setComplete(true);
    setTimeout(() => onComplete({...userData, access_token: token || "demo"}), 1500);
  };

  if (complete) return (
    <div style={{minHeight:"100vh",background:C.bg,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
      <div style={{textAlign:"center",maxWidth:480}}>
        <div style={{fontSize:64,marginBottom:20}}>🎉</div>
        <h2 style={{fontFamily:"serif",fontSize:36,color:C.ink,marginBottom:12}}>Welcome to ZuZan!</h2>
        <p style={{color:C.inkMid,fontSize:15,lineHeight:1.7}}>Your account is ready. Setting up your dashboard...</p>
      </div>
    </div>
  );

  return (
    <div style={{minHeight:"100vh",background:C.bg}}>
      <div style={{background:C.surface,borderBottom:`1px solid ${C.border}`,padding:"16px 32px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div style={{fontFamily:"serif",fontSize:24,fontWeight:800,color:C.ink}}><span style={{color:C.accent}}>Zu</span>Zan</div>
        <div style={{display:"flex",gap:8}}>
          {["Plan","Account","Payment","Confirm"].map((s,i) => (
            <div key={i} style={{display:"flex",alignItems:"center",gap:6}}>
              <div style={{width:26,height:26,borderRadius:"50%",background:step>i+1?C.green:step===i+1?C.accent:C.border,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:step>=i+1?"#fff":C.inkMid}}>{step>i+1?"v":i+1}</div>
              <span style={{fontSize:12,color:step===i+1?C.ink:C.inkDim,fontWeight:step===i+1?600:400}}>{s}</span>
              {i<3 && <div style={{width:24,height:1,background:C.border,marginLeft:2}}/>}
            </div>
          ))}
        </div>
        <div style={{fontSize:12,color:C.inkMid}}>Already have an account? <span style={{color:C.accent,cursor:"pointer",fontWeight:600}} onClick={() => onLogin && onLogin()}>Sign in</span></div>
      </div>
      <div style={{maxWidth:860,margin:"0 auto",padding:"48px 24px"}}>
        {step === 1 && (
          <div>
            <h1 style={{fontFamily:"serif",fontSize:34,color:C.ink,marginBottom:6}}>Choose your plan</h1>
            <p style={{color:C.inkMid,fontSize:15,marginBottom:28}}>14-day free trial on all plans. No credit card charged until trial ends.</p>
            <div style={{display:"inline-flex",background:C.border,borderRadius:10,padding:3,marginBottom:28}}>
              {["monthly","annual"].map(b => <button key={b} onClick={() => setBilling(b)} style={{padding:"8px 20px",borderRadius:8,border:"none",cursor:"pointer",fontSize:13,fontWeight:600,fontFamily:"inherit",background:billing===b?C.surface:"transparent",color:billing===b?C.ink:C.inkMid}}>{b==="monthly"?"Monthly":"Annual (Save 17%)"}</button>)}
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:16,marginBottom:28}}>
              {PLANS.map(plan => {
                const price = billing === "monthly" ? plan.monthly : Math.round(plan.annual / 12);
                const sel = selectedPlan && selectedPlan.id === plan.id;
                return (
                  <div key={plan.id} onClick={() => setPlan(plan)} style={{background:C.surface,border:`2px solid ${sel?plan.color:C.border}`,borderRadius:18,padding:24,cursor:"pointer",position:"relative",transform:sel?"translateY(-3px)":"none",boxShadow:sel?`0 8px 32px ${plan.color}20`:"none"}}>
                    {plan.popular && <div style={{position:"absolute",top:-11,left:"50%",transform:"translateX(-50%)",background:C.accent,color:"#fff",fontSize:9,fontWeight:700,letterSpacing:1,padding:"3px 14px",borderRadius:20,textTransform:"uppercase"}}>Most Popular</div>}
                    <div style={{fontSize:28,marginBottom:10}}>{plan.icon}</div>
                    <div style={{fontSize:11,fontWeight:700,color:C.inkMid,letterSpacing:1.5,textTransform:"uppercase",marginBottom:6}}>{plan.name}</div>
                    <div style={{fontFamily:"serif",fontSize:36,fontWeight:800,color:C.ink,letterSpacing:-1,marginBottom:2}}>R{price.toLocaleString()}</div>
                    <div style={{fontSize:11,color:C.inkDim,marginBottom:14}}>{plan.users} users - {plan.invoices} invoices{plan.invoices!=="Unlimited"?"/mo":""}</div>
                    <div style={{borderTop:`1px solid ${C.border}`,paddingTop:12}}>
                      {plan.features.slice(0,5).map((f,i) => <div key={i} style={{display:"flex",gap:8,fontSize:12,color:C.inkMid,marginBottom:6}}><span style={{color:C.green}}>ok</span>{f}</div>)}
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{background:C.surface,border:`2px solid ${payrollEnabled?C.accent:C.border}`,borderRadius:18,padding:24,marginBottom:28}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:payrollEnabled?20:0}}>
                <div style={{display:"flex",gap:14,alignItems:"flex-start"}}>
                  <span style={{fontSize:32}}>👥</span>
                  <div>
                    <div style={{fontSize:15,fontWeight:700,color:C.ink,marginBottom:4}}>Payroll Module</div>
                    <div style={{fontSize:13,color:C.inkMid}}>SARS-compliant PAYE, UIF, SDL - EMP201 and IRP5 - <strong style={{color:C.accent}}>R18.25/employee/month</strong> (min R99/month)</div>
                  </div>
                </div>
                <button onClick={() => setPayroll(!payrollEnabled)} style={{padding:"8px 18px",borderRadius:8,border:`1.5px solid ${payrollEnabled?C.accent:C.border}`,background:payrollEnabled?C.accentLt:"transparent",color:payrollEnabled?C.accent:C.inkMid,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>{payrollEnabled?"Added":"Add Payroll"}</button>
              </div>
              {payrollEnabled && (
                <div style={{display:"flex",alignItems:"center",gap:16,padding:"16px 0 0",borderTop:`1px solid ${C.border}`}}>
                  <span style={{fontSize:13,color:C.inkMid}}>Number of employees:</span>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <button onClick={() => setEmpCount(Math.max(1,empCount-1))} style={{width:32,height:32,borderRadius:"50%",border:`1px solid ${C.border}`,background:C.bg,cursor:"pointer",fontSize:16,fontFamily:"inherit"}}>-</button>
                    <span style={{fontSize:20,fontWeight:700,color:C.ink,minWidth:30,textAlign:"center"}}>{empCount}</span>
                    <button onClick={() => setEmpCount(empCount+1)} style={{width:32,height:32,borderRadius:"50%",border:`1px solid ${C.border}`,background:C.bg,cursor:"pointer",fontSize:16,fontFamily:"inherit"}}>+</button>
                  </div>
                  <span style={{fontSize:13,color:C.accent,fontWeight:700}}>= {fmt(Math.max(99,empCount*34))}/month</span>
                </div>
              )}
            </div>
            {selectedPlan && (
              <div style={{background:C.ink,borderRadius:16,padding:"20px 24px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div>
                  <div style={{fontSize:11,color:"rgba(255,255,255,0.5)",marginBottom:4}}>MONTHLY TOTAL</div>
                  <div style={{fontFamily:"serif",fontSize:32,color:"#fff",fontWeight:800}}>{fmt(totalMonthly)}</div>
                  <div style={{fontSize:11,color:"rgba(255,255,255,0.4)"}}>{selectedPlan.name}{payrollEnabled?` + Payroll (${empCount} employees)`:""}</div>
                </div>
                <button onClick={() => setStep(2)} style={{background:C.accent,color:"#fff",border:"none",borderRadius:12,padding:"14px 32px",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Continue</button>
              </div>
            )}
          </div>
        )}
        {step === 2 && (
          <div style={{maxWidth:560,margin:"0 auto"}}>
            <h1 style={{fontFamily:"serif",fontSize:34,color:C.ink,marginBottom:24}}>Create your account</h1>
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:18,padding:28,marginBottom:20}}>
              <div style={{fontSize:12,fontWeight:700,color:C.inkMid,letterSpacing:1,textTransform:"uppercase",marginBottom:16}}>Business Details</div>
              {[{l:"Company Name",k:"companyName",p:"Acme Pty Ltd"},{l:"Registration Number",k:"regNumber",p:"2020/123456/07"}].map(f => (
                <div key={f.k} style={{marginBottom:14}}>
                  <label style={{display:"block",fontSize:11,fontWeight:600,color:C.inkMid,marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>{f.l}</label>
                  <input placeholder={f.p} value={form[f.k]} onChange={e => {setForm({...form,[f.k]:e.target.value});if(errors[f.k])setErrors({...errors,[f.k]:""});}} style={{width:"100%",padding:"11px 14px",border:`1.5px solid ${errors[f.k]?"#EF4444":C.border}`,borderRadius:10,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}/>
                  {errors[f.k] && <div style={{color:"#EF4444",fontSize:11,marginTop:4}}>{errors[f.k]}</div>}
                </div>
              ))}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
                <div>
                  <label style={{display:"block",fontSize:11,fontWeight:600,color:C.inkMid,marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Financial Year End</label>
                  <select value={form.financialYearEnd} onChange={e=>setForm({...form,financialYearEnd:e.target.value})} style={{width:"100%",padding:"11px 14px",border:`1.5px solid ${C.border}`,borderRadius:10,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}>
                    {[["01-31","31 January"],["02-28","28 February"],["03-31","31 March"],["04-30","30 April"],["05-31","31 May"],["06-30","30 June"],["07-31","31 July"],["08-31","31 August"],["09-30","30 September"],["10-31","31 October"],["11-30","30 November"],["12-31","31 December"]].map(([v,l])=>(
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </select>
                  <div style={{fontSize:10,color:C.inkMid,marginTop:3}}>The last day of your financial year</div>
                </div>
                <div>
                  <label style={{display:"block",fontSize:11,fontWeight:600,color:C.inkMid,marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>CIPC Registration Date</label>
                  <input type="date" value={form.cipcRegistrationDate} onChange={e=>setForm({...form,cipcRegistrationDate:e.target.value})} style={{width:"100%",padding:"11px 14px",border:`1.5px solid ${C.border}`,borderRadius:10,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}/>
                  <div style={{fontSize:10,color:C.inkMid,marginTop:3}}>Used to calculate your CIPC AR deadline</div>
                </div>
              </div>
            </div>
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:18,padding:28,marginBottom:20}}>
              <div style={{fontSize:12,fontWeight:700,color:C.inkMid,letterSpacing:1,textTransform:"uppercase",marginBottom:16}}>Your Details</div>
              <div style={{marginBottom:16}}>
                <label style={{display:"block",fontSize:11,fontWeight:600,color:C.inkMid,marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>I am a</label>
                <div style={{display:"flex",gap:10}}>
                  {[{v:"business_owner",l:"Business Owner"},{v:"bookkeeper",l:"Bookkeeper / Accountant"}].map(opt => (
                    <button key={opt.v} type="button" onClick={()=>setForm({...form,userType:opt.v})} style={{
                      flex:1,padding:"11px 14px",border:`1.5px solid ${form.userType===opt.v?C.accent:C.border}`,
                      borderRadius:10,background:form.userType===opt.v?C.accentLt:"transparent",
                      color:form.userType===opt.v?C.accent:C.inkMid,fontSize:13,fontWeight:form.userType===opt.v?700:500,
                      cursor:"pointer",fontFamily:"inherit",transition:"all 0.15s",
                    }}>{opt.l}</button>
                  ))}
                </div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
                {[{l:"First Name",k:"firstName",p:"John"},{l:"Last Name",k:"lastName",p:"Smith"}].map(f => (
                  <div key={f.k}>
                    <label style={{display:"block",fontSize:11,fontWeight:600,color:C.inkMid,marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>{f.l}</label>
                    <input placeholder={f.p} value={form[f.k]} onChange={e => {setForm({...form,[f.k]:e.target.value});if(errors[f.k])setErrors({...errors,[f.k]:""});}} style={{width:"100%",padding:"11px 14px",border:`1.5px solid ${errors[f.k]?"#EF4444":C.border}`,borderRadius:10,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}/>
                    {errors[f.k] && <div style={{color:"#EF4444",fontSize:11,marginTop:4}}>{errors[f.k]}</div>}
                  </div>
                ))}
              </div>
              {[{l:"Email Address",k:"email",t:"email",p:"john@acme.co.za"},{l:"Password",k:"password",t:"password",p:"Min 8 characters"},{l:"Confirm Password",k:"confirm",t:"password",p:"Repeat password"}].map(f => (
                <div key={f.k} style={{marginBottom:14}}>
                  <label style={{display:"block",fontSize:11,fontWeight:600,color:C.inkMid,marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>{f.l}</label>
                  <input type={f.t} placeholder={f.p} value={form[f.k]} onChange={e => {setForm({...form,[f.k]:e.target.value});if(errors[f.k])setErrors({...errors,[f.k]:""});}} style={{width:"100%",padding:"11px 14px",border:`1.5px solid ${errors[f.k]?"#EF4444":C.border}`,borderRadius:10,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}/>
                  {errors[f.k] && <div style={{color:"#EF4444",fontSize:11,marginTop:4}}>{errors[f.k]}</div>}
                </div>
              ))}
            </div>
            <div style={{display:"flex",gap:12}}>
              <button onClick={() => setStep(1)} style={{flex:1,padding:"13px",border:`1.5px solid ${C.border}`,borderRadius:10,background:"transparent",color:C.inkMid,fontSize:14,cursor:"pointer",fontFamily:"inherit"}}>Back</button>
              <button onClick={() => { const errs = validateStep2(); if (Object.keys(errs).length > 0) { setErrors(errs); } else { setErrors({}); setStep(3); } }} style={{flex:2,padding:"13px",border:"none",borderRadius:10,background:C.accent,color:"#fff",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Continue</button>
            </div>
          </div>
        )}
        {step === 3 && (
          <div style={{maxWidth:560,margin:"0 auto"}}>
            <h1 style={{fontFamily:"serif",fontSize:34,color:C.ink,marginBottom:6}}>Debit order mandate</h1>
            <p style={{color:C.inkMid,fontSize:15,marginBottom:24}}>Your subscription will be collected monthly after your 14-day free trial.</p>

            {/* Order summary */}
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:18,padding:20,marginBottom:20}}>
              <div style={{fontSize:12,fontWeight:700,color:C.inkMid,letterSpacing:1,textTransform:"uppercase",marginBottom:12}}>Order Summary</div>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:8,fontSize:14}}><span style={{color:C.inkMid}}>{selectedPlan ? selectedPlan.name : ""} Plan ({billing})</span><span style={{fontWeight:600}}>{fmt(planPrice)}/mo</span></div>
              {payrollEnabled && <div style={{display:"flex",justifyContent:"space-between",marginBottom:8,fontSize:14}}><span style={{color:C.inkMid}}>Payroll ({empCount} employees x R18.25)</span><span style={{fontWeight:600}}>{fmt(payrollCost)}/mo</span></div>}
              <div style={{borderTop:`1px solid ${C.border}`,marginTop:10,paddingTop:10,display:"flex",justifyContent:"space-between",fontSize:15,fontWeight:800}}><span>Total (after trial)</span><span style={{color:C.accent}}>{fmt(totalMonthly)}/mo</span></div>
            </div>

            {/* Bank account details */}
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:18,padding:24,marginBottom:20}}>
              <div style={{fontSize:12,fontWeight:700,color:C.inkMid,letterSpacing:1,textTransform:"uppercase",marginBottom:16}}>Bank Account Details</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>

                <div style={{gridColumn:"1/-1"}}>
                  <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Account Holder Name</label>
                  <input value={mandate.accountHolder} onChange={e=>setMandate({...mandate,accountHolder:e.target.value})} placeholder={`${form.firstName} ${form.lastName}`} style={{width:"100%",padding:"11px 14px",border:`1.5px solid ${C.border}`,borderRadius:10,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}/>
                </div>

                <div style={{gridColumn:"1/-1"}}>
                  <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Bank</label>
                  <select value={mandate.bank} onChange={e=>{const b=e.target.value;setMandate({...mandate,bank:b,branchCode:SA_BANK_BRANCHES[b]||"",});}} style={{width:"100%",padding:"11px 14px",border:`1.5px solid ${C.border}`,borderRadius:10,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}>
                    <option value="">Select bank…</option>
                    {Object.keys(SA_BANK_BRANCHES).map(b=><option key={b} value={b}>{b}</option>)}
                  </select>
                </div>

                <div>
                  <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Account Number</label>
                  <input value={mandate.accountNumber} onChange={e=>setMandate({...mandate,accountNumber:e.target.value})} placeholder="e.g. 62012345678" style={{width:"100%",padding:"11px 14px",border:`1.5px solid ${C.border}`,borderRadius:10,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}/>
                </div>

                <div>
                  <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Branch Code</label>
                  <input value={mandate.branchCode} onChange={e=>setMandate({...mandate,branchCode:e.target.value})} placeholder="Auto-filled" style={{width:"100%",padding:"11px 14px",border:`1.5px solid ${C.border}`,borderRadius:10,fontSize:13,fontFamily:"inherit",background:C.bg,color:mandate.branchCode?C.ink:C.inkDim,outline:"none",boxSizing:"border-box"}}/>
                </div>

                <div>
                  <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Account Type</label>
                  <select value={mandate.accountType} onChange={e=>setMandate({...mandate,accountType:e.target.value})} style={{width:"100%",padding:"11px 14px",border:`1.5px solid ${C.border}`,borderRadius:10,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}>
                    <option value="current">Current / Cheque</option>
                    <option value="savings">Savings</option>
                    <option value="transmission">Transmission</option>
                  </select>
                </div>

                <div>
                  <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Collection Day</label>
                  <select value={mandate.collectionDay} onChange={e=>setMandate({...mandate,collectionDay:e.target.value})} style={{width:"100%",padding:"11px 14px",border:`1.5px solid ${C.border}`,borderRadius:10,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}>
                    <option value="1">1st of the month</option>
                    <option value="15">15th of the month</option>
                    <option value="25">25th of the month</option>
                    <option value="last">Last day of the month</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Authorisation */}
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:18,padding:24,marginBottom:20}}>
              <div style={{fontSize:12,fontWeight:700,color:C.inkMid,letterSpacing:1,textTransform:"uppercase",marginBottom:14}}>Authorisation</div>
              <p style={{fontSize:13,color:C.inkMid,lineHeight:1.7,marginBottom:16}}>
                I authorise <strong>Solutha (Pty) Ltd (ZuZan)</strong> to debit the above account on the selected day each month for my ZuZan subscription. This authority remains in force until cancelled in writing. I will receive 20 days' notice of any change to the debit amount.
              </p>
              <div style={{marginBottom:14}}>
                <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Full Name (e-signature)</label>
                <input value={mandate.signedName} onChange={e=>setMandate({...mandate,signedName:e.target.value})} placeholder="Type your full name to sign" style={{width:"100%",padding:"11px 14px",border:`1.5px solid ${mandate.signedName?C.accent:C.border}`,borderRadius:10,fontSize:13,fontFamily:"serif",fontStyle:"italic",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}/>
              </div>
              <label style={{display:"flex",alignItems:"flex-start",gap:12,cursor:"pointer"}}>
                <input type="checkbox" checked={mandate.mandateAccepted} onChange={e=>setMandate({...mandate,mandateAccepted:e.target.checked})} style={{marginTop:2,width:16,height:16,accentColor:C.accent,flexShrink:0}}/>
                <span style={{fontSize:12,color:C.inkMid,lineHeight:1.7}}>I confirm that I am authorised to sign this debit order mandate on behalf of <strong>{form.companyName || "my company"}</strong> and that the banking details above are correct. A signed mandate PDF will be emailed to me for my records.</span>
              </label>
            </div>

            <div style={{display:"flex",gap:12}}>
              <button onClick={() => setStep(2)} style={{flex:1,padding:"13px",border:`1.5px solid ${C.border}`,borderRadius:10,background:"transparent",color:C.inkMid,fontSize:14,cursor:"pointer",fontFamily:"inherit"}}>Back</button>
              <button
                onClick={() => {
                  if (!mandate.bank || !mandate.accountNumber || !mandate.signedName || !mandate.mandateAccepted) {
                    alert("Please complete all mandate fields and sign to continue.");
                    return;
                  }
                  setStep(4);
                }}
                style={{flex:2,padding:"13px",border:"none",borderRadius:10,background:C.accent,color:"#fff",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                Review &amp; Confirm
              </button>
            </div>
          </div>
        )}
        {step === 4 && (
          <div style={{maxWidth:560,margin:"0 auto"}}>
            <h1 style={{fontFamily:"serif",fontSize:34,color:C.ink,marginBottom:24}}>Confirm and Start Trial</h1>
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:18,padding:28,marginBottom:20}}>
              {[
                {label:"Company",value:form.companyName || "Your Company"},
                {label:"Plan",value:`${selectedPlan ? selectedPlan.name : ""} (${billing})`},
                {label:"Users",value:`Up to ${selectedPlan ? selectedPlan.users : ""}`},
                {label:"Invoices",value:selectedPlan ? `${selectedPlan.invoices}${selectedPlan.invoices !== "Unlimited" ? " per month" : ""}` : ""},
                {label:"Payroll",value:payrollEnabled ? `${empCount} employees x R18.25 = ${fmt(payrollCost)}/mo` : "Not included"},
                {label:"Trial Period",value:"14 days FREE"},
                {label:"First Charge",value:`${fmt(totalMonthly)}/month after trial`},
                {label:"Debit Order",value:mandate.bank ? `${mandate.bank} · ****${mandate.accountNumber.slice(-4)} · ${{"1":"1st","15":"15th","25":"25th","last":"Last day"}[mandate.collectionDay]||mandate.collectionDay} of month` : "Not provided"},
              ].map((r,i) => (
                <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"11px 0",borderBottom:i<6?`1px solid ${C.border}30`:"none",fontSize:13}}>
                  <span style={{color:C.inkMid}}>{r.label}</span>
                  <span style={{fontWeight:600,color:C.ink}}>{r.value}</span>
                </div>
              ))}
            </div>
            {/* Cooling-off disclosure */}
            <div style={{background:"#FFF8E1",border:"1px solid #F59E0B",borderRadius:12,padding:"14px 16px",marginBottom:16,fontSize:12,color:"#78350F",lineHeight:1.7}}>
              <strong>Cooling-off right (CPA s16):</strong> After your 14-day free trial ends and your first payment is processed, you have <strong>7 business days</strong> to cancel and receive a full refund of that payment, no questions asked. To exercise this right email <a href="mailto:support@solutha.co.za" style={{color:"#92400E"}}>support@solutha.co.za</a>.
            </div>
            {/* T&C + Privacy Policy acceptance */}
            <label style={{display:"flex",alignItems:"flex-start",gap:12,marginBottom:20,cursor:"pointer",padding:"14px 16px",background:C.surface,border:`1.5px solid ${tcAccepted?C.accent:C.border}`,borderRadius:12}}>
              <input type="checkbox" checked={tcAccepted} onChange={e=>setTcAccepted(e.target.checked)} style={{marginTop:2,width:16,height:16,accentColor:C.accent,flexShrink:0}}/>
              <span style={{fontSize:12,color:C.inkMid,lineHeight:1.7}}>
                I have read and agree to Zuzan's{" "}
                <a href="https://zuzan.co.za/terms" target="_blank" rel="noreferrer" style={{color:C.accent,fontWeight:600}}>Terms &amp; Conditions</a>
                {" "}and{" "}
                <a href="https://zuzan.co.za/privacy" target="_blank" rel="noreferrer" style={{color:C.accent,fontWeight:600}}>Privacy Policy</a>.
                I understand my subscription will auto-renew monthly (or annually) after the free trial and I can cancel anytime from Settings.
              </span>
            </label>
            <div style={{display:"flex",gap:12}}>
              <button onClick={() => setStep(3)} style={{flex:1,padding:"13px",border:`1.5px solid ${C.border}`,borderRadius:10,background:"transparent",color:C.inkMid,fontSize:14,cursor:"pointer",fontFamily:"inherit"}}>Back</button>
              <button onClick={handlePayment} disabled={processing||!tcAccepted} title={!tcAccepted?"Please accept the Terms & Conditions to continue":""} style={{flex:2,padding:"15px",border:"none",borderRadius:10,background:(processing||!tcAccepted)?C.inkDim:C.accent,color:"#fff",fontSize:15,fontWeight:700,cursor:(processing||!tcAccepted)?"not-allowed":"pointer",fontFamily:"inherit"}}>{processing ? "Processing..." : "Start Free Trial"}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── CURRENCY HELPERS ─────────────────────────────────────────────────────────
const CURRENCIES = {
  ZAR:{symbol:"R",  code:"ZAR"},
  USD:{symbol:"$",  code:"USD"},
  EUR:{symbol:"€",  code:"EUR"},
  GBP:{symbol:"£",  code:"GBP"},
  BWP:{symbol:"P",  code:"BWP"},
  ZMW:{symbol:"K",  code:"ZMW"},
  MZN:{symbol:"MT", code:"MZN"},
  NAD:{symbol:"N$", code:"NAD"},
  KES:{symbol:"KSh",code:"KES"},
  CNY:{symbol:"¥",  code:"CNY"},
};
function fmtCurrency(amount, currency="ZAR") {
  const c = CURRENCIES[currency] || CURRENCIES.ZAR;
  return `${c.symbol}${Number(amount).toLocaleString("en-ZA",{minimumFractionDigits:2})}`;
}

// ── QUOTES / ESTIMATES ────────────────────────────────────────────────────────
const QUOTE_STATUSES = {draft:["Draft",C.inkMid,"#E8E0D5"],sent:["Sent",C.blue,C.blueLt],accepted:["Accepted",C.green,C.greenLt],declined:["Declined",C.red,C.redLt]};
function Quotes({live={},user={},onNavigate,docTemplate}) {
  const [quotes,    setQuotes]    = useState([]);
  const [showNew,   setShowNew]   = useState(false);
  const [preview,   setPreview]   = useState(null);
  const [editQuote, setEditQuote] = useState(null);
  const [saving,    setSaving]    = useState(false);
  const emptyForm = {client:"",amount:"",desc:"",validUntil:"",currency:"ZAR",rate:"18.5",notes:"",vatApplicable:true,vatAmount:"0"};
  const [form, setForm] = useState(emptyForm);
  const [customers, setCustomers] = useState([]);

  useEffect(()=>{
    api("/customers/").then(setCustomers).catch(()=>{});
    api("/quotes/").then(data=>setQuotes(data.map(q=>({
      id: q.quote_number, _id: q.id,
      client: q.client_name, desc: q.description,
      amount: q.amount, totalAmount: q.total_amount,
      vatApplicable: q.vat_applicable, vatAmount: q.vat_amount,
      date: q.created_at?.slice(0,10),
      validUntil: q.valid_until, status: q.status,
      currency: q.currency, rate: q.exchange_rate,
      notes: q.notes,
    })))).catch(()=>{});
  },[]);

  const [filterStatus, setFilterStatus] = useState(null); // null | "accepted" | "sent" | "draft" | "declined"
  const toZar = q => (q.totalAmount||q.amount||0) * (q.currency&&q.currency!=="ZAR" ? (q.rate||1) : 1);
  const totalAccepted = quotes.filter(q=>q.status==="accepted").reduce((s,q)=>s+toZar(q),0);
  const totalPending  = quotes.filter(q=>q.status==="sent").reduce((s,q)=>s+toZar(q),0);
  const displayedQuotes = filterStatus ? quotes.filter(q=>q.status===filterStatus) : quotes;
  const toggleFilter = (status) => setFilterStatus(prev => prev === status ? null : status);

  const handleCreate = async (status="draft") => {
    if(!form.client||!form.amount||!form.desc){alert("Client, description and amount are required.");return;}
    setSaving(true);
    try {
      const qBody = {
        client_name: form.client, description: form.desc,
        amount: +form.amount, vat_applicable: form.vatApplicable,
        currency: form.currency, exchange_rate: +form.rate,
        valid_until: form.validUntil||null, notes: form.notes||null,
        status,
      };
      if (form.currency !== "ZAR") qBody.vat_amount_override = +form.vatAmount || 0;
      const res = await api("/quotes/",{method:"POST",body:JSON.stringify(qBody)});
      setQuotes(prev=>[{id:res.quote_number,_id:res.id,client:res.client_name,desc:res.description,amount:res.amount,totalAmount:res.total_amount,vatApplicable:res.vat_applicable,vatAmount:res.vat_amount,date:res.created_at?.slice(0,10),validUntil:res.valid_until,status:res.status,currency:res.currency,rate:res.exchange_rate,notes:res.notes},...prev]);
      setShowNew(false); setForm(emptyForm);
    } catch(e){alert("Failed to create quote: "+(e.message||"Unknown error"));}
    finally{setSaving(false);}
  };

  const updateStatus = async (_id, status) => {
    try {
      await api(`/quotes/${_id}`,{method:"PUT",body:JSON.stringify({status})});
      setQuotes(prev=>prev.map(q=>q._id===_id?{...q,status}:q));
      if(preview&&preview._id===_id) setPreview(p=>({...p,status}));
    } catch(e){alert("Failed to update status.");}
  };

  const deleteQuote = async (_id) => {
    if(!window.confirm("Delete this quote?")) return;
    try {
      await api(`/quotes/${_id}`,{method:"DELETE"});
      setQuotes(prev=>prev.filter(q=>q._id!==_id));
      setPreview(null);
    } catch(e){alert("Failed to delete quote.");}
  };

  const handleAmend = async () => {
    if(!editQuote.client||!editQuote.amount||!editQuote.desc){alert("Client, description and amount are required.");return;}
    setSaving(true);
    try {
      const body = {
        client_name: editQuote.client, description: editQuote.desc,
        amount: +editQuote.amount, currency: editQuote.currency,
        exchange_rate: +editQuote.rate||1,
        valid_until: editQuote.validUntil||null, notes: editQuote.notes||null,
        vat_applicable: editQuote.vatApplicable,
      };
      if (editQuote.currency !== "ZAR") body.vat_amount_override = +editQuote.vatAmount||0;
      const res = await api(`/quotes/${editQuote._id}`,{method:"PUT",body:JSON.stringify(body)});
      setQuotes(prev=>prev.map(q=>q._id===editQuote._id ? {
        ...q, client:res.client_name, desc:res.description,
        amount:res.amount, totalAmount:res.total_amount,
        vatApplicable:res.vat_applicable, vatAmount:res.vat_amount,
        validUntil:res.valid_until, currency:res.currency,
        rate:res.exchange_rate, notes:res.notes,
      } : q));
      setEditQuote(null);
    } catch(e){alert("Failed to amend quote: "+(e.message||"Unknown error"));}
    finally{setSaving(false);}
  };

  const convertToInvoice = async (q) => {
    if (q.status === "accepted") { alert("This quote has already been converted to an invoice."); return; }
    setSaving(true);
    let invoiceCreated = false;
    try {
      // Payment due date = 30 days from today. The quote's valid-until date is how long the
      // offer stands, not when the client must pay (it could already be in the past).
      const _due = new Date(Date.now() + 30*86400000).toISOString().slice(0,10);
      const ciBody = {
        client_name:   q.client,
        description:   q.desc || "Converted from quote",
        amount:        q.amount,
        vat_applicable: q.vatApplicable,
        quote_ref:     q.id,
        due_date:      _due,
        notes:         q.notes || `Converted from ${q.id}`,
        currency:      q.currency || "ZAR",
        exchange_rate: q.rate ? +q.rate : 1.0,
      };
      if (q.currency && q.currency !== "ZAR") ciBody.vat_amount_override = q.vatAmount || 0;
      await api("/invoices/",{method:"POST",body:JSON.stringify(ciBody)});
      invoiceCreated = true;
      await updateStatus(q._id, "accepted");
      setPreview(null);
      if(live && live.reload) await live.reload();
      if(onNavigate) onNavigate("invoicing");
    } catch(e){alert(invoiceCreated ? "The invoice WAS created, but the quote could not be marked accepted ("+(e.message||"error")+"). Mark it accepted manually so it is not converted twice." : "Failed to convert quote: "+(e.message||"Unknown error"));}
    finally{setSaving(false);}
  };

  const inputStyle = {width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"};
  const isMobile = useIsMobile();
  const printQuote = () => { const w=window.open("","_blank");if(!w)return;w.document.write(`<html><head><title>Quote ${(preview||{}).id||""}</title><style>body{font-family:${(docTemplate||DEFAULT_DOC_TEMPLATE).fontFamily};padding:40px;}</style></head><body>${(document.getElementById("quote-print-content")||{}).innerHTML||""}</body></html>`);w.document.close();w.onload=()=>{w.focus();w.print();w.close();}; };

  return (
    <div>
      <SectionHeader title="Quotes & Estimates" sub="Send quotes and convert to invoices" action="+ New Quote" onAction={()=>setShowNew(true)}/>
      <div style={{display:"flex",gap:12,marginBottom:filterStatus?8:24}}>
        <KPI label="Accepted" value={fmtCurrency(totalAccepted)} color={C.green} icon="✅" sub={`${quotes.filter(q=>q.status==="accepted").length} quotes`} onClick={()=>toggleFilter("accepted")} active={filterStatus==="accepted"}/>
        <KPI label="Pending" value={fmtCurrency(totalPending)} color={C.gold} icon="⏳" sub={`${quotes.filter(q=>q.status==="sent").length} sent`} onClick={()=>toggleFilter("sent")} active={filterStatus==="sent"}/>
        <KPI label="Total Quotes" value={quotes.length} color={C.blue} icon="📝" onClick={()=>setFilterStatus(null)} active={filterStatus===null}/>
      </div>
      {filterStatus && (
        <div style={{background:filterStatus==="accepted"?C.greenLt:C.goldLt,border:`1px solid ${filterStatus==="accepted"?C.green:C.gold}30`,borderRadius:10,padding:"10px 16px",marginBottom:16,display:"flex",alignItems:"center",justifyContent:"space-between",fontSize:13}}>
          <span style={{color:C.ink,fontWeight:500}}>Showing: <strong style={{textTransform:"capitalize"}}>{filterStatus==="sent"?"pending":filterStatus}</strong> ({displayedQuotes.length} quotes)</span>
          <button onClick={()=>setFilterStatus(null)} style={{background:"transparent",border:`1px solid ${C.border}`,borderRadius:6,padding:"3px 10px",fontSize:12,cursor:"pointer",color:C.inkMid,fontFamily:"inherit"}}>✕ Show All</button>
        </div>
      )}

      {showNew && (
        <div style={{background:C.surface,border:`2px solid ${C.accent}`,borderRadius:16,padding:24,marginBottom:20}}>
          <h3 style={{fontFamily:"serif",margin:"0 0 16px",color:C.ink}}>New Quote</h3>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
            <div>
              <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Client</label>
              {customers.length>0 && (
                <select value={form.client} onChange={e=>setForm(f=>({...f,client:e.target.value}))} style={{...inputStyle,marginBottom:6}}>
                  <option value="">— Select from customers —</option>
                  {customers.map(c=><option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
              )}
              <input placeholder="Or type new client name" value={form.client} onChange={e=>setForm(f=>({...f,client:e.target.value}))} style={{...inputStyle,marginTop:customers.length>0?4:0}}/>
            </div>
            <div><label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Amount</label><input type="number" placeholder="50000" value={form.amount} onChange={e=>setForm(f=>({...f,amount:e.target.value}))} style={inputStyle}/></div>
            <div><label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Description</label><input placeholder="Services rendered" value={form.desc} onChange={e=>setForm(f=>({...f,desc:e.target.value}))} style={inputStyle}/></div>
            <div><label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Valid Until</label><input type="date" value={form.validUntil} onChange={e=>setForm(f=>({...f,validUntil:e.target.value}))} style={inputStyle}/></div>
            <div>
              <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Currency</label>
              <select value={form.currency} onChange={e=>setForm(f=>({...f,currency:e.target.value}))} style={inputStyle}>
                <option value="ZAR">ZAR — South African Rand</option>
                <option value="USD">USD — US Dollar</option>
                <option value="EUR">EUR — Euro</option>
                <option value="GBP">GBP — British Pound</option>
                <option value="BWP">BWP — Botswana Pula</option>
                <option value="ZMW">ZMW — Zambian Kwacha</option>
                <option value="MZN">MZN — Mozambican Metical</option>
                <option value="NAD">NAD — Namibian Dollar</option>
                <option value="KES">KES — Kenyan Shilling</option>
                <option value="CNY">CNY — Chinese Yuan</option>
              </select>
            </div>
            {form.currency!=="ZAR" && <div><label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Exchange Rate (1 {form.currency} = R...)</label><input type="number" value={form.rate} onChange={e=>setForm(f=>({...f,rate:e.target.value}))} style={inputStyle}/></div>}
            {form.currency!=="ZAR" && <div><label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>VAT Amount ({form.currency}) — 0 if not applicable</label><input type="number" min="0" placeholder="0" value={form.vatAmount} onChange={e=>setForm(f=>({...f,vatAmount:e.target.value}))} style={inputStyle}/></div>}
            <div style={{gridColumn:"1/-1"}}><label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Notes (optional)</label><input placeholder="Payment terms, delivery details..." value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} style={inputStyle}/></div>
          </div>
          {form.amount && form.currency!=="ZAR" && (
            <div style={{background:C.blueLt,borderRadius:8,padding:"10px 14px",marginBottom:12,fontSize:12,color:C.blue}}>
              {form.currency} {fmtCurrency(form.amount,form.currency)}
              {+form.vatAmount > 0 && <> + VAT {fmtCurrency(form.vatAmount,form.currency)} = {fmtCurrency(+form.amount + +form.vatAmount, form.currency)}</>}
              {" "}≈ {fmt((+form.amount + (+form.vatAmount||0)) * +form.rate)} ZAR at R{form.rate}/{form.currency}
            </div>
          )}
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            <button onClick={()=>handleCreate("draft")} disabled={saving} style={{background:C.surface,color:C.inkMid,border:`1px solid ${C.border}`,borderRadius:10,padding:"10px 20px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",opacity:saving?0.6:1}}>{saving?"Saving...":"Save as Draft"}</button>
            <button onClick={()=>handleCreate("sent")} disabled={saving} style={{background:C.accent,color:"#fff",border:"none",borderRadius:10,padding:"10px 20px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",opacity:saving?0.6:1}}>{saving?"Saving...":"Send Quote"}</button>
            <button onClick={()=>setShowNew(false)} style={{background:"transparent",color:C.inkMid,border:`1px solid ${C.border}`,borderRadius:10,padding:"10px 20px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
          </div>
        </div>
      )}

      {/* Amend Quote Modal */}
      {editQuote && (
        <div style={{position:"fixed",inset:0,background:"#00000060",zIndex:100,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={()=>setEditQuote(null)}>
          <div style={{background:C.surface,borderRadius:20,padding:32,width:580,maxHeight:"90vh",overflow:"auto"}} onClick={e=>e.stopPropagation()}>
            <h3 style={{fontFamily:"serif",fontSize:22,color:C.ink,margin:"0 0 20px"}}>Amend Quote — {editQuote.id}</h3>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
              <div><label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Client Name</label><input value={editQuote.client||""} onChange={e=>setEditQuote(q=>({...q,client:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}/></div>
              <div><label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Amount (excl. VAT)</label><input type="number" value={editQuote.amount||""} onChange={e=>setEditQuote(q=>({...q,amount:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}/></div>
              <div style={{gridColumn:"1/-1"}}><label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Description</label><input value={editQuote.desc||""} onChange={e=>setEditQuote(q=>({...q,desc:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}/></div>
              <div><label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Valid Until</label><input type="date" value={editQuote.validUntil||""} onChange={e=>setEditQuote(q=>({...q,validUntil:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}/></div>
              <div><label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Currency</label>
                <select value={editQuote.currency||"ZAR"} onChange={e=>setEditQuote(q=>({...q,currency:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}>
                  <option value="ZAR">ZAR — South African Rand</option>
                  <option value="USD">USD — US Dollar</option>
                  <option value="EUR">EUR — Euro</option>
                  <option value="GBP">GBP — British Pound</option>
                  <option value="BWP">BWP — Botswana Pula</option>
                  <option value="ZMW">ZMW — Zambian Kwacha</option>
                  <option value="MZN">MZN — Mozambican Metical</option>
                  <option value="NAD">NAD — Namibian Dollar</option>
                  <option value="KES">KES — Kenyan Shilling</option>
                  <option value="CNY">CNY — Chinese Yuan</option>
                </select>
              </div>
              {editQuote.currency!=="ZAR" && <div><label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Exchange Rate (1 {editQuote.currency} = R...)</label><input type="number" value={editQuote.rate||"18.5"} onChange={e=>setEditQuote(q=>({...q,rate:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}/></div>}
              {editQuote.currency!=="ZAR" && <div><label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>VAT Amount ({editQuote.currency}) — 0 if not applicable</label><input type="number" min="0" value={editQuote.vatAmount||"0"} onChange={e=>setEditQuote(q=>({...q,vatAmount:e.target.value}))} style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}/></div>}
              {(!editQuote.currency||editQuote.currency==="ZAR") && (
                <div style={{gridColumn:"1/-1",display:"flex",alignItems:"center",gap:10}}>
                  <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",fontSize:13,color:C.ink}}>
                    <input type="checkbox" checked={editQuote.vatApplicable!==false} onChange={e=>setEditQuote(q=>({...q,vatApplicable:e.target.checked}))} style={{width:16,height:16,cursor:"pointer"}}/>
                    Apply VAT @ 15%
                  </label>
                </div>
              )}
              <div style={{gridColumn:"1/-1"}}><label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Notes (optional)</label><input value={editQuote.notes||""} onChange={e=>setEditQuote(q=>({...q,notes:e.target.value}))} placeholder="Payment terms, delivery details..." style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"}}/></div>
            </div>
            <div style={{display:"flex",gap:8,marginTop:8}}>
              <button onClick={handleAmend} disabled={saving} style={{background:C.accent,color:"#fff",border:"none",borderRadius:10,padding:"11px 20px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",opacity:saving?0.6:1}}>{saving?"Saving...":"Save Changes"}</button>
              <button onClick={()=>setEditQuote(null)} style={{background:"transparent",color:C.inkMid,border:`1px solid ${C.border}`,borderRadius:10,padding:"11px 20px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── MOBILE: Full-screen quote detail ── */}
      {preview && isMobile && (
        <div style={{position:"fixed",inset:0,background:C.bg,zIndex:200,display:"flex",flexDirection:"column"}}>
          {/* Sticky header */}
          <div style={{display:"flex",alignItems:"center",gap:12,padding:"14px 16px",background:C.surface,borderBottom:`1px solid ${C.border}`,flexShrink:0}}>
            <button onClick={()=>setPreview(null)} style={{background:"none",border:"none",fontSize:22,cursor:"pointer",color:C.ink,lineHeight:1,padding:"0 4px"}}>←</button>
            <div style={{flex:1}}>
              <div style={{fontWeight:800,fontSize:16,color:C.accent}}>{preview.id}</div>
              <div style={{fontSize:12,color:C.inkMid}}>{preview.client}</div>
            </div>
            {(() => { const [label,color,bg]=QUOTE_STATUSES[preview.status]||QUOTE_STATUSES.draft; return <Badge label={label} color={color} bg={bg}/>; })()}
          </div>
          {/* Scrollable body */}
          <div style={{flex:1,overflowY:"auto",padding:"20px 16px",WebkitOverflowScrolling:"touch"}}>
            <div id="quote-print-content">
              <InvoiceDocument type="quote" doc={preview} user={user} tmpl={docTemplate}/>
              {preview.currency&&preview.currency!=="ZAR" && <div style={{background:C.blueLt,borderRadius:8,padding:"10px 14px",marginTop:12,fontSize:12,color:C.blue}}>≈ {fmt((preview.totalAmount||preview.amount)*(preview.rate||18.5))} ZAR at R{preview.rate||18.5}/{preview.currency}</div>}
              {preview.notes && <div style={{background:C.bg,borderRadius:8,padding:"10px 14px",marginTop:10,fontSize:12,color:C.inkMid}}><strong>Notes:</strong> {preview.notes}</div>}
            </div>
          </div>
          {/* Sticky action bar */}
          <div style={{flexShrink:0,background:C.surface,borderTop:`1px solid ${C.border}`,padding:"12px 16px",display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            {preview.status==="sent" && <button onClick={()=>{setPreview(null);convertToInvoice(preview);}} disabled={saving} style={{gridColumn:"1/-1",background:C.green,color:"#fff",border:"none",borderRadius:12,padding:"14px",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit",opacity:saving?0.6:1}}>{saving?"Converting...":"✓ Convert to Invoice"}</button>}
            {preview.status==="draft" && <button onClick={()=>updateStatus(preview._id,"sent")} style={{gridColumn:"1/-1",background:C.accent,color:"#fff",border:"none",borderRadius:12,padding:"14px",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Send Quote</button>}
            <button onClick={()=>{setPreview(null);setEditQuote({...preview,rate:preview.rate||"18.5",vatAmount:preview.vatAmount||"0"});}} style={{background:C.goldLt,color:C.gold,border:`1px solid ${C.gold}30`,borderRadius:12,padding:"13px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Amend</button>
            <button onClick={printQuote} style={{background:C.accent,color:"#fff",border:"none",borderRadius:12,padding:"13px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Print / PDF</button>
            {preview.status==="sent" && <button onClick={()=>updateStatus(preview._id,"declined")} style={{background:C.redLt,color:C.red,border:`1px solid ${C.red}30`,borderRadius:12,padding:"13px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Decline</button>}
            <button onClick={()=>{if(!window.confirm(`Delete ${preview.id}?`))return;deleteQuote(preview._id);setPreview(null);}} style={{background:C.redLt,color:C.red,border:`1px solid ${C.red}30`,borderRadius:12,padding:"13px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Delete</button>
          </div>
        </div>
      )}

      {/* ── DESKTOP: floating modal ── */}
      {preview && !isMobile && (
        <div style={{position:"fixed",inset:0,background:"#00000060",zIndex:100,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setPreview(null)}>
          <div style={{background:"#fff",borderRadius:20,padding:40,width:580,maxHeight:"85vh",overflow:"auto"}} onClick={e=>e.stopPropagation()}>
            <div id="quote-print-content">
              <InvoiceDocument type="quote" doc={preview} user={user} tmpl={docTemplate}/>
              {preview.currency&&preview.currency!=="ZAR" && <div style={{background:C.blueLt,borderRadius:8,padding:"10px 14px",marginTop:12,fontSize:12,color:C.blue}}>ZAR equivalent: ≈ {fmt((preview.totalAmount||preview.amount)*(preview.rate||18.5))} at R{preview.rate||18.5}/{preview.currency}</div>}
              {preview.notes && <div style={{background:C.bg,borderRadius:8,padding:"10px 14px",marginTop:10,fontSize:12,color:C.inkMid}}><strong>Notes:</strong> {preview.notes}</div>}
            </div>
            <div style={{display:"flex",gap:8,marginTop:20}}>
              <button onClick={()=>{setPreview(null);setEditQuote({...preview,rate:preview.rate||"18.5",vatAmount:preview.vatAmount||"0"});}} style={{flex:1,background:C.goldLt,color:C.gold,border:`1px solid ${C.gold}30`,borderRadius:10,padding:"11px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Amend</button>
              <button onClick={()=>convertToInvoice(preview)} disabled={saving} style={{flex:1,background:C.greenLt,color:C.green,border:`1px solid ${C.green}30`,borderRadius:10,padding:"11px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",opacity:saving?0.6:1}}>{saving?"Converting...":"Convert to Invoice"}</button>
              <button onClick={printQuote} style={{flex:1,background:C.accent,color:"#fff",border:"none",borderRadius:10,padding:"11px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Print / PDF</button>
              <button onClick={()=>setPreview(null)} style={{flex:1,background:"transparent",border:`1px solid ${C.border}`,borderRadius:10,padding:"11px",fontSize:13,cursor:"pointer",fontFamily:"inherit",color:C.inkMid}}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── MOBILE: card list ── */}
      {isMobile ? (
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {quotes.length===0 && <div style={{padding:"32px",textAlign:"center",color:C.inkMid,fontSize:13,background:C.surface,borderRadius:16,border:`1px solid ${C.border}`}}>No quotes yet. Create your first quote above.</div>}
          {displayedQuotes.map(q=>{
            const [label,color,bg]=QUOTE_STATUSES[q.status]||QUOTE_STATUSES.draft;
            return (
              <div key={q.id} onClick={()=>setPreview(q)} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:14,padding:"16px",cursor:"pointer",WebkitTapHighlightColor:"transparent"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                  <div style={{fontWeight:800,fontSize:15,color:C.accent}}>{q.id}</div>
                  <Badge label={label} color={color} bg={bg}/>
                </div>
                <div style={{fontWeight:600,fontSize:14,color:C.ink,marginBottom:2}}>{q.client}</div>
                <div style={{fontSize:12,color:C.inkMid,marginBottom:10,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{q.desc}</div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline"}}>
                  <div style={{fontWeight:800,fontSize:16,color:C.ink}}>{fmt(q.totalAmount||q.amount||0)}</div>
                  <div style={{fontSize:11,color:C.inkMid}}>{q.validUntil?`Valid: ${fmtDate(q.validUntil)}`:"—"}</div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* ── DESKTOP: table ── */
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,overflow:"hidden"}}>
          {quotes.length===0 ? (
            <div style={{padding:48,textAlign:"center",color:C.inkMid,fontSize:13}}>No quotes yet. Create your first quote above.</div>
          ) : (
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:13,minWidth:700}}>
              <thead><tr style={{background:C.bg,borderBottom:`1px solid ${C.border}`}}>{["Quote #","Client","Description","Amount","Currency","Valid Until","Status","Actions"].map(h=><th key={h} style={{textAlign:"left",padding:"12px 14px",fontSize:10,color:C.inkMid,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase"}}>{h}</th>)}</tr></thead>
              <tbody>
                {displayedQuotes.length===0 && <tr><td colSpan={8} style={{padding:"32px",textAlign:"center",color:C.inkMid,fontSize:13}}>No {filterStatus==="sent"?"pending":filterStatus||""} quotes.</td></tr>}
                {displayedQuotes.map(q=>{
                  const [label,color,bg] = QUOTE_STATUSES[q.status]||QUOTE_STATUSES.draft;
                  return (
                    <tr key={q.id} style={{borderBottom:`1px solid ${C.border}30`}}>
                      <td style={{padding:"13px 14px",fontWeight:700,color:C.accent,cursor:"pointer"}} onClick={()=>setPreview(q)}>{q.id}</td>
                      <td style={{padding:"13px 14px",fontWeight:500}}>{q.client}</td>
                      <td style={{padding:"13px 14px",color:C.inkMid,fontSize:12}}>{q.desc}</td>
                      <td style={{padding:"13px 14px",fontWeight:700}}>
                        {q.currency && q.currency!=="ZAR"
                          ? <>{fmt((q.totalAmount||q.amount||0)*(q.rate||1))}<div style={{fontSize:10,color:C.blue,fontWeight:400,marginTop:2}}>{fmtCurrency(q.totalAmount||q.amount,q.currency)}</div></>
                          : fmt(q.totalAmount||q.amount||0)}
                      </td>
                      <td style={{padding:"13px 14px"}}><Badge label={q.currency||"ZAR"} color={C.blue} bg={C.blueLt}/></td>
                      <td style={{padding:"13px 14px",color:C.inkMid}}>{q.validUntil?fmtDate(q.validUntil):"—"}</td>
                      <td style={{padding:"13px 14px"}}><Badge label={label} color={color} bg={bg}/></td>
                      <td style={{padding:"13px 14px"}}>
                        <div style={{display:"flex",gap:5}}>
                          <button onClick={()=>setPreview(q)} style={{background:C.blueLt,color:C.blue,border:"none",borderRadius:6,padding:"4px 8px",fontSize:10,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>View</button>
                          <button onClick={()=>setEditQuote({...q,rate:q.rate||"18.5",vatAmount:q.vatAmount||"0"})} style={{background:C.goldLt,color:C.gold,border:"none",borderRadius:6,padding:"4px 8px",fontSize:10,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>Amend</button>
                          {q.status==="draft" && <button onClick={()=>updateStatus(q._id,"sent")} style={{background:C.goldLt,color:C.gold,border:"none",borderRadius:6,padding:"4px 8px",fontSize:10,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>Send</button>}
                          {q.status==="sent"  && <button onClick={()=>convertToInvoice(q)} style={{background:C.greenLt,color:C.green,border:"none",borderRadius:6,padding:"4px 8px",fontSize:10,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>Accept</button>}
                          {q.status==="sent"  && <button onClick={()=>updateStatus(q._id,"declined")} style={{background:C.redLt,color:C.red,border:"none",borderRadius:6,padding:"4px 8px",fontSize:10,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>Decline</button>}
                          <button onClick={()=>deleteQuote(q._id)} style={{background:C.redLt,color:C.red,border:"none",borderRadius:6,padding:"4px 8px",fontSize:10,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

// ── MULTI-CURRENCY: update Invoicing form to support USD ──────────────────────
// (currency selector added inline in Invoicing component below via patch)

// ── CUSTOMERS ─────────────────────────────────────────────────────────────────
function Customers() {
  const [list,    setList]   = useState([]);
  const [showForm,setShowForm]= useState(false);
  const [editing, setEditing] = useState(null);
  const [search,  setSearch] = useState("");
  const empty = {name:"",contact_person:"",email:"",phone:"",address:"",vat_number:"",payment_terms:30,notes:""};
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const f = k => e => setForm(d=>({...d,[k]:e.target.value}));
  const is = {width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontFamily:"inherit",fontSize:13,background:C.bg};
  const lb = t => <div style={{fontSize:11,fontWeight:600,color:C.inkMid,marginBottom:4}}>{t}</div>;

  const load = async () => {
    try { const d = await api("/customers/"); setList(d); } catch(e){}
  };
  useEffect(()=>{load();},[]);

  const save = async () => {
    if(!form.name){alert("Customer name is required.");return;}
    setSaving(true);
    try {
      if (editing) await api(`/customers/${editing.id}`, {method:"PUT",body:JSON.stringify(form)});
      else await api("/customers/", {method:"POST",body:JSON.stringify(form)});
      setShowForm(false); setEditing(null); setForm(empty); load();
    } catch(e){ alert(e.message); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this customer?")) return;
    try { await api(`/customers/${id}`,{method:"DELETE"}); load(); } catch(e){}
  };

  const edit = (c) => { setEditing(c); setForm({name:c.name,contact_person:c.contact_person||"",email:c.email||"",phone:c.phone||"",address:c.address||"",vat_number:c.vat_number||"",payment_terms:c.payment_terms||30,notes:c.notes||""}); setShowForm(true); };

  const filtered = list.filter(c => c.name.toLowerCase().includes(search.toLowerCase()) || (c.email||"").toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:24}}>
        <div>
          <h2 style={{fontSize:22,fontWeight:700,color:C.ink}}>Customers</h2>
          <p style={{fontSize:13,color:C.inkMid,marginTop:2}}>{list.length} customer{list.length!==1?"s":""} on record</p>
        </div>
        <button onClick={()=>{setEditing(null);setForm(empty);setShowForm(true);}} style={{background:C.accent,color:"#fff",border:"none",borderRadius:8,padding:"10px 18px",fontWeight:600,cursor:"pointer",fontSize:13}}>+ Add Customer</button>
      </div>

      <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search customers..." style={{...is,marginBottom:16,maxWidth:320}}/>

      {showForm && (
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:24,marginBottom:24}}>
          <h3 style={{fontWeight:700,marginBottom:16,color:C.ink}}>{editing?"Edit Customer":"New Customer"}</h3>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
            <div>{lb("Company / Customer Name *")}<input value={form.name} onChange={f("name")} style={is}/></div>
            <div>{lb("Contact Person")}<input value={form.contact_person} onChange={f("contact_person")} style={is}/></div>
            <div>{lb("Email")}<input value={form.email} onChange={f("email")} type="email" style={is}/></div>
            <div>{lb("Phone")}<input value={form.phone} onChange={f("phone")} style={is}/></div>
            <div>{lb("VAT Number")}<input value={form.vat_number} onChange={f("vat_number")} style={is}/></div>
            <div>{lb("Payment Terms")}<select value={form.payment_terms} onChange={f("payment_terms")} style={is}><option value={0}>COD (Cash on Delivery)</option><option value={7}>7 days</option><option value={14}>14 days</option><option value={30}>30 days</option><option value={60}>60 days</option><option value={90}>90 days</option></select></div>
          </div>
          <div style={{marginBottom:12}}>{lb("Address")}<textarea value={form.address} onChange={f("address")} rows={2} style={{...is,resize:"vertical"}}/></div>
          <div style={{marginBottom:16}}>{lb("Notes")}<textarea value={form.notes} onChange={f("notes")} rows={2} style={{...is,resize:"vertical"}}/></div>
          <div style={{display:"flex",gap:10}}>
            <button onClick={save} disabled={saving} style={{background:C.accent,color:"#fff",border:"none",borderRadius:8,padding:"10px 24px",fontWeight:600,cursor:"pointer",opacity:saving?0.6:1}}>{saving?"Saving...":"Save"}</button>
            <button onClick={()=>{setShowForm(false);setEditing(null);}} style={{background:"transparent",border:`1px solid ${C.border}`,borderRadius:8,padding:"10px 24px",cursor:"pointer",color:C.inkMid}}>Cancel</button>
          </div>
        </div>
      )}

      {filtered.length === 0 ? (
        <div style={{textAlign:"center",padding:"60px 0",color:C.inkDim}}>
          <div style={{fontSize:40,marginBottom:12}}>👥</div>
          <div style={{fontSize:15,fontWeight:600}}>No customers yet</div>
          <div style={{fontSize:13,marginTop:4}}>Add your first customer to get started</div>
        </div>
      ) : (
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))",gap:12}}>
          {filtered.map(c=>(
            <div key={c.id} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:18}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                  <div style={{fontWeight:700,fontSize:15,color:C.ink}}>{c.name}</div>
                  {c.notes === "Auto-created from linked client company" && (
                    <span title="Auto-created when this practice linked to the client company via Accountant sync — not added manually" style={{fontSize:10,fontWeight:600,color:C.accent,background:C.accent+"15",border:`1px solid ${C.accent}30`,borderRadius:6,padding:"2px 6px"}}>🔗 Linked client</span>
                  )}
                </div>
                <div style={{display:"flex",gap:6}}>
                  <button onClick={()=>edit(c)} style={{background:"none",border:`1px solid ${C.border}`,borderRadius:6,padding:"4px 10px",fontSize:12,cursor:"pointer",color:C.inkMid}}>Edit</button>
                  <button onClick={()=>del(c.id)} style={{background:"none",border:`1px solid ${C.red}20`,borderRadius:6,padding:"4px 10px",fontSize:12,cursor:"pointer",color:C.red}}>Delete</button>
                </div>
              </div>
              {c.contact_person && <div style={{fontSize:12,color:C.inkMid,marginBottom:4}}>👤 {c.contact_person}</div>}
              {c.email && <div style={{fontSize:12,color:C.inkMid,marginBottom:4}}>✉ {c.email}</div>}
              {c.phone && <div style={{fontSize:12,color:C.inkMid,marginBottom:4}}>📞 {c.phone}</div>}
              {c.vat_number && <div style={{fontSize:11,color:C.inkDim,marginBottom:4}}>VAT: {c.vat_number}</div>}
              <div style={{fontSize:11,color:C.inkDim,marginTop:4,paddingTop:8,borderTop:`1px solid ${C.border}`}}>Payment terms: {c.payment_terms} days</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── SUPPLIERS ─────────────────────────────────────────────────────────────────
function Suppliers() {
  const [list,    setList]   = useState([]);
  const [showForm,setShowForm]= useState(false);
  const [editing, setEditing] = useState(null);
  const [search,  setSearch] = useState("");
  const empty = {name:"",contact_person:"",email:"",phone:"",address:"",vat_number:"",bank_name:"",account_number:"",branch_code:"",account_type:"Cheque",payment_terms:30,notes:""};
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const f = k => e => setForm(d=>({...d,[k]:e.target.value}));
  const is = {width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontFamily:"inherit",fontSize:13,background:C.bg};
  const lb = t => <div style={{fontSize:11,fontWeight:600,color:C.inkMid,marginBottom:4}}>{t}</div>;

  const load = async () => { try { setList(await api("/suppliers/")); } catch(e){} };
  useEffect(()=>{load();},[]);

  const save = async () => {
    if(!form.name){alert("Supplier name is required.");return;}
    setSaving(true);
    try {
      if (editing) await api(`/suppliers/${editing.id}`,{method:"PUT",body:JSON.stringify(form)});
      else await api("/suppliers/",{method:"POST",body:JSON.stringify(form)});
      setShowForm(false); setEditing(null); setForm(empty); load();
    } catch(e){ alert(e.message); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this supplier?")) return;
    try { await api(`/suppliers/${id}`,{method:"DELETE"}); load(); } catch(e){}
  };

  const edit = (s) => { setEditing(s); setForm({name:s.name,contact_person:s.contact_person||"",email:s.email||"",phone:s.phone||"",address:s.address||"",vat_number:s.vat_number||"",bank_name:s.bank_name||"",account_number:s.account_number||"",branch_code:s.branch_code||"",account_type:s.account_type||"Cheque",payment_terms:s.payment_terms||30,notes:s.notes||""}); setShowForm(true); };

  const filtered = list.filter(s => s.name.toLowerCase().includes(search.toLowerCase()) || (s.email||"").toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:24}}>
        <div>
          <h2 style={{fontSize:22,fontWeight:700,color:C.ink}}>Suppliers</h2>
          <p style={{fontSize:13,color:C.inkMid,marginTop:2}}>{list.length} supplier{list.length!==1?"s":""} on record</p>
        </div>
        <button onClick={()=>{setEditing(null);setForm(empty);setShowForm(true);}} style={{background:C.accent,color:"#fff",border:"none",borderRadius:8,padding:"10px 18px",fontWeight:600,cursor:"pointer",fontSize:13}}>+ Add Supplier</button>
      </div>

      <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search suppliers..." style={{...is,marginBottom:16,maxWidth:320}}/>

      {showForm && (
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:24,marginBottom:24}}>
          <h3 style={{fontWeight:700,marginBottom:16,color:C.ink}}>{editing?"Edit Supplier":"New Supplier"}</h3>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
            <div>{lb("Supplier Name *")}<input value={form.name} onChange={f("name")} style={is}/></div>
            <div>{lb("Contact Person")}<input value={form.contact_person} onChange={f("contact_person")} style={is}/></div>
            <div>{lb("Email")}<input value={form.email} onChange={f("email")} type="email" style={is}/></div>
            <div>{lb("Phone")}<input value={form.phone} onChange={f("phone")} style={is}/></div>
            <div>{lb("VAT Number")}<input value={form.vat_number} onChange={f("vat_number")} style={is}/></div>
            <div>{lb("Payment Terms")}<select value={form.payment_terms} onChange={f("payment_terms")} style={is}><option value={0}>COD (Cash on Delivery)</option><option value={7}>7 days</option><option value={14}>14 days</option><option value={30}>30 days</option><option value={60}>60 days</option><option value={90}>90 days</option></select></div>
          </div>
          <div style={{marginBottom:12}}>{lb("Address")}<textarea value={form.address} onChange={f("address")} rows={2} style={{...is,resize:"vertical"}}/></div>
          <div style={{fontWeight:600,fontSize:13,color:C.ink,marginBottom:10,paddingTop:8,borderTop:`1px solid ${C.border}`}}>Banking Details</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16}}>
            <div>{lb("Bank Name")}<select value={form.bank_name} onChange={f("bank_name")} style={is}><option value="">-- Select Bank --</option>{["ABSA","Capitec","Discovery Bank","FNB","Investec","Nedbank","Standard Bank","TymeBank","African Bank","Other"].map(b=><option key={b}>{b}</option>)}</select></div>
            <div>{lb("Account Type")}<select value={form.account_type} onChange={f("account_type")} style={is}><option>Cheque</option><option>Savings</option><option>Transmission</option></select></div>
            <div>{lb("Account Number")}<input value={form.account_number} onChange={f("account_number")} style={is}/></div>
            <div>{lb("Branch Code")}<input value={form.branch_code} onChange={f("branch_code")} style={is}/></div>
          </div>
          <div style={{marginBottom:16}}>{lb("Notes")}<textarea value={form.notes} onChange={f("notes")} rows={2} style={{...is,resize:"vertical"}}/></div>
          <div style={{display:"flex",gap:10}}>
            <button onClick={save} disabled={saving} style={{background:C.accent,color:"#fff",border:"none",borderRadius:8,padding:"10px 24px",fontWeight:600,cursor:"pointer",opacity:saving?0.6:1}}>{saving?"Saving...":"Save"}</button>
            <button onClick={()=>{setShowForm(false);setEditing(null);}} style={{background:"transparent",border:`1px solid ${C.border}`,borderRadius:8,padding:"10px 24px",cursor:"pointer",color:C.inkMid}}>Cancel</button>
          </div>
        </div>
      )}

      {filtered.length === 0 ? (
        <div style={{textAlign:"center",padding:"60px 0",color:C.inkDim}}>
          <div style={{fontSize:40,marginBottom:12}}>🏭</div>
          <div style={{fontSize:15,fontWeight:600}}>No suppliers yet</div>
          <div style={{fontSize:13,marginTop:4}}>Add your first supplier to get started</div>
        </div>
      ) : (
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))",gap:12}}>
          {filtered.map(s=>(
            <div key={s.id} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:18}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                <div style={{fontWeight:700,fontSize:15,color:C.ink}}>{s.name}</div>
                <div style={{display:"flex",gap:6}}>
                  <button onClick={()=>edit(s)} style={{background:"none",border:`1px solid ${C.border}`,borderRadius:6,padding:"4px 10px",fontSize:12,cursor:"pointer",color:C.inkMid}}>Edit</button>
                  <button onClick={()=>del(s.id)} style={{background:"none",border:`1px solid ${C.red}20`,borderRadius:6,padding:"4px 10px",fontSize:12,cursor:"pointer",color:C.red}}>Delete</button>
                </div>
              </div>
              {s.contact_person && <div style={{fontSize:12,color:C.inkMid,marginBottom:4}}>👤 {s.contact_person}</div>}
              {s.email && <div style={{fontSize:12,color:C.inkMid,marginBottom:4}}>✉ {s.email}</div>}
              {s.phone && <div style={{fontSize:12,color:C.inkMid,marginBottom:4}}>📞 {s.phone}</div>}
              {s.account_number && <div style={{fontSize:11,color:C.inkDim,marginBottom:4}}>🏦 {s.bank_name} — {s.account_number}</div>}
              <div style={{fontSize:11,color:C.inkDim,marginTop:4,paddingTop:8,borderTop:`1px solid ${C.border}`}}>Payment terms: {s.payment_terms} days</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── PURCHASE ORDERS ────────────────────────────────────────────────────────────
const PO_STATUSES = ["draft","sent","received","partial","cancelled"];
const PO_STATUS_COLORS = {draft:C.goldLt,sent:C.blueLt,received:C.greenLt,partial:C.accentLt,cancelled:C.redLt};
const PO_STATUS_TEXT   = {draft:C.gold,sent:C.blue,received:C.green,partial:C.accent,cancelled:C.red};

function PurchaseOrders() {
  const [list,      setList]     = useState([]);
  const [suppliers, setSuppliers]= useState([]);
  const [showForm,  setShowForm] = useState(false);
  const [viewing,   setViewing]  = useState(null);
  const [receiving, setReceiving]= useState(null); // PO being receipted
  const [recvQtys,  setRecvQtys] = useState({});   // {item_id: qty_received}
  const [recvCategory, setRecvCategory] = useState("6000 - Cost of Sales");
  const [recvDone,  setRecvDone] = useState(null);  // result after receiving
  const [statusFilter, setStatusFilter] = useState("all");
  const emptyForm = {supplier_id:"",supplier_name:"",delivery_date:"",vat_applicable:true,notes:"",items:[{description:"",quantity:1,unit_price:0}]};
  const [form, setForm] = useState(emptyForm);
  const is = {width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontFamily:"inherit",fontSize:13,background:C.bg};
  const lb = t => <div style={{fontSize:11,fontWeight:600,color:C.inkMid,marginBottom:4}}>{t}</div>;

  const load = async () => {
    try {
      const [pos, sups] = await Promise.all([api("/purchase-orders/"), api("/suppliers/")]);
      setList(pos); setSuppliers(sups);
    } catch(e){ console.warn("PO load error:", e.message); }
  };
  useEffect(()=>{load();},[]);

  const subtotal = form.items.reduce((s,i)=>s+(parseFloat(i.quantity)||0)*(parseFloat(i.unit_price)||0),0);
  const vat      = form.vat_applicable ? subtotal*0.15 : 0;
  const total    = subtotal + vat;

  const setItem = (idx,key,val) => setForm(d=>({...d,items:d.items.map((it,i)=>i===idx?{...it,[key]:val}:it)}));
  const addItem = () => setForm(d=>({...d,items:[...d.items,{description:"",quantity:1,unit_price:0}]}));
  const removeItem = idx => setForm(d=>({...d,items:d.items.filter((_,i)=>i!==idx)}));

  const [saving,  setSaving]  = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [editingPO, setEditingPO] = useState(null); // PO being amended (null = creating new)

  const pingAndSubmit = async (payload, isEdit) => {
    for (let i = 0; i < 18; i++) {
      try {
        setSaveMsg(i === 0 ? "Connecting…" : `Starting up… (${i * 10}s)`);
        const h = await fetch(`${BASE_URL}/health`);
        if (h.ok) break;
      } catch(e) {
        if (i === 17) { setSaving(false); setSaveMsg(""); alert("Server is not responding. Please try again later."); return; }
        await new Promise(r => setTimeout(r, 10000)); continue;
      }
      break;
    }
    setSaveMsg("Saving…");
    try {
      if (isEdit) {
        await api(`/purchase-orders/${editingPO.id}`, {method:"PUT", body:JSON.stringify(payload)});
      } else {
        await api("/purchase-orders/", {method:"POST", body:JSON.stringify(payload)});
      }
      setSaving(false); setSaveMsg("");
      setShowForm(false); setForm(emptyForm); setEditingPO(null); load();
    } catch(e) { setSaving(false); setSaveMsg(""); alert(e.message); }
  };

  const save = async () => {
    if (!form.supplier_name && !form.supplier_id) { alert("Please select or enter a supplier"); return; }
    setSaving(true);
    const payload = {...form, supplier_id: form.supplier_id ? parseInt(form.supplier_id) : null};
    await pingAndSubmit(payload, !!editingPO);
  };

  const saveDraft = async () => {
    if (!form.supplier_name && !form.supplier_id) { alert("Please enter at least a supplier name to save a draft"); return; }
    setSaving(true);
    const payload = {...form, supplier_id: form.supplier_id ? parseInt(form.supplier_id) : null, status:"draft"};
    await pingAndSubmit(payload, !!editingPO);
  };

  const startEdit = (po) => {
    setForm({
      supplier_id: po.supplier_id || "",
      supplier_name: po.supplier_name || "",
      delivery_date: po.delivery_date || "",
      vat_applicable: po.vat_amount > 0,
      notes: po.notes || "",
      items: po.items.map(i => ({description: i.description, quantity: i.quantity, unit_price: i.unit_price})),
    });
    setEditingPO(po);
    setViewing(null);
    setShowForm(true);
  };

  const updateStatus = async (id, status) => {
    try { await api(`/purchase-orders/${id}`,{method:"PUT",body:JSON.stringify({status})}); load(); } catch(e){}
  };

  const del = async (id) => {
    if (!window.confirm("Delete this purchase order?")) return;
    try { await api(`/purchase-orders/${id}`,{method:"DELETE"}); setViewing(null); load(); } catch(e){}
  };

  const filtered = list.filter(p => statusFilter==="all" || p.status===statusFilter);

  return (
    <div>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:24}}>
        <div>
          <h2 style={{fontSize:22,fontWeight:700,color:C.ink}}>Purchase Orders</h2>
          <p style={{fontSize:13,color:C.inkMid,marginTop:2}}>{list.length} PO{list.length!==1?"s":""} total</p>
        </div>
        <button onClick={async()=>{setShowForm(true);setViewing(null);setEditingPO(null);setForm(emptyForm);try{const sups=await api("/suppliers/");setSuppliers(sups);}catch(e){}}} style={{background:C.accent,color:"#fff",border:"none",borderRadius:8,padding:"10px 18px",fontWeight:600,cursor:"pointer",fontSize:13}}>+ New PO</button>
      </div>

      {/* Status filter */}
      <div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap"}}>
        {["all",...PO_STATUSES].map(s=>(
          <button key={s} onClick={()=>setStatusFilter(s)} style={{padding:"6px 14px",borderRadius:20,border:`1px solid ${C.border}`,fontSize:12,cursor:"pointer",fontFamily:"inherit",background:statusFilter===s?C.accent:"transparent",color:statusFilter===s?"#fff":C.inkMid,fontWeight:statusFilter===s?600:400,textTransform:"capitalize"}}>{s}</button>
        ))}
      </div>

      {/* New PO Form */}
      {showForm && (
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:24,marginBottom:24}}>
          <h3 style={{fontWeight:700,marginBottom:16,color:C.ink}}>{editingPO ? `Edit ${editingPO.po_number}` : "New Purchase Order"}</h3>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16}}>
            <div>{lb("Supplier")}
              <select value={form.supplier_id} onChange={e=>{const s=suppliers.find(x=>x.id===parseInt(e.target.value));setForm(d=>({...d,supplier_id:e.target.value,supplier_name:s?s.name:d.supplier_name}));}} style={is}>
                <option value="">— Select supplier —</option>
                {suppliers.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>{lb("Or type supplier name")}<input value={form.supplier_name} onChange={e=>setForm(d=>({...d,supplier_name:e.target.value}))} placeholder="Supplier name" style={is}/></div>
            <div>{lb("Delivery Date")}<input type="date" value={form.delivery_date} onChange={e=>setForm(d=>({...d,delivery_date:e.target.value}))} style={is}/></div>
            <div style={{display:"flex",alignItems:"center",gap:8,paddingTop:20}}>
              <input type="checkbox" checked={form.vat_applicable} onChange={e=>setForm(d=>({...d,vat_applicable:e.target.checked}))} id="po_vat"/>
              <label htmlFor="po_vat" style={{fontSize:13,color:C.inkMid,cursor:"pointer"}}>Apply VAT @ 15%</label>
            </div>
          </div>

          {/* Line items */}
          <div style={{fontWeight:600,fontSize:13,color:C.ink,marginBottom:10}}>Line Items</div>
          <div style={{background:C.bg,borderRadius:8,padding:12,marginBottom:12}}>
            {form.items.map((item,idx)=>(
              <div key={idx} style={{display:"grid",gridTemplateColumns:"1fr 80px 100px 40px",gap:8,marginBottom:8,alignItems:"center"}}>
                <input value={item.description} onChange={e=>setItem(idx,"description",e.target.value)} placeholder="Description" style={is}/>
                <input value={item.quantity} onChange={e=>setItem(idx,"quantity",e.target.value)} type="number" min="0" placeholder="Qty" style={is}/>
                <input value={item.unit_price} onChange={e=>setItem(idx,"unit_price",e.target.value)} type="number" min="0" placeholder="Unit price" style={is}/>
                <button onClick={()=>removeItem(idx)} disabled={form.items.length===1} style={{background:"none",border:"none",cursor:"pointer",fontSize:18,color:C.red,opacity:form.items.length===1?0.3:1}}>×</button>
              </div>
            ))}
            <button onClick={addItem} style={{fontSize:12,color:C.accent,background:"none",border:"none",cursor:"pointer",padding:"4px 0"}}>+ Add line</button>
          </div>

          {/* Totals */}
          <div style={{textAlign:"right",marginBottom:16,fontSize:13,color:C.inkMid}}>
            <div>Subtotal: R{subtotal.toFixed(2)}</div>
            {form.vat_applicable && <div>VAT (15%): R{vat.toFixed(2)}</div>}
            <div style={{fontWeight:700,fontSize:16,color:C.ink,marginTop:4}}>Total: R{total.toFixed(2)}</div>
          </div>

          <div style={{marginBottom:16}}>{lb("Notes")}<textarea value={form.notes} onChange={e=>setForm(d=>({...d,notes:e.target.value}))} rows={2} style={{...is,resize:"vertical"}}/></div>
          <div style={{display:"flex",gap:10}}>
            <button onClick={save} disabled={saving} style={{background:C.accent,color:"#fff",border:"none",borderRadius:8,padding:"10px 24px",fontWeight:600,cursor:"pointer",opacity:saving?0.7:1}}>{saving?(saveMsg||"Saving…"):editingPO?"Save Changes":"Create PO"}</button>
            <button onClick={saveDraft} disabled={saving} style={{background:"transparent",border:`1px solid ${C.border}`,borderRadius:8,padding:"10px 24px",cursor:"pointer",color:C.inkMid,fontWeight:500}}>{editingPO?"Save as Draft":"Save Draft"}</button>
            <button onClick={()=>{setShowForm(false);setEditingPO(null);setForm(emptyForm);}} style={{background:"transparent",border:"none",borderRadius:8,padding:"10px 24px",cursor:"pointer",color:C.inkDim}}>Cancel</button>
          </div>
        </div>
      )}

      {/* PO View modal */}
      {viewing && !receiving && !recvDone && (
        <div style={{position:"fixed",inset:0,background:"#00000060",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={()=>setViewing(null)}>
          <div style={{background:C.surface,borderRadius:16,padding:28,maxWidth:560,width:"100%",maxHeight:"80vh",overflowY:"auto"}} onClick={e=>e.stopPropagation()}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
              <div>
                <div style={{fontWeight:700,fontSize:18,color:C.ink}}>{viewing.po_number}</div>
                <div style={{fontSize:12,color:C.inkMid,marginTop:2}}>{viewing.supplier_name}</div>
                {viewing.received_date && <div style={{fontSize:11,color:C.green,marginTop:2}}>✓ Received {viewing.received_date}</div>}
              </div>
              <span style={{padding:"4px 12px",borderRadius:20,fontSize:12,fontWeight:600,background:PO_STATUS_COLORS[viewing.status]||C.goldLt,color:PO_STATUS_TEXT[viewing.status]||C.gold,textTransform:"capitalize"}}>{viewing.status}</span>
            </div>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:13,marginBottom:16}}>
              <thead><tr style={{background:C.bg}}><th style={{textAlign:"left",padding:"8px 10px"}}>Description</th><th style={{textAlign:"right",padding:"8px 10px"}}>Qty</th><th style={{textAlign:"right",padding:"8px 10px"}}>Unit Price</th><th style={{textAlign:"right",padding:"8px 10px"}}>Total</th></tr></thead>
              <tbody>{viewing.items.map((it,i)=><tr key={i}><td style={{padding:"8px 10px"}}>{it.description}</td><td style={{textAlign:"right",padding:"8px 10px"}}>{it.quantity}</td><td style={{textAlign:"right",padding:"8px 10px"}}>R{it.unit_price.toFixed(2)}</td><td style={{textAlign:"right",padding:"8px 10px"}}>R{it.total.toFixed(2)}</td></tr>)}</tbody>
            </table>
            <div style={{textAlign:"right",fontSize:13,color:C.inkMid,marginBottom:16}}>
              <div>Subtotal: R{viewing.subtotal.toFixed(2)}</div>
              {viewing.vat_amount>0 && <div>VAT: R{viewing.vat_amount.toFixed(2)}</div>}
              <div style={{fontWeight:700,fontSize:16,color:C.ink,marginTop:4}}>Total: R{viewing.total_amount.toFixed(2)}</div>
            </div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
              {!["received","paid","cancelled"].includes(viewing.status) && (
                <button onClick={()=>{
                  // Default each line to the REMAINING quantity (audit fix 2026-07-02):
                  // partial POs can now take further deliveries, and the backend rejects
                  // receipts exceeding the outstanding quantity.
                  const init = {};
                  viewing.items.forEach(it=>{ init[it.id]=Math.max(0,(it.quantity||0)-(it.quantity_received||0)); });
                  setRecvQtys(init); setReceiving(viewing);
                }} style={{background:C.green,color:"#fff",border:"none",borderRadius:8,padding:"8px 18px",fontSize:13,fontWeight:600,cursor:"pointer"}}>
                  📦 {viewing.status==="partial" ? "Receive Remaining Goods" : "Receive Goods"}
                </button>
              )}
              {["received","partial"].includes(viewing.status) && (
                <button onClick={async()=>{
                  try {
                    const res = await api(`/purchase-orders/${viewing.id}/pay`,{method:"POST"});
                    setViewing(res); load();
                  } catch(e){ alert(e.message); }
                }} style={{background:C.accent,color:"#fff",border:"none",borderRadius:8,padding:"8px 18px",fontSize:13,fontWeight:600,cursor:"pointer"}}>
                  💳 Mark as Paid
                </button>
              )}
              {["draft","sent"].includes(viewing.status) && (
                <button onClick={()=>startEdit(viewing)} style={{background:C.goldLt,color:C.gold,border:`1px solid ${C.gold}40`,borderRadius:6,padding:"6px 14px",fontSize:12,cursor:"pointer",fontWeight:600}}>✏️ Edit PO</button>
              )}
              {["draft","sent"].includes(viewing.status) && (
                <button onClick={async()=>{
                  if(!window.confirm(`Mark ${viewing.po_number} as sent and email ${viewing.supplier_name}?`)) return;
                  try {
                    const res = await api(`/purchase-orders/${viewing.id}/send`,{method:"POST"});
                    if (res.email_sent) alert(`✅ PO emailed to ${res.emailed_to}`);
                    else alert(`✅ ${viewing.po_number} marked as Sent. No email sent — supplier has no email address on file.`);
                    setViewing(res); load();
                  } catch(e){ alert(e.message); }
                }} style={{background:C.blueLt,color:C.blue,border:`1px solid ${C.blue}40`,borderRadius:6,padding:"6px 14px",fontSize:12,cursor:"pointer",fontWeight:600}}>📧 Send to Supplier</button>
              )}
              {viewing.status==="draft" && (
                <button onClick={()=>{updateStatus(viewing.id,"cancelled");setViewing({...viewing,status:"cancelled"});}} style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:6,padding:"6px 14px",fontSize:12,cursor:"pointer",color:C.inkMid}}>Cancel PO</button>
              )}
              <button onClick={()=>del(viewing.id)} style={{marginLeft:"auto",background:"none",border:`1px solid ${C.red}30`,borderRadius:6,padding:"6px 14px",fontSize:12,cursor:"pointer",color:C.red}}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Goods Receipt modal */}
      {receiving && (
        <div style={{position:"fixed",inset:0,background:"#00000060",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
          <div style={{background:C.surface,borderRadius:16,padding:28,maxWidth:600,width:"100%",maxHeight:"85vh",overflowY:"auto"}}>
            <div style={{marginBottom:20}}>
              <div style={{fontWeight:700,fontSize:18,color:C.ink}}>📦 Receive Goods — {receiving.po_number}</div>
              <div style={{fontSize:12,color:C.inkMid,marginTop:2}}>{receiving.supplier_name} · Confirm quantities received</div>
            </div>

            {/* Line items with received qty */}
            <div style={{background:C.bg,borderRadius:10,padding:14,marginBottom:16}}>
              <div style={{display:"grid",gridTemplateColumns:"1fr 65px 65px 80px",gap:8,marginBottom:8}}>
                <div style={{fontSize:11,fontWeight:600,color:C.inkMid}}>Description</div>
                <div style={{fontSize:11,fontWeight:600,color:C.inkMid,textAlign:"right"}}>Ordered</div>
                <div style={{fontSize:11,fontWeight:600,color:C.inkMid,textAlign:"right"}}>Prior</div>
                <div style={{fontSize:11,fontWeight:600,color:C.inkMid,textAlign:"right"}}>This delivery</div>
              </div>
              {receiving.items.map(it=>{
                const remaining = Math.max(0, (it.quantity||0) - (it.quantity_received||0));
                return (
                <div key={it.id} style={{display:"grid",gridTemplateColumns:"1fr 65px 65px 80px",gap:8,marginBottom:8,alignItems:"center"}}>
                  <div style={{fontSize:13,color:C.ink}}>{it.description}</div>
                  <div style={{fontSize:13,color:C.inkMid,textAlign:"right"}}>{it.quantity}</div>
                  <div style={{fontSize:13,color:(it.quantity_received||0)>0?C.green:C.inkDim,textAlign:"right"}}>{it.quantity_received||0}</div>
                  <input
                    type="number" min="0" max={remaining} step="0.01"
                    value={recvQtys[it.id]??remaining}
                    onChange={e=>setRecvQtys(q=>({...q,[it.id]:Math.min(remaining,Math.max(0,parseFloat(e.target.value)||0))}))}
                    style={{padding:"6px 8px",border:`1px solid ${C.border}`,borderRadius:6,fontFamily:"inherit",fontSize:13,textAlign:"right",background:C.surface}}
                  />
                </div>
                );
              })}
            </div>

            {/* Received total */}
            {(()=>{
              const recvTotal = receiving.items.reduce((s,it)=>s+(recvQtys[it.id]??Math.max(0,(it.quantity||0)-(it.quantity_received||0)))*it.unit_price,0);
              const vatAmt = receiving.vat_amount>0 ? recvTotal*0.15 : 0;
              return (
                <div style={{background:C.greenLt,borderRadius:8,padding:14,marginBottom:16,textAlign:"right",fontSize:13}}>
                  <div style={{color:C.inkMid}}>Received value: R{recvTotal.toFixed(2)}</div>
                  {vatAmt>0 && <div style={{color:C.inkMid}}>VAT (15%): R{vatAmt.toFixed(2)}</div>}
                  <div style={{fontWeight:700,fontSize:16,color:C.green,marginTop:4}}>Amount payable: R{(recvTotal+vatAmt).toFixed(2)}</div>
                </div>
              );
            })()}

            {/* Expense category */}
            <div style={{marginBottom:16}}>
              <div style={{fontSize:11,fontWeight:600,color:C.inkMid,marginBottom:4}}>Expense category (for payment processing)</div>
              <select value={recvCategory} onChange={e=>setRecvCategory(e.target.value)}
                style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontFamily:"inherit",fontSize:13,background:C.bg}}>
                <option value="5000 - Cost of Sales">5000 - Cost of Sales</option>
                <option value="6000 - Other Expenses">6000 - Other Expenses</option>
                <option value="6100 - Office Expenses">6100 - Office Expenses</option>
                <option value="6200 - Equipment">6200 - Equipment</option>
                <option value="6300 - Raw Materials">6300 - Raw Materials</option>
                <option value="6400 - Repairs & Maintenance">6400 - Repairs & Maintenance</option>
                <option value="6510 - Fuel and Oil">6510 - Fuel and Oil</option>
              </select>
            </div>

            <div style={{display:"flex",gap:10}}>
              <button onClick={async ()=>{
                try {
                  const payload = {
                    items: receiving.items.map(it=>({item_id:it.id, quantity_received: recvQtys[it.id]??Math.max(0,(it.quantity||0)-(it.quantity_received||0))})),
                    create_expense: true,
                    expense_category: recvCategory,
                  };
                  const result = await api(`/purchase-orders/${receiving.id}/receive`,{method:"POST",body:JSON.stringify(payload)});
                  setRecvDone(result); setReceiving(null); load();
                } catch(e){ alert(e.message); }
              }} style={{background:C.green,color:"#fff",border:"none",borderRadius:8,padding:"10px 24px",fontWeight:600,cursor:"pointer"}}>
                ✓ Confirm Receipt
              </button>
              <button onClick={()=>{setReceiving(null);setViewing(null);}} style={{background:"transparent",border:`1px solid ${C.border}`,borderRadius:8,padding:"10px 24px",cursor:"pointer",color:C.inkMid}}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Receipt confirmation / payment summary */}
      {recvDone && (
        <div style={{position:"fixed",inset:0,background:"#00000060",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
          <div style={{background:C.surface,borderRadius:16,padding:32,maxWidth:480,width:"100%",textAlign:"center"}}>
            <div style={{fontSize:48,marginBottom:12}}>✅</div>
            <div style={{fontWeight:700,fontSize:20,color:C.ink,marginBottom:8}}>Goods Received</div>
            <div style={{fontSize:13,color:C.inkMid,marginBottom:20}}>
              {recvDone.po_number} — {recvDone.supplier_name}<br/>
              Status updated to <strong style={{textTransform:"capitalize"}}>{recvDone.status}</strong>
            </div>
            <div style={{background:C.greenLt,borderRadius:10,padding:16,marginBottom:20}}>
              <div style={{fontSize:13,color:C.inkMid,marginBottom:4}}>Amount payable to supplier</div>
              <div style={{fontSize:28,fontWeight:700,color:C.green}}>R{recvDone.received_total?.toFixed(2)}</div>
              <div style={{fontSize:12,color:C.green,marginTop:8}}>✓ Inventory & accounts payable updated in ledger</div>
            </div>
            <div style={{fontSize:13,color:C.inkMid,marginBottom:20}}>
              Journal entries posted: DR Inventory / CR Accounts Payable. Click <strong>Mark as Paid</strong> on the PO when you process payment to clear the liability.
            </div>
            <button onClick={()=>setRecvDone(null)} style={{background:C.accent,color:"#fff",border:"none",borderRadius:8,padding:"12px 32px",fontWeight:600,cursor:"pointer",fontSize:14}}>Done</button>
          </div>
        </div>
      )}

      {filtered.length === 0 ? (
        <div style={{textAlign:"center",padding:"60px 0",color:C.inkDim}}>
          <div style={{fontSize:40,marginBottom:12}}>📋</div>
          <div style={{fontSize:15,fontWeight:600}}>No purchase orders</div>
          <div style={{fontSize:13,marginTop:4}}>Create your first PO to start tracking procurement</div>
        </div>
      ) : (
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,overflow:"hidden"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
            <thead><tr style={{background:C.bg}}>
              <th style={{textAlign:"left",padding:"12px 16px",fontWeight:600,color:C.inkMid}}>PO Number</th>
              <th style={{textAlign:"left",padding:"12px 16px",fontWeight:600,color:C.inkMid}}>Supplier</th>
              <th style={{textAlign:"left",padding:"12px 16px",fontWeight:600,color:C.inkMid}}>Date</th>
              <th style={{textAlign:"left",padding:"12px 16px",fontWeight:600,color:C.inkMid}}>Status</th>
              <th style={{textAlign:"right",padding:"12px 16px",fontWeight:600,color:C.inkMid}}>Total</th>
              <th style={{padding:"12px 16px"}}></th>
            </tr></thead>
            <tbody>{filtered.map(p=>(
              <tr key={p.id} style={{borderTop:`1px solid ${C.border}`}}>
                <td style={{padding:"12px 16px",fontWeight:600,color:C.ink}}>{p.po_number}</td>
                <td style={{padding:"12px 16px",color:C.inkMid}}>{p.supplier_name||"—"}</td>
                <td style={{padding:"12px 16px",color:C.inkMid}}>{p.order_date}</td>
                <td style={{padding:"12px 16px"}}><span style={{padding:"3px 10px",borderRadius:20,fontSize:11,fontWeight:600,background:PO_STATUS_COLORS[p.status]||C.goldLt,color:PO_STATUS_TEXT[p.status]||C.gold,textTransform:"capitalize"}}>{p.status}</span></td>
                <td style={{padding:"12px 16px",textAlign:"right",fontWeight:600,color:C.ink}}>R{p.total_amount.toFixed(2)}</td>
                <td style={{padding:"12px 16px"}}><button onClick={()=>setViewing(p)} style={{background:C.accentLt,color:C.accent,border:"none",borderRadius:6,padding:"5px 12px",fontSize:12,cursor:"pointer",fontWeight:600}}>View</button></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── AI ASSISTANT ──────────────────────────────────────────────────────────────
const AI_SUGGESTIONS = [
  "How do I categorise a fuel expense?",
  "What is the PAYE rate for R35,000/month?",
  "When is EMP201 due?",
  "How do I record a bad debt?",
  "What is the VAT rate in South Africa?",
];
const AI_QUICK_PROMPTS = {
  invoicing: ["How do I create an invoice?","How do I mark an invoice as paid?","What makes a valid tax invoice?","How do I handle overdue invoices?"],
  quotes:    ["How do I create a quote?","How do I convert a quote to an invoice?","What should a quote include?","How long should a quote be valid?"],
  expenses:  ["How do I add an expense?","How do I categorise expenses?","Can I claim input VAT on expenses?","How do I scan a receipt?"],
  payroll:   ["What is the PAYE rate for R35,000/month?","When is EMP201 due?","How is UIF calculated?","What is SDL?"],
  default:   ["How do I create an invoice?","What is the VAT rate?","How do I record a bad debt?","When is EMP201 due?"],
};

function AIAssistant({tab}) {
  const isMobile = useIsMobile();
  const [open,     setOpen]     = useState(false);
  const [messages, setMessages] = useState([{role:"assistant",text:"Hi! I'm ZuZan AI. Ask me anything about invoicing, quotes, expenses, payroll, or SARS compliance."}]);
  const [input,    setInput]    = useState("");
  const [loading,  setLoading]  = useState(false);
  const bottomRef = useRef(null);
  const prompts = AI_QUICK_PROMPTS[tab] || AI_QUICK_PROMPTS.default;

  useEffect(() => { if(bottomRef.current) bottomRef.current.scrollIntoView({behavior:"smooth"}); }, [messages]);

  const send = async (text) => {
    const q = text || input;
    if (!q.trim()) return;
    setInput("");
    setMessages(m=>[...m,{role:"user",text:q}]);
    setLoading(true);
    try {
      const res = await api("/ai/chat",{method:"POST",body:JSON.stringify({message:q,context:tab||"dashboard"})});
      const reply = typeof res === "string" ? res : (res.reply||res.response||res.message||res.answer||res.text||JSON.stringify(res));
      setMessages(m=>[...m,{role:"assistant",text:reply||"I can help with that. Please check the relevant section of the app."}]);
    } catch(e) {
      // Fallback local answers
      const lower = q.toLowerCase();
      let reply = "I'm not connected to the AI service right now. Please try again later.";
      if(lower.includes("paye") && lower.includes("35000")) reply = "For R35,000/month gross: Annual income R420,000. PAYE ≈ R4,673/month after the R17,235 primary rebate. Check the Payroll tab for exact figures.";
      else if(lower.includes("emp201")) reply = "EMP201 is due by the 7th of each month following the payroll period. You can download the EMP201 from the Payroll tab after running payroll.";
      else if(lower.includes("vat")) reply = "The standard VAT rate in South Africa is 15%. Some items are zero-rated (basic foods, exports). VAT201 is due monthly or bi-monthly depending on your registration.";
      else if(lower.includes("fuel") || lower.includes("petrol")) reply = "Fuel should be categorised to account 6510 - Fuel and Oil under Expenses. If the vehicle is used partly for business and partly private, only the business portion is deductible.";
      else if(lower.includes("bad debt")) reply = "Record a bad debt by creating an expense to account 7300 - Bad Debts Written Off, matching it against the outstanding invoice. This reduces your debtors and is tax deductible.";
      else if(lower.includes("uif")) reply = "UIF is 1% employee + 1% employer, capped at R17,712/month gross. Both amounts are calculated automatically in the Payroll tab.";
      setMessages(m=>[...m,{role:"assistant",text:reply}]);
    } finally { setLoading(false); }
  };

  return (
    <>
      {/* Floating button */}
      <button onClick={()=>setOpen(o=>!o)} style={{position:"fixed",bottom:isMobile?78:90,right:isMobile?12:20,width:isMobile?44:50,height:isMobile?44:50,borderRadius:"50%",background:C.accent,color:"#fff",border:"none",fontSize:isMobile?20:22,cursor:"pointer",boxShadow:"0 4px 20px rgba(200,64,26,0.4)",opacity:isMobile&&!open?0.9:1,zIndex:isMobile&&!open?150:500,display:"flex",alignItems:"center",justifyContent:"center"}}>
        {open?"✕":"🤖"}
      </button>

      {/* Chat panel */}
      {open && (
        <div style={{position:"fixed",bottom:152,right:20,width:360,height:460,background:C.surface,borderRadius:20,boxShadow:"0 8px 40px rgba(0,0,0,0.2)",zIndex:500,display:"flex",flexDirection:"column",overflow:"hidden",border:`1px solid ${C.border}`}}>
          <div style={{padding:"14px 18px",background:C.accent,color:"#fff",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div><div style={{fontWeight:700,fontSize:14}}>ZuZan AI Assistant</div><div style={{fontSize:11,opacity:0.8}}>Bookkeeping & SARS help</div></div>
          </div>

          {/* Messages */}
          <div style={{flex:1,overflowY:"auto",padding:"14px 16px",display:"flex",flexDirection:"column",gap:10}}>
            {messages.map((m,i)=>(
              <div key={i} style={{display:"flex",justifyContent:m.role==="user"?"flex-end":"flex-start"}}>
                <div style={{maxWidth:"80%",padding:"10px 14px",borderRadius:m.role==="user"?"16px 16px 4px 16px":"16px 16px 16px 4px",background:m.role==="user"?C.accent:C.bg,color:m.role==="user"?"#fff":C.ink,fontSize:13,lineHeight:1.5}}>
                  {m.text}
                </div>
              </div>
            ))}
            {loading && (
              <div style={{display:"flex",justifyContent:"flex-start"}}>
                <div style={{padding:"10px 14px",borderRadius:"16px 16px 16px 4px",background:C.bg,fontSize:13,color:C.inkMid}}>Thinking...</div>
              </div>
            )}
            <div ref={bottomRef}/>
          </div>

          {/* Suggestions */}
          {messages.length <= 1 && (
            <div style={{padding:"0 12px 8px",display:"flex",flexWrap:"wrap",gap:6}}>
              {prompts.map(s=>(
                <button key={s} onClick={()=>send(s)} style={{padding:"5px 10px",borderRadius:14,border:`1px solid ${C.border}`,background:C.bg,color:C.inkMid,fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>{s}</button>
              ))}
            </div>
          )}

          {/* Input */}
          <div style={{padding:"10px 12px",borderTop:`1px solid ${C.border}`,display:"flex",gap:8}}>
            <input value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&send()} placeholder="Ask a bookkeeping question..." style={{flex:1,padding:"9px 12px",border:`1px solid ${C.border}`,borderRadius:10,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none"}}/>
            <button onClick={()=>send()} disabled={loading} style={{background:C.accent,color:"#fff",border:"none",borderRadius:10,padding:"9px 16px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Send</button>
          </div>
        </div>
      )}
    </>
  );
}

// ── FIXED ASSETS ─────────────────────────────────────────────────────────────
const FA_CATS = ["Land & Buildings","Plant & Machinery","Motor Vehicles","Furniture & Fittings","Computer Equipment","Office Equipment","Leasehold Improvements","Other Fixed Assets"];
const FA_USEFUL_LIVES = {"Land & Buildings":480,"Plant & Machinery":120,"Motor Vehicles":60,"Furniture & Fittings":120,"Computer Equipment":36,"Office Equipment":60,"Leasehold Improvements":120,"Other Fixed Assets":60};

// SARS IN47 Wear & Tear table — client-side mirror for instant previews
const SARS_WT = {
  "Computers — desktops / laptops":          {years:3, section:"11(e)"},
  "Smartphones / Tablets":                   {years:2, section:"11(e)"},
  "Printers / Photocopiers / Scanners":      {years:3, section:"11(e)"},
  "Fax machines":                            {years:3, section:"11(e)"},
  "Software — purchased (off-the-shelf)":    {years:2, section:"11(e)"},
  "Software — custom developed":             {years:3, section:"11(e)"},
  "Office equipment (general)":              {years:5, section:"11(e)"},
  "Televisions / Display screens":           {years:6, section:"11(e)"},
  "Furniture and fittings":                  {years:6, section:"11(e)"},
  "Carpets / Floor coverings":               {years:6, section:"11(e)"},
  "Air conditioners (portable)":             {years:5, section:"11(e)"},
  "Motor vehicles — passenger":              {years:5, section:"11(e)"},
  "Motor vehicles — light delivery (≤3.5t)":{years:4, section:"11(e)"},
  "Trucks / Heavy vehicles (>3.5t)":         {years:4, section:"11(e)"},
  "Motorcycles":                             {years:4, section:"11(e)"},
  "Trailers":                                {years:5, section:"11(e)"},
  "Forklifts":                               {years:4, section:"11(e)"},
  "Aircraft (light)":                        {years:4, section:"11(e)"},
  "Machinery — general":                     {years:5, section:"11(e)"},
  "Manufacturing plant (general)":           {years:5, section:"11(e)"},
  "Manufacturing plant — new/unused (s12C)":{years:4, section:"12C", note:"40% yr1, 20% yrs 2-4"},
  "Tools — hand tools":                      {years:3, section:"11(e)"},
  "Tools — power tools":                     {years:3, section:"11(e)"},
  "Commercial buildings (s13quin)":          {years:25, section:"13quin", note:"Eligible commercial buildings only"},
  "Industrial / Manufacturing buildings":    {years:10, section:"13",     note:"Used in process of manufacture"},
  "Hotel buildings":                         {years:20, section:"13bis"},
  "Residential rental property":             {years:25, section:"13sex",  note:"New/unused residential units only"},
  "Solar PV panels (≤1 MW, s12BA)":         {years:1, section:"12BA", note:"125% first-year deduction"},
  "Wind energy equipment":                   {years:1, section:"12B",  note:"100% first-year deduction"},
  "Biomass / Small hydro equipment":         {years:3, section:"12B"},
  "Bicycles":                                {years:4, section:"11(e)"},
  "Cash registers / POS systems":            {years:3, section:"11(e)"},
  "Security systems / CCTV":                 {years:5, section:"11(e)"},
  "Telephone systems (PABX / VoIP)":         {years:5, section:"11(e)"},
  "Medical / Dental equipment":              {years:5, section:"11(e)"},
  "Kitchen equipment (commercial)":          {years:6, section:"11(e)"},
  "Gym / Fitness equipment":                 {years:5, section:"11(e)"},
};
const SARS_WT_CATS = Object.keys(SARS_WT);
const SA_CIT = 0.27;

function sarsCalcTaxBase(cost, purchaseDateStr, sarsCategory) {
  const info = SARS_WT[sarsCategory];
  if (!info || !purchaseDateStr || !cost) return null;
  const years = info.years;
  const purchaseDate = new Date(purchaseDateStr);
  const now = new Date();
  const monthsElapsed = Math.max(0,
    (now.getFullYear() - purchaseDate.getFullYear()) * 12 +
    (now.getMonth()    - purchaseDate.getMonth()));
  const monthlyAllowance = years > 0 ? cost / (years * 12) : cost;
  const accumulated = years > 0 ? Math.min(monthsElapsed * monthlyAllowance, cost) : cost;
  const taxBase = Math.max(0, cost - accumulated);
  return {taxBase, accumulated, monthlyAllowance, years, section:info.section, note:info.note};
}

function FixedAssets() {
  const [assets,     setAssets]     = useState([]);
  const [schedule,   setSchedule]   = useState([]);
  const [cats,       setCats]       = useState([]);
  const [deferredTax,setDeferredTax]= useState(null);
  const [loading,    setLoading]    = useState(true);
  const [section,    setSection]    = useState("register");  // register | schedule | tax | add
  const [disposing,    setDisposing]    = useState(null);
  const [saving,       setSaving]       = useState(false);
  const [runningDepr,  setRunningDepr]  = useState(false);
  const [form, setForm] = useState({
    asset_name:"", category:"Motor Vehicles", description:"", location:"",
    purchase_date: new Date().toISOString().slice(0,10),
    cost:"", residual_value:"0", useful_life_months:"60",
    depreciation_method:"straight_line", depreciation_rate:"0.20",
    sars_category:"",
    post_journal:true,
  });
  const [dispForm, setDispForm] = useState({disposal_date: new Date().toISOString().slice(0,10), disposal_proceeds:"0", disposal_notes:"", is_write_off:false});

  const load = async () => {
    setLoading(true);
    try {
      const [a, s, c, dt] = await Promise.all([
        api("/fixed-assets/"),
        api("/fixed-assets/schedule"),
        api("/fixed-assets/categories"),
        api("/fixed-assets/deferred-tax"),
      ]);
      setAssets(a); setSchedule(s); setCats(c); setDeferredTax(dt);
    } catch(e) { console.warn("Fixed assets load:", e); }
    setLoading(false);
  };
  useEffect(()=>{ load(); },[]);

  // KPI aggregates
  const activeAssets   = assets.filter(a => a.status === "active");
  const totalCost      = activeAssets.reduce((s,a) => s + a.cost, 0);
  const totalAccumDepr = activeAssets.reduce((s,a) => s + a.accumulated_depreciation, 0);
  const totalNBV       = activeAssets.reduce((s,a) => s + a.carrying_value, 0);
  const monthlyDepr    = activeAssets.reduce((s,a) => s + (a.monthly_depreciation||0), 0);
  const netDT          = deferredTax?.summary?.net_position ?? 0;
  const netDTType      = deferredTax?.summary?.net_type ?? "Nil";

  const statusColor = st => st === "active" ? C.green : st === "disposed" ? C.blue : C.red;
  const statusBg    = st => st === "active" ? C.greenLt : st === "disposed" ? C.blueLt : C.redLt;

  const handleAdd = async () => {
    if (!form.asset_name || !form.cost || !form.purchase_date) {
      alert("Asset name, cost and purchase date are required."); return;
    }
    if (form.depreciation_method === "diminishing_balance" && !form.depreciation_rate) {
      alert("Depreciation rate is required for diminishing balance method."); return;
    }
    setSaving(true);
    try {
      await api("/fixed-assets/", {method:"POST", body: JSON.stringify({
        asset_name:          form.asset_name,
        category:            form.category,
        description:         form.description || null,
        location:            form.location || null,
        purchase_date:       form.purchase_date,
        cost:                +form.cost,
        residual_value:      +form.residual_value || 0,
        useful_life_months:  +form.useful_life_months,
        depreciation_method: form.depreciation_method,
        depreciation_rate:   form.depreciation_method === "diminishing_balance" ? +form.depreciation_rate : null,
        sars_category:       form.sars_category || null,
        post_journal:        form.post_journal,
      })});
      setForm({asset_name:"",category:"Motor Vehicles",description:"",location:"",purchase_date:new Date().toISOString().slice(0,10),cost:"",residual_value:"0",useful_life_months:"60",depreciation_method:"straight_line",depreciation_rate:"0.20",sars_category:"",post_journal:true});
      setSection("register");
      load();
    } catch(e) { alert("Failed: " + e.message); }
    setSaving(false);
  };

  const handleDispose = async () => {
    if (!disposing) return;
    setSaving(true);
    try {
      const res = await api(`/fixed-assets/${disposing.id}/dispose`, {method:"POST", body: JSON.stringify({
        disposal_date:     dispForm.disposal_date,
        disposal_proceeds: dispForm.is_write_off ? 0 : +dispForm.disposal_proceeds,
        disposal_notes:    dispForm.disposal_notes || null,
        is_write_off:      dispForm.is_write_off,
        proceeds_include_vat: !!dispForm.proceeds_include_vat,
      })});
      const gl = res.gain_loss;
      alert(`${res.message}`);
      setDisposing(null); setSection("register"); load();
    } catch(e) { alert("Failed: " + e.message); }
    setSaving(false);
  };

  const INP = {width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"};
  const LBL = {fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:5,textTransform:"uppercase",letterSpacing:0.5};
  const TABS = [{id:"register",label:"Asset Register"},{id:"schedule",label:"Depreciation Schedule"},{id:"tax",label:"Deferred Tax (IAS 12)"},{id:"add",label:"+ Add Asset"}];

  return (
    <div>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20}}>
        <div>
          <h2 style={{fontFamily:"serif",fontSize:26,color:C.ink,margin:0}}>Fixed Assets</h2>
          <p style={{fontSize:12,color:C.inkMid,marginTop:3}}>IAS 16 — cost model, straight-line &amp; diminishing balance</p>
        </div>
        <button
          disabled={runningDepr}
          onClick={async()=>{
            if(!window.confirm("Run monthly depreciation for all active assets now?")) return;
            setRunningDepr(true);
            try {
              const res = await api("/fixed-assets/run-depreciation", {method:"POST"});
              alert(res.message || `Depreciation complete — ${res.assets_depreciated} asset(s), R${(res.total_depreciation||0).toFixed(2)} charged.`);
              load();
            } catch(e) { alert("Depreciation failed: " + e.message); }
            setRunningDepr(false);
          }}
          style={{background:C.gold,color:"#fff",border:"none",borderRadius:10,padding:"10px 18px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",opacity:runningDepr?0.6:1,whiteSpace:"nowrap"}}>
          {runningDepr ? "Running..." : "▶ Run Depreciation"}
        </button>
      </div>

      {/* KPI cards */}
      <div style={{display:"flex",gap:12,marginBottom:20,flexWrap:"wrap"}}>
        <KPI label="At Cost"             value={fmt(totalCost)}      color={C.ink}    icon="🏭"/>
        <KPI label="Accum. Depreciation" value={fmt(totalAccumDepr)} color={C.gold}   icon="📉"/>
        <KPI label="Net Book Value"      value={fmt(totalNBV)}       color={C.accent} icon="📊"/>
        <KPI label="Monthly Charge"      value={fmt(monthlyDepr)}    color={C.blue}   icon="📅"/>
        <KPI label={`Deferred Tax (${netDTType})`}
             value={fmt(Math.abs(netDT))}
             color={netDTType==="DTL"?C.red:netDTType==="DTA"?C.green:C.inkMid}
             icon="🧾"
             onClick={()=>{setSection("tax");setDisposing(null);}}
             active={section==="tax"}/>
      </div>

      {/* Section tabs */}
      <div style={{display:"flex",gap:4,marginBottom:20,borderBottom:`1px solid ${C.border}`}}>
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>{setSection(t.id);setDisposing(null);}} style={{background:"none",border:"none",borderBottom:section===t.id?`2px solid ${C.accent}`:"2px solid transparent",padding:"8px 18px",fontSize:13,fontWeight:section===t.id?700:400,color:section===t.id?C.accent:C.inkMid,cursor:"pointer",fontFamily:"inherit",marginBottom:"-1px"}}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── ASSET REGISTER ── */}
      {section === "register" && !disposing && (
        <div>
          {loading ? <div style={{color:C.inkMid,padding:20}}>Loading...</div> : assets.length === 0 ? (
            <div style={{textAlign:"center",color:C.inkMid,padding:40}}>No assets yet. Click "+ Add Asset" to begin.</div>
          ) : (
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,overflow:"hidden",marginBottom:16}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                <thead>
                  <tr style={{background:C.bg,borderBottom:`1px solid ${C.border}`}}>
                    {["#","Asset","Category","Cost","Accum. Depr","Carrying Value","Method","Monthly Depr","Status",""].map(h=>(
                      <th key={h} style={{textAlign:"left",padding:"11px 14px",fontSize:9,color:C.inkMid,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase",whiteSpace:"nowrap"}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {assets.map(a => (
                    <tr key={a.id} style={{borderBottom:`1px solid ${C.border}30`}}>
                      <td style={{padding:"12px 14px",color:C.inkMid,fontSize:11}}>{a.asset_number}</td>
                      <td style={{padding:"12px 14px"}}>
                        <div style={{fontWeight:600,color:C.ink}}>{a.asset_name}</div>
                        {a.description && <div style={{fontSize:10,color:C.inkMid}}>{a.description}</div>}
                        {a.location    && <div style={{fontSize:10,color:C.inkDim}}>📍 {a.location}</div>}
                      </td>
                      <td style={{padding:"12px 14px",color:C.inkMid,whiteSpace:"nowrap"}}>{a.category}</td>
                      <td style={{padding:"12px 14px",fontWeight:600}}>{fmt(a.cost)}</td>
                      <td style={{padding:"12px 14px",color:C.gold,fontWeight:600}}>{fmt(a.accumulated_depreciation)}</td>
                      <td style={{padding:"12px 14px",fontWeight:700,color:C.accent}}>{fmt(a.carrying_value)}</td>
                      <td style={{padding:"12px 14px",fontSize:11,color:C.inkMid,whiteSpace:"nowrap"}}>
                        {a.depreciation_method === "straight_line" ? `SL / ${a.useful_life_months}m` : `DB ${((a.depreciation_rate||0)*100).toFixed(0)}%`}
                      </td>
                      <td style={{padding:"12px 14px",color:C.blue,fontWeight:600}}>{a.status==="active"?fmt(a.monthly_depreciation):"—"}</td>
                      <td style={{padding:"12px 14px"}}>
                        <span style={{background:statusBg(a.status),color:statusColor(a.status),borderRadius:6,padding:"3px 10px",fontSize:11,fontWeight:700,textTransform:"capitalize"}}>{a.status}</span>
                      </td>
                      <td style={{padding:"12px 14px"}}>
                        {a.status === "active" && (
                          <button onClick={()=>{setDisposing(a);setDispForm({disposal_date:new Date().toISOString().slice(0,10),disposal_proceeds:"0",disposal_notes:"",is_write_off:false});}} style={{background:C.redLt,color:C.red,border:"none",borderRadius:6,padding:"5px 10px",fontSize:10,cursor:"pointer",fontFamily:"inherit",fontWeight:700,whiteSpace:"nowrap"}}>Dispose</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{background:C.bg,borderTop:`2px solid ${C.border}`}}>
                    <td colSpan={3} style={{padding:"12px 14px",fontWeight:800,color:C.ink}}>TOTALS ({activeAssets.length} active)</td>
                    <td style={{padding:"12px 14px",fontWeight:800}}>{fmt(totalCost)}</td>
                    <td style={{padding:"12px 14px",fontWeight:800,color:C.gold}}>{fmt(totalAccumDepr)}</td>
                    <td style={{padding:"12px 14px",fontWeight:800,color:C.accent}}>{fmt(totalNBV)}</td>
                    <td colSpan={2} style={{padding:"12px 14px",fontWeight:800,color:C.blue}}>{fmt(monthlyDepr)}/mo</td>
                    <td colSpan={2}/>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
          <div style={{fontSize:12,color:C.inkMid,padding:"10px 14px",background:C.accentLt,border:`1px solid ${C.accent}30`,borderRadius:10}}>
            <strong style={{color:C.accent}}>Depreciation note:</strong> Click "▶ Run Depreciation" at the top of this page to post monthly depreciation for all active assets. The run is idempotent — running it more than once in the same calendar month has no effect.
          </div>
        </div>
      )}

      {/* ── DISPOSAL MODAL ── */}
      {section === "register" && disposing && (
        <div style={{background:C.surface,border:`2px solid ${C.red}`,borderRadius:16,padding:28,maxWidth:560}}>
          <h3 style={{fontFamily:"serif",fontSize:20,margin:"0 0 4px",color:C.ink}}>Dispose / Write-off Asset</h3>
          <p style={{fontSize:13,color:C.inkMid,marginBottom:20}}>{disposing.asset_number} — {disposing.asset_name}</p>

          <div style={{background:C.bg,borderRadius:10,padding:14,marginBottom:20,fontSize:13}}>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}><span style={{color:C.inkMid}}>Cost</span><span style={{fontWeight:600}}>{fmt(disposing.cost)}</span></div>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}><span style={{color:C.inkMid}}>Accumulated Depreciation</span><span style={{color:C.gold,fontWeight:600}}>{fmt(disposing.accumulated_depreciation)}</span></div>
            <div style={{display:"flex",justifyContent:"space-between",borderTop:`1px solid ${C.border}`,paddingTop:8,marginTop:4}}><span style={{fontWeight:700}}>Carrying Value</span><span style={{fontWeight:800,color:C.accent}}>{fmt(disposing.carrying_value)}</span></div>
          </div>

          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16,padding:"12px",background:C.redLt,borderRadius:10}}>
            <input type="checkbox" checked={dispForm.is_write_off} onChange={e=>setDispForm(v=>({...v,is_write_off:e.target.checked,disposal_proceeds:"0"}))} style={{width:18,height:18}}/>
            <span style={{fontSize:14,color:C.ink}}>Write-off (no proceeds — full loss recognised)</span>
          </div>

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
            <div>
              <label style={LBL}>Disposal Date</label>
              <input type="date" value={dispForm.disposal_date} onChange={e=>setDispForm(v=>({...v,disposal_date:e.target.value}))} style={INP}/>
            </div>
            {!dispForm.is_write_off && (
              <div>
                <label style={LBL}>Proceeds Received (ZAR)</label>
                <input type="number" value={dispForm.disposal_proceeds} onChange={e=>setDispForm(v=>({...v,disposal_proceeds:e.target.value}))} style={INP}/>
                <label style={{display:"flex",alignItems:"center",gap:8,fontSize:12,marginTop:8,color:C.inkMid}}><input type="checkbox" checked={!!dispForm.proceeds_include_vat} onChange={e=>setDispForm(v=>({...v,proceeds_include_vat:e.target.checked}))}/> Proceeds include 15% VAT (VAT-registered sale - output VAT goes on your VAT201)</label>
              </div>
            )}
          </div>
          <div style={{marginBottom:16}}>
            <label style={LBL}>Notes</label>
            <input placeholder="e.g. Sold to XYZ, vehicle scrapped..." value={dispForm.disposal_notes} onChange={e=>setDispForm(v=>({...v,disposal_notes:e.target.value}))} style={INP}/>
          </div>

          {/* Preview gain/loss */}
          {(() => {
            const proceeds = dispForm.is_write_off ? 0 : (+dispForm.disposal_proceeds||0);
            const gl = (dispForm.proceeds_include_vat ? proceeds*100/115 : proceeds) - disposing.carrying_value;
            return (
              <div style={{background: gl >= 0 ? C.greenLt : C.redLt, border:`1px solid ${gl>=0?C.green:C.red}40`, borderRadius:10, padding:"12px 16px", marginBottom:16, fontSize:13}}>
                <strong>{gl >= 0 ? "Gain on disposal" : "Loss on disposal"}:</strong> {fmt(Math.abs(gl))}
                {gl < 0 && <span style={{color:C.inkMid,marginLeft:8}}>— will be posted to Loss on Disposal (6800)</span>}
                {gl > 0 && <span style={{color:C.inkMid,marginLeft:8}}>— will be posted to Gain on Disposal (4900)</span>}
              </div>
            );
          })()}

          <div style={{display:"flex",gap:10}}>
            <button onClick={()=>setDisposing(null)} style={{flex:1,background:"transparent",color:C.inkMid,border:`1px solid ${C.border}`,borderRadius:10,padding:"11px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
            <button onClick={handleDispose} disabled={saving} style={{flex:1,background:C.red,color:"#fff",border:"none",borderRadius:10,padding:"11px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",opacity:saving?0.6:1}}>
              {saving ? "Processing..." : dispForm.is_write_off ? "Write Off Asset" : "Confirm Disposal"}
            </button>
          </div>
        </div>
      )}

      {/* ── DEFERRED TAX (IAS 12) ── */}
      {section === "tax" && (
        <div>
          {/* Summary cards */}
          {deferredTax && (
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:20}}>
              <div style={{background:C.redLt,border:`1px solid ${C.red}30`,borderRadius:12,padding:"16px 20px"}}>
                <div style={{fontSize:10,fontWeight:700,color:C.red,textTransform:"uppercase",letterSpacing:0.5,marginBottom:6}}>Gross DTL</div>
                <div style={{fontSize:20,fontWeight:800,color:C.red}}>{fmt(deferredTax.summary.gross_dtl)}</div>
                <div style={{fontSize:11,color:C.inkMid,marginTop:4}}>Deferred Tax Liabilities</div>
              </div>
              <div style={{background:C.greenLt,border:`1px solid ${C.green}30`,borderRadius:12,padding:"16px 20px"}}>
                <div style={{fontSize:10,fontWeight:700,color:C.green,textTransform:"uppercase",letterSpacing:0.5,marginBottom:6}}>Gross DTA</div>
                <div style={{fontSize:20,fontWeight:800,color:C.green}}>{fmt(deferredTax.summary.gross_dta)}</div>
                <div style={{fontSize:11,color:C.inkMid,marginTop:4}}>Deferred Tax Assets</div>
              </div>
              <div style={{background:C.accentLt,border:`1px solid ${C.accent}30`,borderRadius:12,padding:"16px 20px"}}>
                <div style={{fontSize:10,fontWeight:700,color:C.accent,textTransform:"uppercase",letterSpacing:0.5,marginBottom:6}}>Net Position</div>
                <div style={{fontSize:20,fontWeight:800,color:deferredTax.summary.net_type==="DTL"?C.red:deferredTax.summary.net_type==="DTA"?C.green:C.inkMid}}>{fmt(Math.abs(deferredTax.summary.net_position))}</div>
                <div style={{fontSize:11,color:C.inkMid,marginTop:4}}>Net {deferredTax.summary.net_type} — Balance sheet line</div>
              </div>
            </div>
          )}

          {/* Deferred tax asset schedule */}
          {deferredTax?.assets?.length > 0 ? (
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,overflow:"hidden",marginBottom:16}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                <thead>
                  <tr style={{background:C.bg,borderBottom:`1px solid ${C.border}`}}>
                    {["Asset","SARS Category (s)","Cost","Accum. SARS Allowance","Tax Base","IAS 16 Carrying Value","Temp. Difference","Deferred Tax @ 27%","Type"].map(h=>(
                      <th key={h} style={{textAlign:"left",padding:"10px 12px",fontSize:9,color:C.inkMid,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase",whiteSpace:"nowrap"}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {deferredTax.assets.map(row => {
                    const isLiability = row.deferred_tax_type === "DTL";
                    const isAsset     = row.deferred_tax_type === "DTA";
                    return (
                      <tr key={row.asset_id} style={{borderBottom:`1px solid ${C.border}30`}}>
                        <td style={{padding:"11px 12px"}}>
                          <div style={{fontWeight:600,color:C.ink}}>{row.asset_name}</div>
                          <div style={{fontSize:10,color:C.inkMid}}>{row.asset_number} · {row.category}</div>
                        </td>
                        <td style={{padding:"11px 12px"}}>
                          <div style={{color:C.ink,fontSize:11}}>{row.sars_category}</div>
                          <div style={{fontSize:10,color:C.blue,fontWeight:600}}>s{row.section} · {row.sars_years}yr write-off</div>
                          {row.note && <div style={{fontSize:9,color:C.inkMid,fontStyle:"italic",marginTop:2}}>{row.note}</div>}
                        </td>
                        <td style={{padding:"11px 12px",fontWeight:600}}>{fmt(row.cost)}</td>
                        <td style={{padding:"11px 12px",color:C.gold,fontWeight:600}}>{fmt(row.accumulated_allowance)}</td>
                        <td style={{padding:"11px 12px",fontWeight:700}}>{fmt(row.tax_base)}</td>
                        <td style={{padding:"11px 12px",fontWeight:700,color:C.accent}}>{fmt(row.accounting_carrying_value)}</td>
                        <td style={{padding:"11px 12px",fontWeight:700,color:row.temporary_difference>0?C.red:row.temporary_difference<0?C.green:C.inkMid}}>
                          {row.temporary_difference > 0 ? "+" : ""}{fmt(row.temporary_difference)}
                        </td>
                        <td style={{padding:"11px 12px",fontWeight:800,color:isLiability?C.red:isAsset?C.green:C.inkMid}}>
                          {fmt(Math.abs(row.deferred_tax))}
                        </td>
                        <td style={{padding:"11px 12px"}}>
                          <span style={{background:isLiability?C.redLt:isAsset?C.greenLt:C.bg,color:isLiability?C.red:isAsset?C.green:C.inkMid,borderRadius:6,padding:"3px 10px",fontSize:11,fontWeight:700}}>{row.deferred_tax_type}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{background:C.bg,borderTop:`2px solid ${C.border}`}}>
                    <td colSpan={6} style={{padding:"11px 12px",fontWeight:800,color:C.ink}}>NET DEFERRED TAX POSITION</td>
                    <td style={{padding:"11px 12px",fontWeight:800,color:deferredTax.summary.net_type==="DTL"?C.red:deferredTax.summary.net_type==="DTA"?C.green:C.inkMid}}>
                      {deferredTax.summary.net_position > 0 ? "+" : ""}{fmt(deferredTax.summary.net_position)}
                    </td>
                    <td style={{padding:"11px 12px",fontWeight:800,color:deferredTax.summary.net_type==="DTL"?C.red:C.green}}>
                      {fmt(Math.abs(deferredTax.summary.net_position))}
                    </td>
                    <td style={{padding:"11px 12px"}}>
                      <span style={{background:deferredTax.summary.net_type==="DTL"?C.redLt:deferredTax.summary.net_type==="DTA"?C.greenLt:C.bg,color:deferredTax.summary.net_type==="DTL"?C.red:deferredTax.summary.net_type==="DTA"?C.green:C.inkMid,borderRadius:6,padding:"3px 10px",fontSize:11,fontWeight:700}}>{deferredTax.summary.net_type}</span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div style={{textAlign:"center",color:C.inkMid,padding:40}}>
              No assets with SARS categories assigned yet. Add a SARS category when creating an asset to see deferred tax.
            </div>
          )}

          {/* Unclassified warning */}
          {deferredTax?.unclassified_count > 0 && (
            <div style={{background:"#fff8e1",border:"1px solid #ffc10730",borderRadius:10,padding:"12px 16px",fontSize:12,color:"#7c5c00",marginBottom:12}}>
              ⚠️ <strong>{deferredTax.unclassified_count} active asset{deferredTax.unclassified_count>1?"s":""}</strong> have no SARS category assigned — these are excluded from the deferred tax calculation. Assign a SARS category by editing the asset.
            </div>
          )}

          <div style={{fontSize:12,color:C.inkMid,padding:"10px 14px",background:C.accentLt,border:`1px solid ${C.accent}30`,borderRadius:10}}>
            <strong style={{color:C.accent}}>IAS 12 note:</strong> Temporary differences arise when the IAS 16 carrying value differs from the SARS tax base (cost less IN47 wear & tear allowances claimed). A DTL arises when the carrying value exceeds the tax base (accounting depreciated more slowly than SARS allows). A DTA arises when the tax base exceeds the carrying value. CIT rate used: 27%.
          </div>
        </div>
      )}

      {/* ── DEPRECIATION SCHEDULE ── */}
      {section === "schedule" && (
        <div>
          {loading ? <div style={{color:C.inkMid,padding:20}}>Loading...</div> : schedule.length === 0 ? (
            <div style={{textAlign:"center",color:C.inkMid,padding:40}}>No depreciation entries yet. Use the "▶ Run Depreciation" button to post the first month.</div>
          ) : (
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,overflow:"hidden"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                <thead>
                  <tr style={{background:C.bg,borderBottom:`1px solid ${C.border}`}}>
                    {["Period","Asset","Amount","Posted"].map(h=>(
                      <th key={h} style={{textAlign:"left",padding:"11px 14px",fontSize:9,color:C.inkMid,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase"}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {schedule.map(e=>(
                    <tr key={e.id} style={{borderBottom:`1px solid ${C.border}30`}}>
                      <td style={{padding:"12px 14px",fontWeight:700,color:C.ink}}>{e.period}</td>
                      <td style={{padding:"12px 14px",color:C.inkMid}}>{e.asset_name}</td>
                      <td style={{padding:"12px 14px",fontWeight:600,color:C.gold}}>{fmt(e.amount)}</td>
                      <td style={{padding:"12px 14px",fontSize:11,color:C.inkDim}}>{e.posted_at ? new Date(e.posted_at).toLocaleDateString("en-ZA") : "—"}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{background:C.bg,borderTop:`2px solid ${C.border}`}}>
                    <td colSpan={2} style={{padding:"12px 14px",fontWeight:800}}>Total Depreciation Posted</td>
                    <td style={{padding:"12px 14px",fontWeight:800,color:C.gold}}>{fmt(schedule.reduce((s,e)=>s+e.amount,0))}</td>
                    <td/>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── ADD ASSET FORM ── */}
      {section === "add" && (
        <div style={{background:C.surface,border:`2px solid ${C.accent}`,borderRadius:16,padding:28,maxWidth:680}}>
          <h3 style={{fontFamily:"serif",fontSize:20,margin:"0 0 20px",color:C.ink}}>Add Fixed Asset</h3>

          <div style={{fontSize:11,fontWeight:700,color:C.accent,textTransform:"uppercase",letterSpacing:1,marginBottom:10}}>Asset Details</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16}}>
            <div>
              <label style={LBL}>Asset Name</label>
              <input placeholder="e.g. Delivery Vehicle — Toyota Hilux" value={form.asset_name} onChange={e=>setForm(v=>({...v,asset_name:e.target.value}))} style={INP}/>
            </div>
            <div>
              <label style={LBL}>Category</label>
              <select value={form.category} onChange={e=>{const ul=FA_USEFUL_LIVES[e.target.value]||60;setForm(v=>({...v,category:e.target.value,useful_life_months:String(ul)}));}} style={INP}>
                {FA_CATS.map(c=><option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={LBL}>Description (optional)</label>
              <input placeholder="Registration number, serial, etc." value={form.description} onChange={e=>setForm(v=>({...v,description:e.target.value}))} style={INP}/>
            </div>
            <div>
              <label style={LBL}>Location (optional)</label>
              <input placeholder="e.g. Head Office, Johannesburg" value={form.location} onChange={e=>setForm(v=>({...v,location:e.target.value}))} style={INP}/>
            </div>
          </div>

          <div style={{fontSize:11,fontWeight:700,color:C.accent,textTransform:"uppercase",letterSpacing:1,marginBottom:10}}>Cost &amp; Dates</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:16}}>
            <div>
              <label style={LBL}>Purchase Date</label>
              <input type="date" value={form.purchase_date} onChange={e=>setForm(v=>({...v,purchase_date:e.target.value}))} style={INP}/>
            </div>
            <div>
              <label style={LBL}>Cost (ZAR)</label>
              <input type="number" placeholder="0.00" value={form.cost} onChange={e=>setForm(v=>({...v,cost:e.target.value}))} style={INP}/>
            </div>
            <div>
              <label style={LBL}>Residual Value (ZAR)</label>
              <input type="number" placeholder="0.00" value={form.residual_value} onChange={e=>setForm(v=>({...v,residual_value:e.target.value}))} style={INP}/>
            </div>
          </div>

          <div style={{fontSize:11,fontWeight:700,color:C.accent,textTransform:"uppercase",letterSpacing:1,marginBottom:10}}>Depreciation</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:16}}>
            <div>
              <label style={LBL}>Method</label>
              <select value={form.depreciation_method} onChange={e=>setForm(v=>({...v,depreciation_method:e.target.value}))} style={INP}>
                <option value="straight_line">Straight-Line</option>
                <option value="diminishing_balance">Diminishing Balance</option>
              </select>
            </div>
            {form.depreciation_method === "straight_line" ? (
              <div>
                <label style={LBL}>Useful Life (months)</label>
                <input type="number" placeholder="60" value={form.useful_life_months} onChange={e=>setForm(v=>({...v,useful_life_months:e.target.value}))} style={INP}/>
              </div>
            ) : (
              <div>
                <label style={LBL}>Annual Rate (e.g. 0.20 = 20%)</label>
                <input type="number" step="0.01" placeholder="0.20" value={form.depreciation_rate} onChange={e=>setForm(v=>({...v,depreciation_rate:e.target.value}))} style={INP}/>
              </div>
            )}
            <div style={{alignSelf:"end",paddingBottom:2}}>
              {form.cost && form.depreciation_method === "straight_line" && (
                <div style={{background:C.accentLt,borderRadius:8,padding:"10px 12px",fontSize:12}}>
                  <div style={{color:C.inkMid}}>Monthly charge</div>
                  <div style={{fontWeight:700,color:C.accent}}>{fmt((+form.cost - (+form.residual_value||0)) / Math.max(1,+form.useful_life_months))}</div>
                </div>
              )}
            </div>
          </div>

          <div style={{fontSize:11,fontWeight:700,color:C.accent,textTransform:"uppercase",letterSpacing:1,marginBottom:10}}>SARS Tax Depreciation (IN47)</div>
          <div style={{marginBottom:6}}>
            <label style={LBL}>SARS Wear &amp; Tear Category (optional — for deferred tax)</label>
            <select value={form.sars_category} onChange={e=>setForm(v=>({...v,sars_category:e.target.value}))} style={INP}>
              <option value="">— None assigned —</option>
              {SARS_WT_CATS.map(c=>{
                const info = SARS_WT[c];
                return <option key={c} value={c}>{c} — {info.years}yr (s{info.section})</option>;
              })}
            </select>
          </div>
          {/* SARS preview strip */}
          {form.sars_category && form.cost && (() => {
            const tb = sarsCalcTaxBase(+form.cost, form.purchase_date, form.sars_category);
            if (!tb) return null;
            const accountingCV  = Math.max(0, +form.cost - (((+form.cost - (+form.residual_value||0)) / Math.max(1,+form.useful_life_months)) * 0)); // at acquisition CV = cost
            const tempDiff = +form.cost - tb.taxBase; // at acquisition tax base lags after month 1
            const dtAmt    = Math.abs(tb.taxBase - +form.cost) * SA_CIT;
            const wt       = SARS_WT[form.sars_category];
            return (
              <div style={{background:"#f0f7ff",border:"1px solid #b3d4f530",borderRadius:10,padding:"12px 16px",marginBottom:12,fontSize:12}}>
                <div style={{fontWeight:700,color:C.blue,marginBottom:6}}>SARS {wt.section} — {tb.years}-year write-off</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
                  <div><span style={{color:C.inkMid}}>Monthly SARS allowance</span><br/><strong>{fmt(tb.monthlyAllowance)}/mo</strong></div>
                  <div><span style={{color:C.inkMid}}>Annual SARS allowance</span><br/><strong>{fmt(tb.monthlyAllowance*12)}/yr</strong></div>
                  <div><span style={{color:C.inkMid}}>Full deduction over</span><br/><strong>{tb.years} year{tb.years>1?"s":""}</strong></div>
                </div>
                {tb.note && <div style={{marginTop:8,fontSize:11,color:"#7c5c00",background:"#fff8e1",borderRadius:6,padding:"6px 10px"}}>⚠️ {tb.note}</div>}
              </div>
            );
          })()}

          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:20,padding:"12px",background:C.accentLt,borderRadius:10}}>
            <input type="checkbox" checked={form.post_journal} onChange={e=>setForm(v=>({...v,post_journal:e.target.checked}))} style={{width:18,height:18}}/>
            <span style={{fontSize:14,color:C.ink}}>Post acquisition journal entry (DR Fixed Assets / CR Bank)</span>
          </div>

          <div style={{display:"flex",gap:10}}>
            <button onClick={()=>setSection("register")} style={{flex:1,background:"transparent",color:C.inkMid,border:`1px solid ${C.border}`,borderRadius:10,padding:"11px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
            <button onClick={handleAdd} disabled={saving} style={{flex:2,background:C.accent,color:"#fff",border:"none",borderRadius:10,padding:"11px",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit",opacity:saving?0.6:1}}>
              {saving ? "Saving..." : "Add to Asset Register"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── INVENTORY ─────────────────────────────────────────────────────────────────
function Inventory() {
  const [items,    setItems]   = useState([]);
  const [summary,  setSummary] = useState(null);
  const [showNew,  setShowNew] = useState(false);
  const [editItem, setEditItem]= useState(null);
  const [adjItem,  setAdjItem] = useState(null);
  const [adjQty,   setAdjQty]  = useState("");
  const [adjReason,setAdjReason]=useState("");
  const [search,      setSearch]     = useState("");
  const [stockFilter, setStockFilter]= useState("all"); // all | low | ok
  const [loading,  setLoading] = useState(true);
  const [form, setForm] = useState({name:"",sku:"",category:"",description:"",unit_cost:"",unit_price:"",quantity_on_hand:"",reorder_level:"5",unit_of_measure:"Unit"});

  useEffect(()=>{ load(); },[]);

  const load = async () => {
    setLoading(true);
    try {
      const [it, sm] = await Promise.all([
        api("/inventory/").catch(()=>[]),
        api("/inventory/summary").catch(()=>null),
      ]);
      setItems(it); setSummary(sm);
    } finally { setLoading(false); }
  };

  const handleCreate = async () => {
    try {
      await api("/inventory/",{method:"POST",body:JSON.stringify({...form,unit_cost:+form.unit_cost||0,unit_price:+form.unit_price||0,quantity_on_hand:+form.quantity_on_hand||0,reorder_level:+form.reorder_level||5})});
      setShowNew(false);
      setForm({name:"",sku:"",category:"",description:"",unit_cost:"",unit_price:"",quantity_on_hand:"",reorder_level:"5",unit_of_measure:"Unit"});
      load();
    } catch(e) { alert("Failed: "+e.message); }
  };

  const handleUpdate = async () => {
    try {
      await api(`/inventory/${editItem.id}`,{method:"PUT",body:JSON.stringify({...editItem,unit_cost:+editItem.unit_cost||0,unit_price:+editItem.unit_price||0,quantity_on_hand:+editItem.quantity_on_hand||0,reorder_level:+editItem.reorder_level||5})});
      setEditItem(null); load();
    } catch(e) { alert("Failed: "+e.message); }
  };

  const handleAdjust = async () => {
    try {
      await api(`/inventory/${adjItem.id}/adjust`,{method:"POST",body:JSON.stringify({quantity:+adjQty,reason:adjReason})});
      setAdjItem(null); setAdjQty(""); setAdjReason(""); load();
    } catch(e) { alert("Failed: "+e.message); }
  };

  const handleDelete = async (id,name) => {
    if(!window.confirm(`Delete "${name}"?`)) return;
    await api(`/inventory/${id}`,{method:"DELETE"});
    load();
  };

  const filtered = items
    .filter(i=>(i.name||"").toLowerCase().includes(search.toLowerCase())||(i.sku||"").toLowerCase().includes(search.toLowerCase())||(i.category||"").toLowerCase().includes(search.toLowerCase()))
    .filter(i => {
      if (stockFilter === "low") return i.quantity_on_hand <= i.reorder_level;
      if (stockFilter === "ok")  return i.quantity_on_hand >  i.reorder_level;
      return true;
    });
  const is = {width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.bg,color:C.ink,outline:"none",boxSizing:"border-box"};
  const lb = (t) => <label style={{fontSize:11,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>{t}</label>;

  return (
    <div>
      <SectionHeader title="Inventory" sub="Track stock levels, costs and reorder alerts" action="+ Add Item" onAction={()=>setShowNew(true)}/>

      {/* Summary KPIs — clickable to filter table */}
      <div style={{display:"flex",gap:12,marginBottom:24,flexWrap:"wrap"}}>
        <KPI label="Total Items"        value={summary?.total_items||items.length}  color={C.blue}  icon="📦"
          sub={stockFilter==="all"?"All items":"Tap to show all"}
          onClick={()=>setStockFilter("all")}/>
        <KPI label="Stock Value (Cost)" value={fmt(summary?.total_cost_value||0)}   color={C.ink}   icon="💰"
          sub="Total cost of stock on hand"/>
        <KPI label="Retail Value"       value={fmt(summary?.total_retail_value||0)} color={C.green} icon="🏷️"
          sub="Total selling value of stock"/>
        <KPI label="OK Stock"           value={items.filter(i=>i.quantity_on_hand>i.reorder_level).length} color={C.green} icon="✅"
          sub={stockFilter==="ok"?"Filtering ▼":"Tap to filter"}
          onClick={()=>setStockFilter(f=>f==="ok"?"all":"ok")}/>
        <KPI label="Low Stock Alerts"   value={summary?.low_stock_count||items.filter(i=>i.quantity_on_hand<=i.reorder_level).length} color={C.red} icon="⚠️"
          sub={stockFilter==="low"?"Filtering ▼":"Tap to filter"}
          onClick={()=>setStockFilter(f=>f==="low"?"all":"low")}/>
      </div>
      {/* Active filter indicator */}
      {stockFilter !== "all" && (
        <div style={{background:stockFilter==="low"?C.redLt:C.greenLt,border:`1px solid ${stockFilter==="low"?C.red:C.green}30`,borderRadius:8,padding:"8px 14px",marginBottom:16,display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:12}}>
          <span style={{color:stockFilter==="low"?C.red:C.green,fontWeight:600}}>
            {stockFilter==="low"?"⚠️ Showing low-stock items only":"✅ Showing in-stock items only"}
          </span>
          <button onClick={()=>setStockFilter("all")} style={{background:"none",border:"none",cursor:"pointer",color:C.inkMid,fontSize:12,fontFamily:"inherit"}}>✕ Clear filter</button>
        </div>
      )}

      {/* Low stock banner */}
      {summary?.low_stock_items?.length > 0 && (
        <div style={{background:C.redLt,border:`1px solid ${C.red}30`,borderRadius:12,padding:"12px 18px",marginBottom:20}}>
          <div style={{fontSize:13,fontWeight:700,color:C.red,marginBottom:6}}>⚠️ Low Stock — Reorder needed</div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            {summary.low_stock_items.map(i=>(
              <span key={i.id} style={{background:C.surface,border:`1px solid ${C.red}30`,borderRadius:8,padding:"4px 10px",fontSize:12,color:C.red}}>{i.name} — {i.quantity_on_hand} {i.unit_of_measure} left</span>
            ))}
          </div>
        </div>
      )}

      {/* Add item form */}
      {showNew && (
        <div style={{background:C.surface,border:`2px solid ${C.accent}`,borderRadius:16,padding:24,marginBottom:20}}>
          <h3 style={{fontFamily:"serif",margin:"0 0 16px",color:C.ink}}>New Inventory Item</h3>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:12}}>
            <div>{lb("Item Name")}<input placeholder="Widget A" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} style={is}/></div>
            <div>{lb("SKU / Code")}<input placeholder="WGT-001" value={form.sku} onChange={e=>setForm(f=>({...f,sku:e.target.value}))} style={is}/></div>
            <div>{lb("Category")}<input placeholder="Electronics" value={form.category} onChange={e=>setForm(f=>({...f,category:e.target.value}))} style={is}/></div>
            <div>{lb("Cost Price (ZAR)")}<input type="number" placeholder="100" value={form.unit_cost} onChange={e=>setForm(f=>({...f,unit_cost:e.target.value}))} style={is}/></div>
            <div>{lb("Selling Price (ZAR)")}<input type="number" placeholder="150" value={form.unit_price} onChange={e=>setForm(f=>({...f,unit_price:e.target.value}))} style={is}/></div>
            <div>{lb("Unit of Measure")}<select value={form.unit_of_measure} onChange={e=>setForm(f=>({...f,unit_of_measure:e.target.value}))} style={is}>{["Unit","Each","Box","Kg","Litre","Metre","Hour"].map(u=><option key={u}>{u}</option>)}</select></div>
            <div>{lb("Opening Stock")}<input type="number" placeholder="0" value={form.quantity_on_hand} onChange={e=>setForm(f=>({...f,quantity_on_hand:e.target.value}))} style={is}/></div>
            <div>{lb("Reorder Level")}<input type="number" placeholder="5" value={form.reorder_level} onChange={e=>setForm(f=>({...f,reorder_level:e.target.value}))} style={is}/></div>
            <div>{lb("Description")}<input placeholder="Optional description" value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} style={is}/></div>
          </div>
          {form.unit_cost && form.unit_price && (
            <div style={{background:C.greenLt,borderRadius:8,padding:"8px 14px",marginBottom:12,fontSize:12,color:C.green}}>
              Margin: {fmt(+form.unit_price - +form.unit_cost)} ({form.unit_cost>0?Math.round(((+form.unit_price - +form.unit_cost)/+form.unit_cost)*100):0}%)
            </div>
          )}
          <div style={{display:"flex",gap:8}}>
            <button onClick={handleCreate} style={{background:C.accent,color:"#fff",border:"none",borderRadius:10,padding:"10px 20px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Add Item</button>
            <button onClick={()=>setShowNew(false)} style={{background:"transparent",color:C.inkMid,border:`1px solid ${C.border}`,borderRadius:10,padding:"10px 20px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editItem && (
        <div style={{position:"fixed",inset:0,background:"#00000060",zIndex:100,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={()=>setEditItem(null)}>
          <div style={{background:C.surface,borderRadius:20,padding:32,width:580,maxHeight:"90vh",overflow:"auto"}} onClick={e=>e.stopPropagation()}>
            <h3 style={{fontFamily:"serif",fontSize:20,color:C.ink,margin:"0 0 20px"}}>Edit Item — {editItem.name}</h3>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16}}>
              {[{l:"Item Name",k:"name"},{l:"SKU",k:"sku"},{l:"Category",k:"category"},{l:"Cost Price",k:"unit_cost",t:"number"},{l:"Selling Price",k:"unit_price",t:"number"},{l:"Stock on Hand",k:"quantity_on_hand",t:"number"},{l:"Reorder Level",k:"reorder_level",t:"number"}].map(f=>(
                <div key={f.k}>{lb(f.l)}<input type={f.t||"text"} value={editItem[f.k]||""} onChange={e=>setEditItem(i=>({...i,[f.k]:e.target.value}))} style={is}/></div>
              ))}
            </div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={handleUpdate} style={{background:C.accent,color:"#fff",border:"none",borderRadius:10,padding:"10px 24px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Save</button>
              <button onClick={()=>setEditItem(null)} style={{background:"transparent",color:C.inkMid,border:`1px solid ${C.border}`,borderRadius:10,padding:"10px 20px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Stock adjustment modal */}
      {adjItem && (
        <div style={{position:"fixed",inset:0,background:"#00000060",zIndex:100,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setAdjItem(null)}>
          <div style={{background:C.surface,borderRadius:20,padding:32,width:420}} onClick={e=>e.stopPropagation()}>
            <h3 style={{fontFamily:"serif",fontSize:20,color:C.ink,margin:"0 0 6px"}}>Adjust Stock — {adjItem.name}</h3>
            <p style={{fontSize:12,color:C.inkMid,marginBottom:20}}>Current: {adjItem.quantity_on_hand} {adjItem.unit_of_measure}</p>
            <div style={{marginBottom:14}}>{lb("Quantity (+ receive / − issue)")}<input type="number" placeholder="e.g. 10 or -5" value={adjQty} onChange={e=>setAdjQty(e.target.value)} style={is}/></div>
            <div style={{marginBottom:20}}>{lb("Reason")}<input placeholder="Purchase order, sale, write-off..." value={adjReason} onChange={e=>setAdjReason(e.target.value)} style={is}/></div>
            {adjQty && <div style={{background:C.greenLt,borderRadius:8,padding:"8px 14px",marginBottom:14,fontSize:12,color:C.green}}>New stock: {Math.max(0,adjItem.quantity_on_hand + +adjQty)} {adjItem.unit_of_measure}</div>}
            <div style={{display:"flex",gap:8}}>
              <button onClick={handleAdjust} style={{background:C.accent,color:"#fff",border:"none",borderRadius:10,padding:"10px 20px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Apply</button>
              <button onClick={()=>setAdjItem(null)} style={{background:"transparent",color:C.inkMid,border:`1px solid ${C.border}`,borderRadius:10,padding:"10px 20px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      <div style={{marginBottom:16}}>
        <input placeholder="Search by name, SKU or category..." value={search} onChange={e=>setSearch(e.target.value)} style={{...is,maxWidth:360}}/>
      </div>

      {/* Table */}
      <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,overflow:"hidden"}}>
        {loading ? <div style={{padding:40,textAlign:"center",color:C.inkMid}}>Loading...</div> :
        filtered.length===0 ? <div style={{padding:40,textAlign:"center",color:C.inkMid}}>No items yet. Add your first inventory item.</div> : (
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
            <thead>
              <tr style={{background:C.bg,borderBottom:`1px solid ${C.border}`}}>
                {["SKU","Name","Category","Cost","Price","Margin","Stock","Value","Status","Actions"].map(h=><th key={h} style={{textAlign:"left",padding:"11px 14px",fontSize:9,color:C.inkMid,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase",whiteSpace:"nowrap"}}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {filtered.map(item=>{
                const margin = item.unit_price>0?Math.round(((item.unit_price-item.unit_cost)/item.unit_price)*100):0;
                const isLow  = item.quantity_on_hand <= item.reorder_level;
                return (
                  <tr key={item.id} style={{borderBottom:`1px solid ${C.border}20`,background:isLow?"#FFF5F520":"transparent"}}>
                    <td style={{padding:"12px 14px",fontFamily:"monospace",color:C.inkMid,fontSize:11}}>{item.sku||"—"}</td>
                    <td style={{padding:"12px 14px",fontWeight:600,color:C.ink}}>{item.name}</td>
                    <td style={{padding:"12px 14px"}}>{item.category?<Badge label={item.category} color={C.blue} bg={C.blueLt}/>:"—"}</td>
                    <td style={{padding:"12px 14px",color:C.inkMid}}>{fmt(item.unit_cost)}</td>
                    <td style={{padding:"12px 14px",fontWeight:600}}>{fmt(item.unit_price)}</td>
                    <td style={{padding:"12px 14px",color:margin>=30?C.green:margin>=15?C.gold:C.red,fontWeight:600}}>{margin}%</td>
                    <td style={{padding:"12px 14px",fontWeight:700,color:isLow?C.red:C.ink}}>{item.quantity_on_hand} {item.unit_of_measure}</td>
                    <td style={{padding:"12px 14px",color:C.green,fontWeight:600}}>{fmt(item.stock_value)}</td>
                    <td style={{padding:"12px 14px"}}>{isLow?<Badge label="Low Stock" color={C.red} bg={C.redLt}/>:<Badge label="OK" color={C.green} bg={C.greenLt}/>}</td>
                    <td style={{padding:"12px 14px"}}>
                      <div style={{display:"flex",gap:5}}>
                        <button onClick={()=>setAdjItem(item)} style={{background:C.goldLt,color:C.gold,border:"none",borderRadius:6,padding:"4px 8px",fontSize:10,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>Adjust</button>
                        <button onClick={()=>setEditItem({...item})} style={{background:C.blueLt,color:C.blue,border:"none",borderRadius:6,padding:"4px 8px",fontSize:10,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>Edit</button>
                        <button onClick={()=>handleDelete(item.id,item.name)} style={{background:C.redLt,color:C.red,border:"none",borderRadius:6,padding:"4px 8px",fontSize:10,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>Del</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── DEVELOPER / API ACCESS ────────────────────────────────────────────────────
function Developer() {
  const [keys,     setKeys]     = useState([]);
  const [usage,    setUsage]    = useState(null);
  const [showNew,  setShowNew]  = useState(false);
  const [newKey,   setNewKey]   = useState(null);   // revealed once on creation
  const [form,     setForm]     = useState({name:"", scopes:["read"]});
  const [loading,  setLoading]  = useState(true);
  const [activeDoc,setActiveDoc]= useState("overview");
  const BASE = "https://zuzan-backend.onrender.com";

  useEffect(()=>{ loadKeys(); },[]);

  const loadKeys = async () => {
    setLoading(true);
    try {
      const [k, u] = await Promise.all([
        api("/api-keys/").catch(()=>[]),
        api("/api-keys/usage").catch(()=>null),
      ]);
      setKeys(k); setUsage(u);
    } finally { setLoading(false); }
  };

  const handleCreate = async () => {
    try {
      const res = await api("/api-keys/",{method:"POST",body:JSON.stringify({name:form.name,scopes:form.scopes})});
      setNewKey(res);
      setShowNew(false);
      setForm({name:"",scopes:["read"]});
      loadKeys();
    } catch(e) { alert("Failed to create key: " + e.message); }
  };

  const handleRevoke = async (id, name) => {
    if (!window.confirm(`Revoke "${name}"? This cannot be undone.`)) return;
    await api(`/api-keys/${id}`,{method:"DELETE"});
    loadKeys();
  };

  const toggleScope = (s) => setForm(f=>({...f,scopes:f.scopes.includes(s)?f.scopes.filter(x=>x!==s):[...f.scopes,s]}));

  const DOCS = {
    overview: {
      title:"Overview",
      content: `# ZuZan API

The ZuZan REST API gives you programmatic access to your financial data.

**Base URL**
\`\`\`
${BASE}/v1
\`\`\`

**Authentication**
Pass your API key in the request header:
\`\`\`
X-API-Key: zuzan_xxxxxxxxxxxxxxxx
\`\`\`

**Rate Limits**
- 1,000 requests per key per day
- Responses are JSON

**Scopes**
| Scope | Access |
|---|---|
| read | Invoices, expenses, summary |
| write | Create/update records |
| payroll | Employee and payroll data |
| reports | Financial reports |`,
    },
    invoices: {
      title:"Invoices",
      content:`# GET /v1/invoices

List all invoices for your company.

**Request**
\`\`\`bash
curl ${BASE}/v1/invoices \\
  -H "X-API-Key: YOUR_API_KEY"
\`\`\`

**Response**
\`\`\`json
[
  {
    "id": 1,
    "invoice_number": "INV-001",
    "client_name": "Acme Pty Ltd",
    "amount": 50000.00,
    "status": "paid",
    "issue_date": "2025-04-01",
    "due_date": "2025-05-01"
  }
]
\`\`\`

**Scopes required:** \`read\``,
    },
    expenses: {
      title:"Expenses",
      content:`# GET /v1/expenses

List all expenses.

**Request**
\`\`\`bash
curl ${BASE}/v1/expenses \\
  -H "X-API-Key: YOUR_API_KEY"
\`\`\`

**Response**
\`\`\`json
[
  {
    "id": 1,
    "vendor": "Eskom",
    "description": "Electricity",
    "amount": 3200.00,
    "category": "6220 - Electricity and Water",
    "date": "2025-04-02"
  }
]
\`\`\`

**Scopes required:** \`read\``,
    },
    employees: {
      title:"Employees",
      content:`# GET /v1/employees

List active employees (requires payroll scope).

**Request**
\`\`\`bash
curl ${BASE}/v1/employees \\
  -H "X-API-Key: YOUR_API_KEY"
\`\`\`

**Response**
\`\`\`json
[
  {
    "id": 1,
    "name": "Sipho Dlamini",
    "position": "Developer",
    "department": "Tech",
    "gross_salary": 45000.00
  }
]
\`\`\`

**Scopes required:** \`payroll\``,
    },
    summary: {
      title:"Summary",
      content:`# GET /v1/summary

Get a financial summary of your company.

**Request**
\`\`\`bash
curl ${BASE}/v1/summary \\
  -H "X-API-Key: YOUR_API_KEY"
\`\`\`

**Response**
\`\`\`json
{
  "company": "Acme Pty Ltd",
  "total_revenue": 156500.00,
  "total_expenses": 24600.00,
  "outstanding": 96500.00,
  "net_profit": 131900.00
}
\`\`\`

**Scopes required:** \`read\``,
    },
  };

  const codeBlock = (text) => (
    <pre style={{background:"#1A1209",color:"#C8E6C9",borderRadius:10,padding:"14px 18px",fontSize:12,overflow:"auto",lineHeight:1.7,margin:"10px 0"}}>{text}</pre>
  );

  const renderDoc = (doc) => doc.content.split("\n").map((line,i)=>{
    if(line.startsWith("# ")) return <h2 key={i} style={{fontFamily:"serif",fontSize:20,color:C.ink,margin:"0 0 12px"}}>{line.slice(2)}</h2>;
    if(line.startsWith("## ")) return <h3 key={i} style={{fontFamily:"serif",fontSize:16,color:C.ink,margin:"16px 0 8px"}}>{line.slice(3)}</h3>;
    if(line.startsWith("**") && line.endsWith("**")) return <div key={i} style={{fontWeight:700,color:C.ink,margin:"12px 0 4px"}}>{line.slice(2,-2)}</div>;
    if(line.startsWith("```")) return null;
    if(line.startsWith("|")) return <div key={i} style={{fontFamily:"monospace",fontSize:12,color:C.inkMid,padding:"3px 0",borderBottom:`1px solid ${C.border}20`}}>{line}</div>;
    if(line.trim()==="") return <div key={i} style={{height:8}}/>;
    return <div key={i} style={{fontSize:13,color:C.inkMid,lineHeight:1.7}}>{line}</div>;
  });

  return (
    <div>
      <SectionHeader title="Developer API" sub="Integrate ZuZan with your systems"/>

      {/* Usage summary */}
      {usage && (
        <div style={{display:"flex",gap:12,marginBottom:24}}>
          <KPI label="Active Keys" value={usage.active_keys} color={C.blue} icon="🔑"/>
          <KPI label="Requests Today" value={usage.total_requests_today} sub={`of ${usage.rate_limit_per_key * usage.active_keys} limit`} color={C.green} icon="📡"/>
          <KPI label="Base URL" value="REST API" sub={BASE} color={C.accent} icon="🌐"/>
        </div>
      )}

      <div style={{display:"grid",gridTemplateColumns:"260px 1fr",gap:20}}>
        {/* Left: Keys + sidebar nav */}
        <div>
          {/* API Keys panel */}
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,padding:20,marginBottom:16}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div style={{fontSize:13,fontWeight:700,color:C.ink}}>API Keys</div>
              <button onClick={()=>setShowNew(true)} style={{background:C.accent,color:"#fff",border:"none",borderRadius:8,padding:"5px 12px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>+ New</button>
            </div>

            {showNew && (
              <div style={{background:C.bg,borderRadius:10,padding:14,marginBottom:14}}>
                <div style={{marginBottom:8}}>
                  <label style={{fontSize:10,fontWeight:600,color:C.inkMid,display:"block",marginBottom:4,textTransform:"uppercase"}}>Key Name</label>
                  <input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="My Integration" style={{width:"100%",padding:"8px 10px",border:`1px solid ${C.border}`,borderRadius:6,fontSize:12,fontFamily:"inherit",background:C.surface,color:C.ink,outline:"none",boxSizing:"border-box"}}/>
                </div>
                <div style={{marginBottom:10}}>
                  <label style={{fontSize:10,fontWeight:600,color:C.inkMid,display:"block",marginBottom:4,textTransform:"uppercase"}}>Scopes</label>
                  {["read","write","payroll","reports"].map(s=>(
                    <label key={s} style={{display:"flex",alignItems:"center",gap:6,marginBottom:4,cursor:"pointer",fontSize:12,color:C.inkMid}}>
                      <input type="checkbox" checked={form.scopes.includes(s)} onChange={()=>toggleScope(s)}/>
                      {s}
                    </label>
                  ))}
                </div>
                <div style={{display:"flex",gap:6}}>
                  <button onClick={handleCreate} style={{flex:1,background:C.accent,color:"#fff",border:"none",borderRadius:6,padding:"7px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Create</button>
                  <button onClick={()=>setShowNew(false)} style={{flex:1,background:"transparent",border:`1px solid ${C.border}`,borderRadius:6,padding:"7px",fontSize:11,cursor:"pointer",fontFamily:"inherit",color:C.inkMid}}>Cancel</button>
                </div>
              </div>
            )}

            {newKey && (
              <div style={{background:"#1A1209",borderRadius:10,padding:14,marginBottom:14}}>
                <div style={{fontSize:11,color:"#C8E6C9",fontWeight:700,marginBottom:6}}>⚠️ Copy now — shown once only</div>
                <div style={{fontFamily:"monospace",fontSize:10,color:"#80CBC4",wordBreak:"break-all",marginBottom:8}}>{newKey.key}</div>
                <button onClick={()=>{navigator.clipboard.writeText(newKey.key); alert("Copied!");}} style={{width:"100%",background:"#2A6B3C",color:"#fff",border:"none",borderRadius:6,padding:"6px",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Copy Key</button>
                <button onClick={()=>setNewKey(null)} style={{width:"100%",background:"transparent",color:"#aaa",border:"none",padding:"4px",fontSize:10,cursor:"pointer",fontFamily:"inherit",marginTop:4}}>Dismiss</button>
              </div>
            )}

            {loading ? <div style={{fontSize:12,color:C.inkMid,textAlign:"center",padding:16}}>Loading...</div> :
              keys.length===0 ? <div style={{fontSize:12,color:C.inkMid,textAlign:"center",padding:12}}>No keys yet</div> :
              keys.map(k=>(
                <div key={k.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:`1px solid ${C.border}30`}}>
                  <div>
                    <div style={{fontSize:12,fontWeight:600,color:C.ink}}>{k.name}</div>
                    <div style={{fontSize:10,fontFamily:"monospace",color:C.inkMid}}>{k.key_prefix}...</div>
                    <div style={{fontSize:10,color:C.inkDim}}>{k.scopes} · {k.requests_today} req today</div>
                  </div>
                  <button onClick={()=>handleRevoke(k.id,k.name)} style={{background:C.redLt,color:C.red,border:"none",borderRadius:6,padding:"4px 8px",fontSize:10,cursor:"pointer",fontFamily:"inherit"}}>Revoke</button>
                </div>
              ))
            }
          </div>

          {/* Doc nav */}
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,padding:14}}>
            <div style={{fontSize:11,fontWeight:700,color:C.inkMid,textTransform:"uppercase",letterSpacing:0.5,marginBottom:10}}>Documentation</div>
            {Object.entries(DOCS).map(([key,doc])=>(
              <button key={key} onClick={()=>setActiveDoc(key)} style={{width:"100%",textAlign:"left",padding:"8px 10px",borderRadius:8,border:"none",background:activeDoc===key?C.accentLt:"transparent",color:activeDoc===key?C.accent:C.inkMid,fontSize:12,fontWeight:activeDoc===key?700:400,cursor:"pointer",fontFamily:"inherit",marginBottom:2}}>
                {doc.title}
              </button>
            ))}
          </div>
        </div>

        {/* Right: Documentation */}
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,padding:28}}>
          {/* Code block sections */}
          {activeDoc==="overview" ? (
            <div>
              <h2 style={{fontFamily:"serif",fontSize:22,color:C.ink,margin:"0 0 16px"}}>ZuZan API Overview</h2>
              <p style={{fontSize:13,color:C.inkMid,lineHeight:1.7,marginBottom:16}}>The ZuZan REST API lets you integrate your financial data with other systems — accounting tools, dashboards, ERPs, or custom scripts.</p>
              <div style={{fontWeight:700,color:C.ink,marginBottom:6,fontSize:13}}>Base URL</div>
              {codeBlock(BASE + "/v1")}
              <div style={{fontWeight:700,color:C.ink,marginBottom:6,fontSize:13,marginTop:16}}>Authentication</div>
              {codeBlock(`curl ${BASE}/v1/summary \\\n  -H "X-API-Key: zuzan_your_api_key_here"`)}
              <div style={{fontWeight:700,color:C.ink,marginBottom:6,fontSize:13,marginTop:16}}>Available Endpoints</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginTop:8}}>
                {[["GET /v1/summary","Financial overview",C.green],["GET /v1/invoices","List all invoices",C.blue],["GET /v1/expenses","List all expenses",C.accent],["GET /v1/employees","Employee list (payroll scope)",C.gold]].map(([ep,desc,c])=>(
                  <div key={ep} style={{background:C.bg,borderRadius:10,padding:"12px 14px"}}>
                    <div style={{fontFamily:"monospace",fontSize:11,color:c,fontWeight:700,marginBottom:4}}>{ep}</div>
                    <div style={{fontSize:11,color:C.inkMid}}>{desc}</div>
                  </div>
                ))}
              </div>
              <div style={{background:C.goldLt,border:`1px solid ${C.gold}30`,borderRadius:10,padding:"12px 16px",marginTop:20,fontSize:12,color:C.inkMid}}>
                <strong style={{color:C.ink}}>Rate Limits:</strong> 1,000 requests/day per key. Headers <code>X-RateLimit-Remaining</code> and <code>X-RateLimit-Reset</code> are included in every response.
              </div>
            </div>
          ) : (
            <div>{renderDoc(DOCS[activeDoc])}{activeDoc!=="overview" && codeBlock(DOCS[activeDoc].content.split("```bash\n")[1]?.split("\n```")[0]||"")}</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── MOBILE DASHBOARD ──────────────────────────────────────────────────────────
function MobileDashboard({live, user, onNavigate}) {
  const d = live.dashboard;
  const totalRevenue  = d ? d.total_revenue  : MOCK_INVOICES.filter(i=>i.status==="paid").reduce((s,i)=>s+i.amount,0);
  const totalExpenses = d ? d.total_expenses : MOCK_EXPENSES.reduce((s,e)=>s+e.amount,0);
  const outstanding   = d ? d.total_outstanding : MOCK_INVOICES.filter(i=>i.status!=="paid").reduce((s,i)=>s+i.amount,0);
  const profit        = d ? Math.round((d.net_profit - (d.tax_provision||0))*100)/100 : totalRevenue - totalExpenses;   // same as the desktop dashboard: after payroll and tax

  return (
    <div style={{padding:"0 0 100px"}}>
      {/* Header */}
      <div style={{background:`linear-gradient(135deg,${C.accent},#A03010)`,padding:"28px 20px 32px",marginBottom:-16}}>
        <div style={{fontSize:13,color:"rgba(255,255,255,0.7)",marginBottom:4}}>{(()=>{const h=new Date().getHours();return h<12?"Good morning":h<17?"Good afternoon":"Good evening";})()}</div>
        <div style={{fontFamily:"serif",fontSize:24,color:"#fff",fontWeight:800}}>{user?.companyName||"Your Company"}</div>
        <div style={{fontSize:12,color:"rgba(255,255,255,0.6)",marginTop:2}}>{new Date().toLocaleDateString("en-ZA",{weekday:"long",day:"numeric",month:"long"})}</div>
      </div>

      <div style={{padding:"0 16px"}}>
        {/* Main KPI card */}
        <div style={{background:C.surface,borderRadius:20,padding:20,boxShadow:"0 4px 24px rgba(0,0,0,0.08)",marginBottom:16}}>
          <div style={{fontSize:11,color:C.inkMid,fontWeight:600,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8}}>Net Profit (all time, after tax)</div>
          <div style={{fontFamily:"serif",fontSize:36,fontWeight:800,color:profit>=0?C.green:C.red}}>{fmt(profit)}</div>
          <div style={{display:"flex",gap:16,marginTop:12}}>
            <div><div style={{fontSize:10,color:C.inkMid}}>Revenue</div><div style={{fontSize:15,fontWeight:700,color:C.green}}>{fmt(totalRevenue)}</div></div>
            <div style={{width:1,background:C.border}}/>
            <div><div style={{fontSize:10,color:C.inkMid}}>Expenses</div><div style={{fontSize:15,fontWeight:700,color:C.red}}>{fmt(totalExpenses)}</div></div>
            <div style={{width:1,background:C.border}}/>
            <div><div style={{fontSize:10,color:C.inkMid}}>Outstanding</div><div style={{fontSize:15,fontWeight:700,color:C.gold}}>{fmt(outstanding)}</div></div>
          </div>
        </div>

        {/* Quick actions */}
        <div style={{fontSize:13,fontWeight:700,color:C.ink,marginBottom:10}}>Quick Actions</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:20}}>
          {[
            {label:"New Invoice",   icon:"🧾",tab:"invoicing", color:C.accent},
            {label:"Add Expense",   icon:"💳",tab:"expenses",  color:C.blue},
            {label:"View Reports",  icon:"📊",tab:"reports",   color:C.green},
            {label:"Run Payroll",   icon:"👥",tab:"payroll",   color:C.gold},
          ].map(a=>(
            <button key={a.tab} onClick={()=>onNavigate(a.tab)}
              style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:14,padding:"16px 14px",display:"flex",alignItems:"center",gap:10,cursor:"pointer",fontFamily:"inherit",textAlign:"left",boxShadow:"0 2px 8px rgba(0,0,0,0.04)"}}>
              <div style={{width:40,height:40,borderRadius:12,background:a.color+"15",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}}>{a.icon}</div>
              <span style={{fontSize:13,fontWeight:600,color:C.ink}}>{a.label}</span>
            </button>
          ))}
        </div>

        {/* Recent invoices */}
        <div style={{fontSize:13,fontWeight:700,color:C.ink,marginBottom:10}}>Recent Invoices</div>
        {recentInvoicesFrom(live,4).map(inv=>{
          const statusColor = inv.status==="paid"?C.green:inv.status==="overdue"?C.red:C.gold;
          const statusBg    = inv.status==="paid"?C.greenLt:inv.status==="overdue"?C.redLt:C.goldLt;
          return (
            <div key={inv.id} style={{background:C.surface,borderRadius:14,padding:"14px 16px",marginBottom:10,boxShadow:"0 2px 8px rgba(0,0,0,0.04)"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                <div>
                  <div style={{fontSize:14,fontWeight:700,color:C.ink}}>{inv.client}</div>
                  <div style={{fontSize:11,color:C.inkMid,marginTop:2}}>{inv.id} · Due {fmtDate(inv.due)}</div>
                </div>
                <div style={{textAlign:"right"}}>
                  <div style={{fontSize:15,fontWeight:800,color:C.ink}}>{fmt(inv.amount)}</div>
                  <div style={{marginTop:4,fontSize:10,fontWeight:700,color:statusColor,background:statusBg,padding:"2px 8px",borderRadius:10,textTransform:"uppercase"}}>{inv.status}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── MOBILE SHELL ──────────────────────────────────────────────────────────────
function MobileApp({user, onLogout, onUserUpdate, live, docTemplate, onTemplateChange}) {
  const [tab, setTab] = useState("home");
  const [moreOpen, setMoreOpen] = useState(false);

  const BOTTOM_TABS = [
    {id:"home",      label:"Home",     icon:"🏠"},
    {id:"invoicing", label:"Invoices", icon:"🧾"},
    {id:"expenses",  label:"Expenses", icon:"💳"},
    {id:"payroll",   label:"Payroll",  icon:"👥"},
    {id:"more",      label:"More",     icon:"⋯"},
  ];

  const MORE_ITEMS = [
    {id:"quotes",          label:"Quotes",      icon:"📝"},
    {id:"budgeting",       label:"Budgeting",   icon:"🎯"},
    {id:"customers",       label:"Customers",   icon:"👥"},
    {id:"suppliers",       label:"Suppliers",   icon:"🏭"},
    {id:"purchase_orders", label:"Purchase Orders", icon:"📋"},
    {id:"reports",         label:"Reports",     icon:"📊"},
    {id:"inventory",       label:"Inventory",   icon:"📦"},
    {id:"fixed_assets",   label:"Fixed Assets", icon:"🏭"},
    {id:"bankimport",      label:"Bank",        icon:"🏦"},
    {id:"debtors",         label:"Debtors",     icon:"📥"},
    {id:"creditors",       label:"Creditors",   icon:"📤"},
    {id:"coa",             label:"Accounts",    icon:"📒"},
    {id:"data_import",     label:"Import Data", icon:"⬆️"},
    {id:"settings",        label:"Settings",    icon:"⚙️"},
  ];

  const navigate = (id) => { setTab(id); setMoreOpen(false); };

  const screens = {
    home:       <MobileDashboard live={live} user={user} onNavigate={navigate}/>,
    invoicing:  <div style={{padding:"16px 16px 100px"}}><Invoicing  live={live} user={user} docTemplate={docTemplate}/></div>,
    expenses:   <div style={{padding:"16px 16px 100px"}}><Expenses   live={live}/></div>,
    payroll:    <div style={{padding:"16px 16px 100px"}}><Payroll    live={live} user={user}/></div>,
    quotes:     <div style={{padding:"16px 16px 100px"}}><Quotes     live={live} user={user} onNavigate={navigate} docTemplate={docTemplate}/></div>,
    budgeting:  <div style={{padding:"16px 16px 100px"}}><Budgeting  live={live}/></div>,
    reports:    <div style={{padding:"16px 16px 100px"}}><Reports    live={live} user={user}/></div>,
    inventory:       <div style={{padding:"16px 16px 100px"}}><Inventory/></div>,
    fixed_assets:    <div style={{padding:"16px 16px 100px"}}><FixedAssets/></div>,
    customers:       <div style={{padding:"16px 16px 100px"}}><Customers/></div>,
    suppliers:       <div style={{padding:"16px 16px 100px"}}><Suppliers/></div>,
    purchase_orders: <div style={{padding:"16px 16px 100px"}}><PurchaseOrders/></div>,
    bankimport:      <div style={{padding:"16px 16px 100px"}}><BankImport live={live} onNavigate={navigate}/></div>,
    debtors:    <div style={{padding:"16px 16px 100px"}}><Debtors    live={live}/></div>,
    creditors:  <div style={{padding:"16px 16px 100px"}}><Creditors  live={live}/></div>,
    coa:        <div style={{padding:"16px 16px 100px"}}><ChartOfAccounts/></div>,
    data_import: <div style={{padding:"16px 16px 100px"}}><DataImport/></div>,
    settings:   <div style={{padding:"16px 16px 100px"}}><AppSettings user={user} onLogout={onLogout} onUserUpdate={onUserUpdate} docTemplate={docTemplate} onTemplateChange={onTemplateChange}/></div>,
  };

  const activeLabel = [...BOTTOM_TABS,...MORE_ITEMS].find(t=>t.id===tab)?.label || "ZuZan";

  return (
    <div style={{fontFamily:"sans-serif",background:C.bg,minHeight:"100vh",minHeight:"100dvh"}}>
      <style>{`*{box-sizing:border-box;margin:0;padding:0;}button:active{opacity:0.7;}
        /* wide tables scroll sideways inside their card instead of being cut off */
        div:has(> table), div:has(> div > table){overflow-x:auto !important;-webkit-overflow-scrolling:touch;}
        input:not([type=checkbox]):not([type=radio]),select,textarea{font-size:16px !important;} /* stops iOS zooming in on focus */
        button,select{min-height:36px;} /* comfortable tap size */
        input[type=checkbox],input[type=radio]{width:20px;height:20px;}`}</style>

      {/* Top bar */}
      <div style={{position:"fixed",top:0,left:0,right:0,height:56,background:C.surface,borderBottom:`1px solid ${C.border}`,display:"flex",alignItems:"center",justifyContent:"space-between",padding:"0 16px",zIndex:50}}>
        <div style={{fontFamily:"serif",fontSize:22,fontWeight:800,color:C.ink}}><span style={{color:C.accent}}>Zu</span>Zan</div>
        <div style={{fontSize:14,fontWeight:600,color:C.ink}}>{activeLabel}</div>
        <button onClick={()=>navigate("settings")} style={{background:"none",border:"none",fontSize:22,cursor:"pointer",minWidth:40,minHeight:40}}>{user?.firstName?.[0]||"👤"}</button>
      </div>

      {/* Content */}
      <div style={{paddingTop:56,minHeight:"100vh",overflowY:"auto"}}>
        {screens[tab] || screens.home}
      </div>

      {/* More drawer */}
      {moreOpen && (
        <>
          <div onClick={()=>setMoreOpen(false)} style={{position:"fixed",inset:0,background:"#00000050",zIndex:60}}/>
          <div style={{position:"fixed",bottom:0,left:0,right:0,background:C.surface,borderRadius:"24px 24px 0 0",padding:"12px 0 40px",zIndex:70}}>
            <div style={{width:40,height:4,background:C.border,borderRadius:2,margin:"0 auto 20px"}}/>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:4,padding:"0 16px"}}>
              {MORE_ITEMS.map(item=>(
                <button key={item.id} onClick={()=>navigate(item.id)}
                  style={{background:"transparent",border:"none",cursor:"pointer",fontFamily:"inherit",padding:"12px 4px",display:"flex",flexDirection:"column",alignItems:"center",gap:6,borderRadius:12,background:tab===item.id?C.accentLt:"transparent"}}>
                  <span style={{fontSize:26}}>{item.icon}</span>
                  <span style={{fontSize:10,color:tab===item.id?C.accent:C.inkMid,fontWeight:tab===item.id?700:400}}>{item.label}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Bottom navigation */}
      <div style={{position:"fixed",bottom:0,left:0,right:0,background:C.surface,borderTop:`1px solid ${C.border}`,display:"flex",paddingBottom:"env(safe-area-inset-bottom)",zIndex:50}}>
        {BOTTOM_TABS.map(t=>{
          const active = t.id==="more" ? moreOpen : tab===t.id;
          return (
            <button key={t.id} onClick={()=>{ if(t.id==="more"){setMoreOpen(o=>!o);}else{setTab(t.id);setMoreOpen(false);}}}
              style={{flex:1,background:"none",border:"none",cursor:"pointer",padding:"10px 4px 8px",display:"flex",flexDirection:"column",alignItems:"center",gap:3,fontFamily:"inherit"}}>
              <span style={{fontSize:22}}>{t.icon}</span>
              <span style={{fontSize:10,color:active?C.accent:C.inkMid,fontWeight:active?700:400}}>{t.label}</span>
              {active && t.id!=="more" && <div style={{width:4,height:4,borderRadius:"50%",background:C.accent}}/>}
            </button>
          );
        })}
      </div>
      <AIAssistant tab={tab}/>
    </div>
  );
}

// ── AFS PAYMENT GATE ─────────────────────────────────────────────────────────
function AfsPaymentGate() {
  const now = new Date();
  // SA financial year ends in February — FY label = the year containing February
  const fy = now.getMonth() < 2 ? now.getFullYear() : now.getFullYear() + 1;
  const [status, setStatus] = useState(null); // null=loading, true=paid, false=not paid
  const [paying, setPaying]  = useState(false);
  const [error,  setError]   = useState("");

  useEffect(() => {
    api(`/billing/afs-status?year=${fy}`)
      .then(d => setStatus(d.paid))
      .catch(() => setStatus(false));
  }, [fy]);

  const handleUnlock = async () => {
    setPaying(true); setError("");
    try {
      const res = await api("/billing/afs-initiate", {
        method: "POST",
        body: JSON.stringify({financial_year: fy}),
      });
      pfSubmit(res.payfast_url, res.payfast_data);
    } catch(e) {
      setError(e.message || "Payment failed — please try again.");
      setPaying(false);
    }
  };

  if (status === null) return (
    <div style={{display:"flex",alignItems:"center",justifyContent:"center",minHeight:300}}>
      <div style={{color:C.inkMid,fontSize:14}}>Checking AFS status…</div>
    </div>
  );

  if (status === true) return <FinancialStatements/>;

  return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",minHeight:480,padding:40,textAlign:"center"}}>
      <div style={{fontSize:56,marginBottom:20}}>📑</div>
      <h2 style={{fontFamily:"serif",fontSize:28,color:C.ink,margin:"0 0 12px"}}>Annual Financial Statements</h2>
      <p style={{fontSize:14,color:C.inkMid,maxWidth:460,lineHeight:1.7,margin:"0 0 8px"}}>
        Generate IFRS-compliant AFS for <strong>FY{fy}</strong> — Income Statement, Balance Sheet, Cash Flow Statement, and Statement of Changes in Equity — ready for your accountant or auditor.
      </p>
      <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,padding:"20px 32px",margin:"24px 0",display:"inline-block"}}>
        <div style={{fontSize:13,color:C.inkMid,marginBottom:4}}>Once-off fee for FY{fy}</div>
        <div style={{fontSize:36,fontWeight:800,color:C.accent}}>R1,999</div>
        <div style={{fontSize:12,color:C.inkMid,marginTop:4}}>Unlocks AFS for the full {fy} financial year</div>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:28,textAlign:"left",maxWidth:360}}>
        {["✅ Income Statement","✅ Balance Sheet","✅ Cash Flow Statement","✅ Statement of Changes in Equity","✅ IFRS-compliant notes","✅ PDF download ready"].map(f=>(
          <div key={f} style={{fontSize:13,color:C.ink,padding:"8px 14px",background:C.greenLt,borderRadius:8}}>{f}</div>
        ))}
      </div>
      {error && <div style={{color:C.red,fontSize:13,marginBottom:12}}>{error}</div>}
      <button
        onClick={handleUnlock}
        disabled={paying}
        style={{background:paying?C.inkDim:C.accent,color:"#fff",border:"none",borderRadius:12,padding:"15px 40px",fontSize:16,fontWeight:700,cursor:paying?"not-allowed":"pointer",fontFamily:"inherit"}}>
        {paying ? "Redirecting to payment…" : `Unlock AFS for FY${fy} — R1,999`}
      </button>
      <p style={{fontSize:11,color:C.inkDim,marginTop:12}}>Secure payment via PayFast · Instant unlock on payment confirmation</p>
    </div>
  );
}

// ── ANNUAL FINANCIAL STATEMENTS ───────────────────────────────────────────────
function FinancialStatements() {
  const now   = new Date();
  // SA FY label = the February the year ends in.
  // Mar–Dec → next Feb → FY = current year + 1.  Jan–Feb → this Feb → FY = current year.
  const defFY = now.getMonth() >= 2 ? now.getFullYear() + 1 : now.getFullYear();
  const [year, setYear]   = useState(defFY);
  const [data, setData]   = useState(null);
  const [tab,  setATab]   = useState("is");   // is | bs | cf | notes
  const [loading, setL]   = useState(false);
  const [err,  setErr]    = useState(null);
  const token = localStorage.getItem("zuzan_token");

  const years = Array.from({length:6},(_,i)=>defFY-i);

  useEffect(() => { load(); }, [year]);

  async function load() {
    setL(true); setErr(null);
    try {
      const r = await fetch(`${BASE_URL}/financial-statements/annual?year=${year}`,
        {headers:{Authorization:"Bearer "+token}});
      if (!r.ok) throw new Error((await r.json()).detail || "Failed");
      setData(await r.json());
    } catch(e) { setErr(e.message); }
    finally { setL(false); }
  }

  const fmt = v => "R " + (v||0).toLocaleString("en-ZA",{minimumFractionDigits:2,maximumFractionDigits:2});
  const pct = v => (v||0).toFixed(1) + "%";
  const neg = v => v < 0;

  const Row = ({label, value, bold, indent, hr, sub}) => (
    <>
      {hr && <tr><td colSpan={2}><div style={{borderTop:`1px solid ${C.border}`,margin:"4px 0"}}/></td></tr>}
      <tr style={{background:bold?"#fdf9f7":"transparent"}}>
        <td style={{padding:"5px 8px",paddingLeft: indent?32:8,fontSize:sub?11:13,color:sub?C.inkMid:C.ink,fontWeight:bold?700:400}}>{label}</td>
        <td style={{padding:"5px 8px",textAlign:"right",fontSize:sub?11:13,fontWeight:bold?700:400,
          color:neg(value)?"#C8401A":bold?"#1a1a1a":C.ink,fontFamily:"monospace",whiteSpace:"nowrap"}}>
          {value !== undefined ? fmt(value) : ""}
        </td>
      </tr>
    </>
  );

  function renderIS() {
    if (!data) return null;
    const is = data.income_statement;
    return (
      <table style={{width:"100%",borderCollapse:"collapse"}}>
        <tbody>
          <Row label="REVENUE" bold/>
          {is.revenue_lines.map(l=><Row key={l.code} label={`${l.name}`} value={l.amount} indent sub/>)}
          <Row label="Total Revenue" value={is.total_revenue} bold hr/>
          <Row label="COST OF GOODS SOLD" bold/>
          {is.cogs_lines.map(l=><Row key={l.code} label={l.name} value={l.amount} indent sub/>)}
          <Row label="Total COGS" value={is.total_cogs} bold hr/>
          <Row label="Gross Profit" value={is.gross_profit} bold hr/>
          <tr><td style={{padding:"3px 8px",fontSize:11,color:C.inkMid,fontStyle:"italic"}} colSpan={2}>
            Gross Margin: {pct(is.gross_margin_pct)}</td></tr>
          <Row label="OPERATING EXPENSES" bold/>
          {is.opex_lines.map(l=><Row key={l.code} label={l.name} value={l.amount} indent sub/>)}
          {is.depreciation>0 && <Row label="Depreciation" value={is.depreciation} indent sub/>}
          <Row label="Total Operating Expenses" value={is.total_opex} bold hr/>
          <Row label="Operating Profit (EBIT)" value={is.ebit} bold hr/>
          {is.finance_costs>0 && <Row label="Finance Costs (Interest Paid)" value={is.finance_costs} indent/>}
          {is.finance_costs>0 && <Row label="Profit Before Tax" value={is.profit_before_tax} bold hr/>}
          <Row label="Income Tax (27%)" value={is.tax_expense} indent/>
          <Row label="NET PROFIT FOR THE YEAR" value={is.net_profit} bold hr/>
        </tbody>
      </table>
    );
  }

  function renderBS() {
    if (!data) return null;
    const bs = data.balance_sheet;
    return (
      <table style={{width:"100%",borderCollapse:"collapse"}}>
        <tbody>
          <Row label="ASSETS" bold/>
          <Row label="Non-Current Assets" bold sub/>
          {bs.assets.non_current.map(l=><Row key={l.code} label={l.name} value={l.amount} indent sub/>)}
          <Row label="Total Non-Current Assets" value={bs.assets.total_non_current} bold hr/>
          <Row label="Current Assets" bold sub/>
          {bs.assets.current.map(l=><Row key={l.code} label={l.name} value={l.amount} indent sub/>)}
          <Row label="Total Current Assets" value={bs.assets.total_current} bold hr/>
          <Row label="TOTAL ASSETS" value={bs.assets.total} bold hr/>

          <tr><td colSpan={2} style={{height:12}}/></tr>
          <Row label="EQUITY AND LIABILITIES" bold/>
          <Row label="Equity" bold sub/>
          {bs.equity.lines.map(l=><Row key={l.code} label={l.name} value={l.amount} indent sub/>)}
          <Row label="Retained Earnings" value={bs.equity.retained_earnings} indent sub/>
          <Row label="Total Equity" value={bs.equity.total} bold hr/>

          <Row label="Non-Current Liabilities" bold sub/>
          {bs.liabilities.non_current.map(l=><Row key={l.code} label={l.name} value={l.amount} indent sub/>)}
          {!bs.liabilities.non_current.length && <Row label="No non-current liabilities" indent sub/>}
          <Row label="Total Non-Current Liabilities" value={bs.liabilities.total_non_current} bold hr/>

          <Row label="Current Liabilities" bold sub/>
          {bs.liabilities.current.map(l=><Row key={l.code} label={l.name} value={l.amount} indent sub/>)}
          <Row label="Total Current Liabilities" value={bs.liabilities.total_current} bold hr/>

          <Row label="TOTAL EQUITY AND LIABILITIES" value={bs.total_equity_and_liabilities} bold hr/>
          {bs.balanced
            ? <tr><td colSpan={2} style={{padding:"6px 8px",fontSize:11,color:"#28a745",fontStyle:"italic"}}>✓ Balance sheet balances</td></tr>
            : <tr><td colSpan={2} style={{padding:"6px 8px",fontSize:11,color:"#C8401A",fontStyle:"italic"}}>⚠ Difference: {fmt(Math.abs(bs.assets.total-bs.total_equity_and_liabilities))} — post journal entries to correct.</td></tr>
          }
        </tbody>
      </table>
    );
  }

  function renderCF() {
    if (!data) return null;
    const cf = data.cash_flow;
    return (
      <table style={{width:"100%",borderCollapse:"collapse"}}>
        <tbody>
          <Row label="OPERATING ACTIVITIES" bold/>
          <Row label="Net profit for the year"         value={cf.operating.net_profit}           indent/>
          <Row label="Adjustments:"                                                                indent sub/>
          <Row label="  Add: Depreciation"             value={cf.operating.add_depreciation}     indent sub/>
          <Row label="Changes in working capital:"                                                 indent sub/>
          <Row label="  (Increase)/Decrease in trade receivables" value={cf.operating.change_in_ar}      indent sub/>
          <Row label="  Increase/(Decrease) in trade payables"    value={cf.operating.change_in_ap}      indent sub/>
          <Row label="  (Increase)/Decrease in inventory"         value={cf.operating.change_in_inventory} indent sub/>
          {(cf.operating.change_in_other_assets||0)!==0 && <Row label="  (Increase)/Decrease in other current assets" value={cf.operating.change_in_other_assets} indent sub/>}
          {(cf.operating.change_in_other_liabilities||0)!==0 && <Row label="  Increase/(Decrease) in VAT, PAYE, tax and other liabilities" value={cf.operating.change_in_other_liabilities} indent sub/>}
          {(cf.operating.less_gain_on_disposal||0)!==0 && <Row label="  Less: (gain)/loss on disposal of assets" value={cf.operating.less_gain_on_disposal} indent sub/>}
          <Row label="Net Cash from Operating Activities"         value={cf.operating.total}     bold hr/>

          <tr><td colSpan={2} style={{height:8}}/></tr>
          <Row label="INVESTING ACTIVITIES" bold/>
          <Row label="Purchase of property, plant & equipment" value={cf.investing.purchase_of_fa} indent/>
          {cf.investing.disposal_proceeds!==0 && <Row label="Proceeds from disposal of assets" value={cf.investing.disposal_proceeds} indent/>}
          {(cf.investing.other||0)!==0 && <Row label="Other non-current assets" value={cf.investing.other} indent/>}
          <Row label="Net Cash from Investing Activities" value={cf.investing.total} bold hr/>

          <tr><td colSpan={2} style={{height:8}}/></tr>
          <Row label="FINANCING ACTIVITIES" bold/>
          {(cf.financing.owner_contributions||0)!==0 && <Row label="Owner contributions / capital introduced" value={cf.financing.owner_contributions} indent/>}
          {(cf.financing.owner_drawings||0)!==0 && <Row label="Owner drawings / dividends" value={cf.financing.owner_drawings} indent/>}
          <Row label="Proceeds from / (repayment of) long-term loans" value={cf.financing.change_in_long_term_liabilities||0} indent sub/>
          <Row label="Net Cash from Financing Activities" value={cf.financing.total||0} bold hr/>

          <tr><td colSpan={2} style={{height:8}}/></tr>
          <Row label="Net Increase / (Decrease) in Cash" value={cf.net_change} bold hr/>
          <Row label="Cash at beginning of period"       value={cf.opening_cash} indent/>
          <Row label="Cash at end of period"             value={cf.closing_cash} bold/>
          {cf.reconciles_to_ledger===false && <tr><td colSpan={2} style={{padding:"8px 0",fontSize:12,color:C.red}}>⚠ This does not agree to the bank account in your ledger ({fmt(cf.ledger_closing_cash)}). Check for transactions posted directly to the journal or to accounts outside the normal groups.</td></tr>}
        </tbody>
      </table>
    );
  }

  function renderSCE() {
    if (!data || !data.changes_in_equity) return null;
    const ce   = data.changes_in_equity;
    const fmtV = v => v === 0 ? "—" : ("R " + Math.abs(v).toLocaleString("en-ZA",{minimumFractionDigits:2,maximumFractionDigits:2}));
    const fmtN = v => v < 0 ? `(${fmtV(v)})` : (v === 0 ? "—" : fmtV(v));
    const thSt = {padding:"8px 12px",textAlign:"right",fontSize:12,fontWeight:700,color:C.inkMid,borderBottom:`2px solid ${C.accent}`,background:"#f8f5f2"};
    const td   = (v, bold, color) => <td style={{padding:"7px 12px",textAlign:"right",fontFamily:"monospace",fontSize:13,fontWeight:bold?700:400,color:color||(v<0?"#C8401A":C.ink),whiteSpace:"nowrap"}}>{fmtN(v)}</td>;
    const drwSt = {padding:"7px 12px",textAlign:"right",fontFamily:"monospace",fontSize:13,color:"#C8401A",whiteSpace:"nowrap"};
    const sep  = <tr><td colSpan={4}><div style={{borderTop:`1px solid ${C.border}`,margin:"2px 0"}}/></td></tr>;
    const dbl  = <tr><td colSpan={4}><div style={{borderTop:`3px double ${C.ink}`,margin:"2px 0"}}/></td></tr>;

    return (
      <div>
        <p style={{fontSize:11,color:C.inkMid,marginBottom:16}}>
          Statement of Changes in Equity for the year ended {data.meta.period_end} — amounts in ZAR
        </p>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
            <thead>
              <tr>
                <th style={{...thSt,textAlign:"left",width:"40%"}}>Description</th>
                <th style={thSt}>Share Capital</th>
                <th style={thSt}>Retained Earnings</th>
                <th style={{...thSt,color:C.ink}}>Total Equity</th>
              </tr>
            </thead>
            <tbody>
              {/* Opening balance */}
              <tr style={{background:"#fdf9f7"}}>
                <td style={{padding:"7px 12px",fontWeight:700,fontSize:13,color:C.ink}}>Balance at {data.meta.period_start}</td>
                {td(ce.opening_share_capital, true)}
                {td(ce.opening_retained, true)}
                {td(ce.opening_total, true, C.accent)}
              </tr>
              {sep}

              {/* Net profit */}
              <tr>
                <td style={{padding:"7px 12px 7px 24px",color:C.ink}}>Net profit for the year</td>
                <td style={{padding:"7px 12px",textAlign:"right",fontFamily:"monospace",color:C.inkMid}}>—</td>
                {td(ce.net_profit)}
                {td(ce.net_profit)}
              </tr>

              {/* Contributions */}
              {ce.contributions > 0 && <tr>
                <td style={{padding:"7px 12px 7px 24px",color:C.ink}}>Capital contributions by owners</td>
                {td(ce.contributions)}
                <td style={{padding:"7px 12px",textAlign:"right",fontFamily:"monospace",color:C.inkMid}}>—</td>
                {td(ce.contributions)}
              </tr>}

              {/* Drawings */}
              {ce.drawings > 0 && <tr>
                <td style={{padding:"7px 12px 7px 24px",color:C.ink}}>Drawings by owners</td>
                <td style={drwSt}>({fmtV(ce.drawings)})</td>
                <td style={{padding:"7px 12px",textAlign:"right",fontFamily:"monospace",color:C.inkMid}}>—</td>
                <td style={drwSt}>({fmtV(ce.drawings)})</td>
              </tr>}

              {sep}

              {/* Closing balance */}
              <tr style={{background:"#fdf9f7"}}>
                <td style={{padding:"7px 12px",fontWeight:700,fontSize:13,color:C.ink}}>Balance at {data.meta.period_end}</td>
                {td(ce.closing_share_capital, true)}
                {td(ce.closing_retained, true)}
                {td(ce.closing_total, true, C.accent)}
              </tr>
              {dbl}
            </tbody>
          </table>
        </div>

        {/* Reconciliation note */}
        <div style={{marginTop:20,padding:14,background:"#f8f5f2",borderRadius:8,fontSize:12,color:C.inkMid,lineHeight:1.7}}>
          <strong style={{color:C.ink}}>Note on equity components:</strong>{" "}
          Share capital represents the net credit balance on equity accounts (code 3xxx).
          Retained earnings represent the cumulative net profit since inception, not yet distributed.
          Together these equal total equity as reported in the Balance Sheet.
        </div>
      </div>
    );
  }

  function renderNotes() {
    if (!data) return null;
    const n  = data.notes;
    const p  = n.accounting_policies;
    const hr = <div style={{borderTop:`1px solid ${C.border}`,margin:"24px 0"}}/>;
    const NH = ({num, title}) => (
      <p style={{fontSize:14,fontWeight:700,color:C.ink,marginBottom:12,marginTop:0}}>{num}. {title}</p>
    );
    const TH = ({cols}) => (
      <thead><tr style={{background:"#f8f5f2"}}>
        {cols.map(h=><th key={h} style={{padding:"6px 8px",textAlign:"left",color:C.inkMid,fontWeight:600,fontSize:11,borderBottom:`1px solid ${C.border}`,whiteSpace:"nowrap"}}>{h}</th>)}
      </tr></thead>
    );
    const mono = {fontFamily:"monospace",textAlign:"right"};
    const nil  = <span style={{color:C.inkMid,fontStyle:"italic"}}>Nil</span>;

    return (
      <div style={{fontSize:12,lineHeight:1.7}}>

        {/* ── NOTE 1: ACCOUNTING POLICIES ─────────────────────────── */}
        <NH num={1} title="Accounting Policies"/>
        {Object.entries(p).map(([k,v])=>(
          <div key={k} style={{marginBottom:10}}>
            <span style={{fontWeight:600,color:C.ink,textTransform:"capitalize"}}>{k.replace(/_/g," ")}: </span>
            <span style={{color:C.inkMid}}>{v}</span>
          </div>
        ))}

        {hr}

        {/* ── NOTE 2: PROPERTY, PLANT AND EQUIPMENT ───────────────── */}
        <NH num={2} title="Property, Plant and Equipment"/>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
            <TH cols={["No.","Description","Category","Cost","Accum. Dep.","Carrying Value","Method","Life","Status"]}/>
            <tbody>
              {n.fixed_assets.length === 0
                ? <tr><td colSpan={9} style={{padding:16,textAlign:"center",color:C.inkMid}}>No fixed assets registered.</td></tr>
                : n.fixed_assets.map(fa=>(
                  <tr key={fa.number} style={{borderBottom:`1px solid ${C.border}`}}>
                    <td style={{padding:"5px 8px",color:C.inkMid}}>{fa.number}</td>
                    <td style={{padding:"5px 8px",fontWeight:600}}>{fa.name}</td>
                    <td style={{padding:"5px 8px",color:C.inkMid}}>{fa.category}</td>
                    <td style={{padding:"5px 8px",...mono}}>{fmt(fa.cost)}</td>
                    <td style={{padding:"5px 8px",...mono,color:"#C8401A"}}>{fmt(fa.accum_dep)}</td>
                    <td style={{padding:"5px 8px",...mono,fontWeight:700}}>{fmt(fa.carrying)}</td>
                    <td style={{padding:"5px 8px",color:C.inkMid}}>{fa.dep_method}</td>
                    <td style={{padding:"5px 8px",color:C.inkMid,textAlign:"right"}}>{fa.useful_life} mo</td>
                    <td style={{padding:"5px 8px"}}>
                      <span style={{padding:"2px 7px",borderRadius:20,fontSize:10,fontWeight:700,
                        background:fa.status==="active"?"#d4edda":"#f8d7da",
                        color:fa.status==="active"?"#155724":"#721c24"}}>{fa.status}</span>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {hr}

        {/* ── NOTE 3: TRADE RECEIVABLES ───────────────────────────── */}
        <NH num={3} title="Trade and Other Receivables"/>
        <p style={{color:C.inkMid,marginBottom:8}}>
          At the reporting date, {n.receivables_aging.count} invoice(s) remained outstanding totalling {fmt(n.receivables_aging.total)}.
          Aging analysis:
        </p>
        <table style={{borderCollapse:"collapse",fontSize:12,minWidth:340}}>
          <tbody>
            {[
              ["Not yet due (current)", n.receivables_aging.current, false],
              ["1 – 30 days overdue",   n.receivables_aging.days_1_30,  true],
              ["31 – 60 days overdue",  n.receivables_aging.days_31_60, true],
              ["61 – 90 days overdue",  n.receivables_aging.days_61_90, true],
              ["Over 90 days overdue",  n.receivables_aging.days_90plus, true],
            ].map(([label, val, red])=>(
              <tr key={label}>
                <td style={{padding:"4px 8px",color:C.ink}}>{label}</td>
                <td style={{padding:"4px 8px",...mono,color:red&&val>0?"#C8401A":C.ink}}>{val > 0 ? fmt(val) : nil}</td>
              </tr>
            ))}
            <tr style={{borderTop:`2px solid ${C.border}`,fontWeight:700}}>
              <td style={{padding:"5px 8px"}}>Total Trade Receivables</td>
              <td style={{padding:"5px 8px",...mono}}>{fmt(n.receivables_aging.total)}</td>
            </tr>
          </tbody>
        </table>

        {hr}

        {/* ── NOTE 4: REVENUE BY CUSTOMER ─────────────────────────── */}
        <NH num={4} title="Revenue Analysis"/>
        {n.revenue_by_customer.length === 0
          ? <p style={{color:C.inkMid}}>No revenue recorded for this financial year.</p>
          : <>
            <p style={{color:C.inkMid,marginBottom:8}}>Revenue recognised from the following customers during the year:</p>
            <table style={{borderCollapse:"collapse",fontSize:12,minWidth:300}}>
              <TH cols={["Customer","Revenue (ZAR)"]}/>
              <tbody>
                {n.revenue_by_customer.map(r=>(
                  <tr key={r.client} style={{borderBottom:`1px solid ${C.border}`}}>
                    <td style={{padding:"5px 8px"}}>{r.client}</td>
                    <td style={{padding:"5px 8px",...mono}}>{fmt(r.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        }

        {hr}

        {/* ── NOTE 5: EXPENSE ANALYSIS ────────────────────────────── */}
        <NH num={5} title="Operating Expenses by Category"/>
        <p style={{color:C.inkMid,marginBottom:8}}>
          Total expenses for the year (excluding VAT): <strong>{fmt(n.total_expenses_ex_vat)}</strong>
        </p>
        {n.expense_by_category.length === 0
          ? <p style={{color:C.inkMid}}>No expenses recorded for this financial year.</p>
          : <table style={{borderCollapse:"collapse",fontSize:12,minWidth:300}}>
              <TH cols={["Category","Total (excl. VAT)"]}/>
              <tbody>
                {n.expense_by_category.map(e=>(
                  <tr key={e.category} style={{borderBottom:`1px solid ${C.border}`}}>
                    <td style={{padding:"5px 8px"}}>{e.category}</td>
                    <td style={{padding:"5px 8px",...mono}}>{fmt(e.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
        }

        {hr}

        {/* ── NOTE 6: EMPLOYEE BENEFITS ───────────────────────────── */}
        <NH num={6} title="Employee Benefits"/>
        {n.payroll_summary.periods_count === 0
          ? <p style={{color:C.inkMid}}>No payroll runs recorded for this financial year.</p>
          : <>
            <p style={{color:C.inkMid,marginBottom:8}}>
              {n.payroll_summary.headcount} employee(s) at reporting date.
              Payroll processed over {n.payroll_summary.periods_count} period(s) during the year.
            </p>
            <table style={{borderCollapse:"collapse",fontSize:12,minWidth:340}}>
              <tbody>
                {[
                  ["Gross salaries and wages",   n.payroll_summary.gross_pay],
                  ["Less: PAYE withheld",        -n.payroll_summary.paye],
                  ["Less: UIF (employee)",       -n.payroll_summary.uif_employee],
                  ["Net pay to employees",        n.payroll_summary.net_pay],
                  ["Employer contributions — UIF", n.payroll_summary.uif_employer],
                  ["Employer contributions — SDL", n.payroll_summary.sdl],
                  ["Total employment cost",       n.payroll_summary.total_cost],
                ].map(([label, val])=>(
                  <tr key={label} style={{borderBottom:`1px solid ${C.border}`}}>
                    <td style={{padding:"4px 8px",color:C.ink}}>{label}</td>
                    <td style={{padding:"4px 8px",...mono,color:val<0?"#C8401A":C.ink}}>{fmt(Math.abs(val))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        }

        {hr}

        {/* ── NOTE 7: TRADE PAYABLES ──────────────────────────────── */}
        <NH num={7} title="Trade and Other Payables"/>
        <table style={{borderCollapse:"collapse",fontSize:12,minWidth:340}}>
          <tbody>
            <tr>
              <td style={{padding:"4px 8px"}}>Outstanding purchase orders ({n.payables_summary.open_pos_count})</td>
              <td style={{padding:"4px 8px",...mono}}>{n.payables_summary.open_pos > 0 ? fmt(n.payables_summary.open_pos) : nil}</td>
            </tr>
            <tr>
              <td style={{padding:"4px 8px"}}>Unpaid credit expenses</td>
              <td style={{padding:"4px 8px",...mono}}>{n.payables_summary.unpaid_credit_expenses > 0 ? fmt(n.payables_summary.unpaid_credit_expenses) : nil}</td>
            </tr>
            <tr style={{borderTop:`2px solid ${C.border}`,fontWeight:700}}>
              <td style={{padding:"5px 8px"}}>Total Trade Payables</td>
              <td style={{padding:"5px 8px",...mono}}>{fmt(n.payables_summary.total_payables)}</td>
            </tr>
          </tbody>
        </table>

        {hr}

        {/* ── NOTE 8: INVENTORIES ─────────────────────────────────── */}
        <NH num={8} title="Inventories"/>
        {n.inventory.item_count === 0
          ? <p style={{color:C.inkMid}}>No inventory items on hand at the reporting date.</p>
          : <>
            <p style={{color:C.inkMid,marginBottom:8}}>
              Inventories are carried at cost on a FIFO basis.
              Total inventory value at reporting date: <strong>{fmt(n.inventory.total_value)}</strong> ({n.inventory.item_count} SKU(s)).
            </p>
            <table style={{borderCollapse:"collapse",fontSize:11,width:"100%"}}>
              <TH cols={["SKU","Description","Qty","Unit Cost","Total Value"]}/>
              <tbody>
                {n.inventory.items.map(i=>(
                  <tr key={i.sku||i.name} style={{borderBottom:`1px solid ${C.border}`}}>
                    <td style={{padding:"4px 8px",color:C.inkMid}}>{i.sku||"—"}</td>
                    <td style={{padding:"4px 8px"}}>{i.name}</td>
                    <td style={{padding:"4px 8px",textAlign:"right"}}>{i.qty}</td>
                    <td style={{padding:"4px 8px",...mono}}>{fmt(i.unit_cost)}</td>
                    <td style={{padding:"4px 8px",...mono,fontWeight:600}}>{fmt(i.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        }

        {hr}

        {/* ── NOTE 9: TAXATION ────────────────────────────────────── */}
        <NH num={9} title="Taxation"/>
        <table style={{borderCollapse:"collapse",fontSize:12,minWidth:340}}>
          <tbody>
            <tr><td style={{padding:"4px 8px"}}>Profit before tax</td><td style={{padding:"4px 8px",...mono}}>{fmt(n.tax_note.profit_before_tax)}</td></tr>
            <tr><td style={{padding:"4px 8px"}}>Tax at standard rate ({n.tax_note.tax_rate_pct}%)</td><td style={{padding:"4px 8px",...mono}}>{fmt(n.tax_note.current_tax)}</td></tr>
            {n.tax_note.deferred_tax
              ? <tr><td style={{padding:"4px 8px"}}>Deferred tax</td><td style={{padding:"4px 8px",...mono}}>{fmt(n.tax_note.deferred_tax)}</td></tr>
              : <tr><td style={{padding:"4px 8px",color:C.inkMid}}>Deferred tax</td><td style={{padding:"4px 8px",...mono,color:C.inkMid}}>Nil</td></tr>}
            <tr style={{borderTop:`2px solid ${C.border}`,fontWeight:700}}>
              <td style={{padding:"5px 8px"}}>Total income tax expense</td>
              <td style={{padding:"5px 8px",...mono}}>{fmt(n.tax_note.total_tax)}</td>
            </tr>
            <tr><td style={{padding:"4px 8px",color:C.inkMid,fontStyle:"italic"}} colSpan={2}>
              Effective tax rate: {n.tax_note.effective_rate_pct}%
            </td></tr>
          </tbody>
        </table>
        <p style={{color:C.inkMid,marginTop:8}}>
          No formal tax assessment has been issued. These amounts represent management's best estimate
          of the income tax liability for the period. Provisional tax payments should be made in
          accordance with SARS IRP6 deadlines.
        </p>

        {hr}

        {/* ── NOTE 10: EVENTS AFTER REPORTING DATE ────────────────── */}
        <NH num={10} title="Events after the Reporting Date"/>
        <p style={{color:C.inkMid}}>
          The directors are not aware of any material events that occurred after the reporting date
          that require adjustment to or disclosure in these financial statements.
        </p>

        {hr}

        {/* ── NOTE 11: GOING CONCERN ──────────────────────────────── */}
        <NH num={11} title="Going Concern"/>
        <p style={{color:C.inkMid}}>
          The financial statements have been prepared on the going concern basis. The directors have
          considered the financial position of the entity and are satisfied that the entity has
          adequate resources to continue in operational existence for the foreseeable future.
        </p>

      </div>
    );
  }

  const STABS = [
    {id:"is",    label:"Income Statement"},
    {id:"bs",    label:"Balance Sheet"},
    {id:"cf",    label:"Cash Flow Statement"},
    {id:"sce",   label:"Changes in Equity"},
    {id:"notes", label:"Notes"},
  ];

  const meta = data?.meta;

  return (
    <div>
      <style>{`@media print {
        .afs-no-print { display:none!important; }
        .afs-paper { box-shadow:none!important; border:none!important; }
      }`}</style>

      {/* Header row */}
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:24}}>
        <div>
          <h2 style={{fontSize:20,fontWeight:700,color:C.ink,margin:0}}>Annual Financial Statements</h2>
          <p style={{fontSize:12,color:C.inkMid,margin:0}}>IFRS for SMEs — SA Financial Year (1 March – 28/29 February)</p>
        </div>
        <div style={{marginLeft:"auto",display:"flex",gap:10,alignItems:"center"}} className="afs-no-print">
          <select value={year} onChange={e=>setYear(Number(e.target.value))}
            style={{padding:"8px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",background:C.surface,color:C.ink}}>
            {years.map(y=><option key={y} value={y}>FY {y} (1 Mar {y-1} – 28 Feb {y})</option>)}
          </select>
          {data && (
            <button onClick={()=>window.print()}
              style={{padding:"8px 16px",background:C.accent,color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>
              🖨 Print / PDF
            </button>
          )}
        </div>
      </div>

      {loading && <div style={{textAlign:"center",padding:60,color:C.inkMid}}>Loading financial statements…</div>}
      {err    && <div style={{textAlign:"center",padding:40,color:"#C8401A",fontSize:14}}>⚠ {err}</div>}

      {data && (
        <div className="afs-paper" style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,overflow:"hidden"}}>
          {/* Company header */}
          <div style={{padding:"28px 32px",borderBottom:`1px solid ${C.border}`,textAlign:"center"}}>
            <div style={{fontFamily:"serif",fontSize:22,fontWeight:800,color:C.ink,marginBottom:4}}>{meta.company_name}</div>
            {meta.reg_number && <div style={{fontSize:11,color:C.inkMid}}>Registration No: {meta.reg_number}</div>}
            {meta.vat_number && <div style={{fontSize:11,color:C.inkMid}}>VAT No: {meta.vat_number}</div>}
            <div style={{fontSize:12,fontWeight:600,color:C.ink,marginTop:8}}>Financial Statements for the year ended {meta.period_end}</div>
            <div style={{fontSize:11,color:C.inkMid}}>Prepared in accordance with {meta.basis} | Currency: {meta.currency}</div>
          </div>

          {/* Tabs */}
          <div style={{display:"flex",borderBottom:`1px solid ${C.border}`,overflowX:"auto"}} className="afs-no-print">
            {STABS.map(t=>(
              <button key={t.id} onClick={()=>setATab(t.id)} style={{
                flex:1,padding:"12px 8px",border:"none",cursor:"pointer",fontFamily:"inherit",
                fontSize:12,fontWeight:tab===t.id?700:400,
                background:tab===t.id?C.bg:"transparent",
                color:tab===t.id?C.accent:C.inkMid,
                borderBottom:tab===t.id?`2px solid ${C.accent}`:"2px solid transparent",
                whiteSpace:"nowrap",
              }}>{t.label}</button>
            ))}
          </div>

          {/* Statement body */}
          <div style={{padding:"28px 32px"}}>
            {/* Print shows all 4 sections */}
            <div className="afs-no-print">
              {tab==="is"    && renderIS()}
              {tab==="bs"    && renderBS()}
              {tab==="cf"    && renderCF()}
              {tab==="sce"   && renderSCE()}
              {tab==="notes" && renderNotes()}
            </div>
            {/* Print version — all statements */}
            <div style={{display:"none"}} className="afs-print-all">
              <p style={{fontSize:16,fontWeight:700,color:C.ink,marginBottom:16}}>Income Statement</p>
              {renderIS()}
              <div style={{pageBreakBefore:"always",paddingTop:32}}>
                <p style={{fontSize:16,fontWeight:700,color:C.ink,marginBottom:16}}>Balance Sheet</p>
                {renderBS()}
              </div>
              <div style={{pageBreakBefore:"always",paddingTop:32}}>
                <p style={{fontSize:16,fontWeight:700,color:C.ink,marginBottom:16}}>Cash Flow Statement</p>
                {renderCF()}
              </div>
              <div style={{pageBreakBefore:"always",paddingTop:32}}>
                <p style={{fontSize:16,fontWeight:700,color:C.ink,marginBottom:16}}>Statement of Changes in Equity</p>
                {renderSCE()}
              </div>
              <div style={{pageBreakBefore:"always",paddingTop:32}}>
                <p style={{fontSize:16,fontWeight:700,color:C.ink,marginBottom:16}}>Notes to the Financial Statements</p>
                {renderNotes()}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div style={{padding:"16px 32px",borderTop:`1px solid ${C.border}`,fontSize:11,color:C.inkMid,display:"flex",justifyContent:"space-between"}}>
            <span>Generated by ZuZan on {new Date(meta.generated_at).toLocaleDateString("en-ZA")}</span>
            <span>These financial statements have not been independently audited or reviewed.</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ── DATA IMPORT ───────────────────────────────────────────────────────────────
// ── XERO MIGRATION WIZARD ────────────────────────────────────────────────────
function XeroMigrationWizard() {
  const token = localStorage.getItem("zuzan_token");
  const STEPS = [
    { id:"accounts",   icon:"📒", label:"Chart of Accounts",
      endpoint:"/xero/accounts",
      xeroPath: "Accounting → Chart of Accounts → Export",
      hint: "Exports columns: Code, Name, Type, Tax Code, Description",
      required: false,
      tip: "Import your account list first so opening balances can reference the right codes.",
    },
    { id:"balances",   icon:"⚖️", label:"Opening Balances",
      endpoint:"/xero/opening-balances",
      xeroPath: "Accounting → Reports → Trial Balance → Export",
      hint: "Exports columns: Account Code, Account Name, YTD Debit, YTD Credit",
      required: false,
      tip: "Use the Trial Balance as at your migration date. This posts a single opening-balance journal entry.",
    },
    { id:"contacts",   icon:"👥", label:"Customers & Suppliers",
      endpoint:"/xero/contacts",
      xeroPath: "Contacts → All Contacts → Import/Export → Export contacts",
      hint: "Exports: Name, Email, Phone, IsCustomer, IsSupplier, Tax Number, Postal Address",
      required: false,
      tip: "Contacts flagged IsSupplier=TRUE become Suppliers; all others become Customers.",
    },
    { id:"invoices",   icon:"🧾", label:"Invoices & Bills",
      endpoint:"/xero/invoices",
      xeroPath: "Accounting → Overview → Invoices → Export (or Bills → Export)",
      hint: "Exports: ContactName, InvoiceNumber, InvoiceDate, DueDate, Subtotal, TotalTax, Total, Status, Type",
      required: false,
      tip: "ACCREC type = sales invoices. ACCPAY type = supplier bills. Both are imported.",
    },
    { id:"employees",  icon:"🧑‍💼", label:"Employees (optional)",
      endpoint:"/xero/employees",
      xeroPath: "Payroll → Employees → Export employees",
      hint: "Exports: First Name, Last Name, Date of Birth, Email, Start Date, Job Title, Pay Rate",
      required: false,
      tip: "Skip this step if you are not using ZuZan payroll, or if you prefer to capture employees manually.",
    },
  ];

  const [step,    setStep]    = useState(0);   // 0 = welcome, 1-5 = steps, 6 = done
  const [results, setResults] = useState({});   // stepId → {imported,skipped,errors}
  const [files,   setFiles]   = useState({});   // stepId → File
  const [busy,    setBusy]    = useState(false);
  const [err,     setErr]     = useState(null);
  const fileRefs = useRef({});
  const [asAt, setAsAt] = useState(new Date().toISOString().slice(0,10));   // opening balances: date of the trial balance
  const [replaceOb, setReplaceOb] = useState(false);

  const totalSteps = STEPS.length;
  const isWelcome = step === 0;
  const isDone    = step === totalSteps + 1;
  const curStep   = STEPS[step - 1];

  async function doImport() {
    if (!curStep) return;
    const f = files[curStep.id];
    if (!f) { setErr("Please select a file first."); return; }
    setBusy(true); setErr(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await fetch(`${BASE_URL}${curStep.endpoint}${curStep.id === "balances" ? `?as_at=${encodeURIComponent(asAt)}&replace=${replaceOb}` : ""}`, {
        method:"POST",
        headers:{Authorization:"Bearer "+token},
        body: fd,
      });
      const data = await r.json().catch(() => ({}));
      if (r.status === 401) throw new Error("Your session has expired. Please sign in again.");
      if (!r.ok) throw new Error(typeof data.detail === "string" ? data.detail : (Array.isArray(data.detail) ? data.detail.map(d=>d.msg).join("; ") : "Import failed"));
      setResults(prev => ({...prev, [curStep.id]: data}));
    } catch(e) { setErr(e.message); }
    finally    { setBusy(false); }
  }

  function skipStep() { setStep(s => s + 1); }

  function pickFile(e) {
    const f = e.target.files[0];
    if (!f || !curStep) return;
    setFiles(prev => ({...prev, [curStep.id]: f}));
    setErr(null);
  }

  const totalImported = Object.values(results).reduce((s, r) => s + (r.imported || 0), 0);

  // ── Welcome screen ────────────────────────────────────────────────────────
  if (isWelcome) return (
    <div style={{maxWidth:640,width:"100%",boxSizing:"border-box",margin:"0 auto"}}>
      <div style={{textAlign:"center",padding:"40px 0 32px"}}>
        <div style={{fontSize:52,marginBottom:12}}>🔀</div>
        <h2 style={{fontFamily:"serif",fontSize:26,fontWeight:800,color:C.ink,margin:"0 0 10px"}}>
          Migrate from Xero
        </h2>
        <p style={{fontSize:14,color:C.inkMid,lineHeight:1.7,maxWidth:480,margin:"0 auto 28px"}}>
          This wizard imports your Xero data step by step — accounts, opening balances,
          contacts, invoices, and employees. Each step is independent; skip any you don't need.
        </p>
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:14,padding:24,textAlign:"left",marginBottom:28}}>
          <div style={{fontWeight:700,fontSize:13,color:C.ink,marginBottom:14}}>Before you start, export these files from Xero:</div>
          {STEPS.map((s,i) => (
            <div key={s.id} style={{display:"flex",gap:12,marginBottom:10,alignItems:"flex-start"}}>
              <span style={{fontSize:20,lineHeight:1.3,flexShrink:0}}>{s.icon}</span>
              <div>
                <div style={{fontWeight:600,fontSize:13,color:C.ink}}>{s.label}</div>
                <div style={{fontSize:11,color:C.inkMid,marginTop:2}}>{s.xeroPath}</div>
              </div>
            </div>
          ))}
        </div>
        <button onClick={()=>setStep(1)} style={{
          background:C.accent,color:"#fff",border:"none",borderRadius:12,
          padding:"14px 36px",fontSize:15,fontWeight:700,cursor:"pointer",fontFamily:"inherit",
        }}>Start Migration →</button>
      </div>
    </div>
  );

  // ── Done screen ───────────────────────────────────────────────────────────
  if (isDone) return (
    <div style={{maxWidth:560,margin:"0 auto",textAlign:"center",padding:"40px 0"}}>
      <div style={{fontSize:52,marginBottom:12}}>🎉</div>
      <h2 style={{fontFamily:"serif",fontSize:24,fontWeight:800,color:C.ink,margin:"0 0 8px"}}>Migration Complete</h2>
      <p style={{fontSize:14,color:C.inkMid,marginBottom:28}}>{totalImported} records imported across all steps.</p>
      <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:14,padding:20,marginBottom:28,textAlign:"left"}}>
        {STEPS.map(s => {
          const r = results[s.id];
          if (!r) return (
            <div key={s.id} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:`1px solid ${C.border}`,fontSize:13}}>
              <span style={{color:C.inkMid}}>{s.icon} {s.label}</span>
              <span style={{color:C.inkDim}}>Skipped</span>
            </div>
          );
          return (
            <div key={s.id} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:`1px solid ${C.border}`,fontSize:13}}>
              <span style={{color:C.ink,fontWeight:600}}>{s.icon} {s.label}</span>
              <span style={{color:"#2e7d32",fontWeight:700}}>✓ {r.imported} imported{r.skipped ? ` · ${r.skipped} skipped` : ""}</span>
            </div>
          );
        })}
      </div>
      <p style={{fontSize:12,color:C.inkMid,marginBottom:20}}>Check your Dashboard, Invoicing and Contacts screens to verify the data.</p>
      <button onClick={()=>{setStep(0);setResults({});setFiles({});setErr(null);}} style={{
        background:"transparent",border:`1px solid ${C.border}`,borderRadius:10,
        padding:"10px 24px",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"inherit",color:C.ink,
      }}>Run Again</button>
    </div>
  );

  // ── Step screen ───────────────────────────────────────────────────────────
  const stepFile = files[curStep.id];
  const stepResult = results[curStep.id];

  return (
    <div style={{maxWidth:620,margin:"0 auto"}}>
      {/* Progress bar */}
      <div style={{marginBottom:28}}>
        <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
          {STEPS.map((s,i) => (
            <div key={s.id} onClick={()=>setStep(i+1)} style={{
              display:"flex",flexDirection:"column",alignItems:"center",gap:4,cursor:"pointer",flex:1,
            }}>
              <div style={{
                width:32,height:32,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",
                fontSize:13,fontWeight:700,
                background: step > i+1 ? "#2e7d32" : step === i+1 ? C.accent : C.border,
                color: step >= i+1 ? "#fff" : C.inkMid,
                transition:"background 0.2s",
              }}>{step > i+1 ? "✓" : i+1}</div>
              <div style={{fontSize:9,color:step===i+1?C.accent:C.inkMid,fontWeight:step===i+1?700:400,textAlign:"center",lineHeight:1.3,maxWidth:70}}>{s.label}</div>
            </div>
          ))}
        </div>
        <div style={{height:3,background:C.border,borderRadius:2}}>
          <div style={{height:"100%",background:C.accent,borderRadius:2,transition:"width 0.3s",width:`${((step-1)/totalSteps)*100}%`}}/>
        </div>
      </div>

      {/* Step card */}
      <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,padding:28,marginBottom:16}}>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:18}}>
          <span style={{fontSize:28}}>{curStep.icon}</span>
          <div>
            <div style={{fontSize:17,fontWeight:700,color:C.ink}}>{curStep.label}</div>
            <div style={{fontSize:11,color:C.inkMid,marginTop:2}}>Step {step} of {totalSteps}</div>
          </div>
        </div>

        {/* Xero export instructions */}
        <div style={{background:"#f0f4ff",border:"1px solid #c7d4f5",borderRadius:10,padding:"12px 16px",marginBottom:18}}>
          <div style={{fontSize:11,fontWeight:700,color:"#3b4dc8",marginBottom:4}}>📤 How to export from Xero</div>
          <div style={{fontSize:12,color:"#3b4dc8",marginBottom:4}}><strong>Path:</strong> {curStep.xeroPath}</div>
          <div style={{fontSize:11,color:"#5a6bc4",lineHeight:1.5}}>{curStep.hint}</div>
        </div>

        {/* Tip */}
        <div style={{fontSize:12,color:C.inkMid,lineHeight:1.6,marginBottom:20,padding:"10px 14px",background:C.bg,borderRadius:8,border:`1px solid ${C.border}`}}>
          💡 {curStep.tip}
        </div>

        {curStep.id === "balances" && (
          <div style={{marginBottom:16,padding:"12px 14px",background:C.bg,borderRadius:8,fontSize:12,color:C.inkMid}}>
            <label style={{display:"block",marginBottom:8}}>Trial balance date (as at)<br/>
              <input type="date" value={asAt} onChange={e=>setAsAt(e.target.value)} style={{marginTop:4,padding:"8px 10px",fontSize:16,border:`1px solid ${C.border}`,borderRadius:8,fontFamily:"inherit"}}/>
            </label>
            <label style={{display:"flex",gap:8,alignItems:"flex-start",lineHeight:1.5}}>
              <input type="checkbox" checked={replaceOb} onChange={e=>setReplaceOb(e.target.checked)} style={{marginTop:3}}/>
              <span>Replace opening balances I already imported (tick this if you are re-importing a corrected file)</span>
            </label>
          </div>
        )}

        {/* File upload zone */}
        <div
          onClick={()=>{ if(fileRefs.current[curStep.id]) fileRefs.current[curStep.id].click(); }}
          style={{
            border:`2px dashed ${stepFile ? C.accent : C.border}`,
            borderRadius:12,padding:"28px 20px",textAlign:"center",cursor:"pointer",
            background: stepFile ? C.accentLt : C.bg,
            transition:"all 0.15s",marginBottom:4,
          }}
        >
          <input
            type="file" accept=".csv,.xlsx,.xls" style={{display:"none"}}
            ref={el=>{ if(el) fileRefs.current[curStep.id]=el; }}
            onChange={pickFile}
          />
          {stepFile ? (
            <>
              <div style={{fontSize:22,marginBottom:6}}>📄</div>
              <div style={{fontSize:13,fontWeight:600,color:C.accent}}>{stepFile.name}</div>
              <div style={{fontSize:11,color:C.inkMid,marginTop:4}}>Click to change file</div>
            </>
          ) : (
            <>
              <div style={{fontSize:22,marginBottom:6}}>⬆️</div>
              <div style={{fontSize:13,fontWeight:600,color:C.ink}}>Click to select CSV or Excel file</div>
              <div style={{fontSize:11,color:C.inkMid,marginTop:4}}>Xero CSV export accepted directly</div>
            </>
          )}
        </div>

        {err && <div style={{marginTop:10,padding:"10px 14px",background:"#fff5f5",border:"1px solid #fca5a5",borderRadius:8,fontSize:12,color:"#b91c1c"}}>{err}</div>}

        {stepResult && (
          <div style={{marginTop:12,padding:"12px 16px",background:"#f0fdf4",border:"1px solid #86efac",borderRadius:8,fontSize:12,color:"#166534"}}>
            ✓ <strong>{stepResult.imported}</strong> records imported
            {stepResult.skipped > 0 && <span> · {stepResult.skipped} skipped (already exist)</span>}
            {(stepResult.errors||[]).length > 0 && (
              <ul style={{margin:"6px 0 0 16px",paddingLeft:0}}>
                {stepResult.errors.slice(0,5).map((w,i)=><li key={i} style={{color:"#b91c1c"}}>{w}</li>)}
                {stepResult.errors.length > 5 && <li style={{color:"#b91c1c"}}>…and {stepResult.errors.length - 5} more</li>}
              </ul>
            )}
            {(stepResult.warnings||[]).length > 0 && (
              <ul style={{margin:"6px 0 0 16px",paddingLeft:0}}>
                {stepResult.warnings.slice(0,5).map((w,i)=><li key={i} style={{color:"#92400e"}}>{w}</li>)}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <button onClick={()=>setStep(s=>Math.max(0,s-1))} style={{
          background:"transparent",border:`1px solid ${C.border}`,borderRadius:10,
          padding:"10px 20px",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"inherit",color:C.inkMid,
        }}>← Back</button>
        <div style={{display:"flex",gap:10}}>
          <button onClick={skipStep} style={{
            background:"transparent",border:`1px solid ${C.border}`,borderRadius:10,
            padding:"10px 20px",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"inherit",color:C.inkMid,
          }}>Skip</button>
          <button onClick={stepResult ? skipStep : doImport} disabled={busy || (!stepResult && !stepFile)} style={{
            background: (!stepFile||busy) ? C.border : C.accent,
            color:"#fff",border:"none",borderRadius:10,
            padding:"10px 28px",fontSize:13,fontWeight:700,cursor:(!stepFile||busy)?"not-allowed":"pointer",
            fontFamily:"inherit",opacity:(!stepFile||busy)?0.6:1,
          }}>{busy ? "Importing…" : stepResult ? "Next →" : "Import & Next →"}</button>
        </div>
      </div>
    </div>
  );
}

// ── QUICKBOOKS MIGRATION WIZARD ──────────────────────────────────────────────
function QuickBooksMigrationWizard() {
  const token = localStorage.getItem("zuzan_token");
  const STEPS = [
    { id:"accounts",   icon:"📒", label:"Chart of Accounts",
      endpoint:"/qbo/accounts",
      qboPath: "Accounting → Chart of Accounts → Run Report → Export",
      hint: "Columns: Account, Type, Detail Type, Description, Balance",
      required: false,
      tip: "Import your account list first so opening balances can reference the right codes.",
    },
    { id:"balances",   icon:"⚖️", label:"Opening Balances",
      endpoint:"/qbo/opening-balances",
      qboPath: "Reports → Trial Balance → set date → Export",
      hint: "Columns: Account, Debit, Credit",
      required: false,
      tip: "Run the Trial Balance as at your migration date. This posts a single opening-balance journal entry.",
    },
    { id:"customers",  icon:"👤", label:"Customers",
      endpoint:"/qbo/customers",
      qboPath: "Sales → Customers → Export → Customer Contact List",
      hint: "Columns: Customer, Company, Email, Phone, Street, City, State, ZIP, Country, Tax Reg. No.",
      required: false,
      tip: "QBO exports customers and vendors separately. Import customers first, then vendors.",
    },
    { id:"vendors",    icon:"🏭", label:"Vendors / Suppliers",
      endpoint:"/qbo/vendors",
      qboPath: "Expenses → Vendors → Export → Vendor Contact List",
      hint: "Columns: Vendor, Company, Email, Phone, Street, City, State, ZIP, Country, Tax Reg. No.",
      required: false,
      tip: "Vendors are imported as ZuZan Suppliers.",
    },
    { id:"invoices",   icon:"🧾", label:"Invoices & Bills",
      endpoint:"/qbo/invoices",
      qboPath: "Sales → Invoices → Export  (repeat for Expenses → Bills → Export)",
      hint: "Columns: Invoice No., Customer, Invoice Date, Due Date, Terms, Status, Amount, Balance Due",
      required: false,
      tip: "Run exports for both Invoices (Sales) and Bills (Expenses). Upload each file separately — the same endpoint handles both.",
    },
    { id:"employees",  icon:"🧑‍💼", label:"Employees (optional)",
      endpoint:"/qbo/employees",
      qboPath: "Payroll → Employees → Export → Employee Contact List",
      hint: "Columns: Employee, Email, Phone, Mobile, Street, City, State, Hire Date, Status, Employee Type, Billing Rate",
      required: false,
      tip: "Skip this step if you are not using ZuZan payroll, or if you prefer to capture employees manually.",
    },
  ];

  const [step,    setStep]    = useState(0);
  const [results, setResults] = useState({});
  const [files,   setFiles]   = useState({});
  const [busy,    setBusy]    = useState(false);
  const [err,     setErr]     = useState(null);
  const fileRefs = useRef({});
  const [asAt, setAsAt] = useState(new Date().toISOString().slice(0,10));   // opening balances: date of the trial balance
  const [replaceOb, setReplaceOb] = useState(false);

  const totalSteps = STEPS.length;
  const isWelcome = step === 0;
  const isDone    = step === totalSteps + 1;
  const curStep   = STEPS[step - 1];

  async function doImport() {
    if (!curStep) return;
    const f = files[curStep.id];
    if (!f) { setErr("Please select a file first."); return; }
    setBusy(true); setErr(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await fetch(`${BASE_URL}${curStep.endpoint}${curStep.id === "balances" ? `?as_at=${encodeURIComponent(asAt)}&replace=${replaceOb}` : ""}`, {
        method:"POST",
        headers:{Authorization:"Bearer "+token},
        body: fd,
      });
      const data = await r.json().catch(() => ({}));
      if (r.status === 401) throw new Error("Your session has expired. Please sign in again.");
      if (!r.ok) throw new Error(typeof data.detail === "string" ? data.detail : (Array.isArray(data.detail) ? data.detail.map(d=>d.msg).join("; ") : "Import failed"));
      setResults(prev => ({...prev, [curStep.id]: data}));
    } catch(e) { setErr(e.message); }
    finally    { setBusy(false); }
  }

  function skipStep() { setStep(s => s + 1); }

  function pickFile(e) {
    const f = e.target.files[0];
    if (!f || !curStep) return;
    setFiles(prev => ({...prev, [curStep.id]: f}));
    setErr(null);
  }

  const totalImported = Object.values(results).reduce((s, r) => s + (r.imported || 0), 0);

  // ── Welcome screen ────────────────────────────────────────────────────────
  if (isWelcome) return (
    <div style={{maxWidth:640,width:"100%",boxSizing:"border-box",margin:"0 auto"}}>
      <div style={{textAlign:"center",padding:"40px 0 32px"}}>
        <div style={{fontSize:52,marginBottom:12}}>💚</div>
        <h2 style={{fontFamily:"serif",fontSize:26,fontWeight:800,color:C.ink,margin:"0 0 10px"}}>
          Migrate from QuickBooks
        </h2>
        <p style={{fontSize:14,color:C.inkMid,lineHeight:1.7,maxWidth:480,margin:"0 auto 28px"}}>
          This wizard imports your QuickBooks Online data step by step — accounts, opening balances,
          customers, vendors, invoices and employees. Each step is independent; skip any you don't need.
        </p>
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:14,padding:24,textAlign:"left",marginBottom:28}}>
          <div style={{fontWeight:700,fontSize:13,color:C.ink,marginBottom:14}}>Before you start, export these files from QuickBooks Online:</div>
          {STEPS.map((s,i) => (
            <div key={s.id} style={{display:"flex",gap:12,marginBottom:10,alignItems:"flex-start"}}>
              <span style={{fontSize:20,lineHeight:1.3,flexShrink:0}}>{s.icon}</span>
              <div>
                <div style={{fontWeight:600,fontSize:13,color:C.ink}}>{s.label}</div>
                <div style={{fontSize:11,color:C.inkMid,marginTop:2}}>{s.qboPath}</div>
              </div>
            </div>
          ))}
        </div>
        <button onClick={()=>setStep(1)} style={{
          background:"#2ca01c",color:"#fff",border:"none",borderRadius:12,
          padding:"14px 36px",fontSize:15,fontWeight:700,cursor:"pointer",fontFamily:"inherit",
        }}>Start Migration →</button>
      </div>
    </div>
  );

  // ── Done screen ───────────────────────────────────────────────────────────
  if (isDone) return (
    <div style={{maxWidth:560,margin:"0 auto",textAlign:"center",padding:"40px 0"}}>
      <div style={{fontSize:52,marginBottom:12}}>🎉</div>
      <h2 style={{fontFamily:"serif",fontSize:24,fontWeight:800,color:C.ink,margin:"0 0 8px"}}>Migration Complete</h2>
      <p style={{fontSize:14,color:C.inkMid,marginBottom:28}}>{totalImported} records imported across all steps.</p>
      <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:14,padding:20,marginBottom:28,textAlign:"left"}}>
        {STEPS.map(s => {
          const r = results[s.id];
          if (!r) return (
            <div key={s.id} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:`1px solid ${C.border}`,fontSize:13}}>
              <span style={{color:C.inkMid}}>{s.icon} {s.label}</span>
              <span style={{color:C.inkDim}}>Skipped</span>
            </div>
          );
          return (
            <div key={s.id} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:`1px solid ${C.border}`,fontSize:13}}>
              <span style={{color:C.ink,fontWeight:600}}>{s.icon} {s.label}</span>
              <span style={{color:"#2e7d32",fontWeight:700}}>✓ {r.imported} imported{r.skipped ? ` · ${r.skipped} skipped` : ""}</span>
            </div>
          );
        })}
      </div>
      <p style={{fontSize:12,color:C.inkMid,marginBottom:20}}>Check your Dashboard, Invoicing and Contacts screens to verify the data.</p>
      <button onClick={()=>{setStep(0);setResults({});setFiles({});setErr(null);}} style={{
        background:"transparent",border:`1px solid ${C.border}`,borderRadius:10,
        padding:"10px 24px",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"inherit",color:C.ink,
      }}>Run Again</button>
    </div>
  );

  // ── Step screen ───────────────────────────────────────────────────────────
  const stepFile   = files[curStep.id];
  const stepResult = results[curStep.id];

  return (
    <div style={{maxWidth:620,margin:"0 auto"}}>
      {/* Progress bar */}
      <div style={{marginBottom:28}}>
        <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
          {STEPS.map((s,i) => (
            <div key={s.id} onClick={()=>setStep(i+1)} style={{
              display:"flex",flexDirection:"column",alignItems:"center",gap:4,cursor:"pointer",flex:1,
            }}>
              <div style={{
                width:32,height:32,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",
                fontSize:13,fontWeight:700,
                background: step > i+1 ? "#2e7d32" : step === i+1 ? "#2ca01c" : C.border,
                color: step >= i+1 ? "#fff" : C.inkMid,
                transition:"background 0.2s",
              }}>{step > i+1 ? "✓" : i+1}</div>
              <div style={{fontSize:9,color:step===i+1?"#2ca01c":C.inkMid,fontWeight:step===i+1?700:400,textAlign:"center",lineHeight:1.3,maxWidth:70}}>{s.label}</div>
            </div>
          ))}
        </div>
        <div style={{height:3,background:C.border,borderRadius:2}}>
          <div style={{height:"100%",background:"#2ca01c",borderRadius:2,transition:"width 0.3s",width:`${((step-1)/totalSteps)*100}%`}}/>
        </div>
      </div>

      {/* Step card */}
      <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,padding:28,marginBottom:16}}>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:18}}>
          <span style={{fontSize:28}}>{curStep.icon}</span>
          <div>
            <div style={{fontSize:17,fontWeight:700,color:C.ink}}>{curStep.label}</div>
            <div style={{fontSize:11,color:C.inkMid,marginTop:2}}>Step {step} of {totalSteps}</div>
          </div>
        </div>

        {/* QBO export instructions */}
        <div style={{background:"#f0fdf4",border:"1px solid #86efac",borderRadius:10,padding:"12px 16px",marginBottom:18}}>
          <div style={{fontSize:11,fontWeight:700,color:"#166534",marginBottom:4}}>📤 How to export from QuickBooks Online</div>
          <div style={{fontSize:12,color:"#166534",marginBottom:4}}><strong>Path:</strong> {curStep.qboPath}</div>
          <div style={{fontSize:11,color:"#15803d",lineHeight:1.5}}>{curStep.hint}</div>
        </div>

        {/* Tip */}
        <div style={{fontSize:12,color:C.inkMid,lineHeight:1.6,marginBottom:20,padding:"10px 14px",background:C.bg,borderRadius:8,border:`1px solid ${C.border}`}}>
          💡 {curStep.tip}
        </div>

        {curStep.id === "balances" && (
          <div style={{marginBottom:16,padding:"12px 14px",background:C.bg,borderRadius:8,fontSize:12,color:C.inkMid}}>
            <label style={{display:"block",marginBottom:8}}>Trial balance date (as at)<br/>
              <input type="date" value={asAt} onChange={e=>setAsAt(e.target.value)} style={{marginTop:4,padding:"8px 10px",fontSize:16,border:`1px solid ${C.border}`,borderRadius:8,fontFamily:"inherit"}}/>
            </label>
            <label style={{display:"flex",gap:8,alignItems:"flex-start",lineHeight:1.5}}>
              <input type="checkbox" checked={replaceOb} onChange={e=>setReplaceOb(e.target.checked)} style={{marginTop:3}}/>
              <span>Replace opening balances I already imported (tick this if you are re-importing a corrected file)</span>
            </label>
          </div>
        )}

        {/* File upload zone */}
        <div
          onClick={()=>{ if(fileRefs.current[curStep.id]) fileRefs.current[curStep.id].click(); }}
          style={{
            border:`2px dashed ${stepFile ? "#2ca01c" : C.border}`,
            borderRadius:12,padding:"28px 20px",textAlign:"center",cursor:"pointer",
            background: stepFile ? "#f0fdf4" : C.bg,
            transition:"all 0.15s",marginBottom:4,
          }}
        >
          <input
            type="file" accept=".csv,.xlsx,.xls" style={{display:"none"}}
            ref={el=>{ if(el) fileRefs.current[curStep.id]=el; }}
            onChange={pickFile}
          />
          {stepFile ? (
            <>
              <div style={{fontSize:22,marginBottom:6}}>📄</div>
              <div style={{fontSize:13,fontWeight:600,color:"#2ca01c"}}>{stepFile.name}</div>
              <div style={{fontSize:11,color:C.inkMid,marginTop:4}}>Click to change file</div>
            </>
          ) : (
            <>
              <div style={{fontSize:22,marginBottom:6}}>⬆️</div>
              <div style={{fontSize:13,fontWeight:600,color:C.ink}}>Click to select CSV or Excel file</div>
              <div style={{fontSize:11,color:C.inkMid,marginTop:4}}>QuickBooks Online CSV export accepted directly</div>
            </>
          )}
        </div>

        {err && <div style={{marginTop:10,padding:"10px 14px",background:"#fff5f5",border:"1px solid #fca5a5",borderRadius:8,fontSize:12,color:"#b91c1c"}}>{err}</div>}

        {stepResult && (
          <div style={{marginTop:12,padding:"12px 16px",background:"#f0fdf4",border:"1px solid #86efac",borderRadius:8,fontSize:12,color:"#166534"}}>
            ✓ <strong>{stepResult.imported}</strong> records imported
            {stepResult.skipped > 0 && <span> · {stepResult.skipped} skipped (already exist)</span>}
            {(stepResult.errors||[]).length > 0 && (
              <ul style={{margin:"6px 0 0 16px",paddingLeft:0}}>
                {stepResult.errors.slice(0,5).map((w,i)=><li key={i} style={{color:"#b91c1c"}}>{w}</li>)}
                {stepResult.errors.length > 5 && <li style={{color:"#b91c1c"}}>…and {stepResult.errors.length - 5} more</li>}
              </ul>
            )}
            {(stepResult.warnings||[]).length > 0 && (
              <ul style={{margin:"6px 0 0 16px",paddingLeft:0}}>
                {stepResult.warnings.slice(0,5).map((w,i)=><li key={i} style={{color:"#92400e"}}>{w}</li>)}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <button onClick={()=>setStep(s=>Math.max(0,s-1))} style={{
          background:"transparent",border:`1px solid ${C.border}`,borderRadius:10,
          padding:"10px 20px",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"inherit",color:C.inkMid,
        }}>← Back</button>
        <div style={{display:"flex",gap:10}}>
          <button onClick={skipStep} style={{
            background:"transparent",border:`1px solid ${C.border}`,borderRadius:10,
            padding:"10px 20px",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"inherit",color:C.inkMid,
          }}>Skip</button>
          <button onClick={stepResult ? skipStep : doImport} disabled={busy || (!stepResult && !stepFile)} style={{
            background: (!stepFile||busy) ? C.border : "#2ca01c",
            color:"#fff",border:"none",borderRadius:10,
            padding:"10px 28px",fontSize:13,fontWeight:700,cursor:(!stepFile||busy)?"not-allowed":"pointer",
            fontFamily:"inherit",opacity:(!stepFile||busy)?0.6:1,
          }}>{busy ? "Importing…" : stepResult ? "Next →" : "Import & Next →"}</button>
        </div>
      </div>
    </div>
  );
}

function DataImport() {
  const isMobile = useIsMobile();
  const token = localStorage.getItem("zuzan_token");
  const [tab,       setTab]       = useState("customers");
  const [file,      setFile]      = useState(null);
  const [preview,     setPreview]     = useState(null);   // {headers, rows}
  const [result,      setResult]      = useState(null);   // {imported, skipped, errors, total_rows}
  const [importing,   setImporting]   = useState(false);
  const [err,         setErr]         = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const fileRef = useRef(null);
  const [asAt, setAsAt] = useState(new Date().toISOString().slice(0,10));   // statements: date the balances are as at
  const [replaceImp, setReplaceImp] = useState(false);
  const HAS_ASAT = ["trial_balance","balance_sheet","profit_loss"].includes(tab);

  const ITABS = [
    {id:"xero_wizard",        label:"Migrate from Xero",  icon:"🔀"},
    {id:"qbo_wizard",         label:"Migrate from QuickBooks", icon:"💚"},
    {id:"customers",          label:"Customers",          icon:"👥"},
    {id:"suppliers",          label:"Suppliers",          icon:"🏭"},
    {id:"invoices",           label:"Invoices",           icon:"🧾"},
    {id:"expenses",           label:"Expenses",           icon:"💳"},
    {id:"employees",          label:"Employees",          icon:"🧑‍💼"},
    {id:"payroll_adjustments",label:"Payroll Adjustments",icon:"💰"},
    {id:"trial_balance",      label:"Trial Balance",      icon:"⚖️"},
    {id:"balance_sheet",      label:"Balance Sheet",      icon:"📊"},
    {id:"profit_loss",        label:"Income Statement",   icon:"📈"},
    {id:"general_ledger",     label:"General Ledger",     icon:"📒"},
    {id:"journals",           label:"Journals",           icon:"📓"},
  ];

  // CSV templates — column headers users should use (or what Xero/QBO exports)
  const TEMPLATES = {
    customers:          ["Name","Email","Phone","Address","VAT Number","Payment Terms","Notes"],
    suppliers:          ["Name","Email","Phone","Address","VAT Number","Bank Name","Account Number","Branch Code","Account Type","Payment Terms","Notes"],
    invoices:           ["Invoice Number","Client Name","Client Email","Description","Amount","VAT Amount","Total Amount","Currency","Issue Date","Due Date","Status"],
    expenses:           ["Vendor","Description","Amount","VAT Amount","Category","Expense Date"],
    employees:          ["Employee Number","First Name","Last Name","ID Number","Tax Number","Date of Birth","Appointment Date","Start Date","Address","Position","Department","Grade","Employment Type","Employment Category","Gross Salary","Hourly Rate","Bank Name","Account Number","Branch Code","Account Type","Pension Employee Pct","Pension Employer Pct","Pension Employee Fixed","Pension Employer Fixed","Medical Aid Employee","Medical Aid Employer","Medical Aid Dependants","Union Subscription","Advance Monthly Deduction","On Maternity Leave","Security Grade","Security Area","PSIRA Number","Shift Type","Pay Schedule","Special Allowance Type","PSSPF Exempt"],
    payroll_adjustments:["ID Number","Employee Number","First Name","Last Name","Gross Salary","Pension Employee Pct","Pension Employer Pct","Medical Aid Employee","Medical Aid Employer","Medical Aid Dependants","Position","Department","Grade"],
    trial_balance:      ["Code","Account Name","Type","Debit","Credit"],
    balance_sheet:      ["Classification","Code","Account Name","Balance"],
    profit_loss:        ["Type","Code","Account Name","Amount"],
    general_ledger:     ["Date","Reference","Description","Account Code","Account Name","Debit","Credit"],
    journals:           ["Date","Journal No","Narration","Account Code","Account Name","Debit","Credit"],
  };

  const REQUIRED = {
    customers:          "Name*",
    suppliers:          "Name*",
    invoices:           "Client Name*",
    expenses:           "Amount*",
    employees:          "First Name*, Last Name*, Gross Salary*",
    payroll_adjustments:"ID Number* or Employee Number* or First+Last Name*",
    trial_balance:      "Account Name*",
    balance_sheet:      "Account Name*",
    profit_loss:        "Account Name*",
    general_ledger:     "Debit* or Credit*",
    journals:           "Debit* or Credit*",
  };

  // Xero / QBO aliases shown as hints
  const HINTS = {
    customers:          "Xero: Contact Name · QBO: Customer / Display Name",
    suppliers:          "Xero: Contact Name · QBO: Vendor",
    invoices:           "Xero: InvoiceNo, ContactName, InvoiceDate, Subtotal, TotalTax, Total, Status · QBO: Num, Customer, Date",
    expenses:           "Xero: Contact Name, Description, Subtotal, TotalTax, Date, Account · QBO: Vendor, Memo, Amount, Date",
    employees:          "Required: First Name, Last Name, Gross Salary. Existing employees are UPDATED (matched by Employee Number, then ID Number, then First+Last Name) — new rows are created. Dates as YYYY-MM-DD. Employment Type: salaried or hourly. Employment Category: permanent/fixed_term/temp_labour/learnership. Account Type: Cheque/Savings/Transmission. Security officers: Security Grade A-E and Security Area (1_2 = Areas 1 & 2, 3 = Area 3) are both required - officers are placed on the security pay schedule automatically. Special Allowance Type: none, armed, armed_response, nkp, control_centre, canine or mobile_supervisor. Blank cells never overwrite existing values. On Maternity Leave: true/false. PSSPF Exempt: yes only if the officer is not a PSSPF member (pension columns are ignored for security officers). Only columns present in your file are applied — leave columns out to keep existing values unchanged.",
    payroll_adjustments:"Match employees by ID Number, Employee Number, or First+Last Name. Only columns present in the CSV are updated — partial CSVs are fine.",
    trial_balance:      "Xero: Account Code, Account, Debit, Credit · QBO: Account, Type, Debit Balance, Credit Balance",
    balance_sheet:      "Use Classification column (Asset/Liability/Equity) + Account Name + Balance. Xero/QBO balance sheet exports work directly.",
    profit_loss:        "Use Type column (Revenue/Expense) + Account Name + Amount. Xero/QBO P&L exports work directly.",
    general_ledger:     "Xero: Date, SourceNo, Account Code, Account, Description, Debit, Credit · QBO: Date, Ref No, Account, Description, Debit, Credit",
    journals:           "Xero: Date, Journal No, Narration, Account Code, Account, Debit, Credit · QBO: Date, Journal No, Description, Account, Debit, Credit",
  };

  function switchTab(id) {
    setTab(id); setFile(null); setPreview(null); setResult(null); setErr(null);
    if (fileRef.current) fileRef.current.value = "";
  }

  const SAMPLE_ROWS = {
    employees: ["EMP001","Jane","Smith","8001015009087","9012345678","1980-01-01","2023-03-01","2023-03-01","123 Main St","Bookkeeper","Finance","B1","salaried","permanent","25000","","FNB","62012345678","250655","Cheque","0.05","0.05","0","0","1500","1500","2","0","0","false","","","","day","salaried","none"],
    customers: ["ABC Trading (Pty) Ltd","accounts@abc.co.za","011 000 0000","123 Main St, JHB","4670123456","30","VIP client"],
    suppliers: ["Office World","ap@officeworld.co.za","010 000 0001","45 Park Rd, CPT","4670098765","ABSA","4098765432","632005","Cheque","30","Stationery supplier"],
    invoices:  ["INV-001","Motswedi Motors","accounts@motswedi.co.za","Consulting services","8695.65","1304.35","10000","ZAR","2026-09-01","2026-09-30","sent"],
    expenses:  ["Checkers","Office groceries","230","0","General Expenses","2026-09-05"],
    payroll_adjustments: ["8001015009087","EMP001","Jane","Smith","27000","0.05","0.05","1600","1600","2","Senior Bookkeeper","Finance","B2"],
  };

  function downloadTemplate() {
    const cols   = TEMPLATES[tab];
    const sample = SAMPLE_ROWS[tab];

    // Employees: use XLSX for reliability (35 columns, typed data)
    if (tab === "employees") {
      const dataRow = {};
      cols.forEach((col, i) => { dataRow[col] = sample ? (sample[i] ?? "") : ""; });
      const ws = XLSX.utils.json_to_sheet(cols.length ? [dataRow] : [{}], {header: cols});
      ws["!cols"] = cols.map(() => ({wch: 18}));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Employees");
      XLSX.writeFile(wb, "zuzan_employees_import_template.xlsx");
      return;
    }

    // All other tabs: CSV
    const rows    = [cols.join(",")];
    if (sample) rows.push(sample.map(v => String(v).includes(",") ? `"${v}"` : v).join(","));
    const content = rows.join("\n") + "\n";
    const blob    = new Blob([content], {type:"text/csv"});
    const url     = URL.createObjectURL(blob);
    const a       = document.createElement("a");
    a.href = url; a.download = `zuzan_${tab}_import_template.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Auto-detect the correct import tab from CSV column headers
  function detectTab(headers) {
    const norm = headers.map(h => h.trim().toLowerCase().replace(/[\s_-]+/g, ""));
    const has  = (...keys) => keys.some(k => norm.includes(k));
    if (has("firstname","first_name","firstname"))            return "employees";
    if (has("invoicenumber","invoiceno","inv_no"))            return "invoices";
    if (has("expensedate","expense_date"))                    return "expenses";
    if (has("idnumber","id_number","employeenumber","emp_no"))return "payroll_adjustments";
    // General Ledger: has a date column + a journal/reference column (before trial_balance check,
    // because Xero GL files also have AccountName which would otherwise match trial_balance).
    if (has("journaldate","journalnumber","journalno","journalnum") &&
        has("accountname","accountcode","account_name","account_code")) return "general_ledger";
    if (has("date") && has("journalnumber","journalno","journalnum","reference","ref") &&
        (has("debit","credit","netamount","net_amount")))     return "general_ledger";
    if (has("classification"))                                return "balance_sheet";
    // Trial Balance: account names/codes with debit/credit but NO date column
    if (has("accountname","account_name") && !has("date","journaldate") &&
        has("debit","credit"))                                return "trial_balance";
    if (has("code","accountname","account_name") &&
        has("accountname","account_name") &&
        !has("date","journaldate"))                           return "trial_balance";
    if (has("journalno","journalnum"))                        return "journals";
    // suppliers have bank columns; customers don't
    if (has("bankname","bank_name","accountnumber","account_number")) return "suppliers";
    if (has("name","contactname","customer","company"))       return "customers";
    return null; // no confident match — leave tab as-is
  }

  // Client-side parse for preview only
  function handleFileChange(e) {
    const f = e.target.files[0];
    if (!f) { setFile(null); setPreview(null); setResult(null); return; }
    setFile(f); setResult(null); setErr(null);

    const ext = f.name.split(".").pop().toLowerCase();

    // Excel files: binary format — skip text preview, let backend parse
    if (ext === "xlsx" || ext === "xls") {
      setPreview({headers: [], rows: [], total: null, xlsx: true});
      return;
    }

    const reader = new FileReader();
    reader.onload = ev => {
      const text  = ev.target.result.replace(/^﻿/, ""); // strip BOM
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      if (!lines.length) { setPreview(null); return; }

      // Detect delimiter
      const first = lines[0];
      const delim = (first.split(",").length >= first.split(";").length &&
                     first.split(",").length >= first.split("\t").length) ? ","
                  : first.split(";").length >= first.split("\t").length   ? ";"
                  : "\t";

      const parseLine = l => l.split(delim).map(c => c.trim().replace(/^"|"$/g, ""));
      const headers   = parseLine(lines[0]);
      const rows      = lines.slice(1, 6).map(parseLine);

      // Auto-switch tab if we can confidently identify the data type
      const detected = detectTab(headers);
      if (detected && detected !== tab) setTab(detected);

      setPreview({headers, rows, total: lines.length - 1, xlsx: false});
    };
    reader.readAsText(f);
  }

  async function handleImport() {
    if (!file) return;
    setImporting(true); setErr(null); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${BASE_URL}/import/${tab}${HAS_ASAT ? `?as_at=${encodeURIComponent(asAt)}&replace=${replaceImp}` : ""}`, {
        method: "POST",
        headers: {Authorization: "Bearer " + token},
        body: fd,
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(typeof body.detail === "string" ? body.detail : "Import failed");
      }
      const data = await r.json();
      setResult(data);
      // Employees and payroll adjustments feed the Payroll tab which uses live data
      // loaded at startup — reload so the updated employee list is picked up immediately.
      if ((tab === "employees" || tab === "payroll_adjustments") && (data.imported || 0) > 0) {
        setTimeout(() => window.location.reload(), 2500);
      }
    } catch(e) { setErr(e.message); }
    finally   { setImporting(false); }
  }

  async function handleClearImports() {
    if (!window.confirm("This will delete ALL journal entries that were created by any import (GL, journals, trial balance, balance sheet, P&L). Invoices, expenses and employees are NOT affected.\n\nAre you sure?")) return;
    try {
      const r = await fetch(`${BASE_URL}/import/all`, {
        method: "DELETE",
        headers: {Authorization: "Bearer " + token},
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail || "Failed to clear imports");
      }
      const data = await r.json();
      alert(`Done — ${data.deleted_entries} journal entr${data.deleted_entries === 1 ? "y" : "ies"} removed. You can now re-import.`);
      reset();
    } catch(e) { alert("Error: " + e.message); }
  }

  function reset() {
    setFile(null); setPreview(null); setResult(null); setErr(null);
    if (fileRef.current) fileRef.current.value = "";
  }

  const currentTab = ITABS.find(t => t.id === tab);

  return (
    <div>
      {/* Header */}
      <div style={{marginBottom:24,display:"flex",alignItems:"flex-start",justifyContent:"space-between",flexWrap:"wrap",gap:12}}>
        <div>
          <h2 style={{fontSize:20,fontWeight:700,color:C.ink,margin:"0 0 4px"}}>Import Data</h2>
          <p style={{fontSize:13,color:C.inkMid,margin:0}}>
            Migrate history from Xero, QuickBooks, or any CSV / Excel export. Duplicates are skipped automatically.
          </p>
        </div>
        <button
          onClick={handleClearImports}
          style={{flexShrink:0,padding:"8px 16px",border:"1px solid #dc2626",borderRadius:10,background:"transparent",color:"#dc2626",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}
        >
          🗑 Reset All Imports
        </button>
      </div>

      <div style={{display:"flex",gap:20,alignItems:"flex-start",flexDirection:isMobile?"column":"row"}}>

        {/* ── Left sidebar: entity picker ── */}
        <div style={{width:isMobile?"100%":190,boxSizing:"border-box",display:isMobile?"flex":"block",flexWrap:"wrap",gap:isMobile?6:0,flexShrink:0,background:C.surface,border:`1px solid ${C.border}`,borderRadius:14,padding:10}}>
          <div style={{fontSize:10,fontWeight:700,color:C.inkMid,textTransform:"uppercase",letterSpacing:0.8,padding:"6px 10px 10px"}}>Data Type</div>
          {ITABS.map(t => (
            <button key={t.id} onClick={()=>switchTab(t.id)} style={{
              display:"flex",alignItems:"center",gap:10,width:isMobile?"auto":"100%",textAlign:"left",
              padding:isMobile?"10px 12px":"11px 14px",minHeight:isMobile?40:undefined,borderRadius:10,border:"none",cursor:"pointer",
              fontFamily:"inherit",fontSize:13,marginBottom:2,
              background:tab===t.id ? C.accentLt : "transparent",
              color:tab===t.id ? C.accent : C.ink,
              fontWeight:tab===t.id ? 700 : 400,
            }}>
              <span style={{fontSize:16,lineHeight:1}}>{t.icon}</span>
              <span>{t.label}</span>
            </button>
          ))}
        </div>

        {/* ── Right panel ── */}
        <div style={{flex:1,minWidth:0,width:"100%"}}>

          {/* Xero Migration Wizard — full-panel takeover */}
          {tab === "xero_wizard" ? <XeroMigrationWizard/> : tab === "qbo_wizard" ? <QuickBooksMigrationWizard/> : (<>

          {/* Info + template bar */}
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:14,padding:"18px 22px",marginBottom:16}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:16}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontWeight:700,fontSize:15,color:C.ink,marginBottom:6}}>
                  {currentTab?.icon} {currentTab?.label}
                </div>
                <div style={{fontSize:12,color:C.inkMid,lineHeight:1.6,marginBottom:4}}>
                  <strong style={{color:C.ink}}>Required: </strong>{REQUIRED[tab]}
                </div>
                <div style={{fontSize:12,color:C.inkMid,lineHeight:1.6}}>
                  <strong style={{color:C.ink}}>Column aliases: </strong>{HINTS[tab]}
                </div>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:8,flexShrink:0}}>
                {tab === "employees" && (
                  <button onClick={async () => {
                    // Fetch live employee list and export as pre-filled Excel
                    try {
                      const r = await fetch(`${BASE_URL}/employees`, {
                        headers: {Authorization: "Bearer " + token}
                      });
                      if (!r.ok) throw new Error("Could not load employees");
                      const emps = await r.json();
                      const rows = emps.map(e => ({
                        "Employee Number":          e.employee_number || "",
                        "First Name":               e.first_name || "",
                        "Last Name":                e.last_name  || "",
                        "ID Number":                e.id_number  || "",
                        "Tax Number":               e.tax_number || "",
                        "Date of Birth":            e.date_of_birth    || "",
                        "Appointment Date":         e.appointment_date || "",
                        "Start Date":               e.start_date || "",
                        "Address":                  e.address    || "",
                        "Position":                 e.position   || "",
                        "Department":               e.department || "",
                        "Grade":                    e.grade      || "",
                        "Employment Type":          e.employment_type || "salaried",
                        "Employment Category":      e.employment_category || "permanent",
                        "Gross Salary":             e.gross_salary || 0,
                        "Hourly Rate":              e.hourly_rate  || "",
                        "Bank Name":                e.bank_name    || "",
                        "Account Number":           e.account_number || "",
                        "Branch Code":              e.branch_code  || "",
                        "Account Type":             e.account_type || "",
                        "Pension Employee Pct":     e.pension_fund_employee_pct || 0,
                        "Pension Employer Pct":     e.pension_fund_employer_pct || 0,
                        "Pension Employee Fixed":   e.pension_employee_fixed    || 0,
                        "Pension Employer Fixed":   e.pension_employer_fixed    || 0,
                        "Medical Aid Employee":     e.medical_aid_employee   || 0,
                        "Medical Aid Employer":     e.medical_aid_employer   || 0,
                        "Medical Aid Dependants":   e.medical_aid_dependants || 0,
                        "Union Subscription":       e.union_subscription       || 0,
                        "Advance Monthly Deduction":e.advance_monthly_deduction || 0,
                        "On Maternity Leave":       e.on_maternity_leave ? "true" : "false",
                        "Security Grade":           e.security_grade || "",
                        "Security Area":            e.security_area  || "",
                        "PSIRA Number":             e.psira_number   || "",
                        "Shift Type":               e.shift_type     || "",
                        "Pay Schedule":             e.pay_schedule   || "",
                      }));
                      const ws = XLSX.utils.json_to_sheet(rows);
                      // Column widths
                      ws["!cols"] = [{wch:16},{wch:16},{wch:16},{wch:16},{wch:14},{wch:14},{wch:16},{wch:12},{wch:24},{wch:20},{wch:16},{wch:8},{wch:14},{wch:18},{wch:14},{wch:12},{wch:14},{wch:18},{wch:12},{wch:14},{wch:18},{wch:18},{wch:18},{wch:18},{wch:18},{wch:18},{wch:16},{wch:18},{wch:20},{wch:16},{wch:14},{wch:14},{wch:14},{wch:12},{wch:14}];
                      const wb = XLSX.utils.book_new();
                      XLSX.utils.book_append_sheet(wb, ws, "Employees");
                      XLSX.writeFile(wb, `employees_${new Date().toISOString().slice(0,10)}.xlsx`);
                    } catch(ex) { alert("Failed to export: " + ex.message); }
                  }} style={{
                    display:"flex",alignItems:"center",gap:6,
                    padding:"9px 18px",border:`1px solid ${C.green}`,borderRadius:10,
                    fontSize:13,cursor:"pointer",color:C.green,background:C.greenLt,
                    fontFamily:"inherit",whiteSpace:"nowrap",fontWeight:600,
                  }}>
                    ⬇ Current Employees (Excel)
                  </button>
                )}
                <button onClick={downloadTemplate} style={{
                  display:"flex",alignItems:"center",gap:6,
                  padding:"9px 18px",border:`1px solid ${C.accent}`,borderRadius:10,
                  fontSize:13,cursor:"pointer",color:C.accent,background:"transparent",
                  fontFamily:"inherit",whiteSpace:"nowrap",fontWeight:600,
                }}>
                  ⬇ {tab === "employees" ? "Blank Template (Excel)" : "Template CSV"}
                </button>
              </div>
            </div>
          </div>

          {HAS_ASAT && !result && (
            <div style={{marginBottom:16,padding:"12px 14px",background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,fontSize:12,color:C.inkMid}}>
              <label style={{display:"block",marginBottom:8}}>Balances are as at (date)<br/>
                <input type="date" value={asAt} onChange={e=>setAsAt(e.target.value)} style={{marginTop:4,padding:"8px 10px",fontSize:16,border:`1px solid ${C.border}`,borderRadius:8,fontFamily:"inherit"}}/>
              </label>
              <label style={{display:"flex",gap:8,alignItems:"flex-start",lineHeight:1.5}}>
                <input type="checkbox" checked={replaceImp} onChange={e=>setReplaceImp(e.target.checked)} style={{marginTop:3}}/>
                <span>Replace the opening balances I already imported (tick this if you are re-importing a corrected file; otherwise a second import is refused so balances are never doubled)</span>
              </label>
            </div>
          )}

          {/* Upload zone — shown until a file is chosen */}
          {!result && (
            <div style={{marginBottom:16}}>
              {!file ? (
                <label style={{
                  display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
                  border:`2px dashed ${C.border}`,borderRadius:14,padding:"40px 24px",
                  cursor:"pointer",textAlign:"center",background:C.bg,
                  transition:"border-color 0.15s",
                }}>
                  <div style={{fontSize:36,marginBottom:10}}>📂</div>
                  <div style={{fontWeight:700,fontSize:15,color:C.ink,marginBottom:4}}>
                    Drop a file here, or click to browse
                  </div>
                  <div style={{fontSize:12,color:C.inkMid}}>CSV, Excel (.xlsx / .xls), TSV</div>
                  <input ref={fileRef} type="file" accept=".csv,.tsv,.txt,.xlsx,.xls"
                    onChange={handleFileChange} style={{display:"none"}}/>
                </label>
              ) : (
                /* File chosen — show name + clear */
                <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:14,padding:"16px 22px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
                  <div>
                    <div style={{fontWeight:600,fontSize:14,color:C.ink}}>📄 {file.name}</div>
                    <div style={{fontSize:12,color:C.inkMid,marginTop:2}}>{(file.size/1024).toFixed(1)} KB</div>
                  </div>
                  <button onClick={reset} style={{
                    padding:"7px 16px",border:`1px solid ${C.border}`,borderRadius:8,
                    fontSize:13,cursor:"pointer",color:C.inkMid,background:"transparent",fontFamily:"inherit",
                  }}>✕ Clear</button>
                </div>
              )}
            </div>
          )}

          {/* Preview */}
          {preview && !result && (
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:14,padding:"18px 22px",marginBottom:16}}>
              {preview.xlsx ? (
                <div>
                  <div style={{fontWeight:700,fontSize:14,color:C.ink,marginBottom:6}}>Excel file ready</div>
                  <div style={{fontSize:13,color:C.inkMid,marginBottom:16}}>
                    All rows will be parsed server-side. Click Import to continue.
                  </div>
                  <div style={{display:"flex",gap:12,alignItems:"center"}}>
                    <button onClick={()=>setShowConfirm(true)} disabled={importing} style={{
                      padding:"10px 28px",background:importing?"#ccc":C.accent,color:"#fff",
                      border:"none",borderRadius:10,fontSize:14,fontWeight:700,
                      cursor:importing?"not-allowed":"pointer",fontFamily:"inherit",
                    }}>
                      {importing ? "Importing…" : "Import Excel file"}
                    </button>
                    {err && <span style={{fontSize:12,color:"#C8401A"}}>{err}</span>}
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{fontWeight:700,fontSize:14,color:C.ink,marginBottom:12}}>
                    Preview — {preview.total.toLocaleString()} rows detected <span style={{fontWeight:400,color:C.inkMid,fontSize:12}}>(showing first 5)</span>
                  </div>
                  <div style={{overflowX:"auto",borderRadius:8,border:`1px solid ${C.border}`}}>
                    <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                      <thead>
                        <tr style={{background:C.bg}}>
                          {preview.headers.map((h,i) => (
                            <th key={i} style={{padding:"8px 12px",textAlign:"left",
                              color:C.inkMid,fontWeight:600,fontSize:11,
                              borderBottom:`1px solid ${C.border}`,whiteSpace:"nowrap"}}>
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {preview.rows.map((row,ri) => (
                          <tr key={ri} style={{borderBottom:`1px solid ${C.border}30`}}>
                            {row.map((cell,ci) => (
                              <td key={ci} style={{padding:"7px 12px",color:C.ink,
                                maxWidth:180,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{marginTop:16,display:"flex",gap:12,alignItems:"center"}}>
                    <button onClick={()=>setShowConfirm(true)} disabled={importing} style={{
                      padding:"10px 28px",background:importing?"#ccc":C.accent,color:"#fff",
                      border:"none",borderRadius:10,fontSize:14,fontWeight:700,
                      cursor:importing?"not-allowed":"pointer",fontFamily:"inherit",
                    }}>
                      {importing ? "Importing…" : `Import ${preview.total.toLocaleString()} rows`}
                    </button>
                    {err && <span style={{fontSize:12,color:"#C8401A"}}>{err}</span>}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Results */}
          {result && (
            <div style={{background:C.surface,border:`2px solid ${result.errors.filter(e=>!e.message.includes("already exists")).length ? "#C8401A" : "#28a745"}`,borderRadius:14,padding:"20px 24px"}}>
              <div style={{fontWeight:700,fontSize:16,color:C.ink,marginBottom:16}}>✅ Import Complete</div>
              <div style={{display:"flex",gap:12,flexWrap:"wrap",marginBottom:20}}>
                {[
                  {label:"Rows in file",        value:result.total_rows, color:C.inkMid},
                  ...(tab==="employees" && result.created != null ? [
                    {label:"Created (new)",  value:result.created,  color:"#28a745"},
                    {label:"Updated (existing)", value:result.updated||0, color:"#0072CE"},
                  ] : [
                    {label:tab==="payroll_adjustments"?"Updated":"Imported", value:result.imported, color:"#28a745"},
                  ]),
                  {label:tab==="payroll_adjustments"?"Not Found":"Skipped (duplicate)", value:result.skipped||0, color:C.inkMid},
                  {label:"Errors",               value:result.errors.filter(e=>!e.message.includes("already exists")).length, color:"#C8401A"},
                ].map(k => (
                  <div key={k.label} style={{flex:1,minWidth:110,background:C.bg,borderRadius:10,padding:"14px 16px",textAlign:"center"}}>
                    <div style={{fontSize:28,fontWeight:800,color:k.color}}>{k.value}</div>
                    <div style={{fontSize:11,color:C.inkMid,marginTop:3}}>{k.label}</div>
                  </div>
                ))}
              </div>
              {result.errors.length > 0 && (
                <div style={{marginBottom:16}}>
                  <div style={{fontSize:12,fontWeight:700,color:C.ink,marginBottom:8}}>Row notes ({result.errors.length})</div>
                  <div style={{maxHeight:200,overflowY:"auto",border:`1px solid ${C.border}`,borderRadius:8}}>
                    <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                      <thead>
                        <tr style={{background:C.bg,position:"sticky",top:0}}>
                          <th style={{padding:"6px 12px",textAlign:"left",color:C.inkMid,fontWeight:600,borderBottom:`1px solid ${C.border}`,width:60}}>Row</th>
                          <th style={{padding:"6px 12px",textAlign:"left",color:C.inkMid,fontWeight:600,borderBottom:`1px solid ${C.border}`}}>Note</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.errors.map((e,i) => (
                          <tr key={i} style={{borderBottom:`1px solid ${C.border}30`,background:e.message.includes("already exists")?"transparent":"#fff0ee"}}>
                            <td style={{padding:"5px 12px",fontFamily:"monospace",color:C.inkMid}}>{e.row}</td>
                            <td style={{padding:"5px 12px",color:e.message.includes("already exists")?C.inkMid:"#C8401A"}}>{e.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              <button onClick={reset} style={{
                padding:"9px 22px",border:`1px solid ${C.border}`,borderRadius:10,
                fontSize:13,cursor:"pointer",color:C.ink,background:"transparent",fontFamily:"inherit",fontWeight:600,
              }}>
                ← Import another file
              </button>
            </div>
          )}
          </>)}
        </div>
      </div>

      {/* Confirmation modal */}
      {showConfirm && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:900,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
          <div style={{background:C.surface,borderRadius:16,padding:32,width:"100%",maxWidth:440,boxShadow:"0 8px 40px rgba(0,0,0,0.18)"}}>
            <div style={{fontSize:18,fontWeight:800,color:C.ink,marginBottom:8}}>Confirm Import</div>
            <p style={{fontSize:13,color:C.inkMid,margin:"0 0 20px",lineHeight:1.6}}>
              You're about to import{" "}
              <strong style={{color:C.ink}}>
                {preview?.xlsx ? "all rows" : `${preview?.total?.toLocaleString() ?? "all"} rows`}
              </strong>{" "}
              of <strong style={{color:C.ink}}>{currentTab?.label}</strong> data from{" "}
              <strong style={{color:C.ink}}>{file?.name}</strong> into your books.
              <br/><br/>
              Duplicates are skipped automatically. This action cannot be undone.
            </p>
            <div style={{display:"flex",gap:12}}>
              <button
                onClick={async () => { setShowConfirm(false); await handleImport(); }}
                style={{flex:1,padding:"11px 0",background:C.accent,border:"none",borderRadius:10,color:"#fff",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}
              >
                Yes, Import
              </button>
              <button
                onClick={()=>setShowConfirm(false)}
                style={{flex:1,padding:"11px 0",background:"transparent",border:`1px solid ${C.border}`,borderRadius:10,color:C.inkMid,fontSize:14,cursor:"pointer",fontFamily:"inherit"}}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── DOCUMENT REPOSITORY ───────────────────────────────────────────────────────
function DocumentRepository() {
  const token = localStorage.getItem("zuzan_token");
  const [docs,     setDocs]    = useState([]);
  const [loading,  setLoading] = useState(false);
  const [err,      setErr]     = useState(null);
  const [catFilter,setCatFilter]= useState("All");
  const [uploading,setUploading]= useState(false);
  const [uploadErr,setUploadErr]= useState(null);
  const [showForm, setShowForm] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const fileRef = useRef(null);

  // Upload form state
  const [uName,    setUName]    = useState("");
  const [uCat,     setUCat]     = useState("General");
  const [uDesc,    setUDesc]    = useState("");
  const [uFile,    setUFile]    = useState(null);

  const CATEGORIES = [
    "All",
    "CIPC & Registration",
    "Tax Documents",
    "Contracts",
    "Annual Financial Statements",
    "Payroll Records",
    "Insurance",
    "Banking",
    "General",
  ];

  const CAT_ICONS = {
    "CIPC & Registration":           "🏛",
    "Tax Documents":                 "📋",
    "Contracts":                     "📝",
    "Annual Financial Statements":   "📑",
    "Payroll Records":               "👥",
    "Insurance":                     "🛡",
    "Banking":                       "🏦",
    "General":                       "📁",
  };

  useEffect(() => { fetchDocs(); }, [catFilter]);

  async function fetchDocs() {
    setLoading(true); setErr(null);
    try {
      const url = catFilter === "All"
        ? `${BASE_URL}/documents/`
        : `${BASE_URL}/documents/?category=${encodeURIComponent(catFilter)}`;
      const r = await fetch(url, {headers:{Authorization:"Bearer "+token}});
      if (!r.ok) throw new Error((await r.json()).detail || "Load failed");
      setDocs(await r.json());
    } catch(e) { setErr(e.message); }
    finally { setLoading(false); }
  }

  async function handleUpload(e) {
    e.preventDefault();
    if (!uFile) { setUploadErr("Please select a file."); return; }
    setUploading(true); setUploadErr(null);
    try {
      const fd = new FormData();
      fd.append("name",        uName || uFile.name);
      fd.append("category",    uCat);
      fd.append("description", uDesc);
      fd.append("file",        uFile);
      const r = await fetch(`${BASE_URL}/documents/upload`, {
        method:"POST", headers:{Authorization:"Bearer "+token}, body: fd,
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Upload failed");
      setShowForm(false); setUName(""); setUCat("General"); setUDesc(""); setUFile(null);
      if (fileRef.current) fileRef.current.value = "";
      fetchDocs();
    } catch(e) { setUploadErr(e.message); }
    finally { setUploading(false); }
  }

  async function handleDownload(doc) {
    try {
      const r = await fetch(`${BASE_URL}/documents/${doc.id}/download`,
        {headers:{Authorization:"Bearer "+token}});
      if (!r.ok) throw new Error("Download failed");
      const blob = await r.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url; a.download = doc.file_name; a.click();
      URL.revokeObjectURL(url);
    } catch(e) { alert("Download failed: " + e.message); }
  }

  async function handleDelete(doc) {
    if (!window.confirm(`Delete "${doc.name}"? This cannot be undone.`)) return;
    setDeleting(doc.id);
    try {
      const r = await fetch(`${BASE_URL}/documents/${doc.id}`,
        {method:"DELETE", headers:{Authorization:"Bearer "+token}});
      if (!r.ok) throw new Error((await r.json()).detail || "Delete failed");
      setDocs(prev => prev.filter(d => d.id !== doc.id));
    } catch(e) { alert("Delete failed: " + e.message); }
    finally { setDeleting(null); }
  }

  const fmtSize = (bytes) => {
    if (!bytes) return "—";
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024*1024) return (bytes/1024).toFixed(1) + " KB";
    return (bytes/1024/1024).toFixed(1) + " MB";
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-ZA") : "—";

  const filtered = catFilter === "All" ? docs : docs.filter(d => d.category === catFilter);

  // Group by category for the "All" view
  const grouped = {};
  filtered.forEach(d => {
    const cat = d.category || "General";
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(d);
  });

  return (
    <div>
      {/* Header */}
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:24}}>
        <div>
          <h2 style={{fontSize:20,fontWeight:700,color:C.ink,margin:0}}>Document Repository</h2>
          <p style={{fontSize:12,color:C.inkMid,margin:0}}>Store and access important company documents securely</p>
        </div>
        <button onClick={()=>{setShowForm(true);setUploadErr(null);}}
          style={{marginLeft:"auto",padding:"8px 18px",background:C.accent,color:"#fff",border:"none",
            borderRadius:8,fontSize:13,fontWeight:600,cursor:"pointer"}}>
          + Upload Document
        </button>
      </div>

      {/* Upload form modal */}
      {showForm && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.35)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{background:C.surface,borderRadius:16,padding:28,width:480,maxWidth:"94vw",boxShadow:"0 8px 40px rgba(0,0,0,0.18)"}}>
            <h3 style={{margin:"0 0 20px",fontSize:16,fontWeight:700,color:C.ink}}>Upload Document</h3>
            <form onSubmit={handleUpload}>
              <div style={{marginBottom:14}}>
                <label style={{fontSize:12,fontWeight:600,color:C.inkMid,display:"block",marginBottom:4}}>Document Name</label>
                <input value={uName} onChange={e=>setUName(e.target.value)} placeholder="e.g. Tax Clearance Certificate 2024"
                  style={{width:"100%",padding:"8px 10px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",boxSizing:"border-box"}}/>
              </div>
              <div style={{marginBottom:14}}>
                <label style={{fontSize:12,fontWeight:600,color:C.inkMid,display:"block",marginBottom:4}}>Category</label>
                <select value={uCat} onChange={e=>setUCat(e.target.value)}
                  style={{width:"100%",padding:"8px 10px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",boxSizing:"border-box",background:C.surface}}>
                  {CATEGORIES.filter(c=>c!=="All").map(c=><option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div style={{marginBottom:14}}>
                <label style={{fontSize:12,fontWeight:600,color:C.inkMid,display:"block",marginBottom:4}}>Description (optional)</label>
                <input value={uDesc} onChange={e=>setUDesc(e.target.value)} placeholder="Brief description…"
                  style={{width:"100%",padding:"8px 10px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontFamily:"inherit",boxSizing:"border-box"}}/>
              </div>
              <div style={{marginBottom:18}}>
                <label style={{fontSize:12,fontWeight:600,color:C.inkMid,display:"block",marginBottom:4}}>File <span style={{color:C.inkMid,fontWeight:400}}>(max 10 MB)</span></label>
                <input ref={fileRef} type="file" onChange={e=>setUFile(e.target.files[0]||null)}
                  style={{width:"100%",padding:"6px 0",fontSize:13,fontFamily:"inherit"}}/>
              </div>
              {uploadErr && <p style={{color:"#C8401A",fontSize:12,margin:"0 0 12px"}}>{uploadErr}</p>}
              <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
                <button type="button" onClick={()=>{setShowForm(false);setUFile(null);if(fileRef.current)fileRef.current.value="";}}
                  style={{padding:"8px 18px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,cursor:"pointer",background:C.surface,color:C.ink}}>
                  Cancel
                </button>
                <button type="submit" disabled={uploading}
                  style={{padding:"8px 20px",background:uploading?"#ccc":C.accent,color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:600,cursor:uploading?"not-allowed":"pointer"}}>
                  {uploading ? "Uploading…" : "Upload"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Category filter pills */}
      <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:20}}>
        {CATEGORIES.map(c=>(
          <button key={c} onClick={()=>setCatFilter(c)}
            style={{padding:"5px 14px",borderRadius:20,border:`1px solid ${catFilter===c?C.accent:C.border}`,
              fontSize:12,cursor:"pointer",fontFamily:"inherit",
              background:catFilter===c?C.accentLt:"transparent",color:catFilter===c?C.accent:C.inkMid,
              fontWeight:catFilter===c?700:400}}>
            {c !== "All" && (CAT_ICONS[c]||"📁") + " "}{c}
          </button>
        ))}
      </div>

      {err    && <div style={{padding:16,background:"#fff0ee",borderRadius:8,color:"#C8401A",fontSize:13,marginBottom:16}}>{err}</div>}
      {loading && <div style={{padding:60,textAlign:"center",color:C.inkMid}}>Loading documents…</div>}

      {!loading && filtered.length === 0 && (
        <div style={{padding:48,textAlign:"center",color:C.inkMid,border:`1px dashed ${C.border}`,borderRadius:12}}>
          <div style={{fontSize:40,marginBottom:12}}>📂</div>
          <div style={{fontSize:14,marginBottom:8}}>No documents yet{catFilter !== "All" ? ` in "${catFilter}"` : ""}</div>
          <div style={{fontSize:12}}>Click <strong>Upload Document</strong> to add your first file.</div>
        </div>
      )}

      {/* Document list — grouped by category */}
      {!loading && Object.entries(grouped).map(([cat, items]) => (
        <div key={cat} style={{marginBottom:28}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
            <span style={{fontSize:18}}>{CAT_ICONS[cat]||"📁"}</span>
            <h3 style={{margin:0,fontSize:14,fontWeight:700,color:C.ink}}>{cat}</h3>
            <span style={{fontSize:11,color:C.inkMid}}>({items.length})</span>
          </div>
          <div style={{border:`1px solid ${C.border}`,borderRadius:12,overflow:"hidden"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
              <thead>
                <tr style={{background:"#f8f5f2"}}>
                  <th style={{padding:"10px 14px",textAlign:"left",color:C.inkMid,fontWeight:600,fontSize:12,borderBottom:`1px solid ${C.border}`}}>Document</th>
                  <th style={{padding:"10px 14px",textAlign:"left",color:C.inkMid,fontWeight:600,fontSize:12,borderBottom:`1px solid ${C.border}`}}>File</th>
                  <th style={{padding:"10px 14px",textAlign:"right",color:C.inkMid,fontWeight:600,fontSize:12,borderBottom:`1px solid ${C.border}`}}>Size</th>
                  <th style={{padding:"10px 14px",textAlign:"right",color:C.inkMid,fontWeight:600,fontSize:12,borderBottom:`1px solid ${C.border}`}}>Uploaded</th>
                  <th style={{padding:"10px 14px",textAlign:"right",color:C.inkMid,fontWeight:600,fontSize:12,borderBottom:`1px solid ${C.border}`}}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((doc,i) => (
                  <tr key={doc.id} style={{background:i%2===0?"transparent":"#fdf9f7",borderBottom:`1px solid ${C.border}`}}>
                    <td style={{padding:"10px 14px"}}>
                      <div style={{fontWeight:600,color:C.ink}}>{doc.name}</div>
                      {doc.description && <div style={{fontSize:11,color:C.inkMid,marginTop:2}}>{doc.description}</div>}
                    </td>
                    <td style={{padding:"10px 14px",color:C.inkMid,fontSize:12,maxWidth:160,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                      {doc.file_name}
                    </td>
                    <td style={{padding:"10px 14px",textAlign:"right",color:C.inkMid,fontFamily:"monospace",fontSize:12}}>
                      {fmtSize(doc.file_size)}
                    </td>
                    <td style={{padding:"10px 14px",textAlign:"right",color:C.inkMid,fontSize:12}}>
                      {fmtDate(doc.uploaded_at)}
                    </td>
                    <td style={{padding:"10px 14px",textAlign:"right",whiteSpace:"nowrap"}}>
                      <button onClick={()=>handleDownload(doc)}
                        style={{padding:"4px 12px",marginRight:6,border:`1px solid ${C.accent}`,borderRadius:6,
                          fontSize:12,cursor:"pointer",color:C.accent,background:"transparent",fontFamily:"inherit"}}>
                        Download
                      </button>
                      <button onClick={()=>handleDelete(doc)} disabled={deleting===doc.id}
                        style={{padding:"4px 12px",border:"1px solid #C8401A",borderRadius:6,
                          fontSize:12,cursor:deleting===doc.id?"not-allowed":"pointer",
                          color:"#C8401A",background:"transparent",fontFamily:"inherit",
                          opacity:deleting===doc.id?0.5:1}}>
                        {deleting===doc.id?"…":"Delete"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── PLAN FEATURE GATING ───────────────────────────────────────────────────────
const PLAN_LEVEL = { starter: 1, professional: 2, business: 3 };
const PLAN_LABEL = { professional: "Professional", business: "Business" };
const PLAN_PRICE = { professional: "R699/mo", business: "R1 299/mo" };

function getPlanLevel(user) {
  const id = (user?.plan?.id || "starter").toLowerCase();
  return PLAN_LEVEL[id] || 1;
}

function canAccess(user, requiredPlan) {
  return getPlanLevel(user) >= (PLAN_LEVEL[requiredPlan] || 1);
}

function UpgradeWall({ requiredPlan, onNavigateSettings }) {
  const label = PLAN_LABEL[requiredPlan] || "Professional";
  const price = PLAN_PRICE[requiredPlan] || "R899/mo";
  return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",minHeight:400,padding:40,textAlign:"center"}}>
      <div style={{fontSize:52,marginBottom:16}}>🔒</div>
      <h2 style={{fontFamily:"serif",fontSize:24,color:C.ink,margin:"0 0 10px"}}>{label} Plan Feature</h2>
      <p style={{fontSize:14,color:C.inkMid,maxWidth:400,lineHeight:1.7,margin:"0 0 6px"}}>
        This feature is available on the <strong style={{color:C.ink}}>{label}</strong> plan and above.
      </p>
      <p style={{fontSize:13,color:C.inkMid,maxWidth:400,lineHeight:1.6,margin:"0 0 28px"}}>
        Upgrade to {label} at <strong style={{color:C.ink}}>{price}</strong> to unlock this feature.
      </p>
      <button
        onClick={onNavigateSettings}
        style={{background:C.accent,color:"#fff",border:"none",borderRadius:10,padding:"12px 28px",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}
      >
        Upgrade in Settings →
      </button>
    </div>
  );
}

// ── MAIN APP ──────────────────────────────────────────────────────────────────
function ZuZanApp({user, onLogout, onUserUpdate, onBackToPractice}) {
  const live = useLiveData();
  const [tab, setTab] = useState("dashboard");

  // ── Site analytics: fire a page-view ping on every tab change ────────────
  const _sessionId = useRef((() => {
    try {
      let sid = localStorage.getItem("_zz_sid");
      if (!sid) { sid = Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem("_zz_sid", sid); }
      return sid;
    } catch { return "anon"; }
  })());
  useEffect(() => {
    fetch(`${BASE_URL}/track`, {
      method: "POST",
      headers: {"Content-Type": "application/json"},
      body: JSON.stringify({ session_id: _sessionId.current, page: tab, referrer: document.referrer || "" }),
    }).catch(() => {});
  }, [tab]);
  const [docTemplate, setDocTemplate] = useState(() => {
    try { const s = localStorage.getItem("zuzan_doc_template"); return s ? {...DEFAULT_DOC_TEMPLATE, ...JSON.parse(s)} : DEFAULT_DOC_TEMPLATE; } catch { return DEFAULT_DOC_TEMPLATE; }
  });
  const handleTemplateChange = (tmpl) => {
    setDocTemplate(tmpl);
    try { localStorage.setItem("zuzan_doc_template", JSON.stringify(tmpl)); } catch {}
  };
  const [expanded, setExpanded] = useState({sales: false, procurement: false, banking: false});
  const isMobile = useIsMobile();

  // ── Trial-to-Live modal state ─────────────────────────────────────────────
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [clearSels, setClearSels] = useState({
    sales: false, expenses: false, customers: false, suppliers: false,
    employees: false, inventory: false, banking: false,
    budgets_assets: false, documents: false,
  });
  const [pendingPfData, setPendingPfData] = useState(null);
  const [clearingData, setClearingData] = useState(false);

  // ── Accountant Practice / multi-client: company switcher ──────────────────
  const [companies, setCompanies] = useState([]);
  const [showSwitcher, setShowSwitcher] = useState(false);
  useEffect(() => {
    api("/auth/my-companies").then(setCompanies).catch(() => {});
  }, []);
  const handleSwitchCompany = async (companyId) => {
    try {
      const data = await api(`/auth/switch-company/${companyId}`, {method:"POST"});
      localStorage.setItem("zuzan_token", data.access_token);
      window.location.reload();
    } catch (e) {
      alert(e.message || "Could not switch company.");
    }
  };
  // ── Subscribe intercept: show data-choice modal when moving from trial/expired to live ──
  const subscribeWithDataChoice = (pfUrl, pfData) => {
    const status = user?.subscriptionStatus;
    if (status === "trial" || status === "expired") {
      setPendingPfData({url: pfUrl, data: pfData});
      setClearSels({
        sales: false, expenses: false, customers: false, suppliers: false,
        employees: false, inventory: false, banking: false,
        budgets_assets: false, documents: false,
      });
      setShowTrialModal(true);
    } else {
      pfSubmit(pfUrl, pfData);
    }
  };
  const confirmTrialDataAndProceed = async () => {
    const toDelete = Object.keys(clearSels).filter(k => clearSels[k]);
    if (toDelete.length > 0) {
      setClearingData(true);
      try {
        await api("/companies/clear-data", {
          method: "POST",
          body: JSON.stringify({categories: toDelete, confirm: true}),
        });
      } catch(e) {
        alert("Could not clear data: " + (e.message || "Please try again."));
        setClearingData(false);
        return;
      }
      setClearingData(false);
    }
    setShowTrialModal(false);
    if (pendingPfData) pfSubmit(pendingPfData.url, pendingPfData.data);
  };

  const TABS = [
    {id:"dashboard",  label:"Dashboard",   icon:"🏠"},
    {id:"sales",      label:"Sales",       icon:"💼", children:[
      {id:"invoicing",       label:"Invoices",        icon:"🧾"},
      {id:"quotes",          label:"Quotes",          icon:"📝"},
      {id:"customers",       label:"Customers",       icon:"👥"},
    ]},
    {id:"procurement", label:"Procurement", icon:"🛒", children:[
      {id:"purchase_orders", label:"Purchase Orders", icon:"📋", minPlan:"professional"},
      {id:"suppliers",       label:"Suppliers",       icon:"🏭"},
    ]},
    {id:"expenses",   label:"Expenses",    icon:"💳"},
    {id:"payroll",    label:"Payroll",     icon:"👥"},
    {id:"reports",    label:"Reports",     icon:"📊"},
    {id:"budgeting",  label:"Budgeting",   icon:"🎯", minPlan:"professional"},
    {id:"debtors",    label:"Debtors",     icon:"📥", minPlan:"professional"},
    {id:"creditors",  label:"Creditors",   icon:"📤", minPlan:"professional"},
    {id:"coa",        label:"Accounts",    icon:"📒"},
    {id:"inventory",    label:"Inventory",   icon:"📦", minPlan:"professional"},
    {id:"fixed_assets",    label:"Fixed Assets", icon:"🏭", minPlan:"professional"},
    {id:"fin_statements",  label:"Annual AFS",   icon:"📑"},
    {id:"documents",       label:"Documents",    icon:"📁", minPlan:"business"},
    {id:"data_import",     label:"Import Data",  icon:"⬆️", minPlan:"starter"},
    {id:"banking",    label:"Banking",     icon:"🏦", children:[
      {id:"bankimport", label:"Manual Update",   icon:"📄"},
      {id:"bankfeeds",  label:"Connect to Bank", icon:"🔗"},
    ]},
    {id:"settings",   label:"Settings",    icon:"⚙️"},
  ];

  const toggleGroup = (id) => setExpanded(e => ({...e, [id]: !e[id]}));
  // Role-gated nav: employees see only dashboard and settings
  const visibleTabs = TABS.filter(t => {
    if (user?.role === "employee") return ["dashboard","settings"].includes(t.id);
    if (user?.role === "payroll") return ["payroll"].includes(t.id);
    if (user?.role === "accountant") return !["payroll"].includes(t.id);
    return true; // owner and admin see everything
  });

  const navBtn = (t, isChild=false) => {
    const active = tab === t.id;
    const locked = t.minPlan && !canAccess(user, t.minPlan);
    const lockLabel = t.minPlan === "business" ? "Biz" : "Pro";
    return (
      <button key={t.id} onClick={()=>setTab(t.id)} style={{
        width:"100%", display:"flex", alignItems:"center", gap:10,
        padding: isChild ? "8px 12px 8px 36px" : "10px 12px",
        borderRadius:10, border:"none", cursor:"pointer", fontFamily:"inherit",
        fontSize: isChild ? 12 : 13,
        fontWeight: active ? 700 : 400,
        marginBottom:2,
        background: active ? C.accentLt : "transparent",
        color: active ? C.accent : locked ? C.inkDim : C.inkMid,
        textAlign:"left",
        opacity: locked ? 0.75 : 1,
      }}>
        <span style={{fontSize: isChild ? 14 : 17}}>{t.icon}</span>{t.label}
        {locked
          ? <span style={{marginLeft:"auto",fontSize:9,fontWeight:700,color:"#fff",background:t.minPlan==="business"?"#7C3AED":C.accent,borderRadius:4,padding:"1px 5px",letterSpacing:0.3}}>🔒 {lockLabel}</span>
          : active && <div style={{marginLeft:"auto",width:4,height:4,borderRadius:"50%",background:C.accent}}/>
        }
      </button>
    );
  };

  if (isMobile) return <MobileApp user={user} onLogout={onLogout} onUserUpdate={onUserUpdate} live={live} docTemplate={docTemplate} onTemplateChange={handleTemplateChange}/>;

  const screens = {
    dashboard:  <Dashboard  live={live}/>,
    invoicing:  <Invoicing  live={live} user={user} docTemplate={docTemplate}/>,
    quotes:     <Quotes     live={live} user={user} onNavigate={setTab} docTemplate={docTemplate}/>,
    expenses:   <Expenses   live={live}/>,
    payroll:    user?.payrollEnabled
      ? <Payroll live={live} user={user}/>
      : <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",minHeight:400,padding:40,textAlign:"center"}}>
          <div style={{fontSize:48,marginBottom:16}}>🔒</div>
          <h2 style={{fontFamily:"serif",fontSize:24,color:C.ink,margin:"0 0 10px"}}>Payroll not activated</h2>
          <p style={{fontSize:14,color:C.inkMid,maxWidth:380,lineHeight:1.6,margin:"0 0 28px"}}>
            Your current plan does not include payroll. Add payroll to run PAYE, UIF &amp; SDL calculations, generate payslips, and submit EMP201 reports.
          </p>
          <button onClick={()=>setTab("settings")} style={{background:C.accent,color:"#fff",border:"none",borderRadius:10,padding:"12px 28px",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
            Add Payroll in Settings →
          </button>
        </div>,
    reports:    <Reports    live={live} user={user}/>,
    budgeting:  canAccess(user,"professional") ? <Budgeting  live={live}/> : <UpgradeWall requiredPlan="professional" onNavigateSettings={()=>setTab("settings")}/>,
    debtors:    canAccess(user,"professional") ? <Debtors    live={live}/> : <UpgradeWall requiredPlan="professional" onNavigateSettings={()=>setTab("settings")}/>,
    creditors:  canAccess(user,"professional") ? <Creditors  live={live}/> : <UpgradeWall requiredPlan="professional" onNavigateSettings={()=>setTab("settings")}/>,
    coa:        <ChartOfAccounts/>,
    inventory:       canAccess(user,"professional") ? <Inventory/> : <UpgradeWall requiredPlan="professional" onNavigateSettings={()=>setTab("settings")}/>,
    fixed_assets:    canAccess(user,"professional") ? <FixedAssets/> : <UpgradeWall requiredPlan="professional" onNavigateSettings={()=>setTab("settings")}/>,
    fin_statements:  <AfsPaymentGate/>,
    documents:       canAccess(user,"business") ? <DocumentRepository/> : <UpgradeWall requiredPlan="business" onNavigateSettings={()=>setTab("settings")}/>,
    data_import:     <DataImport/>,
    customers:       <Customers/>,
    suppliers:       <Suppliers/>,
    purchase_orders: canAccess(user,"professional") ? <PurchaseOrders/> : <UpgradeWall requiredPlan="professional" onNavigateSettings={()=>setTab("settings")}/>,
    bankimport:      <BankImport live={live} onNavigate={setTab}/>,
    bankfeeds:       <BankFeeds/>,
    settings:   <AppSettings user={user} onLogout={onLogout} onUserUpdate={onUserUpdate} docTemplate={docTemplate} onTemplateChange={handleTemplateChange} onNavigate={setTab} onSubscribeFlow={subscribeWithDataChoice}/>,
  };

  return (
    <div style={{fontFamily:"sans-serif",background:C.bg,minHeight:"100vh",display:"flex"}}>
      <style>{`*{box-sizing:border-box;margin:0;padding:0;}::-webkit-scrollbar{width:4px;}::-webkit-scrollbar-thumb{background:${C.border};}button:hover{opacity:0.9;}`}</style>
      <div style={{width:230,background:C.surface,borderRight:`1px solid ${C.border}`,display:"flex",flexDirection:"column",position:"fixed",top:0,left:0,bottom:0,zIndex:10}}>
        <div style={{padding:"24px 20px 20px",borderBottom:`1px solid ${C.border}`}}>
          <div style={{fontFamily:"serif",fontSize:26,fontWeight:800,color:C.ink}}><span style={{color:C.accent}}>Zu</span>Zan</div>
          <div style={{fontSize:10,color:C.inkMid,marginTop:3,letterSpacing:0.5}}>SA BOOKKEEPING PLATFORM</div>
        </div>
        <div style={{padding:"14px 20px",borderBottom:`1px solid ${C.border}`,position:"relative"}}>
          <div onClick={()=>setShowSwitcher(s=>!s)} style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",marginBottom:2}}>
            <div style={{fontSize:12,fontWeight:600,color:C.ink,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{user?.companyName||"Your Company"}</div>
            <span style={{fontSize:9,color:C.inkDim,flexShrink:0}}>{showSwitcher?"▴":"▾"}</span>
          </div>
          <div style={{fontSize:10,color:C.inkDim}}>{(user?.plan?.name||"starter").charAt(0).toUpperCase()+(user?.plan?.name||"starter").slice(1)} Plan</div>
          <div style={{marginTop:8,height:3,background:C.border,borderRadius:2}}><div style={{height:"100%",width:"65%",background:C.accent,borderRadius:2}}/></div>
          <div style={{fontSize:9,color:C.inkDim,marginTop:4}}>{user?.billingExempt ? <span style={{color:"#2e7d32",fontWeight:700}}>🤝 Partner Account</span> : user?.subscriptionStatus==="active" ? "Active subscription" : user?.trialEnds ? (()=>{const d=Math.max(0,Math.ceil((new Date(user.trialEnds)-new Date())/86400000));return d>0?`Trial: ${d} day${d===1?"":"s"} remaining`:"Trial expired";})() : "Trial: 14 days remaining"}</div>
          {user?.billingExempt && (
            <button onClick={onBackToPractice} style={{marginTop:8,width:"100%",padding:"6px 0",background:"#e8f5e9",border:"1px solid #a5d6a7",borderRadius:8,color:"#1b5e20",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>← Practice Dashboard</button>
          )}
          {showSwitcher && (
            <div style={{position:"absolute",top:"100%",left:12,right:12,marginTop:6,background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,boxShadow:"0 8px 24px rgba(0,0,0,0.12)",zIndex:50,overflow:"hidden"}}>
              {companies.map(c => (
                <button key={c.id} onClick={()=>{setShowSwitcher(false); handleSwitchCompany(c.id);}} style={{
                  display:"block",width:"100%",textAlign:"left",padding:"10px 14px",border:"none",
                  borderBottom:`1px solid ${C.border}`,background:c.name===user?.companyName?C.accentLt:"transparent",
                  cursor:"pointer",fontFamily:"inherit",
                }}>
                  <div style={{fontSize:12,fontWeight:600,color:C.ink}}>{c.name}</div>
                  <div style={{fontSize:10,color:C.inkDim,textTransform:"capitalize"}}>{c.role||"member"}</div>
                </button>
              ))}
              <button onClick={async ()=>{setShowSwitcher(false); const n=window.prompt("New entity / client company name:"); if(!n||!n.trim()) return; try{ const r1=await api("/auth/add-company",{method:"POST",body:JSON.stringify({company_name:n.trim()})}); localStorage.setItem("zuzan_token",r1.access_token); window.location.reload(); }catch(e){alert(e.message||"Could not create company.");}}} style={{
                display:"block",width:"100%",textAlign:"left",padding:"10px 14px",border:"none",
                background:"transparent",cursor:"pointer",fontFamily:"inherit",color:C.accent,fontWeight:700,fontSize:12,
              }}>+ Add New Entity</button>
            </div>
          )}
        </div>
        <nav style={{flex:1,padding:"12px 10px",overflowY:"auto"}}>
          {visibleTabs.map(t => {
            if (!t.children) return navBtn(t);
            const isOpen = expanded[t.id];
            const childActive = t.children.some(c => c.id === tab);
            const groupLocked = t.minPlan && !canAccess(user, t.minPlan);
            const groupLockLabel = t.minPlan === "business" ? "Biz" : "Pro";
            return (
              <div key={t.id}>
                <button onClick={()=>toggleGroup(t.id)} style={{
                  width:"100%", display:"flex", alignItems:"center", gap:10,
                  padding:"10px 12px", borderRadius:10, border:"none", cursor:"pointer",
                  fontFamily:"inherit", fontSize:13, marginBottom:2,
                  fontWeight: childActive ? 700 : 400,
                  background: childActive ? C.accentLt : "transparent",
                  color: childActive ? C.accent : groupLocked ? C.inkDim : C.inkMid,
                  textAlign:"left",
                  opacity: groupLocked ? 0.75 : 1,
                }}>
                  <span style={{fontSize:17}}>{t.icon}</span>
                  {t.label}
                  {groupLocked
                    ? <span style={{marginLeft:"auto",fontSize:9,fontWeight:700,color:"#fff",background:t.minPlan==="business"?"#7C3AED":C.accent,borderRadius:4,padding:"1px 5px",letterSpacing:0.3}}>🔒 {groupLockLabel}</span>
                    : <span style={{marginLeft:"auto",fontSize:10,opacity:.5}}>{isOpen ? "▾" : "▸"}</span>
                  }
                </button>
                {isOpen && t.children.map(c => navBtn(c, true))}
              </div>
            );
          })}
        </nav>
        <div style={{padding:"14px 20px",borderTop:`1px solid ${C.border}`}}>
          <div style={{fontSize:12,fontWeight:600,color:C.ink}}>{user?.firstName||"User"} {user?.lastName||""}</div>
          <div style={{fontSize:10,color:C.inkDim,marginBottom:4}}>{user?.email||"user@company.co.za"}</div>
          {user?.role && user.role !== "owner" && <span style={{display:"inline-block",marginBottom:8,padding:"2px 8px",borderRadius:20,fontSize:9,fontWeight:700,color:"#00878A",background:"#E0F4F5"}}>{user.role.charAt(0).toUpperCase()+user.role.slice(1)}</span>}
          <button onClick={onLogout} style={{fontSize:11,color:C.inkDim,background:"none",border:"none",cursor:"pointer",padding:0,fontFamily:"inherit"}}>Sign out</button>
        </div>
      </div>
      <div style={{marginLeft:230,flex:1,padding:"36px",maxWidth:"calc(100vw - 230px)",overflowY:"auto",minHeight:"100vh"}}>
        {(()=>{
          const status = user?.subscriptionStatus;
          const trialEnds = user?.trialEnds;
          if (status === "expired") return (
            <div style={{background:C.red,color:"#fff",borderRadius:12,padding:"12px 20px",marginBottom:24,display:"flex",alignItems:"center",justifyContent:"space-between",gap:16}}>
              <div><strong>Subscription expired.</strong><span style={{fontSize:13,marginLeft:8,opacity:0.9}}>Reactivate to keep using ZuZan.</span></div>
              <button onClick={async()=>{try{const r=await api("/billing/subscribe",{method:"POST"});if(r.payfast_url)subscribeWithDataChoice(r.payfast_url,r.payfast_data);}catch(e){alert("Go to Settings → Subscription to reactivate.");}}} style={{background:"#fff",color:C.red,border:"none",borderRadius:8,padding:"8px 16px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>Reactivate</button>
            </div>
          );
          if (status === "trial" && trialEnds) {
            const d = Math.max(0, Math.ceil((new Date(trialEnds) - new Date()) / 86400000));
            if (d <= 5) return (
              <div style={{background:d===0?C.red:C.gold,color:"#fff",borderRadius:12,padding:"12px 20px",marginBottom:24,display:"flex",alignItems:"center",justifyContent:"space-between",gap:16}}>
                <div><strong>{d===0?"Trial expired.":"Trial ends in "+d+" day"+(d===1?"":"s")+"."}</strong><span style={{fontSize:13,marginLeft:8,opacity:0.9}}>{d===0?"Subscribe to continue.":"Subscribe now to keep your data."}</span></div>
                <button onClick={async()=>{try{const r=await api("/billing/subscribe",{method:"POST"});if(r.payfast_url)subscribeWithDataChoice(r.payfast_url,r.payfast_data);}catch(e){alert("Go to Settings → Subscription to subscribe.");}}} style={{background:"#fff",color:d===0?C.red:C.gold,border:"none",borderRadius:8,padding:"8px 16px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>Subscribe Now</button>
              </div>
            );
          }
          return null;
        })()}
        {screens[tab]}
      </div>
      <AIAssistant tab={tab}/>

      {/* ── Trial-to-Live Data Selection Modal ── */}
      {showTrialModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
          <div style={{background:C.surface,borderRadius:20,padding:32,width:"100%",maxWidth:500,boxShadow:"0 8px 40px rgba(0,0,0,0.22)"}}>
            <div style={{fontSize:20,fontWeight:800,color:C.ink,marginBottom:6}}>Going Live 🚀</div>
            <p style={{fontSize:13,color:C.inkMid,marginTop:0,marginBottom:20,lineHeight:1.6}}>
              Before you subscribe, would you like to clear any trial data? Tick what you want removed — unticked items are kept. This cannot be undone.
            </p>
            {[
              {key:"sales",       label:"Sales",                 sub:"Invoices, Quotes, Credit Notes, Recurring Invoices"},
              {key:"expenses",    label:"Expenses",              sub:"All expense records"},
              {key:"customers",   label:"Customers",             sub:"Customer contact records"},
              {key:"suppliers",   label:"Suppliers & Purchases", sub:"Suppliers and Purchase Orders"},
              {key:"employees",   label:"Employees & Payroll",   sub:"Employees, Payslips, Leave, Clocking"},
              {key:"inventory",   label:"Inventory",             sub:"Stock items and quantities"},
              {key:"banking",     label:"Bank Transactions",     sub:"Imported bank transactions and connections"},
              {key:"budgets_assets", label:"Budgets & Fixed Assets", sub:"Budget entries and asset register"},
              {key:"documents",   label:"Documents",             sub:"Uploaded files in Document Repository"},
            ].map(({key, label, sub}) => (
              <label key={key} style={{display:"flex",alignItems:"flex-start",gap:12,marginBottom:12,cursor:"pointer"}}>
                <input type="checkbox" checked={clearSels[key]} onChange={e=>setClearSels(p=>({...p,[key]:e.target.checked}))}
                  style={{marginTop:3,width:16,height:16,accentColor:C.red,flexShrink:0}}/>
                <div>
                  <div style={{fontSize:13,fontWeight:700,color:clearSels[key]?C.red:C.ink}}>{label}</div>
                  <div style={{fontSize:11,color:C.inkDim}}>{sub}</div>
                </div>
              </label>
            ))}
            {Object.values(clearSels).some(Boolean) && (
              <div style={{background:"#FFF3CD",border:"1px solid #FFC107",borderRadius:10,padding:"10px 14px",fontSize:12,color:"#856404",marginTop:8,marginBottom:4}}>
                ⚠️ The ticked data will be <strong>permanently deleted</strong> before payment. This cannot be reversed.
              </div>
            )}
            <div style={{display:"flex",gap:10,marginTop:20}}>
              <button onClick={()=>{setShowTrialModal(false); if(pendingPfData) pfSubmit(pendingPfData.url, pendingPfData.data);}}
                style={{flex:1,padding:"11px 0",borderRadius:10,border:`1px solid ${C.border}`,background:"transparent",color:C.inkMid,fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>
                Keep All & Subscribe
              </button>
              <button onClick={confirmTrialDataAndProceed} disabled={clearingData}
                style={{flex:1,padding:"11px 0",borderRadius:10,border:"none",
                  background: Object.values(clearSels).some(Boolean) ? C.red : C.green,
                  color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",
                  opacity: clearingData ? 0.6 : 1}}>
                {clearingData ? "Clearing…" : Object.values(clearSels).some(Boolean) ? "Clear Selected & Subscribe" : "Subscribe Now"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── ROOT ──────────────────────────────────────────────────────────────────────
export default function App() {
  const [screen, setScreen] = useState("loading");
  const [user,   setUser]   = useState(null);
  const [companies, setCompanies] = useState([]); // Accountant Practice: multi-client memberships
  // Detect invite token from URL path /accept-invite/<token>
  const _inviteMatch = window.location.pathname.match(/\/accept-invite\/([A-Za-z0-9_-]{20,})/);
  const _inviteToken = _inviteMatch ? _inviteMatch[1] : null;
  // Detect portal token from URL path /portal/<token>
  const _portalMatch = window.location.pathname.match(/\/portal\/([A-Za-z0-9]{20,})/);
  const _portalToken = _portalMatch ? _portalMatch[1] : null;

  // Keep Render free-tier backend alive — pings /health every 8 min while logged in
  useEffect(() => {
    if (screen !== "app") return;
    const id = setInterval(() => fetch(`${BASE_URL}/health`).catch(() => {}), 8 * 60 * 1000);
    return () => clearInterval(id);
  }, [screen]);

  useEffect(() => {
    const token = localStorage.getItem("zuzan_token");
    if (_portalToken) { setScreen("portal"); return; }
    if (_inviteToken) { setScreen("accept-invite"); return; }
    if (!token || token.startsWith("demo_")) { setScreen("login"); return; }
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 45000); // 45s — Render free cold start can take 40s
    fetch("https://zuzan-backend.onrender.com/auth/me", {
      headers: {"Authorization": "Bearer " + token},
      signal: controller.signal
    })
      .then(r => { clearTimeout(timeout); return r.ok ? r.json() : Promise.reject(); })
      .then(data => {
        const _meSub = data.company.subscription_status || "trial";
        const _meStatus = (_meSub === "expired" && data.company.trial_ends && new Date(data.company.trial_ends) > new Date()) ? "trial" : _meSub;
        setUser({
          firstName:    data.user.first_name,
          lastName:     data.user.last_name,
          email:        data.user.email,
          companyName:  data.company.name,
          logoUrl:      data.company.logo_url || "",
          headerImageUrl: data.company.invoice_header_url || "",
          invoiceTemplateHtml: data.company.invoice_template_html || "",
          plan:           {name: data.company.plan, id: data.company.plan},
          access_token:   token,
          trialEnds:          data.company.trial_ends,
          subscriptionStatus: _meStatus,
          role:               data.user.role || "owner",
          payrollEnabled: data.company.payroll_enabled || false,
          afsEnabled:     data.company.afs_enabled || false,
          industry:       data.company.industry || "",
          billingExempt:  data.company.billing_exempt || false,
        });
        Sentry.setUser({ email: data.user.email, username: data.company.name });
        if (data.user.role === "accountant") {
          setScreen("accountant-dashboard");
        } else {
          setScreen("app");
        }
      })
      .catch(() => { clearTimeout(timeout); localStorage.removeItem("zuzan_token"); setScreen("login"); });
  }, []);

  const handleLogin = async userData => {
    Sentry.setUser({ email: userData.email, username: userData.companyName });
    // Accountant/partner accounts always go to the Practice Dashboard —
    // they need it even before they've added any clients.
    // Other users get the Client Picker only if they belong to >1 company.
    const isAccountantPractice = userData.role === "accountant";
    if (isAccountantPractice) {
      setUser(userData);
      setScreen("accountant-dashboard");
      return;
    }
    // Non-accountant users always land on their home company dashboard.
    // Company switching is available from the switcher inside the app.
    try {
      const res = await fetch(`${BASE_URL}/auth/my-companies`, {
        headers: {"Authorization": "Bearer " + userData.access_token},
      });
      if (res.ok) {
        const list = await res.json();
        if (Array.isArray(list)) setCompanies(list);
      }
    } catch (e) { /* non-critical — switcher works fine with an empty list */ }
    setUser(userData);
    setScreen("app");
  };

  const handleClientPick = async companyId => {
    try {
      const res = await fetch(`${BASE_URL}/auth/switch-company/${companyId}`, {
        method: "POST",
        headers: {"Authorization": "Bearer " + localStorage.getItem("zuzan_token")},
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Could not switch company.");
      localStorage.setItem("zuzan_token", data.access_token);
      const _rawSub = data.company.subscription_status || "trial";
      const _subStatus = (_rawSub === "expired" && data.company.trial_ends && new Date(data.company.trial_ends) > new Date()) ? "trial" : _rawSub;
      const switchedUser = {
        firstName: data.user.first_name, lastName: data.user.last_name, email: data.user.email,
        companyName: data.company.name, logoUrl: data.company.logo_url || "", headerImageUrl: data.company.invoice_header_url || "", invoiceTemplateHtml: data.company.invoice_template_html || "",
        plan: {name: data.company.plan, id: data.company.plan},
        access_token: data.access_token, trialEnds: data.company.trial_ends,
        subscriptionStatus: _subStatus, role: data.user.role || "owner",
        payrollEnabled: data.company.payroll_enabled || false, afsEnabled: data.company.afs_enabled || false,
        billingExempt: data.company.billing_exempt || false,
      };
      Sentry.setUser({ email: switchedUser.email, username: switchedUser.companyName });
      setUser(switchedUser);
      setScreen("app");
    } catch (e) {
      alert(e.message || "Could not switch company.");
    }
  };

  const handleNewCompany = async (name, plan="starter", industry="") => {
    const token = localStorage.getItem("zuzan_token");
    const res = await fetch(`${BASE_URL}/auth/add-company`, {
      method:"POST", headers:{"Authorization":"Bearer "+token,"Content-Type":"application/json"},
      body: JSON.stringify({company_name: name, plan, industry}),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || "Could not create entity.");
    localStorage.setItem("zuzan_token", data.access_token);
    const _rawSub = data.company.subscription_status || "trial";
    const newUser = {
      firstName: data.user.first_name, lastName: data.user.last_name, email: data.user.email,
      companyName: data.company.name, logoUrl: "", headerImageUrl: "", invoiceTemplateHtml: "",
      plan: {name: data.company.plan, id: data.company.plan},
      access_token: data.access_token, trialEnds: data.company.trial_ends,
      subscriptionStatus: _rawSub, role: "owner",
      payrollEnabled: false, afsEnabled: false, billingExempt: false, industry: data.company.industry || "",
    };
    // Refresh company list then switch into new entity
    try {
      const r2 = await fetch(`${BASE_URL}/auth/my-companies`, {headers:{"Authorization":"Bearer "+data.access_token}});
      if (r2.ok) { const list = await r2.json(); if (Array.isArray(list)) setCompanies(list); }
    } catch(e) {}
    Sentry.setUser({ email: newUser.email, username: newUser.companyName });
    setUser(newUser);
    setScreen("app");
  };

  const handleRegistrationComplete = userData => {
    const savedToken = localStorage.getItem("zuzan_token");
    if (!savedToken || savedToken.startsWith("demo_")) {
      // Backend was unreachable during registration — send to login so user can try again
      localStorage.removeItem("zuzan_token");
      setScreen("login");
      return;
    }
    setUser(userData);
    setScreen("app");
  };

  const handleLogout = () => {
    Sentry.setUser(null);
    localStorage.removeItem("zuzan_token");
    setUser(null);
    setScreen("login");
  };

  // ── Auto-logout after 30 minutes of inactivity ──
  useEffect(() => {
    if (screen !== "app") return;
    const TIMEOUT_MS = 30 * 60 * 1000;
    let timer;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        localStorage.removeItem("zuzan_token");
        setUser(null);
        setScreen("login");
        // Brief delay so React can unmount cleanly before the alert
        setTimeout(() => alert("You were signed out due to 30 minutes of inactivity."), 50);
      }, TIMEOUT_MS);
    };
    const events = ["mousemove","mousedown","keydown","touchstart","scroll","click"];
    events.forEach(e => window.addEventListener(e, reset, {passive:true}));
    reset(); // start timer on mount
    return () => {
      clearTimeout(timer);
      events.forEach(e => window.removeEventListener(e, reset));
    };
  }, [screen]);

  if (screen === "loading") return (
    <div style={{minHeight:"100vh",background:C.bg,display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{textAlign:"center"}}>
        <div style={{fontFamily:"serif",fontSize:40,fontWeight:800,color:C.ink,marginBottom:12}}><span style={{color:C.accent}}>Zu</span>Zan</div>
        <div style={{fontSize:13,color:C.inkMid}}>Loading your account... (may take up to 45s if server is waking up)</div>
      </div>
    </div>
  );

  if (screen === "portal")       return <InvoicePortal token={_portalToken}/>;
  if (screen === "accept-invite") return <AcceptInvite token={_inviteToken} onLogin={handleLogin} onSignIn={()=>setScreen("login")}/>;
  if (screen === "login")        return <Login        onLogin={handleLogin} onRegister={()=>setScreen("registration")}/>;
  if (screen === "registration") return <Registration onComplete={handleRegistrationComplete} onLogin={()=>setScreen("login")}/>;
  if (screen === "client-picker") return <ClientPicker companies={companies} onSelect={handleClientPick} onLogout={handleLogout} onNewCompany={handleNewCompany}/>;
  if (screen === "accountant-dashboard") return <AccountantDashboard user={user} onSelectCompany={handleClientPick} onLogout={handleLogout}/>;

  return (
    <Sentry.ErrorBoundary fallback={
      <div style={{minHeight:"100vh",background:C.bg,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"sans-serif"}}>
        <div style={{textAlign:"center",maxWidth:420,padding:40}}>
          <div style={{fontFamily:"serif",fontSize:36,fontWeight:800,color:C.ink,marginBottom:16}}><span style={{color:C.accent}}>Zu</span>Zan</div>
          <h2 style={{color:C.ink,margin:"0 0 12px"}}>Something went wrong</h2>
          <p style={{color:C.inkMid,lineHeight:1.6,margin:"0 0 24px"}}>An unexpected error occurred. Our team has been notified automatically.</p>
          <button onClick={()=>window.location.reload()} style={{background:C.accent,color:"#fff",border:"none",borderRadius:10,padding:"12px 28px",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
            Reload ZuZan
          </button>
        </div>
      </div>
    }>
      <ZuZanApp user={user} onLogout={handleLogout} onUserUpdate={setUser} onBackToPractice={()=>setScreen("accountant-dashboard")}/>
    </Sentry.ErrorBoundary>
  );
}
